import React, { useState, useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { createPortal } from 'react-dom';
import { Languages, Sun, Moon, Bell, Shield, User, ShoppingCart, Search, Check, Truck, MessageSquare, AlertCircle, Globe } from 'lucide-react';
import { db, auth } from './firebase';
import {
 collection, doc, setDoc, getDoc, getDocs, addDoc, query, where, updateDoc, deleteDoc, onSnapshot, writeBatch
, limit, orderBy, startAfter, getCountFromServer } from 'firebase/firestore';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, GoogleAuthProvider, signInWithPopup, sendPasswordResetEmail, updatePassword } from 'firebase/auth';
const LandingPage = React.lazy(() => import('./LandingPage'));
const CustomerStorefront = React.lazy(() => import('./CustomerStorefront'));
const CallsView = React.lazy(() => import('./CallsView'));
const AiForecastView = React.lazy(() => import('./AiForecastView'));
const EmployeesView = React.lazy(() => import('./EmployeesView'));



// ─── Helper Utilities ────────────────────────────────────────────────────────
export const cleanName = (name) => {
  if (!name) return '';
  return name.toString().trim()
    .replace(/^(د\.?\s*|الطيار\s*|كابتن\s*|دكتور\s*|د\s+)/i, '') // Remove prefixes
    .replace(/\s+/g, '') // Remove all spacing
    .toLowerCase();
};

export const formatShiftDuration = (start, end) => {
  if (!start || !end) return 'غير محدد';
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (isNaN(s) || isNaN(e)) return 'غير محدد';
  
  const diffMs = e - s;
  if (diffMs <= 0) return '0 دقائق';
  
  const hours = Math.floor(diffMs / 3600000);
  const mins = Math.floor((diffMs % 3600000) / 60000);
  
  let res = '';
  if (hours > 0) res += `${hours} ساعة `;
  if (mins > 0) res += `و ${mins} دقيقة`;
  return res || 'أقل من دقيقة';
};


function parseFirebaseDate(d) {
  if (!d) return null;
  if (d instanceof Date) return d;
  if (typeof d.toDate === 'function') return d.toDate();
  if (typeof d.seconds === 'number') return new Date(d.seconds * 1000);
  if (typeof d._seconds === 'number') return new Date(d._seconds * 1000);
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export function parseArabicAndEnglishNumber(val) {
  if (val === null || val === undefined) return 0;
  const str = String(val)
    .replace(/[٠١٢٣٤٥٦٧٨٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
    .replace(/[۰۱۲۳۴۵۶۷۸۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/,/g, '')
    .trim();
  const num = Number(str);
  return isNaN(num) ? 0 : num;
}

/**
 * Returns how many subscription days remain for a user.
 * Returns 'lifetime' for lifetime accounts, a number (possibly negative) otherwise.
 */
function getSubscriptionDaysRemaining(userData) {
  if (!userData) return 0;
  if (userData.subscriptionType === 'lifetime' || userData.isLifetime || userData.plan === 'lifetime') return 'lifetime';
  const dates = [userData.expiryDate, userData.subscriptionExpiresAt, userData.trialExpiresAt]
    .map(parseFirebaseDate)
    .filter(Boolean);
  if (dates.length === 0) return 0;
  const expiry = new Date(Math.max(...dates));
  const now = new Date();
  const diffMs = expiry - now;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Returns how many trial days remain given an ISO expiry date string.
 */
function getTrialRemainingDays(trialExpiresAt) {
  const expiry = parseFirebaseDate(trialExpiresAt);
  if (!expiry) return 0;
  const now = new Date();
  const diffMs = expiry - now;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Safely prepares profile and account data before writing to Firestore,
 * ensuring all date strings are converted to native Javascript Date objects
 * so Firestore stores them as genuine Timestamps, and status flags as booleans.
 */
function prepareDataForFirestore(data, user) {
  if (!data) return {};
  const ready = { ...data };
  
  // 1. Ensure user identification is set
  if (user) {
    if (!ready.uid) ready.uid = user.uid;
    if (!ready.email) ready.email = user.email;
  }

  // 2. Ensure core fields are initialized with defaults if they don't exist
  if (!ready.createdAt) {
    ready.createdAt = new Date(); // Native Firebase Timestamp when saved
  }
  if (!ready.plan) {
    ready.plan = 'trial';
  }
  if (!ready.expiryDate) {
    ready.expiryDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7-day trial default
  }
  if (!ready.trialExpiresAt) {
    ready.trialExpiresAt = ready.expiryDate;
  }
  if (!ready.shortId) {
    ready.shortId = Math.floor(100000 + Math.random() * 900000).toString();
  }
  if (ready.isActive === undefined) {
    ready.isActive = true;
  }
  if (ready.isVerified === undefined) {
    ready.isVerified = true;
  }
  if (ready.promoCode === undefined) {
    ready.promoCode = ""; // Empty string for promo code
  }
  if (ready.deviceLimit === undefined) {
    ready.deviceLimit = 2; // Default device limit
  }

  // 3. Convert standard date fields to Date objects so Firestore stores them as Timestamps
  const timestampFields = ['createdAt', 'expiryDate', 'lastActive', 'lastLogin'];
  timestampFields.forEach(field => {
    if (ready[field] && typeof ready[field] === 'string') {
      ready[field] = new Date(ready[field]);
    }
  });

  // 4. Remove unwanted fields (as requested: referralCount, walletBalance)
  delete ready.referralCount;
  delete ready.walletBalance;

  return ready;
}

function generateDeviceFingerprint() {
  const ua = navigator.userAgent;
  let devType = 'Desktop / Laptop';
  if (/Mobi/i.test(ua)) {
    if (/Android/i.test(ua)) devType = 'Android Mobile';
    else if (/iPhone/i.test(ua)) devType = 'iPhone';
    else devType = 'Mobile Device';
  } else if (/iPad/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) {
    devType = 'Tablet';
  } else if (/Macintosh/i.test(ua)) {
    devType = 'macOS Desktop';
  } else if (/Windows/i.test(ua)) {
    devType = 'Windows PC';
  } else if (/Linux/i.test(ua)) {
    devType = 'Linux PC';
  }

  // Gather screen resolution, color depth, browser languages, time zone, etc.
  const screenSpec = window.screen ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}` : 'unknown';
  const cores = navigator.hardwareConcurrency || 0;
  const memory = navigator.deviceMemory || 0;
  const lang = navigator.language || '';
  const tz = new Date().getTimezoneOffset();

  const rawInfo = `${ua}|${screenSpec}|${cores}|${memory}|${lang}|${tz}`;

  // Stable hashing using djb2 algorithm
  let hash = 5381;
  for (let i = 0; i < rawInfo.length; i++) {
    hash = (hash * 33) ^ rawInfo.charCodeAt(i);
  }
  const hashStr = (hash >>> 0).toString(36).toUpperCase();

  return `${devType} (${hashStr})`;
}

export function calculateInsuranceSplit(cart, contract, subtotal, totalDiscount, currentPointsDiscount, deliveryFee, isDeliveryOrder, currentMonthlyConsumption = 0) {
  if (!contract) {
    const total = parseFloat((subtotal - totalDiscount - currentPointsDiscount + (isDeliveryOrder ? (parseFloat(deliveryFee) || 0) : 0)).toFixed(2));
    return {
      patientShare: total,
      insuranceShare: 0,
      isCapReached: false,
      isMonthlyCapReached: false,
      details: []
    };
  }

  const copayLocal = contract.copayPercentageLocal !== undefined ? contract.copayPercentageLocal : (contract.copayPercentage !== undefined ? contract.copayPercentage : 20);
  const copayImported = contract.copayPercentageImported !== undefined ? contract.copayPercentageImported : (contract.copayPercentage !== undefined ? contract.copayPercentage : 50);
  const maxCap = contract.maxCoverageLimit !== undefined ? Number(contract.maxCoverageLimit) : 0;
  const monthlyCap = contract.monthlyCoverageLimit !== undefined ? Number(contract.monthlyCoverageLimit) : 0;
  const allowDiscounts = contract.allowDiscounts !== undefined ? contract.allowDiscounts : true;
  
  let excludedCats = [];
  if (Array.isArray(contract.excludedCategories)) {
    excludedCats = contract.excludedCategories.map(c => c.trim().toLowerCase());
  } else if (typeof contract.excludedCategories === 'string') {
    excludedCats = contract.excludedCategories.split(',').map(c => c.trim().toLowerCase());
  }

  const totalDiscountAmt = allowDiscounts ? (totalDiscount + currentPointsDiscount) : 0;
  const discountRatio = (allowDiscounts && subtotal > 0) ? (totalDiscountAmt / subtotal) : 0;

  const itemDetails = cart.map(item => {
    let activePrice = item.subUnit ? item.subUnit.price : item.sellingPrice;
    if (allowDiscounts && item.hasDiscount && item.discountedPrice !== undefined) {
      const ratio = item.discountedPrice / item.sellingPrice;
      activePrice = item.subUnit ? Math.round(item.subUnit.price * ratio * 100) / 100 : item.discountedPrice;
    }
    
    const itemSubtotal = activePrice * item.cartQuantity;
    const itemDiscount = itemSubtotal * discountRatio;
    const itemFinalPrice = Math.max(0, itemSubtotal - itemDiscount);

    const category = (item.category || '').trim().toLowerCase();
    const isExcluded = excludedCats.some(cat => cat === category || category.includes(cat));

    let copayPercent = 100;
    if (!isExcluded) {
      if (item.origin === 'imported') {
        copayPercent = copayImported;
      } else {
        copayPercent = copayLocal;
      }
    }

    const patientItemShare = Math.round(itemFinalPrice * (copayPercent / 100) * 100) / 100;
    const insuranceItemShare = Math.round((itemFinalPrice - patientItemShare) * 100) / 100;

    return {
      itemId: item.id,
      tradeName: item.tradeName,
      origin: item.origin || 'local',
      category: item.category || 'عام',
      isExcluded,
      copayPercent,
      finalPrice: itemFinalPrice,
      patientShare: patientItemShare,
      insuranceShare: insuranceItemShare
    };
  });

  let totalPatientShare = itemDetails.reduce((sum, det) => sum + det.patientShare, 0);
  let totalInsuranceShare = itemDetails.reduce((sum, det) => sum + det.insuranceShare, 0);

  if (isDeliveryOrder) {
    totalPatientShare += parseFloat(deliveryFee) || 0;
  }

  let isCapReached = false;
  if (maxCap > 0 && totalInsuranceShare > maxCap) {
    isCapReached = true;
    const difference = totalInsuranceShare - maxCap;
    totalInsuranceShare = maxCap;
    totalPatientShare += difference;
  }

  let isMonthlyCapReached = false;
  if (monthlyCap > 0) {
    const remainingLimit = Math.max(0, monthlyCap - currentMonthlyConsumption);
    if (totalInsuranceShare > remainingLimit) {
      isMonthlyCapReached = true;
      const difference = totalInsuranceShare - remainingLimit;
      totalInsuranceShare = remainingLimit;
      totalPatientShare += difference;
    }
  }

  return {
    patientShare: parseFloat(totalPatientShare.toFixed(2)),
    insuranceShare: parseFloat(totalInsuranceShare.toFixed(2)),
    isCapReached,
    isMonthlyCapReached,
    details: itemDetails
  };
}

export default function App() {
 const [user, setUser] = useState(null);
  const [activeShift, setActiveShift] = useState(null);
  const [userProfileLoading, setUserProfileLoading] = useState(true);
  const [deviceLimitExceeded, setDeviceLimitExceeded] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState(null); // { message, onConfirm, onCancel }
  const [alertDialog, setAlertDialog] = useState(null); // { message, onClose }

  window.triggerDrMasrConfirm = (message, onConfirm, onCancel = null) => {
    setConfirmDialog({ message, onConfirm, onCancel });
  };
  window.triggerDrMasrAlert = (message, onClose = null) => {
    setAlertDialog({ message, onClose });
  };

  const [prefilledBarcode, setPrefilledBarcode] = useState('');
  const [incrementProduct, setIncrementProduct] = useState(null);
  const [incrementCount, setIncrementCount] = useState(0);
  const activeIncrementProductIdRef = useRef(null);
  const [globalToast, setGlobalToast] = useState({ show: false, message: '' });
  const [activeShifts, setActiveShifts] = useState(() => {
    try {
      const savedUser = localStorage.getItem('drmasr_user');
      if (savedUser) {
        const uid = JSON.parse(savedUser).uid;
        const cached = localStorage.getItem('drmasr_activeShifts_' + uid);
        if (cached) return JSON.parse(cached);
      }
    } catch (e) {}
    return [];
  });
  const [shiftsHistory, setShiftsHistory] = useState([]);
  const [isShiftsHistoryLoaded, setIsShiftsHistoryLoaded] = useState(false);
  const [isShiftsHistoryLoading, setIsShiftsHistoryLoading] = useState(false);
  const [isSalesLoaded, setIsSalesLoaded] = useState(false);
  const [isSalesLoading, setIsSalesLoading] = useState(false);
  const [isShiftDialogOpen, setIsShiftDialogOpen] = useState(false);
  const [isAccountingModalOpen, setIsAccountingModalOpen] = useState(false);
  const [employees, setEmployees] = useState(() => {
    try {
      const savedUser = localStorage.getItem('drmasr_user');
      if (savedUser) {
        const uid = JSON.parse(savedUser).uid;
        const cached = localStorage.getItem('drmasr_employees_' + uid);
        if (cached) return JSON.parse(cached);
      }
    } catch (e) {}
    return [];
  });
  const [telegramSettings, setTelegramSettings] = useState(() => {
    try {
      const savedUser = localStorage.getItem('drmasr_user');
      if (savedUser) {
        const uid = JSON.parse(savedUser).uid;
        const cached = localStorage.getItem('drmasr_telegramSettings_' + uid);
        if (cached) return JSON.parse(cached);
      }
    } catch (e) {}
    return {
      enabled: false,
      chatId: '',
      botToken: '8660558967:AAE-aht04AtybmrD3r_afPEy1WddKW0Fz4M'
    };
  });

  const [paymentSettings, setPaymentSettings] = useState(() => {
    try {
      const savedUser = localStorage.getItem('drmasr_user');
      if (savedUser) {
        const uid = JSON.parse(savedUser).uid;
        const cached = localStorage.getItem('drmasr_paymentSettings_' + uid);
        if (cached) return JSON.parse(cached);
      }
    } catch (e) {}
    return { instaPayIPN: '', vodafoneCashNumber: '' };
  });

  const [isSplit, setIsSplit] = useState(false);
  const [splitCashInput, setSplitCashInput] = useState('');
  const [splitCardInput, setSplitCardInput] = useState('');
  const [isEWallet, setIsEWallet] = useState(false);
  const [isInstaPay, setIsInstaPay] = useState(false);
  const [isVodafone, setIsVodafone] = useState(false);

  // Helper: reset all payment mode toggles
  const resetPaymentModes = () => {
    setIsSplit(false); setSplitCashInput(''); setSplitCardInput('');
    setIsEWallet(false);
    setIsInstaPay(false);
    setIsVodafone(false);
  };

  // Delivery Specific POS States
  const [isDeliveryOrder, setIsDeliveryOrder] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryDriver, setDeliveryDriver] = useState('');
  const [deliveryFee, setDeliveryFee] = useState(0);

  const [inventory, setInventory] = useState(() => {
    try {
      const isMobileDevice = window.innerWidth < 1024;
      if (isMobileDevice) return [];
      const savedUser = localStorage.getItem('drmasr_user');
      if (savedUser) {
        const uid = JSON.parse(savedUser).uid;
        const cached = localStorage.getItem('drmasr_inv_' + uid);
        if (cached) {
          return JSON.parse(cached).map(item => {
            let parsedExpiry = item.expiryDate;
            if (parsedExpiry && typeof parsedExpiry === 'object') {
              const { year, month, day } = parsedExpiry;
              if (year && month && day) {
                parsedExpiry = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              }
            }
            return { ...item, expiryDate: parsedExpiry };
          });
        }
      }
    } catch (e) {}
    return [];
  });
  const [sales, setSales] = useState([]);
  const [onlineOrders, setOnlineOrders] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [unreadAnnouncementsCount, setUnreadAnnouncementsCount] = useState(0);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [isOnlineState, setIsOnlineState] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnlineState(true);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("📶 تم استعادة الاتصال بالإنترنت! جاري تحديث ومزامنة البيانات مع السحابة... ☁️");
      }
    };
    const handleOffline = () => {
      setIsOnlineState(false);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("⚠️ انقطع الاتصال بالإنترنت! يعمل السيستم الآن في وضع الأوفلاين الآمن تلقائياً.");
      }
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const [isClockTampered, setIsClockTampered] = useState(false);

  // Clock rollback detection hook
  useEffect(() => {
    if (!user?.uid) return;
    try {
      const lastSavedTimeStr = localStorage.getItem('drmasr_last_known_time_' + user.uid);
      const nowMs = Date.now();
      
      if (lastSavedTimeStr) {
        const lastSavedTime = Number(lastSavedTimeStr);
        // If current clock time is behind the last saved time by more than 5 minutes, flag clock tampering
        if (nowMs < lastSavedTime - 300000) {
          setIsClockTampered(true);
          return;
        }
      }
      
      // Update last known time continuously
      localStorage.setItem('drmasr_last_known_time_' + user.uid, String(nowMs));
      const interval = setInterval(() => {
        const currentMs = Date.now();
        const saved = Number(localStorage.getItem('drmasr_last_known_time_' + user.uid) || '0');
        if (currentMs < saved - 300000) {
          setIsClockTampered(true);
        } else {
          localStorage.setItem('drmasr_last_known_time_' + user.uid, String(currentMs));
        }
      }, 10000);
      
      return () => clearInterval(interval);
    } catch (e) {
      console.error("Clock tampering validation error:", e);
    }
  }, [user?.uid]);

  // Sales chronology validation hook (bulletproof offline clock check)
  useEffect(() => {
    if (sales && sales.length > 0) {
      const maxSaleTime = Math.max(...sales.map(s => {
        if (!s.timestamp) return 0;
        if (s.timestamp.seconds) return s.timestamp.seconds * 1000;
        return new Date(s.timestamp).getTime();
      }).filter(t => !isNaN(t)));
      
      if (maxSaleTime > 0 && Date.now() < maxSaleTime - 300000) {
        setIsClockTampered(true);
      }
    }
  }, [sales]);

  const [cart, setCart] = useState([]);
  const [posSearchTerm, setPosSearchTerm] = useState('');
  const [posDiscount, setPosDiscount] = useState(0);
  const [usePoints, setUsePoints] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryPage, setInventoryPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [tradeName, setTradeName] = useState('');
  const [tradeNameSecondary, setTradeNameSecondary] = useState('');
  const [genericName, setGenericName] = useState('');
  const [requiresFridge, setRequiresFridge] = useState(false);
  const [origin, setOrigin] = useState('local');
  const [category, setCategory] = useState('');
  const [buyingPrice, setBuyingPrice] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [barcode, setBarcode] = useState('');
  const [subUnits, setSubUnits] = useState([]);
  const [newSubUnitName, setNewSubUnitName] = useState('');
  const [newSubUnitRatio, setNewSubUnitRatio] = useState('');
  const [newSubUnitPrice, setNewSubUnitPrice] = useState('');
  const [isAdminPinModalOpen, setIsAdminPinModalOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [selectedUnitProduct, setSelectedUnitProduct] = useState(null);
  const [selectedAlternativesProduct, setSelectedAlternativesProduct] = useState(null);
  const barcodeInputRef = useRef(null);

  const [loyaltyCustomers, setLoyaltyCustomers] = useState([]);
  const [generalCampaigns, setGeneralCampaigns] = useState([]); // Global campaigns from Firestore
  const [refillAlerts, setRefillAlerts] = useState([]);

  const clearForm = () => {
    setEditId(null);
    setTradeName('');
    setTradeNameSecondary('');
    setGenericName('');
    setCategory('');
    setBuyingPrice('');
    setSellingPrice('');
    setQuantity('');
    setExpiryDate('');
    setBarcode('');
    setRequiresFridge(false);
    setOrigin('local');
    setSubUnits([]);
    setNewSubUnitName('');
    setNewSubUnitRatio('');
    setNewSubUnitPrice('');
    setIsModalOpen(false);
    setPrefilledBarcode('');
  };

  // Medical Insurance States
  const [insuranceContracts, setInsuranceContracts] = useState([]);
  const [selectedInsuranceContract, setSelectedInsuranceContract] = useState(null);
  const [insuranceApprovalNumber, setInsuranceApprovalNumber] = useState('');

  // Shop path resolver logic
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const isShopPath = pathParts[0] === 'shop' && pathParts[1];
  const shopSlug = isShopPath ? pathParts[1] : null;
  const [resolvedShopId, setResolvedShopId] = useState(null);
  const [resolvingShop, setResolvingShop] = useState(!!shopSlug);

  useEffect(() => {
    if (!shopSlug) return;
    
    const resolveShop = async () => {
      try {
        // 1. Try to check if shopSlug is actually a UID directly
        const docSnap = await getDoc(doc(db, 'users', shopSlug));
        if (docSnap.exists()) {
          setResolvedShopId(shopSlug);
          setResolvingShop(false);
          return;
        }
        
        // 2. Query Firestore by pharmacySlug
        const qSlug = query(collection(db, 'users'), where('pharmacySlug', '==', shopSlug));
        const snapSlug = await getDocs(qSlug);
        if (!snapSlug.empty) {
          setResolvedShopId(snapSlug.docs[0].id);
          setResolvingShop(false);
          return;
        }
        
        // 3. Query Firestore by shortId
        const qShort = query(collection(db, 'users'), where('shortId', '==', shopSlug));
        const snapShort = await getDocs(qShort);
        if (!snapShort.empty) {
          setResolvedShopId(snapShort.docs[0].id);
          setResolvingShop(false);
          return;
        }
        
        // 4. Fallback: query by pharmacyName (decoded)
        const decodedName = decodeURIComponent(shopSlug).replace(/-/g, ' ');
        const qName = query(collection(db, 'users'), where('pharmacyName', '==', decodedName));
        const snapName = await getDocs(qName);
        if (!snapName.empty) {
          setResolvedShopId(snapName.docs[0].id);
          setResolvingShop(false);
          return;
        }
        
        setResolvingShop(false);
      } catch (err) {
        console.error("Error resolving shop slug:", err);
        setResolvingShop(false);
      }
    };
    resolveShop();
  }, [shopSlug, db]);

  const getInitialTab = () => {
    const path = window.location.pathname.replace(/^\/+/, '');
    const validTabs = ['pos', 'inventory', 'reports', 'shifts', 'customers', 'delivery', 'online_orders', 'eta', 'settings', 'profile', 'subscription', 'purchases', 'discounts', 'calls'];
    if (validTabs.includes(path)) {
      return path;
    }
    return 'pos';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [isMobileCheckoutOpen, setIsMobileCheckoutOpen] = useState(false);

  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const isLoyaltyEnabled = currentUserData?.loyaltySettings?.enabled !== false;
  const [isInventoryLoading, setIsInventoryLoading] = useState(() => {
    try {
      const isMobileDevice = window.innerWidth < 1024;
      if (isMobileDevice) return false;
      const savedUser = localStorage.getItem('drmasr_user');
      if (savedUser) {
        const uid = JSON.parse(savedUser).uid;
        const cached = localStorage.getItem('drmasr_inv_' + uid);
        if (cached) {
          return false;
        }
      }
    } catch (e) {}
    return true;
  });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('drmasr_theme');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('drmasr_language') || 'ar';
  });
  const [loading, setLoading] = useState(false);
  const [globalLoading, setGlobalLoading] = useState({ active: false, title: '', message: '' });
 const [errorMessage, setErrorMessage] = useState('');
 const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
 const [profileMenuOpen, setProfileMenuOpen] = useState(false);
 const checkIsLanding = () => {
    const path = window.location.pathname.toLowerCase().replace(/\/$/, '');
    const hash = window.location.hash.toLowerCase();
    const query = window.location.search.toLowerCase();
    return path === '/landing' || hash === '#/landing' || query.includes('landing');
  };

  const [showLanding, setShowLanding] = useState(checkIsLanding());
  const [isAdminVerified, setIsAdminVerified] = useState(false);
  const [isAdminHovered, setIsAdminHovered] = useState(false);
  const [isPharmacyHovered, setIsPharmacyHovered] = useState(false);
  const [lastAdminVerificationTime, setLastAdminVerificationTime] = useState(0);

  // Centralized Admin PIN Gate states
  const [globalAdminPin, setGlobalAdminPin] = useState('');
  const [globalAdminError, setGlobalAdminError] = useState('');
  const [showGlobalPin, setShowGlobalPin] = useState(false);
  const [isGlobalSubmitHovered, setIsGlobalSubmitHovered] = useState(false);
  const [isGlobalEyeHovered, setIsGlobalEyeHovered] = useState(false);



  // Dynamic Translation Utility for English / Arabic system-wide localization
  useEffect(() => {
    if (language === 'ar') {
      return;
    }

    const translationMap = {
      // Sidebar Navigation
      "واجهة الكاشير (POS)": "Cashier POS",
      "إدارة المخزون": "Inventory",
      "المشتريات والموردين": "Purchases & Suppliers",
      "الخصومات والبروموكود": "Discounts & Promo Codes",
      "التقارير والمبيعات": "Reports & Sales",
      "سجل الورديات": "Shifts Record",
      "إدارة العملاء والـ CRM": "Customers & CRM",
      "الدليفري": "Delivery Status",
      "الطلبات الأونلاين": "Online Orders",
      "إعدادات الضرائب (ETA)": "Tax Settings (ETA)",
      "إدارة التأمين والنقابات": "Insurance & Syndicates",
      "إعدادات النظام": "System Settings",
      "الملف الشخصي": "Profile",
      "الاشتراكات": "Subscriptions",
      "المستلم الحالي (الوردية النشطة):": "Current Cashier (Active Shift):",
      "تسجيل الخروج": "Logout",

      // Common Controls & Headers
      "بحث": "Search",
      "تعديل": "Edit",
      "حذف": "Delete",
      "حفظ": "Save",
      "تأكيد": "Confirm",
      "إلغاء": "Cancel",
      "الغاء": "Cancel",
      "إغلاق": "Close",
      "طباعة": "Print",
      "مشاركة": "Share",
      "إضافة": "Add New",
      "تصدير": "Export",
      "استيراد": "Import",
      "عرض": "View",
      "تفاصيل": "Details",
      "الحالة": "Status",
      "تصفير": "Reset",
      "تاريخ": "Date",
      "الصفحة": "Page",
      "من": "of",
      "التالي": "Next",
      "السابق": "Previous",
      "انتهى": "Expired",
      "صلاحية:": "Expiry:",
      "الوضع المضيء": "Light Mode",
      "الوضع المظلم": "Dark Mode",
      "دخول كمسؤول": "Admin Login",
      "متصل سحابياً (مزامنة فورية آمنة)": "Connected to Cloud (Secure Sync)",
      "وضع الأوفلاين (البيانات محفوظة محلياً)": "Offline Mode (Saved Locally)",

      // Cashier POS Page
      "السلة فارغة. يرجى إضافة أدوية للبدء.": "Shopping cart is empty. Please add items to start.",
      "سلة المشتريات": "Shopping Cart",
      "السلة فارغة": "Shopping cart is empty",
      "بيانات العميل:": "Customer Data:",
      "اسم العميل": "Customer Name",
      "رقم العميل": "Customer Phone",
      "المجموع الفرعي:": "Subtotal:",
      "صافي المطلوب:": "Net Amount:",
      "دفع مختلط (كاش + فيزا)": "Mixed Payment (Cash + Visa)",
      "الدفع الرقمي / المحافظ الإلكترونية": "Digital Payment / E-Wallets",
      "بيع كاش": "Sell Cash",
      "بيع فيزا": "Sell Visa",
      "طلب دليفري": "Delivery Order",
      "اسم المنتج": "Product Name",
      "الكمية": "Qty",
      "السعر": "Price",
      "المجموع": "Subtotal",
      "الخصم": "Discount",
      "الإجمالي قبل الخصم": "Total Before Discount",
      "الإجمالي النهائي": "Final Total",
      "المدفوع من العميل": "Amount Paid by Customer",
      "المتبقي (المرتجع للعميل)": "Change Due",
      "إنهاء الطلب وطباعة الفاتورة": "Checkout & Print Invoice",
      "نوع البيع": "Sale Type",
      "داخل الصالة / تيك أواي": "In-Store / Takeaway",
      "دليفري (توصيل للمنزل)": "Delivery",
      "طريقة الدفع": "Payment Method",
      "كاش (نقدي)": "Cash",
      "فيزا / كارت": "Visa / Card",
      "محفظة إلكترونية": "E-Wallet",
      "نوع المحفظة": "Wallet Type",
      "خصم إضافي": "Additional Discount",
      "قيمة الخصم": "Discount Value",
      "بروموكود (كود الخصم)": "Promo Code",
      "تطبيق": "Apply",
      "اسم العميل (اختياري)": "Customer Name (Optional)",
      "رقم الهاتف (اختياري)": "Phone Number (Optional)",
      "اسم الطيار": "Driver Name",
      "عنوان التوصيل": "Delivery Address",
      "تكلفة التوصيل": "Delivery Fee",
      "العميل يستحق نقاط": "Customer earns points",
      "خصم النقاط": "Points Discount",
      "متاح": "Available",
      "نقطة تعادل": "Points Equal",
      "بحث عن صنف أو مسح باركود...": "Search item or scan barcode...",
      "جميع الأقسام": "All Categories",

      // POS Success Modal
      "تم إتمام العملية بنجاح!": "Transaction completed successfully!",
      "رقم الفاتورة": "Invoice ID",
      "تاريخ الفاتورة": "Invoice Date",
      "الكاشير": "Cashier",
      "المنتج": "Product",
      "المجموع الفرعي": "Subtotal",
      "تكلفة الشحن": "Shipping Fee",
      "المدفوع": "Paid",
      "الباقي": "Change",
      "إرسال الفاتورة عبر واتساب": "Send Invoice via WhatsApp",
      "طباعة الفاتورة حرارياً": "Print Receipt (Thermal)",
      "مشاركة الفاتورة": "Share Invoice",
      "فاتورة جديدة": "New Invoice",

      // Inventory Page
      "إدارة مخزون المنتجات": "Product Inventory Management",
      "إضافة منتج جديد": "Add New Product",
      "تصدير المخزون إلى Excel": "Export Inventory to Excel",
      "استيراد المخزون من Excel": "Import Inventory from Excel",
      "ترجمة أسماء المخزون": "Translate Inventory Names",
      "بحث بالاسم أو الباركود...": "Search by name or barcode...",
      "الباركود": "Barcode",
      "القسم / التصنيف": "Category",
      "سعر الشراء": "Buying Price",
      "سعر البيع": "Selling Price",
      "الكمية المتاحة": "Available Stock",
      "النوع (محلي/مستورد)": "Type (Local/Imported)",
      "محلي": "Local",
      "مستورد": "Imported",
      "العمليات": "Actions",

      // Purchases Page
      "إدارة المشتريات والموردين": "Purchases & Suppliers Management",
      "إضافة فاتورة شراء": "Add Purchase Invoice",
      "المورد": "Supplier",
      "تاريخ الشراء": "Purchase Date",
      "قيمة الفاتورة": "Invoice Value",
      "سجل المشتريات": "Purchases Log",
      "الموردين": "Suppliers",
      "إضافة مورد جديد": "Add New Supplier",
      "اسم المورد": "Supplier Name",
      "رقم الهاتف": "Phone Number",
      "العنوان": "Address",
      "مستحقات المورد": "Supplier Due Balances",

      // Discounts & Promo codes
      "إدارة العروض والخصومات": "Discounts & Offers Management",
      "إنشاء بروموكود جديد": "Create New Promo Code",
      "كود الخصم": "Promo Code",
      "نسبة الخصم": "Discount Percentage",
      "الحد الأقصى للخصم": "Max Discount Limit",
      "تاريخ الانتهاء": "Expiration Date",
      "حالة الكود": "Promo Status",
      "نشط": "Active",
      "منتهي": "Expired",
      "الرموز الفعالة": "Active Promo Codes",

      // Reports Page
      "التقارير والإحصائيات": "Reports & Analytics",
      "إجمالي المبيعات اليوم": "Today's Total Sales",
      "عدد فواتير اليوم": "Today's Invoices Count",
      "صافي الأرباح اليوم": "Today's Net Profit",
      "المبيعات حسب التصنيف": "Sales by Category",
      "المنتجات الأكثر مبيعاً": "Top Selling Products",
      "سجل المبيعات والفواتير": "Sales & Invoices History",
      "تاريخ البدء": "Start Date",
      "تاريخ الانتهاء": "End Date",
      "تعديل الفاتورة": "Edit Invoice",
      "مرتجع": "Refunded",
      "مرتجع جزئي": "Partially Refunded",
      "إرجاع الفاتورة بالكامل": "Fully Refund Invoice",
      "إرجاع جزئي": "Partial Return",

      // Shifts Page
      "سجل ورديات النظام": "System Shifts Log",
      "فتح وردية جديدة": "Open New Shift",
      "إغلاق الوردية الحالية": "Close Current Shift",
      "الموظف": "Employee / Cashier",
      "وقت الفتح": "Opening Time",
      "وقت الإغلاق": "Closing Time",
      "رصيد البداية": "Starting Cash Balance",
      "المبيعات النقدية": "Cash Sales",
      "المبيعات الإلكترونية": "E-Wallet/Card Sales",
      "المبلغ المتوقع": "Expected Total cash",
      "المبلغ الفعلي": "Actual cash entered",
      "العجز / الزيادة": "Shortage / Surplus",
      "الفرق": "Difference",
      "حالة الوردية": "Shift Status",
      "مفتوحة": "Open",
      "مغلقة": "Closed",

      // CRM Customers Page
      "إدارة العملاء والولاء (CRM)": "Customers CRM & Loyalty",
      "إضافة عميل جديد": "Add New Customer",
      "رقم هاتف العميل": "Customer Phone Number",
      "النقاط الحالية": "Current Points",
      "إجمالي المشتريات": "Total Purchased",
      "تاريخ التسجيل": "Registration Date",
      "استيراد عملاء من Excel": "Import Customers from Excel",
      "تصدير العملاء إلى Excel": "Export Customers to Excel",

      // Delivery Page
      "متابعة طلبات الدليفري والطيارين": "Delivery & Drivers Tracking",
      "اسم الطيار / السائق": "Driver Name",
      "حالة التوصيل": "Delivery Status",
      "انتظار": "Pending",
      "خارج للتوصيل": "Out for Delivery",
      "تم التوصيل": "Delivered",
      "تحديث الحالة": "Update Status",
      "تأكيد استلام النقدية من الطيار": "Confirm Cash Handover from Driver",

      // Online Orders Page
      "طلبات المتجر الإلكتروني المعلقة": "Pending Online / Storefront Orders",
      "رقم الطلب": "Order ID",
      "تفاصيل الطلب": "Order Details",
      "قبول الطلب وتجهيزه": "Accept & Prepare Order",
      "رفض الطلب": "Reject Order",

      // ETA Tax Page
      "إعدادات مصلحة الضرائب المصرية (ETA)": "Egyptian Tax Authority (ETA) Integration",
      "رقم التسجيل الضريبي": "Tax Registration Number",
      "الرقم الداخلي للفاتورة": "Internal Invoice Prefix ID",
      "حالة الربط الضريبي": "Integration Status",
      "غير مفعل": "Disabled",
      "مفعل (تجريبي)": "Enabled (Sandbox Mode)",
      "مفعل (رسمي)": "Enabled (Production Mode)",

      // Medical Insurance Page
      "إدارة التعاقدات والجهات الطبية": "Medical Insurance & Syndicates",
      "إضافة جهة تعاقد جديدة": "Add New Contract",
      "اسم الجهة / النقابة": "Syndicate / Agency Name",
      "نسبة تحمل المريض - منتج محلي": "Patient Copay % - Local Products",
      "نسبة تحمل المريض - منتج مستورد": "Patient Copay % - Imported Products",
      "الحد الأقصى لتغطية الفاتورة": "Invoice Max Coverage Limit",
      "الفئات المستثناة من التغطية": "Excluded Categories",
      "قائمة التعاقدات الحالية": "Current Active Contracts",

      // System Settings Page
      "إعدادات النظام العامة": "General System Settings",
      "اسم الصيدلية / النشاط": "Business / Pharmacy Name",
      "عنوان النشاط": "Business Address",
      "رقم الهاتف للتواصل": "Contact Phone Number",
      "رسالة أسفل الفاتورة": "Footer Receipt Message",
      "نسبة ضريبة القيمة المضافة (%)": "VAT Rate (%)",
      "إعدادات تلجرام للتنبيهات": "Telegram Notifications Settings",
      "تفعيل إشعارات تلجرام للورديات": "Enable Shift Telegram Notifications",
      "تصفير التقارير": "Reset system reports",
      "تصفير تقارير المبيعات بالكامل": "Reset All Sales Reports",
      "🚨 تصفير التقارير": "🚨 Clear Reports",

      // Profile Page
      "تحديث بيانات الملف الشخصي": "Update Profile Details",
      "البريد الإلكتروني": "Email Address",
      "كلمة المرور الجديدة": "New Password",
      "رمز الدخول الخاص بالمسؤول (PIN)": "Admin Access Code (PIN)",
      "تحديث البيانات": "Update Profile Info",

      // Subscription Page
      "تفاصيل الاشتراك الحالي": "Current Subscription Details",
      "باقة الاشتراك": "Subscription Package",
      "تاريخ التجديد القادم": "Next Renewal Date",
      "حالة الاشتراك": "Subscription Status",

      // Miscellaneous & General UI
      "ج.م": "EGP",
      "الوردية النشطة": "Active Shift",
      "إدارة الوردية": "Shift Management",
      "فتح القفل": "Unlock System",
      "تاريخ البدء": "Start Date",
      "تاريخ الانتهاء": "End Date",
      "أيام متبقية": "Days remaining",
      "يوم متبقي": "Day remaining",
      "اشتراك مدى الحياة": "Lifetime Subscription"
    };

    // Sort translation keys by length (longest first) to prevent partial replacements of single words inside sentences.
    const sortedTranslationKeys = Object.keys(translationMap).sort((a, b) => b.length - a.length);

    const translateElement = (el) => {
      if (el.nodeType === Node.TEXT_NODE) {
        const text = el.nodeValue.trim();
        if (translationMap[text]) {
          el.nodeValue = translationMap[text];
        } else {
          // Fallback check for substrings using sorted keys to prevent partial matches
          let newText = el.nodeValue;
          let changed = false;
          sortedTranslationKeys.forEach(key => {
            if (newText.includes(key)) {
              newText = newText.replaceAll(key, translationMap[key]);
              changed = true;
            }
          });
          if (changed) {
            el.nodeValue = newText;
          }
        }
      } else {
        if (el.placeholder && translationMap[el.placeholder.trim()]) {
          el.placeholder = translationMap[el.placeholder.trim()];
        }
        if (el.title && translationMap[el.title.trim()]) {
          el.title = translationMap[el.title.trim()];
        }
        el.childNodes.forEach(translateElement);
      }
    };

    // Run translations
    translateElement(document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          translateElement(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [language]);

  // Add routing effect to handle landing path and tab navigation
  useEffect(() => {
    const handlePathChange = () => {
      setShowLanding(checkIsLanding());
      
      // Sync activeTab state from URL path when it changes
      const path = window.location.pathname.replace(/^\/+/, '');
      const validTabs = ['pos', 'inventory', 'reports', 'shifts', 'customers', 'delivery', 'online_orders', 'eta', 'settings', 'profile', 'subscription', 'purchases', 'discounts'];
      if (validTabs.includes(path)) {
        setActiveTab(path);
      }
    };
    window.addEventListener('popstate', handlePathChange);
    return () => {
      window.removeEventListener('popstate', handlePathChange);
    };
  }, []);

  // Sync activeTab state changes to the URL pathname
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const isShop = urlParams.has('shop') || pathParts[0] === 'shop';
    if (isShop) return; // DO NOT sync if we are in the customer storefront!

    const currentPath = window.location.pathname.replace(/^\/+/, '');
    if (activeTab && currentPath !== activeTab && !showLanding) {
      window.history.pushState(null, '', `/${activeTab}`);
    }
  }, [activeTab, showLanding]);

  // Ensure root URL redirects to /pos in history cleanly
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const isShop = urlParams.has('shop') || pathParts[0] === 'shop';
    if (isShop) return; // DO NOT redirect if we are in the customer storefront!

    const currentPath = window.location.pathname.replace(/^\/+/, '');
    if (!currentPath && !showLanding) {
      window.history.replaceState(null, '', '/pos');
    }
  }, [showLanding]);

  // Handle dark/light mode toggling on the body element
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.remove('light-mode');
    } else {
      document.body.classList.add('light-mode');
    }
    localStorage.setItem('drmasr_theme', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // Audio and Toast Helpers
  const playSuccessSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.1); // A5
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.05);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  };

  window.triggerDrMasrToast = (message) => {
    setGlobalToast({ show: true, message });
    playSuccessSound();
    setTimeout(() => setGlobalToast({ show: false, message: '' }), 3500);
  };

  const handleScannedBarcode = (barcodeStr) => {
    try {
      const audio = new Audio('/barcode.mp3');
      audio.play().catch(() => {});
    } catch(e){}

    if (activeTab === 'pos') {
      const found = inventory.find(i => i.barcode === barcodeStr);
      if (found) {
        addItemToCart(found);
        window.triggerDrMasrToast("✅ تم إضافة: " + (found.tradeName) + "");
      } else {
        if (!isAdminVerified) {
          window.triggerDrMasrToast("🔍 هذا الباركود غير مسجل! يرجى مراجعة المسؤول (الأدمن) لإضافته.");
          return;
        }
        window.triggerDrMasrToast("🔍 المنتج غير مسجل. جاري الانتقال لإضافته...");
        setPrefilledBarcode(barcodeStr);
        setActiveTab('inventory');
        setBarcode(barcodeStr);
        setIsModalOpen(true);
      }
    } else if (activeTab === 'inventory') {
      const found = inventory.find(i => i.barcode === barcodeStr);
      if (found) {
        setIncrementProduct(prevProd => {
          if (prevProd && prevProd.id === found.id) {
            return prevProd;
          }
          return found;
        });
        setIncrementCount(prevCount => {
          if (activeIncrementProductIdRef.current === found.id) {
            return prevCount + 1;
          }
          activeIncrementProductIdRef.current = found.id;
          return 1;
        });
      } else {
        if (!isAdminVerified) {
          window.triggerDrMasrToast("❌ هذا الباركود غير مسجل! يرجى الاستعانة بالمسؤول (الأدمن) لإضافة الصنف أولاً.");
          return;
        }
        setPrefilledBarcode(barcodeStr);
        setBarcode(barcodeStr);
        setIsModalOpen(true);
        window.triggerDrMasrToast("📝 صنف جديد، يرجى إدخال البيانات");
      }
    }
  };

  const handleConfirmIncrement = async () => {
    if (!incrementProduct || incrementCount <= 0) return;
    try {
      const nextQty = incrementProduct.quantity + incrementCount;
      await updateDoc(doc(db, 'users', user.uid, 'inventory', incrementProduct.id), {
        quantity: nextQty
      });
      window.triggerDrMasrToast("✅ تم زيادة مخزون " + (incrementProduct.tradeName) + " بمقدار +" + (incrementCount) + " عبوة بنجاح!");
    } catch (err) {
      console.error("Error incrementing quantity:", err);
      window.triggerDrMasrToast('❌ فشل زيادة المخزون.');
    } finally {
      setIncrementProduct(null);
      setIncrementCount(0);
      activeIncrementProductIdRef.current = null;
    }
  };

  const handleCancelIncrement = () => {
    setIncrementProduct(null);
    setIncrementCount(0);
    activeIncrementProductIdRef.current = null;
  };




/* State Block A moved to top */

  useEffect(() => {
    if (isDeliveryOrder) {
      setDeliveryFee(currentUserData?.defaultDeliveryFee !== undefined ? currentUserData.defaultDeliveryFee : 15);
    } else {
      setDeliveryFee(0);
    }
  }, [isDeliveryOrder, currentUserData]);

  useEffect(() => {
    if (customerPhone && customerPhone.trim().length >= 11) {
      const prevSale = sales.find(s => s.customerPhone === customerPhone.trim() && s.customerName);
      if (prevSale && !customerName.trim()) {
        setCustomerName(prevSale.customerName);
      }
      const prevAddrSale = sales.find(s => s.customerPhone === customerPhone.trim() && s.deliveryDetails?.address);
      if (prevAddrSale && !deliveryAddress.trim()) {
        setDeliveryAddress(prevAddrSale.deliveryDetails.address);
      }
    }
  }, [customerPhone, sales]);

/* State Block B moved to top */

  // Lazy load customers on-demand when entering POS, Customers, Delivery, or Online orders tabs to save Firebase quota
  useEffect(() => {
    if (user && (activeTab === 'pos' || activeTab === 'customers' || activeTab === 'delivery' || activeTab === 'online_orders' || activeTab === 'discounts')) {
      const fetchLoyalty = async () => {
        try {
          const [custSnap, campSnap, alertsSnap] = await Promise.all([
            getDocs(collection(db, 'users', user.uid, 'customers')),
            getDocs(collection(db, 'users', user.uid, 'campaigns')),
            getDocs(collection(db, 'users', user.uid, 'refill_alerts'))
          ]);
          setLoyaltyCustomers(custSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          setGeneralCampaigns(campSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          setRefillAlerts(alertsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
          console.error("Error loading loyalty customers / campaigns / alerts on-demand:", err);
        }
      };
      fetchLoyalty();
    }
  }, [user, activeTab]);

 // PWA Automatic Installation Prompt listeners
 useEffect(() => {
 const handleBeforeInstallPrompt = (e) => {
 e.preventDefault();
 setDeferredPrompt(e);
 };
 window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
 return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
 }, []);

 // Global Barcode keydown listener hook (Initialized after state hooks to prevent Temporal Dead Zone)
 useEffect(() => {
   if (!user) return;
   let chars = [];
   let lastCharTime = 0;

   const handleGlobalKeyDown = (e) => {
     // Ignore keydowns if active element is a standard input, unless it types extremely fast like a scanner gun
     const activeEl = document.activeElement;
     const isFocusedInput = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA');

     // Ignore function keys, modifier keys, etc.
     if (e.key.length > 1 && e.key !== 'Enter') return;

     const currentTime = Date.now();
     
     // Barcode scanners type characters extremely fast (typically < 45ms per key)
     if (chars.length > 0 && currentTime - lastCharTime > 60) {
       chars = []; // Reset manual typing
     }

     if (e.key === 'Enter') {
       if (chars.length >= 3) {
         const barcodeStr = chars.join('');
         chars = [];
         e.preventDefault();
         e.stopPropagation();
         handleScannedBarcode(barcodeStr);
       } else {
         chars = [];
       }
     } else {
       if (/[0-9a-zA-Z]/.test(e.key)) {
         // If manually typing inside an input field, do not capture manual characters
         if (isFocusedInput && chars.length === 0 && currentTime - lastCharTime > 60) {
           // Manual typing in focused input, let standard behaviour continue
           return;
         }
         chars.push(e.key);
         lastCharTime = currentTime;
       }
     }
   };

   window.addEventListener('keydown', handleGlobalKeyDown, true);
   return () => window.removeEventListener('keydown', handleGlobalKeyDown, true);
 }, [user, activeTab, inventory, cart, incrementProduct]);

 useEffect(() => {
 if (activeTab === 'pos' && user && !localStorage.getItem('drmasr_install_prompted')) {
 const timer = setTimeout(() => {
 setShowInstallDialog(true);
 }, 2500); // Trigger 2.5 seconds after entering POS
 return () => clearTimeout(timer);
 }
 }, [activeTab, user]);

 // Automatic 24-hour Shortages (Gerd) Report to Telegram
 useEffect(() => {
 if (user && telegramSettings?.enabled && telegramSettings?.botToken && telegramSettings?.chatId && inventory?.length > 0) {
 const checkAndSendShortagesReport = async () => {
 const lastReportTime = localStorage.getItem('drmasr_last_shortages_' + (user.uid));
 const now = Date.now();
 const twentyFourHours = 24 * 60 * 60 * 1000;

 if (!lastReportTime || now - Number(lastReportTime) >= twentyFourHours) {
 const lowStockItems = inventory.filter(item => (item.quantity || 0) <= 5);
 
 if (lowStockItems.length > 0) {
 const pharmacyName = currentUserData?.pharmacyName || 'صيدلية دكتور مصر';
 let msg = `📋 <b>تقرير النواقص والمنقوصات اليومي (الجرد)</b>\n`;
 msg += `🏥 <b>صيدلية:</b> <code>${pharmacyName}</code>\n`;
 msg += `📅 <b>التاريخ:</b> ${new Date().toLocaleDateString('ar-EG')}\n`;
 msg += `━━━━━━━━━━━━━━━━━━━━\n\n`;
 msg += `⚠️ <b>الأصناف المنقوصة وقليلة الكمية (5 وحدات أو أقل):</b>\n\n`;

  lowStockItems.slice(0, 40).forEach((item, idx) => {
    msg += `${idx + 1}️⃣ <b>${item.tradeName}</b>\n`;
    msg += `  └─ الكمية المتبقية: <code>${item.quantity}</code> علبة\n`;
    if (item.category) msg += `  └─ التصنيف: ${item.category}\n`;
    msg += `\n`;
  });

  if (lowStockItems.length > 40) {
    msg += `<i>... وهناك ${lowStockItems.length - 40} أصناف أخرى منقوصة.</i>\n`;
  }

  msg += `━━━━━━━━━━━━━━━━━━━━\n`;
  msg += `<i>تم إرسال هذا التقرير تلقائياً بواسطة نظام دكتور مصر.</i>`;

 try {
 const token = telegramSettings.botToken.trim();
 const chatId = telegramSettings.chatId.trim();
 const url = `https://api.telegram.org/bot${token}/sendMessage`;
 await fetch(url, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 chat_id: chatId,
 text: msg,
 parse_mode: 'HTML'
 })
 });
 localStorage.setItem('drmasr_last_shortages_' + (user.uid), now.toString());
 console.log(" Daily shortages report sent successfully on Telegram.");
 } catch (err) {
 console.error("Error sending daily shortages report to Telegram:", err);
 }
 }
 }
 };

 checkAndSendShortagesReport();
 const interval = setInterval(checkAndSendShortagesReport, 30 * 60 * 1000);
 return () => clearInterval(interval);
 }
 }, [user, telegramSettings, inventory, currentUserData]);

  // ─── Auto Daily Sales Summary Report ───────────────────────────────────────
  useEffect(() => {
    if (user && telegramSettings?.enabled && telegramSettings?.botToken && telegramSettings?.chatId) {
      // Only send if user has opted in (default: true for backwards compat)
      if (telegramSettings?.disableDailySalesReport) return;

      const checkAndSendDailySalesReport = async () => {
        const lastReportKey = 'drmasr_last_daily_sales_' + user.uid;
        const lastReportTime = localStorage.getItem(lastReportKey);
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;

        if (lastReportTime && now - Number(lastReportTime) < twentyFourHours) return;

        try {
          // Get today's and yesterday's sales
          const today = new Date();
          const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
          const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);

          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const salesCol = collection(db, 'users', user.uid, 'sales');

          // Today sales
          const qToday = query(salesCol,
            where('timestamp', '>=', todayStart.toISOString()),
            where('timestamp', '<', today.toISOString())
          );
          const todaySnap = await getDocs(qToday);
          const todaySales = todaySnap.docs.map(d => d.data());

          if (todaySales.length === 0) return; // No sales today, skip

          // Yesterday sales for comparison
          const qYest = query(salesCol,
            where('timestamp', '>=', yesterdayStart.toISOString()),
            where('timestamp', '<', todayStart.toISOString())
          );
          const yestSnap = await getDocs(qYest);
          const yestSales = yestSnap.docs.map(d => d.data());

          const calcSalesStats = (salesArr) => {
            let cashTotal = 0, creditTotal = 0, insuranceTotal = 0, invoiceCount = 0;
            const itemCounts = {};
            salesArr.forEach(sale => {
              if (sale.paymentMethod === 'split') {
                cashTotal += sale.splitAmounts?.cash || 0;
                creditTotal += sale.splitAmounts?.card || 0;
              } else {
                const amt = sale.isInsurance
                  ? (sale.patientShare !== undefined ? sale.patientShare : sale.grandTotal)
                  : (sale.grandTotal || 0);
                if (sale.paymentMethod === 'credit' || sale.paymentMethod === 'visa' || sale.paymentMethod === 'network' || sale.paymentMethod === 'instapay' || sale.paymentMethod === 'vodafone') creditTotal += amt;
                else if (sale.isInsurance) insuranceTotal += amt;
                else cashTotal += amt;
              }
              invoiceCount++;
              if (sale.items) {
                sale.items.forEach(item => {
                  itemCounts[item.tradeName] = (itemCounts[item.tradeName] || 0) + item.quantity;
                });
              }
            });
            const grandTotal = cashTotal + creditTotal + insuranceTotal;
            const topItems = Object.entries(itemCounts).sort((a,b) => b[1]-a[1]).slice(0, 5);
            return { cashTotal, creditTotal, insuranceTotal, grandTotal, invoiceCount, topItems };
          };

          const todayStats = calcSalesStats(todaySales);
          const yestStats = calcSalesStats(yestSales);

          const growthPct = yestStats.grandTotal > 0
            ? Math.round(((todayStats.grandTotal - yestStats.grandTotal) / yestStats.grandTotal) * 100)
            : null;
          const growthText = growthPct === null ? '—' : growthPct >= 0 ? `📈 +${growthPct}%` : `📉 ${growthPct}%`;

          const pharmacyName = currentUserData?.pharmacyName || 'صيدلية دكتور مصر';
          const shortId = currentUserData?.shortId || '';
          const reportDate = today.toLocaleDateString('ar-EG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

          let topItemsText = '';
          if (todayStats.topItems.length > 0) {
            todayStats.topItems.forEach(([name, qty], i) => {
              topItemsText += `  ${['🥇','🥈','🥉','4️⃣','5️⃣'][i]} <b>${name}</b> — ${qty} وحدة\n`;
            });
          }

          // Query customers with outstanding debts
          let debtorListText = '';
          try {
            const custSnap = await getDocs(collection(db, 'users', user.uid, 'customers'));
            const debtors = custSnap.docs
              .map(d => ({ name: d.data().name || 'عميل كاش باك', phone: d.data().phone || '', debt: Number(d.data().debt || 0) }))
              .filter(c => c.debt > 0)
              .sort((a, b) => b.debt - a.debt);

            if (debtors.length > 0) {
              const totalDebtsSum = debtors.reduce((acc, c) => acc + c.debt, 0);
              debtorListText += `⚠️ <b>العملاء المتأخرين والمطلوب سداد مديونياتهم (${debtors.length}):</b>\n`;
              debtors.slice(0, 10).forEach(d => {
                debtorListText += `  • 👤 <b>${d.name}</b> (${d.phone}) — <b>${d.debt.toFixed(2)} ج.م</b>\n`;
              });
              if (debtors.length > 10) {
                debtorListText += `  • <i>وآخرين (إجمالي ${debtors.length} عميل بالديون)...</i>\n`;
              }
              debtorListText += `  💵 <b>إجمالي الديون المستحقة طرف العملاء: ${totalDebtsSum.toFixed(2)} ج.م</b>\n\n`;
            }
          } catch (debtErr) {
            console.error("Failed to fetch debtors for daily telegram message:", debtErr);
          }

          let msg = `📊 <b>التقرير اليومي للمبيعات</b>\n` +
            `🏥 <b>${pharmacyName}</b>${shortId ? ` (${shortId})` : ''}\n` +
            `📅 <code>${reportDate}</code>\n` +
            `━━━━━━━━━━━━━━━━━━━━\n\n` +
            `💰 <b>ملخص اليوم:</b>\n` +
            `  ├─ فواتير محصّلة: <b>${todayStats.invoiceCount} فاتورة</b>\n` +
            `  ├─ مبيعات نقدي (كاش): <b>${todayStats.cashTotal.toFixed(2)} ج.م</b>\n` +
            `  ├─ مبيعات شبكة / فيزا: <b>${todayStats.creditTotal.toFixed(2)} ج.م</b>\n`;

          if (todayStats.insuranceTotal > 0) {
            msg += `  ├─ تأمين ونقابات: <b>${todayStats.insuranceTotal.toFixed(2)} ج.م</b>\n`;
          }

          msg += `  └─ <b>إجمالي المبيعات: ${todayStats.grandTotal.toFixed(2)} ج.م</b>\n\n`;

          msg += `📈 <b>مقارنة بالأمس:</b>\n` +
            `  ├─ مبيعات أمس: ${yestStats.grandTotal.toFixed(2)} ج.م\n` +
            `  └─ نسبة التغير: <b>${growthText}</b>\n\n`;

          if (topItemsText) {
            msg += `🏆 <b>أكثر 5 أصناف مبيعاً اليوم:</b>\n` + topItemsText + '\n';
          }

          if (debtorListText) {
            msg += debtorListText;
          }

          msg += `━━━━━━━━━━━━━━━━━━━━\n` +
            `🕊️ <i>تم الإرسال تلقائياً بواسطة نظام دكتور مصر</i>`;

          await sendTelegramMessage(telegramSettings, msg);
          localStorage.setItem(lastReportKey, now.toString());
        } catch (err) {
          console.error('Daily sales Telegram report error:', err);
        }
      };

      // Send at startup check then every hour
      checkAndSendDailySalesReport();
      const interval = setInterval(checkAndSendDailySalesReport, 60 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user, telegramSettings, currentUserData, sales]);

  // ─── Auto Weekly Performance Report ────────────────────────────────────────
  useEffect(() => {
    if (user && telegramSettings?.enabled && telegramSettings?.botToken && telegramSettings?.chatId) {
      if (telegramSettings?.disableWeeklyReport) return;

      const checkAndSendWeeklyReport = async () => {
        const lastReportKey = 'drmasr_last_weekly_report_' + user.uid;
        const lastReportTime = localStorage.getItem(lastReportKey);
        const now = Date.now();
        const sevenDays = 7 * 24 * 60 * 60 * 1000;

        if (lastReportTime && now - Number(lastReportTime) < sevenDays) return;

        try {
          const today = new Date();
          const weekStart = new Date(today.getTime() - sevenDays);

          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const salesCol = collection(db, 'users', user.uid, 'sales');
          const qWeek = query(salesCol,
            where('timestamp', '>=', weekStart.toISOString()),
            where('timestamp', '<', today.toISOString())
          );
          const weekSnap = await getDocs(qWeek);
          const weekSales = weekSnap.docs.map(d => d.data());

          if (weekSales.length === 0) return;

          let totalRevenue = 0, cashTotal = 0, creditTotal = 0, invoiceCount = 0;
          const dailyMap = {};
          const itemCounts = {};

          weekSales.forEach(sale => {
            const amt = sale.isInsurance
              ? (sale.patientShare !== undefined ? sale.patientShare : sale.grandTotal)
              : (sale.grandTotal || 0);
            if (sale.paymentMethod === 'split') {
              const cashVal = sale.splitAmounts?.cash || 0;
              const cardVal = sale.splitAmounts?.card || 0;
              totalRevenue += cashVal + cardVal;
              cashTotal += cashVal;
              creditTotal += cardVal;
            } else {
              totalRevenue += amt;
              if (sale.paymentMethod === 'credit' || sale.paymentMethod === 'visa' || sale.paymentMethod === 'network' || sale.paymentMethod === 'instapay' || sale.paymentMethod === 'vodafone') creditTotal += amt;
              else cashTotal += amt;
            }
            invoiceCount++;

            // Daily breakdown
            const day = sale.timestamp ? new Date(sale.timestamp).toLocaleDateString('ar-EG', { weekday: 'short', month: 'numeric', day: 'numeric' }) : '—';
            dailyMap[day] = (dailyMap[day] || 0) + amt;

            if (sale.items) {
              sale.items.forEach(item => {
                itemCounts[item.tradeName] = (itemCounts[item.tradeName] || 0) + item.quantity;
              });
            }
          });

          const avgDaily = totalRevenue / 7;
          const topItems = Object.entries(itemCounts).sort((a,b) => b[1]-a[1]).slice(0, 5);

          const pharmacyName = currentUserData?.pharmacyName || 'صيدلية دكتور مصر';
          const shortId = currentUserData?.shortId || '';

          let dailyBreakdown = '';
          Object.entries(dailyMap).forEach(([day, amt]) => {
            dailyBreakdown += `  • ${day}: <b>${amt.toFixed(2)} ج.م</b>\n`;
          });

          let topItemsText = '';
          topItems.forEach(([name, qty], i) => {
            topItemsText += `  ${['🥇','🥈','🥉','4️⃣','5️⃣'][i]} <b>${name}</b> — ${qty} وحدة\n`;
          });

          let msg = `📊 <b>التقرير الأسبوعي الشامل للأداء</b>\n` +
            `🏥 <b>${pharmacyName}</b>${shortId ? ` (${shortId})` : ''}\n` +
            `📅 <code>${weekStart.toLocaleDateString('ar-EG')} → ${today.toLocaleDateString('ar-EG')}</code>\n` +
            `━━━━━━━━━━━━━━━━━━━━\n\n` +
            `💰 <b>الأرقام الأسبوعية:</b>\n` +
            `  ├─ إجمالي الفواتير: <b>${invoiceCount} فاتورة</b>\n` +
            `  ├─ مبيعات كاش: <b>${cashTotal.toFixed(2)} ج.م</b>\n` +
            `  ├─ مبيعات شبكة: <b>${creditTotal.toFixed(2)} ج.م</b>\n` +
            `  ├─ <b>إجمالي الأسبوع: ${totalRevenue.toFixed(2)} ج.م</b>\n` +
            `  └─ معدل مبيعات يومي: <b>${avgDaily.toFixed(2)} ج.م/يوم</b>\n\n`;

          if (dailyBreakdown) {
            msg += `📅 <b>توزيع المبيعات اليومية:</b>\n` + dailyBreakdown + '\n';
          }

          if (topItemsText) {
            msg += `🏆 <b>أكثر 5 أصناف مبيعاً هذا الأسبوع:</b>\n` + topItemsText + '\n';
          }

          msg += `━━━━━━━━━━━━━━━━━━━━\n` +
            `🕊️ <i>التقرير الأسبوعي — نظام دكتور مصر</i>`;

          await sendTelegramMessage(telegramSettings, msg);
          localStorage.setItem(lastReportKey, now.toString());
        } catch (err) {
          console.error('Weekly Telegram report error:', err);
        }
      };

      checkAndSendWeeklyReport();
      const interval = setInterval(checkAndSendWeeklyReport, 6 * 60 * 60 * 1000); // Check every 6 hours
      return () => clearInterval(interval);
    }
  }, [user, telegramSettings, currentUserData]);



  const [setupPin, setSetupPin] = useState('');
  const [setupPharmacyName, setSetupPharmacyName] = useState('');
  const [setupPhone, setSetupPhone] = useState('');
  const [setupOwnerName, setSetupOwnerName] = useState('');
  const [setupPromoCode, setSetupPromoCode] = useState('');
  const [setupError, setSetupError] = useState('');

  // Automatically pre-fill setup fields from existing database values when they become available
  useEffect(() => {
    if (currentUserData) {
      if (currentUserData.pharmacyName && !setupPharmacyName) setSetupPharmacyName(currentUserData.pharmacyName);
      if (currentUserData.phone && !setupPhone) setSetupPhone(currentUserData.phone);
      if (currentUserData.ownerName && !setupOwnerName) setSetupOwnerName(currentUserData.ownerName);
    }
  }, [currentUserData]);

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    if (setupPin.length !== 4) {
      setSetupError('يجب أن يتكون رمز الـ PIN من 4 أرقام بالضبط!');
      return;
    }
    if (!setupOwnerName.trim()) {
      setSetupError('يرجى إدخال اسم صاحب الصيدلية!');
      return;
    }
    if (!setupPharmacyName.trim()) {
      setSetupError('يرجى إدخال اسم الصيدلية!');
      return;
    }
    if (!setupPhone.trim()) {
      setSetupError('يرجى إدخال رقم هاتف الصيدلية!');
      return;
    }

    try {
      let customExpiry = null;
      let appliedPromo = '';
      if (setupPromoCode && setupPromoCode.trim()) {
        const cleanPromo = setupPromoCode.trim();
        const upperPromo = cleanPromo.toUpperCase();
        const lowerPromo = cleanPromo.toLowerCase();
        const candidateCodes = Array.from(new Set([cleanPromo, upperPromo, lowerPromo]));
        
        let promoDoc = null;
        let promoData = null;
        
        for (const code of candidateCodes) {
          let snap = await getDoc(doc(db, 'promocodes', code));
          if (!snap.exists()) {
            snap = await getDoc(doc(db, 'promoCodes', code));
          }
          if (snap.exists()) {
            promoDoc = snap;
            promoData = snap.data();
            break;
          }
        }
        
        if (!promoDoc) {
          const q1 = query(collection(db, 'promocodes'), where('code', 'in', candidateCodes));
          const snap1 = await getDocs(q1);
          if (!snap1.empty) {
            promoDoc = snap1.docs[0];
            promoData = promoDoc.data();
          } else {
            const q2 = query(collection(db, 'promoCodes'), where('code', 'in', candidateCodes));
            const snap2 = await getDocs(q2);
            if (!snap2.empty) {
              promoDoc = snap2.docs[0];
              promoData = promoDoc.data();
            }
          }
        }

        if (promoDoc && promoData) {
          if (promoData.isActive !== false) {
            appliedPromo = promoData.code || promoDoc.id;
            const days = parseInt(promoData.daysBenefit) || 30;
            customExpiry = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
          } else {
            setSetupError('⚠️ عذراً، هذا البروموكود غير نشط حالياً.');
            return;
          }
        } else {
          setSetupError('⚠️ البروموكود المدخل غير صحيح!');
          return;
        }
      }

      const updatedData = {
        ...currentUserData,
        ownerName: setupOwnerName.trim(),
        adminPin: setupPin,
        pharmacyName: setupPharmacyName.trim(),
        phone: setupPhone.trim(),
        promoCode: appliedPromo || currentUserData?.promoCode || '',
        usedPromos: appliedPromo ? [...(currentUserData?.usedPromos || []), appliedPromo] : (currentUserData?.usedPromos || []),
        ...(customExpiry ? { expiryDate: customExpiry } : {})
      };

      await setDoc(doc(db, 'users', user.uid), prepareDataForFirestore(updatedData, user));
      setCurrentUserData(updatedData);
      localStorage.setItem('drmasr_userdata', JSON.stringify(updatedData));
      setSetupError('');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('تم إعداد الحساب بنجاح! أهلاً بك في دكتور مصر 🎉');
      }
    } catch (err) {
      console.error("Error saving setup data:", err);
      setSetupError('فشل حفظ البيانات في قاعدة البيانات السحابية.');
    }
  };

  const handleCompleteGoogleSetup = async (e) => {
    e.preventDefault();
    if (setupPin.length !== 4) {
      setSetupError('يجب أن يتكون رمز الـ PIN من 4 أرقام بالضبط!');
      return;
    }

    const finalPharmacyName = setupPharmacyName.trim() || currentUserData?.pharmacyName || '';
    const finalPhone = setupPhone.trim() || currentUserData?.phone || '';
    const finalOwnerName = setupOwnerName.trim() || currentUserData?.ownerName || user?.displayName || '';

    if (!finalPharmacyName) {
      setSetupError('يرجى إدخال اسم الصيدلية!');
      return;
    }
    if (!finalPhone) {
      setSetupError('يرجى إدخال رقم الهاتف!');
      return;
    }

    try {
      let customExpiry = null;
      let appliedPromo = '';
      if (setupPromoCode && setupPromoCode.trim()) {
        const cleanPromo = setupPromoCode.trim();
        const upperPromo = cleanPromo.toUpperCase();
        const lowerPromo = cleanPromo.toLowerCase();
        const candidateCodes = Array.from(new Set([cleanPromo, upperPromo, lowerPromo]));
        
        let promoDoc = null;
        let promoData = null;
        
        for (const code of candidateCodes) {
          let snap = await getDoc(doc(db, 'promocodes', code));
          if (!snap.exists()) {
            snap = await getDoc(doc(db, 'promoCodes', code));
          }
          if (snap.exists()) {
            promoDoc = snap;
            promoData = snap.data();
            break;
          }
        }
        
        if (!promoDoc) {
          const q1 = query(collection(db, 'promocodes'), where('code', 'in', candidateCodes));
          const snap1 = await getDocs(q1);
          if (!snap1.empty) {
            promoDoc = snap1.docs[0];
            promoData = promoDoc.data();
          } else {
            const q2 = query(collection(db, 'promoCodes'), where('code', 'in', candidateCodes));
            const snap2 = await getDocs(q2);
            if (!snap2.empty) {
              promoDoc = snap2.docs[0];
              promoData = promoDoc.data();
            }
          }
        }

        if (promoDoc && promoData) {
          if (promoData.isActive !== false) {
            appliedPromo = promoData.code || promoDoc.id;
            const days = parseInt(promoData.daysBenefit) || 30;
            customExpiry = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
          } else {
            setSetupError('⚠️ عذراً، هذا البروموكود غير نشط حالياً.');
            return;
          }
        } else {
          setSetupError('⚠️ البروموكود المدخل غير صحيح!');
          return;
        }
      }

      const updatedData = {
        ...currentUserData,
        adminPin: setupPin,
        pharmacyName: finalPharmacyName,
        phone: finalPhone,
        ownerName: finalOwnerName,
        promoCode: appliedPromo || currentUserData?.promoCode || '',
        usedPromos: appliedPromo ? [...(currentUserData?.usedPromos || []), appliedPromo] : (currentUserData?.usedPromos || []),
        ...(customExpiry ? { expiryDate: customExpiry } : {})
      };

      await setDoc(doc(db, 'users', user.uid), prepareDataForFirestore(updatedData, user));
      setCurrentUserData(updatedData);
      localStorage.setItem('drmasr_userdata', JSON.stringify(updatedData));
      localStorage.setItem('drmasr_just_logged_in', 'true');
      setSetupError('');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('تم إعداد حسابك بالكامل بنجاح! 🚀');
      }
    } catch (err) {
      console.error("Error updating user setup data:", err);
      setSetupError('فشل حفظ البيانات في قاعدة البيانات.');
    }
  };

 const handleInstallApp = async () => {
 localStorage.setItem('drmasr_install_prompted', 'true');
 setShowInstallDialog(false);
 if (deferredPrompt) {
 deferredPrompt.prompt();
 const { outcome } = await deferredPrompt.userChoice;
 console.log(`PWA Outcome: ${outcome}`);
 setDeferredPrompt(null);
 } else {
  window.triggerDrMasrAlert(' لتثبيت تطبيق دكتور مصر يدوياً، يرجى النقر على أيقونة التثبيت (+ أو 📥) الموجودة بجانب شريط عنوان المتصفح في الأعلى! ');
 }
 };



  const handleOverrideDevices = async () => {
    if (!user) return;
    try {
      const localDeviceId = localStorage.getItem('drmasr_device_id');
      if (!localDeviceId) return;
      const userRef = doc(db, 'users', user.uid);
      const updatedDevices = [localDeviceId];
      await updateDoc(userRef, { activeDevices: updatedDevices });
      
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        const uData = snap.data();
        const parsedData = { ...uData };
        for (const key in parsedData) {
          const val = parsedData[key];
          if (val && typeof val === 'object' && typeof val.toDate === 'function') {
            parsedData[key] = val.toDate().toISOString();
          }
        }
        setCurrentUserData(parsedData);
        localStorage.setItem('drmasr_userdata', JSON.stringify(parsedData));
      }
      setDeviceLimitExceeded(false);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("✅ تم تفعيل هذا الجهاز وإلغاء ارتباط الأجهزة الأخرى بنجاح!");
      }
    } catch (err) {
      console.error(err);
      window.triggerDrMasrAlert("❌ فشل إلغاء ارتباط الأجهزة الأخرى: " + err.message);
    }
  };

 // Auth and Tab Session Synchronization
 useEffect(() => {
 // 1. Initial local fallback check
 const savedUser = localStorage.getItem('drmasr_user');
 const savedUserData = localStorage.getItem('drmasr_userdata');
 if (savedUser && savedUserData) {
 setUser(JSON.parse(savedUser));
 setCurrentUserData(JSON.parse(savedUserData));
 }

 // 2. Firebase Auth real-time cross-tab state listener
 const unsubscribeAuth = auth.onAuthStateChanged((firebaseUser) => {
 if (firebaseUser) {
 setUser(firebaseUser);
 localStorage.setItem('drmasr_user', JSON.stringify(firebaseUser));
  // Fetch user data from database immediately to restore localStorage
  getDoc(doc(db, 'users', firebaseUser.uid)).then(async (snap) => {
    if (snap.exists()) {
      const uData = snap.data();
      const parsedData = { ...uData };
      const expiryDates = [uData.expiryDate, uData.subscriptionExpiresAt, uData.trialExpiresAt]
        .map(d => d ? (typeof d.toDate === 'function' ? d.toDate() : new Date(d)) : null)
        .filter(d => d && !isNaN(d.getTime()));
      if (expiryDates.length > 0) {
        const maxExpiry = new Date(Math.max(...expiryDates));
        parsedData.expiryDate = maxExpiry.toISOString();
      }
      for (const key in parsedData) {
        const val = parsedData[key];
        if (val && typeof val === 'object' && typeof val.toDate === 'function') {
          parsedData[key] = val.toDate().toISOString();
        }
      }
      if (parsedData.plan && !parsedData.subscriptionStatus) {
        parsedData.subscriptionStatus = parsedData.plan;
      }
      if (parsedData.expiryDate && !parsedData.trialEndsAt) {
        parsedData.trialEndsAt = parsedData.expiryDate;
      }

      // ── DEVICE LIMIT VALIDATION (Deterministic Hardware/Browser Fingerprint) ──
      let localDeviceId = localStorage.getItem('drmasr_device_id');
      if (!localDeviceId) {
        localDeviceId = generateDeviceFingerprint() + '-' + Math.random().toString(36).substring(2, 8);
        localStorage.setItem('drmasr_device_id', localDeviceId);
      }

      const activeDevices = uData.activeDevices || [];
      const deviceLimit = uData.deviceLimit || 2;
      const userRef = doc(db, 'users', firebaseUser.uid);

      // Heartbeat pulse is handled by the 12-hour interval effect below

      if (!activeDevices.includes(localDeviceId)) {
        const justLoggedIn = localStorage.getItem('drmasr_just_logged_in') === 'true';
        if (activeDevices.length >= deviceLimit) {
          setDeviceLimitExceeded(true);
          setUserProfileLoading(false);
          localStorage.removeItem('drmasr_just_logged_in');
          return;
        } else if (justLoggedIn) {
          const updatedDevices = [...activeDevices, localDeviceId];
          parsedData.activeDevices = updatedDevices;
          await updateDoc(userRef, { activeDevices: updatedDevices }).catch(() => {});
          localStorage.removeItem('drmasr_just_logged_in');
        } else {
          signOut(auth).catch(() => {});
          setUser(null);
          setCurrentUserData(null);
          localStorage.removeItem('drmasr_user');
          localStorage.removeItem('drmasr_userdata');
          localStorage.removeItem('drmasr_just_logged_in');
          localStorage.setItem('drmasr_evicted_by_admin', 'true');
          setUserProfileLoading(false);
          return;
        }
      } else {
        localStorage.removeItem('drmasr_just_logged_in');
      }

      setDeviceLimitExceeded(false);
      setCurrentUserData(parsedData);
      localStorage.setItem('drmasr_userdata', JSON.stringify(parsedData));
    } else {
      // If Firestore profile document doesn't exist, initialize default profile data so the user can complete setup.
      const defaultUserData = {
        plan: 'trial',
        expiryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        trialEndsAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        trialExpiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      };
      setCurrentUserData(defaultUserData);
      setUserProfileLoading(false);
      return;
    }
    setUserProfileLoading(false);
  }).catch(err => {
    console.error("Error fetching user on login:", err);
    setErrorMessage(`فشل تحميل بيانات الصيدلية: ${err.message || err}`);
    if (window.triggerDrMasrToast) {
      window.triggerDrMasrToast("⚠️ فشل تحديث البيانات من السيرفر.");
    }
    setUserProfileLoading(false);
  });
 } else {
 setUser(null);
 setCurrentUserData(null);
 localStorage.removeItem('drmasr_user');
 localStorage.removeItem('drmasr_userdata');
 setUserProfileLoading(false);
 }
 });

 // 3. Storage event listener to sync session state instantly across tabs
 const handleStorageChange = (e) => {
 if (e.key === 'drmasr_user') {
 const nextUser = e.newValue ? JSON.parse(e.newValue) : null;
 setUser(nextUser);
 if (!nextUser) {
 setCurrentUserData(null);
 }
 }
 if (e.key === 'drmasr_userdata') {
 setCurrentUserData(e.newValue ? JSON.parse(e.newValue) : null);
 }
 };
 window.addEventListener('storage', handleStorageChange);

 return () => {
 unsubscribeAuth();
 window.removeEventListener('storage', handleStorageChange);
 };
 }, []);

  // Auto-generate pharmacy slug if missing
  useEffect(() => {
    if (user && currentUserData && !currentUserData.pharmacySlug) {
      let needsUpdate = false;
      let updates = {};
      
      if (!currentUserData.pharmacySlug) {
        let nameToSlug = currentUserData.pharmacyName || currentUserData.displayName || 'pharmacy';
        let slug = cleanName(nameToSlug)
          .replace(/[^\w\s-]/g, '')
          .trim()
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-'); // Deduplicate dashes
          
        if (!slug || slug === '-') {
          slug = 'pharmacy-' + Math.floor(1000 + Math.random() * 9000);
        }
        
        // Append shortId to ensure absolute uniqueness globally
        const sId = currentUserData.shortId || Math.floor(100000 + Math.random() * 900000).toString();
        slug = `${slug}-${sId}`;
        
        updates.pharmacySlug = slug;
        needsUpdate = true;
      }

      if (needsUpdate) {
        const updatedData = { ...currentUserData, ...updates };
        setCurrentUserData(updatedData);
        localStorage.setItem('drmasr_userdata', JSON.stringify(updatedData));
        updateDoc(doc(db, 'users', user.uid), updates).catch((e) => {
          console.error("Failed to update profile auto-generation in Firestore:", e);
        });
      }
    }
  }, [user, currentUserData]);

  // 12-Hour Pulse Heartbeat to update lastActive (with localStorage debounce to prevent writes on reload)
  useEffect(() => {
    if (!user) return;
    const sendPulse = async () => {
      try {
        const lastPulseStr = localStorage.getItem('drmasr_last_pulse_' + user.uid);
        const now = Date.now();
        
        // If last pulse was less than 12 hours (43200000 ms) ago, skip writing to Firestore
        if (lastPulseStr && (now - parseInt(lastPulseStr, 10) < 43200000)) {
          console.log("DRMasr Heartbeat pulse skipped (recently sent).");
          return;
        }

        const { doc, updateDoc } = await import('firebase/firestore');
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { lastActive: new Date().toISOString() });
        localStorage.setItem('drmasr_last_pulse_' + user.uid, now.toString());
        console.log("DRMasr Heartbeat pulse sent!");
      } catch (e) {
        console.warn("Heartbeat pulse failed:", e);
      }
    };
    sendPulse(); // Initial pulse on tab open
    const interval = setInterval(sendPulse, 43200000); // Pulse every 12 hours
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = () => {
    signOut(auth)
      .catch((err) => console.error("Firebase signOut error:", err))
      .finally(() => {
        setUser(null);
        setCurrentUserData(null);
        localStorage.removeItem('drmasr_user');
        localStorage.removeItem('drmasr_userdata');
        window.location.reload();
      });
  };

  const handleLanguageChange = (newLang) => {
    localStorage.setItem('drmasr_language', newLang);
    window.location.reload();
  };

  // Keep page layout RTL for both English and Arabic
  useEffect(() => {
    document.documentElement.dir = 'rtl';
    document.body.style.direction = 'rtl';
    document.body.style.textAlign = 'right';
  }, []);

 // History State Routing to handle the browser's Back and Forward buttons without logging out
 useEffect(() => {
 if (user) {
 const currentState = window.history.state;
 if (!currentState || currentState.tab !== activeTab) {
 window.history.pushState({ tab: activeTab }, '', '');
 }
 }
 }, [activeTab, user]);

 useEffect(() => {
 const handlePopState = (event) => {
 if (user && event.state && event.state.tab) {
 setActiveTab(event.state.tab);
 }
 };
 window.addEventListener('popstate', handlePopState);
 return () => window.removeEventListener('popstate', handlePopState);
  }, [user]);

 // Real-time Database Listeners (Syncing across all devices and tabs in real-time)
  useEffect(() => {
    if (!user) return;
    setIsInventoryLoading(true);
    setLoading(true);

 // 1. Sync User Profile & Subscription Data
  const unsubUser = onSnapshot(doc(db, 'users', user.uid), (snap) => {
     if (snap.exists()) {
       const uData = snap.data();
       
       // Real-time device eviction check
       const localDeviceId = localStorage.getItem('drmasr_device_id');
       const activeDevices = uData.activeDevices || [];
       const justLoggedIn = localStorage.getItem('drmasr_just_logged_in') === 'true';
        if (!justLoggedIn && localDeviceId && !activeDevices.includes(localDeviceId)) {
          signOut(auth).catch(() => {});
          setUser(null);
          setCurrentUserData(null);
          localStorage.removeItem('drmasr_user');
          localStorage.removeItem('drmasr_userdata');
          localStorage.setItem('drmasr_evicted_by_admin', 'true');
          return;
        }

       const parsedData = { ...uData };
      for (const key in parsedData) {
        const val = parsedData[key];
        if (val && typeof val === 'object' && typeof val.toDate === 'function') {
          parsedData[key] = val.toDate().toISOString();
        }
      }
      if (parsedData.plan && !parsedData.subscriptionStatus) {
        parsedData.subscriptionStatus = parsedData.plan;
      }
      if (parsedData.expiryDate && !parsedData.trialEndsAt) {
        parsedData.trialEndsAt = parsedData.expiryDate;
      }
      setCurrentUserData(parsedData);
      localStorage.setItem('drmasr_userdata', JSON.stringify(parsedData));
    } else {
      // If Firestore profile document doesn't exist, initialize default profile data so the user can complete setup.
      const defaultUserData = {
        plan: 'trial',
        expiryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        trialEndsAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        trialExpiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      };
      setCurrentUserData(defaultUserData);
      localStorage.setItem('drmasr_userdata', JSON.stringify(defaultUserData));
      return;
    }
    setUserProfileLoading(false);
  }, (err) => {
    console.error("unsubUser listener error:", err);
    setUserProfileLoading(false);
  });

 // 2. Sync Active Shift
 const unsubActiveShift = onSnapshot(collection(db, 'users', user.uid, 'active_shift'), (snap) => {
 const fetchedShifts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
 setActiveShifts(fetchedShifts);
 localStorage.setItem('drmasr_activeShifts_' + (user.uid), JSON.stringify(fetchedShifts));

 const localId = localStorage.getItem('drmasr_active_shift_id_' + (user.uid));
 const currentLocal = fetchedShifts.find(s => s.id === localId) || null;
 setActiveShift(currentLocal);
 if (currentLocal) {
 localStorage.setItem('drmasr_activeShift_' + (user.uid), JSON.stringify(currentLocal));
 } else {
 localStorage.removeItem('drmasr_activeShift_' + (user.uid));
 }
 });

 // 4. Sync Employees
 const unsubEmployees = onSnapshot(collection(db, 'users', user.uid, 'employees'), (snap) => {
 const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
 setEmployees(fetched);
 localStorage.setItem('drmasr_employees_' + (user.uid), JSON.stringify(fetched));
 });

 // 5. Sync Telegram Settings
  const unsubTg = onSnapshot(collection(db, 'users', user.uid, 'telegram_settings'), (snap) => {
    if (!snap.empty) {
      const fetched = snap.docs[0].data();
      const nextSettings = {
        enabled: fetched.enabled || false,
        chatId: fetched.chatId || '',
        botToken: fetched.botToken || '8660558967:AAE-aht04AtybmrD3r_afPEy1WddKW0Fz4M'
      };
      setTelegramSettings(nextSettings);
      localStorage.setItem('drmasr_telegramSettings_' + (user.uid), JSON.stringify(nextSettings));

      // Auto-sync telegram settings to the main pharmacy document for direct guest storefront notifications
      updateDoc(doc(db, 'users', user.uid), { telegramSettings: nextSettings }).catch(e => {
        console.error("Failed to auto-sync Telegram settings to profile document:", e);
      });
    }
  }, (err) => {
    console.error("unsubTg listener error:", err);
  });
  const unsubPayment = onSnapshot(collection(db, 'users', user.uid, 'payment_settings'), (snap) => {
    if (!snap.empty) {
      const fetched = snap.docs[0].data();
      const ps = { instaPayIPN: fetched.instaPayIPN || '', vodafoneCashNumber: fetched.vodafoneCashNumber || '' };
      setPaymentSettings(ps);
      localStorage.setItem('drmasr_paymentSettings_' + user.uid, JSON.stringify(ps));
    }
  }, (err) => { console.error('unsubPayment error:', err); });

  // 6. Sync Online Orders
  const unsubOrders = onSnapshot(collection(db, 'users', user.uid, 'online_orders'), (snap) => {
    const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    fetched.sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
    setOnlineOrders(fetched);
  }, (err) => {
    console.error("unsubOrders listener error:", err);
  });

  // 7. Sync Inventory
  const isMobileDevice = window.innerWidth < 1024;
  let unsubInv = () => {};

  if (!isMobileDevice) {
    unsubInv = onSnapshot(collection(db, 'users', user.uid, 'inventory'), (snap) => {
      const fetched = snap.docs.map(doc => {
        const data = doc.data();
        let parsedExpiry = data.expiryDate;
        if (parsedExpiry && typeof parsedExpiry === 'object') {
          const { year, month, day } = parsedExpiry;
          if (year && month && day) {
            parsedExpiry = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          }
        }
        return { id: doc.id, ...data, expiryDate: parsedExpiry };
      });
      if (fetched.length > 0) {
        setInventory(fetched);
        localStorage.setItem('drmasr_inv_' + user.uid, JSON.stringify(fetched));
      } else {
        loadLocalInventoryFallback();
      }
      setIsInventoryLoading(false);
      setLoading(false);
    }, (err) => {
      console.error("unsubInv listener error:", err);
      setIsInventoryLoading(false);
      setLoading(false);
    });
  } else {
    // If mobile, bypass live sync, remove cached copy, and mark inventory loading complete
    try {
      localStorage.removeItem('drmasr_inv_' + user.uid);
    } catch (e) {}
    setTimeout(() => {
      setIsInventoryLoading(false);
      setLoading(false);
    }, 0);
  }

  let globalList = [];
      let rootNotifs = [];
      let localNotifs = [];
      let localAnnounces = [];

      const updateCombinedAnnouncements = () => {
        const combined = [...globalList, ...rootNotifs, ...localNotifs, ...localAnnounces];
        combined.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setAnnouncements(combined);

        const readIds = JSON.parse(localStorage.getItem('drmasr_read_announcements_' + user.uid) || '[]');
        const unread = combined.filter(item => !readIds.includes(item.id)).length;
        setUnreadAnnouncementsCount(unread);
      };

      const unsubAnnouncements = onSnapshot(collection(db, 'global_announcements'), (snap) => {
        globalList = snap.docs.map(doc => ({ id: doc.id, type: 'global', ...doc.data() }));
        updateCombinedAnnouncements();
      }, (err) => console.warn("global_announcements listener error:", err));

      const unsubRootNotifs = onSnapshot(collection(db, 'notifications'), (snap) => {
        rootNotifs = snap.docs
          .map(doc => ({ id: doc.id, type: 'notification', ...doc.data() }))
          .filter(n => n.target === 'all' || n.target === user.uid);
        updateCombinedAnnouncements();
      }, (err) => console.warn("root notifications listener error:", err));

      const unsubLocalNotifs = onSnapshot(collection(db, 'users', user.uid, 'notifications'), (snap) => {
        localNotifs = snap.docs.map(doc => ({ id: doc.id, type: 'notification', ...doc.data() }));
        updateCombinedAnnouncements();
      }, (err) => console.warn("local notifications listener error:", err));

      const unsubLocalAnnounces = onSnapshot(collection(db, 'users', user.uid, 'announcements'), (snap) => {
        localAnnounces = snap.docs.map(doc => ({ id: doc.id, type: 'announcement', ...doc.data() }));
        updateCombinedAnnouncements();
      }, (err) => console.warn("local announcements listener error:", err));

      const unsubInsurance = onSnapshot(collection(db, 'users', user.uid, 'insuranceContracts'), (snap) => {
        const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setInsuranceContracts(fetched);
      }, (err) => console.warn("Sync insurance contracts error:", err));

      return () => {
        unsubUser();
        unsubActiveShift();
        unsubEmployees();
        unsubTg();
        unsubOrders();
        unsubInv();
        unsubAnnouncements();
        unsubRootNotifs();
        unsubLocalNotifs();
        unsubLocalAnnounces();
        unsubInsurance();
      };
  }, [user]);

  // On-demand fetching for mobile devices to save memory/data
  const fetchInventoryOnDemand = async () => {
    if (!user?.uid) return;
    setIsInventoryLoading(true);
    try {
      const invRef = collection(db, 'users', user.uid, 'inventory');
      const snap = await getDocs(invRef);
      const fetched = snap.docs.map(doc => {
        const data = doc.data();
        let parsedExpiry = data.expiryDate;
        if (parsedExpiry && typeof parsedExpiry === 'object') {
          const { year, month, day } = parsedExpiry;
          if (year && month && day) {
            parsedExpiry = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          }
        }
        return { id: doc.id, ...data, expiryDate: parsedExpiry };
      });
      setInventory(fetched);
    } catch (err) {
      console.error("Error fetching inventory on-demand:", err);
    } finally {
      setIsInventoryLoading(false);
    }
  };

  useEffect(() => {
    const isMobileDevice = window.innerWidth < 1024;
    if (!isMobileDevice || !user?.uid) return;

    // Load inventory on-demand when user visits 'pos' or 'inventory' tabs, if not loaded yet
    if (activeTab === 'pos' || activeTab === 'inventory') {
      if (inventory.length === 0) {
        fetchInventoryOnDemand();
      }
    }
  }, [activeTab, user?.uid, inventory.length]);

  const notificationCount = onlineOrders.length;

  const prevOnlineOrdersCount = useRef(-1);

  useEffect(() => {
    if (!user) return;
    if (prevOnlineOrdersCount.current === -1) {
      prevOnlineOrdersCount.current = onlineOrders.length;
      return;
    }
    if (onlineOrders.length > prevOnlineOrdersCount.current) {
      const newCount = onlineOrders.length - prevOnlineOrdersCount.current;
      const newOrders = onlineOrders.slice(0, newCount);
      newOrders.forEach(order => {
        // Play barcode sound
        try {
          const audio = new Audio('/barcode.mp3');
          audio.play().catch(err => console.warn("Audio autoplay blocked:", err));
        } catch (e) {
          console.error("Audio playback error:", e);
        }

        // Web Audio Chime Sound
        try {
          const AudioContextClass = window.AudioContext || window.webkitAudioContext;
          if (AudioContextClass) {
            const ctx = new AudioContextClass();
            if (ctx.state === 'suspended') {
              ctx.resume();
            }
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.connect(gain1);
            gain1.connect(ctx.destination);
            osc1.type = 'sine';
            osc1.frequency.setValueAtTime(659.25, ctx.currentTime);
            gain1.gain.setValueAtTime(0.15, ctx.currentTime);
            gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
            osc1.start(ctx.currentTime);
            osc1.stop(ctx.currentTime + 0.6);

            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(880.00, ctx.currentTime + 0.15);
            gain2.gain.setValueAtTime(0.15, ctx.currentTime + 0.15);
            gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.85);
            osc2.start(ctx.currentTime + 0.15);
            osc2.stop(ctx.currentTime + 0.85);
          }
        } catch (e) {
          console.warn("Failed to play chime:", e);
        }

        if (window.triggerDrMasrToast) {
          window.triggerDrMasrToast("🔔 طلب أونلاين جديد من العميل: " + (order.customerName || 'غير محدد') + "");
        }
      });
    }
    prevOnlineOrdersCount.current = onlineOrders.length;
  }, [onlineOrders, user]);

  // Self-Healing Logo Sync: Auto-heal/sync logo between local browser and Firestore cloud profile
  useEffect(() => {
    if (user && currentUserData) {
      const localLogo = localStorage.getItem('drmasr_receipt_logo');
      if (!currentUserData.pharmacyLogo && localLogo) {
        // Sync local to Firestore
        const updatedData = {
          ...currentUserData,
          pharmacyLogo: localLogo
        };
        setDoc(doc(db, 'users', user.uid), prepareDataForFirestore(updatedData, user))
          .then(() => {
            setCurrentUserData(updatedData);
            localStorage.setItem('drmasr_userdata', JSON.stringify(updatedData));
            console.log("Successfully auto-healed and synced local logo to Firestore!");
          })
          .catch(err => console.error("Auto-heal logo sync failed:", err));
      } else if (currentUserData.pharmacyLogo && !localLogo) {
        // Sync Firestore to local (new device / cleared cache)
        localStorage.setItem('drmasr_receipt_logo', currentUserData.pharmacyLogo);
        console.log("Successfully synced cloud logo back to local storage!");
      }
    }
  }, [user, currentUserData]);

  // Load Shifts History and Sales from LocalStorage on Startup to keep POS instant
  useEffect(() => {
    if (!user) {
      setShiftsHistory([]);
      setSales([]);
      setIsShiftsHistoryLoaded(false);
      setIsSalesLoaded(false);
      return;
    }
    
    // Load cached Shifts History
    const cachedShifts = localStorage.getItem('drmasr_shiftsHistory_' + (user.uid));
    if (cachedShifts) {
      try {
        setShiftsHistory(JSON.parse(cachedShifts));
      } catch (e) {
        console.warn("Error parsing cached shifts:", e);
      }
    } else {
      setShiftsHistory([]);
    }

    // Load cached Sales
    const cachedSales = localStorage.getItem('drmasr_sales_' + (user.uid));
    if (cachedSales) {
      try {
        setSales(JSON.parse(cachedSales));
      } catch (e) {
        console.warn("Error parsing cached sales:", e);
      }
    } else {
      // initial fallback
      const initSales = [
        { id: 'sale_1', invoiceId: '10000001', timestamp: '2026-05-04T12:00:00Z', items: [{ barcode: '6221111111111', tradeName: 'بانادول إكسترا', quantity: 2, price: 45 }], grandTotal: 90, paymentMethod: 'cash' }
      ];
      setSales(initSales);
      localStorage.setItem('drmasr_sales_' + (user.uid), JSON.stringify(initSales));
    }
  }, [user]);

  // Lazy on-demand Firestore fetchers
  const fetchShiftsHistory = async (force = false) => {
    if (!user) return;
    if (isShiftsHistoryLoaded && !force) return;
    setIsShiftsHistoryLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users', user.uid, 'shifts_history'));
      const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Sort by closedTime or endTime descending
      fetched.sort((a, b) => {
        const timeA = new Date(b.closedTime || b.endTime || b.startTime || 0).getTime();
        const timeB = new Date(a.closedTime || a.endTime || a.startTime || 0).getTime();
        return timeA - timeB;
      });
      
      setShiftsHistory(fetched);
      localStorage.setItem('drmasr_shiftsHistory_' + (user.uid), JSON.stringify(fetched));
      setIsShiftsHistoryLoaded(true);
      if (window.triggerDrMasrToast && force) {
        window.triggerDrMasrToast("تم استدعاء سجل الورديات بنجاح! 📊");
      }
    } catch (err) {
      console.error("Failed to fetch shifts history:", err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ فشل تحديث البيانات من السيرفر.");
      }
    } finally {
      setIsShiftsHistoryLoading(false);
    }
  };

  const fetchSales = async (force = false) => {
    if (!user) return;
    if (isSalesLoaded && !force) return;
    setIsSalesLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users', user.uid, 'sales'));
      const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Sort sales by timestamp descending
      fetched.sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
      
      setSales(fetched);
      localStorage.setItem('drmasr_sales_' + (user.uid), JSON.stringify(fetched));
      setIsSalesLoaded(true);
      if (window.triggerDrMasrToast && force) {
        window.triggerDrMasrToast("تم استدعاء طلبات ومبيعات الدليفري بنجاح! 🛵");
      }
    } catch (err) {
      console.error("Failed to fetch sales:", err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ فشل استدعاء المبيعات من السيرفر.");
      }
    } finally {
      setIsSalesLoading(false);
    }
  };

  // Auto-fetch data once when entering relevant tabs
  useEffect(() => {
    if (!user) return;
    if (activeTab === 'shifts') {
      fetchShiftsHistory();
      fetchSales(); // ShiftsView needs sales for stats
    } else if (activeTab === 'reports') {
      // Do not fetch all sales on tab change to conserve Firestore reads
      // They will be fetched on-demand or for today only
    }
  }, [activeTab, user]);

 // Background Bot Polling to automatically reply to /start with Chat ID
 useEffect(() => {
 if (!user) return;
 let intervalId;
 let lastOffset = parseInt(localStorage.getItem('drmasr_tg_offset_' + (user.uid)) || '0', 10);

 const pollTelegramUpdates = async () => {
 const token = telegramSettings.botToken || '8660558967:AAE-aht04AtybmrD3r_afPEy1WddKW0Fz4M';
 try {
 const res = await fetch(`https://api.telegram.org/bot${token}/getUpdates?offset=${lastOffset + 1}`);
 if (!res.ok) return;
 const data = await res.json();
 if (data.ok && data.result.length > 0) {
 for (const update of data.result) {
 lastOffset = Math.max(lastOffset, update.update_id);
 localStorage.setItem('drmasr_tg_offset_' + (user.uid), lastOffset.toString());

 const message = update.message;
 if (message && message.text && message.text.startsWith('/start')) {
 const chatId = message.chat.id;
 const welcomeText = `أهلاً بك في نظام دكتور مصر \n\nرقم الـ Chat ID الخاص بك هو:\n<code>${chatId}</code>\n\nيرجى نسخ هذا الرقم ووضعه في حقل الـ Chat ID داخل إعدادات النظام لتفعيل الإشعارات بنجاح! `;
 
 await fetch(`https://api.telegram.org/bot${token}/sendMessage`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 chat_id: chatId,
 text: welcomeText,
 parse_mode: 'HTML'
 })
 });
 }
 }
 }
 } catch (err) {
 console.error('Bot polling error:', err);
 }
 };

 intervalId = setInterval(pollTelegramUpdates, 4000);
 return () => clearInterval(intervalId);
 }, [user, telegramSettings.botToken]);



 // Trial expiry check and Telegram alert every 12 hours
 useEffect(() => {
 if (user && currentUserData?.trialExpiresAt && telegramSettings?.enabled) {
 const checkTrialAndAlert = () => {
 const remainingDays = getTrialRemainingDays(currentUserData.trialExpiresAt);
 if (remainingDays <= 3 && remainingDays > 0) {
 const lastAlertTime = localStorage.getItem('drmasr_trialExpiryAlert_' + (user.uid));
 const twelveHoursMs = 12 * 60 * 60 * 1000;
 if (!lastAlertTime || Date.now() - Number(lastAlertTime) >= twelveHoursMs) {
 const pharmacyName = currentUserData?.pharmacyName || "صيدلية دكتور مصر";
 const shortId = currentUserData?.shortId || "غير متوفر";
 const msg = ` <b>تنبيه انتهاء الفترة التجريبية</b>\n\n` +
 `<b>كود العميل (Short ID):</b> <code>${shortId}</code>\n\n` +
 `عزيزي العميل في صيدلية <b>${pharmacyName}</b>،\n` +
 `نود إعلامك بأن حسابك <b>أوشك على الانتهاء</b> ومتبقي له <b>${remainingDays} أيام</b> فقط.\n\n` +
 `لتجنب انقطاع الخدمة، يرجى تجديد الاشتراك.`;
 sendTelegramMessage(telegramSettings, msg);
 localStorage.setItem('drmasr_trialExpiryAlert_' + (user.uid), Date.now().toString());
 }
 }
 };
 checkTrialAndAlert();
 const timer = setInterval(checkTrialAndAlert, 60 * 60 * 1000); // Check once per hour
 return () => clearInterval(timer);
 }
 }, [user, currentUserData, telegramSettings]);

  // Automatically send low-stock and expiring products alert to Telegram once every 12 hours
  useEffect(() => {
    if (user && inventory.length > 0 && telegramSettings?.enabled) {
      if (telegramSettings?.disableShortagesReport) return;
      const checkInventoryAndAlert = () => {
        const lastAlertTime = localStorage.getItem('drmasr_inventoryAlert_' + (user.uid));
        const twelveHoursMs = 12 * 60 * 60 * 1000;
        
        if (!lastAlertTime || Date.now() - Number(lastAlertTime) >= twelveHoursMs) {
          // Filter out of stock & low stock (quantity <= 5)
          const criticalStock = inventory.filter(item => item.quantity <= 5);
          
          // Filter expiring within 6 months
          const expiringSoon = inventory.filter(item => {
            if (!item.expiryDate) return false;
            const now = new Date();
            const date = new Date(item.expiryDate);
            const diffMonth = (date.getFullYear() - now.getFullYear()) * 12 + date.getMonth() - now.getMonth();
            return diffMonth <= 6;
          });
          
          if (criticalStock.length > 0 || expiringSoon.length > 0) {
            const pharmacyName = currentUserData?.pharmacyName || "صيدلية دكتور مصر";
            const shortId = currentUserData?.shortId || "غير متوفر";
            
            let msg = `📊 <b>تقرير تنبيهات المخزون والجرد التلقائي</b>\n` +
                      `🏥 <b>الصيدلية:</b> <code>${pharmacyName}</code> (${shortId})\n` +
                      `━━━━━━━━━━━━━━━━━━━━\n\n`;
            
            if (criticalStock.length > 0) {
              msg += `🚨 <b>نواقص الأدوية والكميات الحرجة:</b>\n`;
              criticalStock.slice(0, 15).forEach(item => {
                msg += `• <b>${item.tradeName}</b>\n  └─ المتبقي: <code>${item.quantity}</code> عبوة\n`;
              });
              if (criticalStock.length > 15) {
                msg += `<i>...وغيرها من النواقص النشطة</i>\n`;
              }
              msg += `\n`;
            }
            
            if (expiringSoon.length > 0) {
              msg += `📅 <b>صلاحيات توشك على الانتهاء (6 أشهر أو أقل):</b>\n`;
              expiringSoon.slice(0, 15).forEach(item => {
                const date = new Date(item.expiryDate);
                const now = new Date();
                const diffMonth = (date.getFullYear() - now.getFullYear()) * 12 + date.getMonth() - now.getMonth();
                const remainingText = diffMonth < 0 ? '❌ منتهي الصلاحية بالفعل' : `⚠️ متبقي ${diffMonth} أشهر`;
                msg += `• <b>${item.tradeName}</b>\n  └─ تاريخ الانتهاء: <code>${item.expiryDate}</code> (${remainingText})\n`;
              });
              if (expiringSoon.length > 15) {
                msg += `<i>...وغيرها من الصلاحيات القريبة</i>\n`;
              }
            }
            
            msg += `━━━━━━━━━━━━━━━━━━━━\n` +
                   `🕊️ <i>نظام دكتور مصر لإدارة الصيدليات وجرد المخازن</i>`;
            
            sendTelegramMessage(telegramSettings, msg);
            localStorage.setItem('drmasr_inventoryAlert_' + (user.uid), Date.now().toString());
          }
        }
      };
      
      checkInventoryAndAlert();
      const timer = setInterval(checkInventoryAndAlert, 60 * 60 * 1000); // Check once per hour
      return () => clearInterval(timer);
    }
  }, [user, inventory, telegramSettings, currentUserData]);

  // Auto-lookup global barcodes
  useEffect(() => {
    if (isModalOpen && !editId && barcode && barcode.trim().length >= 4) {
      const lookupBarcode = async () => {
        try {
          const cleanBarcode = barcode.trim();
          let barcodeDoc = await getDoc(doc(db, 'global_medicines', cleanBarcode));
          if (!barcodeDoc.exists()) {
            barcodeDoc = await getDoc(doc(db, 'global_barcodes', cleanBarcode));
          }
          if (barcodeDoc.exists()) {
            const data = barcodeDoc.data();
            // Only update fields if they are currently empty, to avoid overwriting user edits
            setTradeName(prev => prev || data.tradeName || '');
            setGenericName(prev => prev || data.genericName || '');
            setCategory(prev => prev || data.category || '');
            if (window.triggerDrMasrToast) {
              window.triggerDrMasrToast("✨ تم العثور على الدواء في قاعدة البيانات العامة!");
            }
          }
        } catch (err) {
          console.warn("Failed to fetch global barcode details:", err);
        }
      };
      const delayDebounceFn = setTimeout(() => {
        lookupBarcode();
      }, 600);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [barcode, editId, isModalOpen]);

 const loadLocalInventoryFallback = () => {
  const cached = localStorage.getItem('drmasr_inv_' + (user?.uid));
  if (cached) {
    const parsed = JSON.parse(cached).map(item => {
      let parsedExpiry = item.expiryDate;
      if (parsedExpiry && typeof parsedExpiry === 'object') {
        const { year, month, day } = parsedExpiry;
        if (year && month && day) {
          parsedExpiry = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
      }
      return { ...item, expiryDate: parsedExpiry };
    });
    setInventory(parsed);
 } else {
 // Default placeholder data for a great first impression
 const initData = [
 { id: 'item_1', barcode: '6221111111111', tradeName: 'بانادول إكسترا', genericName: 'باراسيتامول + كافيين', category: 'مسكنات', buyingPrice: 35.0, sellingPrice: 45.0, quantity: 45, expiryDate: '2028-12-31' },
 { id: 'item_2', barcode: '6222222222222', tradeName: 'أوجمنتين 1 جم', genericName: 'أموكسيسيلين + كلافولانات', category: 'مضاد حيوي', buyingPrice: 80.0, sellingPrice: 99.0, quantity: 12, expiryDate: '2027-05-15' }
 ];
 setInventory(initData);
 }
 };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (!isAdminVerified) {
      window.triggerDrMasrToast('❌ عذراً، لا تمتلك صلاحية إضافة أو تعديل المنتجات.');
      return;
    }
    const cleanBarcode = (barcode || '').trim();
    if (cleanBarcode) {
      const barcodeExists = inventory.some(item => item.id !== editId && (item.barcode || '').trim() === cleanBarcode);
      if (barcodeExists) {
        window.triggerDrMasrToast('❌ عذراً، هذا الباركود مسجل بالفعل لصنف آخر في المخزن.');
        return;
      }
    }
    setIsSaving(true);
    try {
      let parsedExpiryDate = expiryDate || '';
      // Normalize expiryDate to a plain string
      if (expiryDate && typeof expiryDate === 'object') {
        const { year, month, day } = expiryDate;
        if (year && month && day) {
          parsedExpiryDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
      }

      // Automatically detect origin (local vs imported) based on barcode
      let detectedOrigin = 'local';
      if (cleanBarcode) {
        const isAutoGenerated = /^20\d{10}$/.test(cleanBarcode);
        if (cleanBarcode.startsWith('622') || isAutoGenerated || cleanBarcode.length < 8) {
          detectedOrigin = 'local';
        } else {
          detectedOrigin = 'imported';
        }
      }

      const itemData = {
        tradeName: tradeName.trim(),
        tradeNameSecondary: (tradeNameSecondary || '').trim(),
        genericName: (genericName || '').trim(),
        requiresFridge: !!requiresFridge,
        origin: detectedOrigin,
        category: (category || '').trim(),
        buyingPrice: Number(buyingPrice) || 0,
        sellingPrice: Number(sellingPrice) || 0,
        quantity: Number(quantity) || 0,
        expiryDate: parsedExpiryDate,
        barcode: (barcode || '').trim(),
        subUnits: subUnits || [],
        updatedAt: new Date().toISOString()
      };

      if (editId) {
        await updateDoc(doc(db, 'users', user.uid, 'inventory', editId), itemData);
        window.triggerDrMasrToast('تم تحديث البيانات بنجاح ✨');
      } else {
        const newItemWithTime = {
          ...itemData,
          createdAt: new Date().toISOString()
        };
        const docRef = await addDoc(collection(db, 'users', user.uid, 'inventory'), newItemWithTime);
        setInventory(prev => [{ id: docRef.id, ...newItemWithTime }, ...prev]);
        window.triggerDrMasrToast('تم إضافة الصنف للمخزون بنجاح ✨');
      }

      // ── Global Barcodes Sync ───────────────────────────────────────────────
      // Rules:
      // 1. Only sync if barcode is REAL (not auto-generated '20...' format)
      // 2. Only write to global_barcodes if this barcode does NOT already exist there.
      //    If it already exists → skip silently (no duplicate, no message).
      const isAutoGeneratedBarcode = /^20\d{10}$/.test(cleanBarcode);
      const isRealBarcode = cleanBarcode.length >= 8 && !isAutoGeneratedBarcode;

      if (isRealBarcode && tradeName && tradeName.trim()) {
        const globalBarcodeRef = doc(db, 'global_barcodes', cleanBarcode);
        (async () => {
          try {
            // Check if barcode already exists in global_barcodes
            const checkSnap = await getDoc(globalBarcodeRef);
            if (!checkSnap.exists()) {
              // New barcode — save it to global_barcodes to share with the network
              await setDoc(globalBarcodeRef, {
                barcode: cleanBarcode,
                tradeName: tradeName.trim(),
                genericName: (genericName || '').trim(),
                category: (category || '').trim(),
                subUnits: subUnits || [],
                updatedAt: new Date().toISOString()
              });
            }
            // If it already exists → do nothing (no duplicate, no overwrite, no message)
          } catch (err) {
            console.error('Error syncing to global_barcodes:', err);
          }
        })();
      }
      // ────────────────────────────────────────────────────────────────────────
      clearForm();
    } catch (err) {
      console.error("Error saving item:", err);
      window.triggerDrMasrToast('❌ فشل في حفظ البيانات: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!user) return;
    if (!isAdminVerified) {
      window.triggerDrMasrToast('❌ عذراً، لا تمتلك صلاحية حذف المنتجات.');
      return;
    }
    window.triggerDrMasrConfirm('هل أنت متأكد من حذف هذا الصنف نهائياً؟', async () => {
      try {
        await deleteDoc(doc(db, 'users', user.uid, 'inventory', id));
        window.triggerDrMasrToast('تم حذف الصنف من المخزون 🗑️');
      } catch (err) {
        console.error("Error deleting item:", err);
        window.triggerDrMasrToast('❌ فشل في حذف الصنف.');
      }
    });
  };

  const addItemToCart = (item, subUnit = null, bypassModal = false) => {
    // Sound playback
    try {
      const audio = new Audio('/barcode.mp3');
      audio.play().catch(() => {});
    } catch(e){}

    if (item.quantity <= 0) {
      setSelectedAlternativesProduct(item);
      return;
    }

    if (item.subUnits && item.subUnits.length > 0 && subUnit === null && !bypassModal) {
      setSelectedUnitProduct(item);
      return;
    }

    const existing = cart.find(i => i.id === item.id && i.subUnit?.name === subUnit?.name);
    if (existing) {
      // Calculate total quantity of this product already in the cart in terms of main units
      let totalUsed = 0;
      cart.forEach(i => {
        if (i.id === item.id) {
          if (i.subUnit) {
            totalUsed += i.cartQuantity / (i.subUnit.ratio || 1);
          } else {
            totalUsed += i.cartQuantity;
          }
        }
      });

      const nextUsed = totalUsed + (subUnit ? 1 / (subUnit.ratio || 1) : 1);
      if (nextUsed > item.quantity) {
        window.triggerDrMasrToast('⚠️ الكمية المطلوبة تتجاوز الرصيد المتاح بالمخزن!');
        return;
      }

      setCart(cart.map(i => (i.id === item.id && i.subUnit?.name === subUnit?.name) 
        ? { ...i, cartQuantity: i.cartQuantity + 1 } : i));
      setIsMobileCheckoutOpen(true);
    } else {
      let totalUsed = 0;
      cart.forEach(i => {
        if (i.id === item.id) {
          if (i.subUnit) {
            totalUsed += i.cartQuantity / (i.subUnit.ratio || 1);
          } else {
            totalUsed += i.cartQuantity;
          }
        }
      });

      const nextUsed = totalUsed + (subUnit ? 1 / (subUnit.ratio || 1) : 1);
      if (nextUsed > item.quantity) {
        window.triggerDrMasrToast('⚠️ الكمية المطلوبة تتجاوز الرصيد المتاح بالمخزن!');
        return;
      }

      setCart([...cart, { ...item, cartQuantity: 1, subUnit }]);
      setIsMobileCheckoutOpen(true);
    }
  };

  const shareSaleViaWhatsApp = (sale) => {
    try {
      const pharmacyName = currentUserData?.pharmacyName || 'صيدلية دكتور مصر';
      const pharmacyPhone = currentUserData?.pharmacyPhone || '';
      
      let message = `🏥 *${pharmacyName}*\n`;
      message += `🧾 *فاتورة مبيعات رقم:* \`#${sale.invoiceId}\`\n`;
      message += `📅 *التاريخ:* ${sale.timestamp ? new Date(sale.timestamp).toLocaleString('ar-EG') : new Date().toLocaleString('ar-EG')}\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n\n`;
      
      (sale.items || []).forEach((item, idx) => {
        const unitLabel = item.subUnit ? `(${item.subUnit.name})` : '';
        message += `${idx + 1}. *${item.tradeName}* ${unitLabel}\n`;
        message += `   الكمية: ${item.cartQuantity} × ${item.price} ج.م = *${(item.price * item.cartQuantity).toFixed(2)} ج.م*\n`;
      });
      
      message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
      message += `💵 *المجموع:* ${sale.subTotal || sale.grandTotal} ج.م\n`;
      if (sale.discount > 0) {
        message += `🔻 *الخصم:* ${sale.discount} - ج.م\n`;
      }
      message += `💰 *المجموع النهائي:* *${sale.grandTotal} ج.م*\n`;
      
      if (sale.isDelivery) {
        message += `\n🛵 *تفاصيل التوصيل:*\n`;
        message += `👤 *العميل:* ${sale.customerName || 'غير مسجل'}\n`;
        message += `📍 *العنوان:* ${sale.deliveryDetails?.address || 'غير محدد'}\n`;
      }
      
      message += `\n🌹 *شكراً لتعاملكم معنا!* 🌹\n`;
      if (pharmacyPhone) {
        message += `📞 *للتواصل:* ${pharmacyPhone}\n`;
      }

      const encodedText = encodeURIComponent(message);
      const targetPhone = sale.customerPhone || '';
      const cleanPhone = targetPhone.replace(/\D/g, '');
      const waUrl = cleanPhone 
        ? `https://api.whatsapp.com/send?phone=${cleanPhone.startsWith('0') ? '2' + cleanPhone : cleanPhone}&text=${encodedText}`
        : `https://api.whatsapp.com/send?text=${encodedText}`;
      
      window.open(waUrl, '_blank');
    } catch (err) {
      console.error('WhatsApp share failed:', err);
    }
  };

  const printSaleReceipt = (sale) => {
    try {
      const printWindow = window.open('', '_blank', 'width=300,height=600');
      if (printWindow) {
        printWindow.document.write(`
        <html>
        <head>
        <title>فاتورة مبيعات - ${sale.invoiceId}</title>
        <style>
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700;800&display=swap');
        body {
          font-family: 'Cairo', sans-serif;
          direction: rtl;
          text-align: center;
          padding: 10px;
          margin: 0;
          color: #000;
          font-size: 11px;
        }
        .header {
          border-bottom: 1px dashed #000;
          padding-bottom: 8px;
          margin-bottom: 8px;
          text-align: center;
        }
        .title {
          font-size: 15px;
          font-weight: 800;
          margin: 0;
        }
        .subtitle {
          font-size: 10px;
          margin: 3px 0;
        }
        .info-row {
          display: flex;
          justify-content: space-between;
          margin: 2px 0;
          font-size: 9.5px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 8px 0;
        }
        th, td {
          text-align: right;
          padding: 4px 0;
          font-size: 10px;
        }
        th {
          border-bottom: 1px dashed #000;
        }
        .total-section {
          border-top: 1px dashed #000;
          padding-top: 6px;
          margin-top: 6px;
          font-weight: bold;
        }
        .footer {
          border-top: 1px dashed #000;
          padding-top: 8px;
          margin-top: 12px;
          font-size: 9px;
          text-align: center;
        }
        </style>
        </head>
        <body>
        <div class="header">
        ${(currentUserData?.pharmacyLogo || localStorage.getItem('drmasr_receipt_logo')) ? `<img src="${currentUserData?.pharmacyLogo || localStorage.getItem('drmasr_receipt_logo')}" style="max-height: 60px; max-width: 130px; object-fit: contain; margin-bottom: 6px;" /><br/>` : ''}
        <p class="title">${currentUserData?.pharmacyName || 'صيدلية دكتور مصر'}</p>
        <p class="subtitle">هاتف: ${currentUserData?.phone || 'غير مسجل'}</p>
        </div>
        
        <div class="info-row">
        <span>رقم الفاتورة:</span>
        <span>#${sale.invoiceId}</span>
        </div>
        ${sale.isDelivery ? `
        <div class="info-row" style="font-weight: bold; color: #d946ef;">
        <span>نوع الطلب:</span>
        <span>أوردر دليفري</span>
        </div>
        ` : `
        <div class="info-row">
        <span>نوع الطلب:</span>
        <span>بيع مباشر (كاشير)</span>
        </div>
        `}
        ${sale.customerName ? `
        <div class="info-row">
        <span>اسم العميل:</span>
        <span>${sale.customerName}</span>
        </div>
        ` : ''}
        ${sale.customerPhone ? `
        <div class="info-row">
        <span>رقم الهاتف:</span>
        <span>${sale.customerPhone}</span>
        </div>
        ` : ''}
        ${sale.deliveryDetails?.address ? `
        <div class="info-row">
        <span>عنوان التوصيل:</span>
        <span>${sale.deliveryDetails.address}</span>
        </div>
        ` : ''}
        ${sale.deliveryDetails?.driverName ? `
        <div class="info-row">
        <span>الطيار:</span>
        <span>${sale.deliveryDetails.driverName}</span>
        </div>
        ` : ''}
        <div class="info-row">
        <span>التاريخ والوقت:</span>
        <span>${new Date(sale.timestamp).toLocaleString('ar-EG', { hour12: true })}</span>
        </div>
        <div class="info-row">
        <span>طريقة الدفع:</span>
        <span>${sale.paymentMethod === 'cash' ? 'كاش' : sale.paymentMethod === 'deferred' ? '⏳ بيع بالآجل (مديونية)' : sale.paymentMethod === 'split' ? `مختلط (كاش: ${sale.splitAmounts?.cash || 0} / فيزا: ${sale.splitAmounts?.card || 0})` : sale.paymentMethod === 'instapay' ? 'إنستاباي 📱' : sale.paymentMethod === 'vodafone' ? 'فودافون كاش 📲' : 'فيزا / شبكة'}</span>
        </div>
        <div class="info-row">
        <span>الدكتور المسؤول:</span>
        <span>${sale.cashierName}</span>
        </div>
        
        <table>
        <thead>
        <tr>
        <th>الصنف</th>
        <th style="text-align: center;">الكمية</th>
        <th style="text-align: left;">السعر</th>
        </tr>
        </thead>
        <tbody>
        ${sale.items.map(c => `
        <tr>
        <td>${c.tradeName}</td>
        <td style="text-align: center;">${c.subUnit ? `${Math.round(c.quantity * c.subUnit.ratio)} ${c.subUnit.name} (${Number(c.quantity.toFixed(4))} علبة)` : c.quantity}</td>
        <td style="text-align: left;">${(c.price * c.quantity).toFixed(2)} ج.م</td>
        </tr>
        `).join('')}
        </tbody>
        </table>
        
        <div class="total-section">
        <div class="info-row">
        <span>المجموع الفرعي:</span>
        <span>${sale.subTotal || sale.grandTotal} ج.م</span>
        </div>
        ${sale.discount > 0 ? `
        <div class="info-row">
        <span>${sale.promoCode ? `خصم الكوبون (${sale.promoCode}):` : 'الخصم المطبق:'}</span>
        <span>-${sale.discount} ج.م</span>
        </div>
        ` : ''}
        ${sale.pointsDiscount > 0 ? `
        <div class="info-row" style="color: #d97706; font-weight: bold;">
        <span>خصم كاش باك:</span>
        <span>- ${sale.pointsDiscount} ج.م</span>
        </div>
        ` : ''}
        ${sale.deliveryFee > 0 ? `
        <div class="info-row">
        <span>خدمة التوصيل:</span>
        <span>${sale.deliveryFee} ج.م</span>
        </div>
        ` : ''}
        <div class="info-row" style="font-size: 12px; font-weight: 800; border-top: 1px dashed #000; padding-top: 4px; margin-top: 4px;">
        <span>صافي المطلوب:</span>
        <span>${sale.grandTotal} ج.م</span>
        </div>
        ${sale.isInsurance ? `
        <div style="border-top: 1px dashed #000; margin-top: 6px; padding-top: 6px; font-size: 9px; text-align: right;">
          <div class="info-row">
            <span>جهة التعاقد:</span>
            <span style="font-weight: bold;">${sale.insuranceContractName}</span>
          </div>
          ${sale.insuranceApprovalNumber ? `
          <div class="info-row">
            <span>رقم الموافقة:</span>
            <span style="font-family: monospace; font-weight: bold;">${sale.insuranceApprovalNumber}</span>
          </div>
          ` : ''}
          <div class="info-row">
            <span>تحمل التأمين (${sale.coveragePercentage}%):</span>
            <span>${sale.insuranceShare} ج.م</span>
          </div>
          <div class="info-row" style="font-weight: bold; font-size: 11px; border-top: 1px solid #000; padding-top: 2px; margin-top: 2px;">
            <span>تحمل المريض (${sale.copayPercentage}%):</span>
            <span>${sale.patientShare} ج.م</span>
          </div>
        </div>
        ` : ''}
        ${sale.pointsEarned > 0 ? `
        <div class="info-row" style="color: #059669; font-weight: bold; border-top: 1px dashed #000; padding-top: 4px; margin-top: 4px;">
        <span>كاش باك مكتسب:</span>
        <span>+ ${sale.pointsEarned} ج.م</span>
        </div>
        ` : ''}
        </div>
        
        <div class="footer">
        <p style="margin: 0; font-weight: bold;">شكراً لتعاملكم معنا!</p>
        <p style="margin: 4px 0 0 0; font-size: 8px; color: #555;">دكتور مصر لإدارة الصيدليات </p>
        </div>
        
        <div style="margin-top: 15px; text-align: center; border-top: 1px dashed #000; padding-top: 10px;">
          ${(sale.etaUuid && (() => {
            const savedSettings = JSON.parse(localStorage.getItem('drmasr_eta_settings')) || {};
            return savedSettings.enabled === true;
          })()) ? (() => {
            const savedSettings = JSON.parse(localStorage.getItem('drmasr_eta_settings')) || {};
            const isProd = savedSettings.envMode === 'production';
            const etaBaseUrl = isProd ? 'https://invoicing.eta.gov.eg/receipts/search/' : 'https://preprod.invoicing.eta.gov.eg/receipts/search/';
            return `
              <p style="font-size: 10px; margin: 0 0 5px 0;">Egypt eReceipt QR Code</p>
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=110x110&data=${encodeURIComponent(etaBaseUrl + sale.etaUuid)}" alt="ETA QR Code" style="display: block; margin: 0 auto;" />
              <p style="font-size: 10px; margin: 5px 0 0 0;">Egypt eReceipt QR Code</p>
              <p style="font-size: 8px; margin-top: 8px; word-break: break-all; padding: 0 10px;">${sale.etaUuid}</p>
            `;
          })() : (() => {
            const taxReg = currentUserData?.taxRegistrationNumber || '395-827-104';
            const vatAmount = (Number(sale.grandTotal) * 14 / 114).toFixed(2);
            const qrText = `Seller: ${currentUserData?.pharmacyName || 'صيدلية دكتور مصر'}\nTax Card: ${taxReg}\nDate: ${new Date(sale.timestamp).toISOString()}\nTotal: ${sale.grandTotal} EGP\nVAT (14%): ${vatAmount} EGP`;
            return `
              <p style="font-size: 9px; margin: 0 0 4px 0; font-weight: bold;">🧾 فاتورة ضريبية مبسطة</p>
              <p style="font-size: 8px; margin: 0 0 6px 0; color: #555;">مصلحة الضرائب المصرية</p>
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrText)}" alt="ETA QR Code" style="display: block; margin: 0 auto; border: 1px solid #ccc; padding: 2px;" />
              <p style="font-size: 8px; margin-top: 6px; color: #333;">الرقم الضريبي: ${taxReg}</p>
              <p style="font-size: 8px; margin-top: 2px;">رقم الفاتورة: Ref-${sale.invoiceId}</p>
            `;
          })()}
        </div>

        <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
          }, 800);
        };
        </script>
        </body>
        </html>
        `);
        printWindow.document.close();
      }
    } catch (err) {
      console.error('Receipt print failed:', err);
    }
  };

  const handleCheckout = async (paymentMethod = 'cash', splitAmounts = null) => {
    if (!user || cart.length === 0) return;
    if (!activeShift) {
      window.triggerDrMasrAlert('⚠️ يرجى فتح وردية أولاً لإتمام عملية البيع!');
      setIsShiftDialogOpen(true);
      return;
    }
    if (isDeliveryOrder) {
      const finalName = selectedCustomer?.name || customerName || '';
      const finalPhone = selectedCustomer?.phone || customerPhone || '';
      const finalAddress = deliveryAddress || '';
      
      if (!finalName.trim() || !finalPhone.trim() || !finalAddress.trim()) {
        window.triggerDrMasrAlert('⚠️ يرجى إدخال البيانات الأساسية للدليفري: الاسم، رقم الهاتف، والعنوان بالتفصيل!');
        return;
      }
      
      if (!deliveryDriver || deliveryDriver.trim() === '') {
        window.triggerDrMasrAlert('⚠️ يرجى اختيار طيار أولاً لتنفيذ طلب الدليفري!');
        return;
      }
    }

    setLoading(true);
    try {
      // 1. Calculate subtotal using discounted prices if applicable
      const subtotal = parseFloat(cart.reduce((acc, item) => {
        let activePrice = item.subUnit ? item.subUnit.price : item.sellingPrice;
        if (item.hasDiscount && item.discountedPrice !== undefined) {
          const ratio = item.discountedPrice / item.sellingPrice;
          activePrice = item.subUnit ? Math.round(item.subUnit.price * ratio * 100) / 100 : item.discountedPrice;
        }
        return acc + activePrice * item.cartQuantity;
      }, 0).toFixed(2));

      // 2. Apply general campaigns automatically from loaded generalCampaigns state if invoice matches minTotal threshold
      let campaignDiscount = 0;
      let appliedCampaignName = '';
      const campList = generalCampaigns.filter(c => c.isActive);
      // Find campaign with highest discount value that is met by this subtotal
      const eligibleCamps = campList.filter(c => subtotal >= (c.minTotal || 0));
      if (eligibleCamps.length > 0) {
        // Sort to find the best campaign discount value
        eligibleCamps.sort((a, b) => {
          const valA = a.type === 'percentage' ? (subtotal * (a.value / 100)) : a.value;
          const valB = b.type === 'percentage' ? (subtotal * (b.value / 100)) : b.value;
          return valB - valA;
        });
        const bestCamp = eligibleCamps[0];
        campaignDiscount = bestCamp.type === 'percentage' ? (subtotal * (bestCamp.value / 100)) : bestCamp.value;
        campaignDiscount = Math.round(campaignDiscount * 100) / 100;
        appliedCampaignName = bestCamp.name;
      }

      // Combine manual cashier discount with auto campaign discount
      const totalDiscount = parseFloat((posDiscount + campaignDiscount).toFixed(2));

      const maxPointsRedeemable = selectedCustomer && isLoyaltyEnabled
        ? Math.min(selectedCustomer.points || 0, subtotal - totalDiscount + (isDeliveryOrder ? (parseFloat(deliveryFee) || 0) : 0))
        : 0;
      const currentPointsDiscount = usePoints ? maxPointsRedeemable : 0;
      const grandTotal = parseFloat((subtotal - totalDiscount - currentPointsDiscount + (isDeliveryOrder ? (parseFloat(deliveryFee) || 0) : 0)).toFixed(2));

      // Insurance Split Calculation
      const currentMonthKey = `insuranceConsumption_${new Date().getFullYear()}_${String(new Date().getMonth() + 1).padStart(2, '0')}`;
      const currentMonthlyConsumption = selectedCustomer ? (Number(selectedCustomer[currentMonthKey]) || 0) : 0;

      const splitResult = calculateInsuranceSplit(
        cart,
        selectedInsuranceContract,
        subtotal,
        posDiscount,
        currentPointsDiscount,
        deliveryFee,
        isDeliveryOrder,
        currentMonthlyConsumption
      );
      const patientShare = splitResult.patientShare;
      const insuranceShare = splitResult.insuranceShare;

      // Approval code check
      if (selectedInsuranceContract) {
        const reqAbove = selectedInsuranceContract.requireApprovalAbove !== undefined ? Number(selectedInsuranceContract.requireApprovalAbove) : 0;
        if (reqAbove > 0 && insuranceShare > reqAbove && !insuranceApprovalNumber.trim()) {
          setLoading(false);
          window.triggerDrMasrAlert(`⚠️ رقم موافقة التأمين مطلوب لهذه العملية لأن حصة الشركة (${insuranceShare} ج.م) تتجاوز الحد المسموح به بدون موافقة (${reqAbove} ج.م)!`);
          return;
        }
      }

      const pointsEarned = isLoyaltyEnabled && (selectedCustomer || (customerPhone && customerName))
        ? Math.round((grandTotal * (currentUserData?.loyaltySettings?.cashbackPer100 !== undefined ? currentUserData?.loyaltySettings?.cashbackPer100 : 5)) / 100 * 100) / 100
        : 0;

      const invoiceId = Date.now().toString().slice(-8);

      const saleData = {
        invoiceId,
        isInsurance: !!selectedInsuranceContract,
        insuranceContractId: selectedInsuranceContract?.id || null,
        insuranceContractName: selectedInsuranceContract?.name || null,
        insuranceApprovalNumber: insuranceApprovalNumber || '',
        patientShare,
        insuranceShare,
        copayPercentage: selectedInsuranceContract
          ? (selectedInsuranceContract.copayPercentageLocal !== undefined
              ? `محلي: ${selectedInsuranceContract.copayPercentageLocal}% / مستورد: ${selectedInsuranceContract.copayPercentageImported}%`
              : selectedInsuranceContract.copayPercentage)
          : 100,
        coveragePercentage: selectedInsuranceContract
          ? (selectedInsuranceContract.copayPercentageLocal !== undefined
              ? `محلي: ${100 - selectedInsuranceContract.copayPercentageLocal}% / مستورد: ${100 - selectedInsuranceContract.copayPercentageImported}%`
              : selectedInsuranceContract.coveragePercentage)
          : 0,
        insuranceCapExceeded: splitResult.isCapReached,
        insuranceMonthlyCapExceeded: splitResult.isMonthlyCapReached,
        insuranceClaimStatus: 'pending',
        items: cart.map(i => {
          const activePrice = i.hasDiscount && i.discountedPrice !== undefined 
            ? (i.subUnit ? Math.round(i.subUnit.price * (i.discountedPrice / i.sellingPrice) * 100) / 100 : i.discountedPrice)
            : (i.subUnit ? i.subUnit.price : i.sellingPrice);
          return {
            id: i.id,
            tradeName: i.tradeName,
            quantity: i.cartQuantity,
            sellingPrice: activePrice,
            price: activePrice,
            subUnit: i.subUnit || null,
            barcode: i.barcode,
            buyingPrice: i.buyingPrice || 0
          };
        }),
        subTotal: subtotal,
        discount: totalDiscount,
        campaignDiscount: campaignDiscount,
        campaignName: appliedCampaignName || null,
        pointsDiscount: currentPointsDiscount,
        pointsEarned: pointsEarned,
        grandTotal,
        paymentMethod,
        splitAmounts,
        timestamp: new Date().toISOString(),
        cashierName: activeShift.cashierName,
        shiftId: activeShift.id,
        isDelivery: isDeliveryOrder,
        deliveryStatus: isDeliveryOrder ? 'pending' : null,
        customerName: isDeliveryOrder ? customerName : (selectedCustomer?.name || customerName || ''),
        customerPhone: isDeliveryOrder ? customerPhone : (selectedCustomer?.phone || customerPhone || ''),
        deliveryDetails: isDeliveryOrder ? {
          address: deliveryAddress,
          driverName: deliveryDriver,
          fee: parseFloat(deliveryFee) || 0
        } : null,
        deliveryFee: isDeliveryOrder ? (parseFloat(deliveryFee) || 0) : 0
      };

      // 1. Update local inventory state and localStorage optimistically (INSTANT stock update)
      const nextInv = inventory.map(invItem => {
        const cartItem = cart.find(i => i.id === invItem.id);
        if (cartItem) {
          let newQty = invItem.quantity;
          if (cartItem.subUnit) {
             newQty -= (cartItem.cartQuantity / cartItem.subUnit.ratio);
          } else {
             newQty -= cartItem.cartQuantity;
          }
          return { ...invItem, quantity: Math.max(0, Number(newQty.toFixed(2))) };
        }
        return invItem;
      });
      setInventory(nextInv);
      localStorage.setItem('drmasr_inv_' + (user.uid), JSON.stringify(nextInv));

      // Generate a new Firestore document reference for the sale early to get the ID
      const saleDocRef = doc(collection(db, 'users', user.uid, 'sales'));
      const saleWithId = { id: saleDocRef.id, ...saleData };
      const nextSales = [saleWithId, ...sales];
      setSales(nextSales);
      localStorage.setItem('drmasr_sales_' + (user.uid), JSON.stringify(nextSales));

      // 2. Trigger receipt printing immediately and toast (OPTIMISTIC UI)
      printSaleReceipt(saleData);
      window.triggerDrMasrToast(isDeliveryOrder ? 'تم تسجيل أوردر الدليفري بنجاح! 🛵' : 'تم إتمام عملية البيع بنجاح! 🧾');
      
      // Reset POS States instantly
      setCart([]);
      setPosDiscount(0);
      setUsePoints(false);
      setSelectedCustomer(null);
      setSelectedInsuranceContract(null);
      setInsuranceApprovalNumber('');
      setIsCartModalOpen(false);
      setIsDeliveryOrder(false);
      setCustomerName('');
      setCustomerPhone('');
      setDeliveryAddress('');
      setDeliveryDriver('');
      setDeliveryFee(0);
      resetPaymentModes();
      setLoading(false); // Disable screen loading spinner instantly

      // 3. Save to Firestore in background via Atomic Write Batch (Highly optimized!)
      try {
        const batch = writeBatch(db);
        batch.set(saleDocRef, saleData);

        // Queue all inventory updates in the same batch
        for (const item of cart) {
          const invItem = inventory.find(i => i.id === item.id);
          if (invItem) {
            let newQty = invItem.quantity;
            if (item.subUnit) {
              newQty -= (item.cartQuantity / item.subUnit.ratio);
            } else {
              newQty -= item.cartQuantity;
            }
            const itemDocRef = doc(db, 'users', user.uid, 'inventory', item.id);
            batch.set(itemDocRef, {
              ...invItem,
              quantity: Math.max(0, Number(newQty.toFixed(2)))
            }, { merge: true });
          }
        }

        // Update customer points and spend in Firestore
        if (selectedCustomer || (customerPhone && customerPhone.trim().length > 0)) {
          const finalPhone = selectedCustomer?.phone || customerPhone;
          const finalName = selectedCustomer?.name || customerName || 'عميل كاش باك';
          const isDeferred = paymentMethod === 'deferred';
          const saleDebtAmount = isDeferred ? grandTotal : 0;
          
          let targetCust = null;
          let isNew = false;
          let customerDocRef = null;

          if (selectedCustomer) {
            targetCust = selectedCustomer;
            customerDocRef = doc(db, 'users', user.uid, 'customers', selectedCustomer.id);
          } else {
            const existingCust = loyaltyCustomers.find(c => c.phone === finalPhone);
            if (existingCust) {
              targetCust = existingCust;
              customerDocRef = doc(db, 'users', user.uid, 'customers', existingCust.id);
            } else {
              isNew = true;
              customerDocRef = doc(collection(db, 'users', user.uid, 'customers'));
            }
          }

          const currentMonthKey = `insuranceConsumption_${new Date().getFullYear()}_${String(new Date().getMonth() + 1).padStart(2, '0')}`;

          const processChronicMeds = (cust) => {
            const currentChronicMeds = cust?.chronicMeds || [];
            let updatedChronicMeds = [...currentChronicMeds];
            const scheduledAlerts = [];

            cart.forEach(item => {
              const medName = item.tradeName;
              const isMarkedChronic = !!item.isChronic;
              let isChronic = isMarkedChronic;
              let intervalDays = 30;

              // AI Detection (if not manually marked and not a new customer)
              if (!isChronic && !isNew && cust) {
                const cutoff = Date.now() - 60 * 24 * 60 * 60 * 1000;
                const pastSalesOfCustomer = sales.filter(s => 
                  s.customerPhone === cust.phone && 
                  new Date(s.timestamp).getTime() > cutoff
                );

                const occurrences = [];
                pastSalesOfCustomer.forEach(sale => {
                  if (sale.items && sale.items.some(i => i.tradeName === medName)) {
                    occurrences.push(new Date(sale.timestamp).getTime());
                  }
                });

                if (occurrences.length >= 2) {
                  isChronic = true;
                  occurrences.sort((a, b) => a - b);
                  let totalDiff = 0;
                  for (let i = 1; i < occurrences.length; i++) {
                    totalDiff += (occurrences[i] - occurrences[i-1]);
                  }
                  const avgMs = totalDiff / (occurrences.length - 1);
                  const avgDays = Math.round(avgMs / (24 * 60 * 60 * 1000));
                  if (avgDays >= 10 && avgDays <= 50) {
                    intervalDays = avgDays;
                  }
                }
              }

              if (isChronic) {
                const existingIdx = updatedChronicMeds.findIndex(m => m.medicationName === medName);
                if (existingIdx > -1) {
                  updatedChronicMeds[existingIdx] = {
                    ...updatedChronicMeds[existingIdx],
                    lastPurchaseDate: new Date().toISOString(),
                    intervalDays: intervalDays
                  };
                } else {
                  updatedChronicMeds.push({
                    medicationName: medName,
                    intervalDays: intervalDays,
                    lastPurchaseDate: new Date().toISOString()
                  });
                }

                const alertDate = new Date();
                alertDate.setDate(alertDate.getDate() + intervalDays - 3);
                const alertDateString = alertDate.toISOString().split('T')[0];

                const newAlertRef = doc(collection(db, 'users', user.uid, 'refill_alerts'));
                const alertData = {
                  id: newAlertRef.id,
                  customerId: customerDocRef.id,
                  customerName: finalName,
                  customerPhone: finalPhone,
                  medicationName: medName,
                  intervalDays: intervalDays,
                  lastPurchaseDate: new Date().toISOString(),
                  alertDate: alertDateString,
                  status: 'pending'
                };

                batch.set(newAlertRef, alertData);
                scheduledAlerts.push(alertData);

                // Add to local state
                setRefillAlerts(prev => [alertData, ...prev]);

                // Telegram notification
                if (telegramSettings && telegramSettings.botToken && telegramSettings.chatId) {
                  const tgMessage = `⏰ تذكير موعد تجديد الدواء:\n👤 المريض: ${finalName}\n📞 الهاتف: ${finalPhone}\n💊 الدواء: ${medName}\n📅 تاريخ التجديد: ${alertDateString}`;
                  sendTelegramMessage(telegramSettings, tgMessage).catch(console.error);
                }

                // Voice Speech notification
                try {
                  if ('speechSynthesis' in window) {
                    const speechMsg = new SpeechSynthesisUtterance(`تنبيه: تم جدولة تجديد دواء ${medName} للمريض ${finalName}`);
                    speechMsg.lang = 'ar-EG';
                    window.speechSynthesis.speak(speechMsg);
                  }
                } catch (e) {
                  console.error(e);
                }
              }
            });

            return { updatedChronicMeds, scheduledAlerts };
          };
          
          if (isNew) {
            // New Customer
            const { updatedChronicMeds } = processChronicMeds(null);

            const newCustomerData = {
              id: customerDocRef.id,
              name: finalName,
              phone: finalPhone,
              visits: 1,
              spend: grandTotal,
              points: Math.round(pointsEarned * 100) / 100,
              debt: Math.round(saleDebtAmount * 100) / 100,
              lastVisit: new Date().toISOString(),
              createdAt: new Date().toISOString(),
              [currentMonthKey]: Math.round(insuranceShare * 100) / 100,
              chronicMeds: updatedChronicMeds
            };

            batch.set(customerDocRef, newCustomerData);
            setLoyaltyCustomers(prev => [...prev, newCustomerData]);
          } else {
            // Existing Customer (targetCust)
            const oldPoints = targetCust.points || 0;
            const newPoints = Math.max(0, oldPoints - currentPointsDiscount + pointsEarned);
            const oldDebt = targetCust.debt || 0;
            const newDebt = oldDebt + saleDebtAmount;
            const oldConsumption = Number(targetCust[currentMonthKey]) || 0;
            const newConsumption = oldConsumption + insuranceShare;

            const { updatedChronicMeds } = processChronicMeds(targetCust);

            const updatedData = {
              name: finalName,
              phone: finalPhone,
              visits: (targetCust.visits || 0) + 1,
              spend: (targetCust.spend || 0) + grandTotal,
              points: Math.round(newPoints * 100) / 100,
              debt: Math.round(newDebt * 100) / 100,
              lastVisit: new Date().toISOString(),
              [currentMonthKey]: Math.round(newConsumption * 100) / 100,
              chronicMeds: updatedChronicMeds
            };

            batch.set(customerDocRef, updatedData, { merge: true });

            setLoyaltyCustomers(prev => prev.map(c => c.id === targetCust.id ? {
              ...c,
              ...updatedData
            } : c));
          }
        }

        batch.commit()
          .then(() => console.log("Atomic checkout batch committed successfully."))
          .catch(err => console.error("Firestore atomic checkout commit failed:", err));
      } catch (err) {
        console.error("Firestore batch creation error:", err);
      }

      // 4. Telegram Notification
      if (telegramSettings?.enabled && !telegramSettings?.disableSalesNotifications) {
        const pharmacyName = currentUserData?.pharmacyName || "صيدلية دكتور مصر";
        const shortId = currentUserData?.shortId || "غير متوفر";
        const payMethodAr = paymentMethod === 'cash' ? '💵 كاش' : paymentMethod === 'deferred' ? '⏳ بيع بالآجل (مديونية)' : paymentMethod === 'card' ? '💳 كارت' : paymentMethod === 'credit' ? '💳 فيزا/شبكة' : paymentMethod === 'split' ? `💵💳 مختلط (كاش: ${splitAmounts?.cash || 0} / فيزا: ${splitAmounts?.card || 0})` : paymentMethod === 'instapay' ? '📱 إنستاباي' : paymentMethod === 'vodafone' ? '📲 فودافون كاش' : paymentMethod;
        const itemsListStr = saleData.items.map(i => `  • <b>${i.tradeName}</b> × <code>${i.quantity}</code> = <b>${(i.price * i.quantity).toFixed(2)} ج.م</b>`).join('\n');
        let msg = '';

        const insuranceDetails = selectedInsuranceContract ? (
          `🏥 <b>تفاصيل التأمين الطبي:</b>\n` +
          `  ├─ جهة التعاقد: ${selectedInsuranceContract.name}\n` +
          `  ├─ رقم الموافقة: <code>${insuranceApprovalNumber || 'لا يوجد'}</code>\n` +
          `  ├─ تحمل التأمين (${selectedInsuranceContract.coveragePercentage}%): ${insuranceShare} ج.م\n` +
          `  └─ تحمل المريض (${selectedInsuranceContract.copayPercentage}%): <b>${patientShare} ج.م</b>\n\n`
        ) : '';

        if (isDeliveryOrder) {
          msg = `🛵 <b>طلب دليفري جديد (قيد الانتظار)</b>\n` +
                `🏥 <b>الصيدلية:</b> <code>${pharmacyName}</code> (${shortId})\n` +
                `👨‍⚕️ <b>الدكتور المسؤول:</b> <b>${activeShift.cashierName}</b>\n` +
                `━━━━━━━━━━━━━━━━━━━━\n\n` +
                `🧾 <b>رقم الفاتورة:</b> <code>#${invoiceId}</code>\n` +
                `💳 <b>طريقة الدفع:</b> ${payMethodAr}\n\n` +
                `📦 <b>المنتجات المطلوبة:</b>\n${itemsListStr}\n\n` +
                `💰 <b>تفاصيل الحساب والماليات:</b>\n` +
                `  ├─ المجموع الفرعي: ${subtotal} ج.م\n` +
                `  ├─ الخصم المطبق: ${posDiscount} ج.م\n` +
                (currentPointsDiscount > 0 ? `  ├─ خصم الكاش باك: ${currentPointsDiscount} ج.م\n` : '') +
                (pointsEarned > 0 ? `  ├─ كاش باك مكتسب: ${pointsEarned} ج.م\n` : '') +
                `  ├─ خدمة التوصيل: ${deliveryFee} ج.م\n` +
                `  └─ <b>الإجمالي المطلوب: ${grandTotal} ج.م</b>\n\n` +
                insuranceDetails +
                `👤 <b>بيانات التوصيل والعميل:</b>\n` +
                `  ├─ الاسم: ${customerName || 'غير مسجل'}\n` +
                `  ├─ الهاتف: <code>${customerPhone || 'غير مسجل'}</code>\n` +
                `  ├─ العنوان: 📍 ${deliveryAddress || 'غير مسجل'}\n` +
                `  └─ الطيار المسند: 🚴 <b>${deliveryDriver || 'غير مسجل'}</b>\n\n` +
                `━━━━━━━━━━━━━━━━━━━━\n` +
                `🕊️ <i>نظام دكتور مصر لإدارة الصيدليات</i>`;
        } else {
          msg = `🧾 <b>عملية بيع جديدة</b>\n` +
                `🏥 <b>الصيدلية:</b> <code>${pharmacyName}</code> (${shortId})\n` +
                `👨‍⚕️ <b>الدكتور المسؤول:</b> <b>${activeShift.cashierName}</b>\n` +
                `━━━━━━━━━━━━━━━━━━━━\n\n` +
                `🧾 <b>رقم الفاتورة:</b> <code>#${invoiceId}</code>\n` +
                `💳 <b>طريقة الدفع:</b> ${payMethodAr}\n\n` +
                `📦 <b>الأصناف المباعة:</b>\n${itemsListStr}\n\n` +
                `💰 <b>تفاصيل الحساب والماليات:</b>\n` +
                `  ├─ المجموع الفرعي: ${subtotal} ج.م\n` +
                `  ├─ الخصم المطبق: ${posDiscount} ج.م\n` +
                (currentPointsDiscount > 0 ? `  ├─ خصم الكاش باك: ${currentPointsDiscount} ج.م\n` : '') +
                (pointsEarned > 0 ? `  ├─ كاش باك مكتسب: ${pointsEarned} ج.م\n` : '') +
                `  └─ <b>الإجمالي المطلوب: ${grandTotal} ج.م</b>\n\n` +
                insuranceDetails +
                (customerPhone ? `👤 <b>بيانات العميل:</b>\n  ├─ الاسم: ${customerName || 'غير مسجل'}\n  └─ الهاتف: <code>${customerPhone}</code>\n\n` : '') +
                `━━━━━━━━━━━━━━━━━━━━\n` +
                `🕊️ <i>نظام دكتور مصر لإدارة الصيدليات</i>`;
        }
        sendTelegramMessage(telegramSettings, msg);
      }

    } catch (err) {
      console.error("Checkout error:", err);
      window.triggerDrMasrToast('❌ فشل في إتمام عملية البيع: ' + err.message);
    } finally {
      setLoading(false);
    }
  };


  // Views Router
  const urlParams = new URLSearchParams(window.location.search);
  const shopId = urlParams.get('shop') || resolvedShopId;

  if (shopId) {
    return (
      <React.Suspense fallback={
        <div style={{
          minHeight: '100vh',
          background: "url('/pharmacy_blur_bg.png') no-repeat center center",
          backgroundSize: 'cover',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'Cairo, sans-serif', color: '#0f172a', direction: 'rtl'
        }}>
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img src="/drmasr_logo_uploaded.png" alt="DrMasr" style={{ width: '120px', borderRadius: '24px', marginBottom: '24px', filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.15))', animation: 'pulse 2s infinite ease-in-out' }} />
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: '0 0 8px 0' }}>جاري تحميل متجر الصيدلية...</h3>
            <p style={{ color: 'rgba(15,23,42,0.6)', fontSize: '0.85rem', margin: 0, fontWeight: 600 }}>مزامنة الأدوية والمخزون المباشر...</p>
          </div>
        </div>
      }>
        <CustomerStorefront shopId={shopId} db={db} />
      </React.Suspense>
    );
  }

  if (resolvingShop) {
    return (
      <div style={{
        minHeight: '100vh',
        background: "url('/pharmacy_blur_bg.png') no-repeat center center",
        backgroundSize: 'cover',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Cairo, sans-serif', color: '#0f172a', direction: 'rtl'
      }}>
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img src="/drmasr_logo_uploaded.png" alt="DrMasr" style={{ width: '120px', borderRadius: '24px', marginBottom: '24px', filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.15))', animation: 'pulse 2s infinite ease-in-out' }} />
          <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: '0 0 8px 0' }}>جاري العثور على فرع الصيدلية...</h3>
          <p style={{ color: 'rgba(15,23,42,0.6)', fontSize: '0.85rem', margin: 0, fontWeight: 600 }}>يرجى الانتظار لحين تحميل المتجر الإلكتروني المباشر.</p>
        </div>
      </div>
    );
  }

  const isShopRequested = pathParts[0] === 'shop' || urlParams.has('shop');
  if (isShopRequested && !shopId && !resolvingShop) {
    return (
      <div style={{
        minHeight: '100vh',
        background: "url('/pharmacy_blur_bg.png') no-repeat center center",
        backgroundSize: 'cover',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Cairo, sans-serif', color: '#0f172a', direction: 'rtl', padding: '20px', textAlign: 'center'
      }}>
        <div className="premium-card" style={{ maxWidth: '400px', padding: '30px', border: '1px solid rgba(244, 63, 94, 0.2)', background: 'rgba(255, 255, 255, 0.95)', borderRadius: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.08)' }}>
          <span style={{ fontSize: '3rem' }}>⚠️</span>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: '16px 0 8px', color: '#1e293b' }}>لم يتم العثور على الصيدلية</h3>
          <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
            الرابط الذي قمت بفتحه غير صحيح أو أن الصيدلية غير مسجلة في نظام دكتور مصر. يرجى إعادة مسح رمز QR الصحيح.
          </p>
        </div>
      </div>
    );
  }

  if (showLanding) {
    return (
      <React.Suspense fallback={
        <div style={{
          minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'Cairo, sans-serif', color: '#fff', direction: 'rtl'
        }}>
          <div style={{ textAlign: 'center' }}>
            <img src="/drmasr_logo_uploaded.png" alt="DrMasr" width="80" height="80" className="animate-pulse" style={{ marginBottom: '16px' }} />
            <div>جاري تحميل الصفحة الرئيسية...</div>
          </div>
        </div>
      }>
        <LandingPage onStartClick={() => {
          window.history.pushState({}, '', '/');
          setShowLanding(false);
        }} />
      </React.Suspense>
    );
  }

 if (!user) {
 return (
 <LoginView
 setUser={setUser}
 setCurrentUserData={setCurrentUserData}
 setErrorMessage={setErrorMessage}
 errorMessage={errorMessage}
 />
 );
 }

 if (userProfileLoading) {
   return (
     <div style={{
       minHeight: '100vh', background: 'radial-gradient(circle at center, #0a0f1d 0%, #030712 100%)',
       display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
       fontFamily: 'Cairo, sans-serif', color: '#fff', direction: 'rtl'
     }}>
       <div style={{ textAlign: 'center' }}>
         <div className="pm-logo-ring-wrapper" style={{ margin: '0 auto 20px' }}>
           <div className="pm-logo-glow-ring" style={{ border: '4px solid var(--primary)', borderTopColor: 'transparent', width: '48px', height: '48px' }}></div>
         </div>
         <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: '0 0 8px 0' }}>جاري تحميل ملف الصيدلية...</h3>
         <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', margin: 0 }}>يرجى الانتظار لاستعادة تفاصيل اشتراكك ومستوى الأمان.</p>
       </div>
     </div>
   );
 }

  // Google Sign-In New User Complete Setup Check
  const isGoogleSetupIncomplete = user && currentUserData && !currentUserData.adminPin;

  if (isGoogleSetupIncomplete) {
    const showOwnerInput = !currentUserData?.ownerName;
    const showPharmacyInput = !currentUserData?.pharmacyName;
    const showPhoneInput = !currentUserData?.phone;

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'radial-gradient(circle at center, #004d49 0%, #002220 40%, #010507 100%)', padding: '24px', direction: 'rtl', fontFamily: 'Cairo, sans-serif' }}>
        <div className="premium-card fade-in-content" style={{ maxWidth: '500px', width: '100%', border: '1.5px solid #2563eb', padding: '36px', borderRadius: '16px', background: 'rgba(13, 20, 36, 0.85)', backdropFilter: 'blur(16px)', boxShadow: '0 0 25px rgba(37, 99, 235, 0.25), inset 0 0 12px rgba(37, 99, 235, 0.05)' }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <img src="/drmasr_logo_transparent.png" alt="دكتور مصر" width="70" height="70" style={{ height: '70px', width: '70px', objectFit: 'contain', marginBottom: '12px' }} />
            <h3 className="gradient-text" style={{ fontSize: '1.65rem', fontWeight: 800, marginBottom: '8px' }}>أكمل إعداد حسابك بلمسة واحدة</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: '1.6', margin: 0 }}>
              أهلاً بك في عائلة دكتور مصر! يرجى إدخال البيانات المتبقية لحماية وتخصيص حساب صيدليتك بالكامل.
            </p>
          </div>

          <form onSubmit={handleCompleteGoogleSetup} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {setupError && (
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--accent-pink)', padding: '12px', borderRadius: '12px', color: '#f87171', fontSize: '0.9rem', textAlign: 'center' }}>
                {setupError}
              </div>
            )}

            {showOwnerInput && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>اسم صاحب الصيدلية (المالك):</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  style={{ fontSize: '0.95rem', padding: '10px 14px' }}
                  placeholder="أدخل اسمك بالكامل" 
                  value={setupOwnerName} 
                  onChange={e => { setSetupError(''); setSetupOwnerName(e.target.value); }} 
                  required 
                />
              </div>
            )}

            {showPharmacyInput && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>اسم الصيدلية:</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  style={{ fontSize: '0.95rem', padding: '10px 14px' }}
                  placeholder="مثال: صيدلية النور" 
                  value={setupPharmacyName} 
                  onChange={e => { setSetupError(''); setSetupPharmacyName(e.target.value); }} 
                  required 
                />
              </div>
            )}

            {showPhoneInput && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>رقم هاتف الصيدلية:</label>
                <input 
                  type="tel" 
                  className="premium-input" 
                  style={{ fontSize: '0.95rem', padding: '10px 14px' }}
                  placeholder="مثال: 01xxxxxxxxx" 
                  value={setupPhone} 
                  onChange={e => { setSetupError(''); setSetupPhone(e.target.value.replace(/\D/g, '')); }} 
                  required 
                />
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>رمز الـ Admin PIN للمالك (4 أرقام):</label>
              <input
                type="password"
                maxLength={4}
                className="premium-input"
                placeholder="••••"
                style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '8px', padding: '8px 14px' }}
                value={setupPin}
                onChange={e => {
                  setSetupError('');
                  setSetupPin(e.target.value.replace(/\D/g, ''));
                }}
                required
              />
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: '1.5', marginTop: '4px' }}>
                <strong>أهمية رمز الـ PIN:</strong> هو الرمز الذي يحمي كافة بياناتك المالية الحساسة، الأرباح، والتقارير المالية والورديات السابقة من تطفل الموظفين أو الكاشير ولا يمكن لأحد تعديلها أو الدخول إليها سواك.
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>برومو كود / كود ترويجي (اختياري):</label>
              <input 
                type="text" 
                className="premium-input" 
                style={{ fontSize: '0.95rem', padding: '10px 14px' }}
                placeholder="أدخل كود الخصم أو التفعيل إن وجد" 
                value={setupPromoCode} 
                onChange={e => { setSetupError(''); setSetupPromoCode(e.target.value); }} 
              />
            </div>

            <button type="submit" className="gradient-btn" style={{ padding: '14px', marginTop: '8px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', transition: 'background 0.2s' }}>
              حفظ البيانات والمتابعة للنظام
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (deviceLimitExceeded) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'radial-gradient(circle at center, #004d49 0%, #002220 40%, #010507 100%)', padding: '24px', direction: 'rtl', fontFamily: 'Cairo, sans-serif' }}>
        <div className="premium-card fade-in-content" style={{ maxWidth: '500px', width: '100%', border: '1.5px solid var(--accent-pink)', padding: '36px', borderRadius: '16px', background: 'rgba(13, 20, 36, 0.85)', backdropFilter: 'blur(16px)', boxShadow: '0 0 25px rgba(239, 68, 68, 0.25), inset 0 0 12px rgba(239, 68, 68, 0.05)' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '1.65rem', fontWeight: 800, color: '#f87171', marginBottom: '8px' }}>تجاوزت الحد الاقصى للاجهزة</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: '1.6', margin: 0 }}>
              عذرا، هذا الحساب مسجل دخول حاليا من الحد الاقصى المسموح به من الاجهزة النشطة في نفس الوقت.
            </p>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button 
              onClick={handleOverrideDevices} 
              className="gradient-btn" 
              style={{ width: '100%', padding: '14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', transition: 'background 0.2s' }}
            >
              الغاء ارتباط الاجهزة الاخرى وتفعيل هذا الجهاز
            </button>
            <button 
              onClick={handleLogout} 
              style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '10px', fontWeight: 'bold', fontSize: '0.95rem', cursor: 'pointer', transition: 'background 0.2s' }}
            >
              تسجيل الخروج
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 3. One-time Setup Screen if pharmacy name is not set
  if (!currentUserData?.pharmacyName) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg-main)', padding: '24px', direction: 'rtl' }}>
        <div className="premium-card fade-in-content" style={{ maxWidth: '550px', width: '100%', border: '1px solid var(--primary)', padding: '32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <img src="/drmasr_logo_uploaded.png" alt="دكتور مصر" width="70" height="70" style={{ height: '70px', width: '70px', objectFit: 'contain', marginBottom: '16px' }} />
            <h2 className="gradient-text" style={{ fontSize: '1.75rem', fontWeight: 800 }}>أهلاً بك في نظام دكتور مصر</h2>
            <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>يرجى إكمال البيانات الأساسية لتفعيل نظام إدارة صيدليتك.</p>
          </div>

          <form onSubmit={handleSetupSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {setupError && <div style={{ color: '#f87171', background: 'rgba(239, 68, 68, 0.1)', padding: '10px', borderRadius: '8px', textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}>{setupError}</div>}
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: '#fff', fontWeight: 700 }}> اسم صاحب الصيدلية (المالك):</label>
              <input 
                type="text" 
                className="premium-input" 
                placeholder="أدخل اسمك بالكامل" 
                value={setupOwnerName} 
                onChange={e => { setSetupError(''); setSetupOwnerName(e.target.value); }} 
                required 
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: '#fff', fontWeight: 700 }}> رمز الـ Admin PIN المكون من 4 أرقام:</label>
              <input 
                type="text" 
                maxLength={4} 
                className="premium-input" 
                style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '8px' }} 
                placeholder="••••" 
                value={setupPin} 
                onChange={e => { setSetupError(''); setSetupPin(e.target.value.replace(/\D/g, '')); }} 
                required 
              />
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: '1.5', marginTop: '4px' }}>
                <strong>أهمية رمز الـ PIN:</strong> هو الرمز الذي يحمي كافة بياناتك المالية الحساسة، الأرباح، والتقارير المالية والورديات السابقة من تطفل الموظفين أو الكاشير ولا يمكن لأحد تعديلها أو الدخول إليها سواك.
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: '#fff', fontWeight: 700 }}> اسم الصيدلية:</label>
              <input 
                type="text" 
                className="premium-input" 
                placeholder="مثال: صيدلية النور" 
                value={setupPharmacyName} 
                onChange={e => { setSetupError(''); setSetupPharmacyName(e.target.value); }} 
                required 
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: '#fff', fontWeight: 700 }}>رقم هاتف الصيدلية:</label>
              <input 
                type="tel" 
                className="premium-input" 
                placeholder="مثال: 01xxxxxxxxx" 
                value={setupPhone} 
                onChange={e => { setSetupError(''); setSetupPhone(e.target.value.replace(/\D/g, '')); }} 
                required 
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: '#fff', fontWeight: 700 }}>برومو كود / كود ترويجي (اختياري):</label>
              <input 
                type="text" 
                className="premium-input" 
                placeholder="أدخل كود الخصم أو التفعيل إن وجد" 
                value={setupPromoCode} 
                onChange={e => { setSetupError(''); setSetupPromoCode(e.target.value); }} 
              />
            </div>

            <button type="submit" className="gradient-btn" style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 700, marginTop: '10px' }}>
              حفظ البيانات والمتابعة للنظام
            </button>
            <button 
              type="button"
              onClick={handleLogout} 
              style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '10px', fontWeight: 'bold', fontSize: '0.95rem', cursor: 'pointer', marginTop: '10px', transition: 'background 0.2s' }}
            >
              تسجيل الخروج
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Clock Tampering Check
  if (isClockTampered) {
    return (
      <div 
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: '#0f172a',
          color: '#fff',
          fontFamily: 'Cairo, sans-serif',
          padding: '20px',
          textAlign: 'center'
        }}
      >
        <div style={{ fontSize: '4.5rem', marginBottom: '1.5rem', animation: 'pulse 2s infinite' }}>⏰</div>
        <h2 style={{ fontSize: '1.8rem', fontWeight: 900, color: '#f87171', marginBottom: '1rem' }}>تم اكتشاف تلاعب في تاريخ الجهاز!</h2>
        <p style={{ color: '#94a3b8', fontSize: '1.1rem', maxWidth: '500px', lineHeight: '1.8', marginBottom: '1.5rem' }}>
          لقد تم اكتشاف أن تاريخ أو وقت جهاز الكمبيوتر غير دقيق أو تم إرجاعه للخلف. يرجى ضبط الساعة والتاريخ الحاليين وتحديث الصفحة للمتابعة.
        </p>
        <button 
          onClick={() => window.location.reload()} 
          style={{
            padding: '12px 30px',
            background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
            border: 'none',
            color: '#fff',
            borderRadius: '10px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)'
          }}
        >
          أعد المحاولة 🔄
        </button>
      </div>
    );
  }

  // Subscription Expiry Locked Screen Check
  const daysRemainingVal = getSubscriptionDaysRemaining(currentUserData);
  const isExpired = daysRemainingVal !== 'lifetime' && Number(daysRemainingVal) <= 0;

  if (isExpired) {
    return (
      <SubscriptionExpiredView 
        currentUserData={currentUserData} 
        handleLogout={handleLogout} 
      />
    );
  }

  if (isInventoryLoading) {
    return (
      <div className="pm-loading-overlay">
        <div className="pm-loading-card">
          <div className="pm-logo-ring-wrapper">
            <div className="pm-logo-glow-ring"></div>
            <div className="pm-logo-glow-ring-inner"></div>
            <div className="pm-logo-container">
              <img 
                src="/drmasr_logo_uploaded.png" 
                alt="Play Masr" 
                className="pm-logo-img"
                width="75"
                height="75"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentNode;
                  if (parent && !parent.querySelector('.pm-logo-fallback')) {
                    const fallback = document.createElement('div');
                    fallback.className = 'pm-logo-fallback';
                    fallback.innerText = 'P';
                    fallback.style.fontSize = '3.5rem';
                    fallback.style.fontWeight = '900';
                    fallback.style.color = '#fff';
                    fallback.style.background = 'linear-gradient(135deg, var(--primary) 0%, #1e40af 100%)';
                    fallback.style.width = '100%';
                    fallback.style.height = '100%';
                    fallback.style.display = 'flex';
                    fallback.style.alignItems = 'center';
                    fallback.style.justifyContent = 'center';
                    fallback.style.textShadow = '0 0 10px rgba(59, 130, 246, 0.5)';
                    parent.appendChild(fallback);
                  }
                }}
              />
            </div>
          </div>
          <h3 className="pm-loading-title">
            <span style={{ fontSize: '1.4rem' }}>🔄</span> جاري تحميل البيانات...
          </h3>
          <p className="pm-loading-subtitle">
            يرجى الانتظار قليلاً بينما نقوم بمزامنة بيانات المخزون بأمان وسرعة من السحابة.
          </p>
          <div className="pm-pulse-badge">
            <span>🛡️</span> بوابة دكتور مصر الرقمية
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="layout-wrapper fade-in-content">

      {/* ── Mobile Header ─────────────────────────────────────────── */}
      <div className="mobile-header">
        <div className="mobile-header-logo" onClick={() => setProfileMenuOpen(!profileMenuOpen)} style={{ cursor: 'pointer', position: 'relative' }}>
          <img src="/drmasr_logo_uploaded.png" alt="دكتور مصر" width="30" height="30" style={{ height: '30px', width: '30px', objectFit: 'contain', borderRadius: '6px' }} />
          <span className="mobile-header-pharmacy-name">
            {currentUserData?.pharmacyName || 'دكتور مصر'}
          </span>
          {profileMenuOpen && (
            <div 
              className="profile-dropdown"
              style={{
                width: '280px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.7)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}
            >
              <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff', textAlign: 'right' }}>{currentUserData?.pharmacyName || 'صيدلية النور'}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', direction: 'ltr', textAlign: 'left' }}>{currentUserData?.email || user?.email || ''}</div>
              
              {/* Short ID Card */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                background: 'rgba(59, 130, 246, 0.08)', 
                padding: '8px 12px', 
                borderRadius: '10px',
                border: '1px dashed rgba(59, 130, 246, 0.25)',
                fontSize: '0.85rem',
                direction: 'rtl'
              }}>
                <span style={{ color: 'var(--text-secondary)' }}>كود العميل (Short ID):</span>
                <span style={{ fontWeight: 'bold', color: 'var(--primary)', fontFamily: 'monospace' }}>{currentUserData?.shortId || 'غير متوفر'}</span>
              </div>

              {/* Plan Card */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '4px', 
                background: 'rgba(16, 185, 129, 0.08)', 
                padding: '8px 12px', 
                borderRadius: '10px',
                border: '1px dashed rgba(16, 185, 129, 0.25)',
                fontSize: '0.85rem',
                direction: 'rtl',
                textAlign: 'right'
              }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>خطة العمل النشطة:</span>
                <span style={{ 
                  fontWeight: 'bold', 
                  color: currentUserData?.plan === 'lifetime' ? 'var(--accent-green)' : (currentUserData?.plan === 'subscribe' ? 'var(--medical-teal)' : '#38bdf8'),
                  fontSize: '0.92rem',
                  marginTop: '2px'
                }}>
                  {currentUserData?.plan === 'lifetime' ? 'مدى الحياة (lifetime) 👑' : (currentUserData?.plan === 'subscribe' ? 'مدفوعة (subscribe)' : 'تجريبية (trial)')}
                </span>
              </div>

              <div style={{ height: '1px', background: 'var(--border)' }} />
              <div onClick={(e) => { e.stopPropagation(); setProfileMenuOpen(false); handleLogout(); }} style={{ color: '#ef4444', fontWeight: 'bold', cursor: 'pointer', padding: '10px', background: 'rgba(239, 68, 68, 0.08)', borderRadius: '10px', textAlign: 'center', transition: 'all 0.2s ease' }}>تسجيل الخروج</div>
            </div>
          )}
        </div>
        <div className="mobile-header-actions">
          {/* Shift status pill */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            background: activeShift ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${activeShift ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.2)'}`,
            borderRadius: '20px', padding: '4px 10px',
            fontSize: '0.72rem', fontWeight: 700,
            color: activeShift ? '#34d399' : '#f87171'
          }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
            {activeShift ? activeShift.cashierName.split(' ')[0] : 'لا وردية'}
          </div>

          {/* Unified Notification Bell */}
          {(() => {
            const totalNotificationsCount = (unreadAnnouncementsCount || 0) + (notificationCount || 0);
            return (
              <div 
                onClick={() => {
                  setShowAnnouncementsModal(true);
                  const allIds = announcements.map(a => a.id);
                  localStorage.setItem('drmasr_read_announcements_' + user?.uid, JSON.stringify(allIds));
                  setUnreadAnnouncementsCount(0);
                }}
                className="mobile-action-btn"
                title="الإشعارات"
              >
                <Bell size={18} strokeWidth={2.5} className={totalNotificationsCount > 0 ? "bell-animate" : ""} />
                {totalNotificationsCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-3px',
                    right: '-3px',
                    background: '#ef4444',
                    color: '#fff',
                    borderRadius: '50%',
                    minWidth: '15px',
                    height: '15px',
                    fontSize: '0.62rem',
                    fontWeight: '900',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 3px',
                    border: '1px solid var(--bg-card)'
                  }}>
                    {totalNotificationsCount}
                  </span>
                )}
              </div>
            );
          })()}

          {/* Cart Icon Button (Mobile Only) */}
          {cart.length > 0 && (
            <div 
              onClick={() => setIsMobileCheckoutOpen(true)}
              className="mobile-action-btn"
              style={{
                background: 'rgba(59, 130, 246, 0.15)',
                borderColor: 'var(--primary)',
                color: 'var(--primary)',
                position: 'relative'
              }}
              title="سلة المشتريات"
            >
              <ShoppingCart size={18} strokeWidth={2.5} />
              <span style={{
                position: 'absolute',
                top: '-3px',
                right: '-3px',
                background: '#ef4444',
                color: '#fff',
                borderRadius: '50%',
                minWidth: '15px',
                height: '15px',
                fontSize: '0.62rem',
                fontWeight: '900',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 3px',
                border: '1px solid var(--bg-card)'
              }}>
                {cart.reduce((acc, item) => acc + item.cartQuantity, 0)}
              </span>
            </div>
          )}

          {/* Online Orders Action Button */}
          <div 
            onClick={() => {
              setActiveTab('online_orders');
              setMobileMenuOpen(false);
            }}
            className="mobile-action-btn"
            style={{
              background: activeTab === 'online_orders'
                ? 'rgba(59, 130, 246, 0.15)'
                : (notificationCount > 0 ? 'rgba(59, 130, 246, 0.1)' : undefined),
              borderColor: activeTab === 'online_orders'
                ? 'var(--primary)'
                : (notificationCount > 0 ? 'rgba(59, 130, 246, 0.3)' : undefined),
              color: activeTab === 'online_orders' || notificationCount > 0 ? 'var(--primary)' : undefined
            }}
            title="الطلبات الأونلاين"
          >
            <Globe size={18} strokeWidth={2.5} />
            {notificationCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-3px',
                right: '-3px',
                background: '#3b82f6',
                color: '#fff',
                borderRadius: '50%',
                minWidth: '15px',
                height: '15px',
                fontSize: '0.62rem',
                fontWeight: '900',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 3px',
                border: '1px solid var(--bg-card)'
              }}>
                {notificationCount}
              </span>
            )}
          </div>

          <button className="mobile-toggle-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="القائمة">
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* ── Full-screen sidebar overlay (tap to close) ────────────── */}
      <div
        className={`mobile-sidebar-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* ── Sidebar Navigation ────────────────────────────────────── */}
      <div className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div>
          {/* Sidebar header: logo + close btn */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px 24px 16px' }}>
            <img src="/drmasr_logo_uploaded.png" alt="Doctor Masr Logo" width="90" height="36" style={{ height: '36px', width: 'auto', objectFit: 'contain' }} />
            <button
              className="sidebar-close-btn"
              onClick={() => setMobileMenuOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'var(--text-secondary)',
                width: '34px', height: '34px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1rem',
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}
            >✕</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div className={`sidebar-link ${activeTab === 'pos' ? 'active' : ''}`} onClick={() => { setActiveTab('pos'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'واجهة الكاشير (POS)' : 'Cashier POS'}
            </div>
            <div className={`sidebar-link ${activeTab === 'inventory' ? 'active' : ''}`} onClick={() => { setActiveTab('inventory'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'إدارة المخزون' : 'Inventory'}
            </div>
            <div className={`sidebar-link ${activeTab === 'purchases' ? 'active' : ''}`} onClick={() => { setActiveTab('purchases'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'المشتريات والموردين' : 'Purchases & Suppliers'}
            </div>
            <div className={`sidebar-link ${activeTab === 'discounts' ? 'active' : ''}`} onClick={() => { setActiveTab('discounts'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'الخصومات والبروموكود' : 'Discounts & Promo'}
            </div>
            <div className={`sidebar-link ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => { setActiveTab('reports'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'التقارير والمبيعات' : 'Reports & Sales'}
            </div>
            <div className={`sidebar-link ${activeTab === 'shifts' ? 'active' : ''}`} onClick={() => { setActiveTab('shifts'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'سجل الورديات' : 'Shifts Record'}
            </div>
            <div className={`sidebar-link ${activeTab === 'customers' ? 'active' : ''}`} onClick={() => { setActiveTab('customers'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'إدارة العملاء والـ CRM' : 'Customers & CRM'}
            </div>
            <div className={`sidebar-link ${activeTab === 'delivery' ? 'active' : ''}`} onClick={() => { setActiveTab('delivery'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'الدليفري' : 'Delivery'}
              {sales.filter(s => s.isDelivery && (s.deliveryStatus === 'pending' || s.deliveryStatus === 'out')).length > 0 && (
                <span className="badge-count purple" style={{ marginRight: 'auto' }}>
                  {sales.filter(s => s.isDelivery && (s.deliveryStatus === 'pending' || s.deliveryStatus === 'out')).length}
                </span>
              )}
            </div>
            <div className={`sidebar-link ${activeTab === 'online_orders' ? 'active' : ''}`} onClick={() => { setActiveTab('online_orders'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'الطلبات الأونلاين' : 'Online Orders'}
              {onlineOrders.length > 0 && (
                <span className="badge-count" style={{ marginRight: 'auto', background: 'var(--primary)', color: '#fff', boxShadow: '0 0 10px var(--primary)' }}>
                  {onlineOrders.length}
                </span>
              )}
            </div>
            <div className={`sidebar-link ${activeTab === 'eta' ? 'active' : ''}`} onClick={() => { setActiveTab('eta'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'إعدادات الضرائب (ETA)' : 'Tax Settings (ETA)'}
            </div>
            <div className={`sidebar-link ${activeTab === 'insurance' ? 'active' : ''}`} onClick={() => { setActiveTab('insurance'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'إدارة التأمين والنقابات' : 'Insurance & Syndicates'}
            </div>
            <div className={`sidebar-link ${activeTab === 'employees' ? 'active' : ''}`} onClick={() => { setActiveTab('employees'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? '👨‍⚕️ الموظفين والرواتب والصلاحيات' : '👨‍⚕️ Employees & Salaries'}
            </div>
            <div className={`sidebar-link ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => { setActiveTab('settings'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'إعدادات النظام' : 'System Settings'}
            </div>
            <div className={`sidebar-link ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => { setActiveTab('profile'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'الملف الشخصي' : 'Profile'}
            </div>
            <div className={`sidebar-link ${activeTab === 'subscription' ? 'active' : ''}`} onClick={() => { setActiveTab('subscription'); setMobileMenuOpen(false); }}>
              {language === 'ar' ? 'الاشتراكات' : 'Subscriptions'}
            </div>
            {user?.email?.toLowerCase() === 'baselsalah07@gmail.com' && (
              <div className={`sidebar-link ${activeTab === 'calls' ? 'active' : ''}`} onClick={() => { setActiveTab('calls'); setMobileMenuOpen(false); }} style={{ color: 'var(--accent-gold)', borderRight: activeTab === 'calls' ? '4px solid var(--accent-gold)' : undefined }}>
                {language === 'ar' ? '📞 متابعة المكالمات' : '📞 Calls Monitoring'}
              </div>
            )}

          </div>
        </div>

        <div>
          <div onClick={() => { setIsShiftDialogOpen(true); setMobileMenuOpen(false); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px', background: 'rgba(51, 65, 85, 0.3)', borderRadius: '12px', marginBottom: '16px', cursor: 'pointer', border: '1px solid var(--border)' }} title={language === 'ar' ? 'إدارة الوردية' : 'Shift Management'}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{language === 'ar' ? 'المستلم الحالي (الوردية النشطة):' : 'Active Shift Receiver:'}</span>
            <span style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--accent-green)' }}>
              {activeShift?.cashierName || (language === 'ar' ? "لا يوجد وردية نشطة" : "No Active Shift")}
            </span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '-8px' }}>
              {currentUserData?.pharmacyName || "صيدلية دكتور مصر"}
            </span>
            {(() => {
              const days = getSubscriptionDaysRemaining(currentUserData);
              if (days === 'lifetime') {
                return (
                  <div className="trial-badge" style={{ background: 'rgba(59, 130, 246, 0.15)', border: '1px solid var(--accent-green)', color: '#34d399' }}>
                    {language === 'ar' ? 'اشتراك مدى الحياة' : 'Lifetime Subscription'}
                  </div>
                );
              }
              const daysNum = Number(days);
              const isWarning = daysNum <= 4;
              return (
                <div 
                  className="trial-badge" 
                  style={isWarning ? {
                    background: 'rgba(234, 179, 8, 0.15)', 
                    border: '1px solid rgba(234, 179, 8, 0.35)', 
                    color: '#facc15'
                  } : {
                    background: 'rgba(16, 185, 129, 0.15)', 
                    border: '1px solid rgba(16, 185, 129, 0.35)', 
                    color: '#34d399'
                  }}
                >
                  {days} {language === 'ar' ? 'أيام متبقية' : 'Days left'}
                </div>
              );
            })()}
          </div>


          <div className="sidebar-link" onClick={() => { handleLogout(); setMobileMenuOpen(false); }} style={{ color: 'var(--accent-pink)' }}>
            {language === 'ar' ? 'تسجيل الخروج' : 'Logout'}
          </div>
        </div>
      </div>

      {/* Main Content Body */}
      <div className="main-content">
        <div className="top-bar" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          width: '100%', 
          marginBottom: '20px',
          gap: '16px'
        }}>
          {/* Cloud Sync Status Indicator */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            borderRadius: '20px',
            background: isOnlineState ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: isOnlineState ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid rgba(239, 68, 68, 0.2)',
            color: isOnlineState ? '#34d399' : '#f87171',
            fontSize: '0.85rem',
            fontWeight: 'bold',
            userSelect: 'none'
          }}>
            <span style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: isOnlineState ? '#10b981' : '#ef4444',
              boxShadow: isOnlineState ? '0 0 8px #10b981' : '0 0 8px #ef4444'
            }}></span>
            {isOnlineState ? '🌐 متصل سحابياً (مزامنة فورية آمنة)' : '⚠️ وضع الأوفلاين (البيانات محفوظة محلياً)'}
          </div>
          


          {/* Left Group (Identity & Administration) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            
            
            {/* Elegant Dynamic Language Toggle (EN/AR) */}
            <div 
              className="desktop-only"
              onClick={() => handleLanguageChange(language === 'ar' ? 'en' : 'ar')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'rgba(99, 102, 241, 0.08)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                userSelect: 'none',
                boxShadow: '0 0 10px rgba(99, 102, 241, 0.08)',
                marginRight: '6px',
                fontSize: '0.82rem',
                fontWeight: 'bold'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(99, 102, 241, 0.15)';
                e.currentTarget.style.transform = 'scale(1.06)';
                e.currentTarget.style.border = '1px solid rgba(99, 102, 241, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(99, 102, 241, 0.08)';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.border = '1px solid rgba(99, 102, 241, 0.25)';
              }}
              title={language === 'ar' ? "Switch to English" : "تغيير للعربية"}
            >
              {language === 'ar' ? 'EN' : 'AR'}
            </div>

            {/* Elegant 2D Minimalist Theme Toggle */}
            <div 
              className="desktop-only"
              onClick={() => setIsDarkMode(!isDarkMode)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: isDarkMode ? 'rgba(251, 191, 36, 0.08)' : 'rgba(99, 102, 241, 0.08)',
                border: isDarkMode ? '1px solid rgba(251, 191, 36, 0.25)' : '1px solid rgba(99, 102, 241, 0.25)',
                color: isDarkMode ? '#fbbf24' : '#6366f1',
                cursor: 'pointer',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                userSelect: 'none',
                boxShadow: isDarkMode ? '0 0 10px rgba(251, 191, 36, 0.08)' : '0 0 10px rgba(99, 102, 241, 0.08)',
                marginRight: '6px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode ? 'rgba(251, 191, 36, 0.15)' : 'rgba(99, 102, 241, 0.15)';
                e.currentTarget.style.transform = 'scale(1.06) rotate(15deg)';
                e.currentTarget.style.border = isDarkMode ? '1px solid rgba(251, 191, 36, 0.5)' : '1px solid rgba(99, 102, 241, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isDarkMode ? 'rgba(251, 191, 36, 0.08)' : 'rgba(99, 102, 241, 0.08)';
                e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                e.currentTarget.style.border = isDarkMode ? '1px solid rgba(251, 191, 36, 0.25)' : '1px solid rgba(99, 102, 241, 0.25)';
              }}
              title={isDarkMode ? "الوضع المضيء" : "الوضع المظلم"}
            >
              {isDarkMode ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="4"/>
                  <path d="M12 2v2"/>
                  <path d="M12 20v2"/>
                  <path d="m4.93 4.93 1.41 1.41"/>
                  <path d="m17.66 17.66 1.41 1.41"/>
                  <path d="M2 12h2"/>
                  <path d="M20 12h2"/>
                  <path d="m6.34 17.66-1.41 1.41"/>
                  <path d="m19.07 4.93-1.41 1.41"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
                </svg>
              )}
            </div>

            {/* Unified Notification Bell */}
            {(() => {
              const totalNotificationsCount = (unreadAnnouncementsCount || 0) + (notificationCount || 0);
              return (
                <div 
                  className="desktop-only"
                  onClick={() => {
                    setShowAnnouncementsModal(true);
                    const allIds = announcements.map(a => a.id);
                    localStorage.setItem('drmasr_read_announcements_' + user.uid, JSON.stringify(allIds));
                    setUnreadAnnouncementsCount(0);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: totalNotificationsCount > 0 
                      ? (isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)')
                      : (isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'),
                    border: totalNotificationsCount > 0 
                      ? (isDarkMode ? '1px solid rgba(239, 68, 68, 0.35)' : '1px solid rgba(239, 68, 68, 0.25)')
                      : (isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)'),
                    color: totalNotificationsCount > 0 
                      ? '#ef4444' 
                      : 'var(--text-secondary)',
                    cursor: 'pointer',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    userSelect: 'none',
                    position: 'relative',
                    marginRight: '6px',
                    boxShadow: totalNotificationsCount > 0 
                      ? (isDarkMode ? '0 0 12px rgba(239, 68, 68, 0.2)' : '0 0 12px rgba(239, 68, 68, 0.1)') 
                      : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = totalNotificationsCount > 0 
                      ? (isDarkMode ? 'rgba(239, 68, 68, 0.25)' : 'rgba(239, 68, 68, 0.15)')
                      : (isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)');
                    e.currentTarget.style.transform = 'scale(1.06) rotate(15deg)';
                    e.currentTarget.style.border = totalNotificationsCount > 0 
                      ? (isDarkMode ? '1px solid rgba(239, 68, 68, 0.6)' : '1px solid rgba(239, 68, 68, 0.5)')
                      : (isDarkMode ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.15)');
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = totalNotificationsCount > 0 
                      ? (isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)')
                      : (isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)');
                    e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                    e.currentTarget.style.border = totalNotificationsCount > 0 
                      ? (isDarkMode ? '1px solid rgba(239, 68, 68, 0.35)' : '1px solid rgba(239, 68, 68, 0.25)')
                      : (isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)');
                  }}
                  title="التنبيهات والإشعارات"
                >
                  <Bell size={18} strokeWidth={2.5} className={totalNotificationsCount > 0 ? "bell-animate" : ""} />
                  
                  {totalNotificationsCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      background: '#ef4444',
                      color: '#fff',
                      borderRadius: '50%',
                      minWidth: '16px',
                      height: '16px',
                      fontSize: '0.65rem',
                      fontWeight: '900',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                      boxShadow: '0 0 8px rgba(239, 68, 68, 0.5)',
                      border: '1px solid var(--bg-card)'
                    }}>
                      {totalNotificationsCount}
                    </span>
                  )}
                </div>
              );
            })()}
            
            {/* 2. Admin Mode Button */}
            <div 
              onClick={() => {
                if (!currentUserData?.adminPin) return;
                if (isAdminVerified) setIsAdminVerified(false);
                else setIsAdminPinModalOpen(true);
              }}
              onMouseEnter={() => setIsAdminHovered(true)}
              onMouseLeave={() => setIsAdminHovered(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                // Premium gradient backgrounds with glassmorphism
                background: isAdminVerified 
                  ? (isAdminHovered 
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(5, 150, 105, 0.35) 100%)' 
                    : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.2) 100%)')
                  : (isAdminHovered 
                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)' 
                    : 'rgba(255, 255, 255, 0.03)'),
                // Glassmorphic borders
                border: isAdminVerified 
                  ? '1px solid rgba(16, 185, 129, 0.6)' 
                  : (isAdminHovered 
                    ? '1px solid rgba(99, 102, 241, 0.5)' 
                    : '1px solid rgba(255, 255, 255, 0.08)'),
                // Glowing text shadow and colors
                color: isAdminVerified 
                  ? '#34d399' 
                  : (isAdminHovered ? '#fff' : 'var(--text-secondary)'),
                textShadow: isAdminHovered 
                  ? (isAdminVerified ? '0 0 8px rgba(52, 211, 153, 0.4)' : '0 0 8px rgba(99, 102, 241, 0.4)')
                  : 'none',
                padding: '8px 18px',
                borderRadius: '30px',
                fontWeight: '700',
                cursor: 'pointer',
                // High premium shadows
                boxShadow: isAdminVerified
                  ? (isAdminHovered 
                    ? '0 0 20px rgba(16, 185, 129, 0.4), inset 0 0 10px rgba(16, 185, 129, 0.2)' 
                    : '0 0 10px rgba(16, 185, 129, 0.15), inset 0 0 5px rgba(16, 185, 129, 0.1)')
                  : (isAdminHovered 
                    ? '0 0 20px rgba(99, 102, 241, 0.25), inset 0 0 8px rgba(255, 255, 255, 0.05)' 
                    : 'inset 0 1px 1px rgba(255, 255, 255, 0.05)'),
                // Micro-interactions and scales
                transform: isAdminHovered ? 'scale(1.04) translateY(-1px)' : 'scale(1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                // Selection prevention
                userSelect: 'none'
              }}
            >
              <span style={{ 
                fontSize: '0.9rem', 
                letterSpacing: '0.3px',
                fontFamily: 'Tajawal, system-ui, sans-serif'
              }}>
                {isAdminVerified ? 'مسؤول نشط' : 'دخول كمسؤول'}
              </span>
              <span style={{ 
                fontSize: '1.1rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                filter: isAdminHovered 
                  ? (isAdminVerified ? 'drop-shadow(0 0 6px rgba(52, 211, 153, 0.8))' : 'drop-shadow(0 0 6px rgba(99, 102, 241, 0.8))')
                  : 'none',
                transform: isAdminHovered && isAdminVerified ? 'rotate(360deg)' : 'none',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                🛡️
              </span>
            </div>

            {/* 1. Pharmacy Profile Dropdown */}
            <div className="desktop-only" style={{ position: 'relative' }}>
              <div 
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                onMouseEnter={() => setIsPharmacyHovered(true)}
                onMouseLeave={() => setIsPharmacyHovered(false)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  // Glassmorphism and premium gradients
                  background: isPharmacyHovered 
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(99, 102, 241, 0.2) 100%)' 
                    : 'rgba(59, 130, 246, 0.08)',
                  border: isPharmacyHovered 
                    ? '1px solid rgba(59, 130, 246, 0.6)' 
                    : '1px solid rgba(59, 130, 246, 0.25)',
                  padding: '6px 16px',
                  borderRadius: '30px',
                  fontWeight: '800',
                  color: 'var(--primary)',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  boxShadow: isPharmacyHovered 
                    ? '0 0 15px rgba(59, 130, 246, 0.25), inset 0 0 8px rgba(255, 255, 255, 0.05)' 
                    : 'none',
                  transform: isPharmacyHovered ? 'scale(1.03) translateY(-1px)' : 'scale(1)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  userSelect: 'none'
                }}
              >
                <span style={{ 
                  fontFamily: 'Tajawal, system-ui, sans-serif',
                  letterSpacing: '0.3px'
                }}>{currentUserData?.pharmacyName || 'صيدلية النور'}</span>
                <span style={{
                  background: 'linear-gradient(135deg, var(--primary) 0%, #1e40af 100%)',
                  color: '#fff',
                  borderRadius: '50%',
                  width: '22px',
                  height: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 6px rgba(59, 130, 246, 0.4)',
                  transform: isPharmacyHovered ? 'rotate(360deg)' : 'none',
                  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>{currentUserData?.pharmacyName?.[0]?.toUpperCase() || 'P'}</span>
              </div>

              {/* Profile Dropdown Logic - Directionally aware to prevent viewport boundary clipping */}
              {/* Profile Dropdown Logic - Positioned safely on the left to prevent screen overflow, with stacked responsive fields */}
              {profileMenuOpen && (
                <div 
                  className="profile-dropdown"
                  style={{
                    width: '280px',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: '16px',
                    padding: '16px',
                    boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}
                >
                  <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff', textAlign: 'right' }}>{currentUserData?.pharmacyName || 'صيدلية النور'}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', direction: 'ltr', textAlign: 'left' }}>{currentUserData?.email || user?.email || ''}</div>
                  
                  {/* Short ID Card (Always side-by-side but protected) */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    background: 'rgba(59, 130, 246, 0.08)', 
                    padding: '8px 12px', 
                    borderRadius: '10px',
                    border: '1px dashed rgba(59, 130, 246, 0.25)',
                    fontSize: '0.85rem',
                    direction: 'rtl'
                  }}>
                    <span style={{ color: 'var(--text-secondary)' }}>كود العميل (Short ID):</span>
                    <span style={{ fontWeight: 'bold', color: 'var(--primary)', fontFamily: 'monospace' }}>{currentUserData?.shortId || 'غير متوفر'}</span>
                  </div>

                  {/* Plan Card (Stacked vertically to prevent wrapping and squeezing) */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '4px', 
                    background: 'rgba(16, 185, 129, 0.08)', 
                    padding: '8px 12px', 
                    borderRadius: '10px',
                    border: '1px dashed rgba(16, 185, 129, 0.25)',
                    fontSize: '0.85rem',
                    direction: 'rtl',
                    textAlign: 'right'
                  }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>خطة العمل النشطة:</span>
                    <span style={{ 
                      fontWeight: 'bold', 
                      color: currentUserData?.plan === 'lifetime' ? 'var(--accent-green)' : (currentUserData?.plan === 'subscribe' ? 'var(--medical-teal)' : '#38bdf8'),
                      fontSize: '0.92rem',
                      marginTop: '2px'
                    }}>
                      {currentUserData?.plan === 'lifetime' ? 'مدى الحياة (lifetime) 👑' : (currentUserData?.plan === 'subscribe' ? 'مدفوعة (subscribe)' : 'تجريبية (trial)')}
                    </span>
                  </div>

                  <div style={{ height: '1px', background: 'var(--border)' }} />
                  <div onClick={() => { setProfileMenuOpen(false); handleLogout(); }} style={{ color: '#ef4444', fontWeight: 'bold', cursor: 'pointer', padding: '10px', background: 'rgba(239, 68, 68, 0.08)', borderRadius: '10px', textAlign: 'center', transition: 'all 0.2s ease' }}>تسجيل الخروج</div>
                </div>
              )}
            </div>



          </div>

        </div>

        <div className="tab-content fade-in-content" style={{ marginTop: '20px' }}>
          {activeTab === 'pos' && (
            <POSView
              sales={sales}
              inventory={inventory}
              cart={cart}
              setCart={setCart}
              posSearchTerm={posSearchTerm}
              setPosSearchTerm={setPosSearchTerm}
              posDiscount={posDiscount}
              setPosDiscount={setPosDiscount}
              handleCheckout={handleCheckout}
              addItemToCart={addItemToCart}
              activeShift={activeShift}
              onNotFoundBarcode={(bc) => {
                setPrefilledBarcode(bc);
                setActiveTab('inventory');
                setIsModalOpen(true);
                setBarcode(bc);
              }}
              isDeliveryOrder={isDeliveryOrder}
              setIsDeliveryOrder={setIsDeliveryOrder}
              customerName={customerName}
              setCustomerName={setCustomerName}
              customerPhone={customerPhone}
              setCustomerPhone={setCustomerPhone}
              deliveryAddress={deliveryAddress}
              setDeliveryAddress={setDeliveryAddress}
              deliveryDriver={deliveryDriver}
              setDeliveryDriver={setDeliveryDriver}
              deliveryFee={deliveryFee}
              setDeliveryFee={setDeliveryFee}
              employees={employees}
              isLoyaltyEnabled={isLoyaltyEnabled}
              loyaltyCustomers={loyaltyCustomers}
              selectedCustomer={selectedCustomer}
              setSelectedCustomer={setSelectedCustomer}
              usePoints={usePoints}
              setUsePoints={setUsePoints}
              isMobileCheckoutOpen={isMobileCheckoutOpen}
              setIsMobileCheckoutOpen={setIsMobileCheckoutOpen}
              insuranceContracts={insuranceContracts}
              selectedInsuranceContract={selectedInsuranceContract}
              setSelectedInsuranceContract={setSelectedInsuranceContract}
              insuranceApprovalNumber={insuranceApprovalNumber}
              setInsuranceApprovalNumber={setInsuranceApprovalNumber}
              isSplit={isSplit}
              setIsSplit={setIsSplit}
              splitCashInput={splitCashInput}
              setSplitCashInput={setSplitCashInput}
              splitCardInput={splitCardInput}
              setSplitCardInput={setSplitCardInput}
              isEWallet={isEWallet}
              setIsEWallet={setIsEWallet}
              isInstaPay={isInstaPay}
              setIsInstaPay={setIsInstaPay}
              isVodafone={isVodafone}
              setIsVodafone={setIsVodafone}
              resetPaymentModes={resetPaymentModes}
              paymentSettings={paymentSettings}
              generalCampaigns={generalCampaigns}
            />
          )}


          {activeTab === 'inventory' && (
            <InventoryView
              inventory={inventory}
              sales={sales}
              telegramSettings={telegramSettings}
              inventorySearch={inventorySearch}
              setInventorySearch={setInventorySearch}
              inventoryPage={inventoryPage}
              setInventoryPage={setInventoryPage}
              setEditId={setEditId}
              setTradeName={setTradeName}
              setTradeNameSecondary={setTradeNameSecondary}
              setGenericName={setGenericName}
              setCategory={setCategory}
              setBuyingPrice={setBuyingPrice}
              setSellingPrice={setSellingPrice}
              setQuantity={setQuantity}
              setExpiryDate={setExpiryDate}
              setBarcode={setBarcode}
              setSubUnits={setSubUnits}
              setIsModalOpen={setIsModalOpen}
              handleDeleteItem={handleDeleteItem}
              getExpiryStatus={getExpiryStatus}
              isAdminVerified={isAdminVerified}
              setIncrementProduct={setIncrementProduct}
              setIncrementCount={setIncrementCount}
              currentUserData={currentUserData}
              db={db}
              user={user}
              setInventory={setInventory}
            />
          )}

          {activeTab === 'delivery' && (
            <DeliveryView
              sales={sales}
              setSales={setSales}
              inventory={inventory}
              setInventory={setInventory}
              user={user}
              employees={employees}
              setActiveTab={setActiveTab}
              telegramSettings={telegramSettings}
              currentUserData={currentUserData}
              isSalesLoading={isSalesLoading}
              fetchSales={fetchSales}
              setIsDeliveryOrder={setIsDeliveryOrder}
              activeShift={activeShift}
              setGlobalLoading={setGlobalLoading}
              db={db}
              printSaleReceipt={printSaleReceipt}
              shareSaleViaWhatsApp={shareSaleViaWhatsApp}
            />
          )}

          {activeTab === 'online_orders' && (
            <OnlineOrdersView
              onlineOrders={onlineOrders}
              currentUserData={currentUserData}
              db={db}
              user={user}
              sales={sales}
              setSales={setSales}
              inventory={inventory}
              setInventory={setInventory}
              printSaleReceipt={printSaleReceipt}
              shareSaleViaWhatsApp={shareSaleViaWhatsApp}
              employees={employees}
              telegramSettings={telegramSettings}
              loyaltyCustomers={loyaltyCustomers}
              activeShift={activeShift}
            />
          )}

          {/* Centralized Admin PIN Lock Gate for Secured Tabs */}
          {['reports', 'shifts', 'eta', 'profile', 'settings', 'subscription', 'insurance', 'purchases', 'discounts', 'employees'].includes(activeTab) && (
            !isAdminVerified && currentUserData?.adminPin ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', direction: 'rtl', fontFamily: 'Cairo, sans-serif' }}>
                <div className="premium-card fade-in-content" style={{ 
                  maxWidth: '480px', 
                  width: '100%', 
                  border: '1px solid rgba(139, 92, 246, 0.35)', 
                  padding: '40px', 
                  borderRadius: '24px', 
                  background: 'linear-gradient(145deg, rgba(13, 20, 36, 0.9) 0%, rgba(5, 8, 16, 0.98) 100%)',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(20px)',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Decorative subtle background glow */}
                  <div style={{
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 60%)',
                    pointerEvents: 'none',
                    zIndex: 0
                  }} />

                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '28px' }}>
                      <div className="pulse-slow" style={{ 
                        width: '96px', 
                        height: '96px', 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(244, 63, 94, 0.15) 100%)', 
                        border: '1px solid rgba(139, 92, 246, 0.4)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '3rem', 
                        boxShadow: '0 0 35px rgba(139, 92, 246, 0.25)' 
                      }}>
                        🔒
                      </div>
                    </div>
                    
                    <h3 style={{ fontSize: '1.65rem', fontWeight: 800, color: '#fff', marginBottom: '12px', letterSpacing: '0.3px' }}>
                      {activeTab === 'reports' ? 'التقارير والإحصائيات محمية' :
                       activeTab === 'employees' ? 'إدارة الموظفين والرواتب محمية' :
                       activeTab === 'insurance' ? 'إدارة التأمين والمطالبات محمية' :
                       activeTab === 'delivery' ? 'طلبات الدليفري محمية' :
                       activeTab === 'shifts' ? 'سجل الورديات محمي' :
                       activeTab === 'settings' ? 'إعدادات النظام محمية' :
                       activeTab === 'profile' ? 'الملف الشخصي محمي' :
                       activeTab === 'subscription' ? 'الاشتراكات محمية' : 
                       activeTab === 'purchases' ? 'إدارة المشتريات والموردين محمية' : 
                        activeTab === 'discounts' ? 'إدارة العروض والخصومات محمية' : 'هذه الصفحة محمية'}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '32px' }}>
                      يُرجى إدخال رمز الـ PIN الخاص بالإدارة للوصول إلى هذه الصفحة الحساسة.
                    </p>
                    
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      setGlobalAdminError('');
                      if (globalAdminPin.trim() === currentUserData?.adminPin) {
                        setIsAdminVerified(true);
                        setLastAdminVerificationTime(Date.now());
                        setGlobalAdminPin('');
                        if (window.triggerDrMasrToast) {
                          window.triggerDrMasrToast("تم التحقق بنجاح! 🔓");
                        }
                      } else {
                        setGlobalAdminError('رمز الـ PIN الخاص بالمسؤول غير صحيح!');
                      }
                    }} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      <div style={{ position: 'relative' }}>
                        <input 
                          type="password" 
                          maxLength={4} 
                          className="premium-input" 
                          placeholder="••••" 
                          value={globalAdminPin} 
                          onChange={e => {
                            setGlobalAdminError('');
                            setGlobalAdminPin(e.target.value.replace(/\D/g, ''));
                          }} 
                          required 
                          style={{ 
                            textAlign: 'center', 
                            fontSize: '2.2rem', 
                            letterSpacing: '16px', 
                            fontWeight: 'bold', 
                            height: '64px', 
                            padding: '0 48px', 
                            borderRadius: '16px', 
                            border: '1px solid rgba(139, 92, 246, 0.3)', 
                            background: 'rgba(0, 0, 0, 0.4)', 
                            color: '#fff',
                            transition: 'all 0.3s ease',
                            boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.5)',
                            width: '100%'
                          }}
                        />
                      </div>

                      {globalAdminError && (
                        <div className="shake" style={{ color: 'var(--accent-pink)', fontSize: '0.95rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <span>⚠️</span> {globalAdminError}
                        </div>
                      )}

                      <button 
                        type="submit" 
                        className="gradient-btn" 
                        onMouseEnter={() => setIsGlobalSubmitHovered(true)}
                        onMouseLeave={() => setIsGlobalSubmitHovered(false)}
                        style={{ 
                          height: '54px', 
                          fontSize: '1.05rem', 
                          fontWeight: 'bold', 
                          width: '100%', 
                          borderRadius: '16px', 
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                          boxShadow: isGlobalSubmitHovered ? '0 8px 24px rgba(139, 92, 246, 0.45)' : '0 4px 12px rgba(139, 92, 246, 0.25)', 
                          border: 'none',
                          color: '#fff',
                          cursor: 'pointer',
                          transform: isGlobalSubmitHovered ? 'translateY(-1px)' : 'translateY(0)',
                          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        <span>فتح القفل</span> 🔐
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'reports' && (
                  <ReportsView
                    sales={sales}
                    inventory={inventory}
                    setSales={setSales}
                    setInventory={setInventory}
                    user={user}
                    employees={employees}
                    isAdminVerified={isAdminVerified}
                    setIsAdminVerified={setIsAdminVerified}
                    adminPin={currentUserData?.adminPin}
                    currentUserData={currentUserData}
                    printSaleReceipt={printSaleReceipt}
                    shareSaleViaWhatsApp={shareSaleViaWhatsApp}
                    loyaltyCustomers={loyaltyCustomers}
                    setLoyaltyCustomers={setLoyaltyCustomers}
                    isLoyaltyEnabled={isLoyaltyEnabled}
                    setGlobalLoading={setGlobalLoading}
                  />
                )}

                {activeTab === 'shifts' && (
                  <ShiftsView
                    sales={sales}
                    db={db}
                    user={user}
                    activeShift={activeShift}
                    activeShifts={activeShifts}
                    setActiveShift={setActiveShift}
                    shiftsHistory={shiftsHistory}
                    setShiftsHistory={setShiftsHistory}
                    employees={employees}
                    setEmployees={setEmployees}
                    telegramSettings={telegramSettings}
                    isShiftsHistoryLoading={isShiftsHistoryLoading}
                    fetchShiftsHistory={fetchShiftsHistory}
                    setGlobalLoading={setGlobalLoading}
                  />
                )}

                {activeTab === 'employees' && (
                  <React.Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>جاري تحميل شاشة الموظفين والرواتب... 👨‍⚕️</div>}>
                    <EmployeesView
                      user={user}
                      sales={sales}
                      employees={employees}
                      setEmployees={setEmployees}
                      currentUserData={currentUserData}
                      activeShift={activeShift}
                      activeShifts={activeShifts}
                      shiftsHistory={shiftsHistory}
                      setGlobalLoading={setGlobalLoading}
                    />
                  </React.Suspense>
                )}
                {activeTab === 'eta' && <ETASettingsView user={user} />}
                {activeTab === 'profile' && <ProfileView user={user} currentUserData={currentUserData} setCurrentUserData={setCurrentUserData} />}
                {activeTab === 'settings' && (
                  <SettingsView
                    user={user}
                    employees={employees}
                    setEmployees={setEmployees}
                    telegramSettings={telegramSettings}
                    setTelegramSettings={setTelegramSettings}
                    currentUserData={currentUserData}
                    setCurrentUserData={setCurrentUserData}
                    isAdminVerified={isAdminVerified}
                    setIsAdminVerified={setIsAdminVerified}
                    paymentSettings={paymentSettings}
                    setPaymentSettings={setPaymentSettings}
                  />
                )}
                {activeTab === 'subscription' && <SubscriptionView currentUserData={currentUserData} />}
                {activeTab === 'insurance' && (
                  <InsuranceView 
                    user={user} 
                    sales={sales} 
                    setSales={setSales}
                    db={db} 
                    currentUserData={currentUserData} 
                    setGlobalLoading={setGlobalLoading} 
                    inventory={inventory}
                  />
                )}
                {activeTab === 'purchases' && (
                  <PurchasesView
                    user={user}
                    db={db}
                    setGlobalLoading={setGlobalLoading}
                    activeShift={activeShift}
                    inventory={inventory}
                    setInventory={setInventory}
                  />
                )}
                {activeTab === 'discounts' && (
                  <DiscountsView
                    user={user}
                    db={db}
                    setGlobalLoading={setGlobalLoading}
                    inventory={inventory}
                    setInventory={setInventory}
                  />
                )}
              </>
            )
          )}

          {activeTab === 'customers' && (
            <CustomersView 
              user={user} 
              sales={sales} 
              db={db} 
              currentUserData={currentUserData} 
              loyaltyCustomers={loyaltyCustomers}
              setGlobalLoading={setGlobalLoading}
              setLoyaltyCustomers={setLoyaltyCustomers}
              activeShift={activeShift}
              isAdminVerified={isAdminVerified}
              telegramSettings={telegramSettings}
              refillAlerts={refillAlerts}
              setRefillAlerts={setRefillAlerts}
            />
          )}

          {activeTab === 'calls' && (
            <React.Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>جاري تحميل صفحة المتابعة... ⏳</div>}>
              <CallsView user={user} setGlobalLoading={setGlobalLoading} />
            </React.Suspense>
          )}

        </div>

        {/* Shared Modals */}
        {isModalOpen && (
          <InventoryModal
            editId={editId}
            tradeName={tradeName}
            setTradeName={setTradeName}
            tradeNameSecondary={tradeNameSecondary}
            setTradeNameSecondary={setTradeNameSecondary}
            genericName={genericName}
            setGenericName={setGenericName}
            category={category}
            setCategory={setCategory}
            barcode={barcode}
            setBarcode={setBarcode}
            quantity={quantity}
            setQuantity={setQuantity}
            expiryDate={expiryDate}
            setExpiryDate={setExpiryDate}
            buyingPrice={buyingPrice}
            setBuyingPrice={setBuyingPrice}
            sellingPrice={sellingPrice}
            setSellingPrice={setSellingPrice}
            requiresFridge={requiresFridge}
            setRequiresFridge={setRequiresFridge}
            origin={origin}
            setOrigin={setOrigin}
            subUnits={subUnits}
            setSubUnits={setSubUnits}
            newSubUnitName={newSubUnitName}
            setNewSubUnitName={setNewSubUnitName}
            newSubUnitRatio={newSubUnitRatio}
            setNewSubUnitRatio={setNewSubUnitRatio}
            newSubUnitPrice={newSubUnitPrice}
            setNewSubUnitPrice={setNewSubUnitPrice}
            handleSaveItem={handleSaveItem}
            clearForm={clearForm}
            isSaving={isSaving}
            barcodeInputRef={barcodeInputRef}
            db={db}
          />
        )}


        {/* Custom Barcode Increment Modal */}
        {incrementProduct && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(8, 13, 27, 0.85)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 110000,
            padding: '24px', fontFamily: 'Cairo, sans-serif'
          }}>
            <div className="premium-card fade-in-content" style={{ width: '450px', maxWidth: '100%', background: 'var(--bg-card)', border: '1px solid var(--primary)', direction: 'rtl', padding: '32px', borderRadius: '16px', boxShadow: '0 12px 40px rgba(0,0,0,0.6)', textAlign: 'center' }}>
              <div style={{ fontSize: '3.5rem', color: 'var(--primary)', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                <div style={{
                  position: 'relative',
                  animation: 'pulse 1.5s infinite'
                }}>
                  📦
                  <span style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-15px',
                    background: 'var(--accent-green)',
                    color: '#fff',
                    fontSize: '1rem',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontWeight: 800,
                    boxShadow: '0 0 10px var(--accent-green)'
                  }}>
                    +{incrementCount}
                  </span>
                </div>
              </div>

              <h3 className="gradient-text" style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '12px' }}>زيادة كمية المخزون</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '20px', lineHeight: '1.6' }}>
                أنت تقوم بزيادة مخزون صنف: <b>{incrementProduct.tradeName}</b>.<br />
                الكمية الحالية في المخزن: <span style={{ color: 'var(--primary)', fontWeight: 700 }}>{incrementProduct.quantity} عبوة</span>.
              </p>

              {/* Dynamic Quantity Incrementor Controls */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '28px', background: 'rgba(255,255,255,0.02)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>الكمية المراد إضافتها للمخزن:</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px' }}>
                  <button 
                    type="button" 
                    onClick={() => setIncrementCount(c => Math.max(1, c - 1))}
                    className="gradient-btn-secondary"
                    style={{ width: '40px', height: '40px', padding: 0, fontSize: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', border: '1px solid var(--border)', cursor: 'pointer' }}
                  >
                    -
                  </button>
                  <input 
                    type="number" 
                    className="premium-input" 
                    value={incrementCount} 
                    onChange={e => setIncrementCount(Math.max(1, parseInt(e.target.value) || 1))}
                    style={{ width: '100px', textAlign: 'center', fontSize: '1.2rem', fontWeight: 700, padding: '8px', border: '1px solid var(--primary)', borderRadius: '8px', color: '#fff', background: 'var(--bg-input)' }} 
                  />
                  <button 
                    type="button" 
                    onClick={() => setIncrementCount(c => c + 1)}
                    className="gradient-btn-secondary"
                    style={{ width: '40px', height: '40px', padding: 0, fontSize: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', border: '1px solid var(--border)', cursor: 'pointer' }}
                  >
                    +
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  onClick={handleConfirmIncrement} 
                  className="gradient-btn" 
                  style={{ flex: 1, padding: '12px 24px', fontSize: '0.95rem', fontWeight: 700 }}
                >
                  تأكيد زيادة المخزون 📦
                </button>
                <button 
                  onClick={handleCancelIncrement} 
                  className="gradient-btn-secondary" 
                  style={{ flex: 0.5, padding: '12px 20px', fontSize: '0.95rem' }}
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        )}

        {isAdminPinModalOpen && (
          <AdminPINVerification
            adminPin={currentUserData?.adminPin}
            onVerified={() => {
              setIsAdminVerified(true);
              setLastAdminVerificationTime(Date.now());
              setIsAdminPinModalOpen(false);
            }}
            onCancel={() => setIsAdminPinModalOpen(false)}
          />
        )}

        {isShiftDialogOpen && (
          <ShiftDialog
            sales={sales}
            user={user}
            activeShift={activeShift}
            activeShifts={activeShifts}
            setActiveShift={setActiveShift}
            shiftsHistory={shiftsHistory}
            setShiftsHistory={setShiftsHistory}
            employees={employees}
            currentUserData={currentUserData}
            setCurrentUserData={setCurrentUserData}
            telegramSettings={telegramSettings}
            onClose={() => setIsShiftDialogOpen(false)}
          />
        )}

        {showAnnouncementsModal && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(2, 6, 23, 0.75)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            zIndex: 1000000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
          }} onClick={() => setShowAnnouncementsModal(false)}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.98))',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '24px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              padding: '2rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px rgba(59, 130, 246, 0.15)',
              position: 'relative',
              color: '#fff',
              direction: 'rtl',
              textAlign: 'right',
              fontFamily: 'Cairo, sans-serif'
            }} onClick={(e) => e.stopPropagation()}>
              <button 
                onClick={() => setShowAnnouncementsModal(false)} 
                style={{
                  position: 'absolute',
                  top: '20px', left: '20px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  width: '36px', height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
              >✕</button>

              <h3 style={{ fontSize: '1.6rem', fontWeight: 900, color: '#3b82f6', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px' }}>
                <span>📢</span> إشعارات وتنبيهات الإدارة
              </h3>

              {/* Combined banner for pending online orders */}
              {notificationCount > 0 && (
                <div 
                  onClick={() => {
                    setShowAnnouncementsModal(false);
                    setActiveTab('online_orders');
                  }}
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    background: 'rgba(239, 68, 68, 0.1)', 
                    border: '1px solid rgba(239, 68, 68, 0.3)', 
                    padding: '12px 16px', 
                    borderRadius: '12px', 
                    marginBottom: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <span style={{ fontWeight: 'bold', color: '#f87171', fontSize: '0.95rem' }}>
                    🌐 يوجد لديك {notificationCount} طلبات أونلاين جديدة معلقة
                  </span>
                  <button style={{ background: '#ef4444', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 'bold', cursor: 'pointer' }}>
                    عرض الطلبات 📥
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {announcements.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
                    <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '12px' }}>📭</span>
                    لا توجد إشعارات أو تنبيهات واردة من الإدارة حالياً.
                  </div>
                ) : (
                  announcements.map(item => (
                    <div key={item.id} style={{ padding: '16px', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '14px', position: 'relative' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                        <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#fff' }}>{item.title}</h4>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ar-EG', { dateStyle: 'medium' }) : ''}
                        </span>
                      </div>
                      <p style={{ margin: 0, color: '#cbd5e1', fontSize: '0.95rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{item.text || item.message}</p>
                    </div>
                  ))
                )}
              </div>

              <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                <button 
                  onClick={() => setShowAnnouncementsModal(false)} 
                  className="gradient-btn" 
                  style={{ padding: '10px 30px', fontSize: '1rem' }}
                >
                  إغلاق النافذة
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedUnitProduct && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(8, 13, 27,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 100000, fontFamily: 'Cairo, sans-serif' }}>
            <div className="premium-card fade-in-content" style={{ width: '450px', maxWidth: '100%', background: 'var(--bg-card)', border: '1px solid var(--primary)', direction: 'rtl', padding: '32px', borderRadius: '16px', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
                <h3 className="gradient-text" style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0 }}>اختر وحدة البيع المطلوبة</h3>
                <button onClick={() => setSelectedUnitProduct(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1.3rem' }}>✕</button>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '24px', lineHeight: '1.6' }}>
                الرجاء اختيار وحدة البيع المناسبة لصنف <strong>({selectedUnitProduct.tradeName})</strong> المراد إضافتها للسلة الآن:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                <button 
                  onClick={() => { 
                    addItemToCart(selectedUnitProduct, null, true); 
                    setSelectedUnitProduct(null); 
                  }} 
                  className="gradient-btn-secondary" 
                  style={{ width: '100%', padding: '16px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}
                >
                  <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>علبة كاملة</span>
                  <span style={{ fontWeight: 700, color: 'var(--primary)' }}>{selectedUnitProduct.sellingPrice} ج.م</span>
                </button>
                {selectedUnitProduct.subUnits && selectedUnitProduct.subUnits.map((sub, idx) => (
                  <button 
                    key={idx} 
                    onClick={() => { 
                      addItemToCart(selectedUnitProduct, sub); 
                      setSelectedUnitProduct(null); 
                    }} 
                    className="gradient-btn" 
                    style={{ width: '100%', padding: '16px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}
                  >
                    <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{sub.name} تجزئة</span>
                    <span style={{ fontWeight: 700 }}>{sub.price} ج.م</span>
                  </button>
                ))}
              </div>
              <button onClick={() => setSelectedUnitProduct(null)} className="gradient-btn-secondary" style={{ width: '100%', padding: '10px', color: 'var(--accent-pink)' }}>إلغاء الإضافة</button>
            </div>
          </div>
        )}

        {selectedAlternativesProduct && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(8, 13, 27,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 100000, fontFamily: 'Cairo, sans-serif' }}>
            <div className="premium-card fade-in-content" style={{ width: '550px', maxWidth: '100%', background: 'var(--bg-card)', border: '1px solid var(--accent-pink)', direction: 'rtl', padding: '32px', borderRadius: '16px', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 className="gradient-text" style={{ fontSize: '1.3rem', fontWeight: 800 }}>بدائل دواء: {selectedAlternativesProduct.tradeName}</h3>
                <button onClick={() => setSelectedAlternativesProduct(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1.3rem' }}>✕</button>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '24px', lineHeight: '1.6' }}>
                عذراً، هذا الصنف غير متوفر حالياً بالمخزن. تشتمل القائمة أدناه على البدائل المتاحة التي تحتوي على نفس المادة الفعالة <strong>({selectedAlternativesProduct.genericName})</strong> ولديها رصيد كافٍ:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '300px', overflowY: 'auto', marginBottom: '20px', paddingLeft: '8px' }}>
                {inventory.filter(item => 
                  item.id !== selectedAlternativesProduct.id && 
                  item.genericName && 
                  selectedAlternativesProduct.genericName && 
                  item.genericName.trim().toLowerCase() === selectedAlternativesProduct.genericName.trim().toLowerCase() && 
                  item.quantity > 0
                ).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.1)' }}>
                    لا توجد بدائل متوفرة حالياً تحتوي على نفس المادة الفعالة.
                  </div>
                ) : (
                  inventory.filter(item => 
                    item.id !== selectedAlternativesProduct.id && 
                    item.genericName && 
                    selectedAlternativesProduct.genericName && 
                    item.genericName.trim().toLowerCase() === selectedAlternativesProduct.genericName.trim().toLowerCase() && 
                    item.quantity > 0
                  ).map((altItem) => (
                    <div 
                      key={altItem.id} 
                      className="premium-card-hover"
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', transition: 'all 0.3s ease' }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontWeight: 700, color: '#fff' }}>{altItem.tradeName}</span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>الكمية المتاحة: {altItem.quantity} - الرف: {altItem.shelfLocation || 'غير محدد'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1.05rem' }}>{altItem.sellingPrice} ج.م</span>
                        <button 
                          onClick={() => {
                            addItemToCart(altItem);
                            setSelectedAlternativesProduct(null);
                          }}
                          className="gradient-btn"
                          style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '0.85rem' }}
                        >
                          إضافة للسلة
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <button 
                onClick={() => setSelectedAlternativesProduct(null)} 
                className="gradient-btn-secondary" 
                style={{ width: '100%', padding: '12px', borderRadius: '12px' }}
              >
                إغلاق النافذة
              </button>
            </div>
          </div>
        )}
      </div>


      {/* Backdrop overlay for mobile drawer checkout sheet */}
      {activeTab === 'pos' && isMobileCheckoutOpen && (
        <div className="sheet-backdrop" onClick={() => setIsMobileCheckoutOpen(false)}></div>
      )}

      {/* Premium Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav">
        <button 
          type="button"
          onClick={() => { setActiveTab('pos'); setMobileMenuOpen(false); }}
          className={`mobile-bottom-nav-item ${activeTab === 'pos' ? 'active' : ''}`}
        >
          <svg viewBox="0 0 24 24">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
          </svg>
          <span>الرئيسية (POS)</span>
        </button>

        <button 
          type="button"
          onClick={() => { setActiveTab('inventory'); setMobileMenuOpen(false); }}
          className={`mobile-bottom-nav-item ${activeTab === 'inventory' ? 'active' : ''}`}
        >
          <svg viewBox="0 0 24 24">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 14H4v-4h11v4zm0-5H4V9h11v4zm5 5h-4V9h4v9z"/>
          </svg>
          <span>المخزن</span>
        </button>

        <button 
          type="button"
          onClick={() => { setActiveTab('delivery'); setMobileMenuOpen(false); }}
          className={`mobile-bottom-nav-item ${activeTab === 'delivery' ? 'active' : ''}`}
        >
          <svg viewBox="0 0 24 24">
            <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm12.5-9l2.25 3H17V9.5h1.5zM18 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
          </svg>
          {sales.filter(s => s.isDelivery && (s.deliveryStatus === 'pending' || s.deliveryStatus === 'out')).length > 0 && (
            <span className="badge-count purple" style={{ position: 'absolute', top: '4px', right: '12px', fontSize: '0.7rem', padding: '1px 5px' }}>
              {sales.filter(s => s.isDelivery && (s.deliveryStatus === 'pending' || s.deliveryStatus === 'out')).length}
            </span>
          )}
          <span>الدليفري</span>
        </button>

        <button 
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`mobile-bottom-nav-item ${mobileMenuOpen ? 'active' : ''}`}
        >
          <svg viewBox="0 0 24 24">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
          </svg>
          <span>المزيد</span>
        </button>
      </div>
      
      {/* Premium Glassmorphic Global Toast Notification */}
      {globalToast && globalToast.show && (
        <div style={{
          position: 'fixed',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(9, 14, 28, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.6), inset 0 0 12px rgba(139, 92, 246, 0.1)',
          borderRadius: '14px',
          padding: '12px 24px',
          zIndex: 999999,
          direction: 'rtl',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          transition: 'all 0.3s ease',
          pointerEvents: 'none'
        }}>
          <span style={{ fontSize: '1.15rem' }}>🔔</span>
          <span style={{ 
            color: '#fff', 
            fontSize: '0.92rem', 
            fontWeight: 'bold', 
            fontFamily: 'Cairo, sans-serif',
            whiteSpace: 'nowrap'
          }}>
            {globalToast.message}
          </span>
        </div>
      )}

      {globalLoading.active && (
        <div className="pm-loading-overlay" style={{ zIndex: 300000 }}>
          <div className="pm-loading-card">
            <div className="pm-logo-ring-wrapper">
              <div className="pm-logo-glow-ring"></div>
              <div className="pm-logo-glow-ring-inner"></div>
              <div className="pm-logo-container">
                <img 
                  src="/drmasr_logo_uploaded.png" 
                  alt="DrMasr" 
                  className="pm-logo-img"
                  width="75"
                  height="75"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentNode;
                    if (parent && !parent.querySelector('.pm-logo-fallback')) {
                      const fallback = document.createElement('div');
                      fallback.className = 'pm-logo-fallback';
                      fallback.innerText = 'D';
                      fallback.style.fontSize = '3.5rem';
                      fallback.style.fontWeight = '900';
                      fallback.style.color = '#fff';
                      fallback.style.background = 'linear-gradient(135deg, var(--primary) 0%, #1e40af 100%)';
                      fallback.style.width = '100%';
                      fallback.style.height = '100%';
                      fallback.style.display = 'flex';
                      fallback.style.alignItems = 'center';
                      fallback.style.justifyContent = 'center';
                      fallback.style.textShadow = '0 0 10px rgba(59, 130, 246, 0.5)';
                      parent.appendChild(fallback);
                    }
                  }}
                />
              </div>
            </div>
            <h3 className="pm-loading-title">
              <span style={{ fontSize: '1.4rem' }}>🔄</span> {globalLoading.title || 'جاري تحميل البيانات...'}
            </h3>
            <p className="pm-loading-subtitle">
              {globalLoading.message || 'يرجى الانتظار قليلاً...'}
            </p>
            <div className="pm-pulse-badge">
              <span>🛡️</span> بوابة دكتور مصر الرقمية
            </div>
          </div>
        </div>
      )}

      {confirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(3, 7, 18, 0.75)',
          backdropFilter: 'blur(10px)',
          zIndex: 9999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          fontFamily: 'Cairo, sans-serif',
          animation: 'fadeIn 0.25s ease-out'
        }}>
          <div className="premium-card fade-in-content" style={{
            maxWidth: '420px',
            width: '100%',
            padding: '30px',
            borderRadius: '24px',
            border: '1.5px solid rgba(139, 92, 246, 0.35)',
            background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.95) 0%, rgba(5, 8, 16, 0.98) 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 30px rgba(139, 92, 246, 0.15)',
            textAlign: 'center',
            position: 'relative'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px', filter: 'drop-shadow(0 0 10px rgba(139, 92, 246, 0.4))' }}>❓</div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', margin: '0 0 12px 0' }}>تأكيد الإجراء</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: '1.6', margin: '0 0 24px 0', whiteSpace: 'pre-line' }}>
              {confirmDialog.message}
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => {
                  if (confirmDialog.onConfirm) confirmDialog.onConfirm();
                  setConfirmDialog(null);
                }}
                className="gradient-btn"
                style={{ flex: 1, padding: '12px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}
              >
                تأكيد
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirmDialog.onCancel) confirmDialog.onCancel();
                  setConfirmDialog(null);
                }}
                className="gradient-btn-secondary"
                style={{ flex: 1, padding: '12px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 'bold', border: '1px solid var(--border)', cursor: 'pointer' }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {alertDialog && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(3, 7, 18, 0.75)',
          backdropFilter: 'blur(10px)',
          zIndex: 9999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          fontFamily: 'Cairo, sans-serif',
          animation: 'fadeIn 0.25s ease-out'
        }}>
          <div className="premium-card fade-in-content" style={{
            maxWidth: '420px',
            width: '100%',
            padding: '30px',
            borderRadius: '24px',
            border: '1.5px solid rgba(59, 130, 246, 0.35)',
            background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.95) 0%, rgba(5, 8, 16, 0.98) 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 30px rgba(59, 130, 246, 0.15)',
            textAlign: 'center',
            position: 'relative'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px', filter: 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.4))' }}>📢</div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', margin: '0 0 12px 0' }}>تنبيه</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: '1.6', margin: '0 0 24px 0', whiteSpace: 'pre-line' }}>
              {alertDialog.message}
            </p>
            <button
              type="button"
              onClick={() => {
                if (alertDialog.onClose) alertDialog.onClose();
                setAlertDialog(null);
              }}
              className="gradient-btn"
              style={{ width: '100%', padding: '12px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 'bold', border: 'none', cursor: 'pointer', background: 'var(--primary)' }}
            >
              حسناً
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

// 3.5. Online Orders & QR Code View
function OnlineOrdersView({ onlineOrders, currentUserData, db, user, sales, setSales, inventory, setInventory, printSaleReceipt, shareSaleViaWhatsApp, employees = [], telegramSettings = {}, loyaltyCustomers = [], activeShift }) {
  const [activeSubTab, setActiveSubTab] = useState('orders'); // orders or poster
  const [actionLoading, setActionLoading] = useState(null);

  const [assigningOrder, setAssigningOrder] = useState(null); // The order currently being assigned
  const [selectedDriver, setSelectedDriver] = useState(''); // Selected driver employee ID or name
  const [manualDriverName, setManualDriverName] = useState(''); // Manual typed name
  const [deliveryFeeVal, setDeliveryFeeVal] = useState(0); // Input delivery fee

  useEffect(() => {
    if (assigningOrder) {
      const dbFee = parseFloat(currentUserData?.defaultDeliveryFee);
      const orderFee = parseFloat(assigningOrder.deliveryFee);
      if (!isNaN(dbFee) && dbFee > 0) {
        setDeliveryFeeVal(dbFee);
      } else if (!isNaN(orderFee) && orderFee > 0) {
        setDeliveryFeeVal(orderFee);
      } else {
        setDeliveryFeeVal(15);
      }
    }
  }, [assigningOrder, currentUserData]);

  const shopUrl = `${window.location.origin}/?shop=${user?.uid}`;
  const driversList = employees ? employees.filter(e => e.role === 'driver' || e.role === 'طيار') : [];
  const isLoyaltyEnabled = currentUserData?.loyaltySettings?.enabled !== false;

  const handleConfirmAndDispatch = async () => {
    if (!assigningOrder) return;

    if (!assigningOrder.customerName?.trim() || !assigningOrder.customerPhone?.trim() || !assigningOrder.deliveryAddress?.trim()) {
      window.triggerDrMasrAlert('⚠️ لا يمكن إتمام تأكيد الطلب أونلاين لعدم اكتمال بيانات العميل الأساسية (الاسم، الهاتف، العنوان)!');
      return;
    }

    if (driversList.length > 0) {
      if (!selectedDriver) {
        window.triggerDrMasrAlert('⚠️ يرجى اختيار طيار للتوصيل!');
        return;
      }
      if (selectedDriver === 'manual_input' && !manualDriverName.trim()) {
        window.triggerDrMasrAlert('⚠️ يرجى كتابة اسم الطيار!');
        return;
      }
    } else {
      if (!manualDriverName.trim()) {
        window.triggerDrMasrAlert('⚠️ يرجى كتابة اسم الطيار!');
        return;
      }
    }

    try {
      setActionLoading(assigningOrder.id);
      
      const invoiceId = assigningOrder.orderId || Date.now().toString().slice(-8);
      const finalDeliveryFee = parseFloat(deliveryFeeVal) || 0;
      
      let driverName = '';
      if (selectedDriver === 'manual_input' || driversList.length === 0) {
        driverName = manualDriverName.trim() || 'غير مسجل';
      } else if (selectedDriver) {
        const found = driversList.find(d => d.id === selectedDriver);
        driverName = found ? found.name : selectedDriver;
      } else {
        driverName = 'غير مسجل';
      }

      const saleData = {
        invoiceId,
        items: assigningOrder.items.map(it => ({
          id: it.id || it.barcode,
          tradeName: it.tradeName,
          quantity: it.quantity,
          price: it.price,
          sellingPrice: it.price,
          barcode: it.barcode,
          buyingPrice: it.buyingPrice || 0,
          subUnit: it.subUnit || null
        })),
        subTotal: assigningOrder.items.reduce((acc, it) => acc + (it.price * it.quantity), 0),
        discount: assigningOrder.promoDiscount || 0,
        promoCode: assigningOrder.promoCode || '',
        promoId: assigningOrder.promoId || '',
        pointsDiscount: assigningOrder.pointsDiscount || 0,
        pointsEarned: assigningOrder.pointsEarned || 0,
        grandTotal: (assigningOrder.grandTotal - (assigningOrder.deliveryFee || 0)) + finalDeliveryFee,
        paymentMethod: 'cash',
        timestamp: new Date().toISOString(),
        cashierName: activeShift ? activeShift.cashierName : 'أونلاين (QR Store)',
        shiftId: activeShift ? activeShift.id : '',
        isDelivery: true,
        deliveryStatus: 'pending',
        customerName: assigningOrder.customerName,
        customerPhone: assigningOrder.customerPhone,
        deliveryDetails: {
          address: assigningOrder.deliveryAddress,
          driverName: driverName,
          fee: finalDeliveryFee
        },
        deliveryFee: finalDeliveryFee,
        onlineOrderId: assigningOrder.orderId || ''
      };

      const saleDocRef = doc(collection(db, 'users', user.uid, 'sales'));
      const saleWithId = { id: saleDocRef.id, ...saleData };

      // Optimistically update sales state
      const nextSales = [saleWithId, ...sales];
      setSales(nextSales);
      localStorage.setItem('drmasr_sales_' + (user.uid), JSON.stringify(nextSales));

      // Reduce inventory stock locally
      const nextInv = inventory.map(invItem => {
        const orderedItem = assigningOrder.items.find(it => it.barcode === invItem.barcode);
        if (orderedItem) {
          return { ...invItem, quantity: Math.max(0, Number((invItem.quantity - orderedItem.quantity).toFixed(2))) };
        }
        return invItem;
      });
      setInventory(nextInv);
      localStorage.setItem('drmasr_inv_' + (user.uid), JSON.stringify(nextInv));

      // Save sale to Firestore
      await setDoc(saleDocRef, saleData);

      // Auto-save the delivery fee to Firebase as the default if it is not already set (or is 0)
      if (finalDeliveryFee > 0 && (currentUserData?.defaultDeliveryFee === undefined || currentUserData?.defaultDeliveryFee === 0)) {
        await updateDoc(doc(db, 'users', user.uid), { defaultDeliveryFee: finalDeliveryFee }).catch(() => {});
        if (currentUserData) {
          currentUserData.defaultDeliveryFee = finalDeliveryFee;
          localStorage.setItem('drmasr_userdata', JSON.stringify(currentUserData));
        }
      }
      
      // Update customer points in Firestore if loyalty is enabled
      const pointsDiscountVal = assigningOrder.pointsDiscount || 0;
      const pointsEarnedVal = assigningOrder.pointsEarned || 0;

      if (assigningOrder.customerPhone) {
        const finalPhone = assigningOrder.customerPhone;
        const finalName = assigningOrder.customerName || 'عميل أونلاين';
        
        const existingCust = loyaltyCustomers.find(c => c.phone === finalPhone);
        if (existingCust) {
          const customerDocRef = doc(db, 'users', user.uid, 'customers', existingCust.id);
          const oldPoints = existingCust.points || 0;
          const newPoints = Math.max(0, oldPoints - pointsDiscountVal + pointsEarnedVal);
          
          await updateDoc(customerDocRef, {
            visits: (existingCust.visits || 0) + 1,
            spend: (existingCust.spend || 0) + ((assigningOrder.grandTotal - (assigningOrder.deliveryFee || 0)) + finalDeliveryFee),
            points: Math.round(newPoints * 100) / 100,
            lastVisit: new Date().toISOString()
          });
        } else {
          const customerDocRef = doc(collection(db, 'users', user.uid, 'customers'));
          await setDoc(customerDocRef, {
            name: finalName,
            phone: finalPhone,
            visits: 1,
            spend: (assigningOrder.grandTotal - (assigningOrder.deliveryFee || 0)) + finalDeliveryFee,
            points: Math.round(pointsEarnedVal * 100) / 100,
            lastVisit: new Date().toISOString(),
            createdAt: new Date().toISOString()
          });
        }
      }
      
      // Update inventory stock in Firestore
      for (const it of assigningOrder.items) {
        const invItem = inventory.find(i => i.barcode === it.barcode);
        if (invItem && invItem.id) {
          try {
            await updateDoc(doc(db, 'users', user.uid, 'inventory', invItem.id), {
              quantity: Math.max(0, Number((invItem.quantity - it.quantity).toFixed(2)))
            });
          } catch (err) {
            console.error("Failed to update inventory in Firestore:", err);
          }
        }
      }

      // Delete online order
      await deleteDoc(doc(db, 'users', user.uid, 'online_orders', assigningOrder.id));

      // Print Receipt
      if (printSaleReceipt) {
        printSaleReceipt(saleWithId);
      }

      // Send Telegram notification upon confirmation and dispatching to pilot
      if (telegramSettings?.enabled && telegramSettings?.chatId) {
        const baseTotalForTelegram = assigningOrder.grandTotal - (assigningOrder.deliveryFee || 0);
        const finalTotalForTelegram = baseTotalForTelegram + finalDeliveryFee;
        
        let msg = `✅ <b>تم تأكيد الطلب وتحويله للتوصيل!</b> 🛵\n\n`;
        msg += `👤 <b>العميل:</b> ${assigningOrder.customerName || 'غير محدد'}\n`;
        msg += `📞 <b>الهاتف:</b> <code>${assigningOrder.customerPhone || 'غير متوفر'}</code>\n`;
        msg += `📍 <b>العنوان:</b> <i>${assigningOrder.deliveryAddress || 'غير محدد'}</i>\n`;
        msg += `🏍️ <b>الطيار المسند:</b> 🚴 <b>${driverName}</b>\n`;
        msg += `━━━━━━━━━━━━━━━━━━━━\n`;
        msg += `📦 <b>الأدوية المطلوبة:</b>\n`;
        if (Array.isArray(assigningOrder.items)) {
          assigningOrder.items.forEach((item, idx) => {
            msg += `  ${idx + 1}. <b>${item.tradeName}</b> (عدد: ${item.quantity}) - ${item.price} ج.م\n`;
          });
        }
        msg += `━━━━━━━━━━━━━━━━━━━━\n`;
        msg += `💰 <b>قيمة الطلب:</b> <b>${baseTotalForTelegram} ج.م</b>\n`;
        if (assigningOrder.pointsDiscount > 0) {
          msg += `🎁 <b>خصم كاش باك:</b> <b>-${assigningOrder.pointsDiscount} ج.م</b>\n`;
        }
        msg += `🛵 <b>خدمة التوصيل:</b> <b>${finalDeliveryFee} ج.م</b>\n`;
        msg += `💵 <b>الإجمالي المستحق للتوصيل:</b> <b>${finalTotalForTelegram} ج.م</b>\n\n`;
        msg += `📲 <i>تم بدء التوصيل الآن بنجاح! ⚡</i>`;
        sendTelegramMessage(telegramSettings, msg).catch(err => console.error(err));
      }

      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('✅ تم تأكيد الطلب بنجاح وتحويله للدليفري للتوصيل! 🛵');
      }

      setAssigningOrder(null);
    } catch (err) {
      console.error("Failed to accept online order:", err);
      window.triggerDrMasrToast("❌ فشل تأكيد الطلب، يرجى المحاولة مرة أخرى.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectOrder = (order) => {
    window.triggerDrMasrConfirm("هل أنت متأكد من رغبتك في إلغاء ورفض هذا الطلب نهائياً؟", async () => {
      try {
        setActionLoading(order.id);
        await deleteDoc(doc(db, 'users', user.uid, 'online_orders', order.id));
        if (window.triggerDrMasrToast) {
          window.triggerDrMasrToast('🗑️ تم إلغاء ورفض الطلب بنجاح.');
        }
      } catch (err) {
        console.error("Failed to reject order:", err);
      } finally {
        setActionLoading(null);
      }
    });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '32px', direction: 'rtl', textAlign: 'right' }}>
      
      {/* Subtabs Header */}
      <div className="delivery-tabs-header" style={{ marginBottom: '0px' }}>
        <button 
          onClick={() => setActiveSubTab('orders')} 
          className={`delivery-tab-btn ${activeSubTab === 'orders' ? 'active' : ''}`}
        >
          📦 الطلبات الواردة من العملاء ({onlineOrders.length})
        </button>
        <button 
          onClick={() => setActiveSubTab('poster')} 
          className={`delivery-tab-btn ${activeSubTab === 'poster' ? 'active' : ''}`}
        >
          🖨️ طباعة ملصق الـ QR للصيدلية
        </button>
      </div>

      {activeSubTab === 'orders' ? (
        <div style={{ animation: 'fadeIn 0.3s ease' }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '8px' }}>
            📥 إدارة طلبات الأدوية أونلاين
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '24px' }}>
            تابع واعفِ عن طلبات الأدوية الواردة لحظة بلحظة مباشرة من العملاء الذين قاموا بمسح الـ QR الخاص بصيدليتك.
          </p>

          {onlineOrders.length === 0 ? (
            <div style={{ 
              padding: '60px 24px', 
              textAlign: 'center', 
              background: 'radial-gradient(circle, rgba(99, 102, 241, 0.05) 0%, rgba(8, 13, 27, 0.4) 100%)', 
              borderRadius: '24px', 
              border: '1px dashed rgba(99, 102, 241, 0.25)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }}>
              <span style={{ fontSize: '3.5rem', display: 'block', marginBottom: '16px', filter: 'drop-shadow(0 0 10px rgba(99,102,241,0.4))' }}>🌐</span>
              <h4 style={{ color: '#fff', fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '8px' }}>لا توجد طلبات أونلاين نشطة حالياً</h4>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', maxWidth: '480px', margin: '0 auto 20px', lineHeight: '1.6' }}>
                شارك الـ QR كود الخاص بصيدليتك مع العملاء؛ ليتصفحوا الأدوية المتاحة بأسعارها ويطلبوا دليفري مباشرة بنقرة واحدة!
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '16px' }}>
                <button 
                  onClick={() => setActiveSubTab('poster')} 
                  className="gradient-btn" 
                  style={{ padding: '10px 24px', fontSize: '0.9rem' }}
                >
                  👀 استعراض وطباعة ملصق الـ QR للصيدلية
                </button>
                <a 
                  href={shopUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="gradient-btn-secondary" 
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 24px', fontSize: '0.9rem', textDecoration: 'none', border: '1px solid var(--primary)', background: 'rgba(99, 102, 241, 0.05)', color: '#fff' }}
                >
                  🌐 تجربة وزيارة صفحة المتجر للزبائن
                </a>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '24px' }}>
              {onlineOrders.map((order) => (
                <div 
                  key={order.id} 
                  className="premium-card fade-in-content" 
                  style={{ 
                    border: '1px solid rgba(99, 102, 241, 0.3)', 
                    background: 'linear-gradient(145deg, rgba(13, 20, 36, 0.9) 0%, rgba(8, 13, 27, 0.95) 100%)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5), inset 0 0 16px rgba(99, 102, 241, 0.05)',
                    borderRadius: '24px', 
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: '20px'
                  }}
                >
                  <div>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed rgba(255,255,255,0.08)', paddingBottom: '12px', marginBottom: '16px' }}>
                      <div>
                        <span style={{ 
                          fontSize: '0.78rem', 
                          background: 'rgba(99, 102, 241, 0.15)', 
                          color: 'var(--primary)', 
                          padding: '4px 10px', 
                          borderRadius: '8px', 
                          fontWeight: 'bold',
                          fontFamily: 'Outfit, Cairo'
                        }}>
                          #{order.orderId || order.id?.slice(-6).toUpperCase()}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontFamily: 'Outfit, Cairo' }}>
                        {order.timestamp ? new Date(order.timestamp).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px', background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.92rem' }}>
                        <span>👤</span>
                        <strong style={{ color: '#fff' }}>{order.customerName}</strong>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}>
                        <span>📞</span>
                        <a href={`tel:${order.customerPhone}`} style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 800, fontFamily: 'Outfit' }}>{order.customerPhone}</a>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                        <span>📍</span>
                        <span>{order.deliveryAddress}</span>
                      </div>
                      {order.customerNote && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.88rem', color: 'var(--accent-gold)', lineHeight: '1.4', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '8px', marginTop: '4px' }}>
                          <span>📝</span>
                          <span style={{ fontWeight: 'bold' }}>ملاحظة العميل: {order.customerNote}</span>
                        </div>
                      )}
                    </div>

                    {/* Products list */}
                    <h5 style={{ fontSize: '0.9rem', color: '#fff', fontWeight: 'bold', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>📦</span> الأدوية والمنتجات المطلوبة:
                    </h5>
                    <div style={{ maxHeight: '160px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', padding: '8px', background: 'rgba(0,0,0,0.15)' }}>
                      {order.items?.map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', padding: '6px 8px', background: 'rgba(255,255,255,0.01)', borderRadius: '6px' }}>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>{item.quantity}x</span>
                            <span style={{ color: '#cbd5e1', fontWeight: 700 }}>{item.tradeName}</span>
                          </div>
                          <span style={{ color: 'var(--accent-green)', fontWeight: 800, fontFamily: 'Outfit' }}>{parseFloat((item.price * item.quantity).toFixed(2))} ج.م</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer & Actions */}
                  <div>
                    {/* Detailed Pricing Breakdown */}
                    <div style={{ 
                      background: 'rgba(255, 255, 255, 0.02)', 
                      borderRadius: '16px', 
                      padding: '12px 16px', 
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      marginBottom: '16px',
                      marginTop: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        <span>💵 إجمالي المنتجات:</span>
                        <span style={{ fontFamily: 'Outfit', fontWeight: 600, color: '#fff' }}>{order.items?.reduce((acc, it) => acc + (it.price * it.quantity), 0) || 0} ج.م</span>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        <span>🛵 سعر الشحن والتوصيل:</span>
                        <span style={{ fontFamily: 'Outfit', fontWeight: 600, color: '#38bdf8' }}>+{order.deliveryFee || 0} ج.م</span>
                      </div>

                      {(order.pointsDiscount || 0) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--accent-gold)' }}>
                          <span>🎁 خصم كاش باك (نقاط):</span>
                          <span style={{ fontFamily: 'Outfit', fontWeight: 600, color: '#f87171' }}>-{order.pointsDiscount} ج.م</span>
                        </div>
                      )}

                      {(order.promoDiscount || 0) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#ec4899' }}>
                          <span>🏷️ خصم كوبون ({order.promoCode || 'بروموكود'}):</span>
                          <span style={{ fontFamily: 'Outfit', fontWeight: 600, color: '#f87171' }}>-{order.promoDiscount} ج.م</span>
                        </div>
                      )}

                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        borderTop: '1px dashed rgba(255, 255, 255, 0.1)', 
                        paddingTop: '8px', 
                        marginTop: '4px' 
                      }}>
                        <strong style={{ fontSize: '0.9rem', color: '#fff' }}>الإجمالي المستحق للتوصيل:</strong>
                        <strong style={{ fontSize: '1.3rem', color: 'var(--accent-green)', fontFamily: 'Outfit, Cairo' }}>
                          {order.grandTotal} <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>ج.م</span>
                        </strong>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button 
                        onClick={() => {
                          setSelectedDriver('');
                          setManualDriverName('');
                          setDeliveryFeeVal(0);
                          setAssigningOrder(order);
                        }} 
                        disabled={actionLoading !== null}
                        className="gradient-btn" 
                        style={{ flex: 1.6, padding: '10px', fontSize: '0.88rem', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                      >
                        {actionLoading === order.id ? (
                          <span>جاري التأكيد...</span>
                        ) : (
                          <>
                            <span>تأكيد وتحويل للدليفري</span>
                            <span>🛵</span>
                          </>
                        )}
                      </button>
                      <button 
                        onClick={() => handleRejectOrder(order)} 
                        disabled={actionLoading !== null}
                        className="gradient-btn-secondary" 
                        style={{ flex: 0.8, padding: '10px', fontSize: '0.88rem', height: '42px', border: '1px solid rgba(239, 68, 68, 0.4)', background: 'rgba(239, 68, 68, 0.05)', color: '#ef4444' }}
                      >
                        رفض
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Tab 2: Printable QR Poster */
        <div className="printable-tab-container" style={{ animation: 'fadeIn 0.3s ease', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div className="no-print-header">
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '8px' }}>
              🖨️ ملصق الـ QR الكود المخصص للصيدلية
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '16px' }}>
              قم بطباعة هذا الملصق الورقي الأنيق وعلقه في صيدليتك؛ ليتمكن الزبائن من مسحه وفتح المتجر وطلب الأدوية دليفري فوراً.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <button 
                onClick={handlePrint} 
                className="gradient-btn" 
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 30px', fontSize: '1rem', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', boxShadow: '0 6px 20px rgba(16, 185, 129, 0.25)' }}
              >
                <span>🖨️ طباعة الملصق الورقي الفوري (A4)</span>
              </button>
              <a 
                href={shopUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="gradient-btn-secondary" 
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 30px', fontSize: '1rem', textDecoration: 'none', border: '1px solid var(--primary)', background: 'rgba(99, 102, 241, 0.05)', color: '#fff' }}
              >
                <span>🌐 تجربة وزيارة المتجر المباشر للزبائن</span>
              </a>
            </div>
          </div>

          {/* Printable Design Card Container */}
          <div className="printable-qr-wrapper" style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
            <div 
              id="printable-qr-poster" 
              style={{ 
                width: '100%', 
                maxWidth: '540px', 
                background: '#0a0f1d', 
                border: '2px solid rgba(99, 102, 241, 0.4)', 
                borderRadius: '32px', 
                padding: '48px 36px', 
                textAlign: 'center', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: '28px',
                boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                color: '#fff',
                fontFamily: 'Cairo, sans-serif',
                position: 'relative'
              }}
            >
              {/* Header logo container - clean & transparent */}
              <div 
                className="logo-container"
                style={{ 
                  width: '160px', 
                  height: '80px', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                {currentUserData?.pharmacyLogo || localStorage.getItem('drmasr_receipt_logo') ? (
                  <img src={currentUserData?.pharmacyLogo || localStorage.getItem('drmasr_receipt_logo')} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: '2.5rem', fontWeight: 900, color: 'var(--primary)' }}>{currentUserData?.pharmacyName?.[0] || 'H'}</span>
                )}
              </div>
 
              {/* Pharmacy Title */}
              <div>
                <h4 style={{ fontSize: '1.1rem', color: 'var(--primary)', fontWeight: 800, margin: '0 0 4px 0', letterSpacing: '0.5px' }}>
                  نظام الأوردر الذكي المباشر
                </h4>
                <h2 style={{ fontSize: '2.2rem', fontWeight: 900, color: '#fff', margin: 0, textShadow: '0 0 10px rgba(255,255,255,0.1)' }}>
                  {currentUserData?.pharmacyName || 'صيدلية دكتور مصر'}
                </h2>
              </div>
 
              {/* Call to Action banner */}
              <div 
                className="cta-banner"
                style={{ 
                  background: 'linear-gradient(90deg, rgba(99, 102, 241, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%)', 
                  border: '1px solid rgba(99, 102, 241, 0.25)', 
                  borderRadius: '16px', 
                  padding: '12px 24px', 
                  fontWeight: 'bold', 
                  color: '#fff', 
                  fontSize: '1.25rem',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <span>اطلب أونلاين من صيدليتنا الآن!</span>
              </div>
 
              {/* High resolution QR Code */}
              <div 
                className="qr-box"
                style={{ 
                  background: '#fff', 
                  padding: '16px', 
                  borderRadius: '24px', 
                  boxShadow: '0 12px 36px rgba(0,0,0,0.4), 0 0 20px rgba(99, 102, 241, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(shopUrl)}`} 
                  alt="Scan QR code" 
                  style={{ width: '240px', height: '240px', display: 'block' }}
                />
              </div>
 
              {/* Instructions list */}
              <div style={{ maxWidth: '440px' }}>
                <p 
                  className="instructions-title"
                  style={{ fontSize: '1rem', color: '#cbd5e1', lineHeight: '1.6', margin: '0 0 12px 0', fontWeight: 'bold' }}
                >
                  امسح الـ QR بكاميرا موبايلك مباشرة
                </p>
                <p 
                  className="instructions-desc"
                  style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: '1.6', margin: 0 }}
                >
                  تصفح جميع الأدوية ومستحضرات التجميل المتاحة بالأسعار الحالية للصيدلية، املأ عربة تسوقك، واطلب دليفري فوراً ليصلك الأوردر في مكانه!
                </p>
              </div>
 
              {/* Branded Footer */}
              <div 
                className="footer-text"
                style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '20px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                  <span>دكتور مصر</span>
                  <span>•</span>
                  <span>النظام الرقمي الأول لإدارة الصيدليات</span>
                </div>
                <strong style={{ fontSize: '0.92rem', color: 'var(--primary)', letterSpacing: '0.5px', fontFamily: 'Outfit, Cairo' }}>DRMasr.com</strong>
              </div>

              {/* Custom CSS Style block injected directly for Print Media formatting */}
              <style>{`
                @page {
                  size: A4 portrait;
                  margin: 8mm !important;
                }
                @media print {
                  html, body, #root, .layout-wrapper, .main-content {
                    height: 0 !important;
                    min-height: 0 !important;
                    max-height: 0 !important;
                    overflow: visible !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    border: none !important;
                    background: transparent !important;
                  }
                  .no-print-header {
                    display: none !important;
                  }
                  .printable-tab-container {
                    animation: none !important;
                    transform: none !important;
                    transition: none !important;
                    gap: 0 !important;
                    margin: 0 !important;
                    padding: 0 !important;
                  }
                  .printable-qr-wrapper {
                    padding: 0 !important;
                    margin: 0 !important;
                    display: block !important;
                  }
                  body * {
                    visibility: hidden !important;
                  }
                  #printable-qr-poster, #printable-qr-poster * {
                    visibility: visible !important;
                  }
                  #printable-qr-poster {
                    position: fixed !important;
                    left: 0 !important;
                    right: 0 !important;
                    top: 10px !important;
                    margin: 0 auto !important;
                    width: 620px !important;
                    max-width: 100% !important;
                    height: 810px !important;
                    background: #ffffff !important;
                    color: #0f172a !important;
                    border: 4px double #0f172a !important;
                    box-shadow: none !important;
                    border-radius: 24px !important;
                    padding: 24px 36px 16px 36px !important;
                    page-break-inside: avoid !important;
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                    text-align: center !important;
                    box-sizing: border-box !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  #printable-qr-poster .footer-text strong {
                    color: #2563eb !important;
                    font-size: 1.1rem !important;
                  }
                  #printable-qr-poster h2 {
                    color: #0f172a !important;
                    font-size: 2.1rem !important;
                    font-weight: 900 !important;
                    text-shadow: none !important;
                    margin: 0 !important;
                  }
                  #printable-qr-poster h4 {
                    color: #2563eb !important;
                    font-size: 1.15rem !important;
                    font-weight: 800 !important;
                    margin: 0 0 4px 0 !important;
                  }
                  #printable-qr-poster p, #printable-qr-poster span, #printable-qr-poster strong, #printable-qr-poster div {
                    color: #1e293b !important;
                  }
                  #printable-qr-poster .logo-container {
                    width: 130px !important;
                    height: 65px !important;
                    border-radius: 0 !important;
                    background: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    overflow: hidden !important;
                  }
                  #printable-qr-poster .logo-container span {
                    font-size: 2.5rem !important;
                  }
                  #printable-qr-poster .logo-container img {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: contain !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  #printable-qr-poster .cta-banner {
                    background: #f1f5f9 !important;
                    border: 1px solid #cbd5e1 !important;
                    color: #0f172a !important;
                    padding: 8px 20px !important;
                    font-size: 1.15rem !important;
                    border-radius: 12px !important;
                  }
                  #printable-qr-poster .cta-banner span {
                    color: #0f172a !important;
                    font-weight: 900 !important;
                  }
                  #printable-qr-poster .instructions-title {
                    color: #0f172a !important;
                    font-weight: 800 !important;
                    font-size: 1.05rem !important;
                    margin: 0 0 6px 0 !important;
                  }
                  #printable-qr-poster .instructions-desc {
                    color: #475569 !important;
                    font-size: 0.88rem !important;
                    line-height: 1.4 !important;
                  }
                  #printable-qr-poster .footer-text {
                    color: #64748b !important;
                    border-top: 1px solid #e2e8f0 !important;
                    padding-top: 10px !important;
                    font-size: 0.82rem !important;
                    width: 100% !important;
                  }
                  #printable-qr-poster .qr-box {
                    background: #ffffff !important;
                    border: 1px solid #e2e8f0 !important;
                    box-shadow: none !important;
                    padding: 10px !important;
                    border-radius: 16px !important;
                  }
                  #printable-qr-poster .qr-box img {
                    width: 190px !important;
                    height: 190px !important;
                  }
                }
              `}</style>
            </div>
          </div>
        </div>
      )}

      {/* Driver Assignment Modal */}
      {assigningOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(5, 8, 22, 0.85)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
          fontFamily: 'Cairo, sans-serif'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '460px',
            background: 'linear-gradient(145deg, #0e1628 0%, #080d1b 100%)',
            border: '1px solid rgba(99, 102, 241, 0.4)',
            borderRadius: '28px',
            padding: '32px 28px',
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(99, 102, 241, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            color: '#fff',
            direction: 'rtl'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: '3rem', filter: 'drop-shadow(0 0 10px rgba(99,102,241,0.4))' }}>🛵</span>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 800, marginTop: '12px', color: '#fff' }}>إسناد الطلب وتأكيد التوصيل</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: '6px' }}>
                للطلب رقم <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>#{assigningOrder.orderId || assigningOrder.id?.slice(-6).toUpperCase()}</span> بقيمة أساسية <span style={{ color: 'var(--accent-green)', fontWeight: 'bold' }}>{assigningOrder.grandTotal - (assigningOrder.deliveryFee || 0)} ج.م</span> {assigningOrder.deliveryFee > 0 && `(شاملة شحن مبدئي ${assigningOrder.deliveryFee} ج.م)`}
              </p>
            </div>

            {/* Form Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Driver Selection */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 700 }}>
                  اختر طيار التوصيل 🚴
                </label>
                {driversList.length > 0 ? (
                  <select
                    className="premium-input"
                    value={selectedDriver}
                    onChange={(e) => setSelectedDriver(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: '#fff',
                      fontFamily: 'Cairo',
                      outline: 'none'
                    }}
                  >
                    <option value="" style={{ background: '#0e1628' }}>-- اختر الطيار --</option>
                    {driversList.map(emp => (
                      <option key={emp.id} value={emp.id} style={{ background: '#0e1628' }}>{emp.name}</option>
                    ))}
                    <option value="manual_input" style={{ background: '#0e1628' }}>✍️ كتابة اسم طيار آخر يدويًا...</option>
                  </select>
                ) : (
                  <div style={{ fontSize: '0.85rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(245, 158, 11, 0.05)', padding: '10px', borderRadius: '10px', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                    ⚠️ لا يوجد طيارين مسجلين. سيتم كتابة الاسم يدويًا.
                  </div>
                )}
              </div>

              {/* Manual Driver Name Input (Visible if manual option selected or no drivers registered) */}
              {(selectedDriver === 'manual_input' || driversList.length === 0) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }} className="fade-in-content">
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 700 }}>
                    اسم الطيار يدويًا ✍️
                  </label>
                  <input
                    type="text"
                    className="premium-input"
                    placeholder="مثال: كابتن أحمد محمد"
                    value={manualDriverName}
                    onChange={(e) => setManualDriverName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: '#fff',
                      fontFamily: 'Cairo',
                      outline: 'none'
                    }}
                  />
                </div>
              )}

              {/* Delivery Fee */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 700 }}>
                  خدمة التوصيل (ج.م) 💰
                </label>
                <input
                  type="number"
                  min="0"
                  className="premium-input"
                  value={deliveryFeeVal}
                  onChange={(e) => setDeliveryFeeVal(Math.max(0, parseFloat(e.target.value) || 0))}
                  disabled={currentUserData?.defaultDeliveryFee !== undefined && parseFloat(currentUserData.defaultDeliveryFee) > 0}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    background: (currentUserData?.defaultDeliveryFee !== undefined && parseFloat(currentUserData.defaultDeliveryFee) > 0) ? 'rgba(255,255,255,0.01)' : 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: (currentUserData?.defaultDeliveryFee !== undefined && parseFloat(currentUserData.defaultDeliveryFee) > 0) ? 'rgba(255,255,255,0.4)' : '#fff',
                    fontFamily: 'Cairo',
                    outline: 'none',
                    cursor: (currentUserData?.defaultDeliveryFee !== undefined && parseFloat(currentUserData.defaultDeliveryFee) > 0) ? 'not-allowed' : 'default'
                  }}
                />
                {currentUserData?.defaultDeliveryFee !== undefined && parseFloat(currentUserData.defaultDeliveryFee) > 0 && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--accent-green)' }}>
                    ✓ تم تطبيق سعر التوصيل الرسمي الافتراضي المحفوظ في السيستم.
                  </span>
                )}
              </div>
            </div>

            {/* Total calculation indicator */}
            <div style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.15)', padding: '12px 16px', borderRadius: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>الإجمالي شامل التوصيل النهائي:</span>
              <strong style={{ fontSize: '1.25rem', color: 'var(--accent-green)', fontFamily: 'Outfit, Cairo' }}>
                {(assigningOrder.grandTotal - (assigningOrder.deliveryFee || 0)) + (parseFloat(deliveryFeeVal) || 0)} ج.م
              </strong>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button
                onClick={handleConfirmAndDispatch}
                disabled={actionLoading !== null}
                className="gradient-btn"
                style={{ flex: 1.5, padding: '12px', borderRadius: '12px', fontSize: '0.92rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                {actionLoading ? 'جاري التحويل...' : 'تأكيد وإرسال للتوصيل 🛵'}
              </button>
              <button
                onClick={() => setAssigningOrder(null)}
                disabled={actionLoading !== null}
                className="gradient-btn-secondary"
                style={{ flex: 0.8, padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)', color: '#fff', fontSize: '0.92rem' }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3.8. Purchases & Suppliers Management View (مكون إدارة المشتريات والموردين)
// ─────────────────────────────────────────────────────────────────────────────
function PurchasesView({ user, db, setGlobalLoading, activeShift, inventory, setInventory }) {
  const [subTab, setSubTab] = useState('new_invoice'); // new_invoice, suppliers, invoices_log
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  
  // Suppliers Tab States
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supName, setSupName] = useState('');
  const [supPhone, setSupPhone] = useState('');
  const [supAddress, setSupAddress] = useState('');
  const [supBalance, setSupBalance] = useState(0);

  // Supplier Payment Modal
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentSupplier, setPaymentSupplier] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  // Invoice Tab States
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [paymentType, setPaymentType] = useState('cash'); // cash, credit, partial
  const [paidAmount, setPaidAmount] = useState('');
  const [invoiceItems, setInvoiceItems] = useState([]);

  // Search/Add Product to Invoice States
  const [prodSearchQuery, setProdSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [prodQty, setProdQty] = useState('');
  const [prodBuyingPrice, setProdBuyingPrice] = useState('');
  const [prodDiscount, setProdDiscount] = useState('');
  const [prodSellingPrice, setProdSellingPrice] = useState('');
  const [prodExpiryDate, setProdExpiryDate] = useState('');
  const [prodBarcode, setProdBarcode] = useState('');
  const [buyingPriceMode, setBuyingPriceMode] = useState('price'); // price, discount

  // Log Tab States
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Fetch Suppliers and Invoices
  useEffect(() => {
    if (!user) return;
    
    const fetchData = async () => {
      if (setGlobalLoading) {
        setGlobalLoading({
          active: true,
          title: 'تحميل البيانات 🚚',
          message: 'جاري استدعاء قائمة الموردين وسجل الفواتير من السيرفر...'
        });
      }
      try {
        // Load Suppliers
        const supSnap = await getDocs(collection(db, 'users', user.uid, 'suppliers'));
        const supList = supSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSuppliers(supList);
        localStorage.setItem('drmasr_suppliers_' + user.uid, JSON.stringify(supList));

        // Load Invoices
        const purSnap = await getDocs(collection(db, 'users', user.uid, 'purchases'));
        const purList = purSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Sort by date desc
        purList.sort((a, b) => new Date(b.date) - new Date(a.date));
        setPurchases(purList);
        localStorage.setItem('drmasr_purchases_' + user.uid, JSON.stringify(purList));
      } catch (err) {
        console.error("Error loading purchases data:", err);
        // Fallback to cache
        const cachedSup = localStorage.getItem('drmasr_suppliers_' + user.uid);
        if (cachedSup) setSuppliers(JSON.parse(cachedSup));
        const cachedPur = localStorage.getItem('drmasr_purchases_' + user.uid);
        if (cachedPur) setPurchases(JSON.parse(cachedPur));
      } finally {
        if (setGlobalLoading) {
          setGlobalLoading({ active: false, title: '', message: '' });
        }
      }
    };

    fetchData();
  }, [user]);

  // Handle Add/Edit Supplier
  const handleSaveSupplier = async (e) => {
    e.preventDefault();
    if (!supName.trim()) return;

    if (setGlobalLoading) {
      setGlobalLoading({ active: true, title: 'جاري الحفظ...', message: 'يتم حفظ بيانات المورد حالياً...' });
    }

    try {
      const data = {
        name: supName.trim(),
        phone: supPhone.trim(),
        address: supAddress.trim(),
        balance: Number(supBalance) || 0,
        updatedAt: new Date().toISOString()
      };

      if (editingSupplier) {
        await updateDoc(doc(db, 'users', user.uid, 'suppliers', editingSupplier.id), data);
        setSuppliers(prev => prev.map(s => s.id === editingSupplier.id ? { ...s, ...data } : s));
        window.triggerDrMasrToast('تم تحديث بيانات المورد بنجاح! ✨');
      } else {
        const newData = { ...data, createdAt: new Date().toISOString() };
        const ref = await addDoc(collection(db, 'users', user.uid, 'suppliers'), newData);
        setSuppliers(prev => [{ id: ref.id, ...newData }, ...prev]);
        window.triggerDrMasrToast('تم إضافة المورد الجديد بنجاح! ✨');
      }

      setIsSupplierModalOpen(false);
      setEditingSupplier(null);
      setSupName('');
      setSupPhone('');
      setSupAddress('');
      setSupBalance(0);
    } catch (err) {
      console.error(err);
      window.triggerDrMasrToast('حدث خطأ أثناء حفظ المورد ❌');
    } finally {
      if (setGlobalLoading) setGlobalLoading({ active: false, title: '', message: '' });
    }
  };

  // Record payment to supplier (سند صرف دفعة)
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentSupplier || !paymentAmount || Number(paymentAmount) <= 0) return;

    if (setGlobalLoading) {
      setGlobalLoading({ active: true, title: 'جاري تسجيل الدفعة...', message: 'يتم الآن خصم الدفعة وتوثيق المعاملة...' });
    }

    try {
      const amount = Number(paymentAmount);
      const newBalance = (paymentSupplier.balance || 0) - amount;

      // 1. Update Supplier Balance in Firestore
      await updateDoc(doc(db, 'users', user.uid, 'suppliers', paymentSupplier.id), {
        balance: newBalance,
        updatedAt: new Date().toISOString()
      });

      // 2. Add Expense Transaction to general Accounting ledger
      const txDesc = `دفعة للمورد: ${paymentSupplier.name} ${paymentNotes ? `(${paymentNotes})` : ''}`;
      const newTx = {
        type: 'expense',
        amount: amount,
        description: txDesc,
        timestamp: new Date().toISOString(),
        recordedBy: activeShift?.cashierName || 'المدير المسؤول 🛡️'
      };
      await addDoc(collection(db, 'users', user.uid, 'accounting'), newTx);

      // Update Local State
      setSuppliers(prev => prev.map(s => s.id === paymentSupplier.id ? { ...s, balance: newBalance } : s));
      window.triggerDrMasrToast('تم تسجيل الدفعة وخصمها من الحساب بنجاح! 💸');
      
      setIsPaymentModalOpen(false);
      setPaymentSupplier(null);
      setPaymentAmount('');
      setPaymentNotes('');
    } catch (err) {
      console.error(err);
      window.triggerDrMasrToast('حدث خطأ أثناء تسجيل الدفعة ❌');
    } finally {
      if (setGlobalLoading) setGlobalLoading({ active: false, title: '', message: '' });
    }
  };

  // Add Item to Invoice temporary list
  const handleAddItemToInvoice = () => {
    if (!selectedProduct && !prodSearchQuery.trim()) {
      window.triggerDrMasrToast('يرجى تحديد أو كتابة اسم الدواء! ⚠️');
      return;
    }
    if (!prodQty || Number(prodQty) <= 0) {
      window.triggerDrMasrToast('يرجى كتابة كمية صحيحة! ⚠️');
      return;
    }
    if (!prodBuyingPrice || Number(prodBuyingPrice) <= 0) {
      window.triggerDrMasrToast('يرجى كتابة سعر شراء صحيح! ⚠️');
      return;
    }

    const item = {
      productId: selectedProduct?.id || null, // null means brand new product
      tradeName: selectedProduct ? selectedProduct.tradeName : prodSearchQuery.trim(),
      genericName: selectedProduct ? selectedProduct.genericName || '' : '',
      category: selectedProduct ? selectedProduct.category || '' : '',
      quantity: Number(prodQty),
      buyingPrice: Number(prodBuyingPrice),
      sellingPrice: Number(prodSellingPrice) || Number(prodBuyingPrice) * 1.25, // default markup if blank
      expiryDate: prodExpiryDate || '',
      barcode: prodBarcode.trim()
    };

    setInvoiceItems(prev => [...prev, item]);
    
    // Reset item input form
    setSelectedProduct(null);
    setProdSearchQuery('');
    setProdQty('');
    setProdBuyingPrice('');
    setProdDiscount('');
    setProdSellingPrice('');
    setProdExpiryDate('');
    setProdBarcode('');
  };

  const handleRemoveInvoiceItem = (index) => {
    setInvoiceItems(prev => prev.filter((_, i) => i !== index));
  };

  // Save the entire purchase invoice
  const handleSaveInvoice = async () => {
    if (!selectedSupplierId) {
      window.triggerDrMasrToast('يرجى اختيار المورد! ⚠️');
      return;
    }
    if (invoiceItems.length === 0) {
      window.triggerDrMasrToast('يرجى إضافة دواء واحد على الأقل للفاتورة! ⚠️');
      return;
    }

    const supplier = suppliers.find(s => s.id === selectedSupplierId);
    if (!supplier) return;

    const total = invoiceItems.reduce((sum, item) => sum + (item.quantity * item.buyingPrice), 0);
    let paid = 0;
    if (paymentType === 'cash') {
      paid = total;
    } else if (paymentType === 'credit') {
      paid = 0;
    } else {
      paid = Number(paidAmount) || 0;
    }
    const remaining = total - paid;

    if (setGlobalLoading) {
      setGlobalLoading({ active: true, title: 'جاري حفظ الفاتورة...', message: 'تحديث المخزون وحسابات المورد المالي حالياً...' });
    }

    try {
      const batch = writeBatch ? writeBatch(db) : null;
      const updatedInventory = [...inventory];

      // Process each item to update inventory
      for (const item of invoiceItems) {
        if (item.productId) {
          // Existing product in inventory -> increment qty and update prices
          const existingIndex = updatedInventory.findIndex(p => p.id === item.productId);
          if (existingIndex !== -1) {
            const current = updatedInventory[existingIndex];
            const updatedQty = (current.quantity || 0) + item.quantity;
            const updatedFields = {
              quantity: updatedQty,
              buyingPrice: item.buyingPrice,
              sellingPrice: item.sellingPrice,
              expiryDate: item.expiryDate || current.expiryDate || '',
              updatedAt: new Date().toISOString()
            };
            if (item.barcode) updatedFields.barcode = item.barcode;

            // Update in Firebase
            if (batch) {
              batch.update(doc(db, 'users', user.uid, 'inventory', item.productId), updatedFields);
            } else {
              await updateDoc(doc(db, 'users', user.uid, 'inventory', item.productId), updatedFields);
            }

            // Update locally
            updatedInventory[existingIndex] = { ...current, ...updatedFields };
          }
        } else {
          // Brand new product -> add to inventory collection
          const newProductData = {
            tradeName: item.tradeName,
            genericName: item.genericName,
            category: item.category || 'عام',
            quantity: item.quantity,
            buyingPrice: item.buyingPrice,
            sellingPrice: item.sellingPrice,
            expiryDate: item.expiryDate,
            barcode: item.barcode || '20' + Date.now().toString().slice(-10), // autogenerated
            subUnits: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          const newDocRef = doc(collection(db, 'users', user.uid, 'inventory'));
          if (batch) {
            batch.set(newDocRef, newProductData);
          } else {
            await setDoc(newDocRef, newProductData);
          }
          updatedInventory.unshift({ id: newDocRef.id, ...newProductData });
        }
      }

      // Update Supplier Balance in Firebase
      if (remaining !== 0) {
        const newBalance = (supplier.balance || 0) + remaining;
        if (batch) {
          batch.update(doc(db, 'users', user.uid, 'suppliers', supplier.id), { balance: newBalance });
        } else {
          await updateDoc(doc(db, 'users', user.uid, 'suppliers', supplier.id), { balance: newBalance });
        }
        setSuppliers(prev => prev.map(s => s.id === supplier.id ? { ...s, balance: newBalance } : s));
      }

      // Record Cash Expense in Accounting ledger if paid from registry
      if (paid > 0) {
        const expenseTx = {
          type: 'expense',
          amount: paid,
          description: `فاتورة مشتريات رقم ${invoiceNumber || 'غير محدد'} من المورد ${supplier.name}`,
          timestamp: new Date().toISOString(),
          recordedBy: activeShift?.cashierName || 'المدير المسؤول 🛡️'
        };
        const accRef = doc(collection(db, 'users', user.uid, 'accounting'));
        if (batch) {
          batch.set(accRef, expenseTx);
        } else {
          await setDoc(accRef, expenseTx);
        }
      }

      // Create Purchase Invoice Document
      const purchaseInvoice = {
        invoiceNumber: invoiceNumber.trim() || 'غير محدد',
        supplierId: supplier.id,
        supplierName: supplier.name,
        items: invoiceItems,
        totalAmount: total,
        paidAmount: paid,
        remainingAmount: remaining,
        paymentType: paymentType,
        date: new Date().toISOString(),
        shiftId: activeShift?.id || 'غير محدد'
      };

      const purRef = doc(collection(db, 'users', user.uid, 'purchases'));
      if (batch) {
        batch.set(purRef, purchaseInvoice);
      } else {
        await setDoc(purRef, purchaseInvoice);
      }

      if (batch) {
        await batch.commit();
      }

      // Sync local states
      setPurchases(prev => [{ id: purRef.id, ...purchaseInvoice }, ...prev]);
      setInventory(updatedInventory);
      
      window.triggerDrMasrToast('تم حفظ الفاتورة وتحديث المخزون والحسابات بنجاح! 🚚🎉');

      // Clear Form
      setInvoiceItems([]);
      setInvoiceNumber('');
      setSelectedSupplierId('');
      setPaymentType('cash');
      setPaidAmount('');
      setSubTab('invoices_log'); // switch to log view
    } catch (err) {
      console.error("Error saving purchase invoice:", err);
      window.triggerDrMasrToast('حدث خطأ أثناء حفظ الفاتورة ❌');
    } finally {
      if (setGlobalLoading) setGlobalLoading({ active: false, title: '', message: '' });
    }
  };

  // Product Autocomplete Search
  const filteredProducts = prodSearchQuery.trim()
    ? inventory.filter(p => 
        (p.tradeName || '').toLowerCase().includes(prodSearchQuery.toLowerCase()) ||
        (p.barcode || '') === prodSearchQuery.trim()
      ).slice(0, 5)
    : [];

  return (
    <div style={{ direction: 'rtl', fontFamily: 'Cairo, sans-serif' }}>
      
      {/* Top Header Card */}
      <div className="premium-card" style={{ 
        marginBottom: '24px', 
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(13, 20, 36, 0.8) 100%)',
        border: '1px solid rgba(59, 130, 246, 0.25)',
        borderRadius: '24px',
        padding: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#fff', margin: 0 }}>🚚 المشتريات والموردين</h2>
            <p style={{ color: 'var(--text-secondary)', margin: '6px 0 0 0', fontSize: '0.9rem' }}>
              أدخل فواتير المشتريات لتحديث المخزون تلقائياً وإدارة حسابات المخازن والموردين بدقة
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              className={`gradient-btn${subTab === 'new_invoice' ? '' : '-secondary'}`}
              onClick={() => { setSubTab('new_invoice'); setSelectedInvoice(null); }}
              style={{ borderRadius: '12px', padding: '10px 18px', fontSize: '0.9rem' }}
            >
              📥 فاتورة جديدة
            </button>
            <button 
              className={`gradient-btn${subTab === 'suppliers' ? '' : '-secondary'}`}
              onClick={() => { setSubTab('suppliers'); setSelectedInvoice(null); }}
              style={{ borderRadius: '12px', padding: '10px 18px', fontSize: '0.9rem' }}
            >
              🏢 دليل الموردين
            </button>
            <button 
              className={`gradient-btn${subTab === 'invoices_log' ? '' : '-secondary'}`}
              onClick={() => { setSubTab('invoices_log'); setSelectedInvoice(null); }}
              style={{ borderRadius: '12px', padding: '10px 18px', fontSize: '0.9rem' }}
            >
              📜 سجل الفواتير
            </button>
          </div>
        </div>
      </div>

      {/* SUBTAB 1: NEW INVOICE */}
      {subTab === 'new_invoice' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '24px' }}>
          
          {/* Right Side: Invoice Info & Items Entry */}
          <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fff', margin: 0, borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
              📝 بيانات الفاتورة والأصناف
            </h3>
            
            {/* Header info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>المخزن / المورد</label>
                <select 
                  className="premium-input"
                  style={{ height: '42px', padding: '0 10px' }}
                  value={selectedSupplierId}
                  onChange={e => setSelectedSupplierId(e.target.value)}
                >
                  <option value="">-- اختر مورد --</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name} (الرصيد: {s.balance || 0} ج.م)</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>رقم الفاتورة</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  placeholder="مثال: INV-987"
                  value={invoiceNumber}
                  onChange={e => setInvoiceNumber(e.target.value)}
                />
              </div>
            </div>

            {/* Add Item form */}
            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '16px', borderRadius: '16px', border: '1px dashed var(--border)' }}>
              <h4 style={{ fontSize: '0.95rem', color: 'var(--primary)', fontWeight: 'bold', margin: '0 0 14px 0' }}>🔍 إدخال دواء للفاتورة</h4>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Search query */}
                <div style={{ position: 'relative' }}>
                  <input 
                    type="text"
                    className="premium-input"
                    style={{ width: '100%', height: '40px' }}
                    placeholder="ابحث عن الدواء بالاسم أو الباركود لإضافته، أو اكتب الاسم مباشرة إذا كان جديداً..."
                    value={prodSearchQuery}
                    onChange={e => {
                      setProdSearchQuery(e.target.value);
                      setShowSearchResults(true);
                      setSelectedProduct(null);
                    }}
                    onFocus={() => setShowSearchResults(true)}
                  />
                  {showSearchResults && filteredProducts.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '44px',
                      left: 0,
                      right: 0,
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      borderRadius: '12px',
                      zIndex: 10,
                      boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                      maxHeight: '220px',
                      overflowY: 'auto'
                    }}>
                      {filteredProducts.map(p => (
                        <div 
                          key={p.id} 
                          style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
                          onClick={() => {
                            setSelectedProduct(p);
                            setProdSearchQuery(p.tradeName);
                            setProdBuyingPrice(p.buyingPrice || '');
                            setProdSellingPrice(p.sellingPrice || '');
                            if (p.sellingPrice && p.buyingPrice) {
                              const disc = ((p.sellingPrice - p.buyingPrice) / p.sellingPrice) * 100;
                              setProdDiscount(disc > 0 ? Number(disc.toFixed(2)) : '0');
                            } else {
                              setProdDiscount('');
                            }
                            setProdBarcode(p.barcode || '');
                            setProdExpiryDate(p.expiryDate || '');
                            setShowSearchResults(false);
                          }}
                        >
                          <span style={{ fontWeight: 'bold', color: '#fff' }}>{p.tradeName}</span>
                          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>الكمية الحالية: {p.quantity || 0}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected feedback */}
                {selectedProduct && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--accent-green)', display: 'flex', gap: '8px' }}>
                    <span>✓ دواء معروف في المخزن</span>
                    <span>|</span>
                    <span>سعر البيع الحالي: {selectedProduct.sellingPrice} ج.م</span>
                  </div>
                )}

                {/* Form fields for the item */}
                <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.6fr 1.4fr 1.1fr', gap: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>الكمية المشتراة</label>
                    <input 
                      type="number" 
                      min="1" 
                      className="premium-input" 
                      style={{ height: '36px', fontSize: '0.9rem' }} 
                      placeholder="5" 
                      value={prodQty}
                      onChange={e => setProdQty(e.target.value)}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>طريقة الحساب</label>
                    <select 
                      className="premium-input"
                      style={{ height: '36px', fontSize: '0.85rem', cursor: 'pointer', background: 'var(--bg-input)', padding: '0 8px' }}
                      value={buyingPriceMode}
                      onChange={e => setBuyingPriceMode(e.target.value)}
                    >
                      <option value="price">سعر شراء (بمبلغ)</option>
                      <option value="discount">خصم المورد (بنسبة)</option>
                    </select>
                  </div>

                  {buyingPriceMode === 'price' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>سعر الشراء للعلبة</label>
                      <input 
                        type="number" 
                        step="0.01" 
                        className="premium-input" 
                        style={{ height: '36px', fontSize: '0.9rem' }} 
                        placeholder="سعر التكلفة" 
                        value={prodBuyingPrice}
                        onChange={e => {
                          const val = e.target.value;
                          setProdBuyingPrice(val);
                          const numBuy = Number(val);
                          const numSell = Number(prodSellingPrice);
                          if (numSell > 0 && numBuy > 0) {
                            const disc = ((numSell - numBuy) / numSell) * 100;
                            setProdDiscount(disc > 0 ? Number(disc.toFixed(2)) : '0');
                          } else {
                            setProdDiscount('');
                          }
                        }}
                      />
                      {prodDiscount && Number(prodDiscount) > 0 ? (
                        <span style={{ fontSize: '0.72rem', color: 'var(--accent-green)', marginTop: '2px' }}>
                          🏷️ نسبة الخصم: {prodDiscount}%
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>نسبة الخصم (%)</label>
                      <input 
                        type="number" 
                        step="0.1" 
                        className="premium-input" 
                        style={{ height: '36px', fontSize: '0.9rem' }} 
                        placeholder="الخصم %" 
                        value={prodDiscount}
                        onChange={e => {
                          const val = e.target.value;
                          setProdDiscount(val);
                          const numDisc = Number(val);
                          const numSell = Number(prodSellingPrice);
                          if (numSell > 0) {
                            const buy = numSell * (1 - (numDisc / 100));
                            setProdBuyingPrice(buy > 0 ? Number(buy.toFixed(2)) : '0');
                          }
                        }}
                      />
                      {prodBuyingPrice && Number(prodBuyingPrice) > 0 ? (
                        <span style={{ fontSize: '0.72rem', color: 'var(--accent-green)', marginTop: '2px' }}>
                          💵 سعر الشراء: {prodBuyingPrice} ج.م
                        </span>
                      ) : null}
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>سعر البيع للجمهور</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      className="premium-input" 
                      style={{ height: '36px', fontSize: '0.9rem' }} 
                      placeholder="سعر المستهلك" 
                      value={prodSellingPrice}
                      onChange={e => {
                        const val = e.target.value;
                        setProdSellingPrice(val);
                        const numSell = Number(val);
                        const numDisc = Number(prodDiscount);
                        if (numDisc > 0 && numSell > 0) {
                          const buy = numSell * (1 - (numDisc / 100));
                          setProdBuyingPrice(buy > 0 ? Number(buy.toFixed(2)) : '0');
                        } else {
                          const numBuy = Number(prodBuyingPrice);
                          if (numSell > 0 && numBuy > 0) {
                            const disc = ((numSell - numBuy) / numSell) * 100;
                            setProdDiscount(disc > 0 ? Number(disc.toFixed(2)) : '0');
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>الباركود (اختياري)</label>
                    <input 
                      type="text" 
                      className="premium-input" 
                      style={{ height: '36px', fontSize: '0.9rem' }} 
                      placeholder="قراءة بالأسكانر أو كتابته" 
                      value={prodBarcode}
                      onChange={e => setProdBarcode(e.target.value)}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>تاريخ الانتهاء (اختياري)</label>
                    <input 
                      type="text" 
                      className="premium-input" 
                      style={{ height: '36px', fontSize: '0.9rem' }} 
                      placeholder="YYYY-MM" 
                      value={prodExpiryDate}
                      onChange={e => setProdExpiryDate(e.target.value)}
                    />
                  </div>
                </div>

                <button 
                  type="button" 
                  className="gradient-btn" 
                  onClick={handleAddItemToInvoice}
                  style={{ padding: '10px', fontSize: '0.88rem', borderRadius: '10px', marginTop: '4px', border: 'none', cursor: 'pointer' }}
                >
                  ➕ إدراج دواء في الفاتورة
                </button>
              </div>
            </div>

            {/* List of items in current invoice */}
            <div style={{ marginTop: '10px' }}>
              <h4 style={{ fontSize: '0.95rem', color: '#fff', fontWeight: 'bold', margin: '0 0 10px 0' }}>📋 أصناف الفاتورة المضافة ({invoiceItems.length})</h4>
              {invoiceItems.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', background: 'rgba(255,255,255,0.01)', borderRadius: '12px', border: '1px solid var(--border)', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  لم يتم إضافة أي دواء للفاتورة بعد. استخدم النموذج بالأعلى للبدء.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'right' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '10px 8px' }}>اسم الدواء</th>
                        <th style={{ padding: '10px 8px' }}>الكمية</th>
                        <th style={{ padding: '10px 8px' }}>سعر الشراء</th>
                        <th style={{ padding: '10px 8px' }}>سعر البيع</th>
                        <th style={{ padding: '10px 8px' }}>الإجمالي</th>
                        <th style={{ padding: '10px 8px', width: '50px' }}>إجراء</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceItems.map((item, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                          <td style={{ padding: '10px 8px', fontWeight: 'bold', color: '#fff' }}>{item.tradeName}</td>
                          <td style={{ padding: '10px 8px', fontFamily: 'Outfit' }}>{item.quantity}</td>
                          <td style={{ padding: '10px 8px', fontFamily: 'Outfit' }}>{item.buyingPrice} ج.م</td>
                          <td style={{ padding: '10px 8px', fontFamily: 'Outfit' }}>{item.sellingPrice} ج.م</td>
                          <td style={{ padding: '10px 8px', fontFamily: 'Outfit', fontWeight: 'bold', color: 'var(--accent-gold)' }}>{(item.quantity * item.buyingPrice).toFixed(2)} ج.م</td>
                          <td style={{ padding: '10px 8px' }}>
                            <button 
                              type="button" 
                              onClick={() => handleRemoveInvoiceItem(index)}
                              style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--accent-pink)', border: 'none', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer' }}
                            >
                              حذف
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>

          {/* Left Side: Summary & Payment Submission */}
          <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '24px', height: 'fit-content' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fff', margin: 0, borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
              💳 ملخص الدفع والترحيل
            </h3>

            {/* Calculations Card */}
            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>إجمالي الأصناف:</span>
                <span style={{ fontWeight: 'bold', fontFamily: 'Outfit' }}>{invoiceItems.length}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>إجمالي الفاتورة:</span>
                <span style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--accent-gold)', fontFamily: 'Outfit' }}>
                  {invoiceItems.reduce((sum, item) => sum + (item.quantity * item.buyingPrice), 0).toFixed(2)} ج.م
                </span>
              </div>
            </div>

            {/* Payment Method selector */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>طريقة السداد للمورد</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                <button 
                  type="button" 
                  className={paymentType === 'cash' ? 'gradient-btn' : 'gradient-btn-secondary'}
                  onClick={() => setPaymentType('cash')}
                  style={{ borderRadius: '10px', padding: '10px 0', fontSize: '0.85rem' }}
                >
                  💵 نقداً (كاش الوردية)
                </button>
                <button 
                  type="button" 
                  className={paymentType === 'credit' ? 'gradient-btn' : 'gradient-btn-secondary'}
                  onClick={() => setPaymentType('credit')}
                  style={{ borderRadius: '10px', padding: '10px 0', fontSize: '0.85rem' }}
                >
                  ⏳ آجل (دين للمورد)
                </button>
                <button 
                  type="button" 
                  className={paymentType === 'partial' ? 'gradient-btn' : 'gradient-btn-secondary'}
                  onClick={() => setPaymentType('partial')}
                  style={{ borderRadius: '10px', padding: '10px 0', fontSize: '0.85rem' }}
                >
                  ⚖️ جزئي
                </button>
              </div>
            </div>

            {/* Partial amount paid input */}
            {paymentType === 'partial' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>المبلغ المدفوع كاش</label>
                <input 
                  type="number" 
                  min="0"
                  step="0.1"
                  className="premium-input" 
                  placeholder="المبلغ المسدد الآن"
                  value={paidAmount}
                  onChange={e => setPaidAmount(e.target.value)}
                />
                <span style={{ fontSize: '0.8rem', color: 'var(--accent-pink)' }}>
                  المتبقي للآجل: {(invoiceItems.reduce((sum, item) => sum + (item.quantity * item.buyingPrice), 0) - (Number(paidAmount) || 0)).toFixed(2)} ج.م
                </span>
              </div>
            )}

            <button 
              type="button" 
              className="gradient-btn" 
              onClick={handleSaveInvoice}
              style={{ padding: '16px', fontSize: '1rem', fontWeight: 'bold', borderRadius: '14px', border: 'none', cursor: 'pointer', display: 'flex', gap: '8px', justifyContent: 'center' }}
            >
              📥 حفظ الفاتورة وتحديث المخزن
            </button>

          </div>
        </div>
      )}

      {/* SUBTAB 2: SUPPLIERS DIRECTORY */}
      {subTab === 'suppliers' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fff', margin: 0 }}>👥 قائمة الموردين والمخازن ({suppliers.length})</h3>
            <button 
              className="gradient-btn" 
              onClick={() => { setEditingSupplier(null); setSupName(''); setSupPhone(''); setSupAddress(''); setSupBalance(0); setIsSupplierModalOpen(true); }}
              style={{ borderRadius: '10px', padding: '8px 16px', fontSize: '0.88rem' }}
            >
              ➕ مورد جديد
            </button>
          </div>

          <div className="premium-card" style={{ padding: 0, overflow: 'hidden' }}>
            {suppliers.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                لا يوجد موردين مسجلين حتى الآن. انقر على "مورد جديد" بالأعلى للبدء.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '0.92rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,0.01)', color: 'var(--text-secondary)' }}>
                      <th style={{ padding: '16px' }}>اسم المورد / المخزن</th>
                      <th style={{ padding: '16px' }}>رقم الهاتف</th>
                      <th style={{ padding: '16px' }}>العنوان</th>
                      <th style={{ padding: '16px' }}>رصيد الحساب المستحق</th>
                      <th style={{ padding: '16px', width: '250px' }}>إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map(s => (
                      <tr key={s.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)', transition: 'background 0.2s' }}>
                        <td style={{ padding: '16px', fontWeight: 'bold', color: '#fff' }}>{s.name}</td>
                        <td style={{ padding: '16px', fontFamily: 'Outfit' }}>{s.phone || '—'}</td>
                        <td style={{ padding: '16px' }}>{s.address || '—'}</td>
                        <td style={{ padding: '16px', fontFamily: 'Outfit', fontWeight: 'bold', color: (s.balance || 0) > 0 ? 'var(--accent-pink)' : 'var(--accent-green)' }}>
                          {(s.balance || 0).toFixed(2)} ج.م
                        </td>
                        <td style={{ padding: '16px', display: 'flex', gap: '8px' }}>
                          <button 
                            className="gradient-btn"
                            style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '8px' }}
                            onClick={() => {
                              setPaymentSupplier(s);
                              setPaymentAmount('');
                              setPaymentNotes('');
                              setIsPaymentModalOpen(true);
                            }}
                          >
                            💸 سداد دفعة
                          </button>
                          <button 
                            className="gradient-btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '8px' }}
                            onClick={() => {
                              setEditingSupplier(s);
                              setSupName(s.name);
                              setSupPhone(s.phone || '');
                              setSupAddress(s.address || '');
                              setSupBalance(s.balance || 0);
                              setIsSupplierModalOpen(true);
                            }}
                          >
                            ✏️ تعديل
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SUBTAB 3: INVOICES LOG */}
      {subTab === 'invoices_log' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fff', margin: 0 }}>📜 سجل فواتير الشراء المستلمة ({purchases.length})</h3>

          {selectedInvoice ? (
            /* Selected Invoice Details View */
            <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
                <h4 style={{ fontSize: '1.1rem', color: '#fff', margin: 0 }}>📄 تفاصيل فاتورة شراء: {selectedInvoice.invoiceNumber}</h4>
                <button 
                  className="gradient-btn-secondary" 
                  onClick={() => setSelectedInvoice(null)}
                  style={{ borderRadius: '8px', padding: '6px 12px' }}
                >
                  ← العودة للسجل
                </button>
              </div>

              {/* Invoice details grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', background: 'rgba(255,255,255,0.02)', padding: '16px', borderRadius: '12px' }}>
                <div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>المورد: </span>
                  <span style={{ fontWeight: 'bold', color: '#fff' }}>{selectedInvoice.supplierName}</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>تاريخ الاستلام: </span>
                  <span style={{ fontFamily: 'Outfit' }}>{new Date(selectedInvoice.date).toLocaleDateString('ar-EG', { dateStyle: 'long' })}</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>طريقة الدفع: </span>
                  <span style={{ fontWeight: 'bold', color: 'var(--primary)' }}>
                    {selectedInvoice.paymentType === 'cash' ? 'نقداً (كامل)' : selectedInvoice.paymentType === 'credit' ? 'آجل' : 'جزئي'}
                  </span>
                </div>
              </div>

              {/* Invoice Items table */}
              <div>
                <h5 style={{ fontSize: '0.95rem', color: '#fff', margin: '0 0 10px 0' }}>أصناف الفاتورة:</h5>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '0.88rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '10px' }}>الدواء</th>
                        <th style={{ padding: '10px' }}>الكمية المستلمة</th>
                        <th style={{ padding: '10px' }}>سعر الشراء</th>
                        <th style={{ padding: '10px' }}>سعر البيع</th>
                        <th style={{ padding: '10px' }}>تاريخ الانتهاء</th>
                        <th style={{ padding: '10px' }}>الإجمالي</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items.map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                          <td style={{ padding: '10px', fontWeight: 'bold', color: '#fff' }}>{item.tradeName}</td>
                          <td style={{ padding: '10px', fontFamily: 'Outfit' }}>{item.quantity}</td>
                          <td style={{ padding: '10px', fontFamily: 'Outfit' }}>{item.buyingPrice.toFixed(2)} ج.م</td>
                          <td style={{ padding: '10px', fontFamily: 'Outfit' }}>{item.sellingPrice.toFixed(2)} ج.م</td>
                          <td style={{ padding: '10px', fontFamily: 'Outfit' }}>{typeof item.expiryDate === 'object' && item.expiryDate ? (item.expiryDate.year ? `${item.expiryDate.year}-${String(item.expiryDate.month).padStart(2, '0')}-${String(item.expiryDate.day).padStart(2, '0')}` : JSON.stringify(item.expiryDate)) : (item.expiryDate || '—')}</td>
                          <td style={{ padding: '10px', fontFamily: 'Outfit', fontWeight: 'bold', color: 'var(--accent-gold)' }}>
                            {(item.quantity * item.buyingPrice).toFixed(2)} ج.م
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Financial Summary */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '24px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>إجمالي الفاتورة: </span>
                  <span style={{ fontSize: '1.2rem', fontWeight: 'bold', fontFamily: 'Outfit', color: 'var(--accent-gold)' }}>{selectedInvoice.totalAmount.toFixed(2)} ج.م</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>المسدد: </span>
                  <span style={{ fontSize: '1.2rem', fontWeight: 'bold', fontFamily: 'Outfit', color: 'var(--accent-green)' }}>{selectedInvoice.paidAmount.toFixed(2)} ج.م</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>المتبقي: </span>
                  <span style={{ fontSize: '1.2rem', fontWeight: 800, fontFamily: 'Outfit', color: 'var(--accent-pink)' }}>{selectedInvoice.remainingAmount.toFixed(2)} ج.م</span>
                </div>
              </div>

            </div>
          ) : (
            /* Log list */
            <div className="premium-card" style={{ padding: 0, overflow: 'hidden' }}>
              {purchases.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  لا يوجد فواتير شراء مسجلة بعد.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '0.92rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,0.01)', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '16px' }}>رقم الفاتورة</th>
                        <th style={{ padding: '16px' }}>المورد</th>
                        <th style={{ padding: '16px' }}>التاريخ</th>
                        <th style={{ padding: '16px' }}>إجمالي الفاتورة</th>
                        <th style={{ padding: '16px' }}>الحالة</th>
                        <th style={{ padding: '16px' }}>إجراء</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchases.map(p => (
                        <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)', transition: 'background 0.2s' }}>
                          <td style={{ padding: '16px', fontWeight: 'bold', color: '#fff' }}>{p.invoiceNumber}</td>
                          <td style={{ padding: '16px' }}>{p.supplierName}</td>
                          <td style={{ padding: '16px', fontFamily: 'Outfit' }}>{new Date(p.date).toLocaleDateString('ar-EG')}</td>
                          <td style={{ padding: '16px', fontFamily: 'Outfit', fontWeight: 'bold' }}>{p.totalAmount.toFixed(2)} ج.م</td>
                          <td style={{ padding: '16px' }}>
                            {p.remainingAmount <= 0 ? (
                              <span style={{ color: 'var(--accent-green)', background: 'rgba(16, 185, 129, 0.1)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem' }}>مدفوعة بالكامل</span>
                            ) : p.paidAmount <= 0 ? (
                              <span style={{ color: 'var(--accent-pink)', background: 'rgba(244, 63, 94, 0.1)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem' }}>آجل غير مدفوع</span>
                            ) : (
                              <span style={{ color: 'var(--accent-gold)', background: 'rgba(217, 119, 6, 0.1)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem' }}>مدفوع جزئياً</span>
                            )}
                          </td>
                          <td style={{ padding: '16px' }}>
                            <button 
                              className="gradient-btn-secondary" 
                              onClick={() => setSelectedInvoice(p)}
                              style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '8px' }}
                            >
                              👁️ استعراض الفاتورة
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Supplier Modal (Add / Edit) */}
      {isSupplierModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999
        }}>
          <div className="premium-card fade-in-content" style={{ width: '100%', maxWidth: '480px', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', color: '#fff', fontWeight: 800 }}>
                {editingSupplier ? '✏️ تعديل بيانات المورد' : '➕ إضافة مورد جديد'}
              </h3>
              <button 
                type="button" 
                onClick={() => setIsSupplierModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontSize: '1.2rem', cursor: 'pointer' }}
              >✕</button>
            </div>

            <form onSubmit={handleSaveSupplier} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>اسم المورد / المخزن</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  required
                  placeholder="مثال: مخزن المتحدة للأدوية"
                  value={supName}
                  onChange={e => setSupName(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>رقم الهاتف</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  placeholder="أدخل رقم التواصل"
                  value={supPhone}
                  onChange={e => setSupPhone(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>العنوان</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  placeholder="عنوان المخزن"
                  value={supAddress}
                  onChange={e => setSupAddress(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>رصيد الحساب المبدئي (مستحق له)</label>
                <input 
                  type="number" 
                  disabled={!!editingSupplier} // disabled during edit to avoid accidental manual balance override
                  className="premium-input" 
                  placeholder="0.00"
                  value={supBalance}
                  onChange={e => setSupBalance(e.target.value)}
                />
              </div>

              <button 
                type="submit" 
                className="gradient-btn"
                style={{ padding: '12px', fontWeight: 'bold', fontSize: '1rem', marginTop: '12px', border: 'none', borderRadius: '12px', cursor: 'pointer' }}
              >
                💾 حفظ البيانات
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Supplier Payment Modal (سند صرف دفعة) */}
      {isPaymentModalOpen && paymentSupplier && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999
        }}>
          <div className="premium-card fade-in-content" style={{ width: '100%', maxWidth: '480px', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', color: '#fff', fontWeight: 800 }}>
                💸 سداد دفعة مالية للمورد
              </h3>
              <button 
                type="button" 
                onClick={() => setIsPaymentModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontSize: '1.2rem', cursor: 'pointer' }}
              >✕</button>
            </div>

            <div style={{ marginBottom: '16px', background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '10px', fontSize: '0.9rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>المورد:</span>
                <span style={{ fontWeight: 'bold', color: '#fff' }}>{paymentSupplier.name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>الحساب المستحق:</span>
                <span style={{ fontWeight: 'bold', color: 'var(--accent-pink)', fontFamily: 'Outfit' }}>{(paymentSupplier.balance || 0).toFixed(2)} ج.م</span>
              </div>
            </div>

            <form onSubmit={handleRecordPayment} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>المبلغ المدفوع (يخصم من كاش الوردية)</label>
                <input 
                  type="number" 
                  min="0.1" 
                  step="0.01"
                  className="premium-input" 
                  required
                  placeholder="المبلغ بالجنيه"
                  value={paymentAmount}
                  onChange={e => setPaymentAmount(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>ملاحظات / رقم السند</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  placeholder="مثال: دفعة شيك أو سند صرف يدوي"
                  value={paymentNotes}
                  onChange={e => setPaymentNotes(e.target.value)}
                />
              </div>

              <button 
                type="submit" 
                className="gradient-btn"
                style={{ padding: '12px', fontWeight: 'bold', fontSize: '1rem', marginTop: '12px', border: 'none', borderRadius: '12px', cursor: 'pointer' }}
              >
                💸 تأكيد صرف الدفعة
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// 3.6. Customer Guest Storefront
/* CustomerStorefront component extracted to CustomerStorefront.jsx */
function ReportsView({ sales, inventory, setSales, setInventory, user, employees, isAdminVerified, setIsAdminVerified, adminPin, currentUserData, printSaleReceipt, shareSaleViaWhatsApp, loyaltyCustomers, setLoyaltyCustomers, isLoyaltyEnabled, setGlobalLoading }) {
  const [refundInvoice, setRefundInvoice] = useState(null);
  const [activeCustomerDetails, setActiveCustomerDetails] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [hoveredSubTab, setHoveredSubTab] = useState(null);
  const [systemDialog, setSystemDialog] = useState(null); // Custom confirmation and alert system modal

  const [accountingTx, setAccountingTx] = useState([]);
  const [txType, setTxType] = useState('expense'); // expense or revenue
  const [txAmount, setTxAmount] = useState('');
  const [txDesc, setTxDesc] = useState('');
  const [txPin, setTxPin] = useState('');

  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showRevenueForm, setShowRevenueForm] = useState(false);
  const [localAdminPin, setLocalAdminPin] = useState('');
  const [localAdminError, setLocalAdminError] = useState('');
  const [selectedPreviewInvoice, setSelectedPreviewInvoice] = useState(null);

  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editCustName, setEditCustName] = useState('');
  const [editCustPhone, setEditCustPhone] = useState('');
  const [editPaymentMethod, setEditPaymentMethod] = useState('cash');
  const [editNote, setEditNote] = useState('');

  const handleSendToEta = async (sale) => {
    const updatedSaleSending = { ...sale, etaStatus: 'sending' };
    setSales(sales.map(s => s.id === sale.id ? updatedSaleSending : s));

    setTimeout(async () => {
      const uuidPart1 = Math.random().toString(16).slice(2, 10);
      const uuidPart2 = Math.random().toString(16).slice(2, 6);
      const uuidPart3 = Math.random().toString(16).slice(2, 6);
      const uuidPart4 = Math.random().toString(16).slice(2, 6);
      const uuidPart5 = Math.random().toString(16).slice(2, 14);
      const mockUuid = `${uuidPart1}-${uuidPart2}-${uuidPart3}-${uuidPart4}-${uuidPart5}`;

      const updatedSaleSuccess = { 
        ...sale, 
        etaStatus: 'sent_success', 
        etaUuid: mockUuid 
      };

      try {
        await updateDoc(doc(db, 'users', user?.uid, 'sales', sale.id), {
          etaStatus: 'sent_success',
          etaUuid: mockUuid
        });
        setSales(prev => prev.map(s => s.id === sale.id ? updatedSaleSuccess : s));
      } catch (err) {
        console.error("Failed to update ETA status:", err);
        setSystemDialog({
          title: 'خطأ',
          message: 'حدث خطأ أثناء حفظ حالة الفاتورة في قاعدة البيانات.',
          type: 'alert'
        });
        setSales(prev => prev.map(s => s.id === sale.id ? { ...sale, etaStatus: 'failed' } : s));
      }
    }, 2000);
  };

  const handleOpenEditInvoice = (sale) => {
    setEditingInvoice(sale);
    setEditCustName(sale.customerName || '');
    setEditCustPhone(sale.customerPhone || '');
    setEditPaymentMethod(sale.paymentMethod || 'cash');
    setEditNote(sale.notes || '');
  };

  const handleSaveInvoiceEdits = async () => {
    if (!user || !editingInvoice) return;
    setGlobalLoading({
      active: true,
      title: 'حفظ التعديلات 💾',
      message: 'جاري تحديث بيانات الفاتورة في السيرفر...'
    });
    try {
      const saleRef = doc(db, 'users', user.uid, 'sales', editingInvoice.id);
      const updatedFields = {
        customerName: editCustName.trim(),
        customerPhone: editCustPhone.trim(),
        paymentMethod: editPaymentMethod,
        notes: editNote.trim()
      };
      
      await updateDoc(saleRef, updatedFields);
      
      // Update local states
      setReportSales(prev => prev.map(s => s.id === editingInvoice.id ? { ...s, ...updatedFields } : s));
      setPaginatedSales(prev => prev.map(s => s.id === editingInvoice.id ? { ...s, ...updatedFields } : s));
      
      setEditingInvoice(null);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("✅ تم تعديل الفاتورة بنجاح!");
      }
    } catch (err) {
      console.error("Error editing invoice:", err);
      window.triggerDrMasrToast("❌ فشل في تعديل الفاتورة.");
    } finally {
      setGlobalLoading({ active: false, title: '', message: '' });
    }
  };

  // On-demand period states for quota savings
  const [period, setPeriod] = useState('today'); // today, this_month, custom
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [reportSales, setReportSales] = useState([]);
  const [reportsLoadedPeriod, setReportsLoadedPeriod] = useState('');
  const [loadingReport, setLoadingReport] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');

  // --- Paginated Sales States for Firebase Quota Optimization ---
  const [paginatedSales, setPaginatedSales] = useState([]);
  const [salesDocSnapshots, setSalesDocSnapshots] = useState([null]);
  const [salesTotalCount, setSalesTotalCount] = useState(0);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesFallbackMode, setSalesFallbackMode] = useState(false);

  const getPeriodBoundaries = () => {
    const now = new Date();
    let startIso, endIso;
    
    if (period === 'today') {
      const calStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
      const busStart = currentUserData?.businessDayStart ? new Date(currentUserData.businessDayStart).getTime() : 0;
      const isWithinLast30Hours = (Date.now() - busStart) < (30 * 60 * 60 * 1000);
      const startTs = (busStart && isWithinLast30Hours) ? busStart : calStart;
      startIso = new Date(startTs).toISOString();
      endIso = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();
    } else if (period === 'this_month') {
      startIso = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).toISOString();
      endIso = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
    } else if (period === 'three_months') {
      startIso = new Date(now.getFullYear(), now.getMonth() - 2, 1, 0, 0, 0, 0).toISOString();
      endIso = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
    } else if (period === 'custom') {
      if (customStart && customEnd) {
        startIso = new Date(new Date(customStart).setHours(0, 0, 0, 0)).toISOString();
        endIso = new Date(new Date(customEnd).setHours(23, 59, 59, 999)).toISOString();
      } else {
        startIso = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).toISOString();
        endIso = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();
      }
    }
    return { startIso, endIso };
  };

  const fetchSalesPage = async (pageNumber, forcePeriod = period) => {
    if (!user) return;
    setSalesLoading(true);
    try {
      const { startIso, endIso } = getPeriodBoundaries();
      const salesCol = collection(db, 'users', user.uid, 'sales');

      // 1. Fetch aggregate count from server (very cheap quota-wise)
      const qCount = query(
        salesCol,
        where('timestamp', '>=', startIso),
        where('timestamp', '<=', endIso)
      );
      const countSnap = await getCountFromServer(qCount);
      const totalCount = countSnap.data().count;
      setSalesTotalCount(totalCount);

      if (salesFallbackMode) {
        // Fallback local pagination from loaded reportSales
        const sorted = [...reportSales].sort((a,b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
        const startIndex = (pageNumber - 1) * 6;
        const pageData = sorted.slice(startIndex, startIndex + 6);
        setPaginatedSales(pageData);
      } else {
        let q;
        const startDoc = salesDocSnapshots[pageNumber - 1];
        
        if (startDoc) {
          q = query(
            salesCol,
            where('timestamp', '>=', startIso),
            where('timestamp', '<=', endIso),
            orderBy('timestamp', 'desc'),
            startAfter(startDoc),
            limit(6)
          );
        } else {
          q = query(
            salesCol,
            where('timestamp', '>=', startIso),
            where('timestamp', '<=', endIso),
            orderBy('timestamp', 'desc'),
            limit(6)
          );
        }
        
        const snap = await getDocs(q);
        const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPaginatedSales(fetched);
        
        if (snap.docs.length > 0) {
          const nextSnapshots = [...salesDocSnapshots];
          nextSnapshots[pageNumber] = snap.docs[snap.docs.length - 1];
          setSalesDocSnapshots(nextSnapshots);
        }
      }
    } catch (err) {
      console.error("Firestore paginated sales fetch error:", err);
      // Fallback local pagination
      setSalesFallbackMode(true);
      const sorted = [...reportSales].sort((a,b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
      setSalesTotalCount(sorted.length);
      const startIndex = (pageNumber - 1) * 6;
      const pageData = sorted.slice(startIndex, startIndex + 6);
      setPaginatedSales(pageData);
    } finally {
      setSalesLoading(false);
    }
  };

  const handlePageChange = async (newPage) => {
    setInvoicePage(newPage);
    if (invoiceSearch.trim() === '') {
      await fetchSalesPage(newPage);
    }
  };

  // Reset pagination state when period or base reportSales array changes
  useEffect(() => {
    setInvoicePage(1);
    setSalesDocSnapshots([null]);
    setSalesFallbackMode(false);
    fetchSalesPage(1);
  }, [user, period, customStart, customEnd, reportSales]);

  const handleRemoteInvoiceSearch = async () => {
    if (!invoiceSearch) return;
    setGlobalLoading({ active: true, title: 'استدعاء سحابي', message: 'جاري البحث عن الفاتورة القديمة في السيرفر...' });
    try {
      const q = query(collection(db, 'users', user.uid, 'sales'), where('invoiceId', '==', invoiceSearch));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const found = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setReportSales(prev => {
          const combined = [...found, ...prev];
          return Array.from(new Map(combined.map(item => [item.id || item.invoiceId, item])).values());
        });
        if (window.triggerDrMasrToast) window.triggerDrMasrToast("✅ تم سحب الفاتورة القديمة بنجاح!");
      } else {
        if (window.triggerDrMasrToast) window.triggerDrMasrToast("❌ لم يتم العثور على فاتورة بهذا الرقم.");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setGlobalLoading({ active: false, title: '', message: '' });
    }
  };

  const [invoicePage, setInvoicePage] = useState(1);
  const [reportsSubTab, setReportsSubTab] = useState('sales'); // sales or customers

  const [confirmEndDay, setConfirmEndDay] = useState(false);
  const [endingDay, setEndingDay] = useState(false);

  const handleEndBusinessDay = async () => {
    if (!confirmEndDay) return;
    setEndingDay(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        businessDayStart: new Date().toISOString()
      });
      setSystemDialog({
        title: 'نجاح',
        message: 'تم إنهاء اليوم المالي بنجاح وبدء يوم جديد. تم تصفير التقارير.',
        type: 'success'
      });
      setConfirmEndDay(false);
      // Force reload of reports
      loadReportsData();
    } catch (err) {
      console.error(err);
      setSystemDialog({
        title: 'خطأ',
        message: 'حدث خطأ أثناء محاولة إنهاء اليوم.',
        type: 'error'
      });
    } finally {
      setEndingDay(false);
    }
  };


  const handleReprintInvoice = (sale) => {
    printSaleReceipt(sale);
  };

  const formatCurrency = (num) => {
    return Number(num || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const getCustomerAnalytics = (phone) => {
    if (!phone) return { customerSales: [], topProducts: [], stats: {} };
    const customerSales = sales.filter(s => s.customerPhone === phone);
    
    // Sort chronological: newest sales first
    customerSales.sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
    
    const productCounts = {};
    let totalDiscount = 0;
    let deliveryCount = 0;
    
    customerSales.forEach(sale => {
      totalDiscount += (sale.discount || 0) + (sale.pointsDiscount || 0);
      if (sale.isDelivery) deliveryCount++;
      if (Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          const name = item.tradeName || 'صنف غير محدد';
          productCounts[name] = (productCounts[name] || 0) + (item.quantity || 0);
        });
      }
    });
    
    const topProducts = Object.entries(productCounts)
      .map(([name, qty]) => ({ name, qty }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 5);
      
    const totalSpend = customerSales.reduce((acc, s) => acc + (s.grandTotal || 0), 0);
    const avgOrderValue = customerSales.length > 0 ? totalSpend / customerSales.length : 0;
    
    return {
      customerSales,
      topProducts,
      stats: {
        totalSpend,
        avgOrderValue,
        totalDiscount,
        deliveryCount,
        inStoreCount: customerSales.length - deliveryCount
      }
    };
  };

  const loadReportsData = async () => {
    setLoadingReport(true);
    setGlobalLoading({
      active: true,
      title: 'جاري استدعاء ومعالجة التقارير المخصصة...',
      message: 'نحن نقوم الآن بجلب وتحليل مبيعاتك وحساباتك للفترة المحددة مباشرة من سحابة Firebase الآمنة لتوفير إحصائيات متكاملة.'
    });

    try {
      const now = new Date();
      let startTs, endTs;
      
      if (period === 'today') {
        const calStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
        const busStart = currentUserData?.businessDayStart ? new Date(currentUserData.businessDayStart).getTime() : 0;
        const isWithinLast30Hours = (Date.now() - busStart) < (30 * 60 * 60 * 1000);
        startTs = (busStart && isWithinLast30Hours) ? busStart : calStart;
        endTs = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
      } else if (period === 'this_month') {
        startTs = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).getTime();
        endTs = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
      } else if (period === 'three_months') {
        startTs = new Date(now.getFullYear(), now.getMonth() - 2, 1, 0, 0, 0, 0).getTime();
        endTs = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
      } else if (period === 'custom') {
        if (!customStart || !customEnd) {
          setSystemDialog({
            title: 'تنبيه',
            message: 'يرجى تحديد تاريخ البداية والنهاية للفترة المخصصة.',
            type: 'alert'
          });
          setLoadingReport(false);
          setGlobalLoading({ active: false, title: '', message: '' });
          return;
        }
        startTs = new Date(new Date(customStart).setHours(0, 0, 0, 0)).getTime();
        endTs = new Date(new Date(customEnd).setHours(23, 59, 59, 999)).getTime();
      }

      // Query Firestore directly for the date range
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const salesCol = collection(db, 'users', user.uid, 'sales');
      const startIso = new Date(startTs).toISOString();
      const endIso = new Date(endTs).toISOString();
      
      const q = query(
        salesCol,
        where('timestamp', '>=', startIso),
        where('timestamp', '<=', endIso)
      );
      
      const snap = await getDocs(q);
      const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setReportSales(fetched);
      setReportsLoadedPeriod(period === 'custom' ? `custom_${customStart}_${customEnd}` : period);
    } catch (e) {
      console.error("Failed to load reports from Firestore:", e);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ فشل استدعاء تقارير المبيعات.");
      }
    } finally {
      setLoadingReport(false);
      setGlobalLoading({ active: false, title: '', message: '' });
    }
  };

  useEffect(() => {
    // Only load today automatically; other periods are loaded on-demand to optimize reads
    if (user && period === 'today') {
      loadReportsData();
    }
  }, [user, period, sales, currentUserData]);

  useEffect(() => {
    const fetchAccounting = async () => {
      try {
        const saved = localStorage.getItem('drmasr_accounting_' + (user?.uid));
        if (saved) setAccountingTx(JSON.parse(saved));

        const q = query(collection(db, 'users', user?.uid, 'accounting'));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setAccountingTx(fetched);
          localStorage.setItem('drmasr_accounting_' + (user?.uid), JSON.stringify(fetched));
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (user) fetchAccounting();
  }, [user]);

  const handleAddAccounting = async (e) => {
    e.preventDefault();
    if (!txAmount || !txDesc) return;

    let recorderName = '';
    const foundEmp = employees?.find(emp => emp.pin === txPin.trim());
    if (foundEmp) {
      recorderName = foundEmp.name;
    } else if (txPin.trim() === adminPin) {
      recorderName = 'المدير المسؤول 🛡️';
    } else {
      setSystemDialog({
        title: 'خطأ في التحقق',
        message: 'رمز الـ PIN المكتوب غير صحيح! يرجى إدخال رمز الـ PIN الخاص بك أو رمز الإدارة.',
        type: 'alert'
      });
      return;
    }

    const newTx = {
      type: txType,
      amount: Number(txAmount),
      description: txDesc.trim(),
      timestamp: new Date().toISOString(),
      recordedBy: recorderName
    };

    let nextTx = [newTx, ...accountingTx];
    try {
      const ref = await addDoc(collection(db, 'users', user?.uid, 'accounting'), newTx);
      newTx.id = ref.id;
      nextTx = [newTx, ...accountingTx];
    } catch (err) {}

    setAccountingTx(nextTx);
    localStorage.setItem('drmasr_accounting_' + (user?.uid), JSON.stringify(nextTx));
    setTxAmount('');
    setTxDesc('');
    setTxPin('');
    setShowExpenseForm(false);
    setShowRevenueForm(false);

    setSystemDialog({
      title: 'تم بنجاح!',
      message: `تم تسجيل ${txType === 'expense' ? 'المصروف' : 'الإيراد الإضافي'} بنجاح بواسطة ${recorderName}.`,
      type: 'alert'
    });
  };

  const handleDeleteAccounting = (id) => {
    setSystemDialog({
      title: 'تأكيد الحذف',
      message: 'هل أنت متأكد من رغبتك في حذف هذا القيد المالي نهائياً؟',
      type: 'confirm',
      onConfirm: async () => {
        setSystemDialog(null);
        const nextTx = accountingTx.filter(t => (t.id || t.timestamp) !== id);
        setAccountingTx(nextTx);
        localStorage.setItem('drmasr_accounting_' + (user?.uid), JSON.stringify(nextTx));
        try {
          await deleteDoc(doc(db, 'users', user?.uid, 'accounting', id));
        } catch (e) {}
      }
    });
  };

  const extraExpenses = accountingTx.filter(t => t.type === 'expense').reduce((acc, t) => acc + t.amount, 0);
  const extraRevenues = accountingTx.filter(t => t.type === 'revenue').reduce((acc, t) => acc + t.amount, 0);

  const salesVolume = reportSales
    .filter(s => !(s.isDelivery && s.deliveryStatus !== 'delivered'))
    .reduce((acc, s) => acc + s.grandTotal, 0) + extraRevenues;

  const deliverySales = reportSales.filter(s => s.isDelivery && s.deliveryStatus === 'delivered' && (!s.onlineOrderId || s.onlineOrderId === ''));
  const deliveryVolume = deliverySales.reduce((acc, s) => acc + s.grandTotal, 0);
  const deliveryCount = deliverySales.length;

  const calculateTotalProfit = () => {
    let profit = 0;
    reportSales.forEach(sale => {
      if (sale.isRefunded) return;
      if (sale.isDelivery && sale.deliveryStatus !== 'delivered') return;
      sale.items.forEach(sold => {
        const item = inventory.find(i => i.barcode === sold.barcode);
        let itemCost = sold.buyingPrice !== undefined ? sold.buyingPrice : (item ? (item.buyingPrice || 0) : 0);
        if (sold.subUnit && sold.subUnit.ratio) {
          itemCost = itemCost / sold.subUnit.ratio;
        }
        profit += (sold.price - itemCost) * sold.quantity;
      });
      profit -= (sale.discount || 0) + (sale.pointsDiscount || 0);
    });
    return profit + extraRevenues - extraExpenses;
  };

  const calculateOnlineProfit = () => {
    let profit = 0;
    reportSales.forEach(sale => {
      if (sale.isRefunded) return;
      if (sale.isDelivery && sale.deliveryStatus !== 'delivered') return;
      const isOnline = (sale.cashierName === 'أونلاين (QR Store)') || (sale.onlineOrderId && sale.onlineOrderId !== '');
      if (!isOnline) return;
      sale.items.forEach(sold => {
        const item = inventory.find(i => i.barcode === sold.barcode);
        let itemCost = sold.buyingPrice !== undefined ? sold.buyingPrice : (item ? (item.buyingPrice || 0) : 0);
        if (sold.subUnit && sold.subUnit.ratio) {
          itemCost = itemCost / sold.subUnit.ratio;
        }
        profit += (sold.price - itemCost) * sold.quantity;
      });
      profit -= (sale.discount || 0) + (sale.pointsDiscount || 0);
    });
    return profit;
  };

  const calculateWarehouseProfit = () => {
    let potential = 0;
    inventory.forEach(item => {
      const cost = item.buyingPrice || 0;
      potential += Math.max(0, item.sellingPrice - cost) * (item.quantity || 0);
    });
    return potential;
  };

  const calculateWarehouseSellingValue = () => {
    return inventory.reduce((acc, item) => acc + ((item.sellingPrice || 0) * (item.quantity || 0)), 0);
  };

  const calculateWarehouseCapital = () => {
    return inventory.reduce((acc, item) => acc + ((item.buyingPrice || 0) * (item.quantity || 0)), 0);
  };

  const handleRefundItem = async (saleId, itemBarcode, returnQty, tradeName) => {
    if (returnQty <= 0) return;

    setSystemDialog({
      title: 'تأكيد عملية الاسترجاع',
      message: 'هل أنت متأكد من رغبتك في إتمام استرجاع هذا الصنف إلى المخزن وإعادة احتساب الأرباح؟',
      type: 'confirm',
      onConfirm: async () => {
        setSystemDialog(null);
        try {
          const sale = sales.find(s => s.invoiceId === saleId || (s.id && s.id === saleId)) ||
                       reportSales.find(s => s.invoiceId === saleId || (s.id && s.id === saleId)) ||
                       (refundInvoice && (refundInvoice.invoiceId === saleId || refundInvoice.id === saleId) ? refundInvoice : null);
          if (!sale) return;

          const itemInSale = sale.items.find(it => 
            (itemBarcode && it.barcode && it.barcode === itemBarcode) ||
            (tradeName && it.tradeName && it.tradeName.trim() === tradeName.trim())
          );
          if (!itemInSale || itemInSale.quantity < returnQty) {
            setSystemDialog({
              title: 'خطأ في الاسترجاع',
              message: 'الكمية المسترجعة غير صالحة!',
              type: 'alert'
            });
            return;
          }

          const nextInv = inventory.map(item => {
            const matchesBarcode = itemBarcode && item.barcode && item.barcode === itemBarcode;
            const matchesName = tradeName && item.tradeName && item.tradeName.trim() === tradeName.trim();
            if (matchesBarcode || matchesName) {
              const newQty = item.quantity + returnQty;
              try {
                updateDoc(doc(db, 'users', user.uid, 'inventory', item.id), { quantity: newQty });
              } catch (e) {}
              return { ...item, quantity: newQty };
            }
            return item;
          });

          setInventory(nextInv);
          localStorage.setItem('drmasr_inv_' + (user?.uid), JSON.stringify(nextInv));

          const nextSaleItems = sale.items.map(it => {
            if (it.barcode === itemBarcode) {
              return { ...it, quantity: Number((it.quantity - returnQty).toFixed(4)) };
            }
            return it;
          }).filter(it => it.quantity > 0.001);

          const originalSubTotal = sale.subTotal || 0;
          const nextSubTotal = parseFloat(nextSaleItems.reduce((acc, it) => acc + (it.price * it.quantity), 0).toFixed(2));
          let nextDiscount = sale.discount || 0;
          if (nextSaleItems.length === 0) {
            nextDiscount = 0;
          } else if (originalSubTotal > 0) {
            nextDiscount = Math.round((nextSubTotal / originalSubTotal) * (sale.discount || 0));
          }
          const nextGrandTotal = parseFloat(Math.max(0, nextSubTotal - nextDiscount).toFixed(2));

          const updatedSale = {
            ...sale,
            items: nextSaleItems,
            subTotal: nextSubTotal,
            discount: nextDiscount,
            grandTotal: nextGrandTotal,
            isRefunded: nextSaleItems.length === 0
          };

          const returnedAmount = parseFloat((sale.grandTotal - nextGrandTotal).toFixed(2));
          let refundCashAmount = 0;
          if (sale.paymentMethod === 'split') {
            const originalCash = sale.splitAmounts?.cash || 0;
            const cashRatio = originalCash / (sale.grandTotal || 1);
            refundCashAmount = parseFloat((returnedAmount * cashRatio).toFixed(2));
          } else if (sale.paymentMethod === 'cash') {
            refundCashAmount = returnedAmount;
          }

          if (refundCashAmount > 0) {
            const refundTx = {
              type: 'expense',
              amount: refundCashAmount,
              description: `مرتجع جزئي للفاتورة رقم #${sale.invoiceId || saleId} (${sale.paymentMethod === 'split' ? 'جزء الكاش' : 'نقدي'})`,
              timestamp: new Date().toISOString(),
              recordedBy: activeShift ? activeShift.cashierName : 'نظام الصيدلية 🤖',
              shiftId: activeShift ? activeShift.id : ''
            };
            try {
              addDoc(collection(db, 'users', user?.uid, 'accounting'), refundTx).then((txRef) => {
                refundTx.id = txRef.id;
                setAccountingTx(prev => [refundTx, ...prev]);
                const saved = localStorage.getItem('drmasr_accounting_' + (user?.uid));
                const currentTxs = saved ? JSON.parse(saved) : [];
                localStorage.setItem('drmasr_accounting_' + (user?.uid), JSON.stringify([refundTx, ...currentTxs]));
              }).catch(e => console.error(e));
            } catch (e) {
              console.error("Error creating partial refund accounting transaction:", e);
            }
          }

          const nextSales = sales.map(s => (s.invoiceId === saleId || (s.id && s.id === saleId)) ? updatedSale : s);
          setSales(nextSales);
          localStorage.setItem('drmasr_sales_' + (user?.uid), JSON.stringify(nextSales));

          const nextReportSales = reportSales.map(s => (s.invoiceId === saleId || (s.id && s.id === saleId)) ? updatedSale : s);
          setReportSales(nextReportSales);
          
          if (typeof setActiveCustomerSales !== 'undefined') {
            setActiveCustomerSales(prev => prev.map(s => (s.invoiceId === saleId || (s.id && s.id === saleId)) ? updatedSale : s));
          }

          setRefundInvoice(updatedSale);
          setSystemDialog({
            title: 'تم بنجاح!',
            message: 'تم استرجاع الصنف بنجاح، وإعادة الحساب وتحديث المخزون بدقة.',
            type: 'alert'
          });

          try {
            if (sale.id) {
              await updateDoc(doc(db, 'users', user.uid, 'sales', sale.id), updatedSale);
              
              if (sale.customerPhone && setLoyaltyCustomers) {
                const existingCust = loyaltyCustomers.find(c => c.phone === sale.customerPhone);
                if (existingCust) {
                  const customerDocRef = doc(db, 'users', user.uid, 'customers', existingCust.id);
                  const refundAmt = sale.grandTotal - nextGrandTotal;
                  const cashbackPer100 = currentUserData?.loyaltySettings?.cashbackPer100 !== undefined ? currentUserData?.loyaltySettings?.cashbackPer100 : 5;
                  const pointsToDeduct = Math.round((refundAmt * cashbackPer100) / 100 * 100) / 100;
                  
                  const updatedFields = {
                    spend: Math.max(0, (existingCust.spend || 0) - refundAmt),
                    points: Math.max(0, (existingCust.points || 0) - pointsToDeduct)
                  };
                  
                  await updateDoc(customerDocRef, updatedFields).catch(e => console.error(e));
                  
                  setLoyaltyCustomers(prev => prev.map(c => c.id === existingCust.id ? { ...c, ...updatedFields } : c));
                }
              }
              
              // Sync the compiled daily summary document if it exists to prevent data drift
              const saleDateStr = sale.timestamp ? sale.timestamp.substring(0, 10) : '';
              if (saleDateStr) {
                const summaryDocRef = doc(db, 'users', user.uid, 'daily_summaries', saleDateStr);
                const summarySnap = await getDoc(summaryDocRef);
                if (summarySnap.exists()) {
                  const summaryData = summarySnap.data();
                  const updatedSales = (summaryData.sales || []).map(s => 
                    (s.id === sale.id || s.invoiceId === sale.invoiceId) ? updatedSale : s
                  );
                  
                  const totalSales = updatedSales
                    .filter(s => !(s.isDelivery && s.deliveryStatus !== 'delivered'))
                    .reduce((acc, s) => acc + s.grandTotal, 0);

                  let totalProfit = 0;
                  updatedSales.forEach(s => {
                    if (s.isRefunded) return;
                    if (s.isDelivery && s.deliveryStatus !== 'delivered') return;
                    s.items.forEach(sold => {
                      const item = inventory.find(i => i.barcode === sold.barcode);
                      const itemCost = sold.buyingPrice !== undefined ? sold.buyingPrice : (item ? (item.buyingPrice || 0) : 0);
                      totalProfit += (sold.price - itemCost) * sold.quantity;
                    });
                    totalProfit -= s.discount || 0;
                  });

                  await setDoc(summaryDocRef, {
                    ...summaryData,
                    sales: updatedSales,
                    totalSales,
                    totalProfit,
                    salesVolume: updatedSales.length
                  });
                }
              }
            }
          } catch (err) {
            console.error("Error updating sale or daily summary in handleRefundItem:", err);
          }
        } catch (error) {
          console.error(error);
          setSystemDialog({
            title: 'حدث خطأ',
            message: 'حدث خطأ غير متوقع أثناء معالجة عملية الاسترجاع.',
            type: 'alert'
          });
        }
      }
    });
  };

  const handleRefundFullInvoice = async (saleId) => {
    setSystemDialog({
      title: 'تأكيد مرتجع الفاتورة بالكامل',
      message: 'هل أنت متأكد من رغبتك في إرجاع جميع أصناف هذه الفاتورة بالكامل إلى المخزن؟ سيتم إلغاء قيمتها المالية تماماً.',
      type: 'confirm',
      onConfirm: async () => {
        setSystemDialog(null);
        try {
          const sale = sales.find(s => s.invoiceId === saleId || (s.id && s.id === saleId)) ||
                       reportSales.find(s => s.invoiceId === saleId || (s.id && s.id === saleId)) ||
                       (refundInvoice && (refundInvoice.invoiceId === saleId || refundInvoice.id === saleId) ? refundInvoice : null);
          if (!sale || sale.items.length === 0) return;

          // 1. Update inventory quantities for all items in the invoice
          const nextInv = [...inventory];
          for (const itemInSale of sale.items) {
            const invItemIdx = nextInv.findIndex(item => 
              (itemInSale.barcode && item.barcode && item.barcode === itemInSale.barcode) ||
              (!itemInSale.barcode && item.tradeName && itemInSale.tradeName && item.tradeName.trim() === itemInSale.tradeName.trim())
            );
            if (invItemIdx !== -1) {
              const newQty = nextInv[invItemIdx].quantity + itemInSale.quantity;
              nextInv[invItemIdx] = { ...nextInv[invItemIdx], quantity: newQty };
              try {
                updateDoc(doc(db, 'users', user.uid, 'inventory', nextInv[invItemIdx].id), { quantity: newQty }).catch(e => console.error(e));
              } catch (e) {
                console.error("Error updating inventory for full refund:", e);
              }
            }
          }

          setInventory(nextInv);
          localStorage.setItem('drmasr_inv_' + (user?.uid), JSON.stringify(nextInv));

          // 2. Set sale items to empty, values to 0, and isRefunded to true
          const updatedSale = {
            ...sale,
            items: [],
            subTotal: 0,
            discount: 0,
            grandTotal: 0,
            isRefunded: true
          };

          // Record refund expense in accounting to balance shift expected cash
          let refundCashAmount = 0;
          if (sale.paymentMethod === 'split') {
            refundCashAmount = sale.splitAmounts?.cash || 0;
          } else if (sale.paymentMethod === 'cash') {
            refundCashAmount = sale.grandTotal;
          }

          if (refundCashAmount > 0) {
            const refundTx = {
              type: 'expense',
              amount: refundCashAmount,
              description: `مرتجع كلي للفاتورة رقم #${sale.invoiceId || saleId} (${sale.paymentMethod === 'split' ? 'جزء الكاش' : 'نقدي'})`,
              timestamp: new Date().toISOString(),
              recordedBy: activeShift ? activeShift.cashierName : 'نظام الصيدلية 🤖',
              shiftId: activeShift ? activeShift.id : ''
            };
            try {
              addDoc(collection(db, 'users', user?.uid, 'accounting'), refundTx).then((txRef) => {
                refundTx.id = txRef.id;
                setAccountingTx(prev => [refundTx, ...prev]);
                const saved = localStorage.getItem('drmasr_accounting_' + (user?.uid));
                const currentTxs = saved ? JSON.parse(saved) : [];
                localStorage.setItem('drmasr_accounting_' + (user?.uid), JSON.stringify([refundTx, ...currentTxs]));
              }).catch(e => console.error(e));
            } catch (e) {
              console.error("Error creating refund accounting transaction:", e);
            }
          }

          const nextSales = sales.map(s => (s.invoiceId === saleId || (s.id && s.id === saleId)) ? updatedSale : s);
          setSales(nextSales);
          localStorage.setItem('drmasr_sales_' + (user?.uid), JSON.stringify(nextSales));

          const nextReportSales = reportSales.map(s => (s.invoiceId === saleId || (s.id && s.id === saleId)) ? updatedSale : s);
          setReportSales(nextReportSales);
          
          if (typeof setActiveCustomerSales !== 'undefined') {
            setActiveCustomerSales(prev => prev.map(s => (s.invoiceId === saleId || (s.id && s.id === saleId)) ? updatedSale : s));
          }

          setRefundInvoice(null);
          setSystemDialog({
            title: 'تم الاسترجاع بالكامل!',
            message: 'تم إرجاع كافة الأصناف إلى المخزن بنجاح وتحديث الحسابات.',
            type: 'alert'
          });

          try {
            if (sale.id) {
              await updateDoc(doc(db, 'users', user.uid, 'sales', sale.id), updatedSale);
              
              if (sale.customerPhone && setLoyaltyCustomers) {
                const existingCust = loyaltyCustomers.find(c => c.phone === sale.customerPhone);
                if (existingCust) {
                  const customerDocRef = doc(db, 'users', user.uid, 'customers', existingCust.id);
                  const updatedFields = {
                    spend: Math.max(0, (existingCust.spend || 0) - sale.grandTotal),
                    points: Math.max(0, (existingCust.points || 0) - (sale.pointsEarned || 0) + (sale.pointsDiscount || 0)),
                    visits: Math.max(0, (existingCust.visits || 0) - 1)
                  };
                  
                  await updateDoc(customerDocRef, updatedFields).catch(e => console.error(e));
                  
                  setLoyaltyCustomers(prev => prev.map(c => c.id === existingCust.id ? { ...c, ...updatedFields } : c));
                }
              }
              
              // Sync the compiled daily summary document if it exists to prevent data drift
              const saleDateStr = sale.timestamp ? sale.timestamp.substring(0, 10) : '';
              if (saleDateStr) {
                const summaryDocRef = doc(db, 'users', user.uid, 'daily_summaries', saleDateStr);
                const summarySnap = await getDoc(summaryDocRef);
                if (summarySnap.exists()) {
                  const summaryData = summarySnap.data();
                  const updatedSales = (summaryData.sales || []).map(s => 
                    (s.id === sale.id || s.invoiceId === sale.invoiceId) ? updatedSale : s
                  );
                  
                  const totalSales = updatedSales
                    .filter(s => !(s.isDelivery && s.deliveryStatus !== 'delivered'))
                    .reduce((acc, s) => acc + s.grandTotal, 0);

                  let totalProfit = 0;
                  updatedSales.forEach(s => {
                    if (s.isRefunded) return;
                    if (s.isDelivery && s.deliveryStatus !== 'delivered') return;
                    s.items.forEach(sold => {
                      const item = inventory.find(i => i.barcode === sold.barcode);
                      const itemCost = sold.buyingPrice !== undefined ? sold.buyingPrice : (item ? (item.buyingPrice || 0) : 0);
                      totalProfit += (sold.price - itemCost) * sold.quantity;
                    });
                    totalProfit -= s.discount || 0;
                  });

                  await setDoc(summaryDocRef, {
                    ...summaryData,
                    sales: updatedSales,
                    totalSales,
                    totalProfit,
                    salesVolume: updatedSales.length
                  });
                }
              }
            }
          } catch (err) {
            console.error("Error updating sale or daily summary in handleRefundFullInvoice:", err);
          }
        } catch (error) {
          console.error(error);
          setSystemDialog({
            title: 'حدث خطأ',
            message: 'حدث خطأ غير متوقع أثناء معالجة عملية الاسترجاع الكاملة.',
            type: 'alert'
          });
        }
      }
    });
  };


  const handleLocalAdminLogin = (e) => {
    e.preventDefault();
    setLocalAdminError('');
    if (localAdminPin.trim() === adminPin) {
      setIsAdminVerified(true);
      setLocalAdminPin('');
    } else {
      setLocalAdminError('رمز الـ PIN الخاص بالمسؤول غير صحيح!');
    }
  };

  return (
    <div style={{ direction: 'rtl', textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '32px', fontFamily: 'Cairo, sans-serif' }}>
      {!isAdminVerified && adminPin ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', animation: 'fadeIn 0.4s ease' }}>
          
          {/* Top Info Banner */}
          <div className="premium-card" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            flexWrap: 'wrap',
            gap: '20px', 
            padding: '24px 32px', 
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.6) 0%, rgba(9, 14, 28, 0.85) 100%)', 
            borderRadius: '20px', 
            border: '1px solid rgba(139, 92, 246, 0.25)', 
            backdropFilter: 'blur(20px)'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.8rem', filter: 'drop-shadow(0 0 8px rgba(139,92,246,0.3))' }}>📊</span>
                <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff', margin: 0 }}>بوابة الحسابات والتقارير</h3>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: '6px', margin: 0 }}>
                يمكن للموظفين تسجيل الحركات المالية (المصروفات والإيرادات) باستخدام الـ PIN الشخصي، بينما تظل تقارير الأرباح والمبيعات محمية للإدارة.
              </p>
            </div>
            <div style={{ 
              background: 'rgba(139, 92, 246, 0.12)', 
              border: '1px solid rgba(139, 92, 246, 0.3)', 
              color: '#a855f7', 
              padding: '8px 16px', 
              borderRadius: '12px', 
              fontSize: '0.88rem', 
              fontWeight: 'bold',
              boxShadow: '0 0 15px rgba(139, 92, 246, 0.1)'
            }}>
              🔒 وضع الحسابات المحدود للموظفين
            </div>
          </div>

          {/* Split Layout: Add Transaction Form & Admin Verification */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', 
            gap: '32px', 
            alignItems: 'start' 
          }}>
            
            {/* Column 1: Add Accounting Form */}
            <div className="premium-card" style={{ 
              background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.85) 0%, rgba(9, 13, 26, 0.98) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '24px',
              padding: '36px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.45)',
              backdropFilter: 'blur(20px)'
            }}>
              <h3 style={{ fontSize: '1.3rem', fontWeight: 800, marginBottom: '28px', color: '#fff', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.5rem' }}>💸</span> تسجيل حركة مالية (مصروف/إيراد)
              </h3>
              
              <form onSubmit={handleAddAccounting} style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                <div style={{ display: 'flex', gap: '14px' }}>
                  <label style={{ 
                    flex: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '10px', 
                    background: txType === 'expense' ? 'rgba(244, 63, 94, 0.15)' : 'rgba(255,255,255,0.01)', 
                    padding: '14px', 
                    borderRadius: '16px', 
                    border: txType === 'expense' ? '2px solid var(--accent-pink)' : '1px solid rgba(255,255,255,0.08)', 
                    cursor: 'pointer', 
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontWeight: 700,
                    boxShadow: txType === 'expense' ? '0 0 15px rgba(244,63,94,0.2)' : 'none'
                  }}>
                    <input type="radio" value="expense" checked={txType === 'expense'} onChange={() => setTxType('expense')} style={{ display: 'none' }} />
                    <span style={{ fontSize: '0.98rem', color: txType === 'expense' ? '#f43f5e' : 'var(--text-secondary)' }}>مصروفات 🔻</span>
                  </label>
                  <label style={{ 
                    flex: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '10px', 
                    background: txType === 'revenue' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255,255,255,0.01)', 
                    padding: '14px', 
                    borderRadius: '16px', 
                    border: txType === 'revenue' ? '2px solid var(--accent-green)' : '1px solid rgba(255,255,255,0.08)', 
                    cursor: 'pointer', 
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontWeight: 700,
                    boxShadow: txType === 'revenue' ? '0 0 15px rgba(16,185,129,0.2)' : 'none'
                  }}>
                    <input type="radio" value="revenue" checked={txType === 'revenue'} onChange={() => setTxType('revenue')} style={{ display: 'none' }} />
                    <span style={{ fontSize: '0.98rem', color: txType === 'revenue' ? '#10b981' : 'var(--text-secondary)' }}>إيرادات أخرى ➕</span>
                  </label>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', fontWeight: 700 }}>المبلغ (ج.م)</label>
                  <input 
                    type="number" 
                    className="premium-input" 
                    placeholder="أدخل القيمة المالية.. مثال: 150" 
                    value={txAmount} 
                    onChange={e => setTxAmount(e.target.value)} 
                    required 
                    style={{ background: 'rgba(5, 8, 16, 0.5)', height: '48px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', fontWeight: 700 }}>الوصف أو البيان</label>
                  <input 
                    type="text" 
                    className="premium-input" 
                    placeholder="مثال: فاتورة كهرباء، صيانة جهاز، إيجار..." 
                    value={txDesc} 
                    onChange={e => setTxDesc(e.target.value)} 
                    required 
                    style={{ background: 'rgba(5, 8, 16, 0.5)', height: '48px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', fontWeight: 700 }}>رمز الـ PIN الخاص بك للتحقق والتسجيل</label>
                  <input 
                    type="password" 
                    maxLength={4} 
                    className="premium-input" 
                    placeholder="أدخل رمز الـ PIN الشخصي المكون من 4 أرقام" 
                    value={txPin} 
                    onChange={e => setTxPin(e.target.value.replace(/\D/g, ''))} 
                    required 
                    style={{ 
                      textAlign: 'center', 
                      fontWeight: 'bold', 
                      letterSpacing: '8px', 
                      fontSize: '1.25rem', 
                      background: 'rgba(5, 8, 16, 0.5)', 
                      height: '48px', 
                      borderRadius: '12px', 
                      border: '1px solid rgba(255,255,255,0.1)' 
                    }}
                  />
                </div>

                <button 
                  type="submit" 
                  className="gradient-btn" 
                  style={{ 
                    marginTop: '12px', 
                    height: '50px', 
                    borderRadius: '14px',
                    fontWeight: 800,
                    fontSize: '1rem',
                    cursor: 'pointer',
                    border: 'none',
                    background: 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)',
                    color: '#fff',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.25)'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(59, 130, 246, 0.35)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.25)'; }}
                >
                  حفظ وتحديث الحسابات 💾
                </button>
              </form>
            </div>

            {/* Column 2: Admin Login */}
            <div className="premium-card" style={{ 
              background: 'linear-gradient(145deg, rgba(13, 20, 36, 0.9) 0%, rgba(5, 8, 16, 0.95) 100%)', 
              border: '1px solid rgba(139, 92, 246, 0.25)', 
              padding: '40px 32px', 
              borderRadius: '24px', 
              boxShadow: '0 25px 60px rgba(139, 92, 246, 0.15), inset 0 0 20px rgba(255, 255, 255, 0.02)', 
              textAlign: 'center', 
              backdropFilter: 'blur(16px)' 
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '28px' }}>
                <div className="pulse-slow" style={{ 
                  width: '92px', 
                  height: '92px', 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(244, 63, 94, 0.15) 100%)', 
                  border: '1px solid rgba(139, 92, 246, 0.35)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '2.8rem', 
                  boxShadow: '0 0 35px rgba(139, 92, 246, 0.3)' 
                }}>
                  🛡️
                </div>
              </div>
              <h3 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff', marginBottom: '10px', letterSpacing: '0.5px' }}>قسم التقارير محمي</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '32px' }}>
                يجب التحقق من هوية المسؤول أولاً. يرجى كتابة رمز الـ PIN الخاص بالإدارة لاستعراض تفاصيل الأرباح والتقارير المالية.
              </p>
              
              <form onSubmit={handleLocalAdminLogin} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{ position: 'relative' }}>
                  <input 
                    type="password" 
                    maxLength={4} 
                    className="premium-input" 
                    placeholder="••••" 
                    value={localAdminPin} 
                    onChange={e => setLocalAdminPin(e.target.value.replace(/\D/g, ''))} 
                    required 
                    style={{ 
                      textAlign: 'center', 
                      fontSize: '2.2rem', 
                      letterSpacing: '16px', 
                      fontWeight: 'bold', 
                      height: '64px', 
                      padding: '0 16px', 
                      borderRadius: '16px', 
                      border: '1px solid rgba(139, 92, 246, 0.3)', 
                      background: 'rgba(0, 0, 0, 0.4)', 
                      color: '#fff',
                      transition: 'all 0.3s ease',
                      boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.5)'
                    }}
                  />
                </div>
                {localAdminError && (
                  <div className="shake" style={{ color: 'var(--accent-pink)', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span>⚠️</span> {localAdminError}
                  </div>
                )}
                <button 
                  type="submit" 
                  className="gradient-btn" 
                  style={{ 
                    height: '54px', 
                    fontSize: '1.05rem', 
                    fontWeight: 'bold', 
                    width: '100%', 
                    borderRadius: '16px', 
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                    boxShadow: '0 8px 20px rgba(99, 102, 241, 0.35)', 
                    border: 'none',
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  تأكيد الدخول الآمن 🔑
                </button>
              </form>
            </div>

          </div>
        </div>
      ) : (
        <>
          {/* Header Cockpit Date Filter Control Bar */}
          <div className="premium-card fade-in-content" style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            gap: '24px', 
            padding: '28px 36px', 
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.6) 0%, rgba(9, 14, 28, 0.85) 100%)', 
            borderRadius: '24px', 
            boxShadow: '0 20px 40px rgba(0,0,0,0.5), inset 0 0 1px rgba(255,255,255,0.08)', 
            border: '1px solid rgba(59, 130, 246, 0.15)', 
            borderRight: '6px solid var(--primary)',
            backdropFilter: 'blur(20px)'
          }}>
            <div style={{ flex: 1, minWidth: '280px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '2.2rem', filter: 'drop-shadow(0 0 10px rgba(59,130,246,0.3))' }}>📊</span>
                <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.5px' }}>التقارير المالية والتحليلات</h2>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', marginTop: '8px', lineHeight: '1.6' }}>
                تابع مبيعات الصيدلية، الأرباح، ورأس المال والمخزون بشكل لحظي ومنظم
              </p>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '20px' }}>
              {/* Period Button Quick Selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', fontWeight: 700 }}>الفترة:</span>
                <div style={{ display: 'flex', gap: '6px', background: 'rgba(255,255,255,0.02)', padding: '4px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <button 
                    onClick={() => setPeriod('today')}
                    className={`delivery-tab-btn ${period === 'today' ? 'active' : ''}`}
                    style={{ padding: '10px 18px', fontSize: '0.9rem', borderRadius: '10px', height: 'auto', background: period === 'today' ? 'var(--primary)' : 'transparent', color: period === 'today' ? '#fff' : 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}
                  >
                    اليوم 📅
                  </button>
                  <button 
                    onClick={() => setPeriod('this_month')}
                    className={`delivery-tab-btn ${period === 'this_month' ? 'active' : ''}`}
                    style={{ padding: '10px 18px', fontSize: '0.9rem', borderRadius: '10px', height: 'auto', background: period === 'this_month' ? 'var(--primary)' : 'transparent', color: period === 'this_month' ? '#fff' : 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}
                  >
                    الشهر 🗓️
                  </button>
                  <button 
                    onClick={() => setPeriod('three_months')}
                    className={`delivery-tab-btn ${period === 'three_months' ? 'active' : ''}`}
                    style={{ padding: '10px 18px', fontSize: '0.9rem', borderRadius: '10px', height: 'auto', background: period === 'three_months' ? 'var(--primary)' : 'transparent', color: period === 'three_months' ? '#fff' : 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}
                  >
                    ٣ أشهر 📊
                  </button>
                  <button 
                    onClick={() => setPeriod('custom')}
                    className={`delivery-tab-btn ${period === 'custom' ? 'active' : ''}`}
                    style={{ padding: '10px 18px', fontSize: '0.9rem', borderRadius: '10px', height: 'auto', background: period === 'custom' ? 'var(--primary)' : 'transparent', color: period === 'custom' ? '#fff' : 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}
                  >
                    فترة مخصصة ⚙️
                  </button>
                </div>
              </div>

              {/* Custom Date Pickers */}
              {period === 'custom' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', animation: 'fadeIn 0.3s ease' }}>
                  <input 
                    type="date" 
                    className="premium-input" 
                    value={customStart} 
                    onChange={e => setCustomStart(e.target.value)}
                    style={{ height: '50px', padding: '12px 18px', fontSize: '0.95rem', borderRadius: '14px', background: 'rgba(10, 15, 30, 0.7)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
                  />
                  <span style={{ color: 'var(--text-secondary)', fontWeight: 'bold' }}>إلى</span>
                  <input 
                    type="date" 
                    className="premium-input" 
                    value={customEnd} 
                    onChange={e => setCustomEnd(e.target.value)}
                    style={{ height: '50px', padding: '12px 18px', fontSize: '0.95rem', borderRadius: '14px', background: 'rgba(10, 15, 30, 0.7)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
                  />
                </div>
              )}

              {/* Action Buttons */}
              <button 
                onClick={loadReportsData} 
                className="gradient-btn" 
                disabled={loadingReport}
                style={{ 
                  padding: '0 28px', 
                  height: '50px', 
                  fontSize: '0.98rem', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  minWidth: '160px', 
                  justifyContent: 'center', 
                  borderRadius: '14px', 
                  boxShadow: '0 8px 24px rgba(59, 130, 246, 0.25)',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)',
                  color: '#fff',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontWeight: 700
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(59, 130, 246, 0.35)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.25)'; }}
              >
                {loadingReport ? (
                  <>
                    <span className="spinner" style={{ display: 'inline-block', width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></span>
                    جاري التحميل...
                  </>
                ) : (
                  <>
                    <span>🔄</span> تحديث البيانات
                  </>
                )}
              </button>

              {adminPin && (
                <button 
                  onClick={() => setIsAdminVerified(false)} 
                  className="gradient-btn-secondary" 
                  style={{ 
                    padding: '0 20px', 
                    height: '50px', 
                    fontSize: '0.98rem', 
                    background: 'rgba(244, 63, 94, 0.08)', 
                    border: '1px solid rgba(244, 63, 94, 0.25)', 
                    color: '#fb7185', 
                    borderRadius: '14px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: 700
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(244, 63, 94, 0.15)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(244, 63, 94, 0.08)'; e.currentTarget.style.transform = 'none'; }}
                >
                  🔒 قفل التقارير
                </button>
              )}
            </div>
          </div>

          {reportsLoadedPeriod !== (period === 'custom' ? `custom_${customStart}_${customEnd}` : period) ? (
            <div className="premium-card" style={{ padding: '60px 40px', textAlign: 'center', background: 'rgba(30, 41, 59, 0.4)', border: '1px dashed rgba(59, 130, 246, 0.3)', borderRadius: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px', margin: '20px 0', fontFamily: 'Cairo' }}>
              <span style={{ fontSize: '4.5rem', filter: 'drop-shadow(0 0 15px rgba(59,130,246,0.4))' }}>📊</span>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#fff', margin: 0 }}>تحميل التقارير المالية والتحليلات عند الطلب</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', maxWidth: '500px', lineHeight: '1.6', margin: 0 }}>
                لقد اخترت فترة {period === 'this_month' ? 'الشهر الحالي 🗓️' : period === 'three_months' ? 'آخر ٣ أشهر 📊' : 'مخصصة ⚙️'}. للحد من استهلاك المزامنة وتحسين الأداء، يرجى الضغط على زر "تحديث البيانات" بالأعلى أو الزر بالأسفل لتحميل وعرض التحليلات كاملة.
              </p>
              <button 
                onClick={loadReportsData} 
                className="premium-btn-primary" 
                style={{ padding: '12px 36px', fontSize: '1.05rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', width: 'auto' }}
              >
                🔄 تحميل البيانات الآن
              </button>
            </div>
          ) : (
            <>
              {/* 6 Premium Statistics Cards Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '28px' }}>
            
            {/* Card 1: Total Sales */}
            <div 
              className="premium-card" 
              onMouseEnter={() => setHoveredCard('sales')} 
              onMouseLeave={() => setHoveredCard(null)}
              style={{ 
                position: 'relative', 
                overflow: 'hidden', 
                border: '1px solid rgba(59, 130, 246, 0.15)',
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.45) 0%, rgba(9, 13, 26, 0.8) 100%)',
                backdropFilter: 'blur(20px)',
                transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)', 
                transform: hoveredCard === 'sales' ? 'translateY(-10px) scale(1.02)' : 'none', 
                boxShadow: hoveredCard === 'sales' ? '0 20px 40px rgba(59, 130, 246, 0.2), inset 0 0 16px rgba(59, 130, 246, 0.15)' : '0 10px 30px rgba(0,0,0,0.4)',
                borderRadius: '24px',
                padding: '30px'
              }}
            >
              {/* Glowing Corner Aura */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(59, 130, 246, 0.25) 0%, transparent 70%)',
                pointerEvents: 'none',
                transition: 'all 0.3s ease',
                transform: hoveredCard === 'sales' ? 'scale(1.5)' : 'none'
              }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6', boxShadow: '0 0 10px #3b82f6' }}></span> 
                    المبيعات والواردات الكلية
                  </h4>
                  <h2 style={{ fontSize: '2.1rem', fontWeight: 800, color: '#3b82f6', marginTop: '18px', marginBottom: '8px', fontFamily: 'Outfit, Cairo', textShadow: '0 0 12px rgba(59,130,246,0.3)' }}>
                    {formatCurrency(salesVolume)} <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>ج.م</span>
                  </h2>
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.5' }}>تشمل المبيعات العادية والإيرادات الإضافية خلال الفترة</p>
                </div>
                <div style={{ 
                  width: '58px', 
                  height: '58px', 
                  borderRadius: '16px', 
                  background: 'rgba(59, 130, 246, 0.12)', 
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '1.8rem',
                  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                  transform: hoveredCard === 'sales' ? 'scale(1.18) rotate(8deg)' : 'none',
                  boxShadow: '0 6px 16px rgba(59, 130, 246, 0.15)'
                }}>
                  📈
                </div>
              </div>
            
</div>

            {/* Card 2: Delivery Sales */}
            {(() => {
              const deliveryProfit = deliverySales.reduce((acc, s) => acc + (parseFloat(s.deliveryFee) || 0), 0);
              return (
                <div 
                  className="premium-card" 
                  onMouseEnter={() => setHoveredCard('delivery')} 
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{ 
                    position: 'relative', 
                    overflow: 'hidden', 
                    border: '1px solid rgba(6, 182, 212, 0.15)',
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.45) 0%, rgba(9, 13, 26, 0.8) 100%)',
                    backdropFilter: 'blur(20px)',
                    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)', 
                    transform: hoveredCard === 'delivery' ? 'translateY(-10px) scale(1.02)' : 'none', 
                    boxShadow: hoveredCard === 'delivery' ? '0 20px 40px rgba(6, 182, 212, 0.25), inset 0 0 16px rgba(6, 182, 212, 0.15)' : '0 10px 30px rgba(0,0,0,0.4)',
                    borderRadius: '24px',
                    padding: '30px'
                  }}
                >
                  {/* Glowing Corner Aura */}
                  <div style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(6, 182, 212, 0.25) 0%, transparent 70%)',
                    pointerEvents: 'none',
                    transition: 'all 0.3s ease',
                    transform: hoveredCard === 'delivery' ? 'scale(1.5)' : 'none'
                  }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#06b6d4', boxShadow: '0 0 10px #06b6d4' }}></span> 
                        مبيعات الدليفري المستلمة
                      </h4>
                      <h2 style={{ fontSize: '2.1rem', fontWeight: 800, color: '#06b6d4', marginTop: '18px', marginBottom: '8px', fontFamily: 'Outfit, Cairo', textShadow: '0 0 12px rgba(6,182,212,0.3)' }}>
                        {formatCurrency(deliveryVolume)} <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>ج.م</span>
                      </h2>
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.5', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span>إجمالي {deliveryCount} شحنات تم تسليمها بنجاح للعملاء (مبيعات POS المباشرة)</span>
                        <span style={{ fontSize: '0.78rem', color: '#06b6d4', marginTop: '4px', fontWeight: 'bold' }}>
                          صافي أرباح الدليفري: {formatCurrency(deliveryProfit)} ج.م
                        </span>
                      </p>
                    </div>
                    <div style={{ 
                      width: '58px', 
                      height: '58px', 
                      borderRadius: '16px', 
                      background: 'rgba(6, 182, 212, 0.12)', 
                      border: '1px solid rgba(6, 182, 212, 0.3)',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '1.8rem',
                      transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                      transform: hoveredCard === 'delivery' ? 'scale(1.18) rotate(8deg)' : 'none',
                      boxShadow: '0 6px 16px rgba(6, 182, 212, 0.15)'
                    }}>
                      🛵
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Card 3: Realized Net Profit */}
            {(() => {
              const profit = calculateTotalProfit();
              const isProfitPositive = profit >= 0;
              const accentColor = isProfitPositive ? '#10b981' : '#f43f5e';
              const cardBgShadow = isProfitPositive ? 'rgba(16, 185, 129, 0.25)' : 'rgba(244, 63, 94, 0.25)';
              const innerGlow = isProfitPositive ? 'rgba(16, 185, 129, 0.15)' : 'rgba(244, 63, 94, 0.15)';
              return (
                <div 
                  className="premium-card" 
                  onMouseEnter={() => setHoveredCard('profit')} 
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{ 
                    position: 'relative', 
                    overflow: 'hidden', 
                    border: `1px solid rgba(${isProfitPositive ? '16, 185, 129' : '244, 63, 94'}, 0.15)`,
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.45) 0%, rgba(9, 13, 26, 0.8) 100%)',
                    backdropFilter: 'blur(20px)',
                    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)', 
                    transform: hoveredCard === 'profit' ? 'translateY(-10px) scale(1.02)' : 'none', 
                    boxShadow: hoveredCard === 'profit' ? `0 20px 40px ${cardBgShadow}, inset 0 0 16px ${innerGlow}` : '0 10px 30px rgba(0,0,0,0.4)',
                    borderRadius: '24px',
                    padding: '30px'
                  }}
                >
                  {/* Glowing Corner Aura */}
                  <div style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, rgba(${isProfitPositive ? '16, 185, 129' : '244, 63, 94'}, 0.25) 0%, transparent 70%)`,
                    pointerEvents: 'none',
                    transition: 'all 0.3s ease',
                    transform: hoveredCard === 'profit' ? 'scale(1.5)' : 'none'
                  }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}></span> 
                        صافي الأرباح المحققة
                      </h4>
                      <h2 style={{ fontSize: '2.1rem', fontWeight: 800, color: accentColor, marginTop: '18px', marginBottom: '8px', fontFamily: 'Outfit, Cairo', textShadow: `0 0 12px rgba(${isProfitPositive ? '16, 185, 129' : '244, 63, 94'}, 0.3)` }}>
                        {formatCurrency(profit)} <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>ج.م</span>
                      </h2>
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.5' }}>
                        {isProfitPositive ? 'أرباح الأصناف المباعة مطروحاً منها المصاريف مع الإيرادات' : 'عجز مالي نتيجة لزيادة المصروفات والخصومات'}
                      </p>
                    </div>
                    <div style={{ 
                      width: '58px', 
                      height: '58px', 
                      borderRadius: '16px', 
                      background: isProfitPositive ? 'rgba(16, 185, 129, 0.12)' : 'rgba(244, 63, 94, 0.12)', 
                      border: isProfitPositive ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(244, 63, 94, 0.3)',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '1.8rem',
                      transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                      transform: hoveredCard === 'profit' ? 'scale(1.18) rotate(8deg)' : 'none',
                      boxShadow: isProfitPositive ? '0 6px 16px rgba(16, 185, 129, 0.15)' : '0 6px 16px rgba(244, 63, 94, 0.15)'
                    }}>
                      {isProfitPositive ? '💰' : '📉'}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Card 3.5: Online Sales Profit */}
            {(() => {
              const onlineProfit = calculateOnlineProfit();
              const onlineSales = reportSales.filter(s => (s.cashierName === 'أونلاين (QR Store)' || (s.onlineOrderId && s.onlineOrderId !== '')) && (!s.isDelivery || s.deliveryStatus === 'delivered'));
              const onlineVolume = onlineSales.reduce((acc, s) => acc + s.grandTotal, 0);
              const onlineCount = onlineSales.length;
              const accentColor = '#6366f1';
              const cardBgShadow = 'rgba(99, 102, 241, 0.25)';
              const innerGlow = 'rgba(99, 102, 241, 0.15)';
              return (
                <div 
                  className="premium-card" 
                  onMouseEnter={() => setHoveredCard('online_profit')} 
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{ 
                    position: 'relative', 
                    overflow: 'hidden', 
                    border: '1px solid rgba(99, 102, 241, 0.15)',
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.45) 0%, rgba(9, 13, 26, 0.8) 100%)',
                    backdropFilter: 'blur(20px)',
                    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)', 
                    transform: hoveredCard === 'online_profit' ? 'translateY(-10px) scale(1.02)' : 'none', 
                    boxShadow: hoveredCard === 'online_profit' ? `0 20px 40px ${cardBgShadow}, inset 0 0 16px ${innerGlow}` : '0 10px 30px rgba(0,0,0,0.4)',
                    borderRadius: '24px',
                    padding: '30px'
                  }}
                >
                  {/* Glowing Corner Aura */}
                  <div style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, transparent 70%)',
                    pointerEvents: 'none',
                    transition: 'all 0.3s ease',
                    transform: hoveredCard === 'online_profit' ? 'scale(1.5)' : 'none'
                  }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}></span> 
                        أرباح البيع الأونلاين فقط
                      </h4>
                      <h2 style={{ fontSize: '2.1rem', fontWeight: 800, color: accentColor, marginTop: '18px', marginBottom: '8px', fontFamily: 'Outfit, Cairo', textShadow: '0 0 12px rgba(99, 102, 241, 0.3)' }}>
                        {formatCurrency(onlineProfit)} <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>ج.م</span>
                      </h2>
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.5', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span>أرباح المنتجات المباعة مباشرة من المتجر الأونلاين المكتملة</span>
                        <span style={{ fontSize: '0.78rem', color: '#6366f1', marginTop: '4px', fontWeight: 'bold' }}>
                          إجمالي المبيعات الأونلاين: {formatCurrency(onlineVolume)} ج.م ({onlineCount} طلبات)
                        </span>
                      </p>
                    </div>
                    <div style={{ 
                      width: '58px', 
                      height: '58px', 
                      borderRadius: '16px', 
                      background: 'rgba(99, 102, 241, 0.12)', 
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '1.8rem',
                      transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                      transform: hoveredCard === 'online_profit' ? 'scale(1.18) rotate(8deg)' : 'none',
                      boxShadow: '0 6px 16px rgba(99, 102, 241, 0.15)'
                    }}>
                      🌐
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Card 4: Warehouse Capital */}
            <div 
              className="premium-card" 
              onMouseEnter={() => setHoveredCard('capital')} 
              onMouseLeave={() => setHoveredCard(null)}
              style={{ 
                position: 'relative', 
                overflow: 'hidden', 
                border: '1px solid rgba(139, 92, 246, 0.15)',
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.45) 0%, rgba(9, 13, 26, 0.8) 100%)',
                backdropFilter: 'blur(20px)',
                transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)', 
                transform: hoveredCard === 'capital' ? 'translateY(-10px) scale(1.02)' : 'none', 
                boxShadow: hoveredCard === 'capital' ? '0 20px 40px rgba(139, 92, 246, 0.25), inset 0 0 16px rgba(139, 92, 246, 0.15)' : '0 10px 30px rgba(0,0,0,0.4)',
                borderRadius: '24px',
                padding: '30px'
              }}
            >
              {/* Glowing Corner Aura */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(139, 92, 246, 0.25) 0%, transparent 70%)',
                pointerEvents: 'none',
                transition: 'all 0.3s ease',
                transform: hoveredCard === 'capital' ? 'scale(1.5)' : 'none'
              }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#8b5cf6', boxShadow: '0 0 10px #8b5cf6' }}></span> 
                    رأس مال المخزن الحالي
                  </h4>
                  <h2 style={{ fontSize: '2.1rem', fontWeight: 800, color: '#8b5cf6', marginTop: '18px', marginBottom: '8px', fontFamily: 'Outfit, Cairo', textShadow: '0 0 12px rgba(139,92,246,0.3)' }}>
                    {formatCurrency(calculateWarehouseCapital())} <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>ج.م</span>
                  </h2>
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.5' }}>إجمالي تكلفة شراء المنتجات الموجودة حالياً بالمخزن</p>
                </div>
                <div style={{ 
                  width: '58px', 
                  height: '58px', 
                  borderRadius: '16px', 
                  background: 'rgba(139, 92, 246, 0.12)', 
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '1.8rem',
                  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                  transform: hoveredCard === 'capital' ? 'scale(1.18) rotate(8deg)' : 'none',
                  boxShadow: '0 6px 16px rgba(139, 92, 246, 0.15)'
                }}>
                  📦
                </div>
              </div>
            </div>

            {/* Card 5: Warehouse Selling Value */}
            <div 
              className="premium-card" 
              onMouseEnter={() => setHoveredCard('sellingValue')} 
              onMouseLeave={() => setHoveredCard(null)}
              style={{ 
                position: 'relative', 
                overflow: 'hidden', 
                border: '1px solid rgba(251, 191, 36, 0.15)',
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.45) 0%, rgba(9, 13, 26, 0.8) 100%)',
                backdropFilter: 'blur(20px)',
                transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)', 
                transform: hoveredCard === 'sellingValue' ? 'translateY(-10px) scale(1.02)' : 'none', 
                boxShadow: hoveredCard === 'sellingValue' ? '0 20px 40px rgba(251, 191, 36, 0.25), inset 0 0 16px rgba(251, 191, 36, 0.15)' : '0 10px 30px rgba(0,0,0,0.4)',
                borderRadius: '24px',
                padding: '30px'
              }}
            >
              {/* Glowing Corner Aura */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(251, 191, 36, 0.25) 0%, transparent 70%)',
                pointerEvents: 'none',
                transition: 'all 0.3s ease',
                transform: hoveredCard === 'sellingValue' ? 'scale(1.5)' : 'none'
              }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#fbbf24', boxShadow: '0 0 10px #fbbf24' }}></span> 
                    قيمة المخزن بسعر البيع
                  </h4>
                  <h2 style={{ fontSize: '2.1rem', fontWeight: 800, color: '#fbbf24', marginTop: '18px', marginBottom: '8px', fontFamily: 'Outfit, Cairo', textShadow: '0 0 12px rgba(251,191,36,0.3)' }}>
                    {formatCurrency(calculateWarehouseSellingValue())} <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>ج.م</span>
                  </h2>
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.5' }}>إجمالي القيمة التقديرية للمخزون الحالي عند البيع للجمهور</p>
                </div>
                <div style={{ 
                  width: '58px', 
                  height: '58px', 
                  borderRadius: '16px', 
                  background: 'rgba(251, 191, 36, 0.12)', 
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '1.8rem',
                  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                  transform: hoveredCard === 'sellingValue' ? 'scale(1.18) rotate(8deg)' : 'none',
                  boxShadow: '0 6px 16px rgba(251, 191, 36, 0.15)'
                }}>
                  🏪
                </div>
              </div>
            </div>

            {/* Card 6: Expected Warehouse Profit */}
            <div 
              className="premium-card" 
              onMouseEnter={() => setHoveredCard('warehouseProfit')} 
              onMouseLeave={() => setHoveredCard(null)}
              style={{ 
                position: 'relative', 
                overflow: 'hidden', 
                border: '1px solid rgba(236, 72, 153, 0.15)',
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.45) 0%, rgba(9, 13, 26, 0.8) 100%)',
                backdropFilter: 'blur(20px)',
                transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)', 
                transform: hoveredCard === 'warehouseProfit' ? 'translateY(-10px) scale(1.02)' : 'none', 
                boxShadow: hoveredCard === 'warehouseProfit' ? '0 20px 40px rgba(236, 72, 153, 0.25), inset 0 0 16px rgba(236, 72, 153, 0.15)' : '0 10px 30px rgba(0,0,0,0.4)',
                borderRadius: '24px',
                padding: '30px'
              }}
            >
              {/* Glowing Corner Aura */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(236, 72, 153, 0.25) 0%, transparent 70%)',
                pointerEvents: 'none',
                transition: 'all 0.3s ease',
                transform: hoveredCard === 'warehouseProfit' ? 'scale(1.5)' : 'none'
              }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#ec4899', boxShadow: '0 0 10px #ec4899' }}></span> 
                    الأرباح المتوقعة للمخزون
                  </h4>
                  <h2 style={{ fontSize: '2.1rem', fontWeight: 800, color: '#ec4899', marginTop: '18px', marginBottom: '8px', fontFamily: 'Outfit, Cairo', textShadow: '0 0 12px rgba(236,72,153,0.3)' }}>
                    {formatCurrency(calculateWarehouseProfit())} <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>ج.م</span>
                  </h2>
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.5' }}>هامش الربح المتوقع تحقيقه بعد بيع كافة المنتجات الحالية</p>
                </div>
                <div style={{ 
                  width: '58px', 
                  height: '58px', 
                  borderRadius: '16px', 
                  background: 'rgba(236, 72, 153, 0.12)', 
                  border: '1px solid rgba(236, 72, 153, 0.3)',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '1.8rem',
                  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                  transform: hoveredCard === 'warehouseProfit' ? 'scale(1.18) rotate(8deg)' : 'none',
                  boxShadow: '0 6px 16px rgba(236, 72, 153, 0.15)'
                }}>
                  ✨
                </div>
              </div>
            </div>

          </div>
          
          {/* Quick Info Bar for Issued Invoices Count */}
          <div style={{
            background: 'linear-gradient(90deg, rgba(139, 92, 246, 0.15) 0%, rgba(99, 102, 241, 0.08) 100%)',
            border: '1px solid rgba(139, 92, 246, 0.25)',
            padding: '20px 32px',
            borderRadius: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 8px 30px rgba(0,0,0,0.25), inset 0 0 10px rgba(139, 92, 246, 0.15)',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span style={{ fontSize: '1.8rem', filter: 'drop-shadow(0 0 8px rgba(139,92,246,0.3))' }}>🧾</span>
              <div>
                <span style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff' }}>إجمالي الفواتير الصادرة</span>
                <span style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginRight: '10px' }}>خلال الفترة المحددة</span>
              </div>
            </div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 850, color: '#a855f7', margin: 0, fontFamily: 'Outfit, Cairo', textShadow: '0 0 10px rgba(168,85,247,0.3)' }}>
              {reportSales.length} <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-secondary)' }}>فاتورة صادرة</span>
            </h2>
          </div>
          
          {/* Grid Layout: Accounting Section */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '32px' }}>
            
            {/* Form to add Expense/Revenue */}
            <div className="premium-card" style={{ 
              height: 'fit-content',
              background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.85) 0%, rgba(9, 13, 26, 0.98) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '24px',
              padding: '36px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.45)',
              backdropFilter: 'blur(20px)'
            }}>
              <h3 style={{ fontSize: '1.3rem', fontWeight: 800, marginBottom: '28px', color: '#fff', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.5rem' }}>💸</span> تسجيل حركة مالية (مصروف/إيراد)
              </h3>
              
              <form onSubmit={handleAddAccounting} style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                <div style={{ display: 'flex', gap: '14px' }}>
                  <label style={{ 
                    flex: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '10px', 
                    background: txType === 'expense' ? 'rgba(244, 63, 94, 0.15)' : 'rgba(255,255,255,0.01)', 
                    padding: '14px', 
                    borderRadius: '16px', 
                    border: txType === 'expense' ? '2px solid var(--accent-pink)' : '1px solid rgba(255,255,255,0.08)', 
                    cursor: 'pointer', 
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontWeight: 700,
                    boxShadow: txType === 'expense' ? '0 0 15px rgba(244,63,94,0.2)' : 'none'
                  }}>
                    <input type="radio" value="expense" checked={txType === 'expense'} onChange={() => setTxType('expense')} style={{ display: 'none' }} />
                    <span style={{ fontSize: '0.98rem', color: txType === 'expense' ? '#f43f5e' : 'var(--text-secondary)' }}>مصروفات 🔻</span>
                  </label>
                  <label style={{ 
                    flex: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '10px', 
                    background: txType === 'revenue' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255,255,255,0.01)', 
                    padding: '14px', 
                    borderRadius: '16px', 
                    border: txType === 'revenue' ? '2px solid var(--accent-green)' : '1px solid rgba(255,255,255,0.08)', 
                    cursor: 'pointer', 
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontWeight: 700,
                    boxShadow: txType === 'revenue' ? '0 0 15px rgba(16,185,129,0.2)' : 'none'
                  }}>
                    <input type="radio" value="revenue" checked={txType === 'revenue'} onChange={() => setTxType('revenue')} style={{ display: 'none' }} />
                    <span style={{ fontSize: '0.98rem', color: txType === 'revenue' ? '#10b981' : 'var(--text-secondary)' }}>إيرادات أخرى ➕</span>
                  </label>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', fontWeight: 700 }}>المبلغ (ج.م)</label>
                  <input 
                    type="number" 
                    className="premium-input" 
                    placeholder="أدخل القيمة المالية.. مثال: 150" 
                    value={txAmount} 
                    onChange={e => setTxAmount(e.target.value)} 
                    required 
                    style={{ background: 'rgba(5, 8, 16, 0.5)', height: '48px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', fontWeight: 700 }}>الوصف أو البيان</label>
                  <input 
                    type="text" 
                    className="premium-input" 
                    placeholder="مثال: فاتورة كهرباء، صيانة جهاز، إيجار..." 
                    value={txDesc} 
                    onChange={e => setTxDesc(e.target.value)} 
                    required 
                    style={{ background: 'rgba(5, 8, 16, 0.5)', height: '48px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', fontWeight: 700 }}>رمز الـ PIN الخاص بك للتحقق والتسجيل</label>
                  <input 
                    type="password" 
                    maxLength={4} 
                    className="premium-input" 
                    placeholder="أدخل رمز الـ PIN الشخصي المكون من 4 أرقام" 
                    value={txPin} 
                    onChange={e => setTxPin(e.target.value.replace(/\D/g, ''))} 
                    required 
                    style={{ 
                      textAlign: 'center', 
                      fontWeight: 'bold', 
                      letterSpacing: '8px', 
                      fontSize: '1.25rem', 
                      background: 'rgba(5, 8, 16, 0.5)', 
                      height: '48px', 
                      borderRadius: '12px', 
                      border: '1px solid rgba(255,255,255,0.1)' 
                    }}
                  />
                </div>

                <button 
                  type="submit" 
                  className="gradient-btn" 
                  style={{ 
                    marginTop: '12px', 
                    height: '50px', 
                    borderRadius: '14px',
                    fontWeight: 800,
                    fontSize: '1rem',
                    cursor: 'pointer',
                    border: 'none',
                    background: 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)',
                    color: '#fff',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.25)'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(59, 130, 246, 0.35)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.25)'; }}
                >
                  حفظ وتحديث الحسابات 💾
                </button>
              </form>
            </div>

            {/* Transactions list */}
            <div className="premium-card" style={{ 
              background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.85) 0%, rgba(9, 13, 26, 0.98) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '24px',
              padding: '36px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 40px rgba(0,0,0,0.45)',
              backdropFilter: 'blur(20px)'
            }}>
              <h3 style={{ fontSize: '1.3rem', fontWeight: 800, marginBottom: '28px', color: '#fff', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.5rem' }}>📜</span> دفتر الحركات المالية الإضافية
              </h3>
              
              {accountingTx.length === 0 ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', gap: '16px' }}>
                  <span style={{ fontSize: '3.5rem', filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.15))' }}>💸</span>
                  <p style={{ color: 'var(--text-secondary)', textAlign: 'center', margin: 0, fontSize: '0.98rem', fontWeight: 600 }}>لا توجد حركات مالية إضافية مسجلة للفترة الحالية.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', flex: 1 }}>
                  <table className="premium-table">
                    <thead>
                      <tr>
                        <th>النوع</th>
                        <th>الوصف والبيان</th>
                        <th>بواسطة</th>
                        <th>المبلغ</th>
                        <th style={{ textAlign: 'center' }}>الخيارات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountingTx.map((tx, idx) => (
                        <tr key={tx.id || tx.timestamp || idx} style={{ transition: 'all 0.2s ease' }}>
                          <td>
                            <span className={`status-badge ${tx.type === 'expense' ? 'danger' : 'success'}`} style={{
                              padding: '6px 12px',
                              borderRadius: '10px',
                              fontSize: '0.82rem',
                              fontWeight: 800,
                              background: tx.type === 'expense' ? 'rgba(244, 63, 94, 0.12)' : 'rgba(16, 185, 129, 0.12)',
                              border: tx.type === 'expense' ? '1px solid rgba(244, 63, 94, 0.3)' : '1px solid rgba(16, 185, 129, 0.3)',
                              color: tx.type === 'expense' ? '#fb7185' : '#34d399',
                              boxShadow: tx.type === 'expense' ? '0 0 8px rgba(244,63,94,0.15)' : '0 0 8px rgba(16,185,129,0.15)'
                            }}>
                              {tx.type === 'expense' ? 'مصروف' : 'إيراد'}
                            </span>
                          </td>
                          <td style={{ fontWeight: 700, color: '#fff' }}>{tx.description}</td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', fontWeight: 700 }}>{tx.recordedBy || 'المسؤول'}</td>
                          <td style={{ color: tx.type === 'expense' ? 'var(--accent-pink)' : 'var(--accent-green)', fontWeight: 800, fontFamily: 'Outfit, Cairo', fontSize: '1.05rem', textShadow: tx.type === 'expense' ? '0 0 8px rgba(244,63,94,0.2)' : '0 0 8px rgba(16,185,129,0.2)' }}>
                            {tx.type === 'expense' ? '-' : '+'}{formatCurrency(tx.amount)} ج.م
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button 
                              onClick={() => handleDeleteAccounting(tx.id || tx.timestamp)} 
                              className="gradient-btn-secondary" 
                              style={{ 
                                padding: '8px 14px', 
                                fontSize: '0.85rem', 
                                color: '#fb7185', 
                                background: 'rgba(244, 63, 94, 0.05)',
                                border: '1px solid rgba(244, 63, 94, 0.18)',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontWeight: 700
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(244, 63, 94, 0.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(244, 63, 94, 0.05)'; e.currentTarget.style.transform = 'none'; }}
                            >
                              🗑️ حذف
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
          
          {/* Sales Transactions & Customers Directory Sub-Tabs Container */}
          <div className="premium-card" style={{ 
            background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.8) 0%, rgba(9, 13, 26, 0.95) 100%)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '28px',
            padding: '36px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
            backdropFilter: 'blur(20px)'
          }}>
            {/* Sub-Tabs Nav - Segmented Pill Slider look */}
            <div style={{ 
              display: 'flex', 
              gap: '6px', 
              marginBottom: '32px', 
              background: 'rgba(5, 8, 16, 0.5)',
              padding: '6px',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.05)',
              width: 'fit-content',
              direction: 'rtl' 
            }}>
              <button
                onClick={() => setReportsSubTab('sales')}
                style={{ 
                  padding: '12px 30px', 
                  fontSize: '0.98rem', 
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: reportsSubTab === 'sales' ? 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)' : 'transparent',
                  color: reportsSubTab === 'sales' ? '#fff' : 'var(--text-secondary)',
                  border: 'none',
                  boxShadow: reportsSubTab === 'sales' ? '0 6px 15px rgba(59, 130, 246, 0.25)' : 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <span>🧾</span> سجل المبيعات والفواتير
              </button>
              <button
                onClick={() => setReportsSubTab('customers')}
                style={{ 
                  padding: '12px 30px', 
                  fontSize: '0.98rem', 
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: reportsSubTab === 'customers' ? 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)' : 'transparent',
                  color: reportsSubTab === 'customers' ? '#fff' : 'var(--text-secondary)',
                  border: 'none',
                  boxShadow: reportsSubTab === 'customers' ? '0 6px 15px rgba(59, 130, 246, 0.25)' : 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <span>👥</span> {isLoyaltyEnabled ? `دليل العملاء ونقاط الولاء (${loyaltyCustomers.length})` : `دليل وعناوين العملاء (${loyaltyCustomers.length})`}
              </button>
              <button
                onClick={() => setReportsSubTab('products')}
                style={{ 
                  padding: '12px 30px', 
                  fontSize: '0.98rem', 
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: reportsSubTab === 'products' ? 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)' : 'transparent',
                  color: reportsSubTab === 'products' ? '#fff' : 'var(--text-secondary)',
                  border: 'none',
                  boxShadow: reportsSubTab === 'products' ? '0 6px 15px rgba(59, 130, 246, 0.25)' : 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <span>📦</span> تحليل مبيعات وأرباح الأصناف
              </button>
            </div>


            {/* Tab 1: Sales Transactions Logs */}
            {reportsSubTab === 'sales' ? (
              <div style={{ direction: 'rtl', textAlign: 'right', animation: 'fadeIn 0.3s ease' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>📊 سجل عمليات البيع الصادرة بالفترة</h3>
                  
                  {/* Search bar */}
                  <div style={{ width: '100%', maxWidth: '360px', position: 'relative' }}>
                    <input
                      type="text"
                      className="premium-input"
                      placeholder="🔍 ابحث برقم الفاتورة أو موبايل العميل..."
                      value={invoiceSearch}
                      onChange={(e) => {
                        setInvoiceSearch(e.target.value);
                        setInvoicePage(1);
                      }}
                      style={{ width: '100%', paddingLeft: '40px', height: '44px', background: 'rgba(5, 8, 16, 0.4)' }}
                    />
                  </div>
                </div>

                {/* Table list */}
                {(() => {
                  const filteredSales = reportSales.filter(sale => {
                    const idMatch = sale.invoiceId && sale.invoiceId.toString().toLowerCase().includes(invoiceSearch.toLowerCase());
                    const phoneMatch = sale.customerPhone && sale.customerPhone.toString().toLowerCase().includes(invoiceSearch.toLowerCase());
                    return idMatch || phoneMatch;
                  });

                  // Determine display sales and total pages
                  const isSearching = invoiceSearch.trim() !== '';
                  const totalSalesPages = isSearching
                    ? (Math.ceil(filteredSales.length / 6) || 1)
                    : (Math.ceil(salesTotalCount / 6) || 1);

                  const displaySales = isSearching
                    ? [...filteredSales].sort((a,b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()).slice((invoicePage - 1) * 6, invoicePage * 6)
                    : paginatedSales;

                  return (
                    <>
                      {salesLoading ? (
                        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.02)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', marginBottom: '16px' }}>
                          <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600 }}>جاري جلب الفواتير من سحابة Firebase الآمنة... ☁️</p>
                        </div>
                      ) : (!isSearching && displaySales.length === 0) || (isSearching && filteredSales.length === 0) ? (
                        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>
                          <span style={{ fontSize: '3rem' }}>🧾</span>
                          <p style={{ marginTop: '16px', fontSize: '0.95rem' }}>لا توجد فواتير مطابقة ضمن السجلات المحملة.</p>
                          <button onClick={handleRemoteInvoiceSearch} className="btn-glow" style={{ marginTop: '10px', fontSize: '0.9rem', padding: '0.5rem 1.5rem', background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                            بحث سحابي متقدم في الفواتير القديمة ☁️
                          </button>
                        </div>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table className="premium-table">
                            <thead>
                              <tr>
                                <th>رقم الفاتورة</th>
                                <th>التاريخ والوقت</th>
                                <th>عدد الأصناف</th>
                                <th>قيمة الإجمالي</th>
                                <th>طريقة الدفع / حالة الشحنة</th>
                                <th>اسم العميل</th>
                                <th>-'D) EF8HE) 'D61'&(</th>
                                <th style={{ textAlign: 'center' }}>الخيارات والتحكم</th>
                              </tr>
                            </thead>
                            <tbody>
                              {displaySales.map(sale => {
                                const isFullyRefunded = sale.isRefunded || sale.items.length === 0;
                                return (
                                  <tr key={sale.id || sale.invoiceId} style={{ 
                                    textDecoration: isFullyRefunded ? 'line-through' : 'none', 
                                    opacity: isFullyRefunded ? 0.5 : 1,
                                    transition: 'all 0.2s ease'
                                  }}>
                                    <td style={{ fontWeight: 700, color: '#fff', fontFamily: 'Outfit, Cairo' }}>#{sale.invoiceId}</td>
                                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{new Date(sale.timestamp).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' })}</td>
                                    <td style={{ fontWeight: 600 }}>{sale.items.length} أصناف 💊</td>
                                    <td style={{ color: isFullyRefunded ? 'var(--text-secondary)' : 'var(--accent-green)', fontWeight: 800, fontFamily: 'Outfit, Cairo', fontSize: '1.05rem' }}>
                                      {formatCurrency(sale.grandTotal)} ج.م
                                    </td>
                                    <td>
                                      {isFullyRefunded ? (
                                        <span className="status-badge danger" style={{ background: 'rgba(244, 63, 94, 0.1)', border: '1px solid rgba(244, 63, 94, 0.2)', color: '#fb7185' }}>مرتجعة بالكامل</span>
                                      ) : (
                                        sale.isInsurance ? (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                            <span className="status-badge" style={{
                                              padding: '4px 10px',
                                              borderRadius: '8px',
                                              fontSize: '0.8rem',
                                              fontWeight: 700,
                                              background: 'rgba(139, 92, 246, 0.1)',
                                              color: '#c084fc',
                                              border: '1px solid rgba(139, 92, 246, 0.2)'
                                            }}>
                                              💼 تعاقد: {sale.insuranceContractName || 'جهة تأمين'}
                                            </span>
                                            <span style={{ 
                                              fontSize: '0.72rem', 
                                              fontWeight: 'bold',
                                              color: sale.insuranceClaimStatus === 'settled' ? '#34d399' : '#fbbf24'
                                            }}>
                                              {sale.insuranceClaimStatus === 'settled' ? '🤝 تمت التسوية' : '⏳ معلقة'}
                                            </span>
                                          </div>
                                        ) : sale.isDelivery ? (
                                          <span className={`status-badge ${
                                            sale.deliveryStatus === 'pending' ? 'delivery-pending' :
                                            sale.deliveryStatus === 'out' ? 'delivery-out' :
                                            sale.deliveryStatus === 'delivered' ? 'delivery-success' : 'delivery-returned'
                                          }`} style={{
                                            padding: '4px 10px',
                                            borderRadius: '8px',
                                            fontSize: '0.8rem',
                                            fontWeight: 700,
                                            background: sale.deliveryStatus === 'delivered' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                            color: sale.deliveryStatus === 'delivered' ? '#34d399' : '#fbbf24',
                                            border: sale.deliveryStatus === 'delivered' ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid rgba(245, 158, 11, 0.2)'
                                          }}>
                                            🛵 دليفري ({
                                              sale.deliveryStatus === 'pending' ? 'قيد التجهيز' :
                                              sale.deliveryStatus === 'out' ? 'خارج للتوصيل' :
                                              sale.deliveryStatus === 'delivered' ? 'تم التسليم' : 'مرتجع'
                                            })
                                            {sale.promoCode && (
                                              <span style={{ display: 'block', fontSize: '0.72rem', background: 'rgba(236, 72, 153, 0.12)', color: '#ec4899', border: '1px solid rgba(236, 72, 153, 0.25)', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold', marginTop: '4px', width: 'fit-content', marginLeft: 'auto', marginRight: 'auto' }}>
                                                🏷️ كود: {sale.promoCode}
                                              </span>
                                            )}
                                          </span>
                                        ) : (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                            <span className="status-badge success" style={{
                                              padding: '4px 10px',
                                              borderRadius: '8px',
                                              fontSize: '0.8rem',
                                              fontWeight: 700,
                                              background: 'rgba(59, 130, 246, 0.1)',
                                              color: '#60a5fa',
                                              border: '1px solid rgba(59, 130, 246, 0.2)'
                                            }}>{sale.paymentMethod === 'cash' ? '💵 كاش' : sale.paymentMethod === 'split' ? '💵💳 دفع مختلط' : sale.paymentMethod === 'instapay' ? '📱 إنستاباي' : sale.paymentMethod === 'vodafone' ? '📲 فودافون كاش' : sale.paymentMethod === 'deferred' ? '⏳ بيع بالآجل' : '💳 شبكة / فيزا'}</span>
                                            {sale.promoCode && (
                                              <span style={{ fontSize: '0.72rem', background: 'rgba(236, 72, 153, 0.12)', color: '#ec4899', border: '1px solid rgba(236, 72, 153, 0.25)', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                🏷️ كود: {sale.promoCode}
                                              </span>
                                            )}
                                          </div>
                                        )
                                      )}
                                    </td>
                                    <td style={{ fontWeight: 600, color: 'var(--primary)' }}>{sale.customerName || 'عميل نقدي عام'}</td>
                                    <td>
                                      {sale.etaStatus === 'sent_success' ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                          <span className="status-badge success" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.2)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 700, width: 'fit-content' }}>
                                            *E 'D%13'D (F,'-  
                                          </span>
                                          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{sale.etaUuid}</span>
                                        </div>
                                      ) : sale.etaStatus === 'sending' ? (
                                        <span className="status-badge" style={{ background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.2)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 700 }}>
                                          ,'1J 'D%13'D...
                                        </span>
                                      ) : (
                                        <button
                                          onClick={() => handleSendToEta(sale)}
                                          disabled={sale.isRefunded || sale.etaStatus === 'sending'}
                                          style={{
                                            padding: '6px 12px',
                                            background: (sale.isRefunded || sale.etaStatus === 'sending') ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                                            color: (sale.isRefunded || sale.etaStatus === 'sending') ? 'var(--text-secondary)' : '#fff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: (sale.isRefunded || sale.etaStatus === 'sending') ? 'not-allowed' : 'pointer',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                          }}
                                        >
                                          إرسال للضرائب 🇪🇬
                                        </button>
                                      )}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                        <button 
                                          onClick={() => setSelectedPreviewInvoice(sale)} 
                                          className="gradient-btn-secondary" 
                                          style={{ padding: '6px 14px', fontSize: '0.85rem', borderRadius: '8px', cursor: 'pointer' }}
                                        >
                                          👁️ معاينة
                                        </button>
                                        <button 
                                          onClick={() => handleOpenEditInvoice(sale)} 
                                          className="gradient-btn-secondary" 
                                          style={{ padding: '6px 14px', fontSize: '0.85rem', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(59, 130, 246, 0.3)', color: '#60a5fa' }}
                                        >
                                          ✏️ تعديل
                                        </button>
                                        <button 
                                          onClick={() => handleReprintInvoice(sale)} 
                                          className="gradient-btn-secondary" 
                                          style={{ padding: '6px 14px', fontSize: '0.85rem', borderRadius: '8px', cursor: 'pointer' }}
                                        >
                                          🖨️ طباعة
                                        </button>
                                        <button 
                                          onClick={() => shareSaleViaWhatsApp(sale)} 
                                          className="gradient-btn-secondary" 
                                          style={{ padding: '6px 14px', fontSize: '0.85rem', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(34, 197, 94, 0.3)', color: '#4ade80' }}
                                        >
                                          💬 مشاركة
                                        </button>
                                        {!isFullyRefunded && (
                                          <button 
                                            onClick={() => setRefundInvoice(sale)} 
                                            className="gradient-btn" 
                                            style={{ 
                                              padding: '6px 14px', 
                                              fontSize: '0.85rem', 
                                              borderRadius: '8px', 
                                              background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
                                              border: 'none',
                                              color: '#fff',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            ↩️ استرجاع
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Pagination Controls */}
                      {totalSalesPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', padding: '16px 0 0 0', borderTop: '1px dashed rgba(255,255,255,0.06)' }}>
                          <button
                            onClick={() => handlePageChange(Math.max(1, invoicePage - 1))}
                            disabled={invoicePage === 1}
                            className="gradient-btn-secondary"
                            style={{ padding: '10px 20px', fontSize: '0.85rem', borderRadius: '10px', cursor: invoicePage === 1 ? 'not-allowed' : 'pointer' }}
                          >
                            ➡️ الصفحة السابقة
                          </button>
                          <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                            الصفحة <strong>{invoicePage}</strong> من <strong>{totalSalesPages}</strong>
                          </span>
                          <button
                            onClick={() => handlePageChange(Math.min(totalSalesPages, invoicePage + 1))}
                            disabled={invoicePage === totalSalesPages}
                            className="gradient-btn-secondary"
                            style={{ padding: '10px 20px', fontSize: '0.85rem', borderRadius: '10px', cursor: invoicePage === totalSalesPages ? 'not-allowed' : 'pointer' }}
                          >
                            الصفحة التالية ⬅️
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            ) : reportsSubTab === 'products' ? (
              (() => {
              const productStats = {};
              sales.forEach(sale => {
                if (!sale.items) return;
                sale.items.forEach(item => {
                  const name = item.tradeName || 'منتج غير معروف';
                  const qty = Number(item.quantity) || Number(item.cartQuantity) || 0;
                  
                  let buyingPrice = Number(item.buyingPrice) || 0;
                  if (!buyingPrice) {
                    const invItem = inventory.find(inv => inv.tradeName === name || inv.id === item.id);
                    buyingPrice = Number(invItem?.buyingPrice) || 0;
                  }
                  
                  const price = Number(item.price) || Number(item.sellingPrice) || 0;
                  const revenue = qty * price;
                  const cost = qty * buyingPrice;
                  const profit = revenue - cost;

                  if (!productStats[name]) {
                    productStats[name] = {
                      name,
                      qty: 0,
                      revenue: 0,
                      profit: 0
                    };
                  }
                  productStats[name].qty += qty;
                  productStats[name].revenue += revenue;
                  productStats[name].profit += profit;
                });
              });

              const statsArray = Object.values(productStats);
              const byQty = [...statsArray].sort((a, b) => b.qty - a.qty).slice(0, 10);
              const byProfit = [...statsArray].sort((a, b) => b.profit - a.profit).slice(0, 10);

              const maxQty = byQty.length > 0 ? byQty[0].qty : 1;
              const maxProfit = byProfit.length > 0 ? byProfit[0].profit : 1;

              const starProduct = byQty[0] || null;
              const cashCow = byProfit[0] || null;

              return (
                <div style={{ direction: 'rtl', textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '28px', animation: 'fadeIn 0.3s ease' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                    <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%)', border: '1px solid rgba(59, 130, 246, 0.25)' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>⭐ المنتج الأكثر طلباً (الأعلى مبيعاً)</span>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: '8px 0 0 0' }}>
                        {starProduct ? `${starProduct.name} (${starProduct.qty} وحدة)` : 'لا توجد بيانات'}
                      </h3>
                    </div>
                    <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>💰 البطل المالي (الأكثر ربحية)</span>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#10b981', margin: '8px 0 0 0' }}>
                        {cashCow ? `${cashCow.name} (+${formatCurrency(cashCow.profit)} ج.م)` : 'لا توجد بيانات'}
                      </h3>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '28px' }}>
                    <div className="premium-card" style={{ padding: '24px' }}>
                      <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        📦 المنتجات الـ 10 الأكثر مبيعاً (من حيث الكمية)
                      </h3>
                      {byQty.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>لا توجد فواتير مبيعات في هذه الفترة لتحليلها.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {byQty.map((item, index) => {
                            const pct = Math.round((item.qty / maxQty) * 100);
                            return (
                              <div key={item.name} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
                                  <span style={{ color: '#fff', fontWeight: 'bold' }}>{index + 1}. {item.name}</span>
                                  <span style={{ color: 'var(--text-secondary)' }}>{item.qty} وحدة | {formatCurrency(item.revenue)} ج.م</span>
                                </div>
                                <div style={{ height: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)', borderRadius: '4px', transition: 'width 0.5s ease' }}></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="premium-card" style={{ padding: '24px' }}>
                      <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        💸 المنتجات الـ 10 الأكثر تحقيقاً للأرباح (صافي الربح)
                      </h3>
                      {byProfit.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>لا توجد فواتير مبيعات في هذه الفترة لتحليلها.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {byProfit.map((item, index) => {
                            const pct = Math.round((Math.max(0, item.profit) / maxProfit) * 100);
                            return (
                              <div key={item.name} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
                                  <span style={{ color: '#fff', fontWeight: 'bold' }}>{index + 1}. {item.name}</span>
                                  <span style={{ color: '#10b981', fontWeight: 'bold' }}>+{formatCurrency(item.profit)} ج.م</span>
                                </div>
                                <div style={{ height: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)', borderRadius: '4px', transition: 'width 0.5s ease' }}></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()
            ) : (
              /* Tab 2: Customers Directory & Loyalty */
              <div style={{ direction: 'rtl', textAlign: 'right', animation: 'fadeIn 0.3s ease' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', marginBottom: '20px' }}>
                  {isLoyaltyEnabled ? '👥 دليل العملاء وسجل نقاط الولاء النشطة' : '👥 دليل وعناوين العملاء المسجلين'}
                </h3>
                
                <div style={{ overflowX: 'auto' }}>
                  <table className="premium-table">
                    <thead>
                      <tr>
                        <th>اسم العميل</th>
                        <th>رقم الهاتف المحمول</th>
                        <th>عدد المشتريات (الزيارات)</th>
                        <th>إجمالي المشتريات المادية</th>
                        {isLoyaltyEnabled && <th>نقاط الولاء النشطة 🎁</th>}
                        <th>تاريخ آخر عملية شراء</th>
                        <th>تفاصيل العميل 📊</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loyaltyCustomers.length === 0 ? (
                        <tr>
                          <td colSpan={isLoyaltyEnabled ? 7 : 6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '36px' }}>
                            لا توجد بيانات عملاء مسجلة حالياً.
                          </td>
                        </tr>
                      ) : (
                        loyaltyCustomers.map(cust => (
                          <tr 
                            key={cust.id} 
                            onClick={() => setActiveCustomerDetails(cust)}
                            style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                            className="clickable-row-hover"
                          >
                            <td style={{ fontWeight: 700, color: '#fff' }}>{cust.name}</td>
                            <td style={{ fontWeight: 600, color: 'var(--primary)', fontFamily: 'Outfit, Cairo' }}>{cust.phone}</td>
                            <td style={{ fontWeight: 600 }}>{cust.visits || 0} زيارات 🏪</td>
                            <td style={{ fontWeight: 700, color: 'var(--accent-green)', fontFamily: 'Outfit, Cairo' }}>{formatCurrency(cust.spend)} ج.م</td>
                            {isLoyaltyEnabled && (
                              <td>
                                <span style={{ 
                                  background: 'rgba(251, 191, 36, 0.12)', 
                                  border: '1px solid rgba(251, 191, 36, 0.3)', 
                                  color: '#fbbf24', 
                                  padding: '5px 12px', 
                                  borderRadius: '10px', 
                                  fontSize: '0.85rem', 
                                  fontWeight: 'bold',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  ✨ {cust.points || 0} نقطة
                                </span>
                              </td>
                            )}
                            <td style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                              {cust.lastVisit ? new Date(cust.lastVisit).toLocaleDateString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'لا يتوفر'}
                            </td>
                            <td>
                              <button 
                                onClick={(e) => { e.stopPropagation(); setActiveCustomerDetails(cust); }}
                                className="gradient-btn" 
                                style={{ padding: '6px 14px', fontSize: '0.8rem', borderRadius: '8px', cursor: 'pointer' }}
                              >
                                📊 عرض التحليلات
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table></div>
                </div>
              )}
            </div>
          </>
        )}
      </>
    )}

      {/* ✏️ Edit Invoice Modal Popup */}
      {editingInvoice && createPortal(
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(5, 8, 16, 0.85)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '16px', 
          zIndex: 1000, 
          backdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.25s ease'
        }}>
          <div className="premium-card fade-in-content" style={{ 
            maxWidth: '500px', 
            width: '100%', 
            maxHeight: '90vh', 
            overflowY: 'auto', 
            background: 'linear-gradient(135deg, #0f172a 0%, #090d1a 100%)', 
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '24px', 
            padding: '28px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '16px', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 900, color: '#fff', margin: 0 }}>✏️ تعديل الفاتورة #{editingInvoice.invoiceId}</h3>
              <button 
                onClick={() => setEditingInvoice(null)} 
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.3rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>اسم العميل:</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  value={editCustName} 
                  onChange={e => setEditCustName(e.target.value)} 
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>هاتف العميل:</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  value={editCustPhone} 
                  onChange={e => setEditCustPhone(e.target.value)} 
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>طريقة الدفع:</label>
                <select 
                  className="premium-input" 
                  value={editPaymentMethod} 
                  onChange={e => setEditPaymentMethod(e.target.value)} 
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', background: '#0f172a', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer' }}
                >
                  <option value="cash">💵 كاش</option>
                  <option value="credit">💳 شبكة / فيزا</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>ملاحظات الفاتورة:</label>
                <textarea 
                  className="premium-input" 
                  value={editNote} 
                  onChange={e => setEditNote(e.target.value)} 
                  style={{ width: '100%', minHeight: '80px', padding: '10px 14px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', resize: 'vertical' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px', borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '16px' }}>
              <button 
                onClick={() => setEditingInvoice(null)} 
                className="gradient-btn-secondary" 
                style={{ padding: '10px 20px', fontSize: '0.9rem', borderRadius: '12px', cursor: 'pointer' }}
              >
                إلغاء
              </button>
              <button 
                onClick={handleSaveInvoiceEdits} 
                className="gradient-btn" 
                style={{ padding: '10px 24px', fontSize: '0.9rem', borderRadius: '12px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}
              >
                حفظ التعديلات 💾
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* 👁️ Preview Invoice Modal Popup */}
      {selectedPreviewInvoice && createPortal(
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(5, 8, 16, 0.85)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '16px', 
          zIndex: 1000, 
          backdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.25s ease'
        }}>
          <div className="premium-card fade-in-content hide-scrollbar" style={{ 
            width: '100%',
            maxWidth: '600px', 
            maxHeight: '90vh',
            overflowY: 'auto',
            background: 'linear-gradient(145deg, rgba(13, 20, 36, 0.95) 0%, rgba(8, 13, 27, 0.98) 100%)', 
            border: '1px solid rgba(255,255,255,0.08)', 
            direction: 'rtl', 
            padding: '32px', 
            borderRadius: '24px', 
            boxShadow: '0 20px 50px rgba(0,0,0,0.6)', 
            fontFamily: 'Cairo, sans-serif' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '16px' }}>
              <h3 style={{ fontSize: '1.35rem', fontWeight: 800, margin: 0, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>📄</span> تفاصيل الفاتورة رقم: #{selectedPreviewInvoice.invoiceId}
              </h3>
              <button 
                onClick={() => setSelectedPreviewInvoice(null)} 
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '1.8rem', outline: 'none' }}
              >
                &times;
              </button>
            </div>

            {/* Invoice Metadata Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px', marginBottom: '24px' }}>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>التاريخ واليوم</span>
                <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', marginTop: '6px' }}>{new Date(selectedPreviewInvoice.timestamp).toLocaleDateString('ar-EG', { dateStyle: 'full' })}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>وقت الزيارة والمسؤول</span>
                <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', marginTop: '6px' }}>
                  {new Date(selectedPreviewInvoice.timestamp).toLocaleTimeString('ar-EG', { timeStyle: 'short' })} | {selectedPreviewInvoice.cashierName || 'طبيب مناوب'}
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>طريقة الدفع والمعاملة</span>
                <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.95rem', marginTop: '6px' }}>
                  {selectedPreviewInvoice.paymentMethod === 'cash' ? '💵 دفع كاش نقدي' : selectedPreviewInvoice.paymentMethod === 'split' ? `💵💳 دفع مختلط (كاش: ${selectedPreviewInvoice.splitAmounts?.cash || 0} / فيزا: ${selectedPreviewInvoice.splitAmounts?.card || 0})` : selectedPreviewInvoice.paymentMethod === 'instapay' ? '📱 دفع إنستاباي' : selectedPreviewInvoice.paymentMethod === 'vodafone' ? '📲 دفع فودافون كاش' : selectedPreviewInvoice.paymentMethod === 'deferred' ? '⏳ بيع بالآجل' : '💳 دفع الكتروني / فيزا'}
                  {' | '}
                  {selectedPreviewInvoice.isDelivery ? '🛵 ديليفري' : '🏪 داخل المحل'}
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>اسم العميل المسجل</span>
                <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.95rem', marginTop: '6px' }}>{selectedPreviewInvoice.customerName || 'عميل نقدي عام'}</div>
              </div>
            </div>

            {/* Items Table */}
            <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>💊</span> الأصناف المشتراة بالفاتورة
            </h4>
            <div style={{ overflowX: 'auto', maxHeight: '220px', overflowY: 'auto', marginBottom: '24px', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px' }}>
              <table className="premium-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>اسم الدواء / الصنف</th>
                    <th style={{ textAlign: 'center' }}>الكمية</th>
                    <th style={{ textAlign: 'left' }}>السعر الفرعي</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPreviewInvoice.items.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: 700, color: '#fff' }}>{item.tradeName}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>
                        {item.subUnit ? `${Math.round(item.quantity * item.subUnit.ratio)} ${item.subUnit.name} (${Number(item.quantity.toFixed(4))} علبة)` : `${item.quantity} علبة`}
                      </td>
                      <td style={{ textAlign: 'left', color: 'var(--primary)', fontWeight: 700, fontFamily: 'Outfit' }}>{formatCurrency(item.price * item.quantity)} ج.م</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pricing sections */}
            <div style={{ borderTop: '1px dashed rgba(255,255,255,0.08)', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                <span>المجموع الفرعي:</span>
                <span style={{ fontWeight: 700, color: '#fff', fontFamily: 'Outfit' }}>{formatCurrency(selectedPreviewInvoice.subTotal || selectedPreviewInvoice.grandTotal)} ج.م</span>
              </div>
              {selectedPreviewInvoice.discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', color: 'var(--accent-pink)' }}>
                  <span>{selectedPreviewInvoice.promoCode ? `خصم الكوبون (${selectedPreviewInvoice.promoCode}):` : 'الخصم الممنوح:'}</span>
                  <span style={{ fontWeight: 700, fontFamily: 'Outfit' }}>-{formatCurrency(selectedPreviewInvoice.discount)} ج.م</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.2rem', fontWeight: 800, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '12px' }}>
                <span style={{ color: '#fff' }}>الإجمالي النهائي للفاتورة:</span>
                <span style={{ color: 'var(--accent-green)', fontFamily: 'Outfit' }}>{formatCurrency(selectedPreviewInvoice.grandTotal)} ج.م</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  onClick={() => { handleReprintInvoice(selectedPreviewInvoice); setSelectedPreviewInvoice(null); }} 
                  className="gradient-btn" 
                  style={{ flex: 1, padding: '12px', borderRadius: '12px', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}
                >
                  🖨️ إعادة طباعة الفاتورة حرارياً
                </button>
                <button 
                  onClick={() => { shareSaleViaWhatsApp(selectedPreviewInvoice); setSelectedPreviewInvoice(null); }} 
                  className="gradient-btn-secondary" 
                  style={{ flex: 1, padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', border: '1px solid rgba(34, 197, 94, 0.4)', color: '#4ade80' }}
                >
                  💬 مشاركة الفاتورة عبر واتساب
                </button>
              </div>
              <button 
                onClick={() => setSelectedPreviewInvoice(null)} 
                className="gradient-btn-secondary" 
                style={{ width: '100%', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                إغلاق المعاينة
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Partial Return / Refund Modal popup */}
      {refundInvoice && createPortal(
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(5, 8, 16, 0.88)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '16px', 
          zIndex: 1000,
          backdropFilter: 'blur(16px)',
          animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div className="premium-card fade-in-content" style={{ 
            width: '100%',
            maxWidth: '540px', 
            background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.95) 0%, rgba(9, 14, 28, 0.98) 100%)', 
            border: '1px solid rgba(244, 63, 94, 0.25)', 
            direction: 'rtl', 
            padding: '36px', 
            borderRadius: '24px', 
            boxShadow: '0 24px 60px rgba(244, 63, 94, 0.25)', 
            fontFamily: 'Cairo, sans-serif' 
          }}>
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '24px', 
              borderBottom: '1px solid rgba(255,255,255,0.06)', 
              paddingBottom: '16px' 
            }}>
              <h3 style={{ fontSize: '1.3rem', fontWeight: 800, margin: 0, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.5rem', filter: 'drop-shadow(0 0 8px rgba(244,63,94,0.4))' }}>↩️</span> معالجة مرتجع الفاتورة رقم: #{refundInvoice.invoiceId}
              </h3>
              <button 
                onClick={() => setRefundInvoice(null)} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'var(--text-secondary)', 
                  cursor: 'pointer', 
                  fontSize: '1.8rem', 
                  outline: 'none',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
              >
                &times;
              </button>
            </div>

            {refundInvoice.items.length > 0 && (
              <>
                {/* Option 1: Full Invoice Return */}
                <div style={{ marginBottom: '24px' }}>
                  <button 
                    onClick={() => handleRefundFullInvoice(refundInvoice.id || refundInvoice.invoiceId)}
                    className="gradient-btn"
                    style={{
                      width: '100%',
                      height: '52px',
                      borderRadius: '14px',
                      fontSize: '0.98rem',
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, rgba(244, 63, 94, 0.16) 0%, rgba(225, 29, 72, 0.25) 100%)',
                      border: '1px solid rgba(244, 63, 94, 0.45)',
                      color: '#ff4b72',
                      boxShadow: '0 8px 24px rgba(244, 63, 94, 0.15)',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.background = 'linear-gradient(135deg, rgba(244, 63, 94, 0.26) 0%, rgba(225, 29, 72, 0.35) 100%)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(244, 63, 94, 0.25)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.background = 'linear-gradient(135deg, rgba(244, 63, 94, 0.16) 0%, rgba(225, 29, 72, 0.25) 100%)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(244, 63, 94, 0.15)'; }}
                  >
                    <span>🔄</span> إرجاع الفاتورة بالكامل (استرجاع كلي)
                  </button>
                </div>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '24px 0 18px 0', color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: 700 }}>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
                  <span>أو استرجاع أصناف محددة جزئياً</span>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
                </div>
              </>
            )}

            {/* Option 2: Partial Items Return */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '14px', 
              maxHeight: '280px', 
              overflowY: 'auto', 
              marginBottom: '28px',
              paddingRight: '4px'
            }}>
              {refundInvoice.items.length === 0 ? (
                <div style={{ 
                  color: 'var(--accent-pink)', 
                  textAlign: 'center', 
                  padding: '24px 16px', 
                  fontWeight: 800,
                  background: 'rgba(244, 63, 94, 0.08)',
                  borderRadius: '16px',
                  border: '1px dashed rgba(244, 63, 94, 0.2)'
                }}>
                  ✨ تم استرجاع كافة أصناف هذه الفاتورة بالكامل!
                </div>
              ) : (
                refundInvoice.items.map((it, idx) => (
                  <RefundRow key={idx} it={it} handleRefundItem={handleRefundItem} invoiceId={refundInvoice.id || refundInvoice.invoiceId} />
                ))
              )}
            </div>

            {/* Footer Summary & Close */}
            <div style={{ 
              borderTop: '1px solid rgba(255,255,255,0.06)', 
              paddingTop: '24px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', fontWeight: 700 }}>القيمة المتبقية بالفاتورة:</span>
                <span style={{ 
                  color: 'var(--accent-green)', 
                  fontWeight: 900, 
                  fontSize: '1.25rem', 
                  marginRight: '8px', 
                  fontFamily: 'Outfit',
                  filter: 'drop-shadow(0 0 6px rgba(16,185,129,0.3))'
                }}>
                  {formatCurrency(refundInvoice.grandTotal)} ج.م
                </span>
              </div>
              <button 
                onClick={() => setRefundInvoice(null)} 
                className="gradient-btn"
                style={{ padding: '10px 24px' }}
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* 📊 Customer Details & Loyalty Analytics Modal */}
      {activeCustomerDetails && createPortal((() => {
        const { customerSales, topProducts, stats } = getCustomerAnalytics(activeCustomerDetails.phone);
        return (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(3, 7, 18, 0.95)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '16px', 
            zIndex: 1100, 
            backdropFilter: 'blur(16px)',
            animation: 'fadeIn 0.25s ease',
            fontFamily: 'Cairo, sans-serif',
            direction: 'rtl'
          }} onClick={() => setActiveCustomerDetails(null)}>
            <div 
              className="premium-card fade-in-content hide-scrollbar" 
              style={{ 
                maxWidth: '900px', 
                width: '100%', 
                background: 'linear-gradient(135deg, #0e1628 0%, #030712 100%)', 
                border: '1.5px solid rgba(99, 102, 241, 0.4)', 
                borderRadius: '28px', 
                boxShadow: '0 25px 60px rgba(0,0,0,0.7), inset 0 0 24px rgba(99, 102, 241, 0.05)', 
                padding: '32px',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '16px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    width: '50px', 
                    height: '50px', 
                    borderRadius: '50%', 
                    background: 'rgba(99, 102, 241, 0.15)', 
                    border: '1.5px solid var(--primary)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '1.5rem'
                  }}>
                    👤
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.4rem', fontWeight: 900, color: '#fff', margin: 0 }}>{activeCustomerDetails.name}</h3>
                    <span style={{ fontSize: '0.88rem', color: 'var(--primary)', fontWeight: 700, fontFamily: 'Outfit, Cairo', display: 'block', marginTop: '2px' }}>
                      📞 {activeCustomerDetails.phone}
                    </span>
                  </div>
                </div>
                <button 
                  onClick={() => setActiveCustomerDetails(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '2rem', outline: 'none' }}
                >
                  &times;
                </button>
              </div>

              {/* Grid Content */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '28px', marginBottom: '28px' }}>
                
                {/* Right Column: Lifetime Stats and Purchases */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  {/* Stats Overview */}
                  <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '20px', padding: '20px' }}>
                    <h4 style={{ fontSize: '1rem', color: '#fff', fontWeight: 'bold', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>📊</span> إحصائيات النشاط العام للعميل
                    </h4>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div style={{ background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.03)' }}>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>إجمالي المشتريات:</span>
                        <strong style={{ fontSize: '1.15rem', color: 'var(--accent-green)', fontFamily: 'Outfit' }}>{formatCurrency(stats.totalSpend)} ج.م</strong>
                      </div>
                      <div style={{ background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.03)' }}>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>نقاط الولاء الحالية:</span>
                        <strong style={{ fontSize: '1.15rem', color: '#fbbf24', fontFamily: 'Outfit' }}>✨ {activeCustomerDetails.points || 0} نقطة</strong>
                      </div>
                      <div style={{ background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.03)' }}>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>عدد الزيارات (الفواتير):</span>
                        <strong style={{ fontSize: '1.1rem', color: '#fff' }}>{customerSales.length} فاتورة</strong>
                      </div>
                      <div style={{ background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.03)' }}>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>متوسط الفاتورة:</span>
                        <strong style={{ fontSize: '1.1rem', color: '#fff', fontFamily: 'Outfit' }}>{formatCurrency(stats.avgOrderValue)} ج.م</strong>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', fontSize: '0.85rem', color: 'var(--text-secondary)', borderTop: '1px dashed rgba(255,255,255,0.06)', paddingTop: '12px' }}>
                      <span>🛵 دليفري: <b>{stats.deliveryCount}</b></span>
                      <span>🏪 داخل الصيدلية: <b>{stats.inStoreCount}</b></span>
                      <span>🎁 التوفير الإجمالي: <b style={{ color: 'var(--accent-green)', fontFamily: 'Outfit' }}>{formatCurrency(stats.totalDiscount)} ج.م</b></span>
                    </div>
                  </div>

                  {/* Top Purchased Products */}
                  <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '20px', padding: '20px', flex: 1 }}>
                    <h4 style={{ fontSize: '1rem', color: '#fff', fontWeight: 'bold', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>💊</span> الأدوية والأصناف الأكثر شراءً للعميل
                    </h4>
                    
                    {topProducts.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                        لا توجد أصناف مشتراة مسجلة في سجل الفواتير لهذا العميل.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {topProducts.map((prod, idx) => {
                          const maxQty = topProducts[0].qty || 1;
                          const pct = (prod.qty / maxQty) * 100;
                          return (
                            <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.88rem' }}>
                                <span style={{ color: '#cbd5e1', fontWeight: 700 }}>{idx + 1}. {prod.name}</span>
                                <strong style={{ color: 'var(--primary)', fontFamily: 'Outfit' }}>{prod.qty} علب</strong>
                              </div>
                              <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px', overflow: 'hidden' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, var(--primary) 0%, #1d4ed8 100%)', borderRadius: '10px' }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Left Column: Points Ledger & Invoices */}
                <div style={{ display: 'flex', flexDirection: 'column', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '20px', padding: '20px' }}>
                  <h4 style={{ fontSize: '1rem', color: '#fff', fontWeight: 'bold', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>📜</span> سجل حركة النقاط والفواتير التفصيلي
                  </h4>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', maxHeight: '420px', paddingLeft: '6px' }}>
                    {customerSales.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                        لا توجد فواتير أو حركات نقاط مسجلة لهذا العميل.
                      </div>
                    ) : (
                      customerSales.map((sale) => {
                        const earned = sale.pointsEarned || 0;
                        const redeemed = sale.pointsDiscount || 0;
                        return (
                          <div 
                            key={sale.id} 
                            style={{ 
                              background: 'rgba(0,0,0,0.2)', 
                              border: '1px solid rgba(255,255,255,0.03)', 
                              borderRadius: '14px', 
                              padding: '14px 16px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '12px'
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '0.78rem', background: 'rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: '6px', fontFamily: 'Outfit', color: 'var(--text-secondary)' }}>
                                  #{sale.invoiceId}
                                </span>
                                {sale.isDelivery ? (
                                  <span style={{ fontSize: '0.72rem', background: 'rgba(139,92,246,0.15)', color: '#a78bfa', padding: '2px 6px', borderRadius: '6px', fontWeight: 'bold' }}>🛵 دليفري</span>
                                ) : (
                                  <span style={{ fontSize: '0.72rem', background: 'rgba(16,185,129,0.15)', color: '#34d399', padding: '2px 6px', borderRadius: '6px', fontWeight: 'bold' }}>🏪 كاشير</span>
                                )}
                              </div>
                              <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                                {new Date(sale.timestamp).toLocaleDateString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <div style={{ fontSize: '0.8rem', color: '#fff', marginTop: '4px', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {sale.items?.map(it => `${it.tradeName} (${it.quantity}x)`).join(' ، ')}
                              </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                              <span style={{ fontSize: '1rem', color: '#fff', fontWeight: 900, fontFamily: 'Outfit' }}>
                                {formatCurrency(sale.grandTotal)} ج.م
                              </span>
                              
                              {/* Points Indicators */}
                              {isLoyaltyEnabled && (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  {earned > 0 && (
                                    <span style={{ fontSize: '0.75rem', background: 'rgba(16, 185, 129, 0.12)', color: 'var(--accent-green)', padding: '2px 8px', borderRadius: '6px', fontWeight: 'bold', border: '1px solid rgba(16,185,129,0.2)' }}>
                                      +{earned} ✨
                                    </span>
                                  )}
                                  {redeemed > 0 && (
                                    <span style={{ fontSize: '0.75rem', background: 'rgba(239, 68, 68, 0.12)', color: '#f87171', padding: '2px 8px', borderRadius: '6px', fontWeight: 'bold', border: '1px solid rgba(239,68,68,0.2)' }}>
                                      -{redeemed} 🎁
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '20px' }}>
                <button 
                  onClick={() => setActiveCustomerDetails(null)} 
                  className="gradient-btn"
                  style={{ padding: '12px 36px', fontSize: '1rem', fontWeight: 'bold', borderRadius: '12px' }}
                >
                  إغلاق تفاصيل العميل
                </button>
              </div>
            </div>
          </div>
        );
      })(), document.body)}

      {/* 🛑 System Modals & Prompts */}
      {systemDialog && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', zIndex: 999999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}
             onClick={() => systemDialog.type !== 'confirm' && setSystemDialog(null)}>
          <div className="premium-card fade-in-content" style={{ maxWidth: '450px', width: '100%', padding: '30px', background: 'linear-gradient(135deg, #1e1e2f 0%, #151522 100%)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.8)' }}
               onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>
              {systemDialog.type === 'confirm' ? '❓' : '⚠️'}
            </div>
            <h3 style={{ fontSize: '1.4rem', color: '#fff', fontWeight: 800, margin: '0 0 12px 0' }}>{systemDialog.title}</h3>
            <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 24px 0' }}>
              {systemDialog.message}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              {systemDialog.type === 'confirm' ? (
                <>
                  <button 
                    onClick={systemDialog.onConfirm}
                    className="gradient-btn"
                    style={{ padding: '12px 24px', flex: 1, borderRadius: '12px', fontWeight: 'bold' }}
                  >
                    نعم، تأكيد
                  </button>
                  <button 
                    onClick={() => setSystemDialog(null)}
                    style={{ padding: '12px 24px', flex: 1, borderRadius: '12px', fontWeight: 'bold', background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
                  >
                    إلغاء
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => setSystemDialog(null)}
                  className="gradient-btn"
                  style={{ padding: '12px 32px', borderRadius: '12px', fontWeight: 'bold' }}
                >
                  حسناً، فهمت
                </button>
              )}
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}

function SubscriptionView({ currentUserData }) {
  const daysRemaining = getSubscriptionDaysRemaining(currentUserData);
  const [deviceCount, setDeviceCount] = useState(currentUserData?.deviceLimit || 2);
  const [copiedVoda, setCopiedVoda] = useState(false);
  const [copiedInsta, setCopiedInsta] = useState(false);

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'voda') {
      setCopiedVoda(true);
      setTimeout(() => setCopiedVoda(false), 2000);
    } else {
      setCopiedInsta(true);
      setTimeout(() => setCopiedInsta(false), 2000);
    }
    if (window.triggerDrMasrToast) {
      window.triggerDrMasrToast('📋 تم نسخ البيانات بنجاح!');
    }
  };

  const extraDevices = Math.max(0, deviceCount - 2);
  const monthlyPrice = 250 + extraDevices * 100;
  const yearlyPrice = 2500 + extraDevices * 1000;
  const lifetimePrice = 60000 + extraDevices * 2000;

  // Admin Subscription Controller State
  const [isAdmin, setIsAdmin] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [newPlan, setNewPlan] = useState('trial');
  const [newExpiry, setNewExpiry] = useState('');
  const [adminDeviceLimitInput, setAdminDeviceLimitInput] = useState(2);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminMsg, setAdminMsg] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [clientItemCounts, setClientItemCounts] = useState({}); // { [clientId]: number }
  const [loadingItemCount, setLoadingItemCount] = useState(false);

  // Admin Sub-Tabs
  const [adminTab, setAdminTab] = useState('clients'); // clients, notifications, promocodes

  // Sales Calculator State
  const [calcStartDate, setCalcStartDate] = useState('');
  const [calcEndDate, setCalcEndDate] = useState('');
  const [calculatedSales, setCalculatedSales] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // Global Announcements State
  const [announcementText, setAnnouncementText] = useState('');
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [publishingAnnounce, setPublishingAnnounce] = useState(false);
  const [adminAnnouncements, setAdminAnnouncements] = useState([]);

  // Promocodes State
  const [promoCodeName, setPromoCodeName] = useState('');
  const [promoDaysBenefit, setPromoDaysBenefit] = useState(30);
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [promocodesList, setPromocodesList] = useState([]);
  const [promoSingleUse, setPromoSingleUse] = useState(false);

  const fetchPromocodes = async () => {
    try {
      const snap1 = await getDocs(collection(db, 'promocodes'));
      const list1 = snap1.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const snap2 = await getDocs(collection(db, 'promoCodes'));
      const list2 = snap2.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const mergedMap = new Map();
      list1.forEach(p => mergedMap.set((p.code || p.id).toUpperCase(), { ...p, code: p.code || p.id }));
      list2.forEach(p => mergedMap.set((p.code || p.id).toUpperCase(), { ...p, code: p.code || p.id }));
      setPromocodesList(Array.from(mergedMap.values()));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const snap = await getDocs(collection(db, 'global_announcements'));
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setAdminAnnouncements(list);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreatePromoCode = async (e) => {
    e.preventDefault();
    if (!promoCodeName.trim()) return;
    setCreatingPromo(true);
    try {
      const cleanName = promoCodeName.trim().toUpperCase();
      await setDoc(doc(db, 'promocodes', cleanName), {
        code: cleanName,
        daysBenefit: parseInt(promoDaysBenefit) || 30,
        isActive: true,
        singleUse: promoSingleUse,
        createdAt: new Date().toISOString()
      });
      setPromoCodeName('');
      setPromoSingleUse(false);
      window.triggerDrMasrToast("✅ تم إنشاء البروموكود بنجاح!");
      fetchPromocodes();
    } catch (err) {
      console.error("Error creating promocode:", err);
      window.triggerDrMasrToast("❌ فشل إنشاء البروموكود.");
    } finally {
      setCreatingPromo(false);
    }
  };

  const handleTogglePromoStatus = async (promo) => {
    try {
      let promoDocRef = doc(db, 'promocodes', promo.code);
      let promoDocSnap = await getDoc(promoDocRef);
      if (!promoDocSnap.exists()) {
        promoDocRef = doc(db, 'promoCodes', promo.code);
      }
      await updateDoc(promoDocRef, {
        isActive: !promo.isActive
      });
      window.triggerDrMasrToast("✅ تم تعديل حالة الكود!");
      fetchPromocodes();
    } catch(err) {
      console.error(err);
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    if (!announcementTitle.trim() || !announcementText.trim()) return;
    setPublishingAnnounce(true);
    try {
      await addDoc(collection(db, 'global_announcements'), {
        title: announcementTitle.trim(),
        text: announcementText.trim(),
        createdAt: new Date().toISOString()
      });
      setAnnouncementTitle('');
      setAnnouncementText('');
      window.triggerDrMasrToast("✅ تم نشر التنبيه لجميع الصيدليات!");
      fetchAnnouncements();
    } catch (err) {
      console.error("Error publishing announcement:", err);
      window.triggerDrMasrToast("❌ فشل نشر التنبيه.");
    } finally {
      setPublishingAnnounce(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await deleteDoc(doc(db, 'global_announcements', id));
      window.triggerDrMasrToast("✅ تم حذف التنبيه!");
      fetchAnnouncements();
    } catch (err) {
      console.error(err);
    }
  };

  const handleCalculateClientSales = async (clientId) => {
    if (!clientId) return;
    setCalculating(true);
    setCalculatedSales(null);
    try {
      const q = collection(db, 'users', clientId, 'sales');
      const snap = await getDocs(q);
      let total = 0;
      let count = 0;
      const start = calcStartDate ? new Date(calcStartDate) : null;
      if (start) start.setHours(0,0,0,0);
      const end = calcEndDate ? new Date(calcEndDate) : null;
      if (end) end.setHours(23,59,59,999);

      snap.forEach(doc => {
        const data = doc.data();
        if (data.grandTotal !== undefined) {
          let matches = true;
          if (data.timestamp) {
            const t = new Date(data.timestamp);
            if (start && t < start) matches = false;
            if (end && t > end) matches = false;
          }
          if (matches) {
            total += data.grandTotal;
            count += 1;
          }
        }
      });
      setCalculatedSales({ total, count });
    } catch (err) {
      console.error("Error calculating sales:", err);
      window.triggerDrMasrToast("❌ فشل احتساب مبيعات هذا المستخدم.");
    } finally {
      setCalculating(false);
    }
  };

  useEffect(() => {
    const adminEmails = ['baselsalah07@gmail.com'];
    if (currentUserData && adminEmails.includes(currentUserData.email)) {
      setIsAdmin(true);
      const fetchClients = async () => {
        try {
          const querySnapshot = await getDocs(collection(db, 'users'));
          const list = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            list.push({ id: doc.id, ...data });
          });
          setClients(list);
        } catch (e) {
          console.error("Error loading clients:", e);
        }
      };
      fetchClients();
      fetchPromocodes();
      fetchAnnouncements();
    }
  }, [currentUserData]);

  const handleUpdateClientPlan = async (e) => {
    e.preventDefault();
    if (!selectedClientId) {
      setAdminMsg('⚠️ يرجى اختيار صيدلية عميل أولاً!');
      return;
    }
    setAdminLoading(true);
    setAdminMsg('');
    try {
      const clientDocRef = doc(db, 'users', selectedClientId);
      let expiryDateObject = null;
      if (newPlan === 'lifetime') {
        expiryDateObject = new Date('2099-12-31T23:59:59Z'); // Lifetime date fallback
      } else {
        expiryDateObject = new Date(newExpiry);
      }
      
      // Update plan, expiryDate and deviceLimit in Firestore
      await updateDoc(clientDocRef, {
        plan: newPlan,
        expiryDate: expiryDateObject,
        deviceLimit: Number(adminDeviceLimitInput) || 2
      });

      // Update local clients state so UI reflects it immediately
      setClients(prev => prev.map(c => {
        if (c.id === selectedClientId) {
          return { ...c, plan: newPlan, expiryDate: expiryDateObject, deviceLimit: Number(adminDeviceLimitInput) || 2 };
        }
        return c;
      }));

      setAdminMsg('✅ تم تحديث خطة العميل بنجاح في قاعدة البيانات! 🎉');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('تم تحديث خطة العميل بنجاح! 🎉');
      }
    } catch (err) {
      console.error("Error updating client plan:", err);
      setAdminMsg('❌ فشل تحديث البيانات في الفايربيز.');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleClientSelection = (clientId) => {
    setSelectedClientId(clientId);
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setNewPlan(client.plan || 'trial');
      
      // Parse client's expiryDate to YYYY-MM-DD
      let dateStr = '';
      if (client.expiryDate) {
        let d = null;
        if (client.expiryDate.toDate && typeof client.expiryDate.toDate === 'function') {
          d = client.expiryDate.toDate();
        } else {
          d = new Date(client.expiryDate);
        }
        if (!isNaN(d.getTime())) {
          dateStr = d.toISOString().substring(0, 10);
        }
      }
      setNewExpiry(dateStr);
      setAdminDeviceLimitInput(client.deviceLimit || 2);
    } else {
      setNewExpiry('');
      setAdminDeviceLimitInput(2);
    }
  };

  // Quick subscription adjustment: add/subtract days from a client's expiryDate
  const handleQuickAdjustExpiry = async (client, daysDelta, resetToToday = false) => {
    if (!client) return;
    setAdminLoading(true);
    try {
      const clientDocRef = doc(db, 'users', client.id);
      let base;
      if (resetToToday) {
        base = new Date();
        base.setHours(23, 59, 59, 0);
      } else {
        // Start from current expiryDate or today if expired
        if (client.expiryDate) {
          const raw = client.expiryDate;
          if (raw && typeof raw.toDate === 'function') base = raw.toDate();
          else base = new Date(raw);
          if (isNaN(base.getTime())) base = new Date();
        } else {
          base = new Date();
        }
        // If expiry is in the past, base from today for additions
        if (daysDelta > 0 && base < new Date()) base = new Date();
        base = new Date(base.getTime() + daysDelta * 24 * 60 * 60 * 1000);
      }
      await updateDoc(clientDocRef, { expiryDate: base });
      setClients(prev => prev.map(c => c.id === client.id ? { ...c, expiryDate: base } : c));
      window.triggerDrMasrToast('✅ تم تعديل تاريخ انتهاء الاشتراك بنجاح!');
    } catch (err) {
      console.error(err);
      window.triggerDrMasrToast('❌ فشل تعديل تاريخ الاشتراك: ' + err.message);
    } finally {
      setAdminLoading(false);
    }
  };

  const handleFetchClientItemCount = async (clientId) => {
    if (clientItemCounts[clientId] !== undefined) return; // cached
    setLoadingItemCount(true);
    try {
      const snap = await getDocs(collection(db, 'users', clientId, 'inventory'));
      setClientItemCounts(prev => ({ ...prev, [clientId]: snap.size }));
    } catch (e) {
      setClientItemCounts(prev => ({ ...prev, [clientId]: '—' }));
    } finally {
      setLoadingItemCount(false);
    }
  };

  const getClientExpiryDate = (client) => {
    if (!client?.expiryDate) return null;
    const raw = client.expiryDate;
    if (raw && typeof raw.toDate === 'function') return raw.toDate();
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  };

  const getClientDeviceType = (deviceId) => {
    if (!deviceId) return { icon: '💻', label: 'جهاز' };
    const lower = deviceId.toLowerCase();
    if (lower.includes('iphone') || lower.includes('ios')) return { icon: '📱', label: 'iPhone' };
    if (lower.includes('android')) return { icon: '📱', label: 'Android' };
    if (lower.includes('ipad') || lower.includes('tablet')) return { icon: '📲', label: 'Tablet' };
    if (lower.includes('mobile')) return { icon: '📱', label: 'موبايل' };
    if (lower.includes('mac')) return { icon: '💻', label: 'MacBook' };
    return { icon: '🖥️', label: 'كمبيوتر' };
  };

  const handleWhatsAppContact = (planName, price, totalDevices) => {
    const pharmacyName = currentUserData?.pharmacyName || "غير محدد";
    const shortId = currentUserData?.shortId || "غير متوفر";
    const message = `أهلاً إدارة دكتور مصر \nأود تفعيل *${planName}*\n- عدد الأجهزة المطلوب: *${totalDevices} أجهزة*\n- القيمة الإجمالية: *${price} ج.م*\n\n *بيانات الصيدلية:*\n- اسم الصيدلية: *${pharmacyName}*\n- كود العميل (Short ID): *${shortId}*\n\nلقد قمت بتحويل قيمة الاشتراك ومرفق لقطة الشاشة للتأكيد والتفعيل الفوري `;
    const encodedText = encodeURIComponent(message);
    window.open(`https://wa.me/201004757940?text=${encodedText}`, '_blank');
  };

  return (
    <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '32px', direction: 'rtl' }}>
      
      {/* 🛠️ Super Admin Control Panel */}
      {isAdmin && (
        <div className="premium-card fade-in-content" style={{ borderRight: '5px solid var(--primary)', background: 'rgba(56, 189, 248, 0.05)', padding: '24px', direction: 'rtl' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🛠️</span> لوحة إدارة خدمات دكتور مصر (الإدارة العليا)
          </h3>
          
          {/* Admin Tabs */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>
            <button 
              type="button" 
              onClick={() => setAdminTab('clients')}
              className={`delivery-tab-btn ${adminTab === 'clients' ? 'active' : ''}`}
              style={{ fontSize: '0.88rem', padding: '8px 16px', background: adminTab === 'clients' ? 'var(--primary)' : 'rgba(255,255,255,0.03)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
            >
              🏢 إدارة المشتركين ({clients.length})
            </button>
            <button 
              type="button" 
              onClick={() => setAdminTab('notifications')}
              className={`delivery-tab-btn ${adminTab === 'notifications' ? 'active' : ''}`}
              style={{ fontSize: '0.88rem', padding: '8px 16px', background: adminTab === 'notifications' ? 'var(--primary)' : 'rgba(255,255,255,0.03)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
            >
              📢 الإشعارات العامة
            </button>
            <button 
              type="button" 
              onClick={() => setAdminTab('promocodes')}
              className={`delivery-tab-btn ${adminTab === 'promocodes' ? 'active' : ''}`}
              style={{ fontSize: '0.88rem', padding: '8px 16px', background: adminTab === 'promocodes' ? 'var(--primary)' : 'rgba(255,255,255,0.03)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
            >
              🎫 إدارة البروموكود
            </button>
          </div>

          {adminMsg && (
            <div style={{ padding: '12px', borderRadius: '8px', background: adminMsg.startsWith('✅') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', color: adminMsg.startsWith('✅') ? 'var(--accent-green)' : '#f87171', fontWeight: 'bold', fontSize: '0.95rem', marginBottom: '16px', border: '1px solid currentColor' }}>
              {adminMsg}
            </div>
          )}

          {/* TAB 1: Clients Subscription & Profits */}
          {adminTab === 'clients' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <form onSubmit={handleUpdateClientPlan} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', alignItems: 'end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>اختر الصيدلية / العميل:</label>
                  <select 
                    className="premium-input" 
                    style={{ background: 'rgba(8, 13, 27, 0.95)', border: '1px solid var(--border)', color: '#fff', padding: '10px', borderRadius: '8px', cursor: 'pointer' }}
                    value={selectedClientId}
                    onChange={(e) => handleClientSelection(e.target.value)}
                  >
                    <option value="">-- اختر صيدلية من القائمة --</option>
                    {clients.map(c => {
                      let planColor = '#f87171'; // Trial red
                      let planText = 'تجريبي';
                      if (c.plan === 'subscribe') {
                        planColor = '#34d399'; // Monthly green
                        planText = 'مشترك شهري/سنوي';
                      } else if (c.plan === 'lifetime') {
                        planColor = '#fbbf24'; // Lifetime gold
                        planText = 'مدى الحياة 👑';
                      }
                      return (
                        <option key={c.id} value={c.id} style={{ color: planColor }}>
                          {c.pharmacyName || 'اسم غير محدد'} ({c.ownerName || 'لا يوجد'}) - [{planText}] {c.promoCode ? `(كود: ${c.promoCode})` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>اختر الخطة المطلوبة (Plan Select):</label>
                  <select 
                    className="premium-input" 
                    style={{ background: 'rgba(8, 13, 27, 0.95)', border: '1px solid var(--border)', color: '#fff', padding: '10px', borderRadius: '8px', cursor: 'pointer' }}
                    value={newPlan}
                    onChange={(e) => setNewPlan(e.target.value)}
                  >
                    <option value="trial">الفترة التجريبية (trial) 🔴</option>
                    <option value="subscribe">الخطة النشطة المدفوعة (subscribe) 🟢</option>
                    <option value="lifetime">الخطة مدى الحياة (lifetime) 👑</option>
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>تاريخ انتهاء الصلاحية:</label>
                  <input 
                    type="date" 
                    className="premium-input" 
                    style={{ background: 'rgba(8, 13, 27, 0.95)', border: '1px solid var(--border)', color: '#fff', padding: '10px', borderRadius: '8px' }}
                    value={newExpiry}
                    onChange={(e) => setNewExpiry(e.target.value)}
                    disabled={newPlan === 'lifetime'}
                    required={newPlan !== 'lifetime'}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>حد الأجهزة المسموح به:</label>
                  <input 
                    type="number" 
                    min="1"
                    className="premium-input" 
                    style={{ background: 'rgba(8, 13, 27, 0.95)', border: '1px solid var(--border)', color: '#fff', padding: '10px', borderRadius: '8px', width: '100px' }}
                    value={adminDeviceLimitInput}
                    onChange={(e) => setAdminDeviceLimitInput(Math.max(1, parseInt(e.target.value) || 2))}
                    required
                  />
                </div>

                <div>
                  <button 
                    type="submit" 
                    className="premium-btn-primary" 
                    style={{ width: '100%', padding: '12px', fontSize: '0.95rem', fontWeight: 'bold' }}
                    disabled={adminLoading}
                  >
                    {adminLoading ? 'جاري التحديث...' : '⚡ تحديث خطة العميل'}
                  </button>
                </div>
              </form>

              {/* Sales Period Calculator */}
              {selectedClientId && (
                <div style={{ padding: '16px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px dashed var(--border)', marginTop: '10px' }}>
                  <h4 style={{ color: '#fff', marginBottom: '12px', fontSize: '0.92rem' }}>💰 احتساب أرباح ومبيعات الصيدلية المختارة خلال فترة معينة:</h4>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'end', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>من تاريخ:</span>
                      <input type="date" className="premium-input" value={calcStartDate} onChange={e => setCalcStartDate(e.target.value)} style={{ padding: '8px', fontSize: '0.85rem' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>إلى تاريخ:</span>
                      <input type="date" className="premium-input" value={calcEndDate} onChange={e => setCalcEndDate(e.target.value)} style={{ padding: '8px', fontSize: '0.85rem' }} />
                    </div>
                    <button 
                      type="button" 
                      onClick={() => handleCalculateClientSales(selectedClientId)} 
                      className="premium-btn-secondary" 
                      style={{ padding: '8px 20px', fontSize: '0.85rem' }}
                      disabled={calculating}
                    >
                      {calculating ? 'جاري الحساب...' : '📊 احسب المبيعات'}
                    </button>
                  </div>

                  {calculatedSales !== null && (
                    <div style={{ marginTop: '14px', background: 'rgba(56, 189, 248, 0.08)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(56, 189, 248, 0.2)', display: 'flex', gap: '24px' }}>
                      <span style={{ color: '#fff', fontSize: '0.9rem' }}>💸 إجمالي المبيعات المحققة: <strong style={{ color: '#38bdf8', fontSize: '1.05rem' }}>{calculatedSales.total.toLocaleString()} ج.م</strong></span>
                      <span style={{ color: '#fff', fontSize: '0.9rem' }}>📦 عدد فواتير المبيعات: <strong style={{ color: '#38bdf8', fontSize: '1.05rem' }}>{calculatedSales.count} فاتورة</strong></span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: Publish Public Alerts/Announcements */}
          {adminTab === 'notifications' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <form onSubmit={handleCreateAnnouncement} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>عنوان الإشعار:</label>
                  <input 
                    type="text" 
                    className="premium-input" 
                    placeholder="مثال: تحديث أمني هام في النظام" 
                    value={announcementTitle} 
                    onChange={e => setAnnouncementTitle(e.target.value)} 
                    required 
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>نص الإشعار:</label>
                  <textarea 
                    className="premium-input" 
                    rows={3} 
                    placeholder="اكتب هنا التفاصيل التي ستظهر لكل الصيدليات المشتركة..." 
                    value={announcementText} 
                    onChange={e => setAnnouncementText(e.target.value)} 
                    required 
                  />
                </div>
                <div>
                  <button 
                    type="submit" 
                    className="premium-btn-primary" 
                    style={{ padding: '10px 24px', fontSize: '0.9rem' }}
                    disabled={publishingAnnounce}
                  >
                    {publishingAnnounce ? 'جاري النشر...' : '📢 نشر وتعميم الإشعار'}
                  </button>
                </div>
              </form>

              {/* Published Notifications List */}
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ color: '#fff', fontSize: '0.92rem', marginBottom: '12px' }}>📋 الإشعارات العامة المنشورة حالياً:</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {adminAnnouncements.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>لا توجد تنبيهات منشورة حالياً.</p>
                  ) : (
                    adminAnnouncements.map(a => (
                      <div key={a.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', padding: '12px 16px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong style={{ color: '#fff', fontSize: '0.9rem' }}>{a.title}</strong>
                          <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: '4px 0 0' }}>{a.text}</p>
                          <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)' }}>تاريخ النشر: {new Date(a.createdAt).toLocaleString('ar-EG')}</span>
                        </div>
                        <button 
                          type="button" 
                          onClick={() => handleDeleteAnnouncement(a.id)} 
                          style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem' }}
                        >
                          حذف 🗑️
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: Promo Codes */}
          {adminTab === 'promocodes' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <form onSubmit={handleCreatePromoCode} style={{ display: 'flex', gap: '16px', alignItems: 'end', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>اسم البروموكود (كود الخصم):</label>
                  <input 
                    type="text" 
                    className="premium-input" 
                    placeholder="مثال: MASR30" 
                    value={promoCodeName} 
                    onChange={e => setPromoCodeName(e.target.value)} 
                    style={{ textTransform: 'uppercase' }}
                    required 
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>عدد أيام الاشتراك المجاني الممنوحة:</label>
                  <input 
                    type="number" 
                    className="premium-input" 
                    value={promoDaysBenefit} 
                    onChange={e => setPromoDaysBenefit(e.target.value)} 
                    style={{ width: '120px' }}
                    required 
                  />
                </div>
                <button 
                  type="submit" 
                  className="premium-btn-primary" 
                  style={{ padding: '10px 24px', fontSize: '0.9rem' }}
                  disabled={creatingPromo}
                >
                  {creatingPromo ? 'جاري الإنشاء...' : '🎫 إنشاء الكود الترويجي'}
                </button>
              </form>

              {/* Promocodes List */}
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ color: '#fff', fontSize: '0.92rem', marginBottom: '12px' }}>📋 قائمة الأكواد الترويجية وتتبع مستخدميها:</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {promocodesList.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>لا توجد أكواد ترويجية حالياً.</p>
                  ) : (
                    promocodesList.map(promo => {
                      const usersUsingCode = clients.filter(c => c.promoCode === promo.code);
                      return (
                        <div key={promo.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', padding: '14px 18px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' }}>
                          <div>
                            <span style={{ fontSize: '1rem', fontWeight: 800, color: '#38bdf8', background: 'rgba(56, 189, 248, 0.1)', padding: '4px 10px', borderRadius: '6px' }}>{promo.code}</span>
                            <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginRight: '12px' }}>🎁 يمنح: {promo.daysBenefit} يوم مجاني</span>
                            <span style={{ fontSize: '0.82rem', color: promo.isActive ? 'var(--accent-green)' : '#f87171', marginRight: '12px', fontWeight: 'bold' }}>[{promo.isActive ? 'نشط' : 'معطل'}]</span>
                            <div style={{ marginTop: '8px' }}>
                              <span style={{ fontSize: '0.78rem', color: '#fff' }}>👥 المستخدمين المسجلين بهذا الكود: <strong>{usersUsingCode.length} مستخدم</strong></span>
                              {usersUsingCode.length > 0 && (
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '4px', background: 'rgba(0,0,0,0.1)', padding: '4px 8px', borderRadius: '4px' }}>
                                  {usersUsingCode.map(u => `${u.pharmacyName || 'صيدلية غير محددة'} (${u.ownerName || 'بدون اسم'})`).join(' ، ')}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <button 
                            type="button" 
                            onClick={() => handleTogglePromoStatus(promo)} 
                            style={{ background: promo.isActive ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)', color: promo.isActive ? '#f87171' : 'var(--accent-green)', border: '1px solid currentColor', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem' }}
                          >
                            {promo.isActive ? 'تعطيل الكود 🚫' : 'تنشيط الكود ⚡'}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Current Active Plan Status */}
      <div className="premium-card" style={{
        borderRight: daysRemaining === 'lifetime' ? '5px solid var(--accent-green)' : '5px solid var(--primary)',
        background: daysRemaining === 'lifetime' 
          ? 'radial-gradient(circle at 10% 20%, rgba(16, 185, 129, 0.08) 0%, rgba(13, 20, 36, 0.7) 90%)' 
          : 'radial-gradient(circle at 10% 20%, rgba(59, 130, 246, 0.08) 0%, rgba(13, 20, 36, 0.7) 90%)',
        boxShadow: daysRemaining === 'lifetime' ? '0 8px 32px rgba(16, 185, 129, 0.1)' : '0 8px 32px rgba(59, 130, 246, 0.1)'
      }}>
        <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#fff', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '1.5rem' }}>📊</span> حالة الاشتراك الحالية للنظام
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '24px' }}>
          <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '16px', borderRadius: '14px', border: '1px solid rgba(255, 255, 255, 0.05)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '2rem' }}>🏥</span>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600 }}>الصيدلية المسجلة:</span>
              <span style={{ color: '#fff', fontSize: '1.05rem', fontWeight: 'bold', marginTop: '2px' }}>{currentUserData?.pharmacyName || "غير محدد"}</span>
            </div>
          </div>
          <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '16px', borderRadius: '14px', border: '1px solid rgba(255, 255, 255, 0.05)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '2rem' }}>🆔</span>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600 }}>كود العميل (Short ID):</span>
              <span style={{ color: 'var(--primary)', fontSize: '1.15rem', fontWeight: '800', fontFamily: 'Outfit', marginTop: '2px' }}>{currentUserData?.shortId || "غير متوفر"}</span>
            </div>
          </div>
          <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '16px', borderRadius: '14px', border: '1px solid rgba(255, 255, 255, 0.05)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '2rem' }}>📱</span>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600 }}>حد الأجهزة المفعل:</span>
              <span style={{ color: 'var(--accent-green)', fontSize: '1.05rem', fontWeight: 'bold', marginTop: '2px' }}>{currentUserData?.deviceLimit || 2} أجهزة نشطة</span>
            </div>
          </div>
        </div>
        
        <div style={{ 
          background: 'rgba(255,255,255,0.01)', 
          borderTop: '1px solid rgba(255,255,255,0.04)', 
          paddingTop: '20px', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '8px' 
        }}>
          {daysRemaining === 'lifetime' ? (
            <div>
              <p style={{ fontSize: '1.15rem', color: '#fff', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <span style={{ color: 'var(--accent-green)' }}>✨</span> خطة الاشتراك الحالية: <strong style={{ color: 'var(--accent-green)' }}>خطة مدى الحياة (Lifetime Plan) 👑</strong>
              </p>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '6px', margin: 0 }}>
                حسابك نشط **مدى الحياة** للأبد مع جميع التحديثات والدعم الفني دون أي انقطاع أو مصاريف إضافية.
              </p>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '1.1rem', color: '#fff', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <span>🎯</span> خطة الاشتراك الحالية: <strong style={{ color: 'var(--primary)' }}>
                  {currentUserData?.plan === 'subscribe' ? 'الخطة النشطة (المدفوعة)' : 'الفترة التجريبية المجانية (7 أيام)'}
                </strong>
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginTop: '10px' }}>
                <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', margin: 0 }}>
                  الأيام المتبقية لانتهاء الخدمة: <strong style={{ color: daysRemaining <= 2 ? 'var(--accent-pink)' : 'var(--accent-green)', fontSize: '1.1rem', fontFamily: 'Outfit' }}>{daysRemaining} يوم</strong>
                </p>
                <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', margin: 0 }}>
                  تاريخ نهاية الاشتراك: <strong style={{ color: '#fff' }}>
                    {currentUserData?.expiryDate 
                      ? new Date(currentUserData.expiryDate).toLocaleDateString('ar-EG', { dateStyle: 'full' })
                      : 'غير محدد'
                    }
                  </strong>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 🛠️ Device Limit Selector Bar */}
      <div className="premium-card" style={{ 
        borderRight: '5px solid var(--primary)', 
        background: 'rgba(59, 130, 246, 0.03)', 
        display: 'flex', 
        flexWrap: 'wrap', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        gap: '20px',
        padding: '24px'
      }}>
        <div style={{ flex: 1, minWidth: '280px' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>⚙️</span> تحديد عدد الأجهزة المطلوبة للاشتراك
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: '1.5', margin: 0 }}>
            باقات دكتور مصر تشمل تشغيل **جهازين (2)** معاً بشكل أساسي مجاناً. يمكنك إضافة أجهزة إضافية حسب حجم صيدليتك بسعر رمزي (100 ج.م شهرياً / 1000 ج.م سنوياً / 2000 ج.م مدى الحياة لكل جهاز إضافي).
          </p>
        </div>

        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '16px', 
          background: 'rgba(8, 13, 27, 0.7)', 
          padding: '10px 20px', 
          borderRadius: '16px', 
          border: '1px solid var(--border)',
          boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.4)'
        }}>
          <button 
            type="button" 
            onClick={() => setDeviceCount(prev => Math.max(2, prev - 1))} 
            style={{ 
              width: '38px', 
              height: '38px', 
              borderRadius: '12px', 
              background: deviceCount > 2 ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255,255,255,0.03)', 
              color: deviceCount > 2 ? 'var(--primary)' : 'rgba(255,255,255,0.2)', 
              border: deviceCount > 2 ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid rgba(255,255,255,0.05)', 
              fontSize: '1.4rem', 
              fontWeight: 'bold', 
              cursor: deviceCount > 2 ? 'pointer' : 'default', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              outline: 'none',
              transition: 'all 0.2s ease'
            }}
          >
            -
          </button>
          <span style={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#fff', minWidth: '95px', textAlign: 'center', fontFamily: 'Cairo, sans-serif' }}>
            {deviceCount} أجهزة
          </span>
          <button 
            type="button" 
            onClick={() => setDeviceCount(prev => prev + 1)} 
            style={{ 
              width: '38px', 
              height: '38px', 
              borderRadius: '12px', 
              background: 'rgba(59, 130, 246, 0.2)', 
              color: 'var(--primary)', 
              border: '1px solid rgba(59, 130, 246, 0.4)', 
              fontSize: '1.3rem', 
              fontWeight: 'bold', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              outline: 'none',
              transition: 'all 0.2s ease'
            }}
          >
            +
          </button>
        </div>
      </div>

      {/* Beautiful Plans/Packages layout card */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '28px' }}>
        
        {/* Monthly Plan */}
        <div className="premium-card" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between', 
          border: '1px solid var(--border)',
          background: 'rgba(13, 20, 36, 0.6)',
          borderRadius: '20px',
          padding: '28px',
          transition: 'all 0.3s ease'
        }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '1.8rem' }}>📅</span>
              <span style={{ background: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary)', fontSize: '0.75rem', fontWeight: 700, padding: '4px 10px', borderRadius: '99px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>مرونة شهرية</span>
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', marginBottom: '6px' }}>الخطة الشهرية</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '20px' }}>مثالية لتجربة النظام وإدارة صيدليتك بحرية</p>
            
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '24px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '2.25rem', fontWeight: '900', color: 'var(--primary)', fontFamily: 'Outfit' }}>{monthlyPrice}</span>
              <span style={{ fontSize: '0.95rem', color: '#fff', fontWeight: 'bold' }}>ج.م</span>
              <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>/ شهرياً</span>
              <span style={{ fontSize: '1rem', color: '#ef4444', textDecoration: 'line-through', marginRight: '10px', fontWeight: 600 }}>بدلاً من {1000 + extraDevices * 400} ج.م</span>
            </div>

            <ul style={{ paddingRight: '18px', color: 'var(--text-secondary)', fontSize: '0.88rem', display: 'flex', flexDirection: 'column', gap: '10px', listStyleType: 'disc' }}>
              <li>صلاحية لعدد <strong style={{ color: '#fff' }}>{deviceCount} أجهزة</strong> متصلة معاً</li>
              <li>جميع ميزات الكاشير والمبيعات السريعة</li>
              <li>إدارة وطباعة الباركود والفواتير</li>
              <li>جرد المخازن وتنبيهات تواريخ الصلاحية</li>
              <li>تقارير الأرباح والمبيعات والشيفتات</li>
            </ul>
          </div>
          <button
            onClick={() => handleWhatsAppContact('الخطة الشهرية (Monthly)', monthlyPrice, deviceCount)}
            className="gradient-btn"
            style={{ 
              marginTop: '32px', 
              width: '100%', 
              fontWeight: 'bold', 
              background: '#25D366', 
              color: '#fff', 
              border: 'none', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '8px',
              padding: '14px',
              borderRadius: '12px',
              fontSize: '0.92rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(37, 211, 102, 0.2)'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 211, 102, 0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(37, 211, 102, 0.2)'; }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.062 5.248 5.308 0 11.786 0c3.137.001 6.086 1.223 8.301 3.442 2.215 2.218 3.434 5.169 3.431 8.307-.005 6.539-5.25 11.787-11.727 11.787-2.001-.001-3.968-.51-5.717-1.478L0 24zm6.09-3.987l.328.195c1.55.922 3.328 1.409 5.313 1.411 5.48.004 9.938-4.453 9.942-9.934.002-2.656-1.03-5.153-2.906-7.03C16.949 2.763 14.455 1.73 11.79 1.729c-5.485 0-9.943 4.456-9.947 9.94 0 2.072.54 4.094 1.562 5.86l.215.372-1.028 3.755 3.865-1.014zM17.5 14.867c-.324-.162-1.917-.946-2.213-1.054-.297-.109-.513-.162-.73.162-.216.324-.838 1.054-1.027 1.27-.189.216-.378.243-.702.081-.324-.162-1.37-.505-2.61-1.611-.964-.86-1.614-1.922-1.802-2.246-.189-.324-.02-.5-.182-.661-.146-.146-.324-.378-.486-.568-.162-.189-.216-.324-.324-.54-.108-.216-.054-.405-.027-.567.027-.162.216-.513.324-.702.108-.189.162-.324.243-.54.08-.217.04-.405.013-.567-.027-.162-.216-.52-.297-.716-.081-.19-.162-.162-.224-.165l-.189-.003c-.216 0-.568.081-.865.405-.297.324-1.135 1.108-1.135 2.702 0 1.594 1.162 3.135 1.324 3.35.162.216 2.284 3.488 5.535 4.894.773.334 1.377.534 1.847.683.777.247 1.484.212 2.043.129.622-.093 1.917-.784 2.187-1.54.27-.756.27-1.405.189-1.54-.08-.135-.297-.216-.621-.378z"/></svg>
            <span>تفعيل الباقة عبر الواتساب</span>
          </button>
        </div>

        {/* Yearly Plan */}
        <div className="premium-card" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between', 
          border: '1px solid rgba(59, 130, 246, 0.6)',
          background: 'rgba(13, 20, 36, 0.85)',
          borderRadius: '20px',
          padding: '28px',
          transform: 'scale(1.03)',
          boxShadow: '0 12px 40px rgba(59, 130, 246, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.25)',
          position: 'relative',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ position: 'absolute', top: '-14px', right: '20px', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: '#fff', fontSize: '0.8rem', fontWeight: 800, padding: '6px 14px', borderRadius: '20px', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }}>
            🔥 الأكثر توفيراً
          </div>
          
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', marginTop: '4px' }}>
              <span style={{ fontSize: '1.8rem' }}>🚀</span>
              <span style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', fontSize: '0.75rem', fontWeight: 700, padding: '4px 10px', borderRadius: '99px' }}>الخيار الذكي</span>
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', marginBottom: '6px' }}>الخطة السنوية</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '20px' }}>الخيار الأفضل للصيدليات والمشاريع المستقرة</p>
            
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '24px' }}>
              <span style={{ fontSize: '2.25rem', fontWeight: '900', color: 'var(--primary)', fontFamily: 'Outfit' }}>{yearlyPrice}</span>
              <span style={{ fontSize: '0.95rem', color: '#fff', fontWeight: 'bold' }}>ج.م</span>
              <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>/ سنوياً</span>
            </div>

            <ul style={{ paddingRight: '18px', color: 'var(--text-secondary)', fontSize: '0.88rem', display: 'flex', flexDirection: 'column', gap: '10px', listStyleType: 'disc' }}>
              <li>صلاحية لعدد <strong style={{ color: '#fff' }}>{deviceCount} أجهزة</strong> متصلة معاً</li>
              <li>كل ميزات النظام المتقدمة بلا حدود</li>
              <li>خصم هائل (يعادل الحصول على <strong style={{ color: '#3b82f6' }}>شهرين مجاناً</strong>)</li>
              <li>أولوية قصوى للدعم الفني والتحديثات</li>
              <li>تأمين كامل لقواعد البيانات والحفظ التلقائي</li>
            </ul>
          </div>
          <button
            onClick={() => handleWhatsAppContact('الخطة السنوية (Yearly)', yearlyPrice, deviceCount)}
            className="gradient-btn"
            style={{ 
              marginTop: '32px', 
              width: '100%', 
              fontWeight: 'bold', 
              background: '#25D366', 
              color: '#fff', 
              border: 'none', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '8px',
              padding: '14px',
              borderRadius: '12px',
              fontSize: '0.92rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(37, 211, 102, 0.2)'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 211, 102, 0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(37, 211, 102, 0.2)'; }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.062 5.248 5.308 0 11.786 0c3.137.001 6.086 1.223 8.301 3.442 2.215 2.218 3.434 5.169 3.431 8.307-.005 6.539-5.25 11.787-11.727 11.787-2.001-.001-3.968-.51-5.717-1.478L0 24zm6.09-3.987l.328.195c1.55.922 3.328 1.409 5.313 1.411 5.48.004 9.938-4.453 9.942-9.934.002-2.656-1.03-5.153-2.906-7.03C16.949 2.763 14.455 1.73 11.79 1.729c-5.485 0-9.943 4.456-9.947 9.94 0 2.072.54 4.094 1.562 5.86l.215.372-1.028 3.755 3.865-1.014zM17.5 14.867c-.324-.162-1.917-.946-2.213-1.054-.297-.109-.513-.162-.73.162-.216.324-.838 1.054-1.027 1.27-.189.216-.378.243-.702.081-.324-.162-1.37-.505-2.61-1.611-.964-.86-1.614-1.922-1.802-2.246-.189-.324-.02-.5-.182-.661-.146-.146-.324-.378-.486-.568-.162-.189-.216-.324-.324-.54-.108-.216-.054-.405-.027-.567.027-.162.216-.513.324-.702.108-.189.162-.324.243-.54.08-.217.04-.405.013-.567-.027-.162-.216-.52-.297-.716-.081-.19-.162-.162-.224-.165l-.189-.003c-.216 0-.568.081-.865.405-.297.324-1.135 1.108-1.135 2.702 0 1.594 1.162 3.135 1.324 3.35.162.216 2.284 3.488 5.535 4.894.773.334 1.377.534 1.847.683.777.247 1.484.212 2.043.129.622-.093 1.917-.784 2.187-1.54.27-.756.27-1.405.189-1.54-.08-.135-.297-.216-.621-.378z"/></svg>
            <span>تجديد الاشتراك بالواتساب</span>
          </button>
        </div>

        {/* Lifetime Plan */}
        <div className="premium-card" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between', 
          border: '1px solid var(--accent-green)',
          background: 'rgba(13, 20, 36, 0.6)',
          borderRadius: '20px',
          padding: '28px',
          transition: 'all 0.3s ease'
        }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '1.8rem' }}>👑</span>
              <span style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--accent-green)', fontSize: '0.75rem', fontWeight: 700, padding: '4px 10px', borderRadius: '99px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>الراحة الدائمة</span>
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', marginBottom: '6px' }}>مدى الحياة (Lifetime)</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '20px' }}>اشترِ راحة بالك للأبد وبدون أي تجديدات أو قلق</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '24px' }}>
              <div style={{ textDecoration: 'line-through', color: '#ef4444', fontSize: '1.1rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>{(120000 + extraDevices * 4000).toLocaleString()}</span>
                <span style={{ fontSize: '0.8rem' }}>ج.م</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                <span style={{ fontSize: '2.25rem', fontWeight: '900', color: 'var(--accent-green)', fontFamily: 'Outfit' }}>{lifetimePrice.toLocaleString()}</span>
                <span style={{ fontSize: '0.95rem', color: '#fff', fontWeight: 'bold' }}>ج.م</span>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>/ للأبد</span>
              </div>
            </div>

            <ul style={{ paddingRight: '18px', color: 'var(--text-secondary)', fontSize: '0.88rem', display: 'flex', flexDirection: 'column', gap: '10px', listStyleType: 'disc' }}>
              <li>صلاحية <strong style={{ color: '#fff' }}>دائمة وبلا نهاية</strong> لـ {deviceCount} أجهزة</li>
              <li>لا توجد أي رسوم تجديد شهرية أو سنوية نهائياً</li>
              <li>تشمل جميع تحديثات وميزات دكتور مصر القادمة</li>
              <li>خط دعم مخصص وإدارة أسرع لقواعد البيانات</li>
              <li>تأمين سيرفرات خاص وأعلى درجات الاستقرار</li>
            </ul>
          </div>
          <button
            onClick={() => handleWhatsAppContact('خطة مدى الحياة (Lifetime)', lifetimePrice, deviceCount)}
            className="gradient-btn"
            style={{ 
              marginTop: '32px', 
              width: '100%', 
              fontWeight: 'bold', 
              background: '#25D366', 
              color: '#fff', 
              border: 'none', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '8px',
              padding: '14px',
              borderRadius: '12px',
              fontSize: '0.92rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(37, 211, 102, 0.2)'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 211, 102, 0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(37, 211, 102, 0.2)'; }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.062 5.248 5.308 0 11.786 0c3.137.001 6.086 1.223 8.301 3.442 2.215 2.218 3.434 5.169 3.431 8.307-.005 6.539-5.25 11.787-11.727 11.787-2.001-.001-3.968-.51-5.717-1.478L0 24zm6.09-3.987l.328.195c1.55.922 3.328 1.409 5.313 1.411 5.48.004 9.938-4.453 9.942-9.934.002-2.656-1.03-5.153-2.906-7.03C16.949 2.763 14.455 1.73 11.79 1.729c-5.485 0-9.943 4.456-9.947 9.94 0 2.072.54 4.094 1.562 5.86l.215.372-1.028 3.755 3.865-1.014zM17.5 14.867c-.324-.162-1.917-.946-2.213-1.054-.297-.109-.513-.162-.73.162-.216.324-.838 1.054-1.027 1.27-.189.216-.378.243-.702.081-.324-.162-1.37-.505-2.61-1.611-.964-.86-1.614-1.922-1.802-2.246-.189-.324-.02-.5-.182-.661-.146-.146-.324-.378-.486-.568-.162-.189-.216-.324-.324-.54-.108-.216-.054-.405-.027-.567.027-.162.216-.513.324-.702.108-.189.162-.324.243-.54.08-.217.04-.405.013-.567-.027-.162-.216-.52-.297-.716-.081-.19-.162-.162-.224-.165l-.189-.003c-.216 0-.568.081-.865.405-.297.324-1.135 1.108-1.135 2.702 0 1.594 1.162 3.135 1.324 3.35.162.216 2.284 3.488 5.535 4.894.773.334 1.377.534 1.847.683.777.247 1.484.212 2.043.129.622-.093 1.917-.784 2.187-1.54.27-.756.27-1.405.189-1.54-.08-.135-.297-.216-.621-.378z"/></svg>
            <span>شراء الباقة مدى الحياة</span>
          </button>
        </div>
      </div>

      {/* Subscription/Payment methods section */}
      <div id="payment-methods" className="premium-card fade-in-content" style={{ 
        borderRight: '5px solid var(--accent-pink)',
        background: 'radial-gradient(circle at top right, rgba(244, 63, 94, 0.04), rgba(13, 20, 36, 0.8))'
      }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>💳</span> طرق دفع وتفعيل الاشتراكات الرسمية
        </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: '1.6', marginBottom: '20px' }}>
          يرجى تحويل قيمة الباقة المناسبة لتجديد وتفعيل الاشتراك الفوري، ثم اضغط على زر التفعيل الأخضر الموضح أسفل الباقة المناسبة ليتم توجيهك فوراً لإرسال إيصال التحويل وصورة الشاشة عبر الواتساب للإدارة.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
          {/* Vodafone Cash Card */}
          <div style={{ 
            background: 'rgba(15, 23, 42, 0.65)', 
            border: '1px solid rgba(244, 63, 94, 0.15)', 
            borderRadius: '16px', 
            padding: '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f43f5e' }}></div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#f87171', margin: 0 }}>فودافون كاش (Vodafone Cash)</h4>
            </div>
            
            {/* Voda Copy Box */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'rgba(244, 63, 94, 0.08)',
              border: '1px dashed rgba(244, 63, 94, 0.4)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginTop: '12px',
              transition: 'all 0.3s ease',
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'right' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>رقم محفظة فودافون كاش:</span>
                <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#fff', letterSpacing: '0.5px', fontFamily: 'Outfit' }}>01004757940</span>
              </div>
              <button 
                type="button"
                onClick={() => handleCopy('01004757940', 'voda')}
                style={{
                  background: copiedVoda ? 'rgba(16, 185, 129, 0.2)' : 'rgba(244, 63, 94, 0.15)',
                  border: copiedVoda ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(244, 63, 94, 0.3)',
                  borderRadius: '10px',
                  padding: '8px 12px',
                  color: copiedVoda ? '#34d399' : '#f87171',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
              >
                {copiedVoda ? (
                  <>
                    <span>تم النسخ!</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </>
                ) : (
                  <>
                    <span>نسخ</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                  </>
                )}
              </button>
            </div>
            
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '12px 0 0 0', textAlign: 'right', lineHeight: '1.4' }}>
              صاحب المحفظة: <strong style={{ color: '#fff' }}>باسل صلاح (Basel Salah)</strong>
            </p>
          </div>

          {/* InstaPay Card */}
          <div style={{ 
            background: 'rgba(15, 23, 42, 0.65)', 
            border: '1px solid rgba(56, 189, 248, 0.15)', 
            borderRadius: '16px', 
            padding: '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#38bdf8' }}></div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#38bdf8', margin: 0 }}>إنستا باي (InstaPay)</h4>
            </div>

            {/* InstaPay Copy Box */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'rgba(56, 189, 248, 0.08)',
              border: '1px dashed rgba(56, 189, 248, 0.4)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginTop: '12px',
              transition: 'all 0.3s ease',
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'right' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>عنوان الدفع إنستا باي:</span>
                <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#fff', letterSpacing: '0.2px', fontFamily: 'Outfit' }}>baselbes0@instapay</span>
              </div>
              <button 
                type="button"
                onClick={() => handleCopy('baselbes0@instapay', 'insta')}
                style={{
                  background: copiedInsta ? 'rgba(16, 185, 129, 0.2)' : 'rgba(56, 189, 248, 0.15)',
                  border: copiedInsta ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(56, 189, 248, 0.3)',
                  borderRadius: '10px',
                  padding: '8px 12px',
                  color: copiedInsta ? '#34d399' : '#38bdf8',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
              >
                {copiedInsta ? (
                  <>
                    <span>تم النسخ!</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </>
                ) : (
                  <>
                    <span>نسخ</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                  </>
                )}
              </button>
            </div>
            
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '12px 0 0 0', textAlign: 'right', lineHeight: '1.4' }}>
              صحد الحساب: <strong style={{ color: '#fff' }}>باسل صلاح (Basel Salah)</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ETASettingsView({ user }) {
  // ETA Settings States
  const defaultEtaSettings = JSON.parse(localStorage.getItem('drmasr_eta_settings')) || {
    enabled: false,
    taxId: '',
    branchId: '',
    activityCode: '',
    clientId: '',
    clientSecret: '',
    envMode: 'simulator'
  };
  const [etaSettings, setEtaSettings] = useState(defaultEtaSettings);
  const [etaStatusMsg, setEtaStatusMsg] = useState('');
  const handleSaveEtaSettings = () => {
    localStorage.setItem('drmasr_eta_settings', JSON.stringify(etaSettings));
    setEtaStatusMsg('تم حفظ إعدادات منظومة الضرائب بنجاح ✅');
    setTimeout(() => setEtaStatusMsg(''), 3000);
  };
  const handleAutoFillEta = () => {
    const mockData = {
      enabled: true,
      taxId: '313-818-494',
      branchId: '0',
      activityCode: '4649',
      clientId: 'mock-client-id-xyz',
      clientSecret: 'mock-client-secret-abc',
      envMode: 'simulator'
    };
    setEtaSettings(mockData);
    localStorage.setItem('drmasr_eta_settings', JSON.stringify(mockData));
    setEtaStatusMsg('تم تعبئة البيانات التجريبية للمحاكي وحفظها ✅');
    setTimeout(() => setEtaStatusMsg(''), 3000);
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // ETA Credentials & Settings
  const [isProdEnv, setIsProdEnv] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [tokenPin, setTokenPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  
  // Business & Hardware Settings
  const [activityCode, setActivityCode] = useState('');
  const [posSerial, setPosSerial] = useState('');
  const [taxBranchAddress, setTaxBranchAddress] = useState('');
  const [isAddressFocused, setIsAddressFocused] = useState(false);

  // Tax Policy
  const [autoSubmit, setAutoSubmit] = useState(false);
  const [defaultVat, setDefaultVat] = useState(14);
  const [defaultTableTax, setDefaultTableTax] = useState(0);

  // Track window width for premium responsiveness
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 1024;

  // Fetch current ETA settings from Firestore on mount
  useEffect(() => {
    const fetchETASettings = async () => {
      if (!user?.uid) return;
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          const eta = data.etaSettings || {};
          
          setIsProdEnv(eta.isProdEnv || false);
          setClientId(eta.clientId || '');
          setClientSecret(eta.clientSecret || '');
          setTokenPin(eta.tokenPin || '');
          setActivityCode(eta.activityCode || '');
          setPosSerial(eta.posSerial || '');
          setTaxBranchAddress(eta.taxBranchAddress || '');
          setAutoSubmit(eta.autoSubmit || false);
          setDefaultVat(eta.defaultVat !== undefined ? eta.defaultVat : 14);
          setDefaultTableTax(eta.defaultTableTax !== undefined ? eta.defaultTableTax : 0);
        }
      } catch (error) {
        console.error("Error loading ETA settings:", error);
        if (window.triggerDrMasrToast) {
          window.triggerDrMasrToast('⚠️ فشل في تحميل إعدادات الضرائب!');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchETASettings();
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user?.uid) return;
    setSaving(true);
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        etaSettings: {
          isProdEnv,
          clientId,
          clientSecret,
          tokenPin,
          activityCode,
          posSerial,
          taxBranchAddress,
          autoSubmit,
          defaultVat: Number(defaultVat),
          defaultTableTax: Number(defaultTableTax),
          updatedAt: new Date().toISOString()
        }
      });
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('✅ تم حفظ إعدادات الضرائب بنجاح! 💾');
      }
    } catch (error) {
      console.error("Error saving ETA settings:", error);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('❌ فشل حفظ إعدادات الضرائب في السيرفر.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = () => {
    if (!clientId || !clientSecret) {
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('⚠️ يرجى إدخال معرف العميل ومفتاح السر أولاً لإجراء الفحص!');
      }
      return;
    }
    setTesting(true);
    setTestResult(null);
    setTimeout(() => {
      setTesting(false);
      setTestResult({
        success: true,
        message: isProdEnv 
          ? 'تم الاتصال بنجاح مع منظومة الفواتير الفعلية (Production API)! 🚀'
          : 'تم الاتصال بنجاح مع منظومة الفواتير التجريبية (Sandbox API)! 🧪',
        details: {
          status: '200 OK',
          environment: isProdEnv ? 'Production' : 'Pre-Production (Sandbox)',
          token_type: 'Bearer',
          expires_in: 3600,
          ping_ms: 124
        }
      });
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('✅ نجح فحص الاتصال الافتراضي بالمنظومة!');
      }
    }, 2000);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '20px' }}>
        <div style={{ 
          width: '50px', 
          height: '50px', 
          borderRadius: '50%', 
          border: '3px solid rgba(59, 130, 246, 0.1)', 
          borderTopColor: 'var(--primary)', 
          animation: 'spin 1s linear infinite' 
        }}></div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', fontWeight: 600, fontFamily: 'Cairo, sans-serif' }}>جاري تحميل إعدادات الضرائب...</p>
      </div>
    );
  }

  return (
    <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px', direction: 'rtl', textAlign: 'right', fontFamily: 'Cairo, sans-serif' }}>
      
      {/* Dynamic Keyframe style helper */}
      <style>{`
        @keyframes pulseGlowGreen {
          0% { box-shadow: 0 0 4px rgba(16, 185, 129, 0.2); }
          50% { box-shadow: 0 0 16px rgba(16, 185, 129, 0.6); }
          100% { box-shadow: 0 0 4px rgba(16, 185, 129, 0.2); }
        }
        @keyframes pulseGlowRed {
          0% { box-shadow: 0 0 4px rgba(244, 63, 94, 0.2); }
          50% { box-shadow: 0 0 16px rgba(244, 63, 94, 0.6); }
          100% { box-shadow: 0 0 4px rgba(244, 63, 94, 0.2); }
        }
        .pulse-badge-green {
          animation: pulseGlowGreen 2s infinite ease-in-out;
        }
        .pulse-badge-red {
          animation: pulseGlowRed 2s infinite ease-in-out;
        }
      `}</style>

      {/* Header Banner */}
      <div className="premium-card" style={{
        background: 'linear-gradient(135deg, rgba(8, 13, 27, 0.85) 0%, rgba(13, 20, 36, 0.5) 100%)',
        borderRight: '5px solid var(--primary)',
        padding: '24px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2rem',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1)'
          }}>
            🏢
          </div>
          <div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
              إعدادات الربط الضريبي <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.15rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>(ETA Integration)</span>
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: 0 }}>تكوين وتفعيل الربط المباشر مع مصلحة الضرائب المصرية لمنظومة الفاتورة والإيصال الإلكتروني</p>
          </div>
        </div>
        
        {/* Pulsing Status Widget */}
        <div className={clientId && clientSecret ? 'pulse-badge-green' : 'pulse-badge-red'} style={{
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px', 
          background: clientId && clientSecret ? 'rgba(16, 185, 129, 0.08)' : 'rgba(244, 63, 94, 0.08)', 
          padding: '8px 18px', 
          borderRadius: '14px', 
          border: clientId && clientSecret ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid rgba(244, 63, 94, 0.25)',
          transition: 'all 0.3s ease'
        }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>حالة الاتصال:</span>
          <span style={{ 
            fontSize: '0.9rem', 
            fontWeight: 'bold', 
            color: clientId && clientSecret ? 'var(--accent-green)' : 'var(--accent-pink)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ 
              width: '10px', 
              height: '10px', 
              borderRadius: '50%', 
              background: clientId && clientSecret ? 'var(--accent-green)' : 'var(--accent-pink)',
              display: 'inline-block'
            }}></span>
            {clientId && clientSecret ? 'مفعل وجاهز' : 'غير متصل'}
          </span>
        </div>
      </div>

      {/* Environment Cards Selector */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: '20px',
        marginTop: '4px'
      }}>
        {/* Sandbox Env Card */}
        <div 
          onClick={() => {
            setIsProdEnv(false);
            setTestResult(null);
          }}
          style={{
            background: !isProdEnv 
              ? 'radial-gradient(circle at 10% 20%, rgba(59, 130, 246, 0.08) 0%, rgba(13, 20, 36, 0.85) 90%)' 
              : 'rgba(13, 20, 36, 0.4)',
            border: !isProdEnv 
              ? '1.5px solid var(--primary)' 
              : '1px solid rgba(255,255,255,0.06)',
            borderRadius: '16px',
            padding: '20px 24px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            opacity: !isProdEnv ? 1 : 0.6,
            boxShadow: !isProdEnv ? '0 8px 24px rgba(59, 130, 246, 0.15), inset 0 0 12px rgba(59, 130, 246, 0.05)' : 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}
          onMouseEnter={e => {
            if (isProdEnv) {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
            }
          }}
          onMouseLeave={e => {
            if (isProdEnv) {
              e.currentTarget.style.opacity = '0.6';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
            }
          }}
        >
          {/* Floating Badge */}
          <span style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            background: 'rgba(59, 130, 246, 0.12)',
            color: '#60a5fa',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            padding: '4px 10px',
            borderRadius: '8px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            fontFamily: 'Outfit, sans-serif'
          }}>
            Sandbox
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              background: 'rgba(59, 130, 246, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.4rem'
            }}>
              🧪
            </div>
            <h4 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff', margin: 0 }}>
              نظام التدريب والتجربة
            </h4>
          </div>
          
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: '1.6', margin: 0 }}>
            تستخدم لاختبار الربط والتدريب على إصدار الفواتير بدون أي التزامات ضريبية حقيقية أو تأثير قانوني.
          </p>
        </div>

        {/* Production Env Card */}
        <div 
          onClick={() => {
            setIsProdEnv(true);
            setTestResult(null);
          }}
          style={{
            background: isProdEnv 
              ? 'radial-gradient(circle at 10% 20%, rgba(16, 185, 129, 0.08) 0%, rgba(13, 20, 36, 0.85) 90%)' 
              : 'rgba(13, 20, 36, 0.4)',
            border: isProdEnv 
              ? '1.5px solid var(--accent-green)' 
              : '1px solid rgba(255,255,255,0.06)',
            borderRadius: '16px',
            padding: '20px 24px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            opacity: isProdEnv ? 1 : 0.6,
            boxShadow: isProdEnv ? '0 8px 24px rgba(16, 185, 129, 0.15), inset 0 0 12px rgba(16, 185, 129, 0.05)' : 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}
          onMouseEnter={e => {
            if (!isProdEnv) {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
            }
          }}
          onMouseLeave={e => {
            if (!isProdEnv) {
              e.currentTarget.style.opacity = '0.6';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
            }
          }}
        >
          {/* Floating Badge */}
          <span style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            background: 'rgba(16, 185, 129, 0.12)',
            color: 'var(--accent-green)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            padding: '4px 10px',
            borderRadius: '8px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            fontFamily: 'Outfit, sans-serif'
          }}>
            Production
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              background: 'rgba(16, 185, 129, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.4rem'
            }}>
              🚀
            </div>
            <h4 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff', margin: 0 }}>
              منظومة الضرائب الرسمية
            </h4>
          </div>
          
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: '1.6', margin: 0 }}>
            تستخدم لإرسال الفواتير والإيصالات الحقيقية لمصلحة الضرائب فور حفظها، ولها أثر قانوني مباشر وضريبي.
          </p>
        </div>
      </div>

      {/* Main Settings Configuration Form in a perfectly symmetrical 3-column grid */}
      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
          gap: '24px',
          alignItems: 'stretch'
        }}>
          
          {/* Card 1: Credentials */}
          <div className="premium-card" style={{ 
            background: 'rgba(13, 20, 36, 0.65)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            padding: '24px',
            height: '100%'
          }}>
            <div className="premium-card-body">
              <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '12px', marginBottom: '4px' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: 'var(--primary)' }}>🔑</span> بيانات شهادات الاتصال
                </h3>
                <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', fontWeight: 500 }}>API Configuration & Credentials</span>
              </div>
              
              <div className="premium-field-group">
                <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>معرف العميل:</span>
                  <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>Client ID</span>
                </label>
                <input 
                  type="text" 
                  value={clientId} 
                  onChange={e => setClientId(e.target.value)} 
                  placeholder="مثال: aa8fbb8b-6f81-482d-82d2-5a2e..." 
                  className="premium-input"
                  style={{ direction: 'ltr', textAlign: 'left', fontFamily: 'Outfit, sans-serif', fontSize: '0.9rem', width: '100%', background: 'rgba(15, 23, 42, 0.45)', border: '1px solid rgba(255,255,255,0.08)' }}
                />
              </div>

              <div className="premium-field-group">
                <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>مفتاح السر للعميل:</span>
                  <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>Client Secret</span>
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input 
                    type={showSecret ? 'text' : 'password'} 
                    value={clientSecret} 
                    onChange={e => setClientSecret(e.target.value)} 
                    placeholder="••••••••••••••••••••••••••••••••" 
                    className="premium-input"
                    style={{ 
                      direction: 'ltr', 
                      textAlign: 'left', 
                      fontFamily: 'Outfit, sans-serif', 
                      paddingLeft: '40px', 
                      fontSize: '0.9rem',
                      width: '100%',
                      background: 'rgba(15, 23, 42, 0.45)',
                      border: '1px solid rgba(255,255,255,0.08)'
                    }}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowSecret(!showSecret)}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontSize: '1.1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      outline: 'none',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                  >
                    {showSecret ? '👁' : '👁‍🗨'}
                  </button>
                </div>
              </div>

              <div className="premium-field-group">
                <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>الرقم السري للتوكن / الفلاشة:</span>
                  <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>Token PIN</span>
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input 
                    type={showPin ? 'text' : 'password'} 
                    value={tokenPin} 
                    onChange={e => setTokenPin(e.target.value)} 
                    placeholder="رمز مرور فلاشة التوقيع الإلكتروني" 
                    className="premium-input"
                    style={{ 
                      direction: 'ltr', 
                      textAlign: 'left', 
                      paddingLeft: '40px', 
                      fontSize: '0.9rem',
                      width: '100%',
                      background: 'rgba(15, 23, 42, 0.45)',
                      border: '1px solid rgba(255,255,255,0.08)'
                    }}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPin(!showPin)}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontSize: '1.1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      outline: 'none',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                  >
                    {showPin ? '👁' : '👁‍🗨'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Branch & POS Settings */}
          <div className="premium-card" style={{ 
            background: 'rgba(13, 20, 36, 0.65)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            padding: '24px',
            height: '100%'
          }}>
            <div className="premium-card-body">
              <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '12px', marginBottom: '4px' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: 'var(--accent-gold)' }}>⚙️</span> إعدادات الفرع ونقاط البيع
                </h3>
                <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', fontWeight: 500 }}>POS & Branch Setup</span>
              </div>

              <div className="premium-field-group">
                <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>كود النشاط الضريبي:</span>
                  <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>Activity Code</span>
                </label>
                <input 
                  type="text" 
                  value={activityCode} 
                  onChange={e => setActivityCode(e.target.value)} 
                  placeholder="مثال: 4772" 
                  className="premium-input"
                  style={{ fontFamily: 'Cairo, Outfit, sans-serif', fontSize: '0.9rem', width: '100%', background: 'rgba(15, 23, 42, 0.45)', border: '1px solid rgba(255,255,255,0.08)' }}
                />
              </div>

              <div className="premium-field-group">
                <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>الرقم التسلسلي لـ POS:</span>
                  <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>POS Serial</span>
                </label>
                <input 
                  type="text" 
                  value={posSerial} 
                  onChange={e => setPosSerial(e.target.value)} 
                  placeholder="الرقم التعريفي المسجل بالفرع" 
                  className="premium-input"
                  style={{ fontFamily: 'Cairo, Outfit, sans-serif', fontSize: '0.9rem', width: '100%', background: 'rgba(15, 23, 42, 0.45)', border: '1px solid rgba(255,255,255,0.08)' }}
                />
              </div>

              <div className="premium-field-group">
                <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>عنوان فرع الصيدلية (المسجل ضريبياً):</span>
                  <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>Branch Address</span>
                </label>
                <textarea 
                  value={taxBranchAddress} 
                  onChange={e => setTaxBranchAddress(e.target.value)} 
                  placeholder="مثال: 12 شارع قصر العيني، القاهرة" 
                  className="premium-input"
                  rows="3"
                  style={{ 
                    resize: 'none', 
                    padding: '10px 12px', 
                    fontSize: '0.88rem', 
                    lineHeight: '1.5',
                    height: isAddressFocused ? '80px' : '42px',
                    fontFamily: 'Cairo, sans-serif',
                    width: '100%',
                    background: 'rgba(15, 23, 42, 0.45)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    transition: 'height 0.25s ease-in-out'
                  }}
                  onFocus={() => setIsAddressFocused(true)}
                  onBlur={() => setIsAddressFocused(false)}
                />
              </div>
            </div>
          </div>

          {/* Card 3: Tax Policy & Rules */}
          <div className="premium-card" style={{ 
            background: 'rgba(13, 20, 36, 0.65)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            padding: '24px',
            height: '100%'
          }}>
            <div className="premium-card-body">
              <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '12px', marginBottom: '4px' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: 'var(--accent-green)' }}>📊</span> سياسة احتساب الضرائب والإرسال
                </h3>
                <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', fontWeight: 500 }}>Tax Calculations & Policy</span>
              </div>

              {/* Toggle switch with glass box */}
              <div className="premium-field-group" style={{ gap: '6px', justifyContent: 'center', minHeight: '83px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span>تقديم الفواتير تلقائياً فور الحفظ:</span>
                    <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.72rem', fontWeight: 500 }}>Auto-Submit Invoices</span>
                  </label>
                  <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '48px', height: '24px', flexShrink: 0 }}>
                    <input 
                      type="checkbox" 
                      checked={autoSubmit} 
                      onChange={e => setAutoSubmit(e.target.checked)} 
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span className="slider round" style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0, left: 0, right: 0, bottom: 0,
                      backgroundColor: autoSubmit ? 'var(--accent-green)' : 'rgba(255,255,255,0.08)',
                      transition: '.4s',
                      borderRadius: '34px',
                      boxShadow: autoSubmit ? '0 0 10px rgba(16, 185, 129, 0.3)' : 'none',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '""',
                        height: '16px', width: '16px',
                        left: autoSubmit ? '4px' : '26px',
                        bottom: '3px',
                        backgroundColor: '#fff',
                        transition: '.4s',
                        borderRadius: '50%'
                      }}></span>
                    </span>
                  </label>
                </div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.72rem', margin: 0, lineHeight: '1.4' }}>
                  توقيع وإرسال الفاتورة إلكترونياً لـ API الضرائب فور الحفظ المباشر.
                </p>
              </div>

              {/* VAT Default Slider */}
              <div className="premium-field-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between', flexGrow: 1 }}>
                    <span>ضريبة القيمة المضافة الافتراضية:</span>
                    <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', marginLeft: '12px' }}>VAT Rate %</span>
                  </label>
                  <span style={{ 
                    color: 'var(--primary)', 
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontWeight: 'bold', 
                    fontSize: '0.85rem', 
                    fontFamily: 'Outfit',
                    flexShrink: 0
                  }}>{defaultVat}%</span>
                </div>
                <input 
                  type="range" 
                  min="0" 
                  max="25" 
                  value={defaultVat} 
                  onChange={e => setDefaultVat(Number(e.target.value))} 
                  style={{ width: '100%', accentColor: 'var(--primary)', background: 'rgba(255,255,255,0.08)', height: '5px', borderRadius: '3px', outline: 'none', cursor: 'pointer' }}
                />
              </div>

              {/* Table Tax Default Slider */}
              <div className="premium-field-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between', flexGrow: 1 }}>
                    <span>ضريبة الجدول / المهن الطبية:</span>
                    <span dir="ltr" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', marginLeft: '12px' }}>Table Tax %</span>
                  </label>
                  <span style={{ 
                    color: 'var(--accent-pink)', 
                    background: 'rgba(244, 63, 94, 0.1)',
                    border: '1px solid rgba(244, 63, 94, 0.2)',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontWeight: 'bold', 
                    fontSize: '0.85rem', 
                    fontFamily: 'Outfit',
                    flexShrink: 0
                  }}>{defaultTableTax}%</span>
                </div>
                <input 
                  type="range" 
                  min="0" 
                  max="10" 
                  step="0.5"
                  value={defaultTableTax} 
                  onChange={e => setDefaultTableTax(Number(e.target.value))} 
                  style={{ width: '100%', accentColor: 'var(--accent-pink)', background: 'rgba(255,255,255,0.08)', height: '5px', borderRadius: '3px', outline: 'none', cursor: 'pointer' }}
                />
              </div>
            </div>
          </div>

        </div>

        {/* Full-width glowing submit button under the 3 columns */}
        <button 
          type="submit"
          disabled={saving || testing}
          className="premium-btn-primary"
          style={{
            padding: '16px 36px',
            borderRadius: '14px',
            fontSize: '1.02rem',
            fontWeight: 800,
            background: 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)',
            color: '#fff',
            border: 'none',
            cursor: (saving || testing) ? 'not-allowed' : 'pointer',
            boxShadow: '0 6px 20px rgba(59, 130, 246, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            transition: 'all 0.3s ease',
            outline: 'none',
            transform: 'scale(1)',
            width: '100%'
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.005)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          {saving ? '⚡ جاري حفظ البيانات وتأكيد التغييرات...' : '💾 حفظ وتثبيت إعدادات الربط الضريبي'}
        </button>
      </form>

      {/* Balanced 2-column Bottom Section: Verification & Developer Guides */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1.1fr',
        gap: '24px',
        alignItems: 'start',
        marginTop: '8px'
      }}>
        
        {/* Right Column: Connection Center & Verification Tools */}
        <div className="premium-card" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '22px', 
          background: 'rgba(13, 20, 36, 0.6)',
          border: '1px solid rgba(255,255,255,0.06)',
          height: '100%'
        }}>
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ color: 'var(--primary)' }}>⚡</span> مركز الاتصال والتحقق الفوري
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>تحقق فوري من سلامة خوادم التوثيق والربط الإلكتروني</p>
          </div>

          {/* Quick stats on selected environment */}
          <div style={{ 
            background: 'rgba(255,255,255,0.01)', 
            padding: '14px 18px', 
            borderRadius: '12px', 
            border: '1px solid rgba(255,255,255,0.03)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                خادم التوثيق <span dir="ltr" style={{ fontFamily: 'Outfit', color: 'var(--text-secondary)', fontSize: '0.78rem' }}>(Identity):</span>
              </span>
              <span style={{ color: '#fff', direction: 'ltr', fontFamily: 'Outfit' }}>
                {isProdEnv ? 'id.eta.gov.eg' : 'id.preprod.eta.gov.eg'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                رابط المبيعات <span dir="ltr" style={{ fontFamily: 'Outfit', color: 'var(--text-secondary)', fontSize: '0.78rem' }}>(API Portal):</span>
              </span>
              <span style={{ color: '#fff', direction: 'ltr', fontFamily: 'Outfit' }}>
                {isProdEnv ? 'api.invoicing.eta.gov.eg' : 'api.preprod.invoicing.eta.gov.eg'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>الأثر القانوني والمالي:</span>
              <span style={{ 
                color: isProdEnv ? 'var(--accent-pink)' : 'var(--accent-green)', 
                fontWeight: 'bold' 
              }}>
                {isProdEnv ? '⚠️ إلزامي وقانوني وحقيقي' : '🧪 تدريبي خالي من الضرائب'}
              </span>
            </div>
          </div>

          {/* Gateway tester button */}
          <button 
            type="button"
            onClick={handleTestConnection}
            disabled={testing || saving}
            className="premium-btn"
            style={{
              padding: '14px 22px',
              borderRadius: '12px',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              background: testing ? 'rgba(255, 255, 255, 0.02)' : 'rgba(59, 130, 246, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.25)',
              color: 'var(--primary)',
              cursor: (testing || saving) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'all 0.2s ease',
              outline: 'none',
              width: '100%'
            }}
          >
            {testing ? (
              <>
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  borderRadius: '50%', 
                  border: '2px solid rgba(59, 130, 246, 0.1)', 
                  borderTopColor: 'var(--primary)', 
                  animation: 'spin 1s linear infinite' 
                }}></div>
                <span>جاري الاتصال والتحقق من الشهادات...</span>
              </>
            ) : (
              <>
                <span>🔍 إجراء فحص اتصال فوري بالبوابة</span>
              </>
            )}
          </button>

          {/* Test Connection Results Render Panel */}
          {testResult && (
            <div className="fade-in-content" style={{
              border: '1px solid rgba(16, 185, 129, 0.35)',
              borderRadius: '14px',
              background: 'radial-gradient(circle at 10% 20%, rgba(16, 185, 129, 0.06) 0%, rgba(13, 20, 36, 0.95) 90%)',
              padding: '18px',
              boxShadow: '0 6px 20px rgba(16, 185, 129, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px'
            }}>
              <h4 style={{ color: 'var(--accent-green)', fontSize: '0.98rem', margin: 0, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>✅</span> نجح الفحص: {testResult.message}
              </h4>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '12px', 
                background: 'rgba(0,0,0,0.25)', 
                padding: '12px', 
                borderRadius: '10px', 
                border: '1px solid rgba(255,255,255,0.05)' 
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                    حالة الاستجابة <span dir="ltr" style={{ fontFamily: 'Outfit', color: 'var(--text-secondary)', fontSize: '0.68rem' }}>(Status):</span>
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 'bold', fontFamily: 'Outfit' }}>{testResult.details.status}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                    البيئة النشطة <span dir="ltr" style={{ fontFamily: 'Outfit', color: 'var(--text-secondary)', fontSize: '0.68rem' }}>(Env):</span>
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 'bold' }}>{testResult.details.environment}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>زمن الاستجابة:</span>
                  <span style={{ fontSize: '0.85rem', color: 'var(--accent-green)', fontWeight: 'bold', fontFamily: 'Outfit' }}>{testResult.details.ping_ms} ms</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                    رمز التوثيق <span dir="ltr" style={{ fontFamily: 'Outfit', color: 'var(--text-secondary)', fontSize: '0.68rem' }}>(Auth Token):</span>
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 'bold', fontFamily: 'Outfit' }}>active (1 hr)</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ETA Simulator Settings Card */}
        <div className="premium-card" style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.4rem' }}>🇪🇬</span> إعدادات منظومة الضرائب والمحاكي
            </h3>
            <button 
              type="button" 
              onClick={handleAutoFillEta}
              className="gradient-btn-secondary"
              style={{ padding: '8px 16px', fontSize: '0.85rem', borderRadius: '8px' }}
            >
              🪄 تعبئة ببيانات تجريبية
            </button>
          </div>
          
          {etaStatusMsg && (
            <div style={{ padding: '12px', background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.3)', color: '#38bdf8', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 'bold', textAlign: 'center', marginBottom: '16px' }}>
              {etaStatusMsg}
            </div>
          )}

          {/* Premium ETA Enable Switch */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            background: 'rgba(255,255,255,0.03)', 
            padding: '14px 18px', 
            borderRadius: '12px', 
            border: '1px solid rgba(255,255,255,0.05)',
            marginBottom: '20px'
          }}>
            <div>
              <span style={{ fontWeight: 'bold', color: '#fff', fontSize: '0.95rem', display: 'block', textAlign: 'right' }}>🖥️ تفعيل الربط الإلكتروني مع مصلحة الضرائب المصرية</span>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', textAlign: 'right', marginTop: '2px' }}>تفعيل طباعة الـ QR الضريبي في الفاتورة وإرسال البيانات للضرائب</span>
            </div>
            <label style={{ position: 'relative', display: 'inline-block', width: '46px', height: '24px', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={etaSettings.enabled || false} 
                onChange={e => setEtaSettings({...etaSettings, enabled: e.target.checked})}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: etaSettings.enabled ? 'var(--primary)' : '#4b5563',
                transition: '.3s', borderRadius: '24px',
                boxShadow: etaSettings.enabled ? '0 0 8px rgba(59, 130, 246, 0.4)' : 'none'
              }}>
                <span style={{
                  position: 'absolute', height: '18px', width: '18px', left: '3px', bottom: '3px',
                  backgroundColor: 'white', transition: '.3s', borderRadius: '50%',
                  transform: etaSettings.enabled ? 'translateX(22px)' : 'translateX(0)'
                }} />
              </span>
            </label>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px', 
            marginBottom: '20px',
            opacity: etaSettings.enabled ? 1 : 0.6,
            transition: 'all 0.3s ease'
          }}>
            <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>رقم التسجيل الضريبي</label>
              <input type="text" className="premium-input" value={etaSettings.taxId} onChange={e => setEtaSettings({...etaSettings, taxId: e.target.value})} placeholder="مثال: 313-818-494" />
            </div>
            <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>كود الفرع (Branch ID)</label>
              <input type="text" className="premium-input" value={etaSettings.branchId} onChange={e => setEtaSettings({...etaSettings, branchId: e.target.value})} placeholder="مثال: 0" />
            </div>
            <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>كود النشاط (Activity Code)</label>
              <input type="text" className="premium-input" value={etaSettings.activityCode} onChange={e => setEtaSettings({...etaSettings, activityCode: e.target.value})} placeholder="مثال: 4649" />
            </div>
            <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>بيئة التشغيل</label>
              <select className="premium-input" value={etaSettings.envMode} onChange={e => setEtaSettings({...etaSettings, envMode: e.target.value})}>
                <option value="simulator">وضع المحاكاة (Simulator) ✅</option>
                <option value="preprod">بيئة الاختبار (Pre-Production)</option>
                <option value="prod">بيئة الإنتاج (Production)</option>
              </select>
            </div>
          </div>

          <button 
            type="button" 
            onClick={handleSaveEtaSettings}
            className="gradient-btn" 
            style={{ 
              padding: '14px', 
              fontSize: '1rem', 
              fontWeight: 700, 
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              boxShadow: '0 4px 12px rgba(14, 165, 233, 0.25)',
              border: 'none',
              borderRadius: '12px',
              width: '100%',
              marginTop: 'auto'
            }}
          >
            💾 حفظ إعدادات منظومة الضرائب
          </button>
        </div>
        {/* End of Simulator Settings Card */}
        {/* Left Column: Guide Panel Card */}
        <div className="premium-card" style={{
          borderRight: '5px solid var(--accent-pink)',
          background: 'rgba(13, 20, 36, 0.4)',
          padding: '24px',
          border: '1px solid rgba(255,255,255,0.04)',
          borderRightWidth: '5px',
          height: '100%'
        }}>
          <h3 style={{ fontSize: '1.18rem', fontWeight: 800, color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>💡</span> دليل تهيئة وإعداد بيانات الضرائب
          </h3>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '16px',
            color: 'var(--text-secondary)',
            fontSize: '0.88rem',
            lineHeight: '1.7'
          }}>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <span style={{
                background: 'rgba(244, 63, 94, 0.12)',
                color: 'var(--accent-pink)',
                border: '1px solid rgba(244, 63, 94, 0.2)',
                minWidth: '24px',
                height: '24px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '0.8rem',
                fontFamily: 'Outfit'
              }}>01</span>
              <div>
                سجل الدخول على بوابة المطورين لمصلحة الضرائب:
                <br />
                - للبيئة التجريبية: <a href="https://preprod.invoicing.eta.gov.eg" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 'bold', textDecoration: 'none', fontFamily: 'Outfit, sans-serif' }}>preprod.invoicing.eta.gov.eg</a>
                <br />
                - للبيئة الفعلية: <a href="https://invoicing.eta.gov.eg" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-green)', fontWeight: 'bold', textDecoration: 'none', fontFamily: 'Outfit, sans-serif' }}>invoicing.eta.gov.eg</a>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <span style={{
                background: 'rgba(244, 63, 94, 0.12)',
                color: 'var(--accent-pink)',
                border: '1px solid rgba(244, 63, 94, 0.2)',
                minWidth: '24px',
                height: '24px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '0.8rem',
                fontFamily: 'Outfit'
              }}>02</span>
              <div>
                توجه إلى علامة تبويب <strong style={{ color: '#fff' }}>نظم ERP (ERP Registrations)</strong> في صفحة الملف الشخصي لشركتك.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <span style={{
                background: 'rgba(244, 63, 94, 0.12)',
                color: 'var(--accent-pink)',
                border: '1px solid rgba(244, 63, 94, 0.2)',
                minWidth: '24px',
                height: '24px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '0.8rem',
                fontFamily: 'Outfit'
              }}>03</span>
              <div>
                سجل نظام ERP جديد واكتب الاسم <strong style={{ color: '#fff' }}>Doctor Masr</strong> ليتم توليد كل من الـ Client ID والـ Client Secret بنجاح.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <span style={{
                background: 'rgba(244, 63, 94, 0.12)',
                color: 'var(--accent-pink)',
                border: '1px solid rgba(244, 63, 94, 0.2)',
                minWidth: '24px',
                height: '24px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '0.8rem',
                fontFamily: 'Outfit'
              }}>04</span>
              <div>
                لتوقيع الفواتير إلكترونياً، صل فلاشة الـ USB المخصصة للتوقيع الضريبي واكتب رمز الـ PIN الخاص بها لتفعيل الإرسال الآمن.
              </div>
            </div>

          </div>
        </div>

      </div>

    </div>
  );
}


// ---------------- Authentication & Login View ----------------
function LoginView({ setUser, setCurrentUserData, setErrorMessage, errorMessage }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [pharmacyName, setPharmacyName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [adminPin, setAdminPin] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showAdminPin, setShowAdminPin] = useState(false);

  const [promoCode, setPromoCode] = useState('');
  const [showEvictedAlert, setShowEvictedAlert] = useState(false);
  const [limitDialog, setLimitDialog] = useState(null); // { user, activeDevices, deviceLimit }

  useEffect(() => {
    if (localStorage.getItem('drmasr_evicted_by_admin') === 'true') {
      setShowEvictedAlert(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    localStorage.setItem('drmasr_just_logged_in', 'true');
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Check device limit
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        const uData = snap.data();
        const activeDevices = uData.activeDevices || [];
        const deviceLimit = uData.deviceLimit || 2;
        let localDeviceId = localStorage.getItem('drmasr_device_id');
        if (!localDeviceId) {
          localDeviceId = generateDeviceFingerprint() + '-' + Math.random().toString(36).substring(2, 8);
          localStorage.setItem('drmasr_device_id', localDeviceId);
        }
        if (!activeDevices.includes(localDeviceId) && activeDevices.length >= deviceLimit) {
          setLimitDialog({ user, activeDevices, deviceLimit });
          setLoading(false);
          return;
        }
      }

      setUser(user);
      localStorage.setItem('drmasr_user', JSON.stringify(user));
      localStorage.setItem('drmasr_just_logged_in', 'true');
    } catch (error) {
      console.error(error);
      let errorMsg = 'خطأ في تسجيل الدخول. يرجى التأكد من البريد الإلكتروني وكلمة المرور.';
      if (error.code === 'auth/user-not-found') {
        errorMsg = 'البريد الإلكتروني غير مسجل بالنظام.';
      } else if (error.code === 'auth/wrong-password') {
        errorMsg = 'كلمة المرور غير صحيحة.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMsg = 'فشل الاتصال بالإنترنت. يرجى التحقق من الشبكة.';
      } else if (error.message) {
        errorMsg = `حدث خطأ: ${error.message}`;
      }
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setErrorMessage('');
    localStorage.setItem('drmasr_just_logged_in', 'true');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check device limit
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        const uData = snap.data();
        const activeDevices = uData.activeDevices || [];
        const deviceLimit = uData.deviceLimit || 2;
        let localDeviceId = localStorage.getItem('drmasr_device_id');
        if (!localDeviceId) {
          localDeviceId = generateDeviceFingerprint() + '-' + Math.random().toString(36).substring(2, 8);
          localStorage.setItem('drmasr_device_id', localDeviceId);
        }
        if (!activeDevices.includes(localDeviceId) && activeDevices.length >= deviceLimit) {
          setLimitDialog({ user, activeDevices, deviceLimit });
          setLoading(false);
          return;
        }
      }

      setUser(user);
      localStorage.setItem('drmasr_user', JSON.stringify(user));
      localStorage.setItem('drmasr_just_logged_in', 'true');
    } catch (error) {
      console.error(error);
      setErrorMessage(`فشل تسجيل الدخول عبر جوجل: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!pharmacyName || !ownerName || !adminPin || adminPin.length !== 4 || !phone) {
      setErrorMessage('يرجى إدخال اسم الصيدلية، اسم المالك، رمز Admin PIN صحيح من 4 أرقام، ورقم الهاتف.');
      return;
    }
    setLoading(true);
    setErrorMessage('');
    try {
      let customExpiry = null;
      let appliedPromo = '';
      if (promoCode && promoCode.trim()) {
        const cleanPromo = promoCode.trim();
        const upperPromo = cleanPromo.toUpperCase();
        const lowerPromo = cleanPromo.toLowerCase();
        const candidateCodes = Array.from(new Set([cleanPromo, upperPromo, lowerPromo]));
        
        let promoDoc = null;
        let promoData = null;
        
        // Try direct ID lookups first
        for (const code of candidateCodes) {
          let snap = await getDoc(doc(db, 'promocodes', code));
          if (!snap.exists()) {
            snap = await getDoc(doc(db, 'promoCodes', code));
          }
          if (snap.exists()) {
            promoDoc = snap;
            promoData = snap.data();
            break;
          }
        }
        
        // Try field queries if not found by ID
        if (!promoDoc) {
          const q1 = query(collection(db, 'promocodes'), where('code', 'in', candidateCodes));
          const snap1 = await getDocs(q1);
          if (!snap1.empty) {
            promoDoc = snap1.docs[0];
            promoData = promoDoc.data();
          } else {
            const q2 = query(collection(db, 'promoCodes'), where('code', 'in', candidateCodes));
            const snap2 = await getDocs(q2);
            if (!snap2.empty) {
              promoDoc = snap2.docs[0];
              promoData = promoDoc.data();
            }
          }
        }

        if (promoDoc && promoData) {
          if (promoData.isActive !== false) {
            appliedPromo = promoData.code || promoDoc.id;
            const days = parseInt(promoData.daysBenefit) || 30;
            customExpiry = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
          } else {
            setErrorMessage('⚠️ عذراً، هذا البروموكود غير نشط حالياً.');
            setLoading(false);
            return;
          }
        } else {
          setErrorMessage('⚠️ البروموكود المدخل غير صحيح!');
          setLoading(false);
          return;
        }
      }

      localStorage.setItem('drmasr_just_logged_in', 'true');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      const userData = prepareDataForFirestore({
        pharmacyName,
        ownerName,
        adminPin,
        phone: phone.trim(),
        promoCode: appliedPromo,
        usedPromos: appliedPromo ? [appliedPromo] : [],
        ...(customExpiry ? { expiryDate: customExpiry } : {})
      }, user);
      
      await setDoc(doc(db, 'users', user.uid), userData);
      setCurrentUserData(userData);
      setUser(user);
      localStorage.setItem('drmasr_user', JSON.stringify(user));
      localStorage.setItem('drmasr_just_logged_in', 'true');
      localStorage.setItem('drmasr_just_logged_in', 'true');
      localStorage.setItem('drmasr_userdata', JSON.stringify(userData));
    } catch (error) {
      console.error(error);
      setErrorMessage(`فشل إنشاء الحساب: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setErrorMessage('يرجى إدخال البريد الإلكتروني أولاً لإرسال رابط إعادة التعيين.');
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      window.triggerDrMasrToast('✉️ تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني.');
    } catch (error) {
      console.error(error);
      setErrorMessage('فشل إرسال بريد إعادة التعيين.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'radial-gradient(circle at center, #004d49 0%, #002220 40%, #010507 100%)', padding: '24px', direction: 'rtl', fontFamily: 'Cairo, sans-serif' }}>
      <div className="premium-card fade-in-content" style={{ maxWidth: isRegistering ? '780px' : '450px', width: '100%', padding: isRegistering ? '28px 36px' : '36px 40px', background: 'rgba(13, 20, 36, 0.85)', backdropFilter: 'blur(16px)', borderRadius: '16px', border: '1.5px solid #2563eb', boxShadow: '0 0 25px rgba(37, 99, 235, 0.25), inset 0 0 12px rgba(37, 99, 235, 0.05)', transition: 'max-width 0.4s ease, padding 0.4s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/drmasr_logo_transparent.png" alt="Doctor Masr" width="190" height="190" style={{ height: isRegistering ? '135px' : '190px', width: isRegistering ? '135px' : '190px', marginBottom: '8px', objectFit: 'contain', transition: 'height 0.4s ease, width 0.4s ease' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginTop: '4px' }}>
            برنامج إدارة ومبيعات الصيدليات الذكي
          </p>
          {isRegistering && (
            <span style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#60a5fa', padding: '4px 12px', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 700, display: 'inline-block', marginTop: '8px' }}>إنشاء حساب جديد</span>
          )}
        </div>

        {errorMessage && (
          <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--accent-pink)', color: '#f87171', padding: '16px', borderRadius: '12px', marginBottom: '20px', textAlign: 'right', fontSize: '0.9rem', direction: 'rtl', lineHeight: '1.6' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>⚠️ {errorMessage}</div>
            <div style={{ borderTop: '1px solid rgba(239, 68, 68, 0.2)', paddingTop: '8px', marginTop: '8px', fontSize: '0.85rem', color: '#fda4af' }}>
              إذا استمرت المشكلة، يمكنك التواصل مع خدمة العملاء مباشرة على الرقم:
              <a href="tel:01004757940" style={{ display: 'block', color: '#fff', fontSize: '1.05rem', fontWeight: 'bold', textDecoration: 'none', marginTop: '6px', textAlign: 'center', background: '#2563eb', padding: '8px 12px', borderRadius: '8px', border: '1px solid #3b82f6', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                📞 01004757940
              </a>
            </div>
          </div>
        )}

        <form onSubmit={isRegistering ? handleRegister : handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {isRegistering ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', textAlign: 'right' }}>
              {/* Right Column (RTL: Column 1) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>اسم الصيدلية</label>
                  <input type="text" className="premium-input" style={{ fontSize: '0.95rem', padding: '10px 14px' }} value={pharmacyName} onChange={e => setPharmacyName(e.target.value)} placeholder="مثال: صيدلية الأمل" required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>اسم صاحب الصيدلية</label>
                  <input type="text" className="premium-input" style={{ fontSize: '0.95rem', padding: '10px 14px' }} value={ownerName} onChange={e => setOwnerName(e.target.value)} placeholder="د. أحمد كمال" required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>رمز الـ Admin PIN للمالك (4 أرقام)</label>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type={showAdminPin ? "text" : "password"} 
                      maxLength={4} 
                      pattern="\d{4}" 
                      className="premium-input" 
                      style={{ width: '100%', textAlign: 'center', fontSize: '1.25rem', letterSpacing: '6px', padding: '8px 8px 8px 40px', boxSizing: 'border-box' }} 
                      value={adminPin} 
                      onChange={e => setAdminPin(e.target.value.replace(/\D/g, ''))} 
                      placeholder="••••" 
                      required 
                    />
                    <button
                      type="button"
                      onClick={() => setShowAdminPin(!showAdminPin)}
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1.1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        outline: 'none',
                        color: showAdminPin ? '#38bdf8' : 'rgba(255,255,255,0.4)',
                        transition: 'color 0.2s',
                        zIndex: 10
                      }}
                    >
                      {showAdminPin ? '👁️' : '🙈'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Left Column (RTL: Column 2) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>البريد الإلكتروني</label>
                  <input type="email" className="premium-input" style={{ fontSize: '0.95rem', padding: '10px 14px' }} value={email} onChange={e => setEmail(e.target.value)} placeholder="example@domain.com" required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>كلمة المرور</label>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type={showPassword ? "text" : "password"} 
                      className="premium-input" 
                      style={{ width: '100%', fontSize: '0.95rem', padding: '10px 14px 10px 40px', boxSizing: 'border-box' }} 
                      value={password} 
                      onChange={e => setPassword(e.target.value)} 
                      placeholder="••••••••" 
                      required 
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1.1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        outline: 'none',
                        color: showPassword ? '#38bdf8' : 'rgba(255,255,255,0.4)',
                        transition: 'color 0.2s',
                        zIndex: 10
                      }}
                    >
                      {showPassword ? '👁️' : '🙈'}
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>رقم هاتف الصيدلية</label>
                  <input 
                    type="tel" 
                    className="premium-input" 
                    style={{ fontSize: '0.95rem', padding: '10px 14px' }} 
                    value={phone} 
                    onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} 
                    placeholder="مثال: 01xxxxxxxxx" 
                    required 
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>برومو كود (اختياري)</label>
                  <input 
                    type="text" 
                    className="premium-input" 
                    style={{ fontSize: '0.95rem', padding: '10px 14px' }} 
                    value={promoCode} 
                    onChange={e => setPromoCode(e.target.value)} 
                    placeholder="كود الخصم أو العرض الترويجي" 
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>البريد الإلكتروني</label>
                <input type="email" className="premium-input" style={{ fontSize: '0.95rem', padding: '10px 14px' }} value={email} onChange={e => setEmail(e.target.value)} placeholder="example@domain.com" required />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>كلمة المرور</label>
                  <button type="button" onClick={handleResetPassword} style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' }}>نسيت كلمة المرور?</button>
                </div>
                <div style={{ position: 'relative' }}>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    className="premium-input" 
                    style={{ width: '100%', fontSize: '0.95rem', padding: '10px 14px 10px 40px', boxSizing: 'border-box' }} 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    placeholder="••••••••" 
                    required 
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '1.1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      outline: 'none',
                      color: showPassword ? '#38bdf8' : 'rgba(255,255,255,0.4)',
                      transition: 'color 0.2s',
                      zIndex: 10
                    }}
                  >
                    {showPassword ? '👁️' : '🙈'}
                  </button>
                </div>
              </div>
            </>
          )}

          <button type="submit" className="gradient-btn" style={{ padding: '14px', marginTop: '8px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', transition: 'background 0.2s' }} disabled={loading}>
            {loading ? 'جاري التحميل...' : (isRegistering ? 'إنشاء الحساب' : 'تسجيل الدخول')}
          </button>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '20px 0' }}>
          <div style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>أو عبر</span>
          <div style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
        </div>

        <button onClick={handleGoogleSignIn} className="premium-input google-btn-hover" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', cursor: 'pointer', background: 'rgba(255,255,255,0.03)', color: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px' }}>
          <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" width="18" height="18" style={{ width: '18px', height: '18px' }} />
          <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>الدخول بواسطة جوجل</span>
        </button>

        <div style={{ textAlign: 'center', marginTop: '24px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
          {isRegistering ? 'لديك حساب بالفعل؟' : 'ليس لديك حساب؟'}
          <button onClick={() => setIsRegistering(!isRegistering)} style={{ background: 'none', border: 'none', color: '#60a5fa', fontWeight: 700, cursor: 'pointer', marginRight: '8px', fontSize: '0.95rem', fontFamily: 'inherit' }}>
            {isRegistering ? 'سجل دخولك هنا' : 'أنشئ حسابك الآن'}
          </button>
        </div>
      </div>

      {showEvictedAlert && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(3, 7, 18, 0.75)',
          backdropFilter: 'blur(10px)',
          zIndex: 9999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          fontFamily: 'Cairo, sans-serif',
          animation: 'fadeIn 0.25s ease-out'
        }}>
          <div className="premium-card fade-in-content" style={{
            maxWidth: '420px',
            width: '100%',
            padding: '30px',
            borderRadius: '24px',
            border: '1.5px solid rgba(59, 130, 246, 0.35)',
            background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.95) 0%, rgba(5, 8, 16, 0.98) 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 30px rgba(59, 130, 246, 0.15)',
            textAlign: 'center',
            position: 'relative'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px', filter: 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.4))' }}>📢</div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', margin: '0 0 12px 0' }}>تنبيه</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: '1.6', margin: '0 0 24px 0', whiteSpace: 'pre-line' }}>
              ⚠️ لقد تم فصل هذا الجهاز وتسجيل الخروج بواسطة المسؤول.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowEvictedAlert(false);
                localStorage.removeItem('drmasr_evicted_by_admin');
              }}
              className="gradient-btn"
              style={{ width: '100%', padding: '12px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 'bold', border: 'none', cursor: 'pointer', background: 'var(--primary)' }}
            >
              حسناً
            </button>
          </div>
        </div>
      )}

      {limitDialog && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(3, 7, 18, 0.75)',
          backdropFilter: 'blur(10px)',
          zIndex: 9999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          fontFamily: 'Cairo, sans-serif',
          animation: 'fadeIn 0.25s ease-out'
        }}>
          <div className="premium-card fade-in-content" style={{
            maxWidth: '450px',
            width: '100%',
            padding: '30px',
            borderRadius: '24px',
            border: '1.5px solid rgba(239, 68, 68, 0.35)',
            background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.95) 0%, rgba(5, 8, 16, 0.98) 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 30px rgba(239, 68, 68, 0.15)',
            textAlign: 'center',
            position: 'relative'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px', filter: 'drop-shadow(0 0 10px rgba(239, 68, 68, 0.4))' }}>⚠️</div>
            <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#f87171', margin: '0 0 12px 0' }}>تم استخدام الحد الاقصى من الاجهزة</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: '1.6', margin: '0 0 24px 0', direction: 'rtl' }}>
              عذراً، هذا الحساب مسجل دخول حالياً من الحد الأقصى المسموح به من الأجهزة النشطة في نفس الوقت ({limitDialog.deviceLimit} أجهزة).<br/>
              هل تريد إلغاء ارتباط الأجهزة الأخرى وتفعيل هذا الجهاز للدخول؟
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                type="button"
                onClick={async () => {
                  setLoading(true);
                  try {
                    let localDeviceId = localStorage.getItem('drmasr_device_id');
                    if (!localDeviceId) {
                      localDeviceId = generateDeviceFingerprint() + '-' + Math.random().toString(36).substring(2, 8);
                      localStorage.setItem('drmasr_device_id', localDeviceId);
                    }
                    const userRef = doc(db, 'users', limitDialog.user.uid);
                    await updateDoc(userRef, { activeDevices: [localDeviceId] });
                    
                    setUser(limitDialog.user);
                    localStorage.setItem('drmasr_user', JSON.stringify(limitDialog.user));
                    localStorage.setItem('drmasr_just_logged_in', 'true');
                    setLimitDialog(null);
                  } catch (err) {
                    console.error(err);
                    setErrorMessage("حدث خطأ أثناء إلغاء ارتباط الأجهزة: " + (err.message || err));
                  } finally {
                    setLoading(false);
                  }
                }}
                className="gradient-btn"
                style={{ width: '100%', padding: '12px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 'bold', border: 'none', cursor: 'pointer', background: '#2563eb', color: '#fff' }}
              >
                إلغاء ارتباط الأجهزة الأخرى وتفعيل هذا الجهاز
              </button>
              <button
                type="button"
                onClick={async () => {
                  await signOut(auth).catch(() => {});
                  setLimitDialog(null);
                }}
                style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '12px', fontWeight: 'bold', fontSize: '0.9rem', cursor: 'pointer', transition: 'background 0.2s' }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 5b. Subscription Expired Locked View

function SubscriptionExpiredView({ currentUserData, handleLogout }) {
  const [deviceCount, setDeviceCount] = useState(currentUserData?.deviceLimit || 2);
  const [copiedVoda, setCopiedVoda] = useState(false);
  const [copiedInsta, setCopiedInsta] = useState(false);

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'voda') {
      setCopiedVoda(true);
      setTimeout(() => setCopiedVoda(false), 2000);
    } else {
      setCopiedInsta(true);
      setTimeout(() => setCopiedInsta(false), 2000);
    }
    if (window.triggerDrMasrToast) {
      window.triggerDrMasrToast('📋 تم نسخ البيانات بنجاح!');
    }
  };

  const extraDevices = Math.max(0, deviceCount - 2);
  const monthlyPrice = 250 + extraDevices * 100;
  const yearlyPrice = 2500 + extraDevices * 1000;
  const lifetimePrice = 60000 + extraDevices * 2000;

  const handleWhatsAppContact = (planName, price, totalDevices) => {
    const pharmacyName = currentUserData?.pharmacyName || "غير محدد";
    const shortId = currentUserData?.shortId || "غير متوفر";
    const message = `أهلاً إدارة دكتور مصر \nأود تجديد الاشتراك وتفعيل *${planName}*\n- عدد الأجهزة المطلوب: *${totalDevices} أجهزة*\n- القيمة الإجمالية: *${price} ج.م*\n\n *بيانات الصيدلية:*\n- اسم الصيدلية: *${pharmacyName}*\n- كود العميل (Short ID): *${shortId}*\n\nلقد قمت بتحويل قيمة الاشتراك ومرفق لقطة الشاشة للتأكيد والتفعيل الفوري `;
    const encodedText = encodeURIComponent(message);
    window.open(`https://wa.me/201004757940?text=${encodedText}`, '_blank');
  };

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh', 
      background: 'radial-gradient(circle at center, #002220 0%, #050810 50%, #010408 100%)', 
      padding: '24px', 
      direction: 'rtl' 
    }}>
      <div 
        className="premium-card fade-in-content" 
        style={{ 
          maxWidth: '780px', 
          width: '100%', 
          border: '1.5px solid var(--accent-pink)', 
          padding: '36px', 
          textAlign: 'center', 
          background: 'rgba(13, 20, 36, 0.85)', 
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(244, 63, 94, 0.15), inset 0 0 20px rgba(244, 63, 94, 0.05)'
        }}
      >
        {/* Animated Expired / Shield Warning Shield Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(244, 63, 94, 0.15) 0%, rgba(225, 29, 72, 0.25) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px auto',
          border: '1px solid rgba(244, 63, 94, 0.4)',
          boxShadow: '0 0 30px rgba(244, 63, 94, 0.3)'
        }}>
          <span style={{ fontSize: '2.5rem', filter: 'drop-shadow(0 0 8px rgba(244, 63, 94, 0.6))' }}>⏳</span>
        </div>

        <h2 className="gradient-text" style={{ 
          fontSize: '2.2rem', 
          fontWeight: 900, 
          marginBottom: '10px',
          background: 'linear-gradient(135deg, #f43f5e, #fb7185, #ef4444)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          انتهت فترة الاشتراك الخاصة بك
        </h2>
        
        <p style={{ 
          color: 'var(--text-secondary)', 
          fontSize: '1.05rem', 
          marginBottom: '28px', 
          lineHeight: '1.6' 
        }}>
          عزيزي العميل في صيدلية <strong style={{ color: '#fff' }}>{currentUserData?.pharmacyName || "صيدلية دكتور مصر"}</strong>، نود إعلامك بأن مدة حسابك قد انتهت بالكامل. لتجنب انقطاع الخدمة وضمان أمان بياناتك المخزنة، يرجى تجديد الاشتراك فوراً.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.05)', textAlign: 'right', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '1.8rem' }}>🆔</span>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>كود العميل (Short ID):</span>
              <strong style={{ color: 'var(--primary)', fontSize: '1.15rem', fontFamily: 'Outfit', marginTop: '2px' }}>{currentUserData?.shortId || "غير متوفر"}</strong>
            </div>
          </div>
          <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.05)', textAlign: 'right', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '1.8rem' }}>🏥</span>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>اسم الصيدلية المسجلة:</span>
              <strong style={{ color: '#fff', fontSize: '1.1rem', marginTop: '2px' }}>{currentUserData?.pharmacyName || "غير محدد"}</strong>
            </div>
          </div>
        </div>

        {/* Device Limit Selector Bar */}
        <div className="premium-card" style={{ 
          borderRight: '5px solid var(--primary)', 
          background: 'rgba(59, 130, 246, 0.03)', 
          display: 'flex', 
          flexWrap: 'wrap', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          gap: '16px', 
          textAlign: 'right', 
          marginBottom: '28px',
          padding: '20px'
        }}>
          <div style={{ flex: 1, minWidth: '260px' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>⚙️</span> تحديد عدد الأجهزة المطلوبة للاشتراك
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0, lineHeight: '1.4' }}>
              باقات دكتور مصر تشمل تشغيل **جهازين (2)** معاً بشكل أساسي مجاناً. كل جهاز إضافي يضيف (100 ج.م شهرياً / 1000 ج.م سنوياً / 2000 ج.م مدى الحياة).
            </p>
          </div>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            background: 'rgba(8, 13, 27, 0.7)', 
            padding: '8px 16px', 
            borderRadius: '12px', 
            border: '1px solid var(--border)' 
          }}>
            <button 
              type="button" 
              onClick={() => setDeviceCount(prev => Math.max(2, prev - 1))} 
              style={{ 
                width: '32px', 
                height: '32px', 
                borderRadius: '8px', 
                background: deviceCount > 2 ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255,255,255,0.03)', 
                color: deviceCount > 2 ? 'var(--primary)' : 'rgba(255,255,255,0.2)', 
                border: 'none', 
                fontSize: '1.2rem', 
                fontWeight: 'bold', 
                cursor: deviceCount > 2 ? 'pointer' : 'default', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                outline: 'none',
                transition: 'all 0.2s'
              }}
            >
              -
            </button>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#fff', minWidth: '80px', textAlign: 'center' }}>
              {deviceCount} أجهزة
            </span>
            <button 
              type="button" 
              onClick={() => setDeviceCount(prev => prev + 1)} 
              style={{ 
                width: '32px', 
                height: '32px', 
                borderRadius: '8px', 
                background: 'rgba(59, 130, 246, 0.2)', 
                color: 'var(--primary)', 
                border: 'none', 
                fontSize: '1.2rem', 
                fontWeight: 'bold', 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                outline: 'none',
                transition: 'all 0.2s'
              }}
            >
              +
            </button>
          </div>
        </div>

        <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', marginBottom: '16px', textAlign: 'right', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🚀</span> اختر خطة الاشتراك لتجديد حسابك فوراً:
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '20px', marginBottom: '28px' }}>
          {/* Monthly */}
          <div className="premium-card" style={{ 
            padding: '20px', 
            border: '1px solid var(--border)', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'space-between', 
            minHeight: '200px', 
            textAlign: 'right',
            background: 'rgba(13, 20, 36, 0.65)',
            borderRadius: '16px'
          }}>
            <div>
              <span style={{ fontSize: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary)', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>باقة مرنة</span>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', marginTop: '8px', marginBottom: '6px' }}>الخطة الشهرية</h4>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px', margin: '12px 0', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.8rem', fontWeight: '900', color: 'var(--primary)', fontFamily: 'Outfit' }}>{monthlyPrice}</span>
                <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 'bold' }}>ج.م</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>/ شهرياً</span>
                <span style={{ fontSize: '0.85rem', color: '#ef4444', textDecoration: 'line-through', marginRight: '6px', fontWeight: 600 }}>بدلاً من {1000 + extraDevices * 400} ج.م</span>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>صلاحية كاملة لـ {deviceCount} أجهزة ⚡</p>
            </div>
            <button 
              onClick={() => handleWhatsAppContact('الخطة الشهرية (Monthly)', monthlyPrice, deviceCount)} 
              className="gradient-btn" 
              style={{ width: '100%', background: '#25D366', color: '#fff', border: 'none', fontSize: '0.88rem', padding: '12px', marginTop: '16px', fontWeight: 'bold', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
            >
              <span>تجديد شهري</span>
            </button>
          </div>

          {/* Yearly */}
          <div className="premium-card" style={{ 
            padding: '20px', 
            border: '1px solid rgba(59, 130, 246, 0.5)', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'space-between', 
            minHeight: '200px', 
            textAlign: 'right',
            background: 'rgba(13, 20, 36, 0.85)',
            borderRadius: '16px',
            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.1)',
            position: 'relative'
          }}>
            <div style={{ position: 'absolute', top: '-10px', right: '12px', background: 'var(--primary)', color: '#fff', fontSize: '0.7rem', fontWeight: 800, padding: '3px 8px', borderRadius: '10px' }}>الأكثر توفيراً</div>
            <div>
              <span style={{ fontSize: '0.75rem', background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>أفضل قيمة</span>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', marginTop: '8px', marginBottom: '6px' }}>الخطة السنوية</h4>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px', margin: '12px 0' }}>
                <span style={{ fontSize: '1.8rem', fontWeight: '900', color: 'var(--primary)', fontFamily: 'Outfit' }}>{yearlyPrice}</span>
                <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 'bold' }}>ج.م</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>/ سنوياً</span>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>صلاحية كاملة لـ {deviceCount} أجهزة ⚡</p>
            </div>
            <button 
              onClick={() => handleWhatsAppContact('الخطة السنوية (Yearly)', yearlyPrice, deviceCount)} 
              className="gradient-btn" 
              style={{ width: '100%', background: '#25D366', color: '#fff', border: 'none', fontSize: '0.88rem', padding: '12px', marginTop: '16px', fontWeight: 'bold', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
            >
              <span>تجديد سنوي</span>
            </button>
          </div>

          {/* Lifetime */}
          <div className="premium-card" style={{ 
            padding: '20px', 
            border: '1px solid var(--accent-green)', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'space-between', 
            minHeight: '200px', 
            textAlign: 'right',
            background: 'rgba(13, 20, 36, 0.65)',
            borderRadius: '16px'
          }}>
            <div>
              <span style={{ fontSize: '0.75rem', background: 'rgba(16, 185, 129, 0.1)', color: 'var(--accent-green)', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>استثمار أبدي</span>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', marginTop: '8px', marginBottom: '6px' }}>مدى الحياة</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', margin: '12px 0' }}>
                <div style={{ textDecoration: 'line-through', color: '#ef4444', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <span>{(120000 + extraDevices * 4000).toLocaleString()}</span>
                  <span style={{ fontSize: '0.7rem' }}>ج.م</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                  <span style={{ fontSize: '1.8rem', fontWeight: '900', color: 'var(--accent-green)', fontFamily: 'Outfit' }}>{lifetimePrice.toLocaleString()}</span>
                  <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 'bold' }}>ج.م</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>/ للأبد</span>
                </div>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>صلاحية دائمة لـ {deviceCount} أجهزة ⚡</p>
            </div>
            <button 
              onClick={() => handleWhatsAppContact('خطة مدى الحياة (Lifetime)', lifetimePrice, deviceCount)} 
              className="gradient-btn" 
              style={{ width: '100%', background: '#25D366', color: '#fff', border: 'none', fontSize: '0.88rem', padding: '12px', marginTop: '16px', fontWeight: 'bold', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
            >
              <span>تفعيل للأبد</span>
            </button>
          </div>
        </div>

        {/* Copy-enabled Payment Methods terminal block */}
        <div style={{ 
          background: 'radial-gradient(circle at top left, rgba(244, 63, 94, 0.04), rgba(15, 23, 42, 0.8))', 
          border: '1px solid rgba(244, 63, 94, 0.2)', 
          borderRadius: '16px', 
          padding: '24px', 
          marginBottom: '28px', 
          textAlign: 'right' 
        }}>
          <h4 style={{ color: '#f87171', fontSize: '1.1rem', fontWeight: 800, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>💳</span> طرق السداد الرسمية للتفعيل الفوري:
          </h4>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: '1.5', marginBottom: '16px' }}>
            يرجى تحويل قيمة الباقة المناسبة لتجديد حسابك وتأكيد المعاملة، ثم اضغط على زر التفعيل الأخضر بالباقة ليتم توجيهك فوراً لتأكيد التفعيل عبر الواتساب:
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
            {/* Vodafone Cash */}
            <div style={{ 
              background: 'rgba(15, 23, 42, 0.8)', 
              border: '1px solid rgba(244, 63, 94, 0.1)', 
              borderRadius: '12px', 
              padding: '14px' 
            }}>
              <span style={{ fontSize: '0.85rem', color: '#f87171', fontWeight: 'bold' }}>فودافون كاش (Vodafone Cash)</span>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'rgba(244, 63, 94, 0.05)',
                border: '1px dashed rgba(244, 63, 94, 0.3)',
                borderRadius: '8px',
                padding: '10px 12px',
                marginTop: '8px'
              }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#fff', fontFamily: 'Outfit' }}>01004757940</span>
                <button 
                  onClick={() => handleCopy('01004757940', 'voda')}
                  style={{
                    background: copiedVoda ? 'rgba(16, 185, 129, 0.15)' : 'rgba(244, 63, 94, 0.12)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '6px 10px',
                    color: copiedVoda ? '#34d399' : '#f87171',
                    cursor: 'pointer',
                    fontSize: '0.78rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    outline: 'none'
                  }}
                >
                  {copiedVoda ? 'تم النسخ!' : 'نسخ'}
                </button>
              </div>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'block', marginTop: '6px' }}>الاسم: باسل صلاح (Basel Salah)</span>
            </div>

            {/* InstaPay */}
            <div style={{ 
              background: 'rgba(15, 23, 42, 0.8)', 
              border: '1px solid rgba(56, 189, 248, 0.1)', 
              borderRadius: '12px', 
              padding: '14px' 
            }}>
              <span style={{ fontSize: '0.85rem', color: '#38bdf8', fontWeight: 'bold' }}>إنستا باي (InstaPay)</span>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'rgba(56, 189, 248, 0.05)',
                border: '1px dashed rgba(56, 189, 248, 0.3)',
                borderRadius: '8px',
                padding: '10px 12px',
                marginTop: '8px'
              }}>
                <span style={{ fontSize: '1.05rem', fontWeight: 'bold', color: '#fff', fontFamily: 'Outfit' }}>baselbes0@instapay</span>
                <button 
                  onClick={() => handleCopy('baselbes0@instapay', 'insta')}
                  style={{
                    background: copiedInsta ? 'rgba(16, 185, 129, 0.15)' : 'rgba(56, 189, 248, 0.12)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '6px 10px',
                    color: copiedInsta ? '#34d399' : '#38bdf8',
                    cursor: 'pointer',
                    fontSize: '0.78rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    outline: 'none'
                  }}
                >
                  {copiedInsta ? 'تم النسخ!' : 'نسخ'}
                </button>
              </div>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'block', marginTop: '6px' }}>الاسم: باسل صلاح (Basel Salah)</span>
            </div>
          </div>
        </div>

        <button 
          onClick={handleLogout} 
          className="gradient-btn-secondary" 
          style={{ 
            padding: '12px 28px', 
            fontSize: '0.92rem', 
            fontWeight: 600, 
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'all 0.25s' 
          }}
        >
          تسجيل الخروج من الحساب
        </button>
      </div>
    </div>
  );
}

// 5c. Admin PIN Verification Locked Screen Component
function AdminPINVerification({ adminPin, onVerified, onCancel }) {
  const [pinInput, setPinInput] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState('');
  
  // High-premium hover states
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);
  const [isCancelHovered, setIsCancelHovered] = useState(false);
  const [isEyeHovered, setIsEyeHovered] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pinInput === adminPin) {
      onVerified();
    } else {
      setError('❌ رمز الـ PIN المدخل غير صحيح!');
      setPinInput('');
    }
  };

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(7, 10, 19, 0.85)', 
      backdropFilter: 'blur(12px)', 
      zIndex: 100000, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: '20px', 
      direction: 'rtl' 
    }}>
      <div 
        className="premium-card fade-in-content" 
        style={{ 
          maxWidth: '420px', 
          width: '100%', 
          border: '1px solid rgba(99, 102, 241, 0.35)', 
          padding: '36px', 
          textAlign: 'center', 
          background: 'rgba(15, 22, 42, 0.9)', 
          borderRadius: '24px',
          boxShadow: '0 24px 50px -12px rgba(0, 0, 0, 0.8), 0 0 35px rgba(99, 102, 241, 0.15)',
          animation: 'scaleUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
      >
        {/* Animated Key Icon Container */}
        <div style={{
          width: '68px',
          height: '68px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px auto',
          border: '1px solid rgba(99, 102, 241, 0.4)',
          boxShadow: '0 0 25px rgba(99, 102, 241, 0.25), inset 0 0 10px rgba(99, 102, 241, 0.2)'
        }}>
          <span style={{ fontSize: '2rem', filter: 'drop-shadow(0 0 8px rgba(99, 102, 241, 0.6))' }}>🔑</span>
        </div>

        <h3 className="gradient-text" style={{ 
          fontSize: '1.6rem', 
          fontWeight: 800, 
          marginBottom: '10px',
          fontFamily: 'Tajawal, Cairo, sans-serif'
        }}>
          تأكيد هوية المسؤول
        </h3>
        
        <p style={{ 
          color: 'var(--text-secondary)', 
          fontSize: '0.92rem', 
          marginBottom: '26px', 
          lineHeight: '1.6',
          fontFamily: 'Tajawal, Cairo, sans-serif'
        }}>
          أنت تحاول الدخول كمسؤول للنظام. يرجى إدخال رمز الـ **PIN** الخاص بالمالك المكون من 4 أرقام للمتابعة.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'right' }}>
            <span style={{ 
              fontSize: '0.85rem', 
              color: 'var(--text-secondary)',
              fontFamily: 'Tajawal, Cairo, sans-serif',
              paddingRight: '4px'
            }}>
              رمز الـ PIN السري:
            </span>
            <div style={{ position: 'relative' }}>
              <input
                type={showPin ? "text" : "password"}
                maxLength={4}
                autoFocus
                style={{ 
                  width: '100%',
                  textAlign: 'center', 
                  fontSize: '2rem', 
                  letterSpacing: '14px', 
                  paddingLeft: '50px',
                  height: '56px',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  background: 'rgba(255, 255, 255, 0.02)',
                  color: '#fff',
                  outline: 'none',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
                  transition: 'all 0.3s ease',
                  fontFamily: 'monospace'
                }}
                className="premium-input-pin"
                value={pinInput}
                onChange={(e) => {
                  setError('');
                  setPinInput(e.target.value.replace(/\D/g, ''));
                }}
                placeholder="••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                onMouseEnter={() => setIsEyeHovered(true)}
                onMouseLeave={() => setIsEyeHovered(false)}
                style={{ 
                  position: 'absolute', 
                  left: '16px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  background: 'none', 
                  border: 'none', 
                  color: isEyeHovered ? '#6366f1' : 'var(--text-secondary)', 
                  cursor: 'pointer', 
                  fontSize: '1.3rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.2s ease',
                  outline: 'none'
                }}
              >
                {showPin ? '👁️' : '🔒'}
              </button>
            </div>
          </div>

          {error && (
            <div style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '12px', padding: '10px 14px', color: '#f87171', fontSize: '0.88rem', fontWeight: 'bold', fontFamily: 'Tajawal, Cairo, sans-serif', textAlign: 'center', marginBottom: '10px' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: '14px', marginTop: '6px' }}>
            <button type="submit" onMouseEnter={() => setIsSubmitHovered(true)} onMouseLeave={() => setIsSubmitHovered(false)} style={{ flex: 1, padding: '14px', fontWeight: 'bold', border: 'none', borderRadius: '14px', background: isSubmitHovered ? 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)', color: '#fff', cursor: 'pointer', fontSize: '0.95rem', fontFamily: 'Tajawal, Cairo, sans-serif', boxShadow: isSubmitHovered ? '0 0 20px rgba(99, 102, 241, 0.45)' : '0 4px 12px rgba(99, 102, 241, 0.25)', transform: isSubmitHovered ? 'scale(1.03) translateY(-1px)' : 'scale(1)', transition: 'all 0.2s ease', outline: 'none' }}>تأكيد الدخول 🛡️</button>
            <button type="button" onClick={onCancel} onMouseEnter={() => setIsCancelHovered(true)} onMouseLeave={() => setIsCancelHovered(false)} style={{ flex: 1, padding: '14px', fontWeight: 'bold', borderRadius: '14px', border: isCancelHovered ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)' , background: isCancelHovered ? 'rgba(239, 68, 68, 0.08)' : 'rgba(255, 255, 255, 0.03)', color: isCancelHovered ? '#f87171' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.95rem', fontFamily: 'Tajawal, Cairo, sans-serif', transform: isCancelHovered ? 'scale(1.03) translateY(-1px)' : 'scale(1)', transition: 'all 0.2s ease', outline: 'none' }}>إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ShiftsView({ sales, user, activeShift, activeShifts = [], setActiveShift, shiftsHistory = [], setShiftsHistory, employees = [], setEmployees, telegramSettings = {}, currentUserData, isShiftsHistoryLoading, fetchShiftsHistory, setGlobalLoading, db }) {
  const formatMoney = (val) => parseFloat(Number(val || 0).toFixed(2));
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedEmployeeForModal, setSelectedEmployeeForModal] = useState(null);
  const [deductionType, setDeductionType] = useState('money');
  const [deductionValue, setDeductionValue] = useState('');
  const [deductionReason, setDeductionReason] = useState('');
  const [deductionLoading, setDeductionLoading] = useState(false);
  const [closingShift, setClosingShift] = useState(null);
  const [actualCash, setActualCash] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [closingStatusMsg, setClosingStatusMsg] = useState('');
  const [isClosingLoading, setIsClosingLoading] = useState(false);
  const [accountingTx, setAccountingTx] = useState([]);

  // Incentives & Targets Tab States
  const [shiftsTab, setShiftsTab] = useState('shifts');
  const [targets, setTargets] = useState([]);
  const [isTargetsLoading, setIsTargetsLoading] = useState(false);


  const [targetEmployeeId, setTargetEmployeeId] = useState('');
  const [targetType, setTargetType] = useState('sales_amount');
  const [targetValue, setTargetValue] = useState('');
  const [targetBonus, setTargetBonus] = useState('');
  const [targetStartDate, setTargetStartDate] = useState('');
  const [targetEndDate, setTargetEndDate] = useState('');

  const [editingTargetId, setEditingTargetId] = useState(null);
  const [editTargetValue, setEditTargetValue] = useState('');
  const [editTargetBonus, setEditTargetBonus] = useState('');
  const [editTargetStartDate, setEditTargetStartDate] = useState('');
  const [editTargetEndDate, setEditTargetEndDate] = useState('');

  const handleOpenEditTarget = (target) => {
    setEditingTargetId(target.id);
    setEditTargetValue(target.value);
    setEditTargetBonus(target.bonus);
    setEditTargetStartDate(target.startDate || '');
    setEditTargetEndDate(target.endDate || '');
  };

  const handleSaveTargetEdits = async (targetId) => {
    if (!editTargetValue || !editTargetBonus || !editTargetStartDate || !editTargetEndDate) {
      window.triggerDrMasrToast("⚠️ يرجى تعبئة كافة الحقول!");
      return;
    }
    setGlobalLoading({ active: true, title: 'تعديل التارجت 🎯', message: 'جاري حفظ التعديلات في السيرفر...' });
    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      const targetDocRef = doc(db, 'users', user.uid, 'targets', targetId);
      
      const updatedFields = {
        value: Number(editTargetValue),
        bonus: Number(editTargetBonus),
        startDate: editTargetStartDate,
        endDate: editTargetEndDate
      };

      await updateDoc(targetDocRef, updatedFields);
      
      const nextTargets = targets.map(t => t.id === targetId ? { ...t, ...updatedFields } : t);
      setTargets(nextTargets);
      localStorage.setItem('drmasr_targets_' + user.uid, JSON.stringify(nextTargets));
      
      setEditingTargetId(null);
      window.triggerDrMasrToast("✅ تم تعديل التارجت والحافز بنجاح!");
    } catch (err) {
      console.error(err);
      window.triggerDrMasrToast("❌ فشل تعديل التارجت.");
    } finally {
      setGlobalLoading({ active: false });
    }
  };

  useEffect(() => {
    const fetchAccounting = async () => {
      if (!user || !db) return;
      try {
        const saved = localStorage.getItem('drmasr_accounting_' + (user.uid));
        if (saved) setAccountingTx(JSON.parse(saved));

        const { collection, query, getDocs } = await import('firebase/firestore');
        const q = query(collection(db, 'users', user.uid, 'accounting'));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setAccountingTx(fetched);
          localStorage.setItem('drmasr_accounting_' + (user.uid), JSON.stringify(fetched));
        }
      } catch (e) {
        console.error("Error fetching accounting for shifts:", e);
      }
    };
    fetchAccounting();
  }, [user, db]);


  // Load Targets
  useEffect(() => {
    if (!user || !db || shiftsTab !== 'incentives') return;
    const fetchTargets = async () => {
      setIsTargetsLoading(true);
      try {
        const saved = localStorage.getItem('drmasr_targets_' + user.uid);
        if (saved) setTargets(JSON.parse(saved));

        const { collection, getDocs } = await import('firebase/firestore');
        const querySnapshot = await getDocs(collection(db, 'users', user.uid, 'targets'));
        const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTargets(list);
        localStorage.setItem('drmasr_targets_' + user.uid, JSON.stringify(list));
      } catch (err) {
        console.error("Error fetching targets:", err);
      } finally {
        setIsTargetsLoading(false);
      }
    };
    fetchTargets();
  }, [user, db, shiftsTab]);

  const handleAddTarget = async (e) => {
    e.preventDefault();
    if (!targetEmployeeId || !targetValue || !targetBonus || !targetStartDate || !targetEndDate) {
      window.triggerDrMasrToast("⚠️ يرجى ملء جميع حقول التارجت!");
      return;
    }

    const matchedEmp = employees.find(emp => emp.id === targetEmployeeId);
    if (!matchedEmp) return;

    const newTarget = {
      employeeId: targetEmployeeId,
      employeeName: matchedEmp.name,
      employeeRole: matchedEmp.role || 'pharmacist',
      type: targetType,
      value: parseFloat(targetValue),
      bonus: parseFloat(targetBonus),
      startDate: targetStartDate,
      endDate: targetEndDate,
      createdAt: new Date().toISOString()
    };

    try {
      const { collection, addDoc } = await import('firebase/firestore');
      const docRef = await addDoc(collection(db, 'users', user.uid, 'targets'), newTarget);
      const savedTarget = { id: docRef.id, ...newTarget };
      const nextTargets = [savedTarget, ...targets];
      setTargets(nextTargets);
      localStorage.setItem('drmasr_targets_' + user.uid, JSON.stringify(nextTargets));
      
      setTargetValue('');
      setTargetBonus('');
      window.triggerDrMasrToast("✅ تم حفظ التارجت والحافز بنجاح!");
    } catch (err) {
      console.error("Error saving target:", err);
      window.triggerDrMasrToast("❌ حدث خطأ أثناء حفظ التارجت.");
    }
  };

  const handleDeleteTarget = async (targetId) => {
    window.triggerDrMasrConfirm("هل أنت متأكد من حذف هذا التارجت؟", async () => {
      try {
        const { doc, deleteDoc } = await import('firebase/firestore');
        await deleteDoc(doc(db, 'users', user.uid, 'targets', targetId));
        const nextTargets = targets.filter(t => t.id !== targetId);
        setTargets(nextTargets);
        localStorage.setItem('drmasr_targets_' + user.uid, JSON.stringify(nextTargets));
        window.triggerDrMasrToast("✅ تم حذف التارجت بنجاح.");
      } catch (err) {
        console.error("Error deleting target:", err);
      }
    });
  };

  const getTargetProgress = (t) => {
    const start = new Date(t.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(t.endDate);
    end.setHours(23, 59, 59, 999);

    const periodSales = sales.filter(sale => {
      if (!sale.timestamp) return false;
      const tTime = new Date(sale.timestamp);
      return tTime >= start && tTime <= end;
    });

    let currentVal = 0;
    const isDriver = t.employeeRole === 'driver' || t.employeeRole === 'طيار';
    if (isDriver) {
      const driverSales = periodSales.filter(s => s.isDelivery && s.deliveryStatus === 'delivered' && cleanName(s.deliveryDetails?.driverName || '') === cleanName(t.employeeName));
      if (t.type === 'sales_amount') {
        currentVal = driverSales.reduce((acc, s) => acc + (s.isInsurance ? (s.patientShare !== undefined ? s.patientShare : s.grandTotal) : s.grandTotal), 0);
      } else {
        currentVal = driverSales.length;
      }
    } else {
      const pharmaSales = periodSales.filter(s => cleanName(s.cashierName || '') === cleanName(t.employeeName));
      if (t.type === 'sales_amount') {
        currentVal = pharmaSales.reduce((acc, s) => acc + (s.isInsurance ? (s.patientShare !== undefined ? s.patientShare : s.grandTotal) : s.grandTotal), 0);
      } else {
        currentVal = pharmaSales.length;
      }
    }
    return Math.round(currentVal * 100) / 100;
  };

  const handleCloseRemoteShift = async (e) => {
    e.preventDefault();
    if (!actualCash) {
      setClosingStatusMsg('يرجى إدخال المبلغ الفعلي في الخزينة لإغلاق الوردية!');
      return;
    }
    setIsClosingLoading(true);
    setClosingStatusMsg('');

    const { cashSales, creditSales, expenses, extraIncomes, expectedCash } = getShiftStats(closingShift);
    const difference = parseFloat(actualCash || 0) - expectedCash;

    const closedShift = {
      ...closingShift,
      endTime: new Date().toISOString(),
      cashSales,
      creditSales,
      expenses,
      extraIncomes,
      expectedCash,
      actualCash: parseFloat(actualCash),
      difference,
      closingNotes,
      status: 'closed'
    };

    try {
      await addDoc(collection(db, 'users', user?.uid, 'shifts_history'), closedShift);

      try {
        const shiftStart = new Date(closingShift.startTime);
        const durationHours = Math.max(0, (new Date() - shiftStart) / (1000 * 60 * 60));
        const cleanedCashier = cleanName(closingShift.cashierName || '');
        const matchedEmp = employees.find(emp => cleanName(emp.name) === cleanedCashier);
        if (matchedEmp && matchedEmp.id) {
          const empDocRef = doc(db, 'users', user?.uid, 'employees', matchedEmp.id);
          const currentHours = matchedEmp.workedHours || 0;
          const currentShifts = matchedEmp.shiftsCount || 0;
          await updateDoc(empDocRef, {
            workedHours: Math.round((currentHours + durationHours) * 100) / 100,
            shiftsCount: currentShifts + 1
          });
        }
      } catch (err) {
        console.error('Failed to update pharmacist firebase hours:', err);
      }

      if (closingShift.id) {
        await deleteDoc(doc(db, 'users', user?.uid, 'active_shift', closingShift.id));
      }

      const nextHistory = [closedShift, ...shiftsHistory];
      setShiftsHistory(nextHistory);
      localStorage.setItem('drmasr_shiftsHistory_' + user?.uid, JSON.stringify(nextHistory));

      if (closingShift.id === localStorage.getItem('drmasr_active_shift_id_' + user?.uid)) {
        setActiveShift(null);
        localStorage.removeItem('drmasr_activeShift_' + user?.uid);
        localStorage.removeItem('drmasr_active_shift_id_' + user?.uid);
      }

      try {
        if (telegramSettings && telegramSettings.enabled) {
          const pharmacyName = currentUserData?.pharmacyName || 'صيدلية دكتور مصر';
          const shortId = currentUserData?.shortId || 'غير متوفر';

          let msg = `📊 <b>تقرير إغلاق وردية (عن بُعد)</b>\n` +
                    `🏥 <b>الصيدلية:</b> <code>${pharmacyName}</code> (${shortId})\n` +
                    `👨‍⚕️ <b>الدكتور المسؤول:</b> <b>${closedShift.cashierName || 'غير معروف'}</b>\n` +
                    `🛡️ <b>تم الإغلاق بواسطة الإدارة عن بُعد</b>\n` +
                    `━━━━━━━━━━━━━━━━━━━━\n\n` +
                    `📅 <b>تفاصيل الوردية:</b>\n` +
                    `• وقت البدء: <code>${closedShift.startTime ? new Date(closedShift.startTime).toLocaleString('ar-EG') : 'غير متوفر'}</code>\n` +
                    `• وقت الإغلاق: <code>${closedShift.endTime ? new Date(closedShift.endTime).toLocaleString('ar-EG') : 'غير متوفر'}</code>\n\n` +
                    `💰 <b>الملخص المالي والمبيعات:</b>\n` +
                    `  ├─ العهدة الافتتاحية: ${closedShift.startingCash} ج.م\n` +
                    `  ├─ مبيعات الكاش (النقدي): ${closedShift.cashSales} ج.م\n` +
                    `  └─ مبيعات الشبكة (الفيزا): ${closedShift.creditSales} ج.م\n\n` +
                    `💵 <b>تطابق الخزينة والعهدة:</b>\n` +
                    `  ├─ المبلغ المتوقع بالخزينة: ${closedShift.expectedCash} ج.م\n` +
                    `  ├─ المبلغ الفعلي بالخزينة: ${closedShift.actualCash} ج.م\n` +
                    `  └─ العجز / الزيادة: <b>${closedShift.difference >= 0 ? '📈 زائد' : '📉 عجز'} (${closedShift.difference >= 0 ? '+' : ''}${closedShift.difference} ج.م)</b>\n\n`;

          if (closedShift.closingNotes) {
            msg += `📝 <b>ملاحظات الطبيب:</b>\n<i>${closedShift.closingNotes}</i>\n\n`;
          }

          msg += `━━━━━━━━━━━━━━━━━━━━\n` +
                 `🕊️ <i>تم إغلاق الوردية وحفظها بنجاح عبر نظام دكتور مصر</i>`;

          sendTelegramMessage(telegramSettings, msg).catch(err => console.error('Telegram send shift close failed:', err));
        }
      } catch (telegramErr) {
        console.error('Telegram notification error:', telegramErr);
      }

      setClosingShift(null);
      setActualCash('');
      setClosingNotes('');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('✅ تم إغلاق الوردية بنجاح!');
      }
      try {
        printShiftReport(closedShift);
      } catch (printErr) {
        console.error('Auto printing shift report failed:', printErr);
      }
    } catch (err) {
      console.error('Remote shift close error:', err);
      setClosingStatusMsg('حدث خطأ أثناء إغلاق الوردية: ' + (err.message || err.toString()));
    } finally {
      setIsClosingLoading(false);
    }
  };

  const cleanName = (name) => {
    if (!name) return '';
    return name.toString().trim()
      .replace(/^(د\.?\s*|الطيار\s*|كابتن\s*|دكتور\s*|د\s+)/i, '')
      .replace(/\s+/g, '')
      .toLowerCase();
  };

  const getInitialDates = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const pad = (num) => String(num).padStart(2, '0');
    const startStr = `${firstDay.getFullYear()}-${pad(firstDay.getMonth() + 1)}-01`;
    const endStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    return { startStr, endStr };
  };

  const { startStr, endStr } = getInitialDates();
  const [hoursStartDate, setHoursStartDate] = useState(startStr);
  const [hoursEndDate, setHoursEndDate] = useState(endStr);
  const [reportData, setReportData] = useState([]);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [activeShortcut, setActiveShortcut] = useState('month'); 

  const getLocalDateString = (dateObjOrStr) => {
    if (!dateObjOrStr) return '';
    const date = new Date(dateObjOrStr);
    if (isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  const handleCalculateReport = async (overrideStart, overrideEnd) => {
    if (!user) return;
    const finalStart = overrideStart || hoursStartDate;
    const finalEnd = overrideEnd || hoursEndDate;

    if (setGlobalLoading) {
      setGlobalLoading({
        active: true,
        title: 'جاري استدعاء التقرير 📊',
        message: 'جاري حساب إنتاجية وساعات عمل الموظفين والطيارين...'
      });
    }

    try {
      if (fetchShiftsHistory) {
        try {
          await fetchShiftsHistory(true);
        } catch (e) {
          console.error("Error refreshing shifts history:", e);
        }
      }

      const loadedShiftsHistory = shiftsHistory || [];
      const loadedActiveShifts = activeShifts || [];
      const loadedSales = sales || [];

      const stats = {};

      if (Array.isArray(employees)) {
        employees.forEach(emp => {
          const isDriver = emp.role === 'driver' || emp.role === 'طيار';
          const roleText = isDriver ? 'طيار 🛵' 
                         : emp.role === 'pharmacist' || emp.role === 'صيدلي' ? 'صيدلي 🩺'
                         : emp.role === 'assistant' || emp.role === 'مساعد' ? 'مساعد 💊'
                         : emp.role === 'cashier' || emp.role === 'كاشير' ? 'كاشير 💵'
                         : emp.role === 'cleaner' || emp.role === 'عامل خدمات' ? 'عامل خدمات 🧹'
                         : emp.role || 'موظف';
          stats[emp.name] = {
            name: emp.name,
            role: roleText,
            shiftsCount: 0,
            ordersCount: 0,
            totalHours: 0,
            regularHours: 0,
            holidayHours: 0,
            deliveryTimes: [],
            isWorkingNow: false,
            firebaseWorkedHours: emp.workedHours || 0,
            firebaseShiftsCount: emp.shiftsCount || 0,
            firebaseOrdersCount: emp.ordersCount || 0,
            salaryValue: emp.salaryValue || 0,
            salaryType: emp.salaryType || 'monthly',
            regularRate: emp.regularRate || 0,
            commissionPerOrder: emp.commissionPerOrder || 0,
            holidayMultiplier: emp.holidayMultiplier || 1.5,
            deductions: emp.deductions || [],
            rawEmp: emp
          };
        });
      }

      loadedShiftsHistory.forEach(shift => {
        if (!shift.startTime) return;
        const shiftLocalDate = getLocalDateString(shift.startTime);
        if (shiftLocalDate && shiftLocalDate >= finalStart && shiftLocalDate <= finalEnd) {
          const cashierName = shift.cashierName || 'غير مسجل';
          const cleanedCashier = cleanName(cashierName);
          
          let matchedEmpName = Object.keys(stats).find(k => cleanName(k) === cleanedCashier);
          if (!matchedEmpName) {
            matchedEmpName = cashierName;
            stats[matchedEmpName] = { name: cashierName, role: 'صيدلي 🩺', shiftsCount: 0, ordersCount: 0, totalHours: 0, regularHours: 0, holidayHours: 0, deliveryTimes: [], isWorkingNow: false, deductions: [] };
          }
          
          const shiftStart = new Date(shift.startTime);
          const shiftEnd = shift.endTime ? new Date(shift.endTime) : shiftStart;
          const durationHours = Math.max(0, (shiftEnd - shiftStart) / (1000 * 60 * 60));
          
          stats[matchedEmpName].shiftsCount += 1;
          const isHoliday = shift.isHoliday === true || shift.isHoliday === 'true';
          if (isHoliday) {
            stats[matchedEmpName].holidayHours += durationHours;
          } else {
            stats[matchedEmpName].regularHours += durationHours;
          }
          stats[matchedEmpName].totalHours += durationHours;
        }
      });

      loadedActiveShifts.forEach(shift => {
        if (!shift.startTime) return;
        const cashierName = shift.cashierName || 'غير مسجل';
        const cleanedCashier = cleanName(cashierName);
        
        let matchedEmpName = Object.keys(stats).find(k => cleanName(k) === cleanedCashier);
        if (!matchedEmpName) {
          matchedEmpName = cashierName;
          stats[matchedEmpName] = { name: cashierName, role: 'صيدلي 🩺', shiftsCount: 0, ordersCount: 0, totalHours: 0, regularHours: 0, holidayHours: 0, deliveryTimes: [], isWorkingNow: false, deductions: [] };
        }
        
        stats[matchedEmpName].isWorkingNow = true;

        const shiftLocalDate = getLocalDateString(shift.startTime);
        if (shiftLocalDate && shiftLocalDate >= finalStart && shiftLocalDate <= finalEnd) {
          const shiftStart = new Date(shift.startTime);
          const durationHours = Math.max(0, (new Date() - shiftStart) / (1000 * 60 * 60));
          stats[matchedEmpName].shiftsCount += 1;
          
          const isHoliday = shift.isHoliday === true || shift.isHoliday === 'true';
          if (isHoliday) {
            stats[matchedEmpName].holidayHours += durationHours;
          } else {
            stats[matchedEmpName].regularHours += durationHours;
          }
          stats[matchedEmpName].totalHours += durationHours;
        }
      });

      loadedSales.forEach(sale => {
        if (!sale.isDelivery || !sale.deliveryDetails?.driverName) return;
        if (!sale.timestamp) return;

        const driverName = sale.deliveryDetails.driverName;
        const cleanedDriver = cleanName(driverName);

        let matchedEmpName = Object.keys(stats).find(k => cleanName(k) === cleanedDriver);
        if (!matchedEmpName) {
          matchedEmpName = driverName;
          stats[matchedEmpName] = { name: driverName, role: 'طيار 🛵', shiftsCount: 0, ordersCount: 0, totalHours: 0, regularHours: 0, holidayHours: 0, deliveryTimes: [], isWorkingNow: false, deductions: [] };
        }

        const saleLocalDate = getLocalDateString(sale.timestamp);
        
        const todayLocalDate = getLocalDateString(new Date());
        if (saleLocalDate === todayLocalDate && (sale.deliveryStatus === 'pending' || sale.deliveryStatus === 'out')) {
          stats[matchedEmpName].isWorkingNow = true;
        }

        if (saleLocalDate && saleLocalDate >= finalStart && saleLocalDate <= finalEnd) {
          if (sale.deliveryStatus === 'delivered') {
            const saleTime = new Date(sale.timestamp);
            const endTime = sale.deliveredAt ? new Date(sale.deliveredAt) : new Date(saleTime.getTime() + 30 * 60 * 1000);
            const durationHours = Math.max(0, (endTime - saleTime) / (1000 * 60 * 60));
            const durationMins = durationHours * 60;

            stats[matchedEmpName].ordersCount += 1;
            
            const isHoliday = sale.isHoliday === true || sale.isHoliday === 'true';
            if (isHoliday) {
              stats[matchedEmpName].holidayHours += durationHours;
            } else {
              stats[matchedEmpName].regularHours += durationHours;
            }
            stats[matchedEmpName].totalHours += durationHours;
            stats[matchedEmpName].deliveryTimes.push(durationMins);
          }
        }
      });

      setReportData(Object.values(stats));
      setHasCalculated(true);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('💾 تم حفظ واحتساب تقارير ساعات العمل بنجاح!');
      }
    } catch (err) {
      console.error("Error calculating employee report:", err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('❌ فشل تحميل التقرير.');
      }
    } finally {
      if (setGlobalLoading) {
        setGlobalLoading({ active: false, title: '', message: '' });
      }
    }
  };

  const handleShortcutClick = (shortcut) => {
    setActiveShortcut(shortcut);
    const today = new Date();
    let start = new Date();
    const pad = (num) => String(num).padStart(2, '0');

    if (shortcut === 'today') {
      start = today;
    } else if (shortcut === 'week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 6 ? 0 : -day - 1);
      start = new Date(today.setDate(diff));
    } else if (shortcut === 'month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
    } else if (shortcut === '3months') {
      start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    }

    const startStr = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
    const endStr = `${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}-${pad(new Date().getDate())}`;

    setHoursStartDate(startStr);
    setHoursEndDate(endStr);
    
    handleCalculateReport(startStr, endStr);
  };

  const printShiftReport = (shift) => {
    const expenses = shift.expenses !== undefined ? shift.expenses : getShiftStats(shift).expenses;
    const extraIncomes = shift.extraIncomes !== undefined ? shift.extraIncomes : getShiftStats(shift).extraIncomes;
    const pharmacyName = currentUserData?.pharmacyName || 'صيدلية دكتور مصر';
    const shortId = currentUserData?.shortId || '';
    const printDate = new Date().toLocaleString('ar-EG');

    const shiftSales = sales.filter(sale => {
      if (!sale.timestamp) return false;
      const saleTime = new Date(sale.timestamp);
      const startTime = new Date(shift.startTime);
      const endTime = shift.endTime ? new Date(shift.endTime) : new Date();
      return saleTime >= startTime && saleTime <= endTime;
    });

    const aggregatedItems = {};
    shiftSales.forEach(sale => {
      if (sale.items) {
        sale.items.forEach(item => {
          if (aggregatedItems[item.barcode]) {
            aggregatedItems[item.barcode].quantity += item.quantity;
            aggregatedItems[item.barcode].totalPrice += item.price * item.quantity;
          } else {
            aggregatedItems[item.barcode] = {
              tradeName: item.tradeName,
              quantity: item.quantity,
              price: item.price,
              totalPrice: item.price * item.quantity
            };
          }
        });
      }
    });
    const itemsList = Object.values(aggregatedItems);

    let itemsRows = '';
    if (itemsList.length > 0) {
      itemsList.forEach((item, i) => {
        itemsRows += `<tr>
          <td>${i + 1}</td>
          <td>${item.tradeName}</td>
          <td>${item.quantity}</td>
          <td>${item.price.toFixed(2)} ج.م</td>
          <td>${item.totalPrice.toFixed(2)} ج.م</td>
        </tr>`;
      });
    } else {
      itemsRows = '<tr><td colspan="5" style="text-align:center; color:#888;">لم يتم بيع أي منتجات خلال هذه الوردية</td></tr>';
    }

    const totalSales = parseFloat(shift.cashSales || 0) + parseFloat(shift.creditSales || 0);
    const difference = parseFloat(shift.difference || 0);
    const diffColor = difference === 0 ? '#059669' : difference > 0 ? '#2563eb' : '#dc2626';
    const diffLabel = difference > 0 ? `📈 زائد +${difference.toFixed(2)} ج.م` : difference < 0 ? `📉 عجز ${difference.toFixed(2)} ج.م` : '✅ متطابق تماماً';

    let deliverySales = 0;
    shiftSales.forEach(sale => {
      if (sale.isDelivery && sale.deliveryStatus === 'delivered') {
        const saleAmt = sale.isInsurance ? (sale.patientShare !== undefined ? sale.patientShare : sale.grandTotal) : sale.grandTotal;
        deliverySales += saleAmt;
      }
    });

    const shiftDuration = shift.startTime && shift.endTime
      ? (() => {
          const ms = new Date(shift.endTime) - new Date(shift.startTime);
          const hours = Math.floor(ms / 3600000);
          const mins = Math.floor((ms % 3600000) / 60000);
          return `${hours} ساعة و ${mins} دقيقة`;
        })()
      : 'وردية مفتوحة';

    const html = `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
<meta charset="UTF-8">
<title>تقرير الوردية - ${shift.cashierName}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;900&display=swap');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Cairo', sans-serif; background: #f8fafc; color: #1e293b; direction: rtl; }
  .page { width: 210mm; min-height: 297mm; margin: 0 auto; background: #fff; padding: 20mm 15mm; }
  .header { text-align: center; border-bottom: 3px solid #6366f1; padding-bottom: 16px; margin-bottom: 20px; }
  .header h1 { font-size: 24px; font-weight: 900; color: #1e293b; margin-bottom: 4px; }
  .header .subtitle { color: #6366f1; font-size: 14px; font-weight: 600; }
  .header .pharmacy { font-size: 18px; font-weight: 700; color: #374151; margin-top: 6px; }
  .header .meta { font-size: 12px; color: #64748b; margin-top: 4px; }
  .doctor-badge { display: inline-flex; align-items: center; gap: 8px; background: linear-gradient(135deg, #6366f1, #8b5cf6); color: #fff; padding: 8px 20px; border-radius: 50px; font-size: 15px; font-weight: 700; margin: 14px auto 0; }
  .section-title { font-size: 15px; font-weight: 800; color: #1e293b; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 2px solid #e2e8f0; display: flex; align-items: center; gap: 8px; }
  .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 20px; }
  .stat-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; text-align: center; }
  .stat-card .label { font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 4px; }
  .stat-card .value { font-size: 20px; font-weight: 900; color: #1e293b; }
  .stat-card.green .value { color: #059669; }
  .stat-card.blue .value { color: #2563eb; }
  .stat-card.red .value { color: #dc2626; }
  .stat-card.purple .value { color: #7c3aed; }
  .timing-row { display: flex; justify-content: space-between; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 16px; margin-bottom: 8px; font-size: 13px; }
  .timing-row .key { color: #64748b; font-weight: 600; }
  .timing-row .val { font-weight: 700; color: #1e293b; }
  .difference-box { padding: 14px; border-radius: 12px; text-align: center; font-size: 20px; font-weight: 900; margin: 16px 0; border: 2px solid; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 20px; }
  thead tr { background: #6366f1; color: #fff; }
  thead th { padding: 10px 8px; font-weight: 700; text-align: center; }
  tbody tr:nth-child(even) { background: #f8fafc; }
  tbody td { padding: 8px; text-align: center; border-bottom: 1px solid #e2e8f0; }
  .notes-box { background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 12px 16px; margin-bottom: 16px; font-size: 13px; }
  .footer { text-align: center; color: #94a3b8; font-size: 11px; border-top: 1px solid #e2e8f0; padding-top: 12px; margin-top: 20px; }
  @media print {
    body { background: #fff; }
    .page { width: 100%; padding: 10mm; }
    .no-print { display: none !important; }
  }
  .print-btn { position: fixed; top: 20px; left: 20px; background: #6366f1; color: #fff; border: none; padding: 10px 24px; border-radius: 10px; font-family: Cairo, sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; z-index: 1000; }
  .print-btn:hover { background: #4f46e5; }
</style>
</head>
<body>
<button class="print-btn no-print" onclick="window.print()">🖨️ طباعة التقرير</button>
<div class="page">
  <div class="header">
    <h1>📊 تقرير الوردية الرسمي</h1>
    <div class="subtitle">نظام دكتور مصر لإدارة الصيدليات</div>
    <div class="pharmacy">${pharmacyName}${shortId ? ' — ' + shortId : ''}</div>
    <div class="meta">تاريخ طباعة التقرير: ${printDate}</div>
    <div class="doctor-badge">👨‍⚕️ د. ${shift.cashierName || 'غير مسجل'}</div>
  </div>

  <div class="section-title">⏱️ توقيتات الوردية</div>
  <div class="timing-row"><span class="key">وقت البدء:</span><span class="val">${shift.startTime ? new Date(shift.startTime).toLocaleString('ar-EG') : '—'}</span></div>
  <div class="timing-row"><span class="key">وقت الإغلاق:</span><span class="val">${shift.endTime ? new Date(shift.endTime).toLocaleString('ar-EG') : 'نشط الآن'}</span></div>
  <div class="timing-row"><span class="key">إجمالي مدة الوردية:</span><span class="val">${shiftDuration}</span></div>

  <br/>
  <div class="section-title">💰 الملخص المالي</div>
  <div class="stats-grid">
    <div class="stat-card"><div class="label">العهدة الافتتاحية</div><div class="value">${parseFloat(shift.startingCash || 0).toFixed(2)} ج.م</div></div>
    <div class="stat-card green"><div class="label">مبيعات كاش (نقدي)</div><div class="value">${parseFloat(shift.cashSales || 0).toFixed(2)} ج.م</div></div>
    <div class="stat-card blue"><div class="label">مبيعات شبكة / فيزا</div><div class="value">${parseFloat(shift.creditSales || 0).toFixed(2)} ج.م</div></div>
    <div class="stat-card blue"><div class="label">مبيعات دليفري (توصيل)</div><div class="value">${deliverySales.toFixed(2)} ج.م</div></div>
    <div class="stat-card green"><div class="label">إيرادات إضافية</div><div class="value">+${parseFloat(extraIncomes || 0).toFixed(2)} ج.م</div></div>
    <div class="stat-card red"><div class="label">المصروفات</div><div class="value">-${parseFloat(expenses || 0).toFixed(2)} ج.م</div></div>
    <div class="stat-card purple"><div class="label">إجمالي المبيعات</div><div class="value">${totalSales.toFixed(2)} ج.م</div></div>
  </div>

  <div class="timing-row"><span class="key">المبلغ الفعلي بالخزينة:</span><span class="val">${parseFloat(shift.actualCash || 0).toFixed(2)} ج.م</span></div>
  <div class="difference-box" style="color:${diffColor}; border-color:${diffColor}; background:${diffColor}11;">${diffLabel}</div>

  ${shift.closingNotes ? `<div class="notes-box"><strong>📝 ملاحظات الإغلاق:</strong><br/>${shift.closingNotes}</div>` : ''}

  <div class="section-title">🛒 المنتجات المباعة خلال الوردية (${itemsList.length} صنف)</div>
  <table>
    <thead><tr><th>#</th><th>اسم المنتج</th><th>الكمية</th><th>سعر الوحدة</th><th>الإجمالي</th></tr></thead>
    <tbody>${itemsRows}</tbody>
  </table>

  <div class="footer">
    نظام دكتور مصر — تم إنشاء هذا التقرير آلياً في ${printDate}
  </div>
</div>
<script>window.onload = function(){ window.print(); }<\/script>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  const getShiftStats = (shift) => {
    let cashSales = 0;
    let creditSales = 0;
    let expenses = 0;
    let extraIncomes = 0;

    const shiftSales = sales.filter(sale => {
      if (!sale.timestamp) return false;
      if (sale.shiftId) {
        return sale.shiftId === shift.id;
      }
      return new Date(sale.timestamp) >= new Date(shift.startTime);
    });

    const shiftTxs = accountingTx.filter(tx => {
      if (!tx.timestamp) return false;
      if (tx.shiftId) return tx.shiftId === shift.id;
      const txDate = new Date(tx.timestamp);
      return txDate >= new Date(shift.startTime) && (!shift.endTime || txDate <= new Date(shift.endTime));
    });

    shiftSales.forEach(sale => {
      if (sale.isDelivery && sale.deliveryStatus !== 'delivered') return;
      if (sale.paymentMethod === 'split') {
        cashSales += sale.splitAmounts?.cash || 0;
        creditSales += sale.splitAmounts?.card || 0;
      } else {
        const saleAmt = sale.isInsurance ? (sale.patientShare !== undefined ? sale.patientShare : sale.grandTotal) : sale.grandTotal;
        if (sale.paymentMethod === 'deferred') {
          // Deferred sales don't add to cash or credit sales in the shift expected cash
        } else if (sale.paymentMethod === 'credit' || sale.paymentMethod === 'visa' || sale.paymentMethod === 'network' || sale.paymentMethod === 'instapay' || sale.paymentMethod === 'vodafone') {
          creditSales += saleAmt;
        } else {
          cashSales += saleAmt;
        }
      }
    });

    shiftTxs.forEach(tx => {
      if (tx.type === 'expense') {
        expenses += parseFloat(tx.amount || 0);
      } else if (tx.type === 'revenue') {
        extraIncomes += parseFloat(tx.amount || 0);
      }
    });

    const expectedCash = parseFloat(shift.startingCash || 0) + cashSales + extraIncomes - expenses;
    return { cashSales, creditSales, expenses, extraIncomes, expectedCash };
  };

  return (
    <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Sub-tab Switcher Bar */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '16px' }}>
        <button 
          onClick={() => setShiftsTab('shifts')} 
          className={`delivery-tab-btn ${shiftsTab === 'shifts' ? 'active' : ''}`}
          style={{ padding: '10px 24px', fontSize: '0.92rem', fontWeight: 'bold', borderRadius: '12px', border: 'none', cursor: 'pointer' }}
        >
          ⏱️ إدارة الورديات وساعات العمل
        </button>
        <button 
          onClick={() => setShiftsTab('incentives')} 
          className={`delivery-tab-btn ${shiftsTab === 'incentives' ? 'active' : ''}`}
          style={{ padding: '10px 24px', fontSize: '0.92rem', fontWeight: 'bold', borderRadius: '12px', border: 'none', cursor: 'pointer' }}
        >
          🏆 نظام الحوافز والأهداف والتارجت
        </button>

      </div>

      {shiftsTab === 'incentives' ? (
        <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Target Settings Card */}
          <div className="premium-card" style={{ borderRight: '5px solid var(--primary)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🎯 تحديد أهداف وتارجت جديد للموظفين والطيارين
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '20px' }}>
              حدد تارجت مبيعات معين للصيادلة أو تارجت أوردرات للطيارين لتشجيعهم، وقم بتحديد مكافأة مالية وحافز فوري لهم عند تحقيق الهدف.
            </p>

            <form onSubmit={handleAddTarget} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', alignItems: 'end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>اختر الموظف أو الطيار</label>
                <select 
                  className="premium-input" 
                  value={targetEmployeeId} 
                  onChange={e => setTargetEmployeeId(e.target.value)}
                  required
                >
                  <option value="">-- اختر موظف --</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.role === 'driver' || emp.role === 'طيار' ? `🛵 ${emp.name} (طيار)` : `👨‍⚕️ ${emp.name} (صيدلي)`}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>نوع التارجت والهدف</label>
                <select 
                  className="premium-input" 
                  value={targetType} 
                  onChange={e => setTargetType(e.target.value)}
                  required
                >
                  <option value="sales_amount">إجمالي قيمة المبيعات (ج.م)</option>
                  <option value="orders_count">عدد أوردرات البيع / التوصيل</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>قيمة الهدف المطلوب</label>
                <input 
                  type="number" 
                  className="premium-input" 
                  placeholder="مثال: 5000 أو 50" 
                  value={targetValue} 
                  onChange={e => setTargetValue(e.target.value)} 
                  required 
                  min="1"
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>قيمة الحافز المالي (ج.م)</label>
                <input 
                  type="number" 
                  className="premium-input" 
                  placeholder="مثال: 500" 
                  value={targetBonus} 
                  onChange={e => setTargetBonus(e.target.value)} 
                  required 
                  min="1"
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>تاريخ البدء</label>
                <input 
                  type="date" 
                  className="premium-input" 
                  value={targetStartDate} 
                  onChange={e => setTargetStartDate(e.target.value)} 
                  required 
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>تاريخ الانتهاء</label>
                <input 
                  type="date" 
                  className="premium-input" 
                  value={targetEndDate} 
                  onChange={e => setTargetEndDate(e.target.value)} 
                  required 
                />
              </div>

              <button type="submit" className="gradient-btn" style={{ height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                ➕ حفظ وتفعيل الهدف
              </button>
            </form>
          </div>

          {/* Targets Progress Tracking Dashboard */}
          <div className="premium-card" style={{ borderRight: '5px solid var(--accent-green)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              📈 شاشة متابعة تحقيق التارجت واستحقاق الحوافز
            </h3>

            {isTargetsLoading ? (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>جاري تحميل أهداف الموظفين...</div>
            ) : targets.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: '16px', color: 'var(--text-secondary)' }}>
                🤷‍♂️ لا توجد أهداف أو تارجت مفعل حالياً. استخدم النموذج أعلاه لإضافة أول هدف للموظفين.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                {targets.map(t => {
                  const currentProgress = getTargetProgress(t);
                  const pct = Math.min(100, Math.round((currentProgress / t.value) * 100)) || 0;
                  const isAchieved = currentProgress >= t.value;
                  const isExpired = new Date() > new Date(t.endDate + 'T23:59:59');

                  if (editingTargetId === t.id) {
                    return (
                      <div key={t.id} className="premium-card" style={{ background: 'rgba(8, 13, 27, 0.45)', border: '1px solid var(--primary)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                          <span style={{ fontWeight: 800, color: '#fff', fontSize: '1.1rem' }}>
                            ✏️ تعديل هدف: {t.employeeName}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.88rem' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>قيمة الهدف المطلوب:</label>
                            <input 
                              type="number" 
                              className="premium-input" 
                              value={editTargetValue}
                              onChange={e => setEditTargetValue(e.target.value)}
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>قيمة الحافز المالي (ج.م):</label>
                            <input 
                              type="number" 
                              className="premium-input" 
                              value={editTargetBonus}
                              onChange={e => setEditTargetBonus(e.target.value)}
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>تاريخ البدء:</label>
                            <input 
                              type="date" 
                              className="premium-input" 
                              value={editTargetStartDate}
                              onChange={e => setEditTargetStartDate(e.target.value)}
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>تاريخ الانتهاء:</label>
                            <input 
                              type="date" 
                              className="premium-input" 
                              value={editTargetEndDate}
                              onChange={e => setEditTargetEndDate(e.target.value)}
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                          <button 
                            onClick={() => handleSaveTargetEdits(t.id)} 
                            className="premium-btn-primary" 
                            style={{ padding: '8px 16px', fontSize: '0.82rem', fontWeight: 'bold', width: 'auto' }}
                          >
                            💾 حفظ التغييرات
                          </button>
                          <button 
                            onClick={() => setEditingTargetId(null)} 
                            className="gradient-btn-secondary" 
                            style={{ padding: '8px 16px', fontSize: '0.82rem', color: '#f87171', width: 'auto' }}
                          >
                            إلغاء
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={t.id} className="premium-card" style={{ background: 'rgba(8, 13, 27, 0.45)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                        <span style={{ fontWeight: 800, color: '#fff', fontSize: '1.1rem' }}>
                          {t.employeeRole === 'driver' || t.employeeRole === 'طيار' ? '🛵' : '👨‍⚕️'} {t.employeeName}
                        </span>
                        {isAchieved ? (
                          <span className="status-badge success" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                            🏆 تم التحقيق
                          </span>
                        ) : isExpired ? (
                          <span className="status-badge error" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                            ⌛ انتهت المدة
                          </span>
                        ) : (
                          <span className="status-badge warning" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                            ⏳ جاري المتابعة
                          </span>
                        )}
                      </div>

                      <div style={{ fontSize: '0.88rem', display: 'flex', flexDirection: 'column', gap: '8px', color: 'var(--text-secondary)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>نوع التارجت المطلوب:</span>
                          <span style={{ color: '#fff', fontWeight: 600 }}>
                            {t.type === 'sales_amount' ? 'إجمالي قيمة المبيعات' : 'عدد أوردرات البيع / التوصيل'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>المكافأة والحافز المالي:</span>
                          <span style={{ color: 'var(--accent-green)', fontWeight: 700, fontSize: '1rem' }}>
                            💵 {t.bonus} ج.م
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>الفترة المحددة:</span>
                          <span style={{ color: '#fff', fontSize: '0.8rem' }}>
                            {t.startDate} ⬅️ {t.endDate}
                          </span>
                        </div>
                      </div>

                      {/* Progress Bar Container */}
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '6px', fontWeight: 'bold' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>نسبة الإنجاز:</span>
                          <span style={{ color: isAchieved ? '#34d399' : 'var(--primary)' }}>{pct}%</span>
                        </div>
                        <div style={{ width: '100%', height: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '99px', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: isAchieved ? 'linear-gradient(90deg, #10b981, #34d399)' : 'linear-gradient(90deg, var(--primary), var(--primary-light, #818cf8))', transition: 'width 0.4s ease' }}></div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginTop: '6px', color: 'rgba(255, 255, 255, 0.5)' }}>
                          <span>الحالي: {currentProgress} {t.type === 'sales_amount' ? 'ج.م' : 'أوردر'}</span>
                          <span>الهدف: {t.value} {t.type === 'sales_amount' ? 'ج.م' : 'أوردر'}</span>
                        </div>
                      </div>

                      {isAchieved && (
                        <div style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.2)', padding: '10px', borderRadius: '10px', textAlign: 'center', marginTop: '4px' }}>
                          <span style={{ fontSize: '0.85rem', color: '#34d399', fontWeight: 'bold' }}>
                            🎉 تهانينا! استحق {t.employeeName} المكافأة بقيمة {t.bonus} ج.م
                          </span>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                        <button 
                          onClick={() => handleOpenEditTarget(t)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#38bdf8',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          ✏️ تعديل الهدف
                        </button>
                        <button 
                          onClick={() => handleDeleteTarget(t.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#f87171',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          🗑️ حذف الهدف
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {/* مراقبة الورديات النشطة حالياً */}
          <div className="premium-card" style={{ borderRight: '5px solid var(--accent-green)' }}>
            <h3 style={{ fontSize: '1.30rem', fontWeight: 800, color: '#fff', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              مراقبة الورديات النشطة حالياً
            </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '20px' }}>
          مراقبة تفاصيل وإحصائيات الورديات المفتوحة لجميع الأطباء والموظفين على نفس الحساب في نفس الوقت بشكل حي وتفاعلي.
        </p>

        {activeShifts.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', background: 'rgba(255,255,255,0.02)', borderRadius: '16px', border: '1px dashed var(--border)' }}>
            <p style={{ color: 'var(--text-secondary)', marginTop: '8px', marginBottom: 0 }}>لا يوجد أي ورديات مفتوحة حالياً.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
            {activeShifts.map((shift) => {
              const { cashSales, creditSales, expectedCash } = getShiftStats(shift);
              return (
                <div key={shift.id} className="premium-card" style={{ background: 'rgba(8, 13, 27, 0.4)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: 800, color: '#fff', fontSize: '1.1rem' }}> {shift.cashierName}</span>
                    <span className="status-badge success">نشط الآن</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>توقيت البدء:</span>
                      <span style={{ color: '#fff', fontWeight: 600 }}>{new Date(shift.startTime).toLocaleString('ar-EG')}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>مبلغ البداية:</span>
                      <span style={{ color: '#fff', fontWeight: 600 }}>{shift.startingCash} ج.م</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>مبيعات كاش:</span>
                      <span style={{ color: 'var(--accent-green)', fontWeight: 700 }}>{cashSales} ج.م</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>مبيعات شبكة/فيزا:</span>
                      <span style={{ color: 'var(--primary)', fontWeight: 700 }}>{creditSales} ج.م</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed var(--border)', paddingTop: '8px', fontSize: '1rem' }}>
                      <span style={{ fontWeight: 700 }}>المتوقع بالخزينة:</span>
                      <span style={{ color: '#fff', fontWeight: 800 }}>{expectedCash} ج.م</span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', borderTop: '1px solid var(--border)', paddingTop: '10px', marginTop: '4px' }}>
                      <button 
                        onClick={() => {
                          setClosingShift(shift);
                          setActualCash('');
                          setClosingNotes('');
                          setClosingStatusMsg('');
                        }}
                        className="gradient-btn"
                        style={{ 
                          flex: 1, 
                          padding: '10px', 
                          borderRadius: '10px', 
                          fontSize: '0.85rem', 
                          fontWeight: 'bold',
                          background: 'linear-gradient(135deg, #f43f5e 0%, #be123c 100%)',
                          boxShadow: '0 4px 12px rgba(244, 63, 94, 0.2)',
                          border: 'none',
                          color: '#fff',
                          cursor: 'pointer'
                        }}
                      >
                        🔒 إغلاق الوردية عن بُعد
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* تقرير ساعات عمل الموظفين والطيارين */}
      <div className="premium-card" style={{ borderRight: '5px solid var(--primary)', background: 'linear-gradient(145deg, rgba(13, 20, 36, 0.9) 0%, rgba(8, 13, 27, 0.95) 100%)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '16px' }}>
          <div>
            <h3 style={{ fontSize: '1.30rem', fontWeight: 800, color: '#fff', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              📊 تقرير إنتاجية وساعات عمل الموظفين والطيارين
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
              متابعة وتحليل دقيق لساعات عمل الصيادلة (من الورديات) وزمن توصيل وتحصيل أوردرات الطيارين على مدار أي فترة يحددها المستخدم.
            </p>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            {/* Shortcut Buttons */}
            <div style={{ display: 'flex', gap: '6px', background: 'rgba(255,255,255,0.02)', padding: '4px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <button 
                onClick={() => handleShortcutClick('today')}
                className={`delivery-tab-btn ${activeShortcut === 'today' ? 'active' : ''}`}
                style={{ padding: '6px 12px', fontSize: '0.8rem', height: '32px', borderRadius: '8px' }}
              >
                اليوم 📅
              </button>
              <button 
                onClick={() => handleShortcutClick('week')}
                className={`delivery-tab-btn ${activeShortcut === 'week' ? 'active' : ''}`}
                style={{ padding: '6px 12px', fontSize: '0.8rem', height: '32px', borderRadius: '8px' }}
              >
                الأسبوع 📆
              </button>
              <button 
                onClick={() => handleShortcutClick('month')}
                className={`delivery-tab-btn ${activeShortcut === 'month' ? 'active' : ''}`}
                style={{ padding: '6px 12px', fontSize: '0.8rem', height: '32px', borderRadius: '8px' }}
              >
                الشهر 🗓️
              </button>
              <button 
                onClick={() => handleShortcutClick('3months')}
                className={`delivery-tab-btn ${activeShortcut === '3months' ? 'active' : ''}`}
                style={{ padding: '6px 12px', fontSize: '0.8rem', height: '32px', borderRadius: '8px' }}
              >
                ٣ أشهر 📊
              </button>
              <button 
                onClick={() => setActiveShortcut('custom')}
                className={`delivery-tab-btn ${activeShortcut === 'custom' ? 'active' : ''}`}
                style={{ padding: '6px 12px', fontSize: '0.8rem', height: '32px', borderRadius: '8px' }}
              >
                فترة مخصصة ✍️
              </button>
            </div>

            {/* Custom Date Pickers */}
            {activeShortcut === 'custom' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }} className="fade-in-content">
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>من:</span>
                  <input 
                    type="date" 
                    className="premium-input" 
                    value={hoursStartDate} 
                    onChange={(e) => { setHoursStartDate(e.target.value); setHasCalculated(false); }} 
                    style={{ padding: '6px 12px', borderRadius: '10px', fontSize: '0.85rem', color: '#fff', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>إلى:</span>
                  <input 
                    type="date" 
                    className="premium-input" 
                    value={hoursEndDate} 
                    onChange={(e) => { setHoursEndDate(e.target.value); setHasCalculated(false); }} 
                    style={{ padding: '6px 12px', borderRadius: '10px', fontSize: '0.85rem', color: '#fff', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                </div>
              </div>
            )}

            {(hasCalculated || activeShortcut !== 'custom') && (
              <button 
                onClick={() => handleCalculateReport()} 
                className="gradient-btn" 
                style={{ padding: '8px 18px', fontSize: '0.85rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                🔄 تحديث الحساب
              </button>
            )}
          </div>
        </div>

        {!hasCalculated ? (
          <div style={{ 
            padding: '48px 24px', 
            textAlign: 'center', 
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.03) 0%, rgba(8, 13, 27, 0.4) 100%)', 
            borderRadius: '20px', 
            border: '1px dashed rgba(99, 102, 241, 0.2)' 
          }}>
            <span style={{ fontSize: '3rem', display: 'block', marginBottom: '16px' }}>📊</span>
            <h4 style={{ color: '#fff', fontSize: '1.15rem', fontWeight: 'bold', marginBottom: '8px' }}>تقرير إنتاجية الموظفين جاهز للاحتساب</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', maxWidth: '460px', margin: '0 auto 20px', lineHeight: '1.6' }}>
              حدد نطاق الفترة الزمنية المطلوبة عبر الأزرار السريعة أو الفترة المخصصة، ثم اضغط على زر "حفظ واحتساب التقرير" لاستعراض تقرير الموظفين والطيارين وساعات عملهم.
            </p>
            <button 
              onClick={() => handleCalculateReport()} 
              className="gradient-btn" 
              style={{ padding: '12px 32px', fontSize: '0.95rem', fontWeight: 'bold' }}
            >
              💾 حفظ واحتساب التقرير الفوري ⚡
            </button>
          </div>
        ) : reportData.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', background: 'rgba(255,255,255,0.01)', borderRadius: '16px', border: '1px dashed rgba(255,255,255,0.1)' }}>
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>لا توجد بيانات عمل أو توصيل مسجلة خلال الفترة المحددة.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="premium-table">
              <thead>
                <tr>
                  <th>حالة العمل اليوم 🕒</th>
                  <th>اسم الموظف / الطيار</th>
                  <th>الدور الوظيفي</th>
                  <th>عدد المهام / الورديات</th>
                  <th>تفصيل الساعات (عادية / أعياد)</th>
                  <th>معدل زمن المهمة / التوصيل</th>
                  <th>المستحقات المالية للفترة 💰</th>
                  <th>الكفاءة والنشاط</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((emp, idx) => {
                  const roundedHours = Math.round(emp.totalHours * 100) / 100;
                  let avgDeliveryText = '—';
                  if (emp.role.includes('طيار') && emp.deliveryTimes.length > 0) {
                    const sum = emp.deliveryTimes.reduce((a, b) => a + b, 0);
                    const avg = Math.round(sum / emp.deliveryTimes.length);
                    avgDeliveryText = `${avg} دقيقة`;
                  } else if (emp.role.includes('صيدلي') && emp.shiftsCount > 0) {
                    const avg = Math.round((emp.totalHours / emp.shiftsCount) * 10) / 10;
                    avgDeliveryText = `${avg} ساعة / وردية`;
                  }

                  let efficiencyBadge = <span className="status-badge success" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.2)' }}>نشط</span>;
                  if (emp.totalHours > 160) {
                    efficiencyBadge = <span className="status-badge success" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.2)' }}>كفاءة ممتازة ⚡</span>;
                  } else if (emp.role.includes('طيار') && emp.ordersCount > 15) {
                    efficiencyBadge = <span className="status-badge success" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#fbbf24', border: '1px solid rgba(245, 158, 11, 0.2)' }}>نشط جداً 🏍️</span>;
                  } else if (emp.totalHours === 0 && emp.shiftsCount === 0 && emp.ordersCount === 0) {
                    efficiencyBadge = <span className="status-badge error" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.2)' }}>لم يعمل 💤</span>;
                  }

                  // Work status today
                  const statusBadge = emp.isWorkingNow ? (
                    <span className="status-badge success" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', display: 'inline-block' }}></span>
                      يعمل اليوم 🟢
                    </span>
                  ) : (
                    <span className="status-badge error" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(255, 255, 255, 0.03)', color: 'var(--text-secondary)', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--text-secondary)', display: 'inline-block' }}></span>
                      غير نشط 💤
                    </span>
                  );

                  return (
                    <tr key={idx} style={{ transition: 'all 0.2s ease' }}>
                      <td>{statusBadge}</td>
                      <td 
                        onClick={() => {
                          const fullEmp = employees.find(e => e.name === emp.name) || emp;
                          setSelectedEmployeeForModal(fullEmp);
                        }}
                        style={{ 
                          fontWeight: 'bold', 
                          color: 'var(--primary)', 
                          fontSize: '0.95rem', 
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textDecorationStyle: 'dashed'
                        }}
                        title="انقر لعرض كامل تفاصيل الموظف، وحساب الراتب، وإدارة الخصومات والجزاءات"
                      >
                        {emp.name}
                      </td>
                      <td>
                        <span style={{ 
                          padding: '4px 10px', 
                          borderRadius: '8px', 
                          fontSize: '0.8rem', 
                          fontWeight: 'bold',
                          background: emp.role.includes('صيدلي') ? 'rgba(99, 102, 241, 0.15)' : 'rgba(56, 189, 248, 0.15)',
                          color: emp.role.includes('صيدلي') ? 'var(--primary)' : '#38bdf8'
                        }}>
                          {emp.role}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600, fontFamily: 'Outfit' }}>
                        <div>الفترة: {emp.role.includes('صيدلي') ? `${emp.shiftsCount} وردية` : `${emp.ordersCount} أوردر`}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '2px' }}>الكلية: {emp.role.includes('صيدلي') ? `${emp.firebaseShiftsCount || 0} وردية` : `${emp.ordersCount || 0} أوردر`}</div>
                      </td>
                      <td style={{ fontWeight: 800, color: emp.totalHours > 0 ? 'var(--accent-green)' : 'var(--text-secondary)', fontFamily: 'Outfit' }}>
                        <div style={{ fontSize: '0.85rem' }}>عادية: {Math.round(emp.regularHours * 100) / 100} س</div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--accent-yellow)', marginTop: '2px' }}>أعياد: {Math.round(emp.holidayHours * 100) / 100} س</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px', borderTop: '1px dashed rgba(255,255,255,0.05)', paddingTop: '2px' }}>الكلية: {Math.round((emp.firebaseWorkedHours || 0) * 100) / 100} ساعة</div>
                      </td>
                      <td style={{ fontWeight: 600, color: emp.totalHours > 0 ? '#f59e0b' : 'var(--text-secondary)', fontFamily: 'Outfit' }}>
                        {avgDeliveryText}
                      </td>
                      <td style={{ fontWeight: '900', color: '#fff', fontFamily: 'Outfit' }}>
                        {(() => {
                          const deductionsList = emp.deductions || [];
                          let totalDeductionsAmount = 0;
                          let totalDeductionsHours = 0;
                          
                          deductionsList.forEach(d => {
                            if (d.type === 'hours') {
                              totalDeductionsHours += parseFloat(d.value) || 0;
                            } else {
                              totalDeductionsAmount += parseFloat(d.value) || 0;
                            }
                          });

                          let periodPay = 0;
                          let explanation = '';
                          
                          if (emp.role.includes('طيار')) {
                            const regRate = parseFloat(emp.regularRate) || 0;
                            const commRate = parseFloat(emp.commissionPerOrder) || 0;
                            const holidayRate = regRate * (parseFloat(emp.holidayMultiplier) || 1.5);
                            
                            const adjustedRegularHours = Math.max(0, emp.regularHours - totalDeductionsHours);
                            const adjustedHolidayHours = Math.max(0, emp.holidayHours - Math.max(0, totalDeductionsHours - emp.regularHours));
                            
                            const hoursPay = (adjustedRegularHours * regRate) + (adjustedHolidayHours * holidayRate);
                            const commPay = emp.ordersCount * commRate;
                            
                            periodPay = Math.max(0, hoursPay + commPay - totalDeductionsAmount);
                            explanation = `(${adjustedRegularHours.toFixed(1)} س عادية + ${adjustedHolidayHours.toFixed(1)} س عطلات + ${emp.ordersCount} طلبات)`;
                          } else {
                            const val = parseFloat(emp.salaryValue) || 0;
                            if (emp.salaryType === 'hourly') {
                              const holidayRate = val * (parseFloat(emp.holidayMultiplier) || 1.5);
                              const adjustedRegularHours = Math.max(0, emp.regularHours - totalDeductionsHours);
                              const adjustedHolidayHours = Math.max(0, emp.holidayHours - Math.max(0, totalDeductionsHours - emp.regularHours));
                              
                              periodPay = Math.max(0, (adjustedRegularHours * val) + (adjustedHolidayHours * holidayRate) - totalDeductionsAmount);
                              explanation = `(${adjustedRegularHours.toFixed(1)} س عادية × ${val} ج.م + ${adjustedHolidayHours.toFixed(1)} س عطلات × ${holidayRate.toFixed(1)} ج.م)`;
                            } else {
                              const hourlyEstimated = val / (30 * 8);
                              const holidayRate = hourlyEstimated * (parseFloat(emp.holidayMultiplier) || 1.5);
                              const extraHolidayBonus = emp.holidayHours * holidayRate;
                              periodPay = Math.max(0, val + extraHolidayBonus - totalDeductionsAmount);
                              explanation = `(راتب شهري: ${val} ج.م + حافز عطلات: ${extraHolidayBonus.toFixed(1)} ج.م)`;
                            }
                          }
                          
                          return (
                            <div>
                              <div style={{ color: 'var(--accent-green)', fontSize: '1.05rem', fontWeight: '900' }}>{periodPay.toFixed(2)} ج.م</div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '2px', fontWeight: 'normal' }}>{explanation}</div>
                            </div>
                          );
                        })()}
                      </td>
                      <td>{efficiencyBadge}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

{/* سجل حركات وإغلاق الورديات السابقة */}
      {(() => {
        const filteredHistory = shiftsHistory.filter(shift => {
          if (!searchTerm.trim()) return true;
          const term = searchTerm.toLowerCase();
          const matchesName = shift.cashierName?.toLowerCase().includes(term);
          const dateStrAr = shift.startTime ? new Date(shift.startTime).toLocaleDateString('ar-EG') : '';
          const dateStrEn = shift.startTime ? new Date(shift.startTime).toLocaleDateString('en-US') : '';
          const matchesDate = dateStrAr.includes(term) || dateStrEn.includes(term);
          return matchesName || matchesDate;
        });

        const totalPages = Math.ceil(filteredHistory.length / 4) || 1;
        const pagedData = filteredHistory.slice((page - 1) * 4, page * 4);

        return (
          <div className="premium-card">
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  سجل حركات وإغلاق الورديات السابقة
                </h3>
                <button
                  onClick={() => fetchShiftsHistory(true)}
                  disabled={isShiftsHistoryLoading}
                  className="premium-btn"
                  style={{
                    padding: '6px 14px',
                    fontSize: '0.85rem',
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)'}
                >
                  {isShiftsHistoryLoading ? '🔄 جاري الاستدعاء...' : '🔄 تحديث سجل الورديات'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: '10px', width: '350px', maxWidth: '100%' }}>
                <input
                  type="text"
                  placeholder="ابحث باليوم أو اسم الدكتور..."
                  className="premium-input"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                  style={{ padding: '8px 12px', borderRadius: '10px', width: '100%' }}
                />
              </div>
            </div>

            {pagedData.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '16px' }}>لا يوجد أي سجلات ورديات مطابقة.</p>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="premium-table">
                    <thead>
                      <tr>
                        <th>توقيت البدء</th>
                        <th>توقيت الإغلاق</th>
                        <th>الدكتور المسؤول</th>
                        <th>عهدة البداية</th>
                        <th>مبيعات كاش</th>
                        <th>المتوقع بالخزينة</th>
                        <th>الفعلي بالخزينة</th>
                        <th>العجز / الزيادة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedData.map((shift, idx) => (
                        <tr key={shift.id || idx} onClick={() => setSelectedShift(shift)} style={{ cursor: 'pointer' }} title="انقر لعرض كامل التفاصيل والمنتجات المباعة">
                          <td>{new Date(shift.startTime).toLocaleString('ar-EG')}</td>
                          <td>{shift.endTime ? new Date(shift.endTime).toLocaleString('ar-EG') : '—'}</td>
                          <td style={{ fontWeight: 700 }}>{shift.cashierName}</td>
                          <td>{shift.startingCash} ج.م</td>
                          <td>{formatMoney(shift.cashSales)} ج.م</td>
                          <td>{formatMoney(shift.expectedCash)} ج.م</td>
                          <td style={{ fontWeight: 700 }}>{formatMoney(shift.actualCash)} ج.م</td>
                          <td style={{ color: (shift.difference || 0) === 0 ? 'var(--accent-green)' : (shift.difference || 0) > 0 ? 'var(--primary)' : 'var(--accent-pink)', fontWeight: 700 }}>
                            {(shift.difference || 0) >= 0 ? '+' : ''}{formatMoney(shift.difference)} ج.م
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '10px 0', borderTop: '1px dashed var(--border)' }}>
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="gradient-btn-secondary" style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
                      ⬅️ الصفحة السابقة
                    </button>
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                      صفحة <strong>{page}</strong> من <strong>{totalPages}</strong>
                    </span>
                    <button onClick={() => setPage(p => Math.max(1, p + 1))} disabled={page === totalPages} className="gradient-btn-secondary" style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
                      الصفحة التالية ➡️
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}
        </div>
      )}

      {/* تفاصيل الوردية السابقة المحددة */}
      {selectedShift && createPortal((() => {
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '20px 0', overflowY: 'auto' }}>
            <div className="premium-card fade-in-scale" style={{ width: '100%', maxWidth: '850px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '24px', position: 'relative', margin: 'auto', padding: '28px', boxShadow: '0 12px 40px rgba(0,0,0,0.6)', fontFamily: 'Cairo, sans-serif', direction: 'rtl' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '14px', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                تفاصيل الوردية: د. {selectedShift.cashierName}
              </h3>
              <button onClick={() => setSelectedShift(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
            </div>

            {(() => {
              const expenses = selectedShift.expenses !== undefined ? selectedShift.expenses : getShiftStats(selectedShift).expenses;
              const extraIncomes = selectedShift.extraIncomes !== undefined ? selectedShift.extraIncomes : getShiftStats(selectedShift).extraIncomes;

              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>توقيت البدء:</span>
                <div style={{ fontWeight: 700, color: '#fff', fontSize: '1rem', marginTop: '4px' }}>{new Date(selectedShift.startTime).toLocaleString('ar-EG')}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>توقيت الإغلاق:</span>
                <div style={{ fontWeight: 700, color: '#fff', fontSize: '1rem', marginTop: '4px' }}>{selectedShift.endTime ? new Date(selectedShift.endTime).toLocaleString('ar-EG') : 'نشط الآن'}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>العهدة الافتتاحية:</span>
                <div style={{ fontWeight: 700, color: '#fff', fontSize: '1.1rem', marginTop: '4px' }}>{selectedShift.startingCash} ج.م</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>مبيعات الكاش:</span>
                <div style={{ fontWeight: 700, color: 'var(--accent-green)', fontSize: '1.1rem', marginTop: '4px' }}>{selectedShift.cashSales || 0} ج.م</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>إيرادات إضافية:</span>
                <div style={{ fontWeight: 700, color: 'var(--accent-green)', fontSize: '1.1rem', marginTop: '4px' }}>+{extraIncomes || 0} ج.م</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>المصروفات:</span>
                <div style={{ fontWeight: 700, color: '#fb7185', fontSize: '1.1rem', marginTop: '4px' }}>-{expenses || 0} ج.م</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>المبلغ المتوقع بالخزينة:</span>
                <div style={{ fontWeight: 700, color: '#fff', fontSize: '1.1rem', marginTop: '4px' }}>{selectedShift.expectedCash || 0} ج.م</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>المبلغ الفعلي بالدرج:</span>
                <div style={{ fontWeight: 700, color: '#fff', fontSize: '1.1rem', marginTop: '4px' }}>{selectedShift.actualCash || 0} ج.م</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)', gridColumn: 'span 2' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>العجز / الزيادة:</span>
                <div style={{ fontWeight: 800, color: (selectedShift.difference || 0) === 0 ? 'var(--accent-green)' : (selectedShift.difference || 0) > 0 ? 'var(--primary)' : 'var(--accent-pink)', fontSize: '1.25rem', marginTop: '4px' }}>
                  {(selectedShift.difference || 0) >= 0 ? '+' : ''}{formatMoney(selectedShift.difference)} ج.م
                </div>
              </div>
            </div>
              );
            })()}

            {selectedShift.closingNotes && (
              <div style={{ background: 'rgba(56, 189, 248, 0.05)', border: '1px solid rgba(56, 189, 248, 0.15)', padding: '14px', borderRadius: '12px', marginBottom: '24px', fontSize: '0.95rem' }}>
                <strong style={{ color: '#fff', display: 'block', marginBottom: '4px' }}>ملاحظات الإغلاق:</strong>
                <span style={{ color: 'var(--text-secondary)' }}>{selectedShift.closingNotes}</span>
              </div>
            )}

            <h4 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              المنتجات التي تم بيعها خلال الوردية
            </h4>

            {(() => {
              const shiftSales = sales.filter(sale => {
                if (!sale.timestamp) return false;
                const saleTime = new Date(sale.timestamp);
                const startTime = new Date(selectedShift.startTime);
                const endTime = selectedShift.endTime ? new Date(selectedShift.endTime) : new Date();
                return saleTime >= startTime && saleTime <= endTime;
              });

              const aggregatedItems = {};
              shiftSales.forEach(sale => {
                if (sale.items) {
                  sale.items.forEach(item => {
                    if (aggregatedItems[item.barcode]) {
                      aggregatedItems[item.barcode].quantity += item.quantity;
                      aggregatedItems[item.barcode].totalPrice += item.price * item.quantity;
                    } else {
                      aggregatedItems[item.barcode] = {
                        barcode: item.barcode,
                        tradeName: item.tradeName,
                        quantity: item.quantity,
                        price: item.price,
                        totalPrice: item.price * item.quantity
                      };
                    }
                  });
                }
              });

              const itemsList = Object.values(aggregatedItems);

              if (itemsList.length === 0) {
                return <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '16px', background: 'rgba(255,255,255,0.01)', borderRadius: '12px' }}>لم يتم بيع أي منتجات خلال هذه الوردية.</p>;
              }

              return (
                <div style={{ overflowX: 'auto', maxHeight: '250px', border: '1px solid var(--border)', borderRadius: '12px' }}>
                  <table className="premium-table" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th>اسم المنتج</th>
                        <th>الكمية المباعة</th>
                        <th>السعر الفردي</th>
                        <th>إجمالي القيمة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsList.map((item, index) => (
                        <tr key={index}>
                          <td style={{ fontWeight: 700 }}>{item.tradeName}</td>
                          <td>{item.quantity}</td>
                          <td>{item.price} ج.م</td>
                          <td style={{ fontWeight: 700, color: 'var(--accent-green)' }}>{item.totalPrice} ج.م</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => printShiftReport(selectedShift)}
                className="gradient-btn"
                style={{ padding: '10px 24px', borderRadius: '12px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                🖨️ طباعة تقرير الوردية
              </button>
              <button onClick={() => setSelectedShift(null)} className="gradient-btn-secondary" style={{ padding: '10px 24px', borderRadius: '12px' }}>✕ إغلاق</button>
            </div>
          </div>
        </div>
        );
      })(), document.body)}

      {/* Remote Shift Closing Modal */}
      {closingShift && createPortal((() => {
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '20px 0', overflowY: 'auto', direction: 'rtl', fontFamily: "'Cairo', sans-serif" }}>
            <div className="premium-card fade-in-scale" style={{ width: '100%', maxWidth: '650px', background: 'linear-gradient(145deg, #1e2433 0%, #111520 100%)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '24px', position: 'relative', margin: 'auto', padding: '30px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column', gap: '20px', color: '#fff' }}>
            {/* Header */}
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: '3rem' }}>🔒</span>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 800, marginTop: '12px', color: '#fff' }}>إغلاق وردية {closingShift.cashierName} عن بُعد</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: '6px' }}>
                تقفيل الوردية المفتوحة وحساب المبيعات الحالية في السيرفر
              </p>
              <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', padding: '12px', borderRadius: '12px', fontSize: '0.82rem', color: '#fbbf24', marginTop: '12px', textAlign: 'right', lineHeight: '1.5' }}>
                ⚠️ <strong>تنبيه إداري:</strong> يرجى استخدام هذا الخيار فقط في الحالات الاستثنائية (مثال: نسيان الموظف إغلاق ورديته أو وجود عطل تقني). في الوضع الطبيعي، يجب إغلاق الوردية بالطريقة التقليدية من واجهة الموظف نفسه لتجنب الأخطاء.
              </div>
            </div>

            {/* Shift Stats Preview */}
            {(() => {
              const { cashSales, creditSales, expenses, extraIncomes, expectedCash } = getShiftStats(closingShift);
              const diff = parseFloat(actualCash || 0) - expectedCash;
              return (
                <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.9rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>توقيت البدء:</span>
                    <strong style={{ color: '#fff' }}>{new Date(closingShift.startTime).toLocaleString('ar-EG')}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>العهدة الافتتاحية:</span>
                    <strong style={{ color: '#fff' }}>{formatMoney(closingShift.startingCash)} ج.م</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>مبيعات الكاش:</span>
                    <strong style={{ color: 'var(--accent-green)' }}>{formatMoney(cashSales)} ج.م</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>إيرادات إضافية:</span>
                    <strong style={{ color: 'var(--accent-green)' }}>+{formatMoney(extraIncomes)} ج.م</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>المصروفات:</span>
                    <strong style={{ color: '#fb7185' }}>-{formatMoney(expenses)} ج.م</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>مبيعات الشبكة/الفيزا:</span>
                    <strong style={{ color: 'var(--primary)' }}>{formatMoney(creditSales)} ج.م</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed var(--border)', paddingTop: '8px', fontSize: '1rem' }}>
                    <span style={{ fontWeight: 700 }}>المبلغ المتوقع بالخزينة:</span>
                    <strong style={{ color: '#fff', fontWeight: 800 }}>{formatMoney(expectedCash)} ج.م</strong>
                  </div>
                  {actualCash && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: '8px', fontSize: '1.05rem' }}>
                      <span style={{ fontWeight: 700 }}>العجز / الزيادة:</span>
                      <strong style={{ color: diff >= 0 ? 'var(--accent-green)' : '#fb7185', fontWeight: 800 }}>
                        {diff >= 0 ? '📈 زائد' : '📉 عجز'} ({diff >= 0 ? '+' : ''}{diff} ج.م)
                      </strong>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Input Form */}
            <form onSubmit={handleCloseRemoteShift} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 700 }}>
                  المبلغ الفعلي المتوفر في الخزينة الآن (ج.م) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="premium-input"
                  placeholder="أدخل المبلغ النقدي الفعلي في الدرج"
                  value={actualCash}
                  onChange={(e) => setActualCash(e.target.value)}
                  required
                  style={{ width: '100%', padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 700 }}>
                  ملاحظات الإغلاق أو التسوية (اختياري)
                </label>
                <textarea
                  className="premium-input"
                  placeholder="أي ملاحظات حول العجز أو الزيادة أو عهدة الوردية..."
                  value={closingNotes}
                  onChange={(e) => setClosingNotes(e.target.value)}
                  style={{ width: '100%', height: '80px', padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none', resize: 'none' }}
                />
              </div>

              {closingStatusMsg && (
                <div style={{ color: '#fb7185', fontSize: '0.85rem', textAlign: 'center', background: 'rgba(244,63,94,0.08)', padding: '10px', borderRadius: '10px', border: '1px solid rgba(244,63,94,0.2)' }}>
                  {closingStatusMsg}
                </div>
              )}

              {/* Form Buttons */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                <button
                  type="submit"
                  disabled={isClosingLoading}
                  className="gradient-btn"
                  style={{ 
                    flex: 1.5, 
                    padding: '14px', 
                    borderRadius: '12px', 
                    fontSize: '0.92rem', 
                    fontWeight: 'bold', 
                    border: 'none',
                    background: 'linear-gradient(135deg, #f43f5e 0%, #be123c 100%)',
                    boxShadow: '0 4px 12px rgba(244,63,94,0.2)',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {isClosingLoading ? 'جاري إغلاق الوردية...' : 'تأكيد إغلاق الوردية عن بُعد 🔒'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setClosingShift(null);
                    setActualCash('');
                    setClosingNotes('');
                    setClosingStatusMsg('');
                  }}
                  disabled={isClosingLoading}
                  className="gradient-btn-secondary"
                  style={{ flex: 0.8, padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)', color: '#fff', fontSize: '0.92rem', cursor: 'pointer' }}
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
        );
      })(), document.body)}
      {/* مودال تفاصيل الموظف والراتب الفخمة عند الضغط على الاسم */}
      {selectedEmployeeForModal && createPortal((() => {
        const emp = selectedEmployeeForModal;
        
        // حساب إجمالي الخصومات
        const deductionsList = emp.deductions || [];
        let totalDeductionsAmount = 0;
        let totalDeductionsHours = 0;
        
        deductionsList.forEach(d => {
          if (d.type === 'hours') {
            totalDeductionsHours += parseFloat(d.value) || 0;
          } else {
            totalDeductionsAmount += parseFloat(d.value) || 0;
          }
        });

        let totalPay = 0;
        let payDetailsText = '';
        if (emp.role === 'driver' || emp.role === 'طيار') {
          const regRate = parseFloat(emp.regularRate) || 0;
          const commRate = parseFloat(emp.commissionPerOrder) || 0;
          const adjustedHours = Math.max(0, (emp.workedHours || 0) - totalDeductionsHours);
          const hoursPay = adjustedHours * regRate;
          const commPay = (emp.ordersCount || 0) * commRate;
          
          totalPay = Math.max(0, hoursPay + commPay - totalDeductionsAmount);
          
          payDetailsText = `(${adjustedHours.toFixed(1)} س × ${regRate} ج.م + ${commPay.toFixed(1)} عمولة)`;
          if (totalDeductionsAmount > 0) payDetailsText += ` - خصم مالي ${totalDeductionsAmount} ج.م`;
          if (totalDeductionsHours > 0) payDetailsText += ` - خصم ${totalDeductionsHours} س`;
        } else {
          const val = parseFloat(emp.salaryValue) || 0;
          if (emp.salaryType === 'hourly') {
            const adjustedHours = Math.max(0, (emp.workedHours || 0) - totalDeductionsHours);
            totalPay = Math.max(0, (adjustedHours * val) - totalDeductionsAmount);
            
            payDetailsText = `(${adjustedHours} ساعة × ${val} ج.م)`;
            if (totalDeductionsAmount > 0) payDetailsText += ` - خصم مالي ${totalDeductionsAmount} ج.م`;
            if (totalDeductionsHours > 0) payDetailsText += ` - خصم ${totalDeductionsHours} س`;
          } else {
            totalPay = Math.max(0, val - totalDeductionsAmount);
            payDetailsText = `(راتب شهري ثابت: ${val} ج.م)`;
            if (totalDeductionsAmount > 0) payDetailsText += ` - خصم مالي ${totalDeductionsAmount} ج.م`;
          }
        }

        // دالة إضافة الخصم
        const handleAddDeduction = async () => {
          if (!deductionValue || parseFloat(deductionValue) <= 0) {
            alert('⚠️ يرجى إدخال قيمة صحيحة للخصم!');
            return;
          }
          if (!deductionReason.trim()) {
            alert('⚠️ يرجى تحديد سبب الخصم لحفظه وتوثيقه!');
            return;
          }

          setDeductionLoading(true);
          try {
            const newDeduction = {
              id: Date.now().toString(),
              type: deductionType, // 'money' or 'hours'
              value: parseFloat(deductionValue),
              reason: deductionReason.trim(),
              createdAt: new Date().toISOString()
            };

            const updatedDeductions = [...deductionsList, newDeduction];
            
            // تحديث الفايربيز
            await updateDoc(doc(db, 'users', user?.uid, 'employees', emp.id), {
              deductions: updatedDeductions
            });

            // تحديث الستيت المحلية
            const updatedEmployees = employees.map(e => {
              if (e.id === emp.id) {
                return { ...e, deductions: updatedDeductions };
              }
              return e;
            });
            
            setEmployees(updatedEmployees);
            localStorage.setItem(`drmasr_employees_${user?.uid}`, JSON.stringify(updatedEmployees));
            
            // تحديث الموظف المختار حالياً في المودال ليعكس التغييرات فوراً
            setSelectedEmployeeForModal({ ...emp, deductions: updatedDeductions });
            
            // تنظيف الحقول
            setDeductionValue('');
            setDeductionReason('');
          } catch (e) {
            console.error(e);
            alert('❌ فشل تسجيل الخصم في قاعدة البيانات.');
          } finally {
            setDeductionLoading(false);
          }
        };

        // دالة حذف الخصم
        const handleDeleteDeduction = async (deductionId) => {
          if (!window.confirm('هل أنت متأكد من رغبتك في إلغاء هذا الخصم؟')) return;
          try {
            const updatedDeductions = deductionsList.filter(d => d.id !== deductionId);
            
            await updateDoc(doc(db, 'users', user?.uid, 'employees', emp.id), {
              deductions: updatedDeductions
            });

            const updatedEmployees = employees.map(e => {
              if (e.id === emp.id) {
                return { ...e, deductions: updatedDeductions };
              }
              return e;
            });
            
            setEmployees(updatedEmployees);
            localStorage.setItem(`drmasr_employees_${user?.uid}`, JSON.stringify(updatedEmployees));
            setSelectedEmployeeForModal({ ...emp, deductions: updatedDeductions });
          } catch (e) {
            console.error(e);
            alert('❌ فشل إلغاء الخصم.');
          }
        };

        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(8px)',
            zIndex: 99999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '16px'
          }}>
            <div className="premium-card fade-in-scale" style={{
              width: '100%',
              maxWidth: '680px',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: '#141229',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              borderRadius: '20px',
              padding: '28px',
              direction: 'rtl',
              fontFamily: "'Cairo', sans-serif"
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '12px' }}>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  👤 تفاصيل الموظف والراتب: {emp.name}
                </h3>
                <button
                  onClick={() => setSelectedEmployeeForModal(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.4rem', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  ✕
                </button>
              </div>

              {/* Grid content */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', marginBottom: '24px' }}>
                
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>المسمى الوظيفي:</span>
                  <strong style={{ color: '#fff', fontSize: '1rem' }}>
                    {emp.role === 'driver' || emp.role === 'طيار' ? '🛵 طيار دليفري' 
                     : emp.role === 'pharmacist' || emp.role === 'صيدلي' ? '🩺 دكتور صيدلي'
                     : emp.role === 'assistant' || emp.role === 'مساعد' ? '💊 مساعد صيدلي'
                     : emp.role === 'cashier' || emp.role === 'كاشير' ? '💵 كاشير مالي'
                     : emp.role === 'cleaner' || emp.role === 'عامل خدمات' ? '🧹 عامل خدمات ونظافة'
                     : emp.role || 'موظف'}
                  </strong>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>رقم الهاتف:</span>
                  <strong style={{ color: '#fff', fontSize: '1rem', fontFamily: 'Outfit' }}>{emp.phone || 'غير مسجل'}</strong>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>الرقم القومي:</span>
                  <strong style={{ color: '#fff', fontSize: '1rem', fontFamily: 'Outfit' }}>{emp.nationalId || 'غير مسجل'}</strong>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>مواعيد الوردية المجدولة:</span>
                  <strong style={{ color: '#fff', fontSize: '1rem' }}>{emp.shiftStartTime ? `${emp.shiftStartTime} - ${emp.shiftEndTime}` : 'غير محددة'}</strong>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>فترة السماح اليومية:</span>
                  <strong style={{ color: '#fff', fontSize: '1rem' }}>{emp.gracePeriodMinutes || 0} دقيقة</strong>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>نظام الراتب الأساسي:</span>
                  <strong style={{ color: 'var(--primary)', fontSize: '1rem' }}>
                    {emp.salaryValue || 0} ج.م ({emp.salaryType === 'hourly' ? 'بالساعة' : 'راتب شهري ثابت'})
                  </strong>
                </div>

                <div style={{ background: 'rgba(16, 185, 129, 0.03)', padding: '14px', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.15)', gridColumn: 'span 2' }}>
                  <span style={{ fontSize: '0.85rem', color: 'rgba(16, 185, 129, 0.9)' }}>الإحصائيات والأداء التراكمي الفعلي (هذا الشهر):</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '8px' }}>
                    <div>⏱️ إجمالي الساعات الفعلية: <span style={{ color: '#fff', fontWeight: 800 }}>{emp.workedHours || 0} ساعة</span></div>
                    
                    {emp.role === 'driver' || emp.role === 'طيار' ? (
                      <div>🏍️ الأوردرات المنجزة: <span style={{ color: '#fff', fontWeight: 800 }}>{emp.ordersCount || 0} أوردر</span></div>
                    ) : (
                      <div>🔄 الورديات المكتملة: <span style={{ color: '#fff', fontWeight: 800 }}>{emp.shiftsCount || 0} وردية</span></div>
                    )}
                  </div>
                  
                  <div style={{ borderTop: '1px dashed rgba(16, 185, 129, 0.2)', marginTop: '12px', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 800, color: 'var(--accent-green)', fontSize: '1.2rem' }}>💵 المستحقات المقدرة الحالية:</span>
                    <span style={{ fontWeight: 900, color: '#fff', fontSize: '1.3rem' }}>{totalPay.toFixed(2)} ج.م</span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '4px', textAlign: 'left' }}>
                    تفصيل الحساب: {payDetailsText}
                  </div>
                </div>

              </div>

              {/* قسم إدارة الخصومات الفخم */}
              <div style={{ background: 'rgba(239, 68, 68, 0.03)', border: '1px solid rgba(239, 68, 68, 0.15)', padding: '16px', borderRadius: '16px', marginBottom: '24px' }}>
                <h4 style={{ color: 'var(--accent-red)', margin: '0 0 12px 0', fontSize: '1.05rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  📉 إدارة الخصومات والجزاءات
                </h4>

                {/* قائمة الخصومات السابقة */}
                {deductionsList.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px', maxHeight: '180px', overflowY: 'auto', paddingLeft: '4px' }}>
                    {deductionsList.map((dec) => (
                      <div key={dec.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.02)', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.03)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 'bold' }}>
                            {dec.type === 'hours' ? `خصم ساعات: ${dec.value} ساعة` : `خصم مالي: ${dec.value} ج.م`}
                          </span>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                            سبب الخصم: {dec.reason}
                          </span>
                        </div>
                        <button 
                          onClick={() => handleDeleteDeduction(dec.id)}
                          style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', fontSize: '0.85rem' }}
                          title="حذف هذا الخصم"
                        >
                          🗑️ إلغاء
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 16px 0' }}>لا يوجد خصومات مسجلة على هذا الموظف حالياً.</p>
                )}

                {/* نموذج إضافة خصم جديد */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', background: 'rgba(0,0,0,0.25)', padding: '16px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.03)' }}>
                  <div style={{ flex: '1 1 150px' }}>
                    <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px', fontWeight: '600' }}>نوع الخصم</label>
                    <select
                      value={deductionType}
                      onChange={(e) => setDeductionType(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '10px 14px', 
                        background: '#0d0b18', 
                        border: '1px solid rgba(255,255,255,0.1)', 
                        color: '#fff', 
                        borderRadius: '10px', 
                        outline: 'none', 
                        cursor: 'pointer',
                        fontSize: '0.88rem',
                        fontFamily: "'Cairo', sans-serif"
                      }}
                    >
                      <option value="money">💰 مبلغ مالي (ج.م)</option>
                      {emp.salaryType === 'hourly' && <option value="hours">⏱️ ساعات عمل (ساعة)</option>}
                    </select>
                  </div>

                  <div style={{ flex: '1 1 120px' }}>
                    <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px', fontWeight: '600' }}>القيمة</label>
                    <input
                      type="number"
                      step="any"
                      placeholder="0.00"
                      value={deductionValue}
                      onChange={(e) => setDeductionValue(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '10px 14px', 
                        background: '#0d0b18', 
                        border: '1px solid rgba(255,255,255,0.1)', 
                        color: '#fff', 
                        borderRadius: '10px', 
                        outline: 'none', 
                        fontSize: '0.88rem',
                        fontFamily: "'Cairo', sans-serif"
                      }}
                    />
                  </div>

                  <div style={{ flex: '2 1 220px' }}>
                    <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px', fontWeight: '600' }}>السبب</label>
                    <input
                      type="text"
                      placeholder="اكتب سبب الخصم هنا للتوضيح..."
                      value={deductionReason}
                      onChange={(e) => setDeductionReason(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '10px 14px', 
                        background: '#0d0b18', 
                        border: '1px solid rgba(255,255,255,0.1)', 
                        color: '#fff', 
                        borderRadius: '10px', 
                        outline: 'none', 
                        fontSize: '0.88rem',
                        fontFamily: "'Cairo', sans-serif"
                      }}
                    />
                  </div>

                  <button
                    onClick={handleAddDeduction}
                    disabled={deductionLoading}
                    className="gradient-btn"
                    style={{ 
                      padding: '11px 22px', 
                      borderRadius: '10px', 
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', 
                      border: 'none', 
                      color: '#fff', 
                      fontWeight: 'bold', 
                      cursor: 'pointer',
                      fontSize: '0.88rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
                      fontFamily: "'Cairo', sans-serif"
                    }}
                  >
                    <span>{deductionLoading ? 'جاري الحفظ...' : '📉 تطبيق الخصم'}</span>
                  </button>
                </div>
              </div>

              {/* Actions Footer */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap', gap: '10px' }}>
                <button
                  onClick={() => {
                    let cleanedPhone = emp.phone ? emp.phone.replace(/\D/g, '') : '';
                    if (cleanedPhone.startsWith('0')) {
                      cleanedPhone = cleanedPhone.substring(1);
                    }
                    if (!cleanedPhone.startsWith('2') && cleanedPhone.length === 10) {
                      cleanedPhone = '20' + cleanedPhone;
                    } else if (cleanedPhone.length === 11 && cleanedPhone.startsWith('1')) {
                      cleanedPhone = '20' + cleanedPhone;
                    }
                    
                    const roleName = emp.role === 'driver' || emp.role === 'طيار' ? 'طيار دليفري 🛵' 
                                   : emp.role === 'pharmacist' || emp.role === 'صيدلي' ? 'دكتور صيدلي 🩺'
                                   : emp.role === 'assistant' || emp.role === 'مساعد' ? 'مساعد صيدلي 💊'
                                   : emp.role === 'cashier' || emp.role === 'كاشير' ? 'كاشير مالي 💵'
                                   : emp.role === 'cleaner' || emp.role === 'عامل خدمات' ? 'عامل خدمات ونظافة 🧹'
                                   : emp.role || 'موظف';

                    // تفصيل الخصومات لنص الواتساب
                    let deductionsMsgBlock = '';
                    if (deductionsList.length > 0) {
                      deductionsMsgBlock = `\n----------------------------------------\n` +
                                           `🔻 *الخصومات والجزاءات المسجلة:* 🔻\n` +
                                           deductionsList.map((d, index) => 
                                             `${index + 1}. ${d.type === 'hours' ? `خصم ${d.value} ساعة` : `خصم ${d.value} ج.م`} (السبب: ${d.reason})`
                                           ).join('\n') + '\n';
                    }

                    const text = `📊 *تقرير الأداء والمستحقات المالية* 📊\n\n` +
                                 `👤 *الاسم:* ${emp.name}\n` +
                                 `💼 *المسمى الوظيفي:* ${roleName}\n` +
                                 `📱 *رقم الهاتف:* ${emp.phone || 'غير مسجل'}\n` +
                                 `📅 *مواعيد الوردية:* ${emp.shiftStartTime ? `${emp.shiftStartTime} - ${emp.shiftEndTime}` : 'غير محددة'}\n` +
                                 `----------------------------------------\n` +
                                 `⏱️ *إجمالي الساعات الفعلية:* ${emp.workedHours || 0} ساعة\n` +
                                 (emp.role === 'driver' || emp.role === 'طيار' 
                                   ? `🏍️ *الأوردرات المنجزة:* ${emp.ordersCount || 0} أوردر\n`
                                   : `🔄 *الورديات المكتملة:* ${emp.shiftsCount || 0} وردية\n`) +
                                 `----------------------------------------\n` +
                                 `💰 *نظام الراتب الأساسي:* ${emp.salaryValue || 0} ج.م (${emp.salaryType === 'hourly' ? 'بالساعة' : 'شهري ثابت'})\n` +
                                 deductionsMsgBlock +
                                 `----------------------------------------\n` +
                                 `💵 *صافي المستحقات المقدرة:* ${totalPay.toFixed(2)} ج.م\n` +
                                 `📝 *تفاصيل الحساب:* ${payDetailsText}\n\n` +
                                 `🩺 *صيدلية دكتور مصر - Doctor Masr*`;

                    const url = `https://wa.me/${cleanedPhone || '201005444663'}?text=${encodeURIComponent(text)}`;
                    window.open(url, '_blank');
                  }}
                  className="gradient-btn"
                  style={{ 
                    padding: '10px 24px', 
                    borderRadius: '10px', 
                    fontWeight: 'bold', 
                    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', 
                    border: 'none', 
                    color: '#fff', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer' 
                  }}
                >
                  <span>💬 إرسال التقرير عبر واتساب</span>
                </button>
                
                <button
                  onClick={() => setSelectedEmployeeForModal(null)}
                  className="gradient-btn-secondary"
                  style={{ padding: '10px 24px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
                >
                  إغلاق النافذة
                </button>
              </div>

            </div>
          </div>
        );
      })(), document.body)}

    </div>
  );
}


function ShiftDialog({ sales, user, activeShift, setActiveShift, shiftsHistory = [], setShiftsHistory, employees = [], currentUserData, telegramSettings = {}, onClose }) {
  const [startingCash, setStartingCash] = useState('');
  const [cashierName, setCashierName] = useState('');
  const [enteredPin, setEnteredPin] = useState('');
  const [actualCash, setActualCash] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [isHandoverStep, setIsHandoverStep] = useState(false);
  const [accountingTx, setAccountingTx] = useState([]);

  useEffect(() => {
    const fetchAccounting = async () => {
      try {
        const q = query(collection(db, 'users', user?.uid, 'accounting'));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setAccountingTx(fetched);
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (user) fetchAccounting();
  }, [user]);

  // Calculate stats for current active shift
  let cashSales = 0;
  let creditSales = 0;
  if (activeShift) {
    const shiftSales = sales.filter(sale => {
      if (!sale.timestamp) return false;
      return new Date(sale.timestamp) >= new Date(activeShift.startTime);
    });

    shiftSales.forEach(sale => {
      if (sale.isDelivery && sale.deliveryStatus !== 'delivered') return;
      if (sale.paymentMethod === 'split') {
        cashSales += sale.splitAmounts?.cash || 0;
        creditSales += sale.splitAmounts?.card || 0;
      } else {
        const saleAmt = sale.isInsurance ? (sale.patientShare !== undefined ? sale.patientShare : sale.grandTotal) : sale.grandTotal;
        if (sale.paymentMethod === 'deferred') {
          // Deferred sales don't add to cash or credit sales in the shift expected cash
        } else if (sale.paymentMethod === 'credit' || sale.paymentMethod === 'visa' || sale.paymentMethod === 'network' || sale.paymentMethod === 'instapay' || sale.paymentMethod === 'vodafone') {
          creditSales += saleAmt;
        } else {
          cashSales += saleAmt;
        }
      }
    });
  }

  let activeShiftExpenses = 0;
  let activeShiftIncomes = 0;
  if (activeShift) {
    const shiftTxs = accountingTx.filter(tx => {
      if (!tx.timestamp) return false;
      if (tx.shiftId) return tx.shiftId === activeShift.id;
      const txDate = new Date(tx.timestamp);
      return txDate >= new Date(activeShift.startTime);
    });
    shiftTxs.forEach(tx => {
      if (tx.type === 'expense') {
        activeShiftExpenses += parseFloat(tx.amount || 0);
      } else if (tx.type === 'revenue') {
        activeShiftIncomes += parseFloat(tx.amount || 0);
      }
    });
  }

  const expectedCash = activeShift ? (parseFloat(activeShift.startingCash || 0) + cashSales + activeShiftIncomes - activeShiftExpenses) : 0;
  const difference = activeShift ? (parseFloat(actualCash || 0) - expectedCash) : 0;

  // Open Shift
  const handleOpenShift = async (e) => {
    e.preventDefault();
    if (!startingCash || !cashierName) {
      setStatusMsg('يرجى ملء جميع البيانات لفتح الوردية!');
      return;
    }
    setLoading(true);
    setStatusMsg('');

    // PIN Validation
    const emp = employees.find(emp => emp.name === cashierName);
    const registeredPharmacists = employees.filter(e => !e.role || e.role === 'pharmacist');
    if (registeredPharmacists.length > 0) {
      if (!emp || (emp.role && emp.role !== 'pharmacist')) {
        setStatusMsg('يرجى اختيار طبيب مسجل في النظام!');
        setLoading(false);
        return;
      }
    }
    if (emp && emp.pin && emp.pin !== enteredPin) {
      setStatusMsg(' رمز الـ PIN المدخل غير صحيح! يرجى إدخل الرمز الصحيح الخاص بالطبيب المختار.');
      setLoading(false);
      return;
    }

    const newShift = {
      startTime: new Date().toISOString(),
      startingCash: parseFloat(startingCash),
      cashierName,
      status: 'open'
    };

    try {
      const docRef = await addDoc(collection(db, 'users', user?.uid, 'active_shift'), newShift);
      const savedShift = { id: docRef.id, ...newShift };
      setActiveShift(savedShift);
      localStorage.setItem('drmasr_activeShift_' + (user?.uid), JSON.stringify(savedShift));
      localStorage.setItem('drmasr_active_shift_id_' + (user?.uid), docRef.id);
      setStatusMsg('تم بدء الوردية بنجاح!');
      setStartingCash('');
      setCashierName('');
      setEnteredPin('');
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      console.error(err);
      setStatusMsg('حدث خطأ أثناء فتح الوردية.');
    } finally {
      setLoading(false);
    }
  };

  // Close Shift
  const handleCloseShift = async (e) => {
    e.preventDefault();
    if (!actualCash) {
      setStatusMsg('يرجى إدخال المبلغ الفعلي في الخزينة لإغلاق الوردية!');
      return;
    }
    setLoading(true);
    setStatusMsg('');

    const closedShift = {
      ...activeShift,
      endTime: new Date().toISOString(),
      cashSales,
      creditSales,
      expectedCash,
      actualCash: parseFloat(actualCash),
      difference,
      closingNotes,
      status: 'closed'
    };

    try {
      await addDoc(collection(db, 'users', user?.uid, 'shifts_history'), closedShift);
      
      // Update Pharmacist's workedHours and shiftsCount in Firestore
      try {
        const shiftStart = new Date(activeShift.startTime);
        const durationHours = Math.max(0, (new Date() - shiftStart) / (1000 * 60 * 60));
        const cleanedCashier = cleanName(activeShift.cashierName || '');
        const matchedEmp = employees.find(emp => cleanName(emp.name) === cleanedCashier);
        if (matchedEmp && matchedEmp.id) {
          const empDocRef = doc(db, 'users', user?.uid, 'employees', matchedEmp.id);
          const currentHours = matchedEmp.workedHours || 0;
          const currentShifts = matchedEmp.shiftsCount || 0;
          await updateDoc(empDocRef, {
            workedHours: Math.round((currentHours + durationHours) * 100) / 100,
            shiftsCount: currentShifts + 1
          });
        }
      } catch (err) {
        console.error('Failed to update pharmacist firebase hours:', err);
      }

      if (activeShift?.id) {
        try {
          await deleteDoc(doc(db, 'users', user?.uid, 'active_shift', activeShift.id));
        } catch (err) {
          console.error('Failed to delete active shift:', err);
        }
      }

      try {
        const nextHistory = [closedShift, ...shiftsHistory];
        setShiftsHistory(nextHistory);
        localStorage.setItem('drmasr_shiftsHistory_' + user?.uid, JSON.stringify(nextHistory));
      } catch (e) {
        console.warn('Storage quota exceeded or failed:', e);
      }

      try {
        if (telegramSettings && telegramSettings.enabled) {
          const pharmacyName = currentUserData?.pharmacyName || 'صيدلية دكتور مصر';
          const shortId = currentUserData?.shortId || 'غير متوفر';

          let msg = `📊 <b>تقرير إغلاق الوردية الفاخر</b>\n` +
                    `🏥 <b>الصيدلية:</b> <code>${pharmacyName}</code> (${shortId})\n` +
                    `👨‍⚕️ <b>الدكتور المسؤول:</b> <b>${closedShift.cashierName || 'غير معروف'}</b>\n` +
                    `━━━━━━━━━━━━━━━━━━━━\n\n` +
                    `📅 <b>تفاصيل الوردية:</b>\n` +
                    `• وقت البدء: <code>${closedShift.startTime ? new Date(closedShift.startTime).toLocaleString('ar-EG') : 'غير متوفر'}</code>\n` +
                    `• وقت الإغلاق: <code>${closedShift.endTime ? new Date(closedShift.endTime).toLocaleString('ar-EG') : 'غير متوفر'}</code>\n\n` +
                    `💰 <b>الملخص المالي والمبيعات:</b>\n` +
                    `  ├─ العهدة الافتتاحية: ${closedShift.startingCash} ج.م\n` +
                    `  ├─ مبيعات الكاش (النقدي): ${closedShift.cashSales} ج.م\n` +
                    `  └─ مبيعات الشبكة (الفيزا): ${closedShift.creditSales} ج.م\n\n` +
                    `💵 <b>تطابق الخزينة والعهدة:</b>\n` +
                    `  ├─ المبلغ المتوقع بالخزينة: ${closedShift.expectedCash} ج.م\n` +
                    `  ├─ المبلغ الفعلي بالخزينة: ${closedShift.actualCash} ج.م\n` +
                    `  └─ العجز / الزيادة: <b>${closedShift.difference >= 0 ? '📈 زائد' : '📉 عجز'} (${closedShift.difference >= 0 ? '+' : ''}${closedShift.difference} ج.م)</b>\n\n`;

          if (closedShift.closingNotes) {
            msg += `📝 <b>ملاحظات الطبيب:</b>\n<i>${closedShift.closingNotes}</i>\n\n`;
          }

          msg += `━━━━━━━━━━━━━━━━━━━━\n` +
                 `🕊️ <i>تم إغلاق الوردية وحفظها بنجاح عبر نظام دكتور مصر</i>`;

          sendTelegramMessage(telegramSettings, msg).catch(err => console.error('Telegram send shift close failed:', err));
        }
      } catch (telegramErr) {
        console.error('Telegram notification error:', telegramErr);
      }

      setActiveShift(null);
      localStorage.removeItem('drmasr_activeShift_' + user?.uid);
      localStorage.removeItem('drmasr_active_shift_id_' + user?.uid);

      setStatusMsg('تم إغلاق الوردية وحفظ بياناتها بنجاح!');
      setActualCash('');
      setClosingNotes('');
      setIsHandoverStep(true);
      try {
        printShiftReport(closedShift);
      } catch (printErr) {
        console.error('Auto printing shift report failed:', printErr);
      }

    } catch (err) {
      console.error('Firestore shift save error:', err);
      setStatusMsg('حدث خطأ أثناء إغلاق الوردية: ' + (err.message || err.toString()));
    } finally {
      setLoading(false);
    }
  };

  const isOpening = !activeShift || isHandoverStep;
  const themeColor = isOpening ? '#10b981' : '#f43f5e';
  const themeColorRgb = isOpening ? '16, 185, 129' : '244, 63, 94';
  const themeGlow = isOpening ? 'rgba(16, 185, 129, 0.15)' : 'rgba(244, 63, 94, 0.15)';
  const themeBorder = isOpening ? 'rgba(16, 185, 129, 0.3)' : 'rgba(244, 63, 94, 0.3)';
  const icon = isOpening ? '🔑' : '📊';
  const titleText = isHandoverStep ? 'تسليم واستلام الوردية' : (isOpening ? 'بدء وردية جديدة' : 'إغلاق وتسليم الوردية');
  const subtitleText = isHandoverStep 
    ? 'تم إغلاق الوردية السابقة. يرجى تسجيل بيانات الطبيب المستلم للوردية الجديدة'
    : (isOpening ? 'سجل حضورك وابدأ وردية جديدة للعمل بالصيدلية' : 'مراجعة الحسابات وتقفيل مبيعات اليوم وتسليم عهدة الخزينة');

  return createPortal((
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', overflowY: 'auto' }}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes modalPulse {
          0% { box-shadow: 0 20px 40px -10px rgba(0,0,0,0.8), 0 0 20px ${themeGlow}; }
          50% { box-shadow: 0 20px 40px -10px rgba(0,0,0,0.9), 0 0 30px rgba(${themeColorRgb}, 0.25); }
          100% { box-shadow: 0 20px 40px -10px rgba(0,0,0,0.8), 0 0 20px ${themeGlow}; }
        }
        .premium-card-dialog::-webkit-scrollbar {
          width: 6px;
        }
        .premium-card-dialog::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 10px;
        }
        .close-btn-rotate:hover {
          transform: rotate(90deg) scale(1.1);
          color: #ef4444 !important;
        }
      `}</style>

      <div 
        className="premium-card premium-card-dialog fade-in-content" 
        style={{ 
          width: '550px', 
          maxWidth: '100%', 
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '32px', 
          position: 'relative', 
          border: `1px solid ${themeBorder}`,
          background: 'rgba(10, 16, 35, 0.85)',
          backdropFilter: 'blur(25px)',
          borderRadius: '24px',
          animation: 'modalPulse 4s infinite ease-in-out',
          fontFamily: 'Cairo, sans-serif',
          direction: 'rtl'
        }}
      >
        {/* Close Button "X" at top-left corner */}
        <button 
          onClick={onClose} 
          className="close-btn-rotate"
          style={{ 
            position: 'absolute', 
            top: '24px', 
            left: '24px', 
            background: 'rgba(255, 255, 255, 0.05)', 
            border: '1px solid rgba(255, 255, 255, 0.1)', 
            color: 'var(--text-secondary)', 
            fontSize: '1.2rem', 
            cursor: 'pointer',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 100
          }}
          title="إغلاق النافذة دون تغيير"
        >
          ✕
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px' }}>
          <span style={{ fontSize: '2.2rem', filter: `drop-shadow(0 0 10px rgba(${themeColorRgb}, 0.5))` }}>{icon}</span>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff', margin: 0 }}>
            {titleText}
          </h2>
        </div>

        <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.92rem', lineHeight: '1.6' }}>
          {subtitleText}
        </p>

        {statusMsg && (
          <div style={{ 
            background: `rgba(${themeColorRgb}, 0.1)`, 
            border: `1px solid ${themeColor}`, 
            padding: '12px 16px', 
            borderRadius: '14px', 
            color: '#fff', 
            textAlign: 'center', 
            marginBottom: '20px', 
            fontWeight: 600,
            fontSize: '0.9rem',
            boxShadow: `0 0 15px rgba(${themeColorRgb}, 0.1)`
          }}>
            {statusMsg}
          </div>
        )}

        {isHandoverStep ? (
          <form onSubmit={handleOpenShift} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ background: 'rgba(34, 197, 94, 0.12)', border: '1px solid #22c55e', padding: '14px', borderRadius: '14px', color: '#22c55e', textAlign: 'center', fontWeight: 600, marginBottom: '4px', fontSize: '0.92rem' }}>
              تم إغلاق الوردية السابقة بنجاح!
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginBottom: '4px' }}>
              قم باختيار الموظف أو الدكتور الذي سيستلم الوردية الجديدة ومبلغ البداية:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 600 }}>اسم الموظف أو الدكتور المستلم</label>
              {employees && employees.filter(e => !e.role || e.role === 'pharmacist').length > 0 ? (
                <select className="premium-input" value={cashierName} onChange={e => setCashierName(e.target.value)} required style={{ background: 'rgba(8, 13, 27, 0.6)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', color: '#fff', outline: 'none' }}>
                  <option value="" style={{ background: '#0a1023', color: '#fff' }}>اختر اسم الموظف/الدكتور</option>
                  {employees.filter(e => !e.role || e.role === 'pharmacist').map(emp => (
                    <option key={emp.id} value={emp.name} style={{ background: '#0a1023', color: '#fff' }}>{emp.name}</option>
                  ))}
                </select>
              ) : (
                <input type="text" className="premium-input" value={cashierName} onChange={e => setCashierName(e.target.value)} placeholder="مثال: د. أحمد محمد" required style={{ background: 'rgba(8, 13, 27, 0.6)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', color: '#fff', outline: 'none' }} />
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 600 }}>رمز الـ PIN للمستلم</label>
              <input
                type="password"
                maxLength={4}
                className="premium-input"
                value={enteredPin}
                onChange={e => setEnteredPin(e.target.value.replace(/\D/g, ''))}
                placeholder="أدخل رمز الـ PIN المكون من 4 أرقام"
                required
                style={{ background: 'rgba(8, 13, 27, 0.6)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', color: '#fff', letterSpacing: '8px', textAlign: 'center', fontSize: '1.2rem', outline: 'none' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 600 }}>مبلغ البداية في الخزينة (Starting Cash)</label>
              <input type="number" className="premium-input" value={startingCash} onChange={e => setStartingCash(e.target.value)} placeholder="0.00" required style={{ background: 'rgba(8, 13, 27, 0.6)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', color: '#fff', outline: 'none' }} />
            </div>

            <button type="submit" className="gradient-btn" style={{ padding: '14px', marginTop: '10px', fontWeight: 'bold', fontSize: '1rem', background: `linear-gradient(135deg, ${themeColor} 0%, #059669 100%)`, boxShadow: `0 4px 15px rgba(16, 185, 129, 0.25)` }} disabled={loading}>
              {loading ? 'جاري التحميل...' : ' تأكيد واستلام الوردية الجديدة'}
            </button>
          </form>
        ) : isOpening ? (
          <form onSubmit={handleOpenShift} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 600 }}>اسم الموظف أو الدكتور المسؤول</label>
              {employees && employees.filter(e => !e.role || e.role === 'pharmacist').length > 0 ? (
                <select className="premium-input" value={cashierName} onChange={e => setCashierName(e.target.value)} required style={{ background: 'rgba(8, 13, 27, 0.6)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', color: '#fff', outline: 'none' }}>
                  <option value="" style={{ background: '#0a1023', color: '#fff' }}>اختر اسم الموظف/الدكتور</option>
                  {employees.filter(e => !e.role || e.role === 'pharmacist').map(emp => (
                    <option key={emp.id} value={emp.name} style={{ background: '#0a1023', color: '#fff' }}>{emp.name}</option>
                  ))}
                </select>
              ) : (
                <input type="text" className="premium-input" value={cashierName} onChange={e => setCashierName(e.target.value)} placeholder="مثال: د. أحمد محمد" required style={{ background: 'rgba(8, 13, 27, 0.6)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', color: '#fff', outline: 'none' }} />
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 600 }}>رمز الـ PIN للطبيب المسؤول</label>
              <input
                type="password"
                maxLength={4}
                className="premium-input"
                value={enteredPin}
                onChange={e => setEnteredPin(e.target.value.replace(/\D/g, ''))}
                placeholder="أدخل رمز الـ PIN المكون من 4 أرقام"
                required
                style={{ background: 'rgba(8, 13, 27, 0.6)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', color: '#fff', letterSpacing: '8px', textAlign: 'center', fontSize: '1.2rem', outline: 'none' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 600 }}>مبلغ البداية في الخزينة (Starting Cash)</label>
              <input type="number" className="premium-input" value={startingCash} onChange={e => setStartingCash(e.target.value)} placeholder="0.00" required style={{ background: 'rgba(8, 13, 27, 0.6)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', color: '#fff', outline: 'none' }} />
            </div>

            <button type="submit" className="gradient-btn" style={{ padding: '14px', marginTop: '10px', fontWeight: 'bold', fontSize: '1rem', background: `linear-gradient(135deg, ${themeColor} 0%, #10b981 100%)`, boxShadow: `0 4px 15px rgba(16, 185, 129, 0.25)` }} disabled={loading}>
              {loading ? 'جاري التحميل...' : ' بدء وفتح الوردية'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleCloseShift} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'rgba(244, 63, 94, 0.05)', border: '1px solid rgba(244, 63, 94, 0.15)', borderRadius: '16px', padding: '18px', color: 'var(--text-secondary)', fontSize: '0.92rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span>الوردية الحالية للموظف:</span>
                <span style={{ fontWeight: 700, color: '#fff' }}>{activeShift.cashierName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span>بدأت في:</span>
                <span style={{ fontWeight: 700, color: '#fff' }}>{new Date(activeShift.startTime).toLocaleString('ar-EG')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>مبلغ البداية:</span>
                <span style={{ fontWeight: 700, color: '#fff' }}>{activeShift.startingCash} ج.م</span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 600 }}>المبلغ الفعلي الموجود في الدرج (الخزينة حالياً)</label>
              <input type="number" className="premium-input" value={actualCash} onChange={e => setActualCash(e.target.value)} placeholder="0.00" required style={{ background: 'rgba(8, 13, 27, 0.6)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', color: '#fff', fontSize: '1.1rem', outline: 'none' }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 600 }}>ملاحظات الإغلاق وتسليم الوردية</label>
              <textarea className="premium-input" value={closingNotes} onChange={e => setClosingNotes(e.target.value)} placeholder="أي تفاصيل أو ملاحظات عن العجز أو المعاملات اليدوية..." style={{ height: '70px', resize: 'none', background: 'rgba(8, 13, 27, 0.6)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', color: '#fff', outline: 'none' }} />
            </div>

            <button type="submit" className="gradient-btn-secondary" style={{ padding: '14px', marginTop: '10px', fontWeight: 'bold', fontSize: '1rem', background: `linear-gradient(135deg, ${themeColor} 0%, #b91c1c 100%)`, color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', boxShadow: `0 4px 15px rgba(244, 63, 94, 0.25)` }} disabled={loading}>
              {loading ? 'جاري التحميل...' : ' إنهاء وإغلاق الوردية (تسليم الوردية)'}
            </button>
          </form>
        )}
      </div>
    </div>
  ), document.body);
}
function SettingsView({ user, employees, setEmployees, telegramSettings = {}, setTelegramSettings, currentUserData, setCurrentUserData, isAdminVerified, setIsAdminVerified, paymentSettings, setPaymentSettings }) {
  const [employeeName, setEmployeeName] = useState('');
  const [employeePin, setEmployeePin] = useState('');
  const [revealedEmpPins, setRevealedEmpPins] = useState({});
  const [employeeRole, setEmployeeRole] = useState('pharmacist');
  const [driverRegularRate, setDriverRegularRate] = useState('20');
  const [driverOvertimeRate, setDriverOvertimeRate] = useState('1.5');
  const [driverHolidayRate, setDriverHolidayRate] = useState('2.0');
  const [driverCommissionPerOrder, setDriverCommissionPerOrder] = useState('5');
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [editingEmpId, setEditingEmpId] = useState(null);
  const [editingPinValue, setEditingPinValue] = useState('');
  const [receiptLogo, setReceiptLogo] = useState(localStorage.getItem('drmasr_receipt_logo') || '');

  // Public Database Migration States
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState('');
  const [migrationError, setMigrationError] = useState('');
  const [medicinesCount, setMedicinesCount] = useState(null);
  const [barcodesCount, setBarcodesCount] = useState(null);
  const [loadingCounts, setLoadingCounts] = useState(false);


  const fetchCollectionCounts = async () => {
    setLoadingCounts(true);
    try {
      const medsSnap = await getCountFromServer(collection(db, "global_medicines"));
      setMedicinesCount(medsSnap.data().count);

      const barcodesSnap = await getCountFromServer(collection(db, "global_barcodes"));
      setBarcodesCount(barcodesSnap.data().count);
    } catch (err) {
      console.warn("Failed to fetch collection counts:", err);
    } finally {
      setLoadingCounts(false);
    }
  };

  useEffect(() => {
    if (isAdminVerified) {
      fetchCollectionCounts();
    }
  }, [isAdminVerified]);

  const handleMigrate = async (source, target) => {
    window.triggerDrMasrConfirm(`⚠️ هل أنت متأكد من أنك تريد نسخ كافة البيانات من ${source} إلى ${target}؟ قد يستغرق ذلك بعض الوقت.`, async () => {
      setMigrationLoading(true);
      setMigrationProgress(`جاري جلب البيانات من ${source}...`);
      setMigrationError('');
      try {
        const sourceSnap = await getDocs(collection(db, source));
        setMigrationProgress(`تم جلب ${sourceSnap.size} صنفًا. جاري البدء في كتابتهم إلى ${target}...`);
        
        if (sourceSnap.size === 0) {
          setMigrationProgress(`لا توجد أدوية في ${source} لنقلها.`);
          setMigrationLoading(false);
          return;
        }

        let batch = writeBatch(db);
        let count = 0;
        let totalSaved = 0;

        for (const docItem of sourceSnap.docs) {
          const data = docItem.data();
          const targetRef = doc(db, target, docItem.id);

          batch.set(targetRef, data);
          count++;
          totalSaved++;

          if (count === 500) {
            setMigrationProgress(`جاري حفظ الدفعة... تم نقل ${totalSaved} من ${sourceSnap.size} أدوية.`);
            await batch.commit();
            batch = writeBatch(db);
            count = 0;
          }
        }

        if (count > 0) {
          setMigrationProgress(`جاري حفظ الدفعة الأخيرة... تم نقل ${totalSaved} من ${sourceSnap.size} أدوية.`);
          await batch.commit();
        }

        setMigrationProgress(`✨ تم اكتمال عملية النقل بنجاح! تم نسخ ${totalSaved} صنفًا بالكامل إلى ${target}.`);
        if (window.triggerDrMasrToast) {
          window.triggerDrMasrToast("✨ تم نقل البيانات بنجاح!");
        }
        fetchCollectionCounts();
      } catch (error) {
        console.error("Migration failed:", error);
        setMigrationError(error.message || "حدث خطأ غير معروف أثناء النقل.");
      } finally {
        setMigrationLoading(false);
      }
    });
  };

  const handleMigrateCommonDrugs = async () => {
    window.triggerDrMasrConfirm("⚠️ هل تريد نسخ قائمة الأدوية المدمجة في الكود (COMMON_EGYPTIAN_DRUGS) إلى قاعدة البيانات global_barcodes؟", async () => {
      setMigrationLoading(true);
      setMigrationProgress("جاري بدء نسخ الأدوية المدمجة...");
      setMigrationError('');
      try {
        let batch = writeBatch(db);
        let count = 0;
        let totalSaved = 0;

        for (const drug of COMMON_EGYPTIAN_DRUGS) {
          if (!drug.barcode) continue;
          const targetRef = doc(db, "global_barcodes", drug.barcode);
          
          const data = {
            barcode: drug.barcode,
            tradeName: drug.tradeName || '',
            genericName: drug.genericName || '',
            category: drug.category || 'عام',
            updatedAt: new Date().toISOString()
          };

          batch.set(targetRef, data);
          count++;
          totalSaved++;

          if (count === 500) {
            await batch.commit();
            batch = writeBatch(db);
            count = 0;
          }
        }

        if (count > 0) {
          await batch.commit();
        }

        setMigrationProgress(`✨ تم بنجاح نسخ ${totalSaved} صنفاً من الأدوية المدمجة إلى global_barcodes!`);
        if (window.triggerDrMasrToast) {
          window.triggerDrMasrToast("✨ تم نسخ الأدوية المدمجة بنجاح!");
        }
        fetchCollectionCounts();
      } catch (error) {
        console.error("Failed to migrate common drugs:", error);
        setMigrationError(error.message || "حدث خطأ أثناء نسخ الأدوية المدمجة.");
      } finally {
        setMigrationLoading(false);
      }
    });
  };

  // Local Admin Verification States for Lock Screen
  const [localAdminPin, setLocalAdminPin] = useState('');
  const [localAdminError, setLocalAdminError] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);
  const [isEyeHovered, setIsEyeHovered] = useState(false);

  const handleLocalAdminLogin = (e) => {
    e.preventDefault();
    setLocalAdminError('');
    if (localAdminPin.trim() === currentUserData?.adminPin) {
      setIsAdminVerified(true);
      setLocalAdminPin('');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('مرحباً بك دكتور! تم فتح إعدادات النظام بنجاح. 🔓');
      }
    } else {
      setLocalAdminError('رمز الـ PIN المدخل غير صحيح! ❌');
      setLocalAdminPin('');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1.5 * 1024 * 1024) {
        setStatusMsg('❌ حجم ملف الشعار كبير جداً! الحد الأقصى هو 1.5 ميجابايت.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result;
        setReceiptLogo(base64);
        localStorage.setItem('drmasr_receipt_logo', base64);
        setStatusMsg('✨ تم تحميل وحفظ شعار الفاتورة بنجاح!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoRemove = () => {
    setReceiptLogo('');
    localStorage.removeItem('drmasr_receipt_logo');
    setStatusMsg('🗑️ تم حذف شعار الفاتورة بنجاح.');
  };

  const [pharmacyNameInput, setPharmacyNameInput] = useState(currentUserData?.pharmacyName || '');
  const [ownerNameInput, setOwnerNameInput] = useState(currentUserData?.ownerName || '');
  const [phoneInput, setPhoneInput] = useState(currentUserData?.phone || '');
  const [emailInput, setEmailInput] = useState(currentUserData?.email || '');
  const [adminPinInput, setAdminPinInput] = useState(currentUserData?.adminPin || '');
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(currentUserData?.loyaltySettings?.enabled !== false);
  const [loyaltyRatio, setLoyaltyRatio] = useState(currentUserData?.loyaltySettings?.cashbackPer100 !== undefined ? currentUserData?.loyaltySettings?.cashbackPer100 : 5);
  const [globalLocalDiscount, setGlobalLocalDiscount] = useState(currentUserData?.globalDiscountSettings?.localDiscount !== undefined ? currentUserData?.globalDiscountSettings?.localDiscount : 0);
  const [globalImportedDiscount, setGlobalImportedDiscount] = useState(currentUserData?.globalDiscountSettings?.importedDiscount !== undefined ? currentUserData?.globalDiscountSettings?.importedDiscount : 0);

  const [instaPayIPNInput, setInstaPayIPNInput] = useState(paymentSettings?.instaPayIPN || '');
  const [vodafoneCashNumberInput, setVodafoneCashNumberInput] = useState(paymentSettings?.vodafoneCashNumber || '');
  const [allowDeferredSalesInput, setAllowDeferredSalesInput] = useState(paymentSettings?.allowDeferredSales !== false);

  useEffect(() => {
    if (paymentSettings) {
      setInstaPayIPNInput(paymentSettings.instaPayIPN || '');
      setVodafoneCashNumberInput(paymentSettings.vodafoneCashNumber || '');
      setAllowDeferredSalesInput(paymentSettings.allowDeferredSales !== false);
    }
  }, [paymentSettings]);

  const handleSavePaymentSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatusMsg('');
    try {
      const q = query(collection(db, 'users', user?.uid, 'payment_settings'));
      const snap = await getDocs(q);
      const updatedObj = {
        instaPayIPN: instaPayIPNInput.trim(),
        vodafoneCashNumber: vodafoneCashNumberInput.trim(),
        allowDeferredSales: allowDeferredSalesInput,
        updatedAt: new Date().toISOString()
      };
      if (!snap.empty) {
        const docRef = doc(db, 'users', user?.uid, 'payment_settings', snap.docs[0].id);
        await updateDoc(docRef, updatedObj);
      } else {
        await addDoc(collection(db, 'users', user?.uid, 'payment_settings'), updatedObj);
      }
      setPaymentSettings(updatedObj);
      localStorage.setItem('drmasr_paymentSettings_' + user?.uid, JSON.stringify(updatedObj));
      setStatusMsg('✨ تم حفظ إعدادات الدفع بنجاح!');
    } catch (err) {
      console.error("Error saving payment settings:", err);
      setStatusMsg('❌ حدث خطأ أثناء حفظ إعدادات الدفع.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePharmacyInfo = async (e) => {
    e.preventDefault();
    if (!pharmacyNameInput.trim() || !ownerNameInput.trim()) {
      setStatusMsg('⚠️ يرجى ملء اسم الصيدلية واسم صاحب الصيدلية أولاً!');
      return;
    }
    if (adminPinInput && adminPinInput.length !== 4) {
      setStatusMsg('⚠️ يجب أن يتكون الـ Admin PIN من 4 أرقام بالضبط!');
      return;
    }
    setLoading(true);
    setStatusMsg('');
    try {
      const updatedData = {
        ...currentUserData,
        pharmacyName: pharmacyNameInput.trim(),
        ownerName: ownerNameInput.trim(),
        phone: phoneInput.trim(),
        email: emailInput.trim(),
        adminPin: adminPinInput.trim(),
        loyaltySettings: {
          enabled: loyaltyEnabled,
          cashbackPer100: Number(loyaltyRatio)
        },
        globalDiscountSettings: {
          localDiscount: Number(globalLocalDiscount),
          importedDiscount: Number(globalImportedDiscount)
        }
      };
      await setDoc(doc(db, 'users', user?.uid), prepareDataForFirestore(updatedData, user));
      setCurrentUserData(updatedData);
      localStorage.setItem('drmasr_userdata', JSON.stringify(updatedData));
      setStatusMsg('✨ تم تحديث كافة البيانات والـ Admin PIN بنجاح!');
    } catch (err) {
      console.error("Error updating pharmacy info:", err);
      setStatusMsg('❌ فشل حفظ البيانات في قاعدة البيانات.');
    } finally {
      setLoading(false);
    }
  };

  // Add a new employee/doctor
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!employeeName.trim()) {
      setStatusMsg('⚠️ يرجى كتابة اسم الموظف أو الدكتور أولاً!');
      return;
    }
    if (employeeRole === 'pharmacist' && (!employeePin || employeePin.length !== 4)) {
      setStatusMsg('⚠️ يجب إدخال رمز الـ PIN المكون من 4 أرقام بالضبط!');
      return;
    }
    setLoading(true);
    setStatusMsg('');

    const newEmp = {
      name: employeeName.trim(),
      role: employeeRole,
      pin: employeeRole === 'pharmacist' ? employeePin.trim() : '',
      createdAt: new Date().toISOString(),
      ...(employeeRole === 'driver' ? {
        regularRate: parseFloat(driverRegularRate) || 0,
        overtimeRate: parseFloat(driverOvertimeRate) || 0,
        holidayRate: parseFloat(driverHolidayRate) || 0,
        commissionPerOrder: parseFloat(driverCommissionPerOrder) || 0
      } : {})
    };

    try {
      const docRef = await addDoc(collection(db, 'users', user?.uid, 'employees'), newEmp);
      const saved = { id: docRef.id, ...newEmp };
      const nextEmployees = [...employees, saved];
      setEmployees(nextEmployees);
      localStorage.setItem(`drmasr_employees_${user?.uid}`, JSON.stringify(nextEmployees));
      setEmployeeName('');
      setEmployeePin('');
      setEmployeeRole('pharmacist');
      setStatusMsg(employeeRole === 'pharmacist' ? '✨ تم إضافة الطبيب/الموظف مع رمز الـ PIN بنجاح!' : '✨ تم إضافة طيار الدليفري بنجاح!');
    } catch (err) {
      console.error(err);
      setStatusMsg('❌ حدث خطأ أثناء الحفظ.');
    } finally {
      setLoading(false);
    }
  };

  // Delete an employee/doctor
  const handleDeleteEmployee = async (id) => {
    window.triggerDrMasrConfirm('هل أنت متأكد من حذف هذا الاسم؟', async () => {
      setLoading(true);
      setStatusMsg('');
      try {
        await deleteDoc(doc(db, 'users', user?.uid, 'employees', id));
        const nextEmployees = employees.filter(emp => emp.id !== id);
        setEmployees(nextEmployees);
        localStorage.setItem(`drmasr_employees_${user?.uid}`, JSON.stringify(nextEmployees));
        setStatusMsg('🗑️ تم حذف الاسم بنجاح.');
      } catch (err) {
        console.error(err);
        setStatusMsg('❌ حدث خطأ أثناء الحذف.');
      } finally {
        setLoading(false);
      }
    });
  };

  // Update an employee/doctor PIN (Admin only)
  const handleUpdateEmployeePin = async (empId, newPin) => {
    if (!newPin || newPin.length !== 4) {
      setStatusMsg('⚠️ يجب أن يتكون رمز الـ PIN الجديد من 4 أرقام بالضبط!');
      return;
    }
    setLoading(true);
    setStatusMsg('');
    try {
      await updateDoc(doc(db, 'users', user?.uid, 'employees', empId), { pin: newPin.trim() });
      const nextEmployees = employees.map(emp => emp.id === empId ? { ...emp, pin: newPin.trim() } : emp);
      setEmployees(nextEmployees);
      localStorage.setItem(`drmasr_employees_${user?.uid}`, JSON.stringify(nextEmployees));
      setEditingEmpId(null);
      setEditingPinValue('');
      setStatusMsg('✨ تم تعديل رمز الـ PIN للموظف بنجاح!');
    } catch (err) {
      console.error("Error updating employee PIN:", err);
      setStatusMsg('❌ فشل تعديل رمز الـ PIN في قاعدة البيانات.');
    } finally {
      setLoading(false);
    }
  };

  const [tgEnabled, setTgEnabled] = useState(telegramSettings?.enabled || false);
  const [tgChatId, setTgChatId] = useState(telegramSettings?.chatId || '');
  const [tgBotToken, setTgBotToken] = useState(telegramSettings?.botToken || '8660558967:AAE-aht04AtybmrD3r_afPEy1WddKW0Fz4M');
  const [tgDisableDailySales, setTgDisableDailySales] = useState(telegramSettings?.disableDailySalesReport || false);
  const [tgDisableWeekly, setTgDisableWeekly] = useState(telegramSettings?.disableWeeklyReport || false);
  const [tgDisableShortages, setTgDisableShortages] = useState(telegramSettings?.disableShortagesReport || false);

  const handleSaveTelegram = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatusMsg('');

    const nextSettings = {
      enabled: tgEnabled,
      chatId: tgChatId.trim(),
      botToken: tgBotToken.trim(),
      disableDailySalesReport: tgDisableDailySales,
      disableWeeklyReport: tgDisableWeekly,
      disableShortagesReport: tgDisableShortages
    };

    try {
      const q = query(collection(db, 'users', user?.uid, 'telegram_settings'));
      const snap = await getDocs(q);
      if (!snap.empty) {
        await setDoc(doc(db, 'users', user?.uid, 'telegram_settings', snap.docs[0].id), nextSettings);
      } else {
        await addDoc(collection(db, 'users', user?.uid, 'telegram_settings'), nextSettings);
      }

      setTelegramSettings(nextSettings);
      localStorage.setItem(`drmasr_telegramSettings_${user?.uid}`, JSON.stringify(nextSettings));
      setStatusMsg('✨ تم حفظ إعدادات التليجرام بنجاح!');
    } catch (err) {
      console.error(err);
      setStatusMsg('❌ حدث خطأ أثناء حفظ إعدادات التليجرام.');
    } finally {
      setLoading(false);
    }
  };

  // Render Secured Lock Gate Screen if not admin verified
  if (!isAdminVerified && currentUserData?.adminPin) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', direction: 'rtl', fontFamily: 'Cairo, sans-serif' }}>
        <div className="premium-card fade-in-content" style={{ 
          maxWidth: '480px', 
          width: '100%', 
          border: '1px solid rgba(139, 92, 246, 0.35)', 
          padding: '40px', 
          borderRadius: '24px', 
          background: 'linear-gradient(145deg, rgba(13, 20, 36, 0.9) 0%, rgba(5, 8, 16, 0.98) 100%)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(20px)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative subtle background glow */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 60%)',
            pointerEvents: 'none',
            zIndex: 0
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '28px' }}>
              <div className="pulse-slow" style={{ 
                width: '96px', 
                height: '96px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(244, 63, 94, 0.15) 100%)', 
                border: '1px solid rgba(139, 92, 246, 0.4)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '3rem', 
                boxShadow: '0 0 35px rgba(139, 92, 246, 0.25)' 
              }}>
                🔒
              </div>
            </div>
            
            <h3 style={{ fontSize: '1.65rem', fontWeight: 800, color: '#fff', marginBottom: '12px', letterSpacing: '0.3px' }}>إعدادات النظام محمية</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '32px' }}>
              يُرجى إدخال رمز الـ PIN الخاص بالإدارة للوصول وتعديل الإعدادات الحساسة للنظام، وإضافة الموظفين والربط مع تليجرام.
            </p>
            
            <form onSubmit={handleLocalAdminLogin} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ position: 'relative' }}>
                <input 
                  type={showPin ? "text" : "password"} 
                  maxLength={4} 
                  className="premium-input" 
                  placeholder="••••" 
                  value={localAdminPin} 
                  onChange={e => {
                    setLocalAdminError('');
                    setLocalAdminPin(e.target.value.replace(/\D/g, ''));
                  }} 
                  required 
                  style={{ 
                    textAlign: 'center', 
                    fontSize: '2.2rem', 
                    letterSpacing: showPin ? '4px' : '16px', 
                    fontWeight: 'bold', 
                    height: '64px', 
                    padding: '0 48px', 
                    borderRadius: '16px', 
                    border: '1px solid rgba(139, 92, 246, 0.3)', 
                    background: 'rgba(0, 0, 0, 0.4)', 
                    color: '#fff',
                    transition: 'all 0.3s ease',
                    boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.5)',
                    width: '100%'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  onMouseEnter={() => setIsEyeHovered(true)}
                  onMouseLeave={() => setIsEyeHovered(false)}
                  style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: showPin ? 'var(--primary)' : 'rgba(255,255,255,0.4)',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    borderRadius: '50%',
                    transition: 'all 0.2s',
                    background: isEyeHovered ? 'rgba(255,255,255,0.05)' : 'transparent'
                  }}
                  title={showPin ? "إخفاء الرمز" : "إظهار الرمز"}
                >
                  {showPin ? "👁️" : "🙈"}
                </button>
              </div>

              {localAdminError && (
                <div className="shake" style={{ color: 'var(--accent-pink)', fontSize: '0.95rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>⚠️</span> {localAdminError}
                </div>
              )}

              <button 
                type="submit" 
                className="gradient-btn" 
                onMouseEnter={() => setIsSubmitHovered(true)}
                onMouseLeave={() => setIsSubmitHovered(false)}
                style={{ 
                  height: '54px', 
                  fontSize: '1.05rem', 
                  fontWeight: 'bold', 
                  width: '100%', 
                  borderRadius: '16px', 
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                  boxShadow: isSubmitHovered ? '0 8px 24px rgba(139, 92, 246, 0.45)' : '0 4px 12px rgba(139, 92, 246, 0.25)', 
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  transform: isSubmitHovered ? 'translateY(-1px)' : 'translateY(0)',
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span>فتح القفل</span> 🔐
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '32px', direction: 'rtl', fontFamily: 'Cairo, sans-serif' }}>
      {statusMsg && (
        <div style={{
          background: 'rgba(56, 189, 248, 0.15)', 
          border: '1px solid var(--primary)', 
          padding: '16px',
          borderRadius: '16px', 
          color: '#38bdf8', 
          fontSize: '1.05rem', 
          textAlign: 'center', 
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(56, 189, 248, 0.15)',
          animation: 'fadeIn 0.3s ease'
        }}>
          {statusMsg}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '28px' }}>
        
        {/* Pharmacy Logo Upload Card */}
        <div className="premium-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.4rem' }}>🖼️</span> شعار الصيدلية على الفاتورة
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.9rem', lineHeight: '1.6' }}>
            قم برفع شعار صيدليتك ليتم طباعته بشكل فوري وتلقائي في أعلى فواتير المبيعات الحرارية والورقية.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', marginTop: 'auto' }}>
            {receiptLogo ? (
              <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', padding: '24px', borderRadius: '16px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>معاينة الشعار الحالي:</span>
                <div style={{ padding: '12px', background: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={receiptLogo} alt="Pharmacy Logo Preview" style={{ maxHeight: '120px', maxWidth: '220px', objectFit: 'contain', borderRadius: '6px' }} />
                </div>
                <button 
                  type="button" 
                  onClick={handleLogoRemove} 
                  className="gradient-btn" 
                  style={{ 
                    padding: '10px 24px', 
                    background: 'linear-gradient(135deg, #ef4444 0%, #f43f5e 100%)', 
                    fontSize: '0.9rem',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: 700,
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                  }}
                >
                  🗑️ حذف الشعار الحالي
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', background: 'rgba(255, 255, 255, 0.01)', border: '2px dashed rgba(59, 130, 246, 0.25)', padding: '32px 24px', borderRadius: '16px', width: '100%', textAlign: 'center', transition: 'all 0.25s', cursor: 'pointer' }}
                   onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)'}
                   onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.25)'}>
                <span style={{ fontSize: '3rem', filter: 'drop-shadow(0 0 10px rgba(59,130,246,0.3))' }}>🖼️</span>
                <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>لم يتم اختيار شعار بعد.</span>
                <label className="gradient-btn" style={{ padding: '10px 24px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 700 }}>
                  📤 اختر صورة الشعار
                  <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Loyalty & Cashback Customization Card */}
        <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.4rem' }}>🎁</span> نظام كاش باك والولاء للعملاء
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.9rem', lineHeight: '1.6' }}>
            قم بضبط نسبة الكاش باك الافتراضية لعملائك لزيادة نسبة المبيعات وتكرار الزيارات لصيدليتك.
          </p>

          <form onSubmit={handleUpdatePharmacyInfo} style={{ display: 'flex', flexDirection: 'column', gap: '22px', marginTop: 'auto' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '14px', 
              background: loyaltyEnabled ? 'rgba(16, 185, 129, 0.08)' : 'rgba(51, 65, 85, 0.15)', 
              border: loyaltyEnabled ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid var(--border)', 
              padding: '16px', 
              borderRadius: '14px',
              transition: 'all 0.25s'
            }}>
              <input 
                type="checkbox" 
                id="loyaltyEnabled" 
                checked={loyaltyEnabled} 
                onChange={e => setLoyaltyEnabled(e.target.checked)} 
                style={{ width: '22px', height: '22px', cursor: 'pointer', accentColor: 'var(--accent-green)' }} 
              />
              <label htmlFor="loyaltyEnabled" style={{ fontSize: '1.05rem', color: '#fff', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                تفعيل نظام كاش باك والولاء
                {loyaltyEnabled && <span style={{ fontSize: '0.8rem', padding: '2px 8px', background: 'rgba(16, 185, 129, 0.2)', color: '#34d399', borderRadius: '6px', fontWeight: 'bold' }}>نشط ✔️</span>}
              </label>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>قيمة الكاش باك لكل 100 جنيه إنفاق (ج.م):</label>
              <div style={{ position: 'relative' }}>
                <input 
                  type="number" 
                  className="premium-input" 
                  value={loyaltyRatio} 
                  onChange={e => setLoyaltyRatio(Math.max(0, Number(e.target.value)))} 
                  placeholder="مثال: 5 جنيه لكل 100 جنيه" 
                  disabled={!loyaltyEnabled}
                  required 
                  style={{ 
                    width: '100%', 
                    paddingRight: '16px', 
                    paddingLeft: '64px',
                    opacity: loyaltyEnabled ? 1 : 0.6,
                    background: loyaltyEnabled ? 'rgba(15, 23, 42, 0.4)' : 'rgba(15, 23, 42, 0.8)'
                  }}
                />
                <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', fontWeight: 'bold', color: loyaltyEnabled ? 'var(--accent-gold)' : 'var(--text-secondary)' }}>ج.م / 100</span>
              </div>
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', lineHeight: '1.5', marginTop: '4px' }}>
                * سيتم احتساب <strong style={{ color: 'var(--accent-gold)' }}>{loyaltyRatio} جنيه</strong> لكل 100 جنيه كاش باك للعملاء تلقائياً عند الدفع.
              </small>
            </div>

            <button 
              type="submit" 
              className="gradient-btn" 
              style={{ 
                padding: '14px', 
                fontSize: '1rem', 
                fontWeight: 700,
                background: 'linear-gradient(135deg, var(--primary) 0%, #2563eb 100%)',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                border: 'none',
                borderRadius: '12px'
              }} 
              disabled={loading}
            >
              {loading ? 'جاري الحفظ...' : '💾 حفظ إعدادات الولاء'}
            </button>
          </form>
        </div>

        {/* Global Promotions & Discounts Card */}
        <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.4rem' }}>🏷️</span> خصومات العروض العامة والترويجية للكل
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.9rem', lineHeight: '1.6' }}>
            حدد نسبة خصم افتراضية تُطبق تلقائياً لكافة العملاء بدون استثناء، مفرقة بين الدواء المحلي والمستورد تلقائياً بناءً على باركود المنتج.
          </p>

          <form onSubmit={handleUpdatePharmacyInfo} style={{ display: 'flex', flexDirection: 'column', gap: '22px', marginTop: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>نسبة خصم الدواء المحلي للكل %</label>
                <input 
                  type="number" 
                  min="0"
                  max="100"
                  className="premium-input" 
                  value={globalLocalDiscount} 
                  onChange={e => setGlobalLocalDiscount(Math.max(0, Number(e.target.value)))} 
                  placeholder="مثال: 5" 
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>نسبة خصم الدواء المستورد للكل %</label>
                <input 
                  type="number" 
                  min="0"
                  max="100"
                  className="premium-input" 
                  value={globalImportedDiscount} 
                  onChange={e => setGlobalImportedDiscount(Math.max(0, Number(e.target.value)))} 
                  placeholder="مثال: 10" 
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="gradient-btn" 
              style={{ 
                padding: '14px', 
                fontSize: '1rem', 
                fontWeight: 700,
                background: 'linear-gradient(135deg, var(--primary) 0%, #2563eb 100%)',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                border: 'none',
                borderRadius: '12px'
              }} 
              disabled={loading}
            >
              {loading ? 'جاري الحفظ...' : '💾 حفظ العروض والخصومات العامة'}
            </button>
          </form>
        </div>

        {/* Form section to add employee */}
        <div className="premium-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.4rem' }}>{employeeRole === 'driver' ? '🛵' : '👨‍⚕️'}</span> 
            {employeeRole === 'driver' ? 'إضافة طيار دليفري جديد' : 'إضافة طبيب / موظف جديد'}
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.9rem', lineHeight: '1.6' }}>
            {employeeRole === 'driver' ? 'أضف اسم طيار الدليفري ليظهر في قائمة الطيارين عند إرسال الطلبات وتوصيلها للعملاء.' : 'أضف اسم الطبيب أو الموظف ليتم اختياره عند فتح الوردية وتعيين المسؤول عن الصيدلية.'}
          </p>

          <form onSubmit={handleAddEmployee} style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: 'auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>طبيعة الموظف / الوظيفة</label>
              <select 
                className="premium-input" 
                value={employeeRole} 
                onChange={e => {
                  setEmployeeRole(e.target.value);
                  if (e.target.value === 'driver') {
                    setEmployeePin('');
                  }
                }} 
                style={{ width: '100%', background: 'var(--bg-card)', color: '#fff', border: '1px solid var(--border)', borderRadius: '12px' }}
              >
                <option value="pharmacist">👨‍⚕️ دكتور صيدلي / كاشير (يتطلب رمز PIN)</option>
                <option value="driver">🛵 طيار دليفري (توصيل طلبات)</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                {employeeRole === 'driver' ? 'اسم الطيار بالكامل' : 'اسم الموظف أو الدكتور'}
              </label>
              <input 
                type="text" 
                className="premium-input" 
                value={employeeName} 
                onChange={e => setEmployeeName(e.target.value)} 
                placeholder={employeeRole === 'driver' ? 'مثال: كابتن أحمد محمد' : 'مثال: د. أحمد محمد'} 
                required 
                style={{ width: '100%' }}
              />
            </div>

            {employeeRole === 'pharmacist' && (
              <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>رمز الـ PIN للطبيب (4 أرقام)</label>
                <input
                  type="text"
                  maxLength={4}
                  className="premium-input"
                  value={employeePin}
                  onChange={e => setEmployeePin(e.target.value.replace(/\D/g, ''))}
                  placeholder="مثال: 4321"
                  required
                  style={{ width: '100%', textAlign: 'center', letterSpacing: '4px', fontSize: '1.2rem', fontWeight: 'bold' }}
                />
              </div>
            )}

            {employeeRole === 'driver' && (
              <div className="fade-in-content" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>سعر الساعة العادية (ج.م)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="premium-input"
                    value={driverRegularRate}
                    onChange={e => setDriverRegularRate(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>معامل الإضافي (ضرب ×)</label>
                  <input
                    type="number"
                    min="1"
                    step="0.1"
                    className="premium-input"
                    value={driverOvertimeRate}
                    onChange={e => setDriverOvertimeRate(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>معامل العيد/المضاعفة (ضرب ×)</label>
                  <input
                    type="number"
                    min="1"
                    step="0.1"
                    className="premium-input"
                    value={driverHolidayRate}
                    onChange={e => setDriverHolidayRate(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>عمولة الأوردر الواحد (ج.م)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="premium-input"
                    value={driverCommissionPerOrder}
                    onChange={e => setDriverCommissionPerOrder(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            )}

            <button 
              type="submit" 
              className="gradient-btn" 
              style={{ 
                padding: '14px', 
                fontSize: '1rem', 
                fontWeight: 700, 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                border: 'none',
                borderRadius: '12px'
              }} 
              disabled={loading}
            >
              {loading ? 'جاري التحميل...' : '➕ إضافة الموظف الآن'}
            </button>
          </form>
        </div>

        {/* Existing employees/doctors list */}
        <div className="premium-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.4rem' }}>👥</span> قائمة الموظفين والأطباء المسجلين
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.9rem', lineHeight: '1.6' }}>
            قائمة كاملة بجميع الموظفين وطياري الدليفري المسجلين حالياً. يمكنك تعديل رموز PIN أو حذف المستخدمين.
          </p>

          <div style={{ overflowY: 'auto', maxHeight: '350px', paddingRight: '4px', marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {employees.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 16px', background: 'rgba(255,255,255,0.01)', border: '1px dashed var(--border)', borderRadius: '12px', color: 'var(--text-secondary)' }}>
                <span style={{ fontSize: '2rem', display: 'block', marginBottom: '8px' }}>🤷‍♂️</span>
                لا يوجد أسماء مسجلة حالياً في النظام.
              </div>
            ) : (
              employees.map((emp) => {
                const isEditing = editingEmpId === emp.id;
                return (
                  <div 
                    key={emp.id} 
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      background: 'rgba(51, 65, 85, 0.15)', 
                      border: '1px solid var(--border)', 
                      padding: '14px 16px', 
                      borderRadius: '14px', 
                      flexWrap: 'wrap', 
                      gap: '12px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1', minWidth: '180px' }}>
                      <span style={{ fontWeight: 700, color: '#fff', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {emp.role === 'driver' ? '🛵' : '👨‍⚕️'} {emp.name}
                        <span style={{ 
                          fontSize: '0.75rem', 
                          padding: '2px 8px', 
                          background: emp.role === 'driver' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(59, 130, 246, 0.15)', 
                          color: emp.role === 'driver' ? '#a78bfa' : '#60a5fa', 
                          borderRadius: '99px',
                          fontWeight: 700
                        }}>
                          {emp.role === 'driver' ? 'طيار دليفري' : 'دكتور صيدلي'}
                        </span>
                      </span>
                      {isEditing ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>الـ PIN الجديد:</span>
                          <input 
                            type="text" 
                            maxLength={4} 
                            value={editingPinValue} 
                            onChange={e => setEditingPinValue(e.target.value.replace(/\D/g, ''))} 
                            style={{ 
                              width: '90px', 
                              padding: '6px 8px', 
                              borderRadius: '8px', 
                              background: 'rgba(0,0,0,0.3)', 
                              border: '1px solid var(--border)', 
                              color: 'var(--accent-gold)', 
                              textAlign: 'center', 
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              letterSpacing: '2px'
                            }}
                            placeholder="4 أرقام"
                          />
                        </div>
                      ) : (
                        <>
                          {emp.pin ? (
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                              رمز الـ PIN الحالي: <strong 
                                onClick={() => setRevealedEmpPins(prev => ({ ...prev, [emp.id]: !prev[emp.id] }))}
                                style={{ color: 'var(--accent-gold)', background: 'rgba(217, 119, 6, 0.1)', padding: '2px 8px', borderRadius: '6px', marginRight: '4px', cursor: 'pointer', userSelect: 'none' }}
                                title="اضغط لإظهار/إخفاء الرمز"
                              >
                                {revealedEmpPins[emp.id] ? emp.pin : '•••• 👁️'}
                              </strong>
                            </span>
                          ) : null}
                          {emp.role === 'driver' && (
                            <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '4px' }}>
                              <span>⏱️ الساعة: {emp.regularRate || 0} ج.م</span>
                              <span>➕ إضافي: {emp.overtimeRate || 1.5}x</span>
                              <span>🎉 عطلة: {emp.holidayRate || 2.0}x</span>
                              <span>🛵 عمولة: {emp.commissionPerOrder || 0} ج.م</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {isEditing ? (
                        <>
                          <button 
                            onClick={() => handleUpdateEmployeePin(emp.id, editingPinValue)} 
                            style={{ background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#34d399', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700, transition: 'all 0.2s' }}
                          >
                            ✔️ حفظ
                          </button>
                          <button 
                            onClick={() => { setEditingEmpId(null); setEditingPinValue(''); }} 
                            style={{ background: 'rgba(255, 255, 255, 0.08)', border: '1px solid var(--border)', color: 'var(--text-secondary)', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700, transition: 'all 0.2s' }}
                          >
                            إلغاء
                          </button>
                        </>
                      ) : (
                        <>
                          {emp.role !== 'driver' && (
                            <button 
                              onClick={() => { setEditingEmpId(emp.id); setEditingPinValue(emp.pin || ''); }} 
                              style={{ background: 'rgba(56, 189, 248, 0.12)', border: '1px solid rgba(56, 189, 248, 0.2)', color: '#38bdf8', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700, transition: 'all 0.2s' }}
                            >
                              ✏️ تعديل PIN
                            </button>
                          )}
                          <button 
                            onClick={() => handleDeleteEmployee(emp.id)} 
                            style={{ background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#fb7185', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700, transition: 'all 0.2s' }}
                          >
                            🗑️ حذف
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Electronic Payment Settings Card */}
        <div className="premium-card" style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg, rgba(13, 20, 36, 0.7) 0%, rgba(225, 29, 72, 0.03) 100%)' }}>
          <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.6rem' }}>⚙️</span> إعدادات المحافظ الرقمية والدفع الإلكتروني (إنستاباي / فودافون كاش)
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.95rem', lineHeight: '1.6' }}>
            أدخل معرف إنستاباي ورابط دفع فودافون كاش الخاص بالصيدلية ليتم تفعيل وعرض طرق الدفع الرقمية هذه تلقائياً لعملائك أثناء عملية الشراء والطلب.
          </p>

          <form onSubmit={handleSavePaymentSettings} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', alignItems: 'end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>عنوان إنستاباي (InstaPay IPN):</label>
              <input 
                type="text" 
                className="premium-input" 
                value={instaPayIPNInput} 
                onChange={e => setInstaPayIPNInput(e.target.value)} 
                placeholder="مثال: pharmacy@instapay" 
                style={{ width: '100%', fontSize: '1rem' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>رابط الدفع فودافون كاش (Vodafone Cash Payment Link):</label>
              <input 
                type="text" 
                className="premium-input" 
                value={vodafoneCashNumberInput} 
                onChange={e => setVodafoneCashNumberInput(e.target.value)} 
                placeholder="مثال: https://vfcash.eg/pay?id=..." 
                style={{ width: '100%', fontSize: '1rem' }}
              />
            </div>

            <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)', marginTop: '8px' }}>
              <input 
                type="checkbox" 
                id="allowDeferredSalesToggle"
                checked={allowDeferredSalesInput}
                onChange={e => setAllowDeferredSalesInput(e.target.checked)}
                style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: 'var(--primary)' }}
              />
              <label htmlFor="allowDeferredSalesToggle" style={{ fontSize: '0.95rem', color: '#fff', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>
                ⏳ تفعيل خيار البيع بالآجل (تسجيل الديون والمبيعات المؤجلة للعملاء)
              </label>
            </div>

            <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
              <button 
                type="submit" 
                className="gradient-btn" 
                style={{ 
                  padding: '14px 28px', 
                  fontSize: '1rem', 
                  fontWeight: 700, 
                  background: 'linear-gradient(135deg, var(--primary) 0%, #2563eb 100%)',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }} 
                disabled={loading}
              >
                {loading ? 'جاري الحفظ...' : '💾 حفظ إعدادات الدفع'}
              </button>
            </div>
          </form>
        </div>

        {/* Telegram settings card */}
        <div className="premium-card" style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg, rgba(13, 20, 36, 0.7) 0%, rgba(13, 148, 136, 0.05) 100%)' }}>
          <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.6rem' }}>✈️</span> إعدادات إشعارات التليجرام الفورية (Telegram)
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.95rem', lineHeight: '1.6' }}>فعل الإشعارات لاستقبال تقارير المبيعات وتقارير إغلاق الورديات بشكل تلقائي وسريع على هاتفك.</p>

          {/* Guide/Explanation Section */}
          <div style={{
            background: 'rgba(8, 13, 27, 0.45)',
            border: '1px solid rgba(59, 130, 246, 0.25)',
            padding: '24px',
            borderRadius: '16px',
            marginBottom: '28px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <h4 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <span>ℹ️</span> دليل تفعيل بوت التليجرام واستلام التقارير التلقائية
            </h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: '1.6', margin: 0 }}>
              لتفعيل الإشعارات التلقائية الفورية (مع كل عملية بيع منتج أو عند إغلاق وتسليم الوردية المالي) مباشرة على هاتفك، يرجى اتباع الخطوات التالية بدقة:
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginTop: '8px' }}>
              <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)', display: 'flex', gap: '12px' }}>
                <span style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--primary)', lineHeight: 1 }}>1</span>
                <div>
                  <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#fff', display: 'block', marginBottom: '4px' }}>ابحث عن البوت</span>
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: '1.4', display: 'block' }}>
                    افتح تليجرام وابحث عن البوت المالي <a href="https://t.me/DR_Masr_Bot" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 700, textDecoration: 'underline' }}>@DR_Masr_Bot</a>.
                  </span>
                </div>
              </div>
              
              <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)', display: 'flex', gap: '12px' }}>
                <span style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--primary)', lineHeight: 1 }}>2</span>
                <div>
                  <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#fff', display: 'block', marginBottom: '4px' }}>ابدأ المحادثة</span>
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: '1.4', display: 'block' }}>
                    اضغط على زر <strong>Start / ابدأ</strong> داخل البوت لتفعيل الاشتراك الأمني والمالي الفوري.
                  </span>
                </div>
              </div>
              
              <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)', display: 'flex', gap: '12px' }}>
                <span style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--primary)', lineHeight: 1 }}>3</span>
                <div>
                  <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#fff', display: 'block', marginBottom: '4px' }}>احصل على الـ Chat ID</span>
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: '1.4', display: 'block' }}>
                    سيرد عليك البوت فوراً برقم <strong>Chat ID</strong> الخاص بك تلقائياً على الشاشة.
                  </span>
                </div>
              </div>
              
              <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)', display: 'flex', gap: '12px' }}>
                <span style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--primary)', lineHeight: 1 }}>4</span>
                <div>
                  <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#fff', display: 'block', marginBottom: '4px' }}>احفظ الإعدادات</span>
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: '1.4', display: 'block' }}>
                    انسخ الرقم المعروض وضعه في خانة <strong>Chat ID</strong> بالأسفل، وفعل الإشعارات ثم احفظ.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSaveTelegram} style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '14px', 
              background: tgEnabled ? 'rgba(59, 130, 246, 0.08)' : 'rgba(51, 65, 85, 0.15)', 
              border: tgEnabled ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid var(--border)', 
              padding: '16px', 
              borderRadius: '14px',
              transition: 'all 0.25s'
            }}>
              <input 
                type="checkbox" 
                id="tgEnabled" 
                checked={tgEnabled} 
                onChange={e => setTgEnabled(e.target.checked)} 
                style={{ width: '22px', height: '22px', cursor: 'pointer', accentColor: 'var(--primary)' }} 
              />
              <label htmlFor="tgEnabled" style={{ fontSize: '1.05rem', color: '#fff', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Tفعيل إشعارات التليجرام التلقائية
                {tgEnabled && <span style={{ fontSize: '0.8rem', padding: '2px 8px', background: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', borderRadius: '6px', fontWeight: 'bold' }}>نشط ✔️</span>}
              </label>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>رقم الشات / معرف التليجرام (Chat ID):</label>
              <input 
                type="text" 
                className="premium-input" 
                value={tgChatId} 
                onChange={e => setTgChatId(e.target.value)} 
                placeholder="مثال: 123456789" 
                style={{ width: '100%', fontSize: '1.1rem', fontWeight: '600' }}
              />
            </div>

            {/* ─── Auto Reports Control Section ─── */}
            {tgEnabled && (
              <div style={{ background: 'rgba(8,13,27,0.5)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '1.3rem' }}>🤖</span>
                  <div>
                    <h4 style={{ color: '#fff', fontWeight: 800, fontSize: '1rem', margin: 0 }}>ضبط التقارير التلقائية</h4>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: '2px 0 0 0' }}>اختر أنواع التقارير التي تريد استقبالها تلقائياً على تليجرام</p>
                  </div>
                </div>

                {/* Daily Sales Report */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: !tgDisableDailySales ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.01)', border: !tgDisableDailySales ? '1px solid rgba(16,185,129,0.2)' : '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                      <span style={{ fontSize: '1.1rem' }}>📊</span>
                      <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>التقرير اليومي الشامل للمبيعات</span>
                      <span style={{ fontSize: '0.75rem', padding: '2px 8px', background: 'rgba(16,185,129,0.15)', color: '#34d399', borderRadius: '6px', fontWeight: 700 }}>يومياً</span>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0, paddingRight: '28px' }}>
                      ملخص مبيعات اليوم: الإجمالي + كاش + شبكة + أعلى 5 أصناف + مقارنة بالأمس
                    </p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={!tgDisableDailySales}
                      onChange={e => setTgDisableDailySales(!e.target.checked)}
                      style={{ width: '20px', height: '20px', accentColor: '#10b981', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.85rem', color: !tgDisableDailySales ? '#34d399' : 'var(--text-secondary)', fontWeight: 700 }}>
                      {!tgDisableDailySales ? '✅ مفعّل' : '⭕ معطّل'}
                    </span>
                  </label>
                </div>

                {/* Weekly Report */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: !tgDisableWeekly ? 'rgba(99,102,241,0.06)' : 'rgba(255,255,255,0.01)', border: !tgDisableWeekly ? '1px solid rgba(99,102,241,0.2)' : '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                      <span style={{ fontSize: '1.1rem' }}>📈</span>
                      <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>التقرير الأسبوعي الشامل للأداء</span>
                      <span style={{ fontSize: '0.75rem', padding: '2px 8px', background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', borderRadius: '6px', fontWeight: 700 }}>أسبوعياً</span>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0, paddingRight: '28px' }}>
                      تقرير أسبوعي: إجمالي المبيعات + توزيع يومي + معدل يومي + أكثر 5 أصناف مبيعاً
                    </p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={!tgDisableWeekly}
                      onChange={e => setTgDisableWeekly(!e.target.checked)}
                      style={{ width: '20px', height: '20px', accentColor: '#6366f1', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.85rem', color: !tgDisableWeekly ? '#a5b4fc' : 'var(--text-secondary)', fontWeight: 700 }}>
                      {!tgDisableWeekly ? '✅ مفعّل' : '⭕ معطّل'}
                    </span>
                  </label>
                </div>

                {/* Shortages & Expiry Report */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: !tgDisableShortages ? 'rgba(245,158,11,0.06)' : 'rgba(255,255,255,0.01)', border: !tgDisableShortages ? '1px solid rgba(245,158,11,0.2)' : '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                      <span style={{ fontSize: '1.1rem' }}>🚨</span>
                      <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>تنبيهات النواقص والصلاحيات</span>
                      <span style={{ fontSize: '0.75rem', padding: '2px 8px', background: 'rgba(245,158,11,0.15)', color: '#fbbf24', borderRadius: '6px', fontWeight: 700 }}>كل 12 ساعة</span>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0, paddingRight: '28px' }}>
                      قائمة الأدوية التي مخزونها ≤5 وحدات + الصلاحيات التي ستنتهي خلال 6 أشهر
                    </p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={!tgDisableShortages}
                      onChange={e => setTgDisableShortages(!e.target.checked)}
                      style={{ width: '20px', height: '20px', accentColor: '#f59e0b', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.85rem', color: !tgDisableShortages ? '#fbbf24' : 'var(--text-secondary)', fontWeight: 700 }}>
                      {!tgDisableShortages ? '✅ مفعّل' : '⭕ معطّل'}
                    </span>
                  </label>
                </div>

                {/* Always-on reports info */}
                <div style={{ background: 'rgba(59,130,246,0.04)', border: '1px solid rgba(59,130,246,0.12)', borderRadius: '10px', padding: '12px 14px', fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  ℹ️ <strong style={{ color: '#60a5fa' }}>تقارير تلقائية دائمة:</strong> إشعار إغلاق الوردية يُرسل دائماً عند كل إغلاق وردية، وتنبيه انتهاء الاشتراك يُرسل تلقائياً عند الاقتراب من نهاية المدة.
                </div>
              </div>
            )}

            <button 
              type="submit" 
              className="gradient-btn" 
              style={{ 
                padding: '14px', 
                fontSize: '1rem', 
                fontWeight: 700, 
                marginTop: '8px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                border: 'none',
                borderRadius: '12px'
              }} 
              disabled={loading}
            >
              {loading ? 'جاري الحفظ...' : '💾 حفظ إعدادات إشعارات تليجرام'}
            </button>
          </form>
        </div>

        {/* Migration / Copy database card */}
        {user?.email === 'baselsalah07@gmail.com' && (
          <div className="premium-card" style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', background: 'linear-gradient(135deg, rgba(13, 20, 36, 0.7) 0%, rgba(168, 85, 247, 0.05) 100%)' }}>
            <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.6rem' }}>⚡</span> ترحيل ومزامنة قاعدة البيانات العامة
            </h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.95rem', lineHeight: '1.6' }}>
              يتيح لك هذا الخيار نسخ ومزامنة الأدوية والباركود بين مجموعات قاعدة البيانات لتحديث وتكامل سجلات البحث العامة.
            </p>

            {/* Database Collection Statistics Badges */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '150px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>عدد أدوية global_medicines</span>
                <strong style={{ fontSize: '1.4rem', color: '#38bdf8' }}>
                  {loadingCounts ? '🔄' : (medicinesCount !== null ? medicinesCount.toLocaleString() : '—')}
                </strong>
              </div>

              <div style={{ flex: 1, minWidth: '150px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>عدد أدوية global_barcodes</span>
                <strong style={{ fontSize: '1.4rem', color: '#a855f7' }}>
                  {loadingCounts ? '🔄' : (barcodesCount !== null ? barcodesCount.toLocaleString() : '—')}
                </strong>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {migrationError && (
                <div style={{ padding: '14px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171', borderRadius: '12px', fontSize: '0.95rem', fontWeight: 'bold' }}>
                  ❌ حدث خطأ أثناء الترحيل: {migrationError}
                </div>
              )}

              {migrationProgress && (
                <div style={{ padding: '14px', background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.3)', color: '#38bdf8', borderRadius: '12px', fontSize: '0.95rem', fontWeight: 'bold', textAlign: 'center' }}>
                  ℹ️ {migrationProgress}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                <button
                  type="button"
                  onClick={() => handleMigrate('global_medicines', 'global_barcodes')}
                  className="gradient-btn"
                  style={{
                    padding: '14px',
                    fontSize: '1rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #a855f7 0%, #d946ef 100%)',
                    boxShadow: '0 4px 12px rgba(168, 85, 247, 0.25)',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  disabled={migrationLoading}
                >
                  {migrationLoading ? '🔄 جاري الترحيل...' : '🚀 نسخ global_medicines ⬅️ global_barcodes'}
                </button>

                <button
                  type="button"
                  onClick={() => handleMigrate('global_barcodes', 'global_medicines')}
                  className="gradient-btn"
                  style={{
                    padding: '14px',
                    fontSize: '1rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                    boxShadow: '0 4px 12px rgba(13, 148, 136, 0.25)',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  disabled={migrationLoading}
                >
                  {migrationLoading ? '🔄 جاري الترحيل...' : '🚀 نسخ global_barcodes ⬅️ global_medicines'}
                </button>

                <button
                  type="button"
                  onClick={handleMigrateCommonDrugs}
                  className="gradient-btn"
                  style={{
                    gridColumn: 'span 2',
                    padding: '14px',
                    fontSize: '1rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    marginTop: '8px'
                  }}
                  disabled={migrationLoading}
                >
                  {migrationLoading ? '🔄 جاري نسخ الأدوية...' : `🚀 نسخ الأدوية المدمجة في الكود (COMMON_EGYPTIAN_DRUGS) ⬅️ global_barcodes (${COMMON_EGYPTIAN_DRUGS.length} دواء)`}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}



function RefundRow({ it, handleRefundItem, invoiceId }) {
  const [qty, setQty] = React.useState(it.quantity);
  
  return (
    <div className="premium-card" style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 18px',
      background: 'rgba(255, 255, 255, 0.02)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      borderRadius: '14px',
      gap: '12px',
      marginBottom: '8px'
    }}>
      <div style={{ flex: 1, textAlign: 'right' }}>
        <h4 style={{ color: '#fff', fontSize: '0.92rem', margin: '0 0 4px 0' }}>
          {it.tradeName || it.name || 'صنف'}
        </h4>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          الكمية المباعة: {it.subUnit ? `${Math.round(it.quantity * it.subUnit.ratio)} ${it.subUnit.name} (${Number(it.quantity.toFixed(4))} علبة)` : `${it.quantity} علبة`} | السعر: {it.price} ج.م
        </span>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input 
          type="number" 
          step={it.subUnit ? Number((1/it.subUnit.ratio).toFixed(4)) : "1"}
          min={it.subUnit ? Number((1/it.subUnit.ratio).toFixed(4)) : "1"}
          max={it.quantity} 
          value={qty} 
          onChange={e => {
            const val = parseFloat(e.target.value);
            if (!isNaN(val)) {
              setQty(Math.min(it.quantity, val));
            } else {
              setQty('');
            }
          }}
          style={{
            width: '70px',
            padding: '6px 10px',
            borderRadius: '8px',
            background: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#fff',
            textAlign: 'center',
            fontSize: '0.85rem'
          }}
        />
        <button 
          onClick={() => handleRefundItem(invoiceId, it.barcode || '', qty, it.tradeName || '')}
          className="gradient-btn"
          style={{
            padding: '6px 12px',
            fontSize: '0.85rem',
            background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            borderRadius: '8px'
          }}
        >
          إرجاع ↩️
        </button>
      </div>
    </div>
  );
}


function DeliveryView({ 
  sales, setSales, inventory, setInventory, user, employees, setActiveTab,
  telegramSettings, currentUserData, isSalesLoading, fetchSales, setIsDeliveryOrder,
  activeShift, setGlobalLoading, db, printSaleReceipt, shareSaleViaWhatsApp
}) {
  const [activeSubTab, setActiveSubTab] = useState('pending'); 
  const [reconcilingSale, setReconcilingSale] = useState(null); 
  const [confirmFullReturnSale, setConfirmFullReturnSale] = useState(null); 
  const [returnedQuantities, setReturnedQuantities] = useState({}); 
  const [actionLoading, setActionLoading] = useState(false);

  // Driver Shifts & Compensation States
  const [driverShifts, setDriverShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [isHolidayShift, setIsHolidayShift] = useState(false);
  const [unpaidSales, setUnpaidSales] = useState([]);
  const [loadingUnpaidSales, setLoadingUnpaidSales] = useState(false);
  const [driverShiftsTabStatus, setDriverShiftsTabStatus] = useState('');

  const fetchDriverShifts = async () => {
    if (!user) return;
    setShiftsLoading(true);
    try {
      const q = query(
        collection(db, 'users', user.uid, 'driver_shifts'),
        orderBy('checkIn', 'desc'),
        limit(100)
      );
      const snap = await getDocs(q);
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDriverShifts(list);
    } catch (err) {
      console.error("Error fetching driver shifts:", err);
    } finally {
      setShiftsLoading(false);
    }
  };

  const fetchUnpaidDriverSales = async (driverName) => {
    if (!user || !driverName) return;
    setLoadingUnpaidSales(true);
    try {
      const q = query(
        collection(db, 'users', user.uid, 'sales'),
        where('isDelivery', '==', true),
        where('deliveryStatus', '==', 'delivered'),
        limit(200)
      );
      const snap = await getDocs(q);
      const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const filtered = fetched.filter(s => 
        (s.deliveryDetails?.driverName && s.deliveryDetails.driverName.trim() === driverName.trim()) &&
        s.driverPaid !== true
      );
      setUnpaidSales(filtered);
    } catch (err) {
      console.error("Error fetching unpaid driver sales:", err);
    } finally {
      setLoadingUnpaidSales(false);
    }
  };

  useEffect(() => {
    if (activeSubTab === 'driver_shifts') {
      fetchDriverShifts();
      if (selectedDriverId) {
        const dr = employees.find(emp => emp.id === selectedDriverId);
        if (dr) {
          fetchUnpaidDriverSales(dr.name);
        }
      }
    }
  }, [activeSubTab, selectedDriverId]);

  const handleDriverCheckIn = async (driver) => {
    if (!user || !driver) return;
    setDriverShiftsTabStatus('⏳ جاري تسجيل حضور الطيار...');
    try {
      const newShift = {
        driverId: driver.id,
        driverName: driver.name,
        checkIn: new Date().toISOString(),
        checkOut: null,
        isHoliday: isHolidayShift,
        status: 'active',
        paid: false
      };
      await addDoc(collection(db, 'users', user.uid, 'driver_shifts'), newShift);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast(`🏁 تم تسجيل حضور الطيار ${driver.name} بنجاح!`);
      }
      fetchDriverShifts();
    } catch (err) {
      console.error("Check-in error:", err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ فشل تسجيل الحضور: " + err.message);
      }
    } finally {
      setDriverShiftsTabStatus('');
    }
  };

  const handleDriverCheckOut = async (shift) => {
    if (!user || !shift) return;
    setDriverShiftsTabStatus('⏳ جاري تسجيل انصراف الطيار...');
    try {
      const checkOutTime = new Date().toISOString();
      const elapsedMs = new Date(checkOutTime) - new Date(shift.checkIn);
      const hoursWorked = Math.max(0.1, Math.round((elapsedMs / (1000 * 60 * 60)) * 100) / 100);

      const shiftRef = doc(db, 'users', user.uid, 'driver_shifts', shift.id);
      await updateDoc(shiftRef, {
        checkOut: checkOutTime,
        hoursWorked: hoursWorked,
        status: 'completed'
      });

      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast(`🛑 تم تسجيل انصراف الطيار بنجاح! عدد الساعات: ${hoursWorked} ساعة.`);
      }
      fetchDriverShifts();
    } catch (err) {
      console.error("Check-out error:", err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ فشل تسجيل الانصراف: " + err.message);
      }
    } finally {
      setDriverShiftsTabStatus('');
    }
  };

  const handleDriverSettlement = async (driver, totalDue, unpaidShiftsToPay, unpaidSalesToPay) => {
    if (!user || !driver || totalDue <= 0) return;
    setDriverShiftsTabStatus('⏳ جاري صرف المستحقات...');
    try {
      const batch = writeBatch(db);

      // 1. Mark shifts as paid
      unpaidShiftsToPay.forEach(sh => {
        const shRef = doc(db, 'users', user.uid, 'driver_shifts', sh.id);
        batch.update(shRef, { paid: true });
      });

      // 2. Mark sales as driverPaid = true
      unpaidSalesToPay.forEach(s => {
        const sRef = doc(db, 'users', user.uid, 'sales', s.id);
        batch.update(sRef, { driverPaid: true });
      });

      // 3. Record cash expense transaction in Accounting ledger
      const accRef = doc(collection(db, 'users', user.uid, 'accounting'));
      const expenseTx = {
        type: 'expense',
        amount: Number(totalDue),
        description: `تصفية وصرف مستحقات الطيار: ${driver.name}`,
        timestamp: new Date().toISOString(),
        recordedBy: activeShift?.cashierName || 'المدير المسؤول 🛡️',
        shiftId: activeShift?.id || ''
      };
      batch.set(accRef, expenseTx);

      await batch.commit();

      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast(`💵 تم تصفية حساب الطيار ${driver.name} وصرف مبلغ ${totalDue} ج.م بنجاح!`);
      }

      fetchDriverShifts();
      fetchUnpaidDriverSales(driver.name);
    } catch (err) {
      console.error("Settlement error:", err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ فشل تصفية الحساب: " + err.message);
      }
    } finally {
      setDriverShiftsTabStatus('');
    }
  };

  // Default Delivery Fee Manager State & Handlers
  const [defaultFee, setDefaultFee] = useState(currentUserData?.defaultDeliveryFee !== undefined ? currentUserData.defaultDeliveryFee : 15);
  const [isSavingFee, setIsSavingFee] = useState(false);

  useEffect(() => {
    if (currentUserData?.defaultDeliveryFee !== undefined) {
      setDefaultFee(currentUserData.defaultDeliveryFee);
    }
  }, [currentUserData?.defaultDeliveryFee]);

  const handleSaveDefaultFee = async () => {
    if (!user) return;
    setIsSavingFee(true);
    const val = parseFloat(defaultFee);
    const finalVal = isNaN(val) ? 0 : Math.max(0, val);
    try {
      await updateDoc(doc(db, 'users', user.uid), { defaultDeliveryFee: finalVal });
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("✅ تم حفظ سعر توصيل الدليفري الجديد بنجاح!");
      }
    } catch (err) {
      console.error("Failed to update default delivery fee:", err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ فشل حفظ السعر: " + err.message);
      }
    } finally {
      setIsSavingFee(false);
    }
  };

  const [localLoading, setLocalLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [counts, setCounts] = useState({ pending: 0, out: 0, delivered: 0, returned: 0 });
  const [pageSales, setPageSales] = useState([]);
  
  const [docSnapshots, setDocSnapshots] = useState([null]);
  const [fallbackMode, setFallbackMode] = useState(false);

  const [receiptSearchTerm, setReceiptSearchTerm] = useState('');

  const handleSearchReceipt = async () => {
    const term = receiptSearchTerm.trim();
    if (!term) return;
    
    if (setGlobalLoading) {
      setGlobalLoading({
        active: true,
        title: 'جاري البحث عن البون 🔍',
        message: `جاري البحث عن فاتورة دليفري برقم البون أو الهاتف (${term}) في السيرفر...`
      });
    }
    
    try {
      const qInvoice = query(
        collection(db, 'users', user.uid, 'sales'),
        where('invoiceId', '==', term),
        where('isDelivery', '==', true)
      );
      const qPhone = query(
        collection(db, 'users', user.uid, 'sales'),
        where('customerPhone', '==', term),
        where('isDelivery', '==', true)
      );
      
      const [snapInvoice, snapPhone] = await Promise.all([
        getDocs(qInvoice),
        getDocs(qPhone)
      ]);
      
      const mergedMap = new Map();
      snapInvoice.docs.forEach(doc => mergedMap.set(doc.id, { id: doc.id, ...doc.data() }));
      snapPhone.docs.forEach(doc => mergedMap.set(doc.id, { id: doc.id, ...doc.data() }));
      
      const found = Array.from(mergedMap.values());
      
      if (found.length > 0) {
        found.sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
        setPageSales(found);
        
        const newCounts = { pending: 0, out: 0, delivered: 0, returned: 0 };
        found.forEach(s => {
          const status = s.deliveryStatus || 'pending';
          if (newCounts[status] !== undefined) {
            newCounts[status]++;
          }
        });
        setCounts(newCounts);
        setActiveSubTab(found[0].deliveryStatus || 'pending');
        
        if (window.triggerDrMasrToast) {
          window.triggerDrMasrToast(`✅ تم العثور على عدد (${found.length}) بون دليفري بنجاح!`);
        }
      } else {
        if (window.triggerDrMasrToast) {
          window.triggerDrMasrToast("❌ لم يتم العثور على بون دليفري بهذا الرقم أو الهاتف.");
        }
      }
    } catch (err) {
      console.error("Error searching receipt:", err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ خطأ أثناء البحث.");
      }
    } finally {
      if (setGlobalLoading) {
        setGlobalLoading({ active: false, title: '', message: '' });
      }
    }
  };

  // Map paginated stats and page lists to legacy JSX variables to avoid ReferenceErrors
  const pendingSales = { length: counts.pending || 0 };
  const outSales = { length: counts.out || 0 };
  const completedSales = { length: counts.delivered || 0 };
  const returnedSales = { length: counts.returned || 0 };
  const currentList = pageSales || [];
  const totalPages = Math.ceil((counts[activeSubTab] || 0) / 6) || 1;

  const fetchCounts = async () => {
    if (!user) return;
    try {
      const statuses = ['pending', 'out', 'delivered', 'returned'];
      const newCounts = { pending: 0, out: 0, delivered: 0, returned: 0 };
      
      await Promise.all(statuses.map(async (status) => {
        let constraints = [
          where('isDelivery', '==', true),
          where('deliveryStatus', '==', status)
        ];
        
        if (status === 'delivered' || status === 'returned') {
          if (!activeShift) {
            newCounts[status] = 0;
            return;
          }
          constraints.push(where('shiftId', '==', activeShift.id));
        }
        
        const qCount = query(
          collection(db, 'users', user.uid, 'sales'),
          ...constraints
        );
        const snapshot = await getCountFromServer(qCount);
        newCounts[status] = snapshot.data().count;
      }));
      setCounts(newCounts);
    } catch (err) {
      console.error("Failed to fetch counts from server:", err);
    }
  };

  const fetchFallbackSales = async () => {
    try {
      const q = query(
        collection(db, 'users', user.uid, 'sales'),
        where('isDelivery', '==', true),
        limit(100)
      );
      const snap = await getDocs(q);
      const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      fetched.sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
      return fetched;
    } catch (err) {
      console.error("Fallback query error:", err);
      return [];
    }
  };

  const fetchPage = async (pageNumber, subTab = activeSubTab) => {
    if (!user) return;
    setLocalLoading(true);
    try {
      if (fallbackMode) {
        const allFetched = await fetchFallbackSales();
        const filtered = allFetched.filter(s => {
          if (s.deliveryStatus !== subTab) return false;
          if (subTab === 'delivered' || subTab === 'returned') {
            return activeShift ? s.shiftId === activeShift.id : false;
          }
          return true;
        });
        
        const computedCounts = { pending: 0, out: 0, delivered: 0, returned: 0 };
        allFetched.forEach(s => {
          if (s.deliveryStatus && computedCounts[s.deliveryStatus] !== undefined) {
            if (s.deliveryStatus === 'delivered' || s.deliveryStatus === 'returned') {
              if (activeShift && s.shiftId === activeShift.id) {
                computedCounts[s.deliveryStatus]++;
              }
            } else {
              computedCounts[s.deliveryStatus]++;
            }
          }
        });
        setCounts(computedCounts);

        const startIndex = (pageNumber - 1) * 6;
        const pageData = filtered.slice(startIndex, startIndex + 6);
        setPageSales(pageData);
      } else {
        const salesCol = collection(db, 'users', user.uid, 'sales');
        let q;
        const startDoc = docSnapshots[pageNumber - 1];
        
        const constraints = [
          where('isDelivery', '==', true),
          where('deliveryStatus', '==', subTab)
        ];
        
        if (subTab === 'delivered' || subTab === 'returned') {
          if (!activeShift) {
            setPageSales([]);
            setCounts(prev => ({ ...prev, [subTab]: 0 }));
            setLocalLoading(false);
            return;
          }
          constraints.push(where('shiftId', '==', activeShift.id));
        }
        
        if (startDoc) {
          q = query(
            salesCol,
            ...constraints,
            orderBy('timestamp', 'desc'),
            startAfter(startDoc),
            limit(6)
          );
        } else {
          q = query(
            salesCol,
            ...constraints,
            orderBy('timestamp', 'desc'),
            limit(6)
          );
        }
        
        const snap = await getDocs(q);
        const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPageSales(fetched);
        
        if (snap.docs.length > 0) {
          const nextSnapshots = [...docSnapshots];
          nextSnapshots[pageNumber] = snap.docs[snap.docs.length - 1];
          setDocSnapshots(nextSnapshots);
        }
      }
    } catch (err) {
      console.error("Firestore paginated fetch error:", err);
      if (err.message && (err.message.includes("index") || err.message.includes("INDEX"))) {
        console.warn("Composite index missing. Falling back to local pagination mode...");
        setFallbackMode(true);
        const allFetched = await fetchFallbackSales();
        const filtered = allFetched.filter(s => {
          if (s.deliveryStatus !== subTab) return false;
          if (subTab === 'delivered' || subTab === 'returned') {
            return activeShift ? s.shiftId === activeShift.id : false;
          }
          return true;
        });
        
        const computedCounts = { pending: 0, out: 0, delivered: 0, returned: 0 };
        allFetched.forEach(s => {
          if (s.deliveryStatus && computedCounts[s.deliveryStatus] !== undefined) {
            if (s.deliveryStatus === 'delivered' || s.deliveryStatus === 'returned') {
              if (activeShift && s.shiftId === activeShift.id) {
                computedCounts[s.deliveryStatus]++;
              }
            } else {
              computedCounts[s.deliveryStatus]++;
            }
          }
        });
        setCounts(computedCounts);

        const startIndex = (pageNumber - 1) * 6;
        const pageData = filtered.slice(startIndex, startIndex + 6);
        setPageSales(pageData);
      } else {
        if (window.triggerDrMasrToast) {
          window.triggerDrMasrToast("❌ فشل استدعاء طلبات الدليفري.");
        }
      }
    } finally {
      setLocalLoading(false);
    }
  };

  useEffect(() => {
    if (receiptSearchTerm.trim() !== '') return;
    const loadDeliveryData = async () => {
      if (setGlobalLoading) {
        setGlobalLoading({
          active: true,
          title: 'تحميل الدليفري 🛵',
          message: 'جاري استدعاء طلبات التوصيل وأوردرات الطيارين من السيرفر...'
        });
      }
      setCurrentPage(1);
      setDocSnapshots([null]);
      try {
        await Promise.all([
          fetchCounts(),
          fetchPage(1, activeSubTab)
        ]);
      } catch (err) {
        console.error("Error loading delivery view data:", err);
      } finally {
        if (setGlobalLoading) {
          setGlobalLoading({ active: false, title: '', message: '' });
        }
      }
    };
    loadDeliveryData();
  }, [activeSubTab, user, receiptSearchTerm, activeShift]);

  const updateDeliveryStatus = async (sale, newStatus) => {
    setActionLoading(true);
    try {
      const nextSales = sales.map(s => {
        if (s.invoiceId === sale.invoiceId) {
          const updated = { ...s, deliveryStatus: newStatus };
          if (newStatus === 'delivered') {
            updated.deliveredAt = new Date().toISOString();
            if (activeShift) {
              updated.shiftId = activeShift.id;
            }
          }
          return updated;
        }
        return s;
      });
      setSales(nextSales);
      localStorage.setItem('drmasr_sales_' + (user.uid), JSON.stringify(nextSales));

      const updateData = { deliveryStatus: newStatus };
      if (newStatus === 'delivered') {
        updateData.deliveredAt = new Date().toISOString();
        if (activeShift) {
          updateData.shiftId = activeShift.id;
        }
      }
      await updateDoc(doc(db, 'users', user.uid, 'sales', sale.id), updateData);

      setPageSales(prev => prev.filter(s => s.invoiceId !== sale.invoiceId));
      setCounts(prev => ({
        ...prev,
        [activeSubTab]: Math.max(0, prev[activeSubTab] - 1),
        [newStatus]: (prev[newStatus] || 0) + 1
      }));
      
      // Update Pilot's workedHours and ordersCount in Firestore
      if (newStatus === 'delivered' && sale.deliveryDetails?.driverName) {
        try {
          const saleTime = new Date(sale.timestamp);
          const endTime = new Date();
          const durationHours = Math.max(0, (endTime - saleTime) / (1000 * 60 * 60));
          const finalDuration = durationHours > 5 ? 0.5 : durationHours; // Clamp if too long
          const cleanedDriver = cleanName(sale.deliveryDetails.driverName);
          const matchedEmp = employees.find(emp => cleanName(emp.name) === cleanedDriver);
          if (matchedEmp && matchedEmp.id) {
            const empDocRef = doc(db, 'users', user.uid, 'employees', matchedEmp.id);
            const currentHours = matchedEmp.workedHours || 0;
            const currentOrders = matchedEmp.ordersCount || 0;
            await updateDoc(empDocRef, {
              workedHours: Math.round((currentHours + finalDuration) * 100) / 100,
              ordersCount: currentOrders + 1
            });
          }
        } catch (err) {
          console.error('Failed to update pilot firebase hours:', err);
        }
      }

      setPageSales(prev => prev.filter(s => s.invoiceId !== sale.invoiceId));
      setCounts(prev => ({
        ...prev,
        [activeSubTab]: Math.max(0, prev[activeSubTab] - 1),
        [newStatus]: (prev[newStatus] || 0) + 1
      }));

      if (telegramSettings && telegramSettings.enabled) {
        const pharmacyName = currentUserData?.pharmacyName || "صيدلية دكتور مصر";
        const shortId = currentUserData?.shortId || "غير متوفر";

        if (newStatus === 'out') {
          let outMsg = `🛵 <b>خروج أوردر دليفري (قيد التوصيل)</b>\n` +
                       `🏥 <b>الصيدلية:</b> <code>${pharmacyName}</code> (${shortId})\n` +
                       `━━━━━━━━━━━━━━━━━━━━\n\n` +
                       `🧾 <b>رقم الفاتورة:</b> <code>#${sale.invoiceId}</code>\n` +
                       `👤 <b>العميل:</b> <b>${sale.customerName || 'غير مسجل'}</b>\n` +
                       `📞 <b>الهاتف:</b> <code>${sale.customerPhone || 'غير مسجل'}</code>\n` +
                       `📍 <b>العنوان:</b> 📍 <i>${sale.deliveryDetails?.address || 'غير مسجل'}</i>\n` +
                       `🚴 <b>الطيار المسند:</b> 🚴 <b>${sale.deliveryDetails?.driverName || 'غير مسجل'}</b>\n\n` +
                       `💰 <b>المطلوب تحصيله:</b> <b>${sale.grandTotal} ج.م</b>\n` +
                       `  └─ (شامل رسوم التوصيل: ${sale.deliveryFee || 0} ج.م)\n\n` +
                       `━━━━━━━━━━━━━━━━━━━━\n` +
                       `🕊️ <i>نظام دكتور مصر لإدارة الصيدليات</i>`;
          sendTelegramMessage(telegramSettings, outMsg);
        } else if (newStatus === 'delivered') {
           let deliveryMsg = `✅ <b>تم تسليم أوردر الدليفري بنجاح!</b>\n` +
                             `🏥 <b>الصيدلية:</b> <code>${pharmacyName}</code> (${shortId})\n` +
                             `━━━━━━━━━━━━━━━━━━━━\n\n` +
                             `🧾 <b>رقم الفاتورة:</b> <code>#${sale.invoiceId}</code>\n` +
                             `👤 <b>العميل:</b> <b>${sale.customerName || 'غير مسجل'}</b>\n` +
                             `🚴 <b>الطيار:</b> 🚴 <b>${sale.deliveryDetails?.driverName || 'غير مسجل'}</b>\n\n` +
                             `💰 <b>المبلغ الذي تم تحصيله واستلامه:</b>\n` +
                             `  └─ <b>${sale.grandTotal} ج.م</b> (شامل خدمة التوصيل: ${sale.deliveryFee || 0} ج.م)\n\n` +
                             `━━━━━━━━━━━━━━━━━━━━\n` +
                             `🕊️ <i>نظام دكتور مصر لإدارة الصيدليات</i>`;
          sendTelegramMessage(telegramSettings, deliveryMsg);
        }
      }
      
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("تم تحديث حالة الطلب بنجاح! ✨");
      }
    } catch (err) {
      console.error("Error updating delivery status:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const executeFullReturn = async (sale) => {
    setActionLoading(true);
    try {
      const nextInv = inventory.map(item => {
        // Match by barcode OR tradeName to be 100% robust!
        const saleItem = sale.items.find(si => 
          (si.barcode && item.barcode && si.barcode === item.barcode) || 
          (si.tradeName && item.tradeName && si.tradeName.trim() === item.tradeName.trim())
        );
        
        if (saleItem) {
          let returnedQty = saleItem.quantity;
          if (saleItem.selectedUnitIdx !== undefined && saleItem.selectedUnitIdx !== -1) {
            returnedQty = saleItem.quantity / (saleItem.ratio || 1);
          }
          const nextQty = Number((item.quantity + returnedQty).toFixed(2));
          
          // Use setDoc with merge: true so it's guaranteed to update successfully!
          const itemDocRef = doc(db, 'users', user.uid, 'inventory', item.id);
          setDoc(itemDocRef, {
            ...item,
            quantity: nextQty
          }, { merge: true }).catch(err => console.error("Full Return Inventory Firestore update error:", err));
          
          return { ...item, quantity: nextQty };
        }
        return item;
      });
      
      setInventory(nextInv);
      localStorage.setItem('drmasr_inv_' + (user.uid), JSON.stringify(nextInv));

      const nextSales = sales.map(s => {
        if (s.invoiceId === sale.invoiceId) {
          const updated = { ...s, deliveryStatus: 'returned', isRefunded: true };
          if (activeShift) {
            updated.shiftId = activeShift.id;
          }
          return updated;
        }
        return s;
      });
      setSales(nextSales);
      localStorage.setItem('drmasr_sales_' + (user.uid), JSON.stringify(nextSales));

      const returnUpdateData = { deliveryStatus: 'returned', isRefunded: true };
      if (activeShift) {
        returnUpdateData.shiftId = activeShift.id;
      }
      await updateDoc(doc(db, 'users', user.uid, 'sales', sale.id), returnUpdateData);

      setPageSales(prev => prev.filter(s => s.invoiceId !== sale.invoiceId));
      setCounts(prev => ({
        ...prev,
        [activeSubTab]: Math.max(0, prev[activeSubTab] - 1),
        returned: (prev.returned || 0) + 1
      }));

      if (telegramSettings && telegramSettings.enabled) {
        const pharmacyName = currentUserData?.pharmacyName || "صيدلية دكتور مصر";
        const shortId = currentUserData?.shortId || "غير متوفر";
        const itemsListStr = sale.items.map(item => "• <b>" + (item.tradeName) + "</b> (" + (item.quantity) + " عبوة × " + (item.price) + " ج.م)").join('\n');

        let returnMsg = `🔄 <b>تم عمل مرتجع بالكامل لأوردر دليفري</b>\n` +
                      `🏥 <b>الصيدلية:</b> <code>${pharmacyName}</code> (${shortId})\n` +
                      `━━━━━━━━━━━━━━━━━━━━\n\n` +
                      `🧾 <b>رقم الفاتورة:</b> <code>#${sale.invoiceId}</code>\n` +
                      `👤 <b>العميل:</b> <b>${sale.customerName || 'غير مسجل'}</b>\n` +
                      `🚴 <b>الطيار:</b> 🚴 <b>${sale.deliveryDetails?.driverName || 'غير مسجل'}</b>\n\n` +
                      `📦 <b>الأصناف المرتجعة بالكامل للمخزن:</b>\n${itemsListStr}\n\n` +
                      `💰 <b>المبلغ المرتجع للمخزن:</b>\n` +
                      `  └─ <b>${sale.grandTotal} ج.م</b> (شامل خدمة التوصيل: ${sale.deliveryFee || 0} ج.م)\n\n` +
                      `━━━━━━━━━━━━━━━━━━━━\n` +
                      `🕊️ <i>نظام دكتور مصر لإدارة الصيدليات</i>`;
        sendTelegramMessage(telegramSettings, returnMsg);
      }

      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("تم إرجاع الفاتورة بالكامل للمخزن وإلغاء الطلب بنجاح!");
      }

    } catch (err) {
      console.error("Full return failed:", err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ حدث خطأ أثناء إرجاع الفاتورة: " + err.message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const executeRevertDelivery = async (sale) => {
    setActionLoading(true);
    try {
      let nextInv = [...inventory];
      const itemsToDeduct = [];

      sale.items.forEach(si => {
        let qtyToDeduct = 0;
        if (sale.deliveryStatus === 'returned' && !si.returnedQuantity) {
          qtyToDeduct = si.quantity;
        } else if (si.returnedQuantity > 0) {
          qtyToDeduct = si.returnedQuantity;
        }

        if (qtyToDeduct > 0) {
          if (si.selectedUnitIdx !== undefined && si.selectedUnitIdx !== -1) {
            qtyToDeduct = qtyToDeduct / (si.ratio || 1);
          }
          itemsToDeduct.push({
            barcode: si.barcode,
            tradeName: si.tradeName,
            qty: qtyToDeduct
          });
        }
      });

      if (itemsToDeduct.length > 0) {
        nextInv = inventory.map(item => {
          const deductItem = itemsToDeduct.find(di => 
            (di.barcode && item.barcode && di.barcode === item.barcode) || 
            (di.tradeName && item.tradeName && di.tradeName.trim() === item.tradeName.trim())
          );
          if (deductItem) {
            const nextQty = Number((item.quantity - deductItem.qty).toFixed(2));
            const itemDocRef = doc(db, 'users', user.uid, 'inventory', item.id);
            setDoc(itemDocRef, {
              ...item,
              quantity: Math.max(0, nextQty)
            }, { merge: true }).catch(err => console.error("Revert Delivery Inventory Firestore update error:", err));
            return { ...item, quantity: Math.max(0, nextQty) };
          }
          return item;
        });
        setInventory(nextInv);
        localStorage.setItem('drmasr_inv_' + (user.uid), JSON.stringify(nextInv));
      }

      const restoredItems = sale.items.map(si => {
        if (si.originalQuantity !== undefined) {
          return {
            ...si,
            quantity: si.originalQuantity,
            returnedQuantity: 0
          };
        }
        return si;
      });

      const restoredSubTotal = restoredItems.reduce((acc, si) => acc + (si.price * si.quantity), 0);
      const discountAndPoints = (sale.discount || 0) + (sale.pointsDiscount || 0);
      const restoredGrandTotal = Math.max(0, restoredSubTotal - discountAndPoints) + (sale.deliveryFee || 0);

      const nextStatus = 'out';

      const nextSales = sales.map(s => {
        if (s.invoiceId === sale.invoiceId) {
          return {
            ...s,
            deliveryStatus: nextStatus,
            items: restoredItems,
            subTotal: restoredSubTotal,
            grandTotal: restoredGrandTotal,
            isRefunded: false,
            deliveredAt: null
          };
        }
        return s;
      });
      setSales(nextSales);
      localStorage.setItem('drmasr_sales_' + (user.uid), JSON.stringify(nextSales));

      setPageSales(prev => prev.filter(s => s.invoiceId !== sale.invoiceId));
      setCounts(prev => ({
        ...prev,
        [activeSubTab]: Math.max(0, prev[activeSubTab] - 1),
        [nextStatus]: (prev[nextStatus] || 0) + 1
      }));

      await updateDoc(doc(db, 'users', user.uid, 'sales', sale.id), {
        deliveryStatus: nextStatus,
        items: restoredItems,
        subTotal: restoredSubTotal,
        grandTotal: restoredGrandTotal,
        isRefunded: false,
        deliveredAt: null
      });

      if (telegramSettings && telegramSettings.enabled) {
        const pharmacyName = currentUserData?.pharmacyName || "صيدلية دكتور مصر";
        const shortId = currentUserData?.shortId || "غير متوفر";
        let revertMsg = `🔄 <b>تراجع وإلغاء عملية التسليم/المرتجع لأوردر دليفري</b>\n` +
                        `🏥 <b>الصيدلية:</b> <code>${pharmacyName}</code> (${shortId})\n` +
                        `━━━━━━━━━━━━━━━━━━━━\n\n` +
                        `🧾 <b>رقم الفاتورة:</b> <code>#${sale.invoiceId}</code>\n` +
                        `👤 <b>العميل:</b> <b>${sale.customerName || 'غير مسجل'}</b>\n` +
                        `🚴 <b>الطيار:</b> 🚴 <b>${sale.deliveryDetails?.driverName || 'غير مسجل'}</b>\n\n` +
                        `⚠️ <b>الحالة الجديدة:</b> تم إرجاع الطلب إلى حالة <b>مع الطيار (خارج للتوصيل)</b> وتم تعديل الكميات والماليات تلقائياً.\n\n` +
                        `━━━━━━━━━━━━━━━━━━━━\n` +
                        `🕊️ <i>نظام دكتور مصر لإدارة الصيدليات</i>`;
        sendTelegramMessage(telegramSettings, revertMsg);
      }

      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("تم التراجع وإعادة الطلب إلى الطيار بنجاح! 🛵");
      }
    } catch (err) {
      console.error("Revert delivery failed:", err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ حدث خطأ أثناء التراجع: " + err.message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleFullReturn = (sale) => {
    setConfirmFullReturnSale(sale);
  };

  const initiateReconciliation = (sale) => {
    setReconcilingSale(sale);
    const initialReturns = {};
    sale.items.forEach(si => {
      const key = si.barcode || si.tradeName;
      initialReturns[key] = 0;
    });
    setReturnedQuantities(initialReturns);
  };

  const handleConfirmPartialReturn = async () => {
    if (!reconcilingSale) return;
    setActionLoading(true);
    try {
      const sale = reconcilingSale;
      const nextItems = sale.items.map(si => {
        const retQty = parseFloat(returnedQuantities[si.barcode] || returnedQuantities[si.tradeName] || 0);
        const originalQty = si.originalQuantity !== undefined ? si.originalQuantity : si.quantity;
        return {
          ...si,
          originalQuantity: originalQty,
          returnedQuantity: retQty,
          quantity: Number((originalQty - retQty).toFixed(2))
        };
      });

      const nextSubTotal = nextItems.reduce((acc, si) => acc + (si.price * si.quantity), 0);
      const discountAndPoints = (sale.discount || 0) + (sale.pointsDiscount || 0);
      const nextGrandTotal = Math.max(0, nextSubTotal - discountAndPoints) + (sale.deliveryFee || 0);

      const nextInv = inventory.map(item => {
        // Match by barcode OR tradeName to be 100% robust!
        const saleItem = sale.items.find(si => 
          (si.barcode && item.barcode && si.barcode === item.barcode) || 
          (si.tradeName && item.tradeName && si.tradeName.trim() === item.tradeName.trim())
        );
        
        let retQty = 0;
        if (saleItem) {
          retQty = parseFloat(returnedQuantities[saleItem.barcode] || returnedQuantities[saleItem.tradeName] || 0);
        }
        
        if (saleItem && retQty > 0) {
          let backToInvQty = retQty;
          if (saleItem.selectedUnitIdx !== undefined && saleItem.selectedUnitIdx !== -1) {
            backToInvQty = retQty / (saleItem.ratio || 1);
          }
          const nextQty = Number((item.quantity + backToInvQty).toFixed(2));
          
          // Use setDoc with merge: true so it's guaranteed to update successfully!
          const itemDocRef = doc(db, 'users', user.uid, 'inventory', item.id);
          setDoc(itemDocRef, {
            ...item,
            quantity: nextQty
          }, { merge: true }).catch(err => console.error("Partial Return Inventory Firestore update error:", err));
          
          return { ...item, quantity: nextQty };
        }
        return item;
      });

      setInventory(nextInv);
      localStorage.setItem('drmasr_inv_' + (user.uid), JSON.stringify(nextInv));

      const isFullyEmpty = nextItems.every(si => si.quantity === 0);
      const finalStatus = isFullyEmpty ? 'returned' : 'delivered';

      const nextSales = sales.map(s => {
        if (s.invoiceId === sale.invoiceId) {
          const updated = { 
            ...s, 
            deliveryStatus: finalStatus,
            items: nextItems,
            subTotal: nextSubTotal,
            grandTotal: nextGrandTotal,
            isRefunded: isFullyEmpty
          };
          if (finalStatus === 'delivered' || finalStatus === 'returned') {
            if (activeShift) {
              updated.shiftId = activeShift.id;
            }
            if (finalStatus === 'delivered') {
              updated.deliveredAt = new Date().toISOString();
            } else {
              updated.returnedAt = new Date().toISOString();
            }
          }
          return updated;
        }
        return s;
      });
      setSales(nextSales);
      localStorage.setItem('drmasr_sales_' + (user.uid), JSON.stringify(nextSales));

      const updateData = { 
        deliveryStatus: finalStatus,
        items: nextItems,
        subTotal: nextSubTotal,
        grandTotal: nextGrandTotal,
        isRefunded: isFullyEmpty
      };
      if (finalStatus === 'delivered' || finalStatus === 'returned') {
        if (activeShift) {
          updateData.shiftId = activeShift.id;
        }
        if (finalStatus === 'delivered') {
          updateData.deliveredAt = new Date().toISOString();
        } else {
          updateData.returnedAt = new Date().toISOString();
        }
      }
      await updateDoc(doc(db, 'users', user.uid, 'sales', sale.id), updateData);

      setPageSales(prev => prev.filter(s => s.invoiceId !== sale.invoiceId));
      setCounts(prev => ({
        ...prev,
        [activeSubTab]: Math.max(0, prev[activeSubTab] - 1),
        [finalStatus]: (prev[finalStatus] || 0) + 1
      }));

      // Telegram notification for partial return
      if (telegramSettings && telegramSettings.enabled) {
        const pharmacyName = currentUserData?.pharmacyName || "صيدلية دكتور مصر";
        const shortId = currentUserData?.shortId || "غير متوفر";
        
        // List returned items and quantities
        const returnedItemsList = sale.items.map(si => {
          const ret = parseFloat(returnedQuantities[si.barcode] || returnedQuantities[si.tradeName] || 0);
          if (ret > 0) {
            return "• <b>" + (si.tradeName) + "</b>\\n  └─ إرجاع: <code>" + (ret) + "</code> من أصل <code>" + (si.originalQuantity !== undefined ? si.originalQuantity : si.quantity) + "</code>";
          }
          return null;
        }).filter(Boolean).join('\n');

        let returnMsg = `🔄 <b>تم عمل مرتجع جزئي لأوردر دليفري</b>\n` +
                      `🏥 <b>الصيدلية:</b> <code>${pharmacyName}</code> (${shortId})\n` +
                      `━━━━━━━━━━━━━━━━━━━━\n\n` +
                      `🧾 <b>رقم الفاتورة:</b> <code>#${sale.invoiceId}</code>\n` +
                      `👤 <b>العميل:</b> <b>${sale.customerName || 'غير مسجل'}</b>\n` +
                      `🚴 <b>الطيار:</b> 🚴 <b>${sale.deliveryDetails?.driverName || 'غير مسجل'}</b>\n\n` +
                      `📦 <b>الأصناف المرتجعة للرفوف:</b>\n${returnedItemsList}\n\n` +
                      `💰 <b>الملخص المالي والماليات:</b>\n` +
                      `  ├─ خدمة التوصيل: ${sale.deliveryFee || 0} ج.م\n` +
                      `  ├─ القيمة الأصلية للطلب: ${sale.grandTotal} ج.م\n` +
                      `  ├─ إجمالي المسترجع للمخزن: <b>${sale.grandTotal - nextGrandTotal} ج.م</b>\n` +
                      `  └─ <b>المطلوب تحصيله الجديد: ${nextGrandTotal} ج.م</b>\n\n` +
                      `━━━━━━━━━━━━━━━━━━━━\n` +
                      `🕊️ <i>نظام دكتور مصر لإدارة الصيدليات</i>`;
        sendTelegramMessage(telegramSettings, returnMsg);
      }


      // Print Partial Return Receipt
      if (!isFullyEmpty) {
        try {
          const printWindow = window.open('', '_blank', 'width=300,height=600');
          if (printWindow) {
            printWindow.document.write(`
            <html>
            <head>
            <title>بون مرتجع جزئي - ${sale.invoiceId}</title>
            <style>
            @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700;800&display=swap');
            body { font-family: 'Cairo', sans-serif; direction: rtl; padding: 10px; color: #000; font-size: 11px; margin: 0; }
            .header { text-align: center; margin-bottom: 12px; border-bottom: 2px dashed #000; padding-bottom: 8px; }
            .title { font-size: 16px; font-weight: 900; margin: 0; }
            .subtitle { font-size: 10px; margin: 2px 0; color: #333; }
            .info-row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 10px; }
            table { width: 100%; border-collapse: collapse; margin: 8px 0; }
            th, td { text-align: right; padding: 4px 0; font-size: 10px; }
            th { border-bottom: 1px dashed #000; }
            .total-section { border-top: 1px dashed #000; padding-top: 6px; margin-top: 6px; font-weight: bold; }
            .footer { border-top: 1px dashed #000; padding-top: 8px; margin-top: 12px; font-size: 9px; text-align: center; }
            .refund-alert { background: #000; color: #fff; padding: 6px; text-align: center; font-weight: bold; margin: 8px 0; border-radius: 4px; }
            </style>
            </head>
            <body>
            <div class="header">
            ${(currentUserData?.pharmacyLogo || localStorage.getItem('drmasr_receipt_logo')) ? `<img src="${currentUserData?.pharmacyLogo || localStorage.getItem('drmasr_receipt_logo')}" style="max-height: 60px; max-width: 130px; object-fit: contain; margin-bottom: 6px;" /><br/>` : ''}
            <p class="title">بون استلام دليفري وتعديل</p>
            <p class="subtitle">تم عمل مرتجع جزئي لهذا الطلب</p>
            </div>
            <div class="info-row">
            <span>رقم الفاتورة الأصلي:</span>
            <span>#${sale.invoiceId}</span>
            </div>
            <div class="info-row">
            <span>التاريخ واليوم:</span>
            <span>${new Date().toLocaleDateString('ar-EG')}</span>
            </div>
            
            <div class="refund-alert">
            تم خصم مبلغ ${sale.grandTotal - nextGrandTotal} ج.م من الأوردر بسبب المرتجع الجزئي
            </div>
            <table>
            <thead>
            <tr>
            <th>الصنف (بعد التعديل)</th>
            <th style="text-align: center;">الكمية</th>
            <th style="text-align: left;">السعر</th>
            </tr>
            </thead>
            <tbody>
            ${nextItems.map(c => `
            <tr>
            <td>${c.tradeName}</td>
            <td style="text-align: center;">${c.quantity}</td>
            <td style="text-align: left;">${c.sellingPrice * c.quantity} ج.م</td>
            </tr>
            `).join('')}
            </tbody>
            </table>
            
            <div class="total-section">
            <div class="info-row">
            <span>المجموع الفرعي (بعد التعديل):</span>
            <span>${nextSubTotal} ج.م</span>
            </div>
            ${(sale.discount || 0) > 0 ? `
            <div class="info-row">
            <span>الخصم اليدوي:</span>
            <span>${sale.discount} ج.م</span>
            </div>
            ` : ''}
            ${(sale.pointsDiscount || 0) > 0 ? `
            <div class="info-row">
            <span>خصم نقاط الولاء:</span>
            <span>${sale.pointsDiscount} ج.م</span>
            </div>
            ` : ''}
            ${(sale.deliveryFee || 0) > 0 ? `
            <div class="info-row">
            <span>خدمة التوصيل:</span>
            <span>${sale.deliveryFee} ج.م</span>
            </div>
            ` : ''}
            <div class="info-row" style="font-size: 14px; font-weight: 900; margin-top: 8px;">
            <span>المجمل المطلوب تحصيله:</span>
            <span>${nextGrandTotal} ج.م</span>
            </div>
            </div>
            
            <div class="footer">
            <p style="margin: 0; font-weight: bold;">شكراً لتعاملكم معنا!</p>
            <p style="margin: 4px 0 0 0; font-size: 8px; color: #555;">دكتور مصر لإدارة الصيدليات </p>
            </div>
            
            <script>
            window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
            };
            </script>
            </body>
            </html>
            `);
            printWindow.document.close();
          }
        } catch (err) {
          console.error('Receipt print failed:', err);
        }
      }


      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("تم تأكيد تسليم الطلب وتعديل المسترجعات بنجاح!");
      }
    } catch (err) {
      console.error("Partial reconciliation failed:", err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ حدث خطأ أثناء تعديل المسترجعات: " + err.message);
      }
    } finally {
      setReconcilingSale(null);
      setActionLoading(false);
    }
  };

  return (
    <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Default Delivery Fee Configuration Card */}
      <div className="premium-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', padding: '16px 24px', background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(99, 102, 241, 0.02) 100%)', border: '1px solid rgba(139, 92, 246, 0.15)', borderRadius: '16px', direction: 'rtl', textAlign: 'right' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '1.5rem' }}>🛵</span>
          <div>
            <h4 style={{ margin: 0, color: '#fff', fontSize: '0.95rem', fontWeight: 800 }}>سعر توصيل الدليفري</h4>
            <p style={{ margin: '2px 0 0 0', color: 'var(--text-secondary)', fontSize: '0.78rem' }}>سيتم احتسابه تلقائياً في فواتير الدليفري وفي متجر العملاء أونلاين لتسريع تسجيل الطلبات.</p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', width: '130px' }}>
            <input 
              type="number" 
              className="premium-input" 
              style={{ 
                textAlign: 'center', 
                fontSize: '1.1rem', 
                fontWeight: 'bold', 
                height: '42px', 
                paddingLeft: '48px', 
                paddingRight: '16px',
                width: '100%',
                boxSizing: 'border-box'
              }} 
              value={defaultFee} 
              onChange={e => setDefaultFee(e.target.value)} 
              min={0}
            />
            <span style={{ 
              position: 'absolute', 
              left: '14px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              fontSize: '0.85rem', 
              color: 'var(--accent-green)', 
              fontWeight: 'bold',
              pointerEvents: 'none'
            }}>ج.م</span>
          </div>
          
          <button
            onClick={handleSaveDefaultFee}
            disabled={isSavingFee}
            className="gradient-btn"
            style={{
              padding: '10px 20px',
              fontSize: '0.92rem',
              fontWeight: 'bold',
              height: '42px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, var(--accent-purple) 0%, #6366f1 100%)',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.25)',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
          >
            <span>{isSavingFee ? '💾 جاري الحفظ...' : '💾 حفظ السعر'}</span>
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '-10px', flexWrap: 'wrap', gap: '12px' }}>
        <button
          onClick={() => fetchSales(true)}
          disabled={isSalesLoading}
          className="premium-btn"
          style={{
            padding: '10px 18px',
            fontSize: '0.92rem',
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '10px',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)'}
        >
          {isSalesLoading ? '🔄 جاري استدعاء البيانات...' : '🔄 تحديث طلبات الدليفري (من السيرفر)'}
        </button>
        <button onClick={() => { setIsDeliveryOrder(true); setActiveTab('pos'); }} className="gradient-btn" style={{ padding: '12px 24px', fontSize: '1.1rem', background: 'var(--accent-purple)' }}>
          + إنشاء طلب دليفري جديد
        </button>
      </div>

      {/* Receipt Search Bar */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
        <input 
          type="text" 
          className="premium-input" 
          placeholder="🔍 ابحث برقم البون (الفاتورة) هنا..." 
          value={receiptSearchTerm}
          onChange={e => setReceiptSearchTerm(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              handleSearchReceipt();
            }
          }}
          style={{ padding: '10px 16px', fontSize: '0.9rem', flex: 1, color: '#fff', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.1)' }}
        />
        <button 
          onClick={handleSearchReceipt}
          className="gradient-btn"
          style={{ padding: '10px 24px', fontSize: '0.9rem', fontWeight: 'bold', borderRadius: '12px', background: 'linear-gradient(135deg, var(--primary) 0%, #6366f1 100%)', color: '#fff', border: 'none', cursor: 'pointer' }}
        >
          ابحث 🔍
        </button>
      </div>


      <div className="delivery-tabs-header" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
        <button onClick={() => setActiveSubTab('pending')} className={`delivery-tab-btn ${activeSubTab === 'pending' ? 'active' : ''}`}>
          قيد التجهيز ({pendingSales.length})
        </button>
        <button onClick={() => setActiveSubTab('out')} className={`delivery-tab-btn ${activeSubTab === 'out' ? 'active' : ''}`}>
          مع الطيار ({outSales.length})
        </button>
        <button onClick={() => setActiveSubTab('delivered')} className={`delivery-tab-btn ${activeSubTab === 'delivered' ? 'active' : ''}`}>
          تم التوصيل ({completedSales.length})
        </button>
        <button onClick={() => setActiveSubTab('returned')} className={`delivery-tab-btn ${activeSubTab === 'returned' ? 'active' : ''}`}>
          المرتجعات ({returnedSales.length})
        </button>
        <button onClick={() => setActiveSubTab('driver_shifts')} className={`delivery-tab-btn ${activeSubTab === 'driver_shifts' ? 'active' : ''}`} style={{ borderRight: '1px solid rgba(255,255,255,0.1)', paddingRight: '16px' }}>
          ⏱️ ورديات وحسابات الطيارين
        </button>
      </div>

      {driverShiftsTabStatus && (
        <div style={{ padding: '12px 16px', background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '10px', color: '#60a5fa', marginBottom: '20px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
          {driverShiftsTabStatus}
        </div>
      )}

      {activeSubTab === 'driver_shifts' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Driver Selection & Status Card */}
          <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>🛵</span> اختر طيار الدليفري للمتابعة
            </h3>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              <div style={{ flex: '1', minWidth: '240px' }}>
                <select
                  className="premium-input"
                  value={selectedDriverId}
                  onChange={e => setSelectedDriverId(e.target.value)}
                  style={{ width: '100%', height: '48px', padding: '0 16px' }}
                >
                  <option value="">-- اختر الطيار --</option>
                  {(employees || []).filter(emp => emp.role === 'driver').map(dr => (
                    <option key={dr.id} value={dr.id}>{dr.name}</option>
                  ))}
                </select>
              </div>

              {selectedDriverId && (
                <div>
                  {(() => {
                    const dr = employees.find(emp => emp.id === selectedDriverId);
                    const activeShift = driverShifts.find(sh => sh.driverId === selectedDriverId && sh.status === 'active');
                    if (activeShift) {
                      return (
                        <span style={{ padding: '8px 16px', borderRadius: '20px', background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', fontWeight: 700, fontSize: '0.9rem' }}>
                          🔴 في وردية عمل حالياً (بدأت: {new Date(activeShift.checkIn).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })})
                        </span>
                      );
                    } else {
                      return (
                        <span style={{ padding: '8px 16px', borderRadius: '20px', background: 'rgba(16,185,129,0.15)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)', fontWeight: 700, fontSize: '0.9rem' }}>
                          🟢 متاح لبدء وردية عمل جديدة
                        </span>
                      );
                    }
                  })()}
                </div>
              )}
            </div>
          </div>

          {!selectedDriverId ? (
            <div className="premium-card" style={{ textAlign: 'center', padding: '48px 24px', background: 'rgba(255,255,255,0.01)', border: '1px dashed var(--border)' }}>
              <span style={{ fontSize: '3rem', display: 'block', marginBottom: '16px' }}>🛵</span>
              <h3 style={{ color: '#fff', marginBottom: '8px' }}>يرجى اختيار أحد الطيارين المسجلين لعرض الوردية وتصفية الحساب.</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                إذا لم تكن قد قمت بإضافة طيارين، يرجى الذهاب إلى شاشة "إعدادات النظام" ثم "إدارة الموظفين والأطباء" لإضافتهم أولاً.
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
              
              {/* Check-In / Check-Out Actions Card */}
              {(() => {
                const dr = employees.find(emp => emp.id === selectedDriverId);
                const activeShiftForDriver = driverShifts.find(sh => sh.driverId === selectedDriverId && sh.status === 'active');
                
                return (
                  <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <h4 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', marginTop: 0, marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>
                        ⏱️ تسجيل الحضور والانصراف (Shift)
                      </h4>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: '1.6', marginBottom: '24px' }}>
                        قم بتسجيل حضور الطيار {dr.name} عند استلام الأوردرات أو تسجيل الانصراف لحساب مستحقات الساعات التراكمية.
                      </p>
                    </div>

                    {!activeShiftForDriver ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#fff', fontSize: '0.92rem', cursor: 'pointer', background: 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '10px', border: '1px solid var(--border)' }}>
                          <input
                            type="checkbox"
                            checked={isHolidayShift}
                            onChange={e => setIsHolidayShift(e.target.checked)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span>🎉 احتساب الوردية كعطلة رسمية / يوم عيد (سعر الساعة × {dr.holidayRate || 2.0})</span>
                        </label>

                        <button
                          onClick={() => handleDriverCheckIn(dr)}
                          className="gradient-btn"
                          style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.25)', border: 'none', borderRadius: '12px', fontWeight: 800 }}
                        >
                          🏁 تسجيل حضور (Check-in) وبدء الوردية
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ background: 'rgba(0,0,0,0.2)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>وقت بدء الوردية:</div>
                          <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#fff' }}>
                            {new Date(activeShiftForDriver.checkIn).toLocaleDateString('ar-EG')} - {new Date(activeShiftForDriver.checkIn).toLocaleTimeString('ar-EG')}
                          </div>
                          {activeShiftForDriver.isHoliday && (
                            <div style={{ display: 'inline-block', marginTop: '8px', fontSize: '0.8rem', padding: '2px 8px', background: 'rgba(139,92,246,0.2)', color: '#c084fc', borderRadius: '99px', fontWeight: 700 }}>
                              🎉 وردية عيد / عطلة
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => handleDriverCheckOut(activeShiftForDriver)}
                          className="gradient-btn"
                          style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)', boxShadow: '0 4px 15px rgba(239, 68, 68, 0.25)', border: 'none', borderRadius: '12px', fontWeight: 800 }}
                        >
                          🛑 تسجيل انصراف (Check-out) وإنهاء الوردية
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Financial Ledger & Payout Settlement Card */}
              {(() => {
                const dr = employees.find(emp => emp.id === selectedDriverId) || {};
                const unpaidShifts = driverShifts.filter(sh => sh.driverId === selectedDriverId && sh.status === 'completed' && sh.paid !== true);
                
                // Calculate hours
                let regularHours = 0;
                let holidayHours = 0;
                unpaidShifts.forEach(sh => {
                  if (sh.isHoliday) {
                    holidayHours += sh.hoursWorked || 0;
                  } else {
                    regularHours += sh.hoursWorked || 0;
                  }
                });

                const regularRate = Number(dr.regularRate || 0);
                const holidayRateVal = regularRate * Number(dr.holidayRate || 2.0);
                const commissionRate = Number(dr.commissionPerOrder || 0);

                const regularPay = regularHours * regularRate;
                const holidayPay = holidayHours * holidayRateVal;
                const totalCommission = unpaidSales.length * commissionRate;
                const totalDue = regularPay + holidayPay + totalCommission;

                return (
                  <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <h4 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', marginTop: 0, marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>
                        💵 تصفية الحساب وصرف المستحقات
                      </h4>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'rgba(0,0,0,0.15)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>ساعات العمل العادية:</span>
                          <span style={{ color: '#fff', fontWeight: 700 }}>{regularHours} ساعة × {regularRate} ج.م = {regularPay} ج.م</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>ساعات العمل (العيد/مضاعفة):</span>
                          <span style={{ color: '#fff', fontWeight: 700 }}>{holidayHours} ساعة × {holidayRateVal} ج.م = {holidayPay} ج.م</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>عمولة الأوردرات المعلقة:</span>
                          <span style={{ color: '#fff', fontWeight: 700 }}>{unpaidSales.length} أوردر × {commissionRate} ج.م = {totalCommission} ج.م</span>
                        </div>
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '10px', marginTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 800, color: '#fff', fontSize: '1.05rem' }}>المبلغ الإجمالي المستحق:</span>
                          <span style={{ fontWeight: 800, color: 'var(--accent-gold)', fontSize: '1.3rem' }}>{totalDue} ج.م</span>
                        </div>
                      </div>
                    </div>

                    {totalDue > 0 ? (
                      <button
                        onClick={() => {
                          setSystemDialog({
                            title: 'تأكيد صرف المستحقات 💵',
                            message: `هل أنت متأكد من صرف مبلغ ${totalDue} ج.م نقداً للطيار ${dr.name}؟\nسيتم تصفير حسابه بالكامل، وتحديث حالة جميع الوردياث والأوردرات إلى (مدفوع)، وتسجيل الحركة كمصروف في شفت الصيدلية الحالي.`,
                            type: 'confirm',
                            onConfirm: () => {
                              setSystemDialog(null);
                              handleDriverSettlement(dr, totalDue, unpaidShifts, unpaidSales);
                            }
                          });
                        }}
                        className="gradient-btn"
                        style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', boxShadow: '0 4px 15px rgba(245, 158, 11, 0.25)', border: 'none', borderRadius: '12px', fontWeight: 800, marginTop: '20px' }}
                      >
                        💵 صرف المستحقات وتصفية الحساب الآن
                      </button>
                    ) : (
                      <div style={{ textAlign: 'center', color: '#34d399', background: 'rgba(16,185,129,0.08)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(16,185,129,0.15)', fontWeight: 600, fontSize: '0.9rem', marginTop: '20px' }}>
                        ✅ الحساب مصفى بالكامل ولا توجد مستحقات معلقة حالياً.
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Unpaid Orders List (Pending Commissions) */}
              <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px', gridColumn: 'span 2' }}>
                <h4 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0, borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>
                  🛵 الأوردرات التي تم تسليمها وغير مدفوعة للطيار ({unpaidSales.length} أوردر)
                </h4>
                
                {loadingUnpaidSales ? (
                  <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '20px' }}>⏳ جاري تحميل الأوردرات...</div>
                ) : unpaidSales.length === 0 ? (
                  <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '20px', background: 'rgba(0,0,0,0.1)', borderRadius: '8px' }}>
                    لا توجد أوردرات معلقة لم يتم محاسبة الطيار عليها حالياً.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'right' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          <th style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>بون #</th>
                          <th style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>العميل</th>
                          <th style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>العنوان</th>
                          <th style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>تاريخ التوصيل</th>
                          <th style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>قيمة الأوردر</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unpaidSales.map(sale => (
                          <tr key={sale.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '10px 8px', fontWeight: 'bold', color: '#fff' }}>#{sale.invoiceId}</td>
                            <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>{sale.customerName || 'دليفري'}</td>
                            <td style={{ padding: '10px 8px', color: '#fff' }}>{sale.deliveryDetails?.address || 'غير محدد'}</td>
                            <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>
                              {sale.deliveredAt ? new Date(sale.deliveredAt).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' }) : 'غير متوفر'}
                            </td>
                            <td style={{ padding: '10px 8px', fontWeight: 'bold', color: 'var(--accent-green)' }}>{sale.grandTotal} ج.م</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Shifts History Log */}
              <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px', gridColumn: 'span 2' }}>
                <h4 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0, borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>
                  ⏳ سجل ورديات العمل الأخيرة للطيار (آخر 15 وردية)
                </h4>

                {shiftsLoading ? (
                  <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '20px' }}>⏳ جاري تحميل سجل الورديات...</div>
                ) : driverShifts.filter(sh => sh.driverId === selectedDriverId).length === 0 ? (
                  <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '20px', background: 'rgba(0,0,0,0.1)', borderRadius: '8px' }}>
                    لا توجد ورديات عمل مسجلة لهذا الطيار بعد.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'right' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          <th style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>تاريخ الحضور</th>
                          <th style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>بداية الوردية</th>
                          <th style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>نهاية الوردية</th>
                          <th style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>عدد الساعات</th>
                          <th style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>نوع الوردية</th>
                          <th style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>حالة المستحقات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {driverShifts.filter(sh => sh.driverId === selectedDriverId).slice(0, 15).map(sh => (
                          <tr key={sh.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '10px 8px', color: '#fff' }}>{new Date(sh.checkIn).toLocaleDateString('ar-EG')}</td>
                            <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>{new Date(sh.checkIn).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}</td>
                            <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }}>
                              {sh.checkOut ? new Date(sh.checkOut).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' }) : <span style={{ color: 'var(--accent-pink)' }}>مستمرة ⚡</span>}
                            </td>
                            <td style={{ padding: '10px 8px', fontWeight: 'bold', color: '#fff' }}>{sh.hoursWorked || '--'} ساعة</td>
                            <td style={{ padding: '10px 8px' }}>
                              {sh.isHoliday ? (
                                <span style={{ color: '#c084fc', background: 'rgba(139,92,246,0.1)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>عيد/عطلة 👑</span>
                              ) : (
                                <span style={{ color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.03)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>وردية عادية</span>
                              )}
                            </td>
                            <td style={{ padding: '10px 8px' }}>
                              {sh.paid ? (
                                <span style={{ color: '#34d399', fontWeight: 'bold' }}>✔️ تم الدفع والتصفية</span>
                              ) : sh.status === 'active' ? (
                                <span style={{ color: '#a78bfa' }}>⚡ قيد التشغيل</span>
                              ) : (
                                <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>⏳ مستحق ولم يصرف</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            </div>
          )}

        </div>
      ) : currentList.length === 0 ? (
        <div className="premium-card" style={{ textAlign: 'center', padding: '40px 20px', background: 'rgba(255,255,255,0.02)' }}>
          <span style={{ fontSize: '3rem' }}></span>
          <h3 style={{ color: '#fff', margin: '12px 0' }}>لا توجد طلبات دليفري حالياً في هذا التبويب.</h3>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>
          {currentList.map(sale => (
            <div key={sale.id} className="delivery-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>
                <span style={{ fontWeight: 800, color: '#fff', fontSize: '1.1rem' }}>بون #{sale.invoiceId}</span>
                <span className={`status-badge ${
                  sale.deliveryStatus === 'pending' ? 'delivery-pending' :
                  sale.deliveryStatus === 'out' ? 'delivery-out' :
                  sale.deliveryStatus === 'delivered' ? 'delivery-success' : 'delivery-returned'
                }`}>
                  {sale.deliveryStatus === 'pending' ? 'قيد التجهيز' :
                   sale.deliveryStatus === 'out' ? 'خارج للتوصيل' :
                   sale.deliveryStatus === 'delivered' ? 'تم التسليم' : 'مرتجع'}
                </span>
              </div>

              <div className="delivery-details-row">
                <span>العميل:</span>
                <span style={{ fontWeight: 700, color: 'var(--primary)' }}>{sale.customerName || 'عميل دليفري'}</span>
              </div>
              <div className="delivery-details-row">
                <span>الهاتف:</span>
                <span>{sale.customerPhone || 'غير متوفر'}</span>
              </div>
              <div className="delivery-details-row">
                <span>العنوان:</span>
                <span style={{ fontWeight: 600, color: '#fff' }}>{sale.deliveryDetails?.address || 'العنوان غير محدد'}</span>
              </div>
              <div className="delivery-details-row">
                <span>الطيار المسند إليه:</span>
                <span style={{ color: 'var(--accent-gold)', fontWeight: 700 }}>{sale.deliveryDetails?.driverName || 'غير محدد'}</span>
              </div>
              <div className="delivery-details-row">
                <span>تاريخ ووقت الطلب:</span>
                <span style={{ fontFamily: 'Outfit, Cairo', fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                  {sale.timestamp ? new Date(sale.timestamp).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' }) : 'غير محدد'}
                </span>
              </div>
              {sale.deliveredAt && (
                <div className="delivery-details-row" style={{ borderLeft: '3px solid var(--accent-green)', paddingLeft: '8px' }}>
                  <span style={{ color: 'var(--accent-green)', fontWeight: 'bold' }}>تاريخ ووقت التوصيل:</span>
                  <span style={{ fontFamily: 'Outfit, Cairo', fontSize: '0.88rem', color: 'var(--accent-green)', fontWeight: 'bold' }}>
                    {new Date(sale.deliveredAt).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                </div>
              )}
              <div className="delivery-details-row" style={{ borderBottom: 'none', paddingTop: '4px' }}>
                <span style={{ fontWeight: 800 }}>المطلوب تحصيله:</span>
                <span style={{ color: 'var(--accent-green)', fontWeight: 800, fontSize: '1.1rem' }}>{sale.grandTotal} ج.م</span>
              </div>

              <div className="delivery-items-list">
                <div style={{ fontWeight: 'bold', color: '#94a3b8', marginBottom: '4px', fontSize: '0.8rem' }}>محتويات الشنطة:</div>
                {sale.items.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span>• {item.tradeName}</span>
                    <span>
                      {item.subUnit ? (
                        `${Math.round(item.quantity * item.subUnit.ratio)} ${item.subUnit.name} (${Number(item.quantity.toFixed(4))} علبة)`
                      ) : (
                        `x${item.quantity} علبة`
                      )}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                {sale.deliveryStatus === 'pending' && (
                  <button onClick={() => updateDeliveryStatus(sale, 'out')} disabled={actionLoading} className="gradient-btn" style={{ flex: 1, padding: '8px' }}>
                    خروج للعميل
                  </button>
                )}
                {sale.deliveryStatus === 'out' && (
                  <>
                    <button onClick={() => updateDeliveryStatus(sale, 'delivered')} disabled={actionLoading} className="gradient-btn" style={{ flex: 1, padding: '8px', background: 'var(--accent-green)' }}>
                      استلام النقدية
                    </button>
                    <button onClick={() => initiateReconciliation(sale)} disabled={actionLoading} className="gradient-btn-purple" style={{ flex: 1, padding: '8px' }}>
                      مسترجع جزئي
                    </button>
                  </>
                )}
                {(sale.deliveryStatus === 'pending' || sale.deliveryStatus === 'out') && (
                  <button onClick={() => setConfirmFullReturnSale(sale)} disabled={actionLoading} className="gradient-btn" style={{ padding: '8px', background: 'var(--accent-pink)' }}>
                    مرتجع بالكامل
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', padding: '16px 0 0 0', borderTop: '1px dashed rgba(255,255,255,0.06)' }}>
          <button
            onClick={() => {
              const prev = Math.max(1, currentPage - 1);
              setCurrentPage(prev);
              fetchPage(prev);
            }}
            disabled={currentPage === 1 || localLoading}
            className="gradient-btn-secondary"
            style={{ padding: '10px 20px', fontSize: '0.85rem', borderRadius: '10px', cursor: (currentPage === 1 || localLoading) ? 'not-allowed' : 'pointer' }}
          >
            ➡️ الصفحة السابقة
          </button>
          <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            الصفحة <strong>{currentPage}</strong> من <strong>{totalPages}</strong>
          </span>
          <button
            onClick={() => {
              const next = Math.min(totalPages, currentPage + 1);
              setCurrentPage(next);
              fetchPage(next);
            }}
            disabled={currentPage === totalPages || localLoading}
            className="gradient-btn-secondary"
            style={{ padding: '10px 20px', fontSize: '0.85rem', borderRadius: '10px', cursor: (currentPage === totalPages || localLoading) ? 'not-allowed' : 'pointer' }}
          >
            الصفحة التالية ⬅️
          </button>
        </div>
      )}

      {reconcilingSale && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(5, 8, 16, 0.88)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '16px', 
          zIndex: 100000,
          backdropFilter: 'blur(16px)',
          animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div className="premium-card fade-in-content" style={{ 
            width: '100%',
            maxWidth: '540px', 
            background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.95) 0%, rgba(9, 14, 28, 0.98) 100%)', 
            border: '1px solid rgba(244, 63, 94, 0.25)', 
            direction: 'rtl', 
            padding: '36px', 
            borderRadius: '24px', 
            boxShadow: '0 24px 60px rgba(244, 63, 94, 0.25)', 
            fontFamily: 'Cairo, sans-serif' 
          }}>
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '24px', 
              borderBottom: '1px solid rgba(255,255,255,0.06)', 
              paddingBottom: '16px' 
            }}>
              <h3 style={{ fontSize: '1.3rem', fontWeight: 800, margin: 0, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.5rem', filter: 'drop-shadow(0 0 8px rgba(244,63,94,0.4))' }}>↩️</span> معالجة مسترجعات البون #{reconcilingSale.invoiceId}
              </h3>
              <button 
                onClick={() => setReconcilingSale(null)} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'var(--text-secondary)', 
                  cursor: 'pointer', 
                  fontSize: '1.8rem', 
                  outline: 'none',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
              >
                &times;
              </button>
            </div>
            
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 20px 0', lineHeight: 1.6 }}>
              حدد الكمية التي <b>أرجعها العميل</b> ولم يستلمها من كل صنف، وسيعيد النظام تلقائياً حساب الصافي وإعادة الأدوية المرتجعة للمخزن.
            </p>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '14px', 
              maxHeight: '280px', 
              overflowY: 'auto', 
              marginBottom: '28px',
              paddingRight: '4px'
            }}>
              {reconcilingSale.items.map(item => {
                const originalQty = item.originalQuantity !== undefined ? item.originalQuantity : item.quantity;
                const key = item.barcode || item.tradeName;
                return (
                  <div key={item.barcode || item.tradeName} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    background: 'rgba(15, 23, 42, 0.45)', 
                    border: '1px solid rgba(255,255,255,0.06)', 
                    padding: '16px 20px', 
                    borderRadius: '16px',
                    gap: '12px',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontWeight: 800, fontSize: '0.98rem', color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#f43f5e', fontSize: '0.6rem' }}>●</span> {item.tradeName}
                      </h4>
                      <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '4px', display: 'inline-block' }}>
                        الكمية المرسلة: <strong style={{ color: '#fff', fontFamily: 'Outfit' }}>{item.subUnit ? `${Math.round(originalQty * item.subUnit.ratio)} ${item.subUnit.name} (${Number(originalQty.toFixed(4))} علبة)` : `${originalQty} علبة`}</strong> | السعر: <strong style={{ color: 'var(--accent-green)', fontFamily: 'Outfit' }}>{item.price} ج.م</strong>
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>مرتجع:</span>
                      
                      {/* Quantity Controls Cockpit */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px', 
                        background: 'rgba(5, 8, 16, 0.65)', 
                        borderRadius: '12px', 
                        padding: '4px 8px', 
                        border: '1px solid rgba(255,255,255,0.08)' 
                      }}>
                        <button 
                          type="button"
                          onClick={() => {
                            const cur = returnedQuantities[key] || 0;
                            const val = Math.max(0, cur - 1);
                            setReturnedQuantities({ ...returnedQuantities, [key]: val });
                          }}
                          style={{
                            background: 'rgba(244, 63, 94, 0.15)',
                            border: 'none',
                            color: '#f43f5e',
                            borderRadius: '8px',
                            width: '28px',
                            height: '28px',
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            outline: 'none',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(244, 63, 94, 0.3)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(244, 63, 94, 0.15)'; }}
                        >-</button>
                        
                        <span style={{ minWidth: '28px', textAlign: 'center', fontWeight: '800', fontSize: '1rem', color: '#fff', fontFamily: 'Outfit' }}>
                          {returnedQuantities[key] || 0}
                        </span>
                        
                        <button 
                          type="button"
                          onClick={() => {
                            const cur = returnedQuantities[key] || 0;
                            const val = Math.min(originalQty, cur + 1);
                            setReturnedQuantities({ ...returnedQuantities, [key]: val });
                          }}
                          style={{
                            background: 'rgba(16, 185, 129, 0.15)',
                            border: 'none',
                            color: '#10b981',
                            borderRadius: '8px',
                            width: '28px',
                            height: '28px',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            outline: 'none',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(16, 185, 129, 0.3)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(16, 185, 129, 0.15)'; }}
                        >+</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer Summary & Actions */}
            <div style={{ 
              borderTop: '1px solid rgba(255,255,255,0.06)', 
              paddingTop: '24px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', fontWeight: 700 }}>الصافي الجديد المتوقع:</span>
                <span style={{ 
                  color: 'var(--accent-green)', 
                  fontWeight: 900, 
                  fontSize: '1.25rem', 
                  marginRight: '8px', 
                  fontFamily: 'Outfit',
                  filter: 'drop-shadow(0 0 6px rgba(16,185,129,0.3))'
                }}>
                  {Math.max(0, reconcilingSale.items.reduce((acc, item) => {
                    const orig = item.originalQuantity !== undefined ? item.originalQuantity : item.quantity;
                    const ret = returnedQuantities[item.barcode || item.tradeName] || 0;
                    return acc + (item.price * (orig - ret));
                  }, 0) - (reconcilingSale.discount || 0) - (reconcilingSale.pointsDiscount || 0)) + (reconcilingSale.deliveryFee || 0)} ج.م
                </span>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  onClick={() => setReconcilingSale(null)} 
                  className="gradient-btn-secondary" 
                  style={{ 
                    padding: '10px 24px', 
                    fontSize: '0.9rem', 
                    borderRadius: '12px', 
                    cursor: 'pointer',
                    fontWeight: 700,
                    transition: 'all 0.2s'
                  }}
                >
                  إلغاء
                </button>
                <button 
                  onClick={handleConfirmPartialReturn} 
                  className="gradient-btn" 
                  style={{ 
                    padding: '10px 24px', 
                    fontSize: '0.9rem', 
                    borderRadius: '12px', 
                    cursor: 'pointer',
                    fontWeight: 700,
                    background: 'var(--accent-green)',
                    boxShadow: '0 8px 24px rgba(16, 185, 129, 0.25)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(16, 185, 129, 0.35)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.25)'; }}
                >
                  تأكيد واستلام
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmFullReturnSale && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(5, 8, 16, 0.88)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '16px', 
          zIndex: 100000,
          backdropFilter: 'blur(16px)',
          animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div className="premium-card fade-in-content" style={{ 
            width: '100%',
            maxWidth: '480px', 
            background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.95) 0%, rgba(9, 14, 28, 0.98) 100%)', 
            border: '1px solid rgba(244, 63, 94, 0.35)', 
            direction: 'rtl', 
            padding: '36px', 
            borderRadius: '24px', 
            boxShadow: '0 24px 60px rgba(244, 63, 94, 0.3)', 
            fontFamily: 'Cairo, sans-serif' 
          }}>
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '24px', 
              borderBottom: '1px solid rgba(255,255,255,0.06)', 
              paddingBottom: '16px' 
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, color: 'var(--accent-pink)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.5rem', filter: 'drop-shadow(0 0 8px rgba(244,63,94,0.4))' }}>⚠️</span> تأكيد إرجاع الفاتورة بالكامل
              </h3>
              <button 
                onClick={() => setConfirmFullReturnSale(null)} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'var(--text-secondary)', 
                  cursor: 'pointer', 
                  fontSize: '1.8rem', 
                  outline: 'none',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
              >
                &times;
              </button>
            </div>
            
            <p style={{ fontSize: '0.98rem', color: '#fff', margin: '0 0 16px 0', lineHeight: 1.6, fontWeight: 700 }}>
              هل أنت متأكد من رغبتك في <span style={{ color: 'var(--accent-pink)' }}>إرجاع الفاتورة بالكامل للمخزن وإلغاء الطلب</span>؟
            </p>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 24px 0', lineHeight: 1.6 }}>
              سيقوم النظام تلقائياً بإعادة جميع كميات الأدوية في الفاتورة رقم <strong style={{ color: '#fff' }}>#{confirmFullReturnSale.invoiceId}</strong> إلى المخزن، وتغيير حالة الطلب إلى مرتجع بالكامل وإرسال إشعار فوري للتليجرام.
            </p>

            {/* Footer buttons */}
            <div style={{ 
              borderTop: '1px solid rgba(255,255,255,0.06)', 
              paddingTop: '24px', 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px' 
            }}>
              <button 
                onClick={() => setConfirmFullReturnSale(null)} 
                className="gradient-btn-secondary" 
                style={{ 
                  padding: '10px 24px', 
                  fontSize: '0.9rem', 
                  borderRadius: '12px', 
                  cursor: 'pointer',
                  fontWeight: 700,
                  transition: 'all 0.2s'
                }}
              >
                تراجع وإلغاء
              </button>
              <button 
                onClick={() => { executeFullReturn(confirmFullReturnSale); setConfirmFullReturnSale(null); }} 
                className="gradient-btn" 
                style={{ 
                  padding: '10px 24px', 
                  fontSize: '0.9rem', 
                  borderRadius: '12px', 
                  cursor: 'pointer',
                  fontWeight: 700,
                  background: 'var(--accent-pink)',
                  boxShadow: '0 8px 24px rgba(244, 63, 94, 0.25)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(244, 63, 94, 0.35)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(244, 63, 94, 0.25)'; }}
              >
                نعم، تأكيد المرتجع
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function POSView({ 
  sales, inventory, cart, setCart, posSearchTerm, setPosSearchTerm, posDiscount, setPosDiscount, 
  handleCheckout, addItemToCart, activeShift, onNotFoundBarcode,
  isDeliveryOrder, setIsDeliveryOrder, customerName, setCustomerName, 
  customerPhone, setCustomerPhone, deliveryAddress, setDeliveryAddress, 
  deliveryDriver, setDeliveryDriver, deliveryFee, setDeliveryFee, employees,
  isLoyaltyEnabled, loyaltyCustomers, selectedCustomer, setSelectedCustomer,
  usePoints, setUsePoints,
  isMobileCheckoutOpen, setIsMobileCheckoutOpen,
  insuranceContracts = [], selectedInsuranceContract, setSelectedInsuranceContract,
  insuranceApprovalNumber, setInsuranceApprovalNumber,
  isSplit, setIsSplit,
  splitCashInput, setSplitCashInput,
  splitCardInput, setSplitCardInput,
  isEWallet, setIsEWallet,
  isInstaPay, setIsInstaPay,
  isVodafone, setIsVodafone,
  resetPaymentModes, paymentSettings, generalCampaigns = []
}) {
  const [posPage, setPosPage] = useState(1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMobileCheckoutExpanded, setIsMobileCheckoutExpanded] = useState(false);
  const touchStartY = useRef(0);
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [displayPage, setDisplayPage] = useState(1);

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    const currentY = e.touches[0].clientY;
    const diff = touchStartY.current - currentY;
    
    if (diff > 50) {
      setIsMobileCheckoutExpanded(true);
    }
    if (diff < -50) {
      if (isMobileCheckoutExpanded) {
        setIsMobileCheckoutExpanded(false);
      } else {
        setIsMobileCheckoutOpen(false);
      }
    }
  };

  // Debounce search term on mobile to prevent key freezing and simulate loading on demand
  useEffect(() => {
    if (posSearchTerm !== '') {
      setIsDataLoading(true);
      const timer = setTimeout(() => {
        setDebouncedTerm(posSearchTerm);
        setIsDataLoading(false);
      }, 350); // 350ms delay
      return () => clearTimeout(timer);
    } else {
      setDebouncedTerm('');
      setIsDataLoading(false);
    }
  }, [posSearchTerm]);

  // Handle on-demand page load delay
  useEffect(() => {
    if (posPage > 1) {
      setIsDataLoading(true);
      const timer = setTimeout(() => {
        setDisplayPage(posPage);
        setIsDataLoading(false);
      }, 250); // 250ms page load delay
      return () => clearTimeout(timer);
    } else {
      setDisplayPage(1);
      setIsDataLoading(false);
    }
  }, [posPage]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPosPage(1);
  }, [posSearchTerm]);

  const termLower = (debouncedTerm || '').toLowerCase();
  const filtered = inventory
    .filter(item => 
      (item.tradeName && item.tradeName.toLowerCase().includes(termLower)) ||
      (item.genericName && item.genericName.toLowerCase().includes(termLower)) ||
      (item.barcode && item.barcode.includes(termLower))
    )
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  const itemsPerPage = 6;
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginatedItems = filtered.slice((displayPage - 1) * itemsPerPage, displayPage * itemsPerPage);

  const subtotal = parseFloat(cart.reduce((acc, item) => {
    const activePrice = item.hasDiscount && item.discountedPrice !== undefined
      ? (item.subUnit ? Math.round(item.subUnit.price * (item.discountedPrice / item.sellingPrice) * 100) / 100 : item.discountedPrice)
      : (item.subUnit ? item.subUnit.price : item.sellingPrice);
    return acc + activePrice * item.cartQuantity;
  }, 0).toFixed(2));

  // Calc live campaign discount based on active general campaigns
  const activeCampaigns = generalCampaigns.filter(c => c.isActive && subtotal >= (c.minTotal || 0));
  const liveCampaignDiscount = activeCampaigns.length > 0
    ? (() => {
        activeCampaigns.sort((a, b) => {
          const va = a.type === 'percentage' ? subtotal * (a.value / 100) : a.value;
          const vb = b.type === 'percentage' ? subtotal * (b.value / 100) : b.value;
          return vb - va;
        });
        const best = activeCampaigns[0];
        return Math.round((best.type === 'percentage' ? subtotal * (best.value / 100) : best.value) * 100) / 100;
      })()
    : 0;
  const totalDiscount = parseFloat((posDiscount + liveCampaignDiscount).toFixed(2));
  
  const maxPointsRedeemable = selectedCustomer && isLoyaltyEnabled
    ? Math.min(selectedCustomer.points || 0, subtotal - totalDiscount + (isDeliveryOrder ? (parseFloat(deliveryFee) || 0) : 0))
    : 0;
  const currentPointsDiscount = usePoints ? maxPointsRedeemable : 0;
  const total = parseFloat((subtotal - totalDiscount - currentPointsDiscount + (isDeliveryOrder ? (parseFloat(deliveryFee) || 0) : 0)).toFixed(2));

  const splitResult = selectedInsuranceContract
    ? calculateInsuranceSplit(
        cart, 
        selectedInsuranceContract, 
        subtotal, 
        totalDiscount, 
        currentPointsDiscount, 
        isDeliveryOrder ? (parseFloat(deliveryFee) || 0) : 0, 
        isDeliveryOrder, 
        0
      )
    : { patientShare: total, insuranceShare: 0, isCapReached: false, isMonthlyCapReached: false };

  const filteredCustomers = isLoyaltyEnabled && (customerPhone.trim().length >= 2 || customerName.trim().length >= 2)
    ? loyaltyCustomers.filter(c => 
        (c.phone && c.phone.includes(customerPhone.trim())) || 
        (c.name && c.name.toLowerCase().includes(customerName.trim().toLowerCase()))
      )
    : [];

  // Active pharmacist targets notification alerts
  const activeTargetsAlert = (() => {
    if (!activeShift?.cashierName) return null;
    try {
      let targetsList = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('drmasr_targets_')) {
          targetsList = JSON.parse(localStorage.getItem(key) || '[]');
          break;
        }
      }

      const cleanName = (n) => (n || '').trim().replace(/\s+/g, ' ');
      const cashierNameCleaned = cleanName(activeShift.cashierName);
      
      const cashierTargets = targetsList.filter(t => {
        const isSelf = cleanName(t.employeeName) === cashierNameCleaned;
        const now = new Date();
        const start = new Date(t.startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(t.endDate);
        end.setHours(23, 59, 59, 999);
        return isSelf && now >= start && now <= end;
      });

      if (cashierTargets.length === 0) return null;

      return cashierTargets.map(t => {
        const start = new Date(t.startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(t.endDate);
        end.setHours(23, 59, 59, 999);

        const periodSales = sales.filter(sale => {
          if (!sale.timestamp) return false;
          const tTime = new Date(sale.timestamp);
          return tTime >= start && tTime <= end;
        });

        let currentVal = 0;
        const pharmaSales = periodSales.filter(s => cleanName(s.cashierName || '') === cashierNameCleaned);
        if (t.type === 'sales_amount') {
          currentVal = pharmaSales.reduce((acc, s) => acc + (s.isInsurance ? (s.patientShare !== undefined ? s.patientShare : s.grandTotal) : s.grandTotal), 0);
        } else {
          currentVal = pharmaSales.length;
        }
        currentVal = Math.round(currentVal * 100) / 100;

        const pct = Math.min(100, Math.round((currentVal / t.value) * 100)) || 0;
        const isAchieved = currentVal >= t.value;

        return (
          <div 
            key={t.id} 
            style={{ 
              background: isAchieved 
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.25) 100%)' 
                : 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.2) 100%)', 
              border: isAchieved ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(99, 102, 241, 0.3)', 
              borderRadius: '16px', 
              padding: '14px 20px', 
              marginBottom: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 800, color: '#fff', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {isAchieved ? '🏆 تم تحقيق التارجت بنجاح!' : '🎯 هدفك الحافز النشط حالياً'}
              </span>
              <span style={{ fontWeight: 700, color: isAchieved ? '#10b981' : '#818cf8', fontSize: '0.85rem' }}>
                المكافأة: {t.bonus} ج.م 💰
              </span>
            </div>
            
            <div style={{ marginTop: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px', fontWeight: 'bold' }}>
                <span style={{ color: 'var(--text-secondary)' }}>نسبة الإنجاز:</span>
                <span style={{ color: isAchieved ? '#34d399' : '#fff' }}>{pct}%</span>
              </div>
              <div style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '99px', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: isAchieved ? 'linear-gradient(90deg, #10b981, #34d399)' : 'linear-gradient(90deg, var(--primary), var(--primary-light, #818cf8))', transition: 'width 0.4s ease' }}></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginTop: '6px', color: 'rgba(255, 255, 255, 0.7)' }}>
                {isAchieved ? (
                  <span style={{ color: '#10b981', fontWeight: 'bold' }}>🎉 تم اكتمال الهدف</span>
                ) : (
                  <span>الحالي: {currentVal} {t.type === 'sales_amount' ? 'ج.م' : 'أوردر'}</span>
                )}
                <span>الهدف: {t.value} {t.type === 'sales_amount' ? 'ج.م' : 'أوردر'}</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
              <span>تاريخ الانتهاء:</span>
              <span style={{ color: '#fbbf24', fontWeight: 600 }}>{t.endDate}</span>
            </div>
          </div>
        );
      });
    } catch (e) {
      return null;
    }
  })();

  const cleanName = (n) => (n || '').trim().replace(/\s+/g, ' ');

  return (
    <div className="pos-container fade-in-content" style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '20px' }}>
      <div className="pos-main">
        {activeTargetsAlert}
        <div className="premium-card" style={{ marginBottom: '20px' }}>
          <input 
            type="text" 
            className="premium-input" 
            placeholder="بحث عن صنف أو مسح باركود..." 
            value={posSearchTerm}
            onChange={(e) => {
              const term = e.target.value;
              setPosSearchTerm(term);
              const found = inventory.find(i => i.barcode === term.trim());
              if (found) {
                addItemToCart(found);
                setPosSearchTerm('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const term = posSearchTerm.trim();
                if (!term) return;
                const found = inventory.find(i => i.barcode === term);
                if (found) {
                  addItemToCart(found);
                  setPosSearchTerm('');
                } else {
                  onNotFoundBarcode(term);
                  setPosSearchTerm('');
                }
              }
            }}
            style={{ width: '100%' }}
          />
        </div>
        
        {/* 2x3 Products Grid */}
        <div className="pos-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {paginatedItems.map(item => {
            const isOutOfStock = item.quantity <= 0;
            return (
              <div 
                key={item.id} 
                className="pos-product-card" 
                onClick={() => addItemToCart(item)}
                style={{
                  position: 'relative',
                  cursor: 'pointer',
                  opacity: isOutOfStock ? 0.65 : 1,
                  filter: isOutOfStock ? 'grayscale(35%)' : 'none',
                  background: isOutOfStock ? 'rgba(239, 68, 68, 0.05)' : 'rgba(255, 255, 255, 0.02)',
                  border: isOutOfStock ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid var(--border)',
                  transition: 'all 0.2s ease',
                  padding: '16px',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: '130px'
                }}
              >
                {/* Out of Stock / Quantity Badge */}
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  left: '8px',
                  background: isOutOfStock ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                  color: isOutOfStock ? '#ef4444' : '#10b981',
                  padding: '2px 8px',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: 700
                }}>
                  {isOutOfStock ? 'انتهى' : `${item.quantity} متاح`}
                </div>

                {/* Discount Badge */}
                {item.hasDiscount && item.discountedPrice !== undefined && item.sellingPrice > item.discountedPrice && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                    padding: '2px 8px',
                    borderRadius: '8px',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)'
                  }}>
                    خصم {Math.round(((item.sellingPrice - item.discountedPrice) / item.sellingPrice) * 100)}%
                  </div>
                )}

                <div className="pos-card-name" style={{ fontWeight: 700, fontSize: '1rem', color: '#fff', marginTop: '16px', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'wrap', gap: '4px', flexDirection: 'row-reverse' }}>
                  <span>{item.tradeName}</span>
                  {item.requiresFridge && (
                    <span style={{ padding: '1px 5px', background: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4', borderRadius: '4px', fontSize: '0.62rem', fontWeight: 700 }}>
                      ❄️ ثلاجة
                    </span>
                  )}
                </div>
                <div className="pos-card-sub" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'right', marginBottom: '8px' }}>
                  {item.genericName}
                  {item.expiryDate && (() => {
                    const expiryStr = typeof item.expiryDate === 'object' ? (item.expiryDate.year ? `${item.expiryDate.year}-${String(item.expiryDate.month).padStart(2, '0')}-${String(item.expiryDate.day).padStart(2, '0')}` : JSON.stringify(item.expiryDate)) : String(item.expiryDate);
                    const clean = expiryStr.trim();
                    let date = null;
                    const dmYRegex = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/;
                    const matchDmY = clean.match(dmYRegex);
                    if (matchDmY) {
                      date = new Date(parseInt(matchDmY[3], 10), parseInt(matchDmY[2], 10) - 1, parseInt(matchDmY[1], 10));
                    } else {
                      const YmdRegex = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;
                      const matchYmd = clean.match(YmdRegex);
                      if (matchYmd) {
                        date = new Date(parseInt(matchYmd[1], 10), parseInt(matchYmd[2], 10) - 1, parseInt(matchYmd[3], 10));
                      } else {
                        const parsed = new Date(expiryStr);
                        if (!isNaN(parsed.getTime())) date = parsed;
                      }
                    }

                    if (!date) {
                      return (
                        <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '3px', fontWeight: 600 }}>
                          📅 صلاحية: {expiryStr}
                        </span>
                      );
                    }

                    const now = new Date();
                    const diffMonth = (date.getFullYear() - now.getFullYear()) * 12 + date.getMonth() - now.getMonth();

                    let color = '#86efac'; // أخضر فاتح
                    if (diffMonth < 0) {
                      color = '#f87171'; // أحمر
                    } else if (diffMonth <= 6) {
                      color = '#facc15'; // أصفر
                    }

                    return (
                      <span style={{ display: 'block', fontSize: '0.72rem', color: color, marginTop: '3px', fontWeight: 700 }}>
                        📅 صلاحية: {expiryStr}
                      </span>
                    );
                  })()}
                </div>
                <div className="pos-card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                  <span className="pos-card-price" style={{ fontWeight: 800, color: 'var(--primary)', fontSize: '1.05rem' }}>
                    {item.hasDiscount && item.discountedPrice !== undefined && item.sellingPrice > item.discountedPrice ? (
                      <>
                        <span style={{ fontSize: '0.78rem', textDecoration: 'line-through', color: 'rgba(255,255,255,0.4)', marginLeft: '6px', fontWeight: 'normal' }}>
                          {item.sellingPrice}
                        </span>
                        <span style={{ color: '#10b981' }}>
                          {item.discountedPrice} ج.م
                        </span>
                      </>
                    ) : (
                      <>{item.sellingPrice} ج.م</>
                    )}
                  </span>
                  <button 
                    className="pos-card-add"
                    type="button"
                    style={{
                      background: isOutOfStock ? 'rgba(239, 68, 68, 0.1)' : 'var(--primary)',
                      color: isOutOfStock ? '#ef4444' : '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      cursor: 'pointer'
                    }}
                  >
                    {isOutOfStock ? '🔍' : '+'}
                  </button>
                </div>
              </div>
            );
          })}
          
          {filtered.length === 0 && posSearchTerm.length > 5 && (
            <div className="premium-card" style={{ gridColumn: 'span 3', textAlign: 'center', padding: '40px' }}>
              <p>هذا الصنف غير موجود في المخزن.</p>
              <button className="gradient-btn" onClick={() => onNotFoundBarcode(posSearchTerm)}>إضافة صنف جديد بالباركود: {posSearchTerm}</button>
            </div>
          )}
        </div>

        {/* 2x3 Grid Pagination controls */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '20px' }}>
          <button 
            type="button"
            onClick={() => setPosPage(p => Math.max(1, p - 1))} 
            disabled={posPage === 1}
            className="gradient-btn-secondary"
            style={{ padding: '8px 16px', fontSize: '0.85rem', color: '#fff', border: '1px solid var(--border)', borderRadius: '8px', cursor: posPage === 1 ? 'not-allowed' : 'pointer', opacity: posPage === 1 ? 0.4 : 1, transition: 'all 0.2s' }}
          >
            السابق
          </button>
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontWeight: 600 }}>
            صفحة {posPage} من {totalPages}
          </span>
          <button 
            type="button"
            onClick={() => setPosPage(p => Math.min(totalPages, p + 1))} 
            disabled={posPage === totalPages}
            className="gradient-btn-secondary"
            style={{ padding: '8px 16px', fontSize: '0.85rem', color: '#fff', border: '1px solid var(--border)', borderRadius: '8px', cursor: posPage === totalPages ? 'not-allowed' : 'pointer', opacity: posPage === totalPages ? 0.4 : 1, transition: 'all 0.2s' }}
          >
            التالي
          </button>
        </div>
      </div>

      <div className={`pos-sidebar ${isMobileCheckoutOpen ? 'mobile-open' : ''} ${isMobileCheckoutExpanded ? 'expanded' : ''}`}>
        {/* Interactive Drag Handle for Mobile */}
        <div 
          className="mobile-only" 
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          style={{
            width: '100%',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'row-resize',
            margin: '-4px 0 8px 0',
            flexShrink: 0
          }}
          onClick={() => setIsMobileCheckoutExpanded(!isMobileCheckoutExpanded)}
          title="اسحب لأعلى للتوسيع / لأسفل للتصغير"
        >
          <div style={{
            width: '44px',
            height: '5px',
            background: 'rgba(255, 255, 255, 0.25)',
            borderRadius: '3px',
            transition: 'background 0.2s'
          }} />
        </div>

        <div className="premium-card" style={{ position: 'sticky', top: '20px', display: 'flex', flexDirection: 'column', padding: '20px' }}>
          
          {/* Close button for Mobile Drawer Bottom Sheet */}
          <div className="mobile-only" style={{ display: 'none', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '12px' }}>
            <h3 style={{ fontSize: '1.1rem', color: '#fff', margin: 0, fontWeight: 800 }}>السلة والتسوية</h3>
            <button 
              type="button"
              onClick={() => setIsMobileCheckoutOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff',
                fontSize: '1.2rem',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              &times;
            </button>
          </div>
          <h3 style={{ marginBottom: '15px', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
            سلة المشتريات
          </h3>
          
          <div className="cart-items" style={{ flex: 1, maxHeight: '200px', overflowY: 'auto', marginBottom: '8px', paddingRight: '5px' }}>
            {cart.length === 0 ? (
              <div style={{ textAlign: 'center', marginTop: '40px', color: 'var(--text-secondary)' }}>
                <p>السلة فارغة. يرجى إضافة أدوية للبدء.</p>
              </div>
            ) : (
              cart.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff' }}>{item.tradeName}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {item.cartQuantity} × {item.subUnit ? item.subUnit.price : item.sellingPrice} ج.م
                    </div>
                    {selectedCustomer && (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', color: item.isChronic ? '#10b981' : 'var(--text-secondary)', marginTop: '4px', cursor: 'pointer', userSelect: 'none' }}>
                        <input 
                          type="checkbox" 
                          checked={!!item.isChronic} 
                          onChange={() => {
                            const updated = [...cart];
                            updated[idx].isChronic = !updated[idx].isChronic;
                            setCart(updated);
                          }}
                          style={{ accentColor: '#10b981', cursor: 'pointer' }}
                        />
                        🔄 دواء تكرار دوري
                      </label>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 800, color: 'var(--primary)', fontSize: '0.9rem' }}>
                      {parseFloat(((item.subUnit ? item.subUnit.price : item.sellingPrice) * item.cartQuantity).toFixed(2))}
                    </span>
                    <button onClick={() => setCart(cart.filter((_, i) => i !== idx))} style={{ color: 'var(--accent-pink)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '0 4px' }}>&times;</button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '8px', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
               <button 
                type="button"
                onClick={() => setIsDeliveryOrder(!isDeliveryOrder)} 
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  fontSize: '0.95rem', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '8px', 
                  fontWeight: 'bold',
                  borderRadius: '10px',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: isDeliveryOrder 
                    ? 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)' 
                    : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', 
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                }}
               >
                 {isDeliveryOrder ? '🛒 إلغاء الدليفري (بيع مباشر)' : '🛵 طلب دليفري'}
               </button>
            </div>

            {isDeliveryOrder && (
              <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '10px', position: 'relative' }}>
                {selectedCustomer ? (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.18) 100%)',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    borderRadius: '10px',
                    padding: '8px 12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#fbbf24' }}>
                        👤 {selectedCustomer.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#e0a82e', marginTop: '2px' }}>
                        رصيد النقاط: <strong style={{ fontSize: '0.9rem' }}>{selectedCustomer.points || 0}</strong> نقطة
                      </div>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => {
                        setSelectedCustomer(null);
                        setCustomerName('');
                        setCustomerPhone('');
                        setUsePoints(false);
                      }} 
                      style={{
                        background: 'rgba(239, 68, 68, 0.2)',
                        color: '#f87171',
                        border: 'none',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <>
                    <input 
                      type="text" 
                      className="premium-input" 
                      style={{ fontSize: '0.85rem', padding: '8px' }} 
                      placeholder="اسم العميل" 
                      value={customerName} 
                      onChange={e => {
                        setCustomerName(e.target.value);
                        setShowSuggestions(true);
                      }} 
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 250)}
                    />
                    <input 
                      type="text" 
                      className="premium-input" 
                      style={{ fontSize: '0.85rem', padding: '8px' }} 
                      placeholder="رقم الهاتف" 
                      value={customerPhone} 
                      onChange={e => {
                        setCustomerPhone(e.target.value);
                        setShowSuggestions(true);
                      }} 
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 250)}
                    />
                  </>
                )}

                {/* Suggestions List in Delivery */}
                {!selectedCustomer && showSuggestions && filteredCustomers.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '96px',
                    left: 0,
                    right: 0,
                    background: '#141229',
                    border: '1px solid rgba(139, 92, 246, 0.4)',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 0 15px rgba(139, 92, 246, 0.2)',
                    zIndex: 9999,
                    maxHeight: '180px',
                    overflowY: 'auto',
                    marginTop: '4px',
                    padding: '6px'
                  }}>
                    {filteredCustomers.map(cust => (
                      <div 
                        key={cust.id || cust.phone}
                        onMouseDown={(e) => {
                          e.preventDefault(); // prevents input blur before click
                          setSelectedCustomer(cust);
                          setCustomerName(cust.name || '');
                          setCustomerPhone(cust.phone || '');
                          setShowSuggestions(false);
                        }}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(255,255,255,0.03)',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          textAlign: 'right'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = 'none';
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff' }}>{cust.name}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px' }}>📱 {cust.phone}</div>
                        </div>
                        <div style={{ textAlign: 'left' }}>
                          <span style={{
                            background: 'rgba(245, 158, 11, 0.15)',
                            color: '#fbbf24',
                            padding: '2px 8px',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            {cust.points || 0} نقطة
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <input type="text" className="premium-input" style={{ fontSize: '0.85rem', padding: '8px' }} placeholder="العنوان بالتفصيل" value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)} />
                
                {(() => {
                  const searchPhone = selectedCustomer?.phone || customerPhone;
                  const prevAddresses = searchPhone 
                    ? [...new Set(sales
                        .filter(s => s.customerPhone === searchPhone && s.deliveryDetails?.address)
                        .map(s => s.deliveryDetails.address)
                      )].slice(0, 3)
                    : [];
                  if (prevAddresses.length > 0) {
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '-4px', background: 'rgba(139, 92, 246, 0.03)', border: '1px dashed rgba(139, 92, 246, 0.25)', padding: '10px', borderRadius: '10px', direction: 'rtl' }}>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 'bold', textAlign: 'right' }}>💡 عناوين مسجلة سابقة لهذا الهاتف (اضغط للاختيار):</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {prevAddresses.map((addr, idx) => (
                            <span
                              key={idx}
                              onClick={() => setDeliveryAddress(addr)}
                              style={{
                                background: 'rgba(139, 92, 246, 0.1)',
                                border: '1px solid rgba(139, 92, 246, 0.3)',
                                color: '#c084fc',
                                fontSize: '0.78rem',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)'}
                            >
                              📍 {addr}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                <select className="premium-input" style={{ fontSize: '0.85rem', padding: '8px' }} value={deliveryDriver} onChange={e => setDeliveryDriver(e.target.value)}>
                  <option value="">اختر الطيار</option>
                  {employees.filter(e => e.role === 'driver' || e.role === 'طيار').map(emp => (
                    <option key={emp.id} value={emp.name}>{emp.name}</option>
                  ))}
                  <option value="طيار خارجي">طيار خارجي</option>
                </select>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>خدمة التوصيل:</span>
                  <input type="number" className="premium-input" style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'center' }} value={deliveryFee} onChange={e => setDeliveryFee(e.target.value)} />
                </div>
              </div>
            )}

            {!isDeliveryOrder && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', background: 'rgba(255,255,255,0.02)', padding: '8px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '10px', position: 'relative' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <strong>بيانات العميل:</strong>
                </span>
                {selectedCustomer ? (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.18) 100%)',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    borderRadius: '10px',
                    padding: '8px 12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#fbbf24' }}>
                        👤 {selectedCustomer.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#e0a82e', marginTop: '2px' }}>
                        رصيد النقاط: <strong style={{ fontSize: '0.9rem' }}>{selectedCustomer.points || 0}</strong> نقطة
                      </div>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => {
                        setSelectedCustomer(null);
                        setCustomerName('');
                        setCustomerPhone('');
                        setUsePoints(false);
                      }} 
                      style={{
                        background: 'rgba(239, 68, 68, 0.2)',
                        color: '#f87171',
                        border: 'none',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input 
                      type="text" 
                      className="premium-input" 
                      placeholder="اسم العميل" 
                      style={{ flex: 1, padding: '4px 8px', fontSize: '0.8rem' }} 
                      value={customerName} 
                      onChange={e => {
                        setCustomerName(e.target.value);
                        setShowSuggestions(true);
                      }} 
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 250)}
                    />
                    <input 
                      type="tel" 
                      className="premium-input" 
                      placeholder="رقم العميل" 
                      style={{ flex: 1, padding: '4px 8px', fontSize: '0.8rem', textAlign: 'center' }} 
                      value={customerPhone} 
                      onChange={e => {
                        setCustomerPhone(e.target.value.replace(/\D/g, ''));
                        setShowSuggestions(true);
                      }} 
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 250)}
                    />
                  </div>
                )}

                {/* Suggestions List in Direct Sale */}
                {!selectedCustomer && showSuggestions && filteredCustomers.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#141229',
                    border: '1px solid rgba(139, 92, 246, 0.4)',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 0 15px rgba(139, 92, 246, 0.2)',
                    zIndex: 9999,
                    maxHeight: '180px',
                    overflowY: 'auto',
                    marginTop: '4px',
                    padding: '6px'
                  }}>
                    {filteredCustomers.map(cust => (
                      <div 
                        key={cust.id || cust.phone}
                        onMouseDown={(e) => {
                          e.preventDefault(); // prevents input blur before click
                          setSelectedCustomer(cust);
                          setCustomerName(cust.name || '');
                          setCustomerPhone(cust.phone || '');
                          setShowSuggestions(false);
                        }}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(255,255,255,0.03)',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          textAlign: 'right'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = 'none';
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff' }}>{cust.name}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px' }}>📱 {cust.phone}</div>
                        </div>
                        <div style={{ textAlign: 'left' }}>
                          <span style={{
                            background: 'rgba(245, 158, 11, 0.15)',
                            color: '#fbbf24',
                            padding: '2px 8px',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            {cust.points || 0} نقطة
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Loyalty Cashback Redemption Panel */}
            {selectedCustomer && isLoyaltyEnabled && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.12) 100%)',
                border: '1px solid rgba(245, 158, 11, 0.25)',
                borderRadius: '12px',
                padding: '12px',
                marginBottom: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(245, 158, 11, 0.05)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.85rem', color: '#fbbf24', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    ✨ كاش باك الولاء
                  </span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    المتاح: <strong style={{ color: '#fbbf24' }}>{selectedCustomer.points || 0} ج.م</strong>
                  </span>
                </div>
                
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '6px 8px',
                  background: 'rgba(255,255,255,0.02)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}>
                  <input 
                    type="checkbox" 
                    checked={usePoints} 
                    onChange={(e) => setUsePoints(e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      accentColor: '#fbbf24',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontSize: '0.8rem', color: '#fff', fontWeight: 600 }}>
                    استخدام النقاط للخصم من الفاتورة
                  </span>
                </label>
                
                {usePoints && (
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#fbbf24', 
                    background: 'rgba(245, 158, 11, 0.1)', 
                    padding: '6px 10px', 
                    borderRadius: '6px',
                    textAlign: 'right'
                  }}>
                    سيتم خصم <strong>{maxPointsRedeemable} ج.م</strong> من الفاتورة (مقابل {maxPointsRedeemable} نقطة).
                  </div>
                )}
              </div>
            )}

            {/* Medical Insurance & Syndicates Selection Panel */}
            {insuranceContracts.filter(c => c.isActive !== false).length > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.12) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                borderRadius: '12px',
                padding: '12px',
                marginBottom: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.05)',
                direction: 'rtl',
                textAlign: 'right'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.85rem', color: '#34d399', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    🏥 التأمين الطبي والنقابات
                  </span>
                  {selectedInsuranceContract && (
                    <button 
                      onClick={() => {
                        setSelectedInsuranceContract(null);
                        setInsuranceApprovalNumber('');
                      }}
                      style={{ background: 'none', border: 'none', color: 'var(--accent-pink)', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      إلغاء ✕
                    </button>
                  )}
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <select
                    className="premium-input"
                    style={{ padding: '6px 10px', fontSize: '0.8rem', background: '#141229', color: '#fff', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', cursor: 'pointer', width: '100%' }}
                    value={selectedInsuranceContract ? selectedInsuranceContract.id : ''}
                    onChange={(e) => {
                      const contract = insuranceContracts.find(c => c.id === e.target.value);
                      setSelectedInsuranceContract(contract || null);
                    }}
                  >
                    <option value="">-- اختر جهة التعاقد / التأمين --</option>
                    {insuranceContracts.filter(c => c.isActive !== false).map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} (تحمل المريض {c.copayPercentage}%)
                      </option>
                    ))}
                  </select>
                  
                  {selectedInsuranceContract && (
                    <input
                      type="text"
                      className="premium-input"
                      placeholder="رقم الموافقة / كود التصديق"
                      style={{ padding: '6px 10px', fontSize: '0.8rem', background: '#141229', color: '#fff', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}
                      value={insuranceApprovalNumber}
                      onChange={(e) => setInsuranceApprovalNumber(e.target.value)}
                    />
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                <span>المجموع الفرعي:</span>
                <span>{subtotal} ج.م</span>
              </div>
              {posDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--accent-pink)' }}>
                  <span>الخصم اليدوي:</span>
                  <span>- {posDiscount} ج.م</span>
                </div>
              )}
              {liveCampaignDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#10b981' }}>
                  <span>خصم العروض والترويج:</span>
                  <span>- {liveCampaignDiscount} ج.م</span>
                </div>
              )}
              {currentPointsDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#fbbf24' }}>
                  <span>خصم النقاط (كاش باك):</span>
                  <span>- {currentPointsDiscount} ج.م</span>
                </div>
              )}
              {isDeliveryOrder && deliveryFee > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--primary)' }}>
                  <span>خدمة التوصيل:</span>
                  <span>+ {deliveryFee} ج.م</span>
                </div>
              )}
            </div>
          </div>

          {selectedInsuranceContract && (
            <div style={{ 
              background: 'rgba(16, 185, 129, 0.05)', 
              border: '1px dashed rgba(16, 185, 129, 0.25)', 
              borderRadius: '8px', 
              padding: '8px 12px', 
              marginBottom: '10px',
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
              direction: 'rtl',
              textAlign: 'right'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>تحمل شركة التأمين:</span>
                <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>{splitResult.insuranceShare.toFixed(2)} ج.م</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>تحمل المريض:</span>
                <span style={{ color: 'var(--accent-green)', fontWeight: 'bold' }}>{splitResult.patientShare.toFixed(2)} ج.م</span>
              </div>
              {splitResult.isCapReached && (
                <div style={{ color: 'var(--accent-pink)', fontSize: '0.75rem', marginTop: '4px', fontWeight: 'bold' }}>
                  ⚠️ تم الوصول للحد الأقصى للتغطية ({selectedInsuranceContract.maxCoverageLimit} ج.م) وتم ترحيل الزيادة للمريض.
                </div>
              )}
              {selectedInsuranceContract.notes && (
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '4px', marginTop: '4px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  📝 شروط خاصة: {selectedInsuranceContract.notes}
                </div>
              )}
            </div>
          )}

          <div className="cart-summary" style={{ borderTop: '2px solid var(--border)', paddingTop: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontWeight: 700 }}>{selectedInsuranceContract ? 'تحمل المريض (الصافي):' : 'صافي المطلوب:'}</span>
              <span style={{ fontWeight: 800, fontSize: '1.3rem', color: 'var(--accent-green)' }}>
                {selectedInsuranceContract 
                  ? splitResult.patientShare.toFixed(2) 
                  : total} ج.م
              </span>
            </div>
            {isDeliveryOrder ? (
              <button 
                className="gradient-btn" 
                style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 'bold' }}
                onClick={() => handleCheckout('cash')}
                disabled={cart.length === 0}
              >
                تسجيل طلب الدليفري
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                {/* ── Payment Modes Selection (Side-by-Side Flex) ── */}
                <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                  {/* ── 1) Split Toggle ── */}
                  <label style={{
                    flex: 1,
                    display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', direction: 'rtl',
                    background: isSplit ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.03)',
                    border: isSplit ? '1px solid rgba(139,92,246,0.45)' : '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '8px', padding: '6px 10px', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', userSelect: 'none',
                    transform: isSplit ? 'scale(1.01)' : 'scale(1)',
                    boxShadow: isSplit ? '0 4px 12px rgba(139,92,246,0.15)' : 'none'
                  }}>
                    <input type="checkbox" checked={isSplit}
                      onChange={e => {
                        const checked = e.target.checked;
                        resetPaymentModes();
                        if (checked) {
                          setIsSplit(true);
                          const actualTotal = selectedInsuranceContract 
                            ? parseFloat((total * (selectedInsuranceContract.copayPercentage / 100)).toFixed(2))
                            : total;
                          const half = parseFloat((actualTotal / 2).toFixed(2));
                          setSplitCashInput(String(half));
                          setSplitCardInput(String(parseFloat((actualTotal - half).toFixed(2))));
                        }
                      }}
                      style={{ width: '15px', height: '15px', accentColor: '#8b5cf6', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: isSplit ? '#a78bfa' : 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      💵💳 دفع مختلط
                    </span>
                  </label>

                  {/* ── 2) E-Wallet Toggle (InstaPay / Vodafone Cash) ── */}
                  {(paymentSettings?.instaPayIPN || paymentSettings?.vodafoneCashNumber) ? (
                    <label style={{
                      flex: 1,
                      display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', direction: 'rtl',
                      background: isEWallet ? 'rgba(14,165,233,0.12)' : 'rgba(255,255,255,0.03)',
                      border: isEWallet ? '1px solid rgba(14,165,233,0.45)' : '1px solid rgba(255,255,255,0.07)',
                      borderRadius: '8px', padding: '6px 10px', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', userSelect: 'none',
                      transform: isEWallet ? 'scale(1.01)' : 'scale(1)',
                      boxShadow: isEWallet ? '0 4px 12px rgba(14,165,233,0.15)' : 'none'
                    }}>
                      <input type="checkbox" checked={isEWallet}
                        onChange={e => {
                          const checked = e.target.checked;
                          resetPaymentModes();
                          if (checked) {
                            setIsEWallet(true);
                          }
                        }}
                        style={{ width: '15px', height: '15px', accentColor: '#0ea5e9', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: isEWallet ? '#38bdf8' : 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        📱 دفع رقمي
                      </span>
                    </label>
                  ) : null}
                </div>

                {/* ── Normal mode: كاش + فيزا + آجل buttons ── */}
                {!isSplit && !isEWallet && !isInstaPay && !isVodafone && (
                  <div style={{ display: 'flex', gap: '8px', width: '100%', flexWrap: 'wrap', animation: 'fadeInDown 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <button className="gradient-btn"
                      style={{ flex: 1, minWidth: '80px', padding: '13px 4px', fontSize: '0.95rem', fontWeight: 'bold', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', transition: 'all 0.2s ease' }}
                      onClick={() => handleCheckout('cash')} disabled={cart.length === 0}>
                      بيع كاش
                    </button>
                    <button className="gradient-btn"
                      style={{ flex: 1, minWidth: '80px', padding: '13px 4px', fontSize: '0.95rem', fontWeight: 'bold', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', transition: 'all 0.2s ease' }}
                      onClick={() => handleCheckout('credit')} disabled={cart.length === 0}>
                      بيع فيزا
                    </button>
                    {(selectedCustomer || (customerName.trim().length > 0 && customerPhone.trim().length > 0)) && paymentSettings?.allowDeferredSales !== false && (
                      <button className="gradient-btn"
                        style={{ flex: 1, minWidth: '80px', padding: '13px 4px', fontSize: '0.95rem', fontWeight: 'bold', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', transition: 'all 0.2s ease' }}
                        onClick={() => handleCheckout('deferred')} disabled={cart.length === 0}>
                        ⏳ بيع بالآجل
                      </button>
                    )}
                  </div>
                )}

                {isEWallet && !isInstaPay && !isVodafone && (
                  <div style={{ display: 'flex', gap: '8px', width: '100%', animation: 'fadeInDown 0.3s ease' }}>
                    {paymentSettings?.instaPayIPN && (
                      <button className="gradient-btn"
                        style={{ flex: 1, padding: '13px 4px', fontSize: '0.95rem', fontWeight: 'bold', background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' }}
                        onClick={() => setIsInstaPay(true)} disabled={cart.length === 0}>
                        📱 إنستاباي
                      </button>
                    )}
                    {paymentSettings?.vodafoneCashNumber && (
                      <button className="gradient-btn"
                        style={{ flex: 1, padding: '13px 4px', fontSize: '0.95rem', fontWeight: 'bold', background: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)' }}
                        onClick={() => setIsVodafone(true)} disabled={cart.length === 0}>
                        📲 فودافون كاش
                      </button>
                    )}
                  </div>
                )}

                {/* ── Split mode inputs ── */}
                {isSplit && (() => {
                  const actualTotal = selectedInsuranceContract 
                    ? parseFloat((total * (selectedInsuranceContract.copayPercentage / 100)).toFixed(2))
                    : total;
                  const cashVal = parseFloat(splitCashInput) || 0;
                  const cardVal = parseFloat(splitCardInput) || 0;
                  const sumOk = Math.abs(cashVal + cardVal - actualTotal) < 0.05;
                  const remaining = parseFloat((actualTotal - cashVal - cardVal).toFixed(2));
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', animation: 'fadeInDown 0.3s ease' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ flex: 1, borderRadius: '12px', overflow: 'hidden', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', padding: '2px', boxShadow: '0 4px 14px rgba(16,185,129,0.35)' }}>
                          <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: '10px', padding: '10px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6ee7b7' }}>💵 كاش</span>
                            <input type="number" min="0" step="0.01" value={splitCashInput}
                              onChange={e => setSplitCashInput(e.target.value)} placeholder="0.00"
                              style={{ background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontWeight: 800, fontSize: '1.1rem', textAlign: 'center', width: '100%' }} />
                            <span style={{ fontSize: '0.7rem', color: '#a7f3d0' }}>ج.م</span>
                          </div>
                        </div>
                        <div style={{ flex: 1, borderRadius: '12px', overflow: 'hidden', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', padding: '2px', boxShadow: '0 4px 14px rgba(59,130,246,0.35)' }}>
                          <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: '10px', padding: '10px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#93c5fd' }}>💳 فيزا</span>
                            <input type="number" min="0" step="0.01" value={splitCardInput}
                              onChange={e => setSplitCardInput(e.target.value)} placeholder="0.00"
                              style={{ background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontWeight: 800, fontSize: '1.1rem', textAlign: 'center', width: '100%' }} />
                            <span style={{ fontSize: '0.7rem', color: '#bfdbfe' }}>ج.م</span>
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', fontSize: '0.8rem', fontWeight: 600, color: sumOk ? '#10b981' : remaining > 0 ? '#fbbf24' : '#f87171', transition: 'color 0.2s ease' }}>
                        {sumOk ? '✅ المجموع مضبوط' : remaining > 0 ? `⚠️ باقي ${remaining} ج.م` : `❌ زيادة ${Math.abs(remaining)} ج.م`}
                      </div>
                      <button className="gradient-btn"
                        style={{ width: '100%', padding: '13px', fontSize: '1rem', fontWeight: 800, background: sumOk ? 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' : 'rgba(100,100,120,0.4)', cursor: sumOk ? 'pointer' : 'not-allowed', transition: 'all 0.3s ease' }}
                        disabled={cart.length === 0 || !sumOk}
                        onClick={() => handleCheckout('split', { cash: cashVal, card: cardVal })}>
                        💵💳 تأكيد الدفع المختلط
                      </button>
                    </div>
                  );
                })()}

                {/* ── InstaPay confirm button ── */}
                {isInstaPay && (
                  <div style={{ animation: 'fadeInDown 0.3s ease', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{
                      borderRadius: '12px', overflow: 'hidden',
                      background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                      padding: '2px', boxShadow: '0 4px 18px rgba(2,132,199,0.4)'
                    }}>
                      <div style={{ background: 'rgba(0,0,0,0.4)', borderRadius: '10px', padding: '14px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#7dd3fc', marginBottom: '4px' }}>📱 إنستاباي — IPN</div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#fff', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {paymentSettings?.instaPayIPN}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#bae6fd', marginTop: '4px' }}>
                          المبلغ المطلوب: <b>{selectedInsuranceContract ? (total * (selectedInsuranceContract.copayPercentage / 100)).toFixed(2) : total} ج.م</b>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="gradient-btn"
                        style={{ padding: '10px', fontSize: '0.9rem', background: '#475569', flex: 1 }}
                        onClick={() => { setIsInstaPay(false); setIsEWallet(true); }}>
                        رجوع
                      </button>
                      <button className="gradient-btn"
                        style={{ width: '100%', padding: '13px', fontSize: '1rem', fontWeight: 800, background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', transition: 'all 0.3s ease', flex: 2 }}
                        disabled={cart.length === 0}
                        onClick={() => handleCheckout('instapay')}>
                        📱 تأكيد الدفع
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Vodafone Cash confirm button ── */}
                {isVodafone && (
                  <div style={{ animation: 'fadeInDown 0.3s ease', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{
                      borderRadius: '12px', overflow: 'hidden',
                      background: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)',
                      padding: '2px', boxShadow: '0 4px 18px rgba(225,29,72,0.4)'
                    }}>
                      <div style={{ background: 'rgba(0,0,0,0.4)', borderRadius: '10px', padding: '14px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fda4af', marginBottom: '8px' }}>📲 فودافون كاش — رابط الدفع الإلكتروني</div>
                        
                        <a 
                          href={paymentSettings?.vodafoneCashNumber} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="gradient-btn"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: '#fff',
                            fontWeight: 'bold',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            textDecoration: 'none',
                            fontSize: '0.85rem',
                            marginBottom: '8px',
                            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
                            border: 'none'
                          }}
                        >
                          🔗 الانتقال لرابط الدفع السريع
                        </a>

                        <div style={{ fontSize: '0.72rem', color: '#fecdd3', wordBreak: 'break-all', opacity: 0.8, marginBottom: '6px', fontFamily: 'monospace' }}>
                          {paymentSettings?.vodafoneCashNumber}
                        </div>

                        <div style={{ fontSize: '0.85rem', color: '#fecdd3', marginTop: '4px' }}>
                          المبلغ المطلوب: <b>{selectedInsuranceContract ? (total * (selectedInsuranceContract.copayPercentage / 100)).toFixed(2) : total} ج.م</b>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="gradient-btn"
                        style={{ padding: '10px', fontSize: '0.9rem', background: '#475569', flex: 1 }}
                        onClick={() => { setIsVodafone(false); setIsEWallet(true); }}>
                        رجوع
                      </button>
                      <button className="gradient-btn"
                        style={{ width: '100%', padding: '13px', fontSize: '1rem', fontWeight: 800, background: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)', transition: 'all 0.3s ease', flex: 2 }}
                        disabled={cart.length === 0}
                        onClick={() => handleCheckout('vodafone')}>
                        📲 تأكيد الدفع
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

const translateProductText = async (text) => {
  if (!text || !text.trim()) return "";
  const trimmed = text.trim();
  const hasArabic = /[\u0600-\u06FF]/.test(trimmed);
  const sourceLang = hasArabic ? 'ar' : 'en';
  const targetLang = hasArabic ? 'en' : 'ar';
  try {
    const response = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=${targetLang}&dt=t&q=${encodeURIComponent(trimmed)}`);
    if (!response.ok) throw new Error("Translation API failed");
    const data = await response.json();
    if (data && data[0] && data[0][0] && data[0][0][0]) {
      return data[0][0][0];
    }
  } catch (err) {
    console.error("Translation error:", err);
  }
  return trimmed;
};

function InventoryView({ inventory, inventorySearch, setInventorySearch, inventoryPage, setInventoryPage, setEditId, setTradeName, setTradeNameSecondary, setGenericName, setCategory, setBuyingPrice, setSellingPrice, setQuantity, setExpiryDate, setBarcode, setSubUnits, setIsModalOpen, handleDeleteItem, getExpiryStatus, isAdminVerified, setIncrementProduct, setIncrementCount, currentUserData, db, user, setInventory, sales, telegramSettings }) {
  const [inventoryTab, setInventoryTab] = useState('inventory'); // 'inventory' | 'ai-forecast'
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'lowStock', 'expiring'

  const [isBulkTranslationModalOpen, setIsBulkTranslationModalOpen] = useState(false);
  const [isBulkTranslating, setIsBulkTranslating] = useState(false);
  const [bulkTranslationProgress, setBulkTranslationProgress] = useState(0);
  const [bulkTranslationStatusMsg, setBulkTranslationStatusMsg] = useState('');

  const handleExecuteBulkTranslation = async () => {
    setIsBulkTranslating(true);
    setBulkTranslationProgress(0);
    setBulkTranslationStatusMsg("جاري تحليل أسماء الأدوية وبدء الترجمة...");

    try {
      const shopId = user?.uid;
      if (!shopId) throw new Error("حساب الصيدلية غير متاح");

      const itemsToTranslate = [...inventory];
      const batchSize = 100;
      let totalTranslated = 0;

      for (let i = 0; i < itemsToTranslate.length; i += batchSize) {
        const batch = writeBatch(db);
        const chunk = itemsToTranslate.slice(i, i + batchSize);

        const translations = await Promise.all(chunk.map(async (item) => {
          const oldName = item.tradeName || '';
          const existingSecondary = item.tradeNameSecondary || '';
          if (!oldName.trim() || existingSecondary.trim()) {
            return { item, newName: existingSecondary, skip: true };
          }
          const newName = await translateProductText(oldName);
          return { item, newName, skip: false };
        }));

        translations.forEach(({ item, newName, skip }) => {
          if (!skip && newName && newName !== item.tradeNameSecondary) {
            const itemDocRef = doc(db, 'users', shopId, 'inventory', item.id);
            batch.update(itemDocRef, {
              tradeNameSecondary: newName,
              updatedAt: new Date().toISOString()
            });
            const idx = itemsToTranslate.findIndex(p => p.id === item.id);
            if (idx !== -1) {
              itemsToTranslate[idx] = { ...itemsToTranslate[idx], tradeNameSecondary: newName };
            }
          }
          totalTranslated++;
        });

        await batch.commit();
        const progress = Math.min(100, Math.round((totalTranslated / itemsToTranslate.length) * 100));
        setBulkTranslationProgress(progress);
        setBulkTranslationStatusMsg(`تم ترجمة ${totalTranslated} من إجمالي ${itemsToTranslate.length} صنف (${progress}%)...`);
      }

      setInventory(itemsToTranslate);
      window.triggerDrMasrToast(`🎉 تم ترجمة جميع الأصناف بنجاح! يرجى مراجعتها يدوياً.`);
      setIsBulkTranslationModalOpen(false);
    } catch (err) {
      console.error(err);
      window.triggerDrMasrToast("❌ حدث خطأ أثناء الترجمة: " + err.message);
    } finally {
      setIsBulkTranslating(false);
      setBulkTranslationProgress(0);
    }
  };

  // Excel/CSV Importer States
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importHeaders, setImportHeaders] = useState([]);
  const [importRows, setImportRows] = useState([]);
  const [duplicateImportItems, setDuplicateImportItems] = useState([]);
  const [newImportItems, setNewImportItems] = useState([]);
  const [showDuplicateImportDialog, setShowDuplicateImportDialog] = useState(false);
  const [importMapping, setImportMapping] = useState({
    tradeName: '',
    genericName: '',
    category: '',
    barcode: '',
    quantity: '',
    buyingPrice: '',
    sellingPrice: '',
    expiryDate: ''
  });
  const [importProgress, setImportProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatusMsg, setImportStatusMsg] = useState('');

  const printInventoryReport = () => {
    const pharmacyName = currentUserData?.pharmacyName || 'صيدلية دكتور مصر';
    const shortId = currentUserData?.shortId || '';
    const printDate = new Date().toLocaleString('ar-EG');
    const now = new Date();

    // Filter: Only expired/expiring or low stock (qty <= 5)
    const filteredInv = inventory.filter(item => {
      const qty = parseInt(item.quantity || 0);
      const isLow = qty <= 5;
      
      let isExpiredOrExpiring = false;
      if (item.expiryDate) {
        const exp = new Date(item.expiryDate);
        const diffMs = exp - now;
        const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30);
        isExpiredOrExpiring = diffMonths <= 6; // expired or expiring in <= 6 months
      }
      return isLow || isExpiredOrExpiring;
    });

    const totalItems = filteredInv.length;
    const lowStockItemsCount = filteredInv.filter(i => (i.quantity || 0) <= 5).length;
    const expiringItemsCount = filteredInv.filter(i => {
      if (!i.expiryDate) return false;
      const exp = new Date(i.expiryDate);
      const diffMs = exp - now;
      const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30);
      return diffMonths <= 6;
    }).length;

    const sortedInv = [...filteredInv].sort((a, b) => (a.tradeName || '').localeCompare(b.tradeName || '', 'ar'));

    let itemsRows = '';
    sortedInv.forEach((item, i) => {
      const qty = parseInt(item.quantity || 0);
      const exp = item.expiryDate ? new Date(item.expiryDate) : null;
      const diffMonths = exp ? (exp - now) / (1000 * 60 * 60 * 24 * 30) : null;
      const isExpired = diffMonths !== null && diffMonths < 0;
      const isExpiringSoon = diffMonths !== null && diffMonths <= 6;
      const isLow = qty <= 5;

      let statusText = '✅ جيد';
      let statusColor = '#059669';
      if (isExpired) { statusText = '❌ منتهي'; statusColor = '#dc2626'; }
      else if (isExpiringSoon && isLow) { statusText = '⚠️ حرج'; statusColor = '#dc2626'; }
      else if (isExpiringSoon) { statusText = '⚠️ صلاحية قريبة'; statusColor = '#d97706'; }
      else if (isLow) { statusText = '⚠️ مخزون منخفض'; statusColor = '#d97706'; }

      const rowBg = isExpired ? '#fff5f5' : isExpiringSoon ? '#fffbeb' : isLow ? '#fff8f0' : (i % 2 === 0 ? '#fff' : '#f8fafc');

      itemsRows += `<tr style="background:${rowBg};">
        <td>${i + 1}</td>
        <td style="text-align:right; font-weight:700;">${item.tradeName || '—'}</td>
        <td style="text-align:right; color:#475569; font-size:11px;">${item.genericName || '—'}</td>
        <td style="text-align:center;">${item.category || '—'}</td>
        <td style="text-align:center; font-weight:800; color:${qty <= 0 ? '#dc2626' : qty <= 5 ? '#d97706' : '#059669'}">${qty}</td>
        <td style="text-align:center;">${parseFloat(item.buyingPrice || 0).toFixed(2)} ج.م</td>
        <td style="text-align:center; font-weight:700;">${parseFloat(item.sellingPrice || 0).toFixed(2)} ج.م</td>
        <td style="text-align:center;">${item.expiryDate || '—'}</td>
        <td style="text-align:center; font-weight:700; color:${statusColor};">${statusText}</td>
      </tr>`;
    });

    const html = `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
<meta charset="UTF-8">
<title>تقرير النواقص والصلاحيات - ${pharmacyName}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;900&display=swap');
  @page { size: A4 landscape; margin: 10mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Cairo', sans-serif; background: #f8fafc; color: #1e293b; direction: rtl; }
  .page { background: #fff; padding: 16px; }
  .header { text-align: center; border-bottom: 3px solid #6366f1; padding-bottom: 14px; margin-bottom: 16px; }
  .header h1 { font-size: 22px; font-weight: 900; color: #1e293b; }
  .header .pharmacy { font-size: 16px; font-weight: 700; color: #374151; margin-top: 4px; }
  .header .meta { font-size: 11px; color: #64748b; margin-top: 4px; }
  .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 16px; }
  .sum-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; text-align: center; }
  .sum-card .lbl { font-size: 10px; color: #64748b; font-weight: 600; }
  .sum-card .val { font-size: 18px; font-weight: 900; color: #1e293b; }
  .sum-card.green .val { color: #059669; }
  .sum-card.red .val { color: #dc2626; }
  .sum-card.orange .val { color: #d97706; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  thead tr { background: #6366f1; color: #fff; }
  thead th { padding: 8px 6px; font-weight: 700; text-align: center; }
  tbody tr { border-bottom: 1px solid #e2e8f0; }
  tbody td { padding: 6px; }
  .footer { text-align: center; color: #94a3b8; font-size: 10px; border-top: 1px solid #e2e8f0; padding-top: 10px; margin-top: 16px; }
  @media print { .no-print { display: none !important; } body { background: #fff; } }
  .print-btn { position: fixed; top: 14px; left: 14px; background: #6366f1; color: #fff; border: none; padding: 8px 20px; border-radius: 8px; font-family: Cairo, sans-serif; font-size: 13px; font-weight: 700; cursor: pointer; z-index: 999; }
</style>
</head>
<body>
<button class="print-btn no-print" onclick="window.print()">🖨️ طباعة</button>
<div class="page">
  <div class="header">
    <h1>📋 تقرير النواقص وصلاحية الأدوية القريبة</h1>
    <div class="pharmacy">${pharmacyName}${shortId ? ' — ' + shortId : ''}</div>
    <div class="meta">تاريخ طباعة التقرير: ${printDate}</div>
  </div>
  <div class="summary">
    <div class="sum-card"><div class="lbl">إجمالي الأصناف المشمولة</div><div class="val">${totalItems}</div></div>
    <div class="sum-card red"><div class="lbl">أصناف النواقص (كمية ≤ 5)</div><div class="val">${lowStockItemsCount}</div></div>
    <div class="sum-card orange"><div class="lbl">صلاحيات قريبة (≤ 6 شهور)</div><div class="val">${expiringItemsCount}</div></div>
  </div>
  <table>
    <thead><tr>
      <th>#</th><th>الاسم التجاري</th><th>الاسم العلمي</th><th>التصنيف</th><th>الكمية</th><th>سعر الشراء</th><th>سعر البيع</th><th>تاريخ الصلاحية</th><th>الحالة</th>
    </tr></thead>
    <tbody>${itemsRows}</tbody>
  </table>
  <div class="footer">نظام دكتور مصر — تقرير النواقص والصلاحيات | ${printDate}</div>
</div>
<script>window.onload = function(){ window.print(); }<\/script>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  const loadSheetJS = () => {
    return new Promise((resolve) => {
      if (window.XLSX) {
        resolve(window.XLSX);
        return;
      }
      const script = document.createElement('script');
      script.src = "https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";
      script.onload = () => resolve(window.XLSX);
      document.head.appendChild(script);
    });
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setIsImporting(true);
    setImportStatusMsg("جاري تحليل ملف البيانات...");
    
    try {
      const reader = new FileReader();
      if (file.name.endsWith('.csv')) {
        reader.onload = (evt) => {
          const text = evt.target.result;
          const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
          if (lines.length === 0) {
            window.triggerDrMasrToast("⚠️ الملف المرفوع فارغ!");
            setIsImporting(false);
            return;
          }
          const headers = lines[0].split(',').map(h => h.replace(/^["']|["']$/g, '').trim());
          const rows = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.replace(/^["']|["']$/g, '').trim());
            const obj = {};
            headers.forEach((h, i) => {
              obj[h] = values[i] || '';
            });
            return obj;
          });
          setImportHeaders(headers);
          setImportRows(rows);
          const autoMap = {
            tradeName: '',
            genericName: '',
            category: '',
            barcode: '',
            quantity: '',
            buyingPrice: '',
            sellingPrice: '',
            expiryDate: ''
          };
          headers.forEach(h => {
            const hl = h.toLowerCase();
            if (hl.includes('name') || hl.includes('تجاري') || hl.includes('الاسم') || hl.includes('صنف') || hl.includes('المنتج')) autoMap.tradeName = h;
            if (hl.includes('generic') || hl.includes('علمي')) autoMap.genericName = h;
            if (hl.includes('category') || hl.includes('تصنيف') || hl.includes('فئة')) autoMap.category = h;
            if (hl.includes('barcode') || hl.includes('باركود') || hl.includes('كود')) autoMap.barcode = h;
            if (hl.includes('qty') || hl.includes('quantity') || hl.includes('الكمية') || hl.includes('عدد')) autoMap.quantity = h;
            if (hl.includes('buy') || hl.includes('شراء') || hl.includes('جملة')) autoMap.buyingPrice = h;
            if (hl.includes('sell') || hl.includes('بيع') || hl.includes('جمهور') || hl.includes('سعر')) autoMap.sellingPrice = h;
            if (hl.includes('expiry') || hl.includes('صلاحية') || hl.includes('تاريخ')) autoMap.expiryDate = h;
          });
          setImportMapping(autoMap);
          setIsImporting(false);
          setImportStatusMsg("");
        };
        reader.readAsText(file, 'UTF-8');
      } else {
        const XLSX = await loadSheetJS();
        reader.onload = (evt) => {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          // ── Smart header detection ──────────────────────────────────────
          // First try the default (headers in row 1)
          let jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

          // Check if the headers look like EMPTY__ / __EMPTY (SheetJS placeholders)
          // If so, scan raw rows to find the first row that contains real text headers
          const firstRowKeys = jsonData.length > 0 ? Object.keys(jsonData[0]) : [];
          const hasEmptyHeaders = firstRowKeys.some(k => /^(__EMPTY|EMPTY)/i.test(k));

          if (hasEmptyHeaders || jsonData.length === 0) {
            // Read all rows as raw arrays (no header inference)
            const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
            // Find the first row where most cells contain non-empty text (likely the header row)
            let headerRowIdx = -1;
            for (let ri = 0; ri < rawRows.length; ri++) {
              const row = rawRows[ri];
              const nonEmptyCells = row.filter(c => c !== null && c !== undefined && String(c).trim() !== '');
              if (nonEmptyCells.length >= 2) { // at least 2 filled cells = likely a header
                headerRowIdx = ri;
                break;
              }
            }

            if (headerRowIdx === -1) {
              window.triggerDrMasrToast("⚠️ ملف إكسل فارغ أو لا يحتوي على صفوف بيانات!");
              setIsImporting(false);
              return;
            }

            // Re-parse using detected header row
            jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: rawRows[headerRowIdx].map((c, i) => (c !== null && c !== undefined && String(c).trim() !== '' ? String(c).trim() : `Col_${i}`)),
              range: headerRowIdx,
              defval: ''
            });
            // Remove the header row itself (SheetJS includes it as first data row when header is explicit)
            if (jsonData.length > 0) jsonData = jsonData.slice(1);
            // Remove completely empty rows
            jsonData = jsonData.filter(row => Object.values(row).some(v => v !== null && v !== undefined && String(v).trim() !== ''));

            if (headerRowIdx > 0) {
              window.triggerDrMasrToast(`✅ تم اكتشاف الأعمدة تلقائياً من الصف ${headerRowIdx + 1}`);
            }
          }

          if (jsonData.length === 0) {
            window.triggerDrMasrToast("⚠️ ملف إكسل فارغ أو لا يحتوي على صفوف بيانات!");
            setIsImporting(false);
            return;
          }
          const headers = Object.keys(jsonData[0]);
          setImportHeaders(headers);
          setImportRows(jsonData);
          const autoMap = {
            tradeName: '',
            genericName: '',
            category: '',
            barcode: '',
            quantity: '',
            buyingPrice: '',
            sellingPrice: '',
            expiryDate: ''
          };
          headers.forEach(h => {
            const hl = h.toLowerCase();
            if (hl.includes('name') || hl.includes('تجاري') || hl.includes('الاسم') || hl.includes('صنف') || hl.includes('المنتج')) autoMap.tradeName = h;
            if (hl.includes('generic') || hl.includes('علمي')) autoMap.genericName = h;
            if (hl.includes('category') || hl.includes('تصنيف') || hl.includes('فئة')) autoMap.category = h;
            if (hl.includes('barcode') || hl.includes('باركود') || hl.includes('كود')) autoMap.barcode = h;
            if (hl.includes('qty') || hl.includes('quantity') || hl.includes('الكمية') || hl.includes('عدد')) autoMap.quantity = h;
            if (hl.includes('buy') || hl.includes('شراء') || hl.includes('جملة')) autoMap.buyingPrice = h;
            if (hl.includes('sell') || hl.includes('بيع') || hl.includes('جمهور') || hl.includes('سعر')) autoMap.sellingPrice = h;
            if (hl.includes('expiry') || hl.includes('صلاحية') || hl.includes('تاريخ')) autoMap.expiryDate = h;
          });
          setImportMapping(autoMap);
          setIsImporting(false);
          setImportStatusMsg("");
        };
        reader.readAsArrayBuffer(file);
      }
    } catch (err) {
      console.error(err);
      window.triggerDrMasrToast("❌ فشل قراءة الملف، يرجى التأكد من الصيغة.");
      setIsImporting(false);
    }
  };

  const handleStartImportAnalysis = () => {
    if (!importFile || importRows.length === 0) return;
    if (!importMapping.tradeName) {
      window.triggerDrMasrToast("⚠️ يجب تحديد عمود الاسم التجاري على الأقل لاستيراد البيانات!");
      return;
    }

    const newItems = [];
    const duplicateItems = [];

    importRows.forEach(row => {
      const tName = row[importMapping.tradeName];
      if (!tName || !tName.toString().trim()) return;

      const bcode = importMapping.barcode ? (row[importMapping.barcode] || '') : '';
      let rawBarcode = bcode.toString().trim().replace(/\D/g, '');
      if (rawBarcode.length > 13) rawBarcode = rawBarcode.slice(0, 13);

      const qty = importMapping.quantity ? parseArabicAndEnglishNumber(row[importMapping.quantity]) : 0;

      const existingItem = rawBarcode ? inventory.find(item => (item.barcode || '').trim() === rawBarcode) : null;
      if (existingItem) {
        duplicateItems.push({
          row,
          existingItem,
          qty
        });
      } else {
        newItems.push(row);
      }
    });

    if (duplicateItems.length > 0) {
      setNewImportItems(newItems);
      setDuplicateImportItems(duplicateItems);
      setShowDuplicateImportDialog(true);
    } else {
      executeImportExcel(importRows, []);
    }
  };

  const executeImportExcel = async (rowsToCreate, itemsToIncrement = []) => {
    setIsImporting(true);
    setImportProgress(0);
    setImportStatusMsg("جاري تجهيز وبدء استيراد البيانات...");
    
    try {
      const shopId = user?.uid;
      if (!shopId) throw new Error("مستشعر حساب الصيدلية غير متاح");
      
      const batchSize = 100;
      let totalImported = 0;
      let totalIncremented = 0;
      const totalOverall = rowsToCreate.length + itemsToIncrement.length;

      // 1. Create new items
      for (let i = 0; i < rowsToCreate.length; i += batchSize) {
        const inventoryBatch = writeBatch(db);
        const globalBatch = writeBatch(db);
        let globalBatchHasWrites = false;
        const chunk = rowsToCreate.slice(i, i + batchSize);

        chunk.forEach(row => {
          const tName = row[importMapping.tradeName];
          if (!tName || !tName.toString().trim()) return;

          const gName = importMapping.genericName ? (row[importMapping.genericName] || '') : '';
          const cat = importMapping.category ? (row[importMapping.category] || 'عام') : 'عام';
          const bcode = importMapping.barcode ? (row[importMapping.barcode] || '') : '';
          const qty = importMapping.quantity ? parseArabicAndEnglishNumber(row[importMapping.quantity]) : 0;
          const buyPrice = importMapping.buyingPrice ? parseArabicAndEnglishNumber(row[importMapping.buyingPrice]) : 0;
          const sellPrice = importMapping.sellingPrice ? parseArabicAndEnglishNumber(row[importMapping.sellingPrice]) : 0;
          const expDate = importMapping.expiryDate ? (row[importMapping.expiryDate] || '') : '';

          let rawBarcode = bcode.toString().trim().replace(/\D/g, '');
          if (rawBarcode.length > 13) {
            rawBarcode = rawBarcode.slice(0, 13);
          }
          const finalBarcode = rawBarcode || ('20' + Math.floor(1000000000 + Math.random() * 9000000000).toString());

          const itemDocRef = doc(collection(db, 'users', shopId, 'inventory'));
          inventoryBatch.set(itemDocRef, {
            id: itemDocRef.id,
            tradeName: tName.toString().trim(),
            genericName: gName.toString().trim(),
            category: cat.toString().trim(),
            barcode: finalBarcode,
            quantity: qty,
            buyingPrice: buyPrice,
            sellingPrice: sellPrice,
            expiryDate: expDate.toString().trim(),
            subUnits: [],
            createdAt: new Date().toISOString()
          });
          totalImported++;

          const isAutoGen = /^20\d{10}$/.test(finalBarcode);
          const isRealBarcode = finalBarcode.length >= 8 && !isAutoGen;
          if (isRealBarcode) {
            const globalRef = doc(db, 'global_medicines', finalBarcode);
            const globalBarcodeRef = doc(db, 'global_barcodes', finalBarcode);
            globalBatch.set(globalRef, {
              barcode: finalBarcode,
              tradeName: tName.toString().trim(),
              genericName: gName.toString().trim(),
              category: cat.toString().trim(),
              sellingPrice: Number(sellPrice) || 0,
              buyingPrice: Number(buyPrice) || 0,
              updatedAt: new Date().toISOString()
            }, { merge: true });

            globalBatch.set(globalBarcodeRef, {
              barcode: finalBarcode,
              tradeName: tName.toString().trim(),
              genericName: gName.toString().trim(),
              category: cat.toString().trim(),
              sellingPrice: Number(sellPrice) || 0,
              buyingPrice: Number(buyPrice) || 0,
              updatedAt: new Date().toISOString()
            }, { merge: true });

            globalBatchHasWrites = true;
          }
        });

        await inventoryBatch.commit();
        if (globalBatchHasWrites) {
          await globalBatch.commit();
        }

        await new Promise(resolve => setTimeout(resolve, 150));

        const progress = Math.min(100, Math.round(((i + chunk.length) / totalOverall) * 100));
        setImportProgress(progress);
        setImportStatusMsg(`تم استيراد ${totalImported} من إجمالي ${totalOverall} صنف (${progress}%)...`);
      }

      // 2. Increment existing items (if approved)
      for (let i = 0; i < itemsToIncrement.length; i += batchSize) {
        const inventoryBatch = writeBatch(db);
        const chunk = itemsToIncrement.slice(i, i + batchSize);

        chunk.forEach(item => {
          const itemDocRef = doc(db, 'users', shopId, 'inventory', item.existingItem.id);
          const newQty = Number(item.existingItem.quantity || 0) + Number(item.qty);
          inventoryBatch.update(itemDocRef, {
            quantity: newQty,
            updatedAt: new Date().toISOString()
          });
          totalIncremented++;
        });

        await inventoryBatch.commit();

        await new Promise(resolve => setTimeout(resolve, 150));

        const progress = Math.min(100, Math.round(((rowsToCreate.length + i + chunk.length) / totalOverall) * 100));
        setImportProgress(progress);
        setImportStatusMsg(`تم زيادة كمية ${totalIncremented} صنف مكرر (${progress}%)...`);
      }

      let toastMsg = `🎉 تم استيراد ${totalImported} صنف جديد بنجاح!`;
      if (totalIncremented > 0) {
        toastMsg += ` وتم زيادة كمية ${totalIncremented} صنف مسجل مسبقاً.`;
      }
      window.triggerDrMasrToast(toastMsg);
      setIsImportModalOpen(false);
      setImportFile(null);
      setImportRows([]);
      setImportHeaders([]);
      setShowDuplicateImportDialog(false);
      
    } catch (err) {
      console.error(err);
      window.triggerDrMasrToast("❌ حدث خطأ أثناء الاستيراد: " + err.message);
    } finally {
      setIsImporting(false);
      setImportProgress(0);
    }
  };
  
  // Barcode Printing States
  const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
  const [selectedBarcodeItem, setSelectedBarcodeItem] = useState(null);
  const [labelWidth, setLabelWidth] = useState(38); // mm
  const [labelHeight, setLabelHeight] = useState(25); // mm
  const [showPriceOnLabel, setShowPriceOnLabel] = useState(true);
  const [showPharmacyName, setShowPharmacyName] = useState(true);
  const [labelCopies, setLabelCopies] = useState(1);
  const [customBarcodeVal, setCustomBarcodeVal] = useState('');

  // Barcode live preview generator
  useEffect(() => {
    if (isBarcodeModalOpen && selectedBarcodeItem) {
      const barcodeValue = customBarcodeVal || selectedBarcodeItem.barcode || '123456789012';
      const container = document.getElementById("preview-barcode-svg");
      if (container) {
        try {
          // Clear previous contents first
          container.innerHTML = "";

          JsBarcode(container, barcodeValue, {
            format: "CODE128",
            width: 1.25,
            height: 35,
            displayValue: true,
            fontSize: 9,
            margin: 2
          });
        } catch (err) {
          console.error("Barcode preview generation error:", err);
          container.innerHTML = `<text x="50%" y="50%" text-anchor="middle" fill="#f87171" font-size="10">⚠️ باركود غير صالح</text>`;
        }
      }
    }
  }, [isBarcodeModalOpen, selectedBarcodeItem, customBarcodeVal]);

  const handlePrintBarcodeLabel = () => {
    if (!selectedBarcodeItem) return;
    const pharmacyName = currentUserData?.pharmacyName || 'صيدلية دكتور مصر';
    const barcodeValue = customBarcodeVal || selectedBarcodeItem.barcode || '123456789012';
    
    const printWindow = window.open('', '_blank', 'width=600,height=500');
    if (!printWindow) {
      window.triggerDrMasrToast("⚠️ يرجى السماح بالنوافذ المنبثقة (Popups) لتتمكن من طباعة الباركود!");
      return;
    }
    
    // Pre-generate barcode SVG locally to support fully offline printing without external CDNs
    let barcodeSVGHTML = '';
    try {
      const tempSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      JsBarcode(tempSvg, barcodeValue, {
        format: "CODE128",
        width: 1.3,
        height: 32,
        displayValue: true,
        fontSize: 7.5,
        margin: 1
      });
      tempSvg.setAttribute("class", "barcode-svg");
      barcodeSVGHTML = tempSvg.outerHTML;
    } catch (e) {
      console.error("Barcode generation error in offline printing:", e);
      barcodeSVGHTML = `<svg class="barcode-svg"><text x="50%" y="50%" text-anchor="middle" fill="#000" font-size="10">⚠️ باركود غير صالح</text></svg>`;
    }

    let labelsHTML = '';
    for (let i = 0; i < labelCopies; i++) {
      labelsHTML += `
        <div class="label-container">
          ${showPharmacyName ? `<div class="pharmacy-name">${pharmacyName}</div>` : ''}
          <div class="product-name">${selectedBarcodeItem.tradeName}</div>
          ${barcodeSVGHTML}
          ${showPriceOnLabel ? `<div class="product-price">${selectedBarcodeItem.sellingPrice} ج.م</div>` : ''}
        </div>
      `;
    }

    printWindow.document.write(
      '<html>' +
      '<head>' +
      '  <title>طباعة الملصقات - ' + selectedBarcodeItem.tradeName + '</title>' +
      '  <style>' +
      '    @import url("https://fonts.googleapis.com/css2?family=Cairo:wght@400;700;800&display=swap");' +
      '    @page {' +
      '      size: ' + labelWidth + 'mm ' + labelHeight + 'mm;' +
      '      margin: 0;' +
      '    }' +
      '    body {' +
      '      margin: 0;' +
      '      padding: 0;' +
      '      background: #fff;' +
      '      color: #000;' +
      '      font-family: "Cairo", sans-serif;' +
      '      direction: rtl;' +
      '      text-align: center;' +
      '      -webkit-print-color-adjust: exact;' +
      '    }' +
      '    .label-container {' +
      '      width: ' + labelWidth + 'mm;' +
      '      height: ' + labelHeight + 'mm;' +
      '      box-sizing: border-box;' +
      '      padding: 1.5mm;' +
      '      display: flex;' +
      '      flex-direction: column;' +
      '      justify-content: space-between;' +
      '      align-items: center;' +
      '      page-break-after: always;' +
      '      overflow: hidden;' +
      '    }' +
      '    .label-container:last-child {' +
      '      page-break-after: avoid;' +
      '      margin-bottom: 0;' +
      '    }' +
      '    .pharmacy-name {' +
      '      font-size: 7.5pt;' +
      '      font-weight: 800;' +
      '      color: #000;' +
      '      margin: 0;' +
      '      white-space: nowrap;' +
      '      overflow: hidden;' +
      '      text-overflow: ellipsis;' +
      '      width: 100%;' +
      '      line-height: 1.2;' +
      '    }' +
      '    .product-name {' +
      '      font-size: 6.5pt;' +
      '      font-weight: 700;' +
      '      color: #333;' +
      '      margin: 1px 0;' +
      '      white-space: nowrap;' +
      '      overflow: hidden;' +
      '      text-overflow: ellipsis;' +
      '      width: 100%;' +
      '      line-height: 1.1;' +
      '    }' +
      '    .barcode-svg {' +
      '      width: 92%;' +
      '      height: calc(' + labelHeight + 'mm - 12mm);' +
      '      max-height: 14mm;' +
      '    }' +
      '    .product-price {' +
      '      font-size: 7.5pt;' +
      '      font-weight: 800;' +
      '      color: #000;' +
      '      margin: 0;' +
      '      border-top: 0.5px dashed #ccc;' +
      '      padding-top: 1px;' +
      '      width: 100%;' +
      '      line-height: 1.2;' +
      '    }' +
      '  </style>' +
      '</head>' +
      '<body>' +
      '  ' + labelsHTML +
      '  <script>' +
      '    window.onload = function() {' +
      '      setTimeout(function() {' +
      '        window.print();' +
      '        window.close();' +
      '      }, 250);' +
      '    };' +
      '  <' + '/script>' +
      '</body>' +
      '</html>'
    );
    printWindow.document.close();
  };



  const searchLower = (inventorySearch || '').toLowerCase();
  
  // Expiry calculation helper for badges
  const getExpiryMonths = (expiry) => {
    if (!expiry) return null;
    const now = new Date();
    const date = new Date(expiry);
    return (date.getFullYear() - now.getFullYear()) * 12 + date.getMonth() - now.getMonth();
  };

  // Filter by search terms and active filter card selection
  const filteredInventory = inventory.filter(item => {
    // 1. Search term match
    const matchSearch = (item.tradeName && item.tradeName.toLowerCase().includes(searchLower)) ||
      (item.genericName && item.genericName.toLowerCase().includes(searchLower)) ||
      (item.barcode && item.barcode.includes(searchLower));
      
    if (!matchSearch) return false;

    // 2. Active filter match
    if (activeFilter === 'lowStock') {
      return item.quantity <= 5;
    }
    if (activeFilter === 'expiring') {
      const diffMonth = getExpiryMonths(item.expiryDate);
      return diffMonth !== null && diffMonth <= 6;
    }
    if (activeFilter === 'fridge') {
      return !!item.requiresFridge;
    }
    return true;
  });

  const totalInvPages = Math.ceil(filteredInventory.length / 10) || 1;
  const sortedFilteredInventory = [...filteredInventory].reverse();
                  const paginatedInventory = sortedFilteredInventory.slice((inventoryPage - 1) * 10, inventoryPage * 10);

  // Counts for the summary cards
  const lowStockCount = inventory.filter(item => item.quantity <= 5).length;
  const expiringCount = inventory.filter(item => {
    const diffMonth = getExpiryMonths(item.expiryDate);
    return diffMonth !== null && diffMonth <= 6;
  }).length;
  const fridgeCount = inventory.filter(item => !!item.requiresFridge).length;

  const printShortagesReport = () => {
    const pharmacyName = currentUserData?.pharmacyName || 'صيدلية دكتور مصر';
    const shortId = currentUserData?.shortId || '';
    const printDate = new Date().toLocaleString('ar-EG');
    const now = new Date();

    // Low stock: quantity <= 5
    const lowStockItems = inventory
      .filter(i => (i.quantity || 0) <= 5)
      .sort((a, b) => (a.quantity || 0) - (b.quantity || 0));

    // Expiring soon: within 6 months (but not already in low stock to avoid duplication—show all)
    const expiringItems = inventory
      .filter(i => {
        if (!i.expiryDate) return false;
        const exp = new Date(i.expiryDate);
        const diffMonths = (exp - now) / (1000 * 60 * 60 * 24 * 30);
        return diffMonths <= 6;
      })
      .sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate));

    const lowStockRows = lowStockItems.map((item, i) => {
      const qty = parseInt(item.quantity || 0);
      const isEmpty = qty <= 0;
      return `<tr style="background:${isEmpty ? '#fff5f5' : i % 2 === 0 ? '#fff' : '#f8fafc'}">
        <td>${i + 1}</td>
        <td style="text-align:right;font-weight:700;">${item.tradeName || '—'}</td>
        <td style="text-align:right;color:#475569;font-size:11px;">${item.genericName || '—'}</td>
        <td style="text-align:center;">${item.category || '—'}</td>
        <td style="text-align:center;font-weight:900;font-size:15px;color:${isEmpty ? '#dc2626' : '#d97706'}">${qty}</td>
        <td style="text-align:center;">${parseFloat(item.sellingPrice || 0).toFixed(2)} ج.م</td>
        <td style="text-align:center;font-weight:700;color:${isEmpty ? '#dc2626' : '#d97706'}">${isEmpty ? '❌ نافد' : '⚠️ منخفض'}</td>
      </tr>`;
    }).join('');

    const expiringRows = expiringItems.map((item, i) => {
      const exp = new Date(item.expiryDate);
      const diffMonths = Math.round((exp - now) / (1000 * 60 * 60 * 24 * 30));
      const isExpired = diffMonths < 0;
      const isCritical = diffMonths <= 1;
      let statusText = isExpired ? '❌ منتهي الصلاحية' : isCritical ? `⚠️ شهر واحد متبقي` : `⚠️ ${diffMonths} أشهر متبقية`;
      let statusColor = isExpired ? '#dc2626' : isCritical ? '#dc2626' : '#d97706';
      return `<tr style="background:${isExpired ? '#fff5f5' : isCritical ? '#fff5f5' : i % 2 === 0 ? '#fff' : '#fffbeb'}">
        <td>${i + 1}</td>
        <td style="text-align:right;font-weight:700;">${item.tradeName || '—'}</td>
        <td style="text-align:right;color:#475569;font-size:11px;">${item.genericName || '—'}</td>
        <td style="text-align:center;">${item.category || '—'}</td>
        <td style="text-align:center;font-weight:800;color:#374151;">${parseInt(item.quantity || 0)}</td>
        <td style="text-align:center;font-weight:800;color:${statusColor};">${item.expiryDate}</td>
        <td style="text-align:center;font-weight:700;color:${statusColor};">${statusText}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
<meta charset="UTF-8">
<title>تقرير النواقص والصلاحيات - ${pharmacyName}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;900&display=swap');
  @page { size: A4 portrait; margin: 12mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Cairo', sans-serif; background: #fff; color: #1e293b; direction: rtl; }
  .header { text-align: center; padding-bottom: 14px; margin-bottom: 18px; border-bottom: 3px solid #ef4444; }
  .header h1 { font-size: 20px; font-weight: 900; color: #1e293b; }
  .header .pharmacy { font-size: 15px; font-weight: 700; color: #374151; margin-top: 4px; }
  .header .meta { font-size: 11px; color: #64748b; margin-top: 3px; }
  .summary { display: flex; gap: 12px; margin-bottom: 20px; }
  .sum-card { flex: 1; border-radius: 10px; padding: 12px; text-align: center; }
  .sum-card .lbl { font-size: 10px; color: #64748b; font-weight: 600; }
  .sum-card .val { font-size: 22px; font-weight: 900; margin-top: 2px; }
  .sum-card.red { background: #fef2f2; border: 1px solid #fecaca; }
  .sum-card.red .val { color: #dc2626; }
  .sum-card.orange { background: #fffbeb; border: 1px solid #fde68a; }
  .sum-card.orange .val { color: #d97706; }
  .section-title { font-size: 14px; font-weight: 800; color: #1e293b; margin-bottom: 10px; padding: 8px 12px; border-radius: 8px; display: flex; align-items: center; gap: 8px; }
  .section-title.red { background: #fef2f2; color: #dc2626; border-right: 4px solid #ef4444; }
  .section-title.orange { background: #fffbeb; color: #d97706; border-right: 4px solid #f59e0b; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 24px; }
  thead tr { background: #1e293b; color: #fff; }
  thead th { padding: 7px 6px; font-weight: 700; text-align: center; }
  tbody tr { border-bottom: 1px solid #e2e8f0; }
  tbody td { padding: 6px; }
  .empty-state { text-align: center; padding: 20px; color: #059669; font-weight: 700; font-size: 13px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; margin-bottom: 20px; }
  .footer { text-align: center; color: #94a3b8; font-size: 10px; border-top: 1px solid #e2e8f0; padding-top: 10px; margin-top: 10px; }
  @media print { .no-print { display: none !important; } }
  .print-btn { position: fixed; top: 14px; left: 14px; background: #ef4444; color: #fff; border: none; padding: 8px 20px; border-radius: 8px; font-family: Cairo, sans-serif; font-size: 13px; font-weight: 700; cursor: pointer; z-index: 999; }
</style>
</head>
<body>
<button class="print-btn no-print" onclick="window.print()">🖨️ طباعة</button>
<div class="header">
  <h1>🚨 تقرير النواقص والصلاحيات القريبة</h1>
  <div class="pharmacy">${pharmacyName}${shortId ? ' — ' + shortId : ''}</div>
  <div class="meta">تاريخ الطباعة: ${printDate}</div>
</div>

<div class="summary">
  <div class="sum-card red">
    <div class="lbl">أصناف نافدة / منخفضة</div>
    <div class="val">${lowStockItems.length}</div>
  </div>
  <div class="sum-card orange">
    <div class="lbl">صلاحيات قريبة (≤ 6 أشهر)</div>
    <div class="val">${expiringItems.length}</div>
  </div>
</div>

<div class="section-title red">🚨 النواقص — أصناف الكمية ≤ 5 وحدات (${lowStockItems.length} صنف)</div>
${lowStockItems.length > 0 ? `
<table>
  <thead><tr>
    <th>#</th><th>الاسم التجاري</th><th>الاسم العلمي</th><th>التصنيف</th><th>الكمية المتبقية</th><th>سعر البيع</th><th>الحالة</th>
  </tr></thead>
  <tbody>${lowStockRows}</tbody>
</table>` : '<div class="empty-state">✅ لا توجد نواقص — جميع الأصناف بمخزون كافٍ</div>'}

<div class="section-title orange">📅 الصلاحيات القريبة — ستنتهي خلال 6 أشهر أو أقل (${expiringItems.length} صنف)</div>
${expiringItems.length > 0 ? `
<table>
  <thead><tr>
    <th>#</th><th>الاسم التجاري</th><th>الاسم العلمي</th><th>التصنيف</th><th>الكمية</th><th>تاريخ الصلاحية</th><th>الحالة</th>
  </tr></thead>
  <tbody>${expiringRows}</tbody>
</table>` : '<div class="empty-state">✅ لا توجد صلاحيات قريبة الانتهاء</div>'}

<div class="footer">نظام دكتور مصر — تقرير النواقص والصلاحيات | ${printDate}</div>
<script>window.onload = function(){ window.print(); }<\/script>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };

  return (
    <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
      {/* Internal Tab Switcher */}
      <div style={{ display: 'flex', gap: '10px', borderBottom: '1px solid var(--border)', paddingBottom: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setInventoryTab('inventory')}
          className={`delivery-tab-btn ${inventoryTab === 'inventory' ? 'active' : ''}`}
          style={{ padding: '10px 24px', fontSize: '0.92rem', fontWeight: 'bold', borderRadius: '12px', border: 'none', cursor: 'pointer' }}
        >
          📦 جرد المخزون
        </button>
        <button
          onClick={() => setInventoryTab('ai-forecast')}
          className={`delivery-tab-btn ${inventoryTab === 'ai-forecast' ? 'active' : ''}`}
          style={{ padding: '10px 24px', fontSize: '0.92rem', fontWeight: 'bold', borderRadius: '12px', border: 'none', cursor: 'pointer', color: inventoryTab === 'ai-forecast' ? '#a78bfa' : undefined }}
        >
          🤖 التوقع الذكي للمخزون
        </button>
      </div>

      {inventoryTab === 'ai-forecast' ? (
        <React.Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>جاري تحميل التوقع الذكي... 🤖</div>}>
          <AiForecastView
            inventory={inventory}
            sales={sales}
            telegramSettings={telegramSettings}
            currentUserData={currentUserData}
          />
        </React.Suspense>
      ) : (
      <div className="premium-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 800 }}>جرد ومتابعة تواريخ الصلاحية</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            className="gradient-btn-secondary"
            onClick={printInventoryReport}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', padding: '10px 16px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc' }}
          >
            🖨️ طباعة تقرير المخزون
          </button>
          {isAdminVerified && (
            <button
              type="button"
              className="gradient-btn-secondary"
              onClick={() => setIsBulkTranslationModalOpen(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', padding: '10px 16px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd' }}
            >
              🌐 ترجمة أسماء المخزون
            </button>
          )}
          {isAdminVerified && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              type="button"
              className="gradient-btn-secondary" 
              onClick={() => setIsImportModalOpen(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', padding: '10px 16px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)' }}
            >
              📥 استيراد جرد (Excel/CSV)
            </button>
            <button className="gradient-btn" onClick={() => { setEditId(null); setIsModalOpen(true); }}>+ إضافة صنف جديد</button>
          </div>
          )}
        </div>
      </div>

      {/* Smart Alert Summary Cards */}
      <div className="inventory-alerts-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div 
          onClick={() => { setActiveFilter('all'); setInventoryPage(1); }}
          style={{
            cursor: 'pointer',
            padding: '16px',
            borderRadius: '12px',
            background: activeFilter === 'all' ? 'rgba(59, 130, 246, 0.08)' : 'rgba(255, 255, 255, 0.01)',
            border: activeFilter === 'all' ? '1px solid var(--primary)' : '1px solid var(--border)',
            textAlign: 'center',
            transition: 'all 0.25s ease',
            boxShadow: activeFilter === 'all' ? '0 0 15px rgba(59, 130, 246, 0.15)' : 'none'
          }}
        >
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>إجمالي الأصناف</div>
          <h2 style={{ color: '#fff', margin: '8px 0 0 0', fontSize: '1.6rem', fontWeight: 800 }}>{inventory.length}</h2>
        </div>

        <div 
          onClick={() => { setActiveFilter('lowStock'); setInventoryPage(1); }}
          style={{
            cursor: 'pointer',
            padding: '16px',
            borderRadius: '12px',
            background: activeFilter === 'lowStock' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(255, 255, 255, 0.01)',
            border: activeFilter === 'lowStock' ? '1px solid #ef4444' : lowStockCount > 0 ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid var(--border)',
            textAlign: 'center',
            transition: 'all 0.25s ease',
            boxShadow: activeFilter === 'lowStock' ? '0 0 15px rgba(239, 68, 68, 0.15)' : 'none'
          }}
        >
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <span>أصناف نافدة / حرجة</span>
            {lowStockCount > 0 && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', display: 'inline-block' }}></span>}
          </div>
          <h2 style={{ color: lowStockCount > 0 ? '#ef4444' : '#fff', margin: '8px 0 0 0', fontSize: '1.6rem', fontWeight: 800 }}>{lowStockCount}</h2>
        </div>

        <div 
          onClick={() => { setActiveFilter('expiring'); setInventoryPage(1); }}
          style={{
            cursor: 'pointer',
            padding: '16px',
            borderRadius: '12px',
            background: activeFilter === 'expiring' ? 'rgba(245, 158, 11, 0.08)' : 'rgba(255, 255, 255, 0.01)',
            border: activeFilter === 'expiring' ? '1px solid #f59e0b' : expiringCount > 0 ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid var(--border)',
            textAlign: 'center',
            transition: 'all 0.25s ease',
            boxShadow: activeFilter === 'expiring' ? '0 0 15px rgba(245, 158, 11, 0.15)' : 'none'
          }}
        >
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <span>صلاحيات قريبة (≤ 6 أشهر)</span>
            {expiringCount > 0 && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }}></span>}
          </div>
          <h2 style={{ color: expiringCount > 0 ? '#f59e0b' : '#fff', margin: '8px 0 0 0', fontSize: '1.6rem', fontWeight: 800 }}>{expiringCount}</h2>
        </div>

        <div 
          onClick={() => { setActiveFilter('fridge'); setInventoryPage(1); }}
          style={{
            cursor: 'pointer',
            padding: '16px',
            borderRadius: '12px',
            background: activeFilter === 'fridge' ? 'rgba(6, 182, 212, 0.08)' : 'rgba(255, 255, 255, 0.01)',
            border: activeFilter === 'fridge' ? '1px solid #06b6d4' : fridgeCount > 0 ? '1px solid rgba(6, 182, 212, 0.3)' : '1px solid var(--border)',
            textAlign: 'center',
            transition: 'all 0.25s ease',
            boxShadow: activeFilter === 'fridge' ? '0 0 15px rgba(6, 182, 212, 0.15)' : 'none'
          }}
        >
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <span>❄️ منتجات الثلاجة</span>
            {fridgeCount > 0 && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#06b6d4', display: 'inline-block' }}></span>}
          </div>
          <h2 style={{ color: fridgeCount > 0 ? '#06b6d4' : '#fff', margin: '8px 0 0 0', fontSize: '1.6rem', fontWeight: 800 }}>{fridgeCount}</h2>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          className="premium-input"
          placeholder=" بحث بالمنتج في المخزن (اسم الصنف أو الاسم العلمي أو الباركود)..."
          value={inventorySearch}
          onChange={(e) => {
            setInventorySearch(e.target.value);
            setInventoryPage(1);
          }}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="premium-table">
          <thead>
            <tr>
              <th>الصنف</th>
              <th>المخزون</th>
              <th>سعر البيع</th>
              <th>الصلاحية</th>
              <th>الخيارات</th>
            </tr>
          </thead>
          <tbody>
            {paginatedInventory.map(item => {
              const diffMonth = getExpiryMonths(item.expiryDate);
              const isExpired = diffMonth !== null && diffMonth < 0;
              const isExpiringSoon = diffMonth !== null && diffMonth <= 6;
              const isLowStock = item.quantity <= 5;
              const isOutOfStock = item.quantity <= 0;
              
              return (
                <tr 
                  key={item.id}
                  style={{
                    background: isOutOfStock ? 'rgba(239, 68, 68, 0.02)' : isExpired ? 'rgba(239, 68, 68, 0.02)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  <td style={{ fontWeight: 700 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span>{item.tradeName}</span>
                      {item.requiresFridge && (
                        <span style={{ padding: '2px 6px', background: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700 }}>
                          ❄️ ثلاجة
                        </span>
                      )}
                      {item.origin === 'imported' ? (
                        <span style={{ padding: '2px 6px', background: 'rgba(236, 72, 153, 0.15)', color: '#f472b6', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700 }}>
                          ✈️ مستورد
                        </span>
                      ) : (
                        <span style={{ padding: '2px 6px', background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700 }}>
                          🇪🇬 محلي
                        </span>
                      )}
                      {isOutOfStock && <span style={{ padding: '2px 6px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700 }}>نافد</span>}
                      {!isOutOfStock && isLowStock && <span style={{ padding: '2px 6px', background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700 }}>حرج</span>}
                      {isExpired && <span style={{ padding: '2px 6px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700 }}>منتهي</span>}
                    </div>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{item.genericName}</span>
                  </td>
                  <td>
                    <span style={{ color: isOutOfStock ? '#ef4444' : isLowStock ? '#f59e0b' : 'var(--accent-green)', fontWeight: 600 }}>
                      {isOutOfStock ? 'نافد من المخزن' : `${item.quantity} عبوة`}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: 'var(--primary)', fontWeight: 700 }}>{item.sellingPrice} ج.م</span>
                  </td>
                  <td>
                    <span className={`status-badge ${getExpiryStatus(item.expiryDate)}`}>
                      {typeof item.expiryDate === 'object' && item.expiryDate ? (item.expiryDate.year ? `${item.expiryDate.year}-${String(item.expiryDate.month).padStart(2, '0')}-${String(item.expiryDate.day).padStart(2, '0')}` : JSON.stringify(item.expiryDate)) : (item.expiryDate || 'غير محدد')}
                      {isExpiringSoon && !isExpired && ` (متبقي ${diffMonth} أشهر)`}
                      {isExpired && ` (منتهي)`}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {isAdminVerified ? (
                        <>
                          <button
                            onClick={() => {
                              setEditId(item.id);
                              setTradeName(item.tradeName);
                              setTradeNameSecondary(item.tradeNameSecondary || '');
                              setGenericName(item.genericName);
                              setCategory(item.category);
                              setBuyingPrice(item.buyingPrice);
                              setSellingPrice(item.sellingPrice);
                              setQuantity(item.quantity);
                              setExpiryDate(item.expiryDate);
                              setBarcode(item.barcode);
                              setSubUnits(item.subUnits || []);
                              setIsModalOpen(true);
                            }}
                            className="gradient-btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                          >
                            تعديل ✏️
                          </button>
                          <button
                            onClick={() => {
                              setSelectedBarcodeItem(item);
                              setCustomBarcodeVal(item.barcode || '');
                              setLabelCopies(1);
                              setIsBarcodeModalOpen(true);
                            }}
                            className="gradient-btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '0.85rem', background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: '#fff', border: 'none' }}
                          >
                            ملصق 🏷️
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            className="gradient-btn"
                            style={{ padding: '6px 12px', fontSize: '0.85rem', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}
                          >
                            حذف 🗑️
                          </button>
                        </>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>🔒</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalInvPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
          <button 
            disabled={inventoryPage === 1} 
            onClick={() => setInventoryPage(prev => Math.max(1, prev - 1))}
            className="gradient-btn-secondary"
            style={{ padding: '6px 14px', fontSize: '0.85rem' }}
          >
            السابق
          </button>
          <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>صفحة {inventoryPage} من {totalInvPages}</span>
          <button 
            disabled={inventoryPage === totalInvPages} 
            onClick={() => setInventoryPage(prev => Math.min(totalInvPages, prev + 1))}
            className="gradient-btn-secondary"
            style={{ padding: '6px 14px', fontSize: '0.85rem' }}
          >
            التالي
          </button>
        </div>
      )}

      {/* Barcode Print Configuration Modal */}
      {isBarcodeModalOpen && selectedBarcodeItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(5, 8, 16, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          direction: 'rtl',
          textAlign: 'right',
          fontFamily: 'Cairo, sans-serif'
        }}>

          <div className="premium-card" style={{
            width: '90%',
            maxWidth: '680px',
            background: 'linear-gradient(135deg, #0f172a 0%, #030712 100%)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            padding: '24px',
            borderRadius: '20px'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '14px' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.2rem', fontWeight: 800 }}>🏷️ طباعة الباركود للملصقات</h3>
              <button 
                onClick={() => setIsBarcodeModalOpen(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '1.5rem' }}
              >
                &times;
              </button>
            </div>

            {/* Content Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'start' }}>
              {/* Configuration Panel */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>الصنف</label>
                  <input type="text" className="premium-input" style={{ background: 'rgba(255,255,255,0.03)', color: '#94a3b8' }} value={selectedBarcodeItem.tradeName} disabled />
                </div>

                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>الباركود (رقمي)</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input 
                      type="text" 
                      className="premium-input" 
                      value={customBarcodeVal} 
                      onChange={e => setCustomBarcodeVal(e.target.value)} 
                      placeholder="أدخل رقم الباركود"
                      style={{ flex: 1 }}
                    />
                    {!selectedBarcodeItem.barcode && (
                      <button 
                        type="button"
                        onClick={() => setCustomBarcodeVal('20' + Math.floor(1000000000 + Math.random() * 9000000000).toString())}
                        className="gradient-btn-secondary"
                        style={{ padding: '8px', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                        title="توليد باركود تلقائي مخصص للمحل"
                      >
                        توليد ⚡
                      </button>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>عرض الملصق (مم)</label>
                    <input type="number" className="premium-input" value={labelWidth} onChange={e => setLabelWidth(Number(e.target.value))} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>ارتفاع الملصق (مم)</label>
                    <input type="number" className="premium-input" value={labelHeight} onChange={e => setLabelHeight(Number(e.target.value))} />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>عدد النسخ المطبوعة</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input 
                      type="number" 
                      className="premium-input" 
                      style={{ width: '80px' }} 
                      value={labelCopies} 
                      onChange={e => setLabelCopies(Math.max(1, Number(e.target.value)))} 
                      min="1" 
                    />
                    <button 
                      type="button" 
                      onClick={() => setLabelCopies(selectedBarcodeItem.quantity || 1)}
                      className="gradient-btn-secondary"
                      style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                    >
                      بعدد الجرد الحالي ({selectedBarcodeItem.quantity})
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.82rem', color: '#fff' }}>
                    <input type="checkbox" checked={showPharmacyName} onChange={e => setShowPharmacyName(e.target.checked)} />
                    طباعة اسم الصيدلية بالملصق
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.82rem', color: '#fff' }}>
                    <input type="checkbox" checked={showPriceOnLabel} onChange={e => setShowPriceOnLabel(e.target.checked)} />
                    طباعة سعر البيع ({selectedBarcodeItem.sellingPrice} ج.م)
                  </label>
                </div>
              </div>

              {/* Live Preview Panel */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', height: '100%' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', alignSelf: 'flex-start' }}>👁️ معاينة الملصق (38x25 مم تقريباً)</span>
                
                {/* Physical Label Mockup container */}
                <div style={{
                  width: '200px',
                  height: '135px',
                  background: '#fff',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                  padding: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: '#000',
                  boxSizing: 'border-box'
                }}>
                  {showPharmacyName ? (
                    <div style={{ fontSize: '9px', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'center' }}>
                      {currentUserData?.pharmacyName || 'صيدلية دكتور مصر'}
                    </div>
                  ) : <div />}
                  <div style={{ fontSize: '8px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'center', margin: '1px 0' }}>
                    {selectedBarcodeItem.tradeName}
                  </div>
                  
                  {/* SVG generated dynamically */}
                  <svg id="preview-barcode-svg" style={{ width: '100%', height: '55px', maxHeight: '55px' }}></svg>
                  
                  {showPriceOnLabel ? (
                    <div style={{ fontSize: '9px', fontWeight: 800, borderTop: '0.5px dashed #ccc', width: '100%', textAlign: 'center', paddingTop: '2px' }}>
                      {selectedBarcodeItem.sellingPrice} ج.م
                    </div>
                  ) : <div />}
                </div>

              </div>
            </div>

            {/* Actions Footer */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '10px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '16px' }}>
              <button 
                onClick={handlePrintBarcodeLabel}
                className="gradient-btn"
                style={{ flex: 1, padding: '12px', fontWeight: 'bold' }}
              >
                🖨️ بدء طباعة الملصقات ({labelCopies})
              </button>
              <button 
                onClick={() => setIsBarcodeModalOpen(false)}
                className="gradient-btn-secondary"
                style={{ padding: '12px 24px', fontWeight: 'bold' }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Translation Modal */}
      {isBulkTranslationModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(5, 8, 16, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          direction: 'rtl',
          textAlign: 'right',
          padding: '16px'
        }}>
          <div className="premium-card fade-in" style={{ width: '100%', maxWidth: '500px', padding: '24px', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 20px 40px rgba(0,0,0,0.5)', background: 'var(--card-bg)' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 800, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🌐 ترجمة جميع أسماء المخزون تلقائياً
            </h3>
            
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '20px' }}>
              سيقوم هذا الإجراء بترجمة أسماء الأصناف التجارية التي لا تحتوي على اسم بديل (من إنجليزي إلى عربي أو العكس) وإضافتها في خانة "اسم الصنف التجاري البديل" تلقائياً.
            </p>

            {isBulkTranslating ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{bulkTranslationStatusMsg || 'جاري العمل...'}</span>
                  <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'var(--primary)' }}>{bulkTranslationProgress}%</span>
                </div>
                <div style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${bulkTranslationProgress}%`, height: '100%', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)', borderRadius: '4px', transition: 'width 0.3s ease' }}></div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                <button 
                  onClick={handleExecuteBulkTranslation}
                  className="gradient-btn"
                  style={{ flex: 1, padding: '12px', fontWeight: 'bold' }}
                >
                  🚀 ابدأ الترجمة الجماعية
                </button>
                <button 
                  onClick={() => setIsBulkTranslationModalOpen(false)}
                  className="gradient-btn-secondary"
                  style={{ padding: '12px 24px', fontWeight: 'bold' }}
                >
                  إلغاء
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Excel/CSV Mass Importer Modal */}
      {isImportModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(5, 8, 16, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          direction: 'rtl',
          textAlign: 'right',
          fontFamily: 'Cairo, sans-serif'
        }}>
          <div className="premium-card" style={{
            width: '90%',
            maxWidth: '750px',
            maxHeight: '90vh',
            overflowY: 'auto',
            background: 'linear-gradient(135deg, #0f172a 0%, #030712 100%)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            padding: '24px',
            borderRadius: '20px'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '14px' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.2rem', fontWeight: 800 }}>📥 استيراد جرد الأدوية من ملف Excel أو CSV</h3>
              <button 
                onClick={() => {
                  if (isImporting) return;
                  setIsImportModalOpen(false);
                  setImportFile(null);
                  setImportRows([]);
                }}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '1.5rem' }}
                disabled={isImporting}
              >
                &times;
              </button>
            </div>

            {/* Importer Steps / Main Panel */}
            {importRows.length === 0 ? (
              /* Step 1: Upload File dropzone */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{
                  border: '2px dashed rgba(255, 255, 255, 0.15)',
                  borderRadius: '12px',
                  padding: '40px 20px',
                  textAlign: 'center',
                  background: 'rgba(255,255,255,0.01)',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.3s'
                }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    const fakeEvent = { target: { files: [file] } };
                    handleImportFileChange(fakeEvent);
                  }
                }}
                onClick={() => document.getElementById('import-file-selector').click()}
                >
                  <input 
                    id="import-file-selector"
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleImportFileChange}
                    style={{ display: 'none' }}
                  />
                  <div style={{ fontSize: '3rem' }}>📊</div>
                  <h4 style={{ margin: 0, color: '#fff' }}>اسحب وأفلت ملف الجرد الخاص بك هنا</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>يدعم الملفات بصيغة Excel (.xlsx, .xls) أو ملفات CSV (.csv)</p>
                  <button type="button" className="gradient-btn-secondary" style={{ padding: '8px 20px', fontSize: '0.85rem', marginTop: '10px' }}>تصفح الملفات</button>
                </div>

                {/* Naming Guidelines Card */}
                <div style={{
                  padding: '16px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  fontSize: '0.82rem',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.6'
                }}>
                  <div style={{ fontWeight: 'bold', color: '#fff', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    💡 <span>كيف يتعرف النظام على أعمدة ملفك تلقائياً؟</span>
                  </div>
                  لتوفير الوقت والمطابقة تلقائياً، قم بتسمية صف العناوين (الرأس) في ملف الإكسل بأي من المسميات المعتمدة التالية ليقوم النظام بالتعرف عليها فوراً:
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginTop: '10px' }}>
                    <div>• <strong>الاسم التجاري (إجباري):</strong> <code>الاسم</code>، <code>Name</code>، <code>تجاري</code>، <code>صنف</code>، <code>المنتج</code></div>
                    <div>• <strong>الاسم العلمي:</strong> <code>علمي</code>، <code>Generic</code>، <code>الاسم العلمي</code></div>
                    <div>• <strong>التصنيف:</strong> <code>تصنيف</code>، <code>Category</code>، <code>فئة</code>، <code>المجموعة</code></div>
                    <div>• <strong>الباركود:</strong> <code>باركود</code>، <code>Barcode</code>، <code>كود</code>، <code>رقم الكود</code></div>
                    <div>• <strong>الكمية:</strong> <code>الكمية</code>، <code>qty</code>، <code>quantity</code>، <code>عدد</code>، <code>الرصيد</code></div>
                    <div>• <strong>سعر البيع:</strong> <code>بيع</code>، <code>جمهور</code>، <code>سعر</code>، <code>sell</code>، <code>سعر البيع</code></div>
                    <div>• <strong>سعر الشراء:</strong> <code>شراء</code>، <code>جملة</code>، <code>buy</code>، <code>سعر الشراء</code></div>
                    <div>• <strong>الصلاحية:</strong> <code>صلاحية</code>، <code>expiry</code>، <code>تاريخ</code>، <code>انتهاء</code></div>
                  </div>
                </div>
              </div>
            ) : (
              /* Step 2: Mapping columns and Previewing */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ padding: '12px', background: 'rgba(59, 130, 246, 0.08)', borderRadius: '10px', border: '1px solid rgba(59, 130, 246, 0.2)', fontSize: '0.85rem', color: '#93c5fd' }}>
                  📊 تم تحليل <strong>{importRows.length}</strong> صف بيانات من الملف: <strong>{importFile?.name}</strong>. يرجى مطابقة أعمدة ملفك بالأعمدة المطلوبة أدناه:
                </div>

                {/* Column Mapping Selectors */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>الاسم التجاري *</label>
                    <select className="premium-input" value={importMapping.tradeName} onChange={e => setImportMapping({...importMapping, tradeName: e.target.value})} required>
                      <option value="">-- اختر العمود --</option>
                      {importHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>الاسم العلمي</label>
                    <select className="premium-input" value={importMapping.genericName} onChange={e => setImportMapping({...importMapping, genericName: e.target.value})}>
                      <option value="">-- اختياري --</option>
                      {importHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>التصنيف</label>
                    <select className="premium-input" value={importMapping.category} onChange={e => setImportMapping({...importMapping, category: e.target.value})}>
                      <option value="">-- اختياري (تلقائي: عام) --</option>
                      {importHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>الباركود</label>
                    <select className="premium-input" value={importMapping.barcode} onChange={e => setImportMapping({...importMapping, barcode: e.target.value})}>
                      <option value="">-- اختياري (توليد تلقائي) --</option>
                      {importHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>الكمية بالعلبة</label>
                    <select className="premium-input" value={importMapping.quantity} onChange={e => setImportMapping({...importMapping, quantity: e.target.value})}>
                      <option value="">-- اختياري (تلقائي: 0) --</option>
                      {importHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>سعر الشراء</label>
                    <select className="premium-input" value={importMapping.buyingPrice} onChange={e => setImportMapping({...importMapping, buyingPrice: e.target.value})}>
                      <option value="">-- اختياري (تلقائي: 0) --</option>
                      {importHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>سعر البيع جمهور</label>
                    <select className="premium-input" value={importMapping.sellingPrice} onChange={e => setImportMapping({...importMapping, sellingPrice: e.target.value})}>
                      <option value="">-- اختياري (تلقائي: 0) --</option>
                      {importHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>تاريخ الصلاحية</label>
                    <select className="premium-input" value={importMapping.expiryDate} onChange={e => setImportMapping({...importMapping, expiryDate: e.target.value})}>
                      <option value="">-- اختياري --</option>
                      {importHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                </div>

                {/* Preview Grid */}
                <div>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>👁️ معاينة شكل البيانات المطبقة (أول 5 صفوف):</span>
                  <div style={{ overflowX: 'auto', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', maxHeight: '150px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', color: '#fff', textAlign: 'right' }}>
                      <thead>
                        <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <th style={{ padding: '8px 12px' }}>الاسم التجاري</th>
                          <th style={{ padding: '8px 12px' }}>الاسم العلمي</th>
                          <th style={{ padding: '8px 12px' }}>التصنيف</th>
                          <th style={{ padding: '8px 12px' }}>الباركود</th>
                          <th style={{ padding: '8px 12px' }}>الكمية</th>
                          <th style={{ padding: '8px 12px' }}>سعر الجمهور</th>
                          <th style={{ padding: '8px 12px' }}>تاريخ الصلاحية</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importRows.slice(0, 5).map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '8px 12px' }}>{row[importMapping.tradeName] || '-'}</td>
                            <td style={{ padding: '8px 12px' }}>{importMapping.genericName ? row[importMapping.genericName] : '-'}</td>
                            <td style={{ padding: '8px 12px' }}>{importMapping.category ? row[importMapping.category] : 'عام'}</td>
                            <td style={{ padding: '8px 12px' }}>{importMapping.barcode ? row[importMapping.barcode] : 'توليد تلقائي'}</td>
                            <td style={{ padding: '8px 12px' }}>{importMapping.quantity ? row[importMapping.quantity] : '0'}</td>
                            <td style={{ padding: '8px 12px' }}>{importMapping.sellingPrice ? `${row[importMapping.sellingPrice]} ج.م` : '0 ج.م'}</td>
                            <td style={{ padding: '8px 12px' }}>{importMapping.expiryDate ? row[importMapping.expiryDate] : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Importer Loader Status / Progress Bar */}
            {isImporting && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '14px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
                <div style={{ fontSize: '0.85rem', color: '#60a5fa', fontWeight: 'bold' }}>{importStatusMsg}</div>
                {importProgress > 0 && (
                  <div style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '999px', overflow: 'hidden' }}>
                    <div style={{ width: `${importProgress}%`, height: '100%', background: 'var(--primary)', transition: 'width 0.3s' }}></div>
                  </div>
                )}
              </div>
            )}

            {/* Actions Footer */}
            <div style={{ display: 'flex', gap: '12px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '16px', marginTop: '10px' }}>
              {importRows.length > 0 && (
                <button 
                  onClick={handleStartImportAnalysis}
                  className="gradient-btn"
                  style={{ flex: 1, padding: '12px', fontWeight: 'bold' }}
                  disabled={isImporting}
                >
                  🚀 تأكيد واستيراد الأصناف ({importRows.length})
                </button>
              )}
              <button 
                onClick={() => {
                  setIsImportModalOpen(false);
                  setImportFile(null);
                  setImportRows([]);
                }}
                className="gradient-btn-secondary"
                style={{ padding: '12px 24px', fontWeight: 'bold' }}
                disabled={isImporting}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
        {showDuplicateImportDialog && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(8, 13, 27, 0.9)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 120000,
            padding: '24px', fontFamily: 'Cairo, sans-serif'
          }}>
            <div className="premium-card fade-in-content" style={{ width: '500px', maxWidth: '100%', background: 'var(--bg-card)', border: '1px solid var(--accent-pink)', direction: 'rtl', padding: '32px', borderRadius: '16px', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}>
              <div style={{ fontSize: '3.5rem', color: 'var(--accent-pink)', marginBottom: '16px', textAlign: 'center' }}>
                ⚠️
              </div>
              <h3 className="gradient-text" style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '12px', textAlign: 'center' }}>تنبيه: منتجات مكررة بالفعل!</h3>
              <p style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '16px', lineHeight: '1.6', textAlign: 'right' }}>
                تم العثور على <strong>{duplicateImportItems.length}</strong> منتج في الملف يحملون باركود مسجل مسبقاً في المخزن.
              </p>
              
              <div style={{ maxHeight: '180px', overflowY: 'auto', background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '24px', textAlign: 'right' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>أمثلة من المنتجات المكررة:</span>
                {duplicateImportItems.slice(0, 5).map((item, idx) => (
                  <div key={idx} style={{ fontSize: '0.85rem', color: '#cbd5e1', marginBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.04)', paddingBottom: '4px' }}>
                    🔹 {item.existingItem.tradeName} (باركود: {item.existingItem.barcode}) - الكمية المكتوبة بالملف: {item.qty}
                  </div>
                ))}
                {duplicateImportItems.length > 5 && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: '6px' }}>
                    ...وغيرها من المنتجات المكررة.
                  </div>
                )}
              </div>

              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '24px', lineHeight: '1.6', textAlign: 'center' }}>
                <strong>هل توافق على زيادة كمية هذه المنتجات المسجلة مسبقاً بالمخزن بالقيم الواردة في الملف؟</strong>
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button 
                  onClick={() => {
                    executeImportExcel(newImportItems, duplicateImportItems);
                  }}
                  className="gradient-btn"
                  style={{ width: '100%', padding: '12px', fontWeight: 'bold' }}
                >
                  ➕ نعم، زيادة كميات الأدوية المكررة واستيراد الجديد
                </button>
                
                <button 
                  onClick={() => {
                    executeImportExcel(newImportItems, []);
                  }}
                  className="gradient-btn-secondary"
                  style={{ width: '100%', padding: '12px', fontWeight: 'bold', color: '#60a5fa' }}
                >
                  🚫 تخطي الأدوية المكررة واستيراد الجديد فقط ({newImportItems.length})
                </button>

                <button 
                  onClick={() => {
                    setShowDuplicateImportDialog(false);
                  }}
                  style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.9rem', textDecoration: 'underline', marginTop: '4px', textAlign: 'center', alignSelf: 'center' }}
                >
                  إلغاء عملية الاستيراد بالكامل
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
      )}
    </div>
  );
}



const COMMON_EGYPTIAN_DRUGS = [
  { tradeName: "بانادول اكسترا", genericName: "Paracetamol + Caffeine", category: "مسكنات وخافض حرارة", barcode: "6223002621118", sellingPrice: 45, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 22.5 }] },
  { tradeName: "بانادول ادفانس", genericName: "Paracetamol", category: "مسكنات وخافض حرارة", barcode: "6223002621071", sellingPrice: 30, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 15 }] },
  { tradeName: "كونجستال أقراص", genericName: "Paracetamol + Chlorpheniramine + Pseudoephedrine", category: "برد وإنفلونزا", barcode: "6223000216118", sellingPrice: 31, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 15.5 }] },
  { tradeName: "باي الكوفان 150", genericName: "Ketoprofen", category: "مسكنات ومضادات التهاب", barcode: "6221007010411", sellingPrice: 35, subUnits: [{ id: "strip", name: "شريط", ratio: 3, price: 11.67 }] },
  { tradeName: "أوجمنتين 1 جم أقراص", genericName: "Amoxicillin + Clavulanic Acid", category: "مضادات حيوية", barcode: "5016000122119", sellingPrice: 99, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 49.5 }] },
  { tradeName: "سيتال أقراص", genericName: "Paracetamol", category: "مسكنات وخافض حرارة", barcode: "6222003503212", sellingPrice: 15, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 7.5 }] },
  { tradeName: "كتافلام 50 ملجم أقراص", genericName: "Diclofenac Potassium", category: "مسكنات ومضادات التهاب", barcode: "6221008010519", sellingPrice: 51, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 25.5 }] },
  { tradeName: "بروفين 400 ملجم أقراص", genericName: "Ibuprofen", category: "مسكنات وخافض حرارة", barcode: "6223002620517", sellingPrice: 34, subUnits: [{ id: "strip", name: "شريط", ratio: 3, price: 11.33 }] },
  { tradeName: "أنتينال كبسول", genericName: "Nifuroxazide", category: "مطهرات معوية ومضادات إسهال", barcode: "6223000210215", sellingPrice: 36, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 18 }] },
  { tradeName: "أوميز 20 ملجم كبسول", genericName: "Omeprazole", category: "علاج حموضة وقرحة المعدة", barcode: "6223002620814", sellingPrice: 42, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 21 }] },
  { tradeName: "أماريل 2 ملجم أقراص", genericName: "Glimepiride", category: "علاج السكري", barcode: "6223002620111", sellingPrice: 46.5, subUnits: [{ id: "strip", name: "شريط", ratio: 3, price: 15.5 }] },
  { tradeName: "سيبتازول أقراص", genericName: "Sulfamethoxazole + Trimethoprim", category: "مضادات حيوية", barcode: "6223000211113", sellingPrice: 18, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 9 }] },
  { tradeName: "ريفو أقراص", genericName: "Aspirin (Acetylsalicylic Acid)", category: "مسكنات وحماية القلب", barcode: "6223000210017", sellingPrice: 10, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 5 }] },
  { tradeName: "بروفين 600 ملجم أقراص", genericName: "Ibuprofen", category: "مسكنات ومضادات التهاب", barcode: "6223002620524", sellingPrice: 48, subUnits: [{ id: "strip", name: "شريط", ratio: 3, price: 16 }] },
  { tradeName: "فولتارين 50 ملجم أقراص", genericName: "Diclofenac Sodium", category: "مسكنات ومضادات التهاب", barcode: "7611136001224", sellingPrice: 53, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 26.5 }] },
  { tradeName: "فولتارين جيل 100 جم", genericName: "Diclofenac Diethylamine", category: "مسكنات ومضادات التهاب موضعية", barcode: "7611136155613", sellingPrice: 85, subUnits: [] },
  { tradeName: "موبيك 15 ملجم أقراص", genericName: "Meloxicam", category: "مسكنات ومضادات التهاب", barcode: "9002234001112", sellingPrice: 65, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 32.5 }] },
  { tradeName: "بنادول ساينس للجيوب الأنفية", genericName: "Paracetamol + Pseudoephedrine", category: "برد وإنفلونزا وجيوب أنفية", barcode: "6223002621019", sellingPrice: 40, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 20 }] },
  { tradeName: "فلورست أقراص", genericName: "Paracetamol + Pseudoephedrine + Chlorpheniramine", category: "برد وإنفلونزا", barcode: "6223000212219", sellingPrice: 15, subUnits: [{ id: "strip", name: "شريط", ratio: 1, price: 15 }] },
  { tradeName: "أدول 500 ملجم أقراص", genericName: "Paracetamol", category: "مسكنات وخافض حرارة", barcode: "6224000301111", sellingPrice: 20, subUnits: [{ id: "strip", name: "شريط", ratio: 2, price: 10 }] }
];

// ── Session-level barcode cache ────────────────────────────────────────────────
// Caches Firestore global_medicines lookups for the lifetime of the browser session.
// Same barcode = zero extra Firestore reads after the first hit. Firebase-quota friendly.
const _barcodeLookupCache = new Map(); // barcode string → data object | 'not-found'
// ────────────────────────────────────────────────────────────────────────────────

function InventoryModal({ editId, tradeName, setTradeName, tradeNameSecondary, setTradeNameSecondary, genericName, setGenericName, category, setCategory, barcode, setBarcode, quantity, setQuantity, expiryDate, setExpiryDate, buyingPrice, setBuyingPrice, sellingPrice, setSellingPrice, subUnits, setSubUnits, newSubUnitName, setNewSubUnitName, newSubUnitRatio, setNewSubUnitRatio, newSubUnitPrice, setNewSubUnitPrice, requiresFridge, setRequiresFridge, origin, setOrigin, handleSaveItem, clearForm, isSaving, barcodeInputRef, db }) {
  const isAutoGen = !barcode || /^20\d{10}$/.test(barcode.trim());
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [barcodeStatus, setBarcodeStatus] = useState(null); // null | 'searching' | 'found-global' | 'found-local' | 'not-found'

  const [dbSuggestions, setDbSuggestions] = useState([]);

  // Local suggestions
  const localSuggestions = tradeName.trim().length >= 1
    ? COMMON_EGYPTIAN_DRUGS.filter(d => 
        d.tradeName.toLowerCase().includes(tradeName.toLowerCase()) || 
        d.genericName.toLowerCase().includes(tradeName.toLowerCase())
      )
    : [];

  // Combine local suggestions and DB suggestions (deduplicated by tradeName)
  const suggestionsMap = new Map();
  localSuggestions.forEach(item => suggestionsMap.set(item.tradeName.toLowerCase(), item));
  dbSuggestions.forEach(item => {
    if (!suggestionsMap.has(item.tradeName.toLowerCase())) {
      suggestionsMap.set(item.tradeName.toLowerCase(), item);
    }
  });
  const suggestions = Array.from(suggestionsMap.values()).slice(0, 10);

  // Firestore suggestions search (debounced & optimized)
  useEffect(() => {
    const term = tradeName.trim();
    if (term.length < 2 || editId) {
      setDbSuggestions([]);
      return;
    }

    let active = true;

    const timer = setTimeout(async () => {
      if (!db || !active) return;
      try {
        // Prepare case variations because Firestore queries are case-sensitive
        const termLower = term.toLowerCase();
        const termUpper = term.toUpperCase();
        const termCapitalized = term.charAt(0).toUpperCase() + term.slice(1).toLowerCase();
        
        const uniqueTerms = Array.from(new Set([term, termLower, termUpper, termCapitalized]));
        const results = [];

        // Optimize: trigger queries in parallel
        const promises = uniqueTerms.flatMap(t => {
          const q1 = query(
            collection(db, 'global_medicines'),
            where('tradeName', '>=', t),
            where('tradeName', '<=', t + '\uf8ff'),
            limit(3)
          );
          const q2 = query(
            collection(db, 'global_barcodes'),
            where('tradeName', '>=', t),
            where('tradeName', '<=', t + '\uf8ff'),
            limit(3)
          );
          const q3 = query(
            collection(db, 'global_barcodes'),
            where('trade_name', '>=', t),
            where('trade_name', '<=', t + '\uf8ff'),
            limit(3)
          );
          return [getDocs(q1), getDocs(q2), getDocs(q3)];
        });

        const snapshots = await Promise.all(promises);
        
        if (!active) return;

        snapshots.forEach((snap) => {
          snap.forEach(doc => {
            const d = doc.data();
            const tName = d.tradeName || d.trade_name || '';
            if (tName && !results.some(r => r.tradeName.toLowerCase() === tName.toLowerCase())) {
              results.push({
                tradeName: tName,
                genericName: d.genericName || d.generic_name || '',
                category: d.category || 'عام',
                barcode: d.barcode || doc.id,
                sellingPrice: d.sellingPrice || 0,
                subUnits: d.subUnits || []
              });
            }
          });
        });

        if (active) {
          setDbSuggestions(results.slice(0, 8));
        }
      } catch (err) {
        console.warn("Error fetching autocomplete suggestions from DB:", err);
      }
    }, 120); // Slightly faster debounce

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [tradeName, editId, db]);

  // Helper: detect if barcode is auto-generated by our system (starts with '20' = internal use only)
  const isAutoGeneratedBarcode = (bc) => {
    if (!bc) return false;
    const clean = bc.trim();
    // Auto-generated barcodes start with '20' and are exactly 12 digits (our internal format)
    return /^20\d{10}$/.test(clean);
  };

  // ── Barcode lookup: Plan A (Firestore) → Plan B (local) ─────────────────────
  // Quota-friendly design:
  //   1. Debounced 600ms — waits until user stops typing/scanning before any read
  //   2. Plan B (local array) checked FIRST — zero Firestore reads for known local drugs
  //   3. Session cache (_barcodeLookupCache) — same barcode never hits Firestore twice
  //   4. Minimum 8 chars — real barcodes, avoids reads on partial input
  //   5. Auto-generated barcodes (^20\d{10}$) skipped entirely
  useEffect(() => {
    const clean = barcode ? barcode.trim() : '';

    // Guard: too short, editing existing item, or auto-generated internal barcode
    const isAutoGen = /^20\d{10}$/.test(clean);
    if (!clean || clean.length < 8 || editId || isAutoGen) {
      setBarcodeStatus(null);
      return;
    }

    // Start debounce timer — only fires 600ms after user stops typing
    setBarcodeStatus('searching');
    const timer = setTimeout(async () => {
      try {
        // ── Step 1: Check Plan B (local) first — zero Firestore cost ──────────
        const localMatch = COMMON_EGYPTIAN_DRUGS.find(d => d.barcode === clean);
        if (localMatch) {
          setTradeName(localMatch.tradeName);
          if (setGenericName) setGenericName(localMatch.genericName);
          if (setCategory) setCategory(localMatch.category);
          // sellingPrice intentionally NOT autofilled — each pharmacy sets its own
          if (setSubUnits && localMatch.subUnits) setSubUnits(localMatch.subUnits);
          setBarcodeStatus('found-local');
          return;
        }

        // ── Step 2: Check session cache before hitting Firestore ───────────────
        if (_barcodeLookupCache.has(clean)) {
          const cached = _barcodeLookupCache.get(clean);
          if (cached === 'not-found') {
            setBarcodeStatus('not-found');
          } else {
            setTradeName(cached.tradeName || '');
            if (setGenericName) setGenericName(cached.genericName || '');
            if (setCategory) setCategory(cached.category || '');
            if (setSubUnits && cached.subUnits) setSubUnits(cached.subUnits);
            setBarcodeStatus('found-global');
          }
          return;
        }

        // ── Step 3: Plan A — single Firestore read ─────────────────────────────
        if (db) {
          // Check global_medicines first
          let snap = await getDoc(doc(db, 'global_medicines', clean));
          // If not found, check global_barcodes
          if (!snap.exists()) {
            snap = await getDoc(doc(db, 'global_barcodes', clean));
          }
          
          if (snap.exists()) {
            const data = snap.data();
            _barcodeLookupCache.set(clean, data); // cache for future use this session
            setTradeName(data.tradeName || data.trade_name || '');
            if (setGenericName) setGenericName(data.genericName || data.generic_name || '');
            if (setCategory) setCategory(data.category || '');
            // sellingPrice intentionally NOT autofilled — each pharmacy sets its own
            if (setSubUnits && data.subUnits) setSubUnits(data.subUnits);
            setBarcodeStatus('found-global');
            return;
          } else {
            _barcodeLookupCache.set(clean, 'not-found'); // cache miss too
          }
        }

        // Not found anywhere
        setBarcodeStatus('not-found');
      } catch (err) {
        console.warn('Barcode lookup error:', err);
        setBarcodeStatus('not-found');
      }
    }, 600); // 600ms debounce

    return () => clearTimeout(timer); // cleanup on re-render or unmount
  }, [barcode, editId]);
  // ─────────────────────────────────────────────────────────────────────────────

  const handleAddSubUnit = (e) => {
    e.preventDefault();
    if (!newSubUnitName.trim() || !newSubUnitRatio || !newSubUnitPrice) {
      window.triggerDrMasrToast("⚠️ يرجى ملء جميع حقول الوحدة الفرعية!");
      return;
    }
    const newUnit = {
      id: Date.now().toString(),
      name: newSubUnitName.trim(),
      ratio: Number(newSubUnitRatio),
      price: Number(newSubUnitPrice)
    };
    setSubUnits([...subUnits, newUnit]);
    setNewSubUnitName('');
    setNewSubUnitRatio('');
    setNewSubUnitPrice('');
  };

  const handleRemoveSubUnit = (id) => {
    setSubUnits(subUnits.filter(u => u.id !== id));
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(8, 13, 27, 0.85)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      padding: '24px'
    }}>
      <div className="premium-card fade-in-content" style={{ width: '650px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid var(--primary)', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid var(--border)', paddingBottom: '16px' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0 }}>
            {editId ? 'تعديل صنف دوائي' : 'إضافة صنف جديد للمخزون'}
          </h3>
          <button onClick={clearForm} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
        </div>

        <form onSubmit={handleSaveItem} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Top Row: Horizontal input fields for Barcode, Trade Name, Translation */}
          <div className="product-modal-top-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr 1.3fr', gap: '16px', alignItems: 'start' }}>
            <div>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>الباركود</label>
              <input 
                ref={barcodeInputRef} 
                type="text" 
                className="premium-input" 
                value={barcode} 
                onChange={e => { 
                  const val = e.target.value.replace(/\D/g, ''); // Keep numbers only for barcodes
                  setBarcode(val.slice(0, 13)); 
                  setBarcodeStatus(null); 
                }} 
                maxLength={13}
                placeholder="امسح الباركود أو اكتبه" 
              />
            </div>

            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>اسم الصنف التجاري *</label>
                {tradeName.trim() && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const translated = await translateProductText(tradeName);
                        setTradeNameSecondary(translated);
                        window.triggerDrMasrToast("✨ تم ترجمة الاسم ووضعه في خانة البديل!");
                      } catch (err) {
                        window.triggerDrMasrToast("❌ فشلت الترجمة التلقائية.");
                      }
                    }}
                    style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                  >
                    🌐 ترجمة تلقائية
                  </button>
                )}
              </div>
              <input 
                type="text" 
                className="premium-input" 
                value={tradeName} 
                onChange={e => {
                  setTradeName(e.target.value);
                  setShowSuggestions(true);
                }} 
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  setTimeout(() => setShowSuggestions(false), 250);
                  if (tradeName.trim()) {
                    translateProductText(tradeName).then(translated => {
                      setTradeNameSecondary(translated);
                    }).catch(err => console.error(err));
                  }
                }}
                required 
                placeholder="مثال: Panadol Extra" 
              />
              {showSuggestions && suggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#1e293b',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  maxHeight: '180px',
                  overflowY: 'auto',
                  zIndex: 9999,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                  marginTop: '4px'
                }}>
                  {suggestions.map((item, idx) => (
                    <div 
                      key={idx}
                      onClick={() => {
                        setTradeName(item.tradeName);
                        if (setGenericName) setGenericName(item.genericName);
                        if (setCategory) setCategory(item.category);
                        if (setBarcode) setBarcode(item.barcode);
                        if (setSellingPrice) setSellingPrice(item.sellingPrice);
                        if (setSubUnits && item.subUnits) setSubUnits(item.subUnits);
                        setShowSuggestions(false);
                      }}
                      style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        fontSize: '0.85rem',
                        color: '#fff',
                        textAlign: 'right'
                      }}
                      onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={e => e.target.style.background = 'transparent'}
                    >
                      <div style={{ fontWeight: 'bold' }}>{item.tradeName}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{item.genericName} - {item.sellingPrice} ج.م</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>ترجمة الاسم التجاري</label>
                {tradeNameSecondary.trim() && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const translated = await translateProductText(tradeNameSecondary);
                        setTradeNameSecondary(translated);
                        window.triggerDrMasrToast("✨ تم ترجمة الاسم تلقائياً!");
                      } catch (err) {
                        window.triggerDrMasrToast("❌ فشلت الترجمة التلقائية.");
                      }
                    }}
                    style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                  >
                    🌐 ترجمة تلقائية
                  </button>
                )}
              </div>
              <input 
                type="text" 
                className="premium-input" 
                value={tradeNameSecondary} 
                onChange={e => setTradeNameSecondary(e.target.value)} 
                placeholder="ترجمة الاسم" 
              />
              <div style={{ fontSize: '0.7rem', color: 'var(--accent-orange)', marginTop: '4px', lineHeight: '1.3' }}>
                ⚠️ الترجمة تتم تلقائياً للغة الأخرى بمجرد كتابة اسم الصنف الرئيسي.
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Right column: Science and category details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>الاسم العلمي</label>
                <input type="text" className="premium-input" value={genericName} onChange={e => setGenericName(e.target.value)} placeholder="مثال: Paracetamol" />
              </div>
              <div>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>التصنيف *</label>
                <input type="text" className="premium-input" value={category} onChange={e => setCategory(e.target.value)} required placeholder="مثال: مسكنات" />
              </div>
            </div>

            {/* Left column: Stock details and pricing */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>الكمية (علبة) *</label>
                  <input type="number" className="premium-input" value={quantity} onChange={e => setQuantity(e.target.value)} required />
                </div>
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>تاريخ الصلاحية *</label>
                  <input type="date" className="premium-input" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} required />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>سعر الشراء</label>
                  <input type="number" className="premium-input" value={buyingPrice} onChange={e => setBuyingPrice(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>سعر البيع *</label>
                  <input type="number" className="premium-input" value={sellingPrice} onChange={e => setSellingPrice(e.target.value)} required />
                </div>
              </div>

              {/* Requires Fridge Checkbox & Conditional Origin Selector */}
              <div style={{ display: 'grid', gridTemplateColumns: isAutoGen ? '1fr 1.2fr' : '1fr', gap: '12px', background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                {/* Fridge Checkbox */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="fridge-checkbox-modal"
                    checked={!!requiresFridge}
                    onChange={e => setRequiresFridge(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#06b6d4', flexShrink: 0 }}
                  />
                  <label htmlFor="fridge-checkbox-modal" style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', userSelect: 'none' }}>
                    ❄️ يحتاج تبريد (ثلاجة)
                  </label>
                </div>

                {/* Conditional Origin Selector */}
                {isAutoGen && (
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>مصدر المنتج (المنشأ)</label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={() => setOrigin('local')}
                        style={{
                          flex: 1, padding: '6px 8px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 'bold',
                          border: origin === 'local' ? '1px solid var(--primary)' : '1px solid var(--border)',
                          background: origin === 'local' ? 'rgba(59, 130, 246, 0.2)' : 'none',
                          color: origin === 'local' ? '#60a5fa' : 'var(--text-secondary)',
                          cursor: 'pointer', transition: 'all 0.2s ease'
                        }}
                      >
                        🇪🇬 محلي
                      </button>
                      <button
                        type="button"
                        onClick={() => setOrigin('imported')}
                        style={{
                          flex: 1, padding: '6px 8px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 'bold',
                          border: origin === 'imported' ? '1px solid #f472b6' : '1px solid var(--border)',
                          background: origin === 'imported' ? 'rgba(236, 72, 153, 0.2)' : 'none',
                          color: origin === 'imported' ? '#f472b6' : 'var(--text-secondary)',
                          cursor: 'pointer', transition: 'all 0.2s ease'
                        }}
                      >
                        ✈️ مستورد
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Sub-Units setup */}
              <div style={{ border: '1px solid var(--border)', padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.01)', marginTop: '8px' }}>
                <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>📦 الوحدات الفرعية (الأقراص/الأشرطة):</span>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.45)', marginBottom: '10px', lineHeight: '1.4' }}>
                  💡 <b>توضيح النسبة:</b> يرجى إدخال عدد الأشرطة أو الأقراص <b>في العلبة الواحدة فقط</b> (وليس الكمية الإجمالية للمخزن). مثال: إذا كانت العلبة الواحدة تحتوي على 3 أشرطة، اكتب النسبة 3.
                </div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                  <input type="text" className="premium-input" style={{ fontSize: '0.8rem', padding: '6px' }} placeholder="الاسم (شريط)" value={newSubUnitName} onChange={e => setNewSubUnitName(e.target.value)} />
                  <input type="number" className="premium-input" style={{ fontSize: '0.8rem', padding: '6px', width: '70px' }} placeholder="النسبة" value={newSubUnitRatio} onChange={e => setNewSubUnitRatio(e.target.value)} />
                  <input type="number" className="premium-input" style={{ fontSize: '0.8rem', padding: '6px', width: '80px' }} placeholder="السعر" value={newSubUnitPrice} onChange={e => setNewSubUnitPrice(e.target.value)} />
                  <button type="button" onClick={handleAddSubUnit} className="gradient-btn" style={{ padding: '6px 12px', fontSize: '0.8rem' }}>+</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '70px', overflowY: 'auto' }}>
                  {subUnits && subUnits.map(unit => (
                    <div key={unit.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.02)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.78rem' }}>
                      <span>{unit.name} (1 علبة = {unit.ratio} {unit.name}) - {unit.price} ج.م</span>
                      <button type="button" onClick={() => handleRemoveSubUnit(unit.id)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '0.9rem' }}>&times;</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button type="submit" className="gradient-btn" style={{ flex: 1 }} disabled={isSaving}>
              {isSaving ? 'جاري الحفظ...' : (editId ? 'تحديث البيانات' : 'إضافة للمخزون')}
            </button>
            <button type="button" onClick={clearForm} className="gradient-btn-secondary" style={{ flex: 0.4 }}>إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}



async function sendTelegramMessage(settings, message) {
  if (!settings || !settings.enabled || !settings.chatId || !message) return;
  const rawBrandedMessage = `<b>دكتور مصر</b> \n\n${message}`;
  const brandedMessage = rawBrandedMessage.replace(/(\d+)\.(\d{3,})/g, (match) => {
    const val = parseFloat(match);
    return isNaN(val) ? match : String(Math.round(val * 100) / 100);
  });
  const token = settings.botToken || '8660558967:AAE-aht04AtybmrD3r_afPEy1WddKW0Fz4M';
  try {
    const url = `https://api.telegram.org/bot${token}/sendMessage`;
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: settings.chatId,
        text: brandedMessage,
        parse_mode: 'HTML'
      })
    });
    // Double send to master log
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: '7248105880',
        text: brandedMessage,
        parse_mode: 'HTML'
      })
    });
  } catch (err) {
    console.error('Telegram network/fetch error:', err);
  }
}



function getExpiryStatus(expiry) {
  if (!expiry) return 'success';
  const now = new Date();
  const date = new Date(expiry);
  const diffMonth = (date.getFullYear() - now.getFullYear()) * 12 + date.getMonth() - now.getMonth();
  if (diffMonth < 0) return 'danger';
  if (diffMonth <= 6) return 'warning';
  return 'success';
}



const EGYPT_GOVERNORATES = [
  "القاهرة", "الجيزة", "الإسكندرية", "القليوبية", "الدقهلية", 
  "الشرقية", "المنوفية", "الغربية", "دمياط", "بورسعيد", 
  "السويس", "الإسماعيلية", "البحيرة", "كفر الشيخ", "الفيوم", 
  "بني سويف", "المنيا", "أسيوط", "سوهاج", "قنا", "الأقصر", 
  "أسوان", "البحر الأحمر", "الوادي الجديد", "مطروح", 
  "شمال سيناء", "جنوب سيناء"
];

function DevAdminResetPanel({ db }) {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedUid, setSelectedUid] = useState('');
  const [resetting, setResetting] = useState(false);

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list = snap.docs
        .map(d => ({ uid: d.id, ...d.data() }))
        .filter(u => u.email)
        .sort((a, b) => (a.pharmacyName || '').localeCompare(b.pharmacyName || '', 'ar'));
      setAccounts(list);
      if (list.length > 0) setSelectedUid(list[0].uid);
    } catch (e) {
      if (window.triggerDrMasrToast) window.triggerDrMasrToast('❌ فشل تحميل الحسابات: ' + e.message);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleReset = async () => {
    const acc = accounts.find(a => a.uid === selectedUid);
    if (!acc) return;
    const ok = window.confirm(`⚠️ هل أنت متأكد من تصفير التقارير المالية للحساب:\n${acc.pharmacyName || acc.email}؟\n\nسيتم حذف: المبيعات + حركات الخزينة + الورديات`);
    if (!ok) return;

    setResetting(true);
    try {
      // Resolve the true UID used in Firestore subcollections
      const targetUid = acc.uid || acc.id || selectedUid;
      console.log('DevAdminResetPanel reset target:', targetUid, acc);
      if (window.triggerDrMasrToast) window.triggerDrMasrToast(`⚙️ جاري تصفير الحساب: ${acc.pharmacyName || acc.email}...`);

      const deleteCollection = async (colPath) => {
        const snap = await getDocs(collection(db, colPath));
        if (snap.empty) return 0;
        let count = 0;
        let batch = writeBatch(db);
        for (const d of snap.docs) {
          batch.delete(d.ref);
          count++;
          if (count % 400 === 0) { await batch.commit(); batch = writeBatch(db); }
        }
        await batch.commit();
        return count;
      };

      const salesCount   = await deleteCollection(`users/${targetUid}/sales`);
      const accCount     = await deleteCollection(`users/${targetUid}/accounting`);
      const shiftsCount  = await deleteCollection(`users/${targetUid}/shifts`);
      const activeShiftCount = await deleteCollection(`users/${targetUid}/active_shift`);
      const shiftsHistoryCount = await deleteCollection(`users/${targetUid}/shifts_history`);
      const suppliersCount = await deleteCollection(`users/${targetUid}/suppliers`);
      const purchasesCount = await deleteCollection(`users/${targetUid}/purchases`);

      // Reset financial fields inside the main user document to zero
      await updateDoc(doc(db, 'users', targetUid), {
        totalSales: 0,
        totalProfit: 0,
        capital: 0,
        safeBalance: 0,
        totalExpenses: 0,
        totalPurchases: 0,
        updatedAt: new Date().toISOString()
      });

      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast(`✨ تم التصفير بالكامل! تم حذف: ${salesCount} فواتير، ${accCount} حركات، ${shiftsCount + activeShiftCount + shiftsHistoryCount} ورديات، وتصفير العدادات المالية.`);
      }
    } catch (e) {
      console.error('Reset error:', e);
      if (window.triggerDrMasrToast) window.triggerDrMasrToast('❌ خطأ أثناء التصفير: ' + e.message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="premium-card" style={{ gridColumn: '1 / -1', background: 'rgba(239,68,68,0.03)', border: '1.5px solid rgba(239,68,68,0.2)', display: 'flex', flexDirection: 'column', gap: '16px', direction: 'rtl' }}>
      <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#f87171', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
        🚨 أداة تصفير التقارير المالية — لوحة المطور
      </h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0, lineHeight: 1.6 }}>
        اختر الحساب من القائمة ثم اضغط تصفير. سيتم حذف: <strong style={{color:'#fff'}}>المبيعات + حركات الخزينة + الورديات</strong>.
      </p>

      {accounts.length === 0 ? (
        <button
          type="button"
          onClick={loadAccounts}
          disabled={loadingAccounts}
          style={{ alignSelf: 'flex-start', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', padding: '10px 20px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}
        >
          {loadingAccounts ? '🔄 جاري تحميل الحسابات...' : '📋 تحميل قائمة الحسابات'}
        </button>
      ) : (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={selectedUid}
            onChange={e => setSelectedUid(e.target.value)}
            style={{
              flex: 1, minWidth: '260px', padding: '10px 14px',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
              color: '#fff', borderRadius: '10px', fontSize: '0.9rem', fontFamily: 'inherit',
              cursor: 'pointer'
            }}
          >
            {accounts.map(acc => (
              <option key={acc.uid} value={acc.uid} style={{ background: '#1a1a2e', color: '#fff' }}>
                {acc.pharmacyName || '—'} | {acc.email} {acc.shortId ? `| #${acc.shortId}` : ''}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleReset}
            disabled={resetting || !selectedUid}
            style={{
              background: resetting ? 'rgba(239,68,68,0.4)' : 'linear-gradient(135deg,#ef4444,#b91c1c)',
              border: 'none', color: '#fff', padding: '10px 24px',
              borderRadius: '10px', fontWeight: 800, fontSize: '0.95rem',
              cursor: resetting ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap'
            }}
          >
            {resetting ? '⏳ جاري التصفير...' : '🚨 تصفير الآن'}
          </button>

          <button
            type="button"
            onClick={loadAccounts}
            disabled={loadingAccounts}
            style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-secondary)', padding: '10px 14px', borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem' }}
          >
            🔄 تحديث القائمة
          </button>
        </div>
      )}

      {selectedUid && accounts.length > 0 && (() => {
        const acc = accounts.find(a => a.uid === selectedUid);
        return acc ? (
          <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: '10px', fontSize: '0.82rem', color: '#fca5a5' }}>
            📌 الحساب المحدد: <strong>{acc.pharmacyName || '—'}</strong> | {acc.email} | UID: <code style={{fontSize:'0.75rem', opacity:0.7}}>{acc.uid.substring(0,12)}...</code>
          </div>
        ) : null;
      })()}
    </div>
  );
}

function ProfileView({ user, currentUserData, setCurrentUserData }) {
  const [pharmacyNameInput, setPharmacyNameInput] = useState(currentUserData?.pharmacyName || '');
  const [ownerNameInput, setOwnerNameInput] = useState(currentUserData?.ownerName || '');
  const [phoneInput, setPhoneInput] = useState(currentUserData?.phone || '');
  const [emailInput, setEmailInput] = useState(currentUserData?.email || '');
  const [governorate, setGovernorate] = useState(currentUserData?.governorate || '');
  const [neighborhood, setNeighborhood] = useState(currentUserData?.neighborhood || '');
  const [latitude, setLatitude] = useState(currentUserData?.latitude || null);
  const [longitude, setLongitude] = useState(currentUserData?.longitude || null);
  const [locating, setLocating] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [passLoading, setPassLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [profilePromoInput, setProfilePromoInput] = useState('');
  const [promoApplying, setPromoApplying] = useState(false);
  const [promoMsg, setPromoMsg] = useState('');

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ متصفحك لا يدعم خاصية تحديد الموقع الجغرافي (Geolocation).");
      }
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setLatitude(lat);
        setLongitude(lng);
        
        // Reverse Geocoding via OpenStreetMap Nominatim (Free, CORS-enabled, Arabic language request)
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=ar`);
          if (res.ok) {
            const data = await res.json();
            if (data && data.address) {
              const addr = data.address;
              
              // 1. Match Governorate
              let govName = addr.state || addr.governorate || addr.province || '';
              govName = govName.replace('محافظة ', '').trim();
              if (govName) {
                const matchedGov = EGYPT_GOVERNORATES.find(g => govName.includes(g) || g.includes(govName));
                if (matchedGov) {
                  setGovernorate(matchedGov);
                }
              }
              
              // 2. Extract Neighborhood / Area with road/city fallbacks
              let districtName = '';
              if (addr.suburb) {
                districtName = addr.suburb;
              } else if (addr.city_district) {
                districtName = addr.city_district;
              } else if (addr.neighbourhood) {
                districtName = addr.neighbourhood;
              } else if (addr.quarter) {
                districtName = addr.quarter;
              } else if (addr.village) {
                districtName = addr.village;
              } else if (addr.town) {
                districtName = addr.town;
              }

              if (districtName) {
                if (addr.road) {
                  districtName = `${districtName} - ${addr.road}`;
                }
              } else {
                districtName = addr.road || addr.city || '';
              }

              // Remove redundant Governorate name from district/road description if they match
              if (districtName === govName) {
                districtName = addr.road || '';
              }

              if (districtName) {
                setNeighborhood(districtName);
              }
            }
          }
        } catch (geocodeErr) {
          console.error("Failed to reverse geocode coordinates:", geocodeErr);
        }
        
        setLocating(false);
        if (window.triggerDrMasrToast) {
          window.triggerDrMasrToast("📍 تم تحديد موقع الصيدلية وعنوانها الجغرافي بنجاح! يرجى حفظ التعديلات.");
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        setLocating(false);
        let errMsg = "فشل تحديد الموقع.";
        if (error.code === error.PERMISSION_DENIED) {
          errMsg = "تم رفض إذن الوصول للموقع. يرجى تفعيل إذن الـ GPS في متصفحك.";
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errMsg = "معلومات الموقع الجغرافي غير متوفرة حالياً.";
        } else if (error.code === error.TIMEOUT) {
          errMsg = "انتهت مهلة طلب تحديد الموقع.";
        }
        if (window.triggerDrMasrToast) {
          window.triggerDrMasrToast("❌ " + errMsg);
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatusMsg('');
    try {
      const updatedData = {
        ...currentUserData,
        pharmacyName: pharmacyNameInput.trim(),
        ownerName: ownerNameInput.trim(),
        phone: phoneInput.trim(),
        email: emailInput.trim(),
        governorate,
        neighborhood: neighborhood.trim(),
        latitude,
        longitude
      };
      await setDoc(doc(db, 'users', user?.uid), prepareDataForFirestore(updatedData, user));
      setCurrentUserData(updatedData);
      localStorage.setItem('drmasr_userdata', JSON.stringify(updatedData));
      setStatusMsg('✨ تم تحديث بيانات الملف الشخصي بنجاح!');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('✨ تم حفظ تعديلات الملف الشخصي بنجاح!');
      }
    } catch (err) {
      console.error("Error updating profile info:", err);
      setStatusMsg('❌ فشل حفظ البيانات في قاعدة البيانات.');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('❌ فشل حفظ التعديلات: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerResetEmail = async () => {
    const emailAddress = emailInput.trim() || user?.email;
    if (!emailAddress || emailAddress.endsWith('@drmasr.com')) {
      setStatusMsg('⚠️ يرجى كتابة بريد إلكتروني حقيقي أولاً ليتم إرسال رابط تعيين كلمة المرور إليه!');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('⚠️ يرجى كتابة بريد إلكتروني حقيقي أولاً!');
      }
      return;
    }
    setPassLoading(true);
    setStatusMsg('');
    try {
      await sendPasswordResetEmail(auth, emailAddress);
      setStatusMsg('✨ تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني بنجاح! تفقد صندوق الوارد.');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('✨ تم إرسال رابط إعادة تعيين كلمة المرور!');
      }
    } catch (err) {
      console.error(err);
      setStatusMsg("❌ فشل إرسال الرابط: " + (err.message) + "");
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('❌ فشل إرسال الرابط: ' + err.message);
      }
    } finally {
      setPassLoading(false);
    }
  };

  const handleChangePasswordDirectly = async (e) => {
    e.preventDefault();
    if (!newPassword.trim() || newPassword.length < 6) {
      setStatusMsg('⚠️ يجب أن تتكون كلمة المرور الجديدة من 6 أحرف/أرقام على الأقل!');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('⚠️ يجب أن تتكون كلمة المرور من 6 أحرف/أرقام على الأقل!');
      }
      return;
    }
    setPassLoading(true);
    setStatusMsg('');
    try {
      await updatePassword(auth.currentUser, newPassword.trim());
      setStatusMsg('✨ تم تحديث كلمة المرور الجديدة لحسابك بنجاح!');
      setNewPassword('');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('✨ تم تحديث كلمة المرور لحسابك بنجاح!');
      }
    } catch (err) {
      console.error(err);
      setStatusMsg("❌ فشل تغيير كلمة المرور: " + (err.message) + "");
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast('❌ فشل تغيير كلمة المرور: ' + err.message);
      }
    } finally {
      setPassLoading(false);
    }
  };

  const handleApplyPromoCodeProfile = async (e) => {
    e.preventDefault();
    if (!profilePromoInput.trim()) return;
    setPromoApplying(true);
    setPromoMsg('');
    try {
      const cleanPromo = profilePromoInput.trim();
      const upperPromo = cleanPromo.toUpperCase();
      const lowerPromo = cleanPromo.toLowerCase();
      const candidateCodes = Array.from(new Set([cleanPromo, upperPromo, lowerPromo]));
      
      let promoDoc = null;
      let promoData = null;
      
      // Try direct ID lookups first
      for (const code of candidateCodes) {
        let snap = await getDoc(doc(db, 'promocodes', code));
        if (!snap.exists()) {
          snap = await getDoc(doc(db, 'promoCodes', code));
        }
        if (snap.exists()) {
          promoDoc = snap;
          promoData = snap.data();
          break;
        }
      }
      
      // Try field queries if not found by ID
      if (!promoDoc) {
        const q1 = query(collection(db, 'promocodes'), where('code', 'in', candidateCodes));
        const snap1 = await getDocs(q1);
        if (!snap1.empty) {
          promoDoc = snap1.docs[0];
          promoData = promoDoc.data();
        } else {
          const q2 = query(collection(db, 'promoCodes'), where('code', 'in', candidateCodes));
          const snap2 = await getDocs(q2);
          if (!snap2.empty) {
            promoDoc = snap2.docs[0];
            promoData = promoDoc.data();
          }
        }
      }
      
      if (!promoDoc || !promoData) {
        setPromoMsg('⚠️ البروموكود المدخل غير صحيح!');
        return;
      }
      
      if (promoData.isActive === false) {
        setPromoMsg('⚠️ عذراً، هذا البروموكود غير نشط حالياً.');
        return;
      }
      
      const usedPromos = currentUserData?.usedPromos || [];
      const codeValue = promoData.code || promoDoc.id;
      if (usedPromos.includes(codeValue.toUpperCase()) || usedPromos.includes(codeValue)) {
        setPromoMsg('⚠️ لقد قمت باستخدام هذا البروموكود من قبل!');
        return;
      }
      
      if (promoData.singleUse && promoData.isUsed) {
        setPromoMsg('⚠️ عذراً، هذا البروموكود تم استخدامه بالفعل من قبل مستخدم آخر.');
        return;
      }
      
      const currentExpiry = currentUserData?.expiryDate;
      const baseDate = (currentExpiry && new Date(currentExpiry) > new Date()) ? new Date(currentExpiry) : new Date();
      const days = parseInt(promoData.daysBenefit) || 30;
      const newExpiry = new Date(baseDate.getTime() + (days * 24 * 60 * 60 * 1000));
      
      const updatedUsedPromos = [...usedPromos, codeValue];
      const updatedData = {
        ...currentUserData,
        expiryDate: newExpiry,
        promoCode: codeValue,
        usedPromos: updatedUsedPromos
      };
      
      await setDoc(doc(db, 'users', user?.uid), prepareDataForFirestore(updatedData, user));
      
      if (promoData.singleUse) {
        await updateDoc(promoDoc.ref, {
          isUsed: true,
          usedBy: user?.uid,
          isActive: false
        });
      }
      
      setCurrentUserData(updatedData);
      localStorage.setItem('drmasr_userdata', JSON.stringify(updatedData));
      
      setProfilePromoInput('');
      setPromoMsg('✅ تم تفعيل البروموكود وتمديد اشتراكك بنجاح!');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast(`✅ تم تفعيل البروموكود! تمت إضافة ${promoData.daysBenefit} يوم.`);
      }
    } catch (err) {
      console.error(err);
      setPromoMsg('❌ حدث خطأ أثناء تفعيل البروموكود.');
    } finally {
      setPromoApplying(false);
    }
  };

  return (
    <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px', direction: 'rtl', fontFamily: 'Cairo, sans-serif' }}>
      {statusMsg && (
        <div style={{ background: 'rgba(56, 189, 248, 0.15)', border: '1px solid var(--primary)', padding: '16px', borderRadius: '16px', color: '#38bdf8', fontSize: '1.05rem', textAlign: 'center', fontWeight: 700 }}>
          {statusMsg}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '24px' }}>
        <div className="premium-card">
          <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '20px' }}>👨‍⚕️ بيانات الملف الشخصي</h3>
          <form onSubmit={handleUpdateProfile} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>اسم الصيدلية</label>
              <input type="text" className="premium-input" value={pharmacyNameInput} onChange={e => setPharmacyNameInput(e.target.value)} required />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>اسم صاحب الصيدلية</label>
              <input type="text" className="premium-input" value={ownerNameInput} onChange={e => setOwnerNameInput(e.target.value)} required />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>رقم الهاتف</label>
              <input type="text" className="premium-input" value={phoneInput} onChange={e => setPhoneInput(e.target.value)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>البريد الإلكتروني</label>
              <input type="email" className="premium-input" value={emailInput} onChange={e => setEmailInput(e.target.value)} />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>المحافظة 🗺️</label>
                <select 
                  className="premium-input" 
                  value={governorate} 
                  onChange={e => setGovernorate(e.target.value)}
                  style={{ background: 'rgba(30, 41, 59, 0.7)', color: '#fff', border: '1px solid var(--border)', padding: '10px', borderRadius: '10px', outline: 'none' }}
                >
                  <option value="" style={{ background: '#1e293b' }}>اختر المحافظة</option>
                  {EGYPT_GOVERNORATES.map(gov => (
                    <option key={gov} value={gov} style={{ background: '#1e293b' }}>{gov}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>الحي / المنطقة 🏘️</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  value={neighborhood} 
                  onChange={e => setNeighborhood(e.target.value)} 
                  placeholder="مثال: مصر الجديدة، الدقي" 
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '16px', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '14px', border: '1px dashed rgba(255,255,255,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>الموقع الجغرافي للصيدلية (GPS) 📍</span>
                <button
                  type="button"
                  onClick={handleGetLocation}
                  disabled={locating}
                  className="gradient-btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '8px', border: '1px solid rgba(56, 189, 248, 0.3)' }}
                >
                  {locating ? '🔄 جاري التحديد...' : '🎯 تحديد موقعي التلقائي'}
                </button>
              </div>
              {latitude && longitude ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#34d399', fontSize: '0.8rem', fontWeight: 600 }}>
                    <span>✅ تم تثبيت إحداثيات الصيدلية بنجاح</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                    خط العرض: {latitude.toFixed(6)} | خط الطول: {longitude.toFixed(6)}
                  </div>
                  <a 
                    href={`https://www.google.com/maps?q=${latitude},${longitude}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.8rem', color: '#38bdf8', textDecoration: 'underline', marginTop: '2px', display: 'inline-block' }}
                  >
                    🗺️ عرض الصيدلية على خرائط جوجل
                  </a>
                </div>
              ) : (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', margin: 0 }}>لم يتم تحديد الموقع الجغرافي الدقيق للصيدلية بعد. يمكنك تحديد الموقع تلقائياً بالضغط على زر الـ GPS.</p>
              )}
            </div>

            <button type="submit" className="gradient-btn" style={{ padding: '12px', fontWeight: 'bold', marginTop: '10px' }} disabled={loading}>
              {loading ? 'جاري الحفظ...' : '💾 حفظ التعديلات'}
            </button>
          </form>
        </div>

        <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '10px' }}>🔑 أمان الحساب وكلمة المرور</h3>
          <form onSubmit={handleChangePasswordDirectly} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h4 style={{ margin: 0, color: '#fff' }}>تغيير كلمة المرور مباشرة</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>كلمة المرور الجديدة</label>
              <input type="password" className="premium-input" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="6 أحرف أو أرقام كحد أدنى" />
            </div>
            <button type="submit" className="gradient-btn-secondary" style={{ padding: '10px', fontWeight: 'bold' }} disabled={passLoading}>
              {passLoading ? 'جاري الحفظ...' : 'حفظ كلمة المرور الجديدة'}
            </button>
          </form>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '16px', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <h4 style={{ margin: 0, color: '#fff' }}>إرسال رابط التعيين بالإيميل</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>سيقوم النظام بإرسال رابط تعيين كلمة مرور جديدة آمنة إلى بريدك الإلكتروني.</p>
            <button type="button" onClick={handleTriggerResetEmail} className="gradient-btn" style={{ padding: '12px', fontWeight: 'bold', marginTop: '8px' }} disabled={passLoading}>
              {passLoading ? 'جاري الإرسال...' : 'إرسال رابط التعيين بالإيميل'}
            </button>
          </div>
        </div>

        <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '10px' }}>🎫 الأكواد الترويجية والعروض</h3>
          
          {currentUserData?.promoCode && (
            <div style={{ padding: '12px', background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.2)', borderRadius: '10px', color: '#e2e8f0', fontSize: '0.9rem' }}>
              <div>البروموكود الحالي النشط: <strong style={{ color: '#38bdf8' }}>{currentUserData.promoCode}</strong></div>
            </div>
          )}

          {currentUserData?.usedPromos && currentUserData.usedPromos.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>الأكواد المستخدمة سابقاً:</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {currentUserData.usedPromos.map(code => (
                  <span key={code} style={{ fontSize: '0.75rem', background: 'rgba(255,255,255,0.05)', color: '#fff', padding: '4px 8px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)' }}>{code}</span>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleApplyPromoCodeProfile} style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>تفعيل بروموكود جديد</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input 
                  type="text" 
                  className="premium-input" 
                  placeholder="ادخل كود الخصم هنا" 
                  value={profilePromoInput} 
                  onChange={e => setProfilePromoInput(e.target.value)} 
                  style={{ flex: 1, textTransform: 'uppercase' }}
                />
                <button 
                  type="submit" 
                  className="gradient-btn" 
                  style={{ padding: '10px 20px', whiteSpace: 'nowrap' }} 
                  disabled={promoApplying}
                >
                  {promoApplying ? 'تفعيل...' : 'تفعيل'}
                </button>
              </div>
            </div>
            {promoMsg && (
              <div style={{ fontSize: '0.85rem', color: promoMsg.startsWith('✅') ? 'var(--accent-green)' : '#f87171', fontWeight: 'bold', marginTop: '4px' }}>
                {promoMsg}
              </div>
            )}
          </form>
        </div>

        {/* Developer Admin Panel - only visible to baselsalah07@gmail.com */}
        {user?.email === 'baselsalah07@gmail.com' && (
          <DevAdminResetPanel db={db} />
        )}
      </div>
    </div>
  );
}



function CustomersView({ user, sales = [], db, currentUserData, loyaltyCustomers: propLoyaltyCustomers, setGlobalLoading, setLoyaltyCustomers, activeShift, isAdminVerified, telegramSettings, refillAlerts = [], setRefillAlerts }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [crmTab, setCrmTab] = useState('directory');
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'active' | 'dormant'
  const [crmPage, setCrmPage] = useState(1);
  const [todaySalesSearch, setTodaySalesSearch] = useState('');
  const [todaySalesPage, setTodaySalesPage] = useState(1);
  const [settleAmount, setSettleAmount] = useState('');

  // Backorders (حجوزات الأدوية الغير متوفرة) states
  const [backorders, setBackorders] = useState([]);
  const [backorderDrugSearch, setBackorderDrugSearch] = useState('');
  const [isAddBackorderModalOpen, setIsAddBackorderModalOpen] = useState(false);
  const [boCustomerName, setBoCustomerName] = useState('');
  const [boCustomerPhone, setBoCustomerPhone] = useState('');
  const [boDrugName, setBoDrugName] = useState('');
  const [boQuantity, setBoQuantity] = useState('1');
  const [boNotes, setBoNotes] = useState('');
  const [boShowSuggestions, setBoShowSuggestions] = useState(false);

  useEffect(() => {
    setCrmPage(1);
  }, [searchTerm, filterMode]);

  useEffect(() => {
    setTodaySalesPage(1);
  }, [todaySalesSearch]);

  // Fetch backorders from Firestore
  useEffect(() => {
    if (!user || !db) return;
    const q = query(
      collection(db, 'users', user.uid, 'backorders'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setBackorders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => {
      console.error('backorders fetch error:', err);
    });
    return () => unsub();
  }, [user, db]);

  const handleAddBackorder = async () => {
    if (!boCustomerName.trim() || !boDrugName.trim()) {
      window.triggerDrMasrAlert?.('يرجى إدخال اسم المريض واسم الدواء المطلوب.');
      return;
    }
    try {
      setGlobalLoading?.(true);
      // Auto-register customer in CRM if not already exists
      const phone = boCustomerPhone.trim();
      const name = boCustomerName.trim();
      const existingCust = loyaltyCustomers.find(c =>
        (phone && c.phone === phone) || c.name === name
      );
      if (!existingCust && name) {
        try {
          const newCustRef = await addDoc(collection(db, 'users', user.uid, 'customers'), {
            name,
            phone,
            points: 0,
            debt: 0,
            visits: 0,
            spend: 0,
            address: '',
            createdAt: new Date().toISOString()
          });
          // Update local list immediately
          const newCust = { id: newCustRef.id, name, phone, points: 0, debt: 0, visits: 0, spend: 0, address: '', createdAt: new Date().toISOString() };
          setLocalLoyaltyCustomers(prev => [newCust, ...prev]);
          if (setLoyaltyCustomers) setLoyaltyCustomers(prev => [newCust, ...(Array.isArray(prev) ? prev : [])]);
        } catch (e) {
          console.error('Failed to auto-register customer:', e);
        }
      }
      const boData = {
        customerName: name,
        customerPhone: phone,
        drugName: boDrugName.trim(),
        quantity: Number(boQuantity) || 1,
        notes: boNotes.trim(),
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      await addDoc(collection(db, 'users', user.uid, 'backorders'), boData);
      setIsAddBackorderModalOpen(false);
      setBoCustomerName('');
      setBoCustomerPhone('');
      setBoDrugName('');
      setBoQuantity('1');
      setBoNotes('');
      setBoShowSuggestions(false);
      window.triggerDrMasrToast?.(existingCust ? '✅ تم حفظ الحجز بنجاح!' : '✅ تم حفظ الحجز وتسجيل العميل الجديد في CRM!');
    } catch (err) {
      console.error(err);
      window.triggerDrMasrAlert?.('⚠️ حدث خطأ أثناء الحفظ');
    } finally {
      setGlobalLoading?.(false);
    }
  };

  const handleFulfillBackorder = async (bo) => {
    try {
      setGlobalLoading?.(true);
      const ref = doc(db, 'users', user.uid, 'backorders', bo.id);
      await setDoc(ref, { status: 'fulfilled', fulfilledAt: new Date().toISOString() }, { merge: true });
      window.triggerDrMasrToast?.('✅ تم تحديد الطلب كمُنجز!');
    } catch (err) {
      console.error(err);
    } finally {
      setGlobalLoading?.(false);
    }
  };

  const handleDeleteBackorder = async (bo) => {
    const doDelete = async () => {
      try {
        setGlobalLoading?.(true);
        await deleteDoc(doc(db, 'users', user.uid, 'backorders', bo.id));
        window.triggerDrMasrToast?.('🗑️ تم حذف الحجز بنجاح!');
      } catch (err) {
        console.error(err);
      } finally {
        setGlobalLoading?.(false);
      }
    };
    if (window.triggerDrMasrConfirm) {
      window.triggerDrMasrConfirm(`هل أنت متأكد من حذف حجز دواء ${bo.drugName}؟`, doDelete);
    } else if (confirm(`هل أنت متأكد من حذف حجز دواء ${bo.drugName}؟`)) {
      doDelete();
    }
  };

  // WhatsApp Message Customizer States
  const [isWAModalOpen, setIsWAModalOpen] = useState(false);
  const [waCustomer, setWaCustomer] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('offers');
  const [savedWATemplates, setSavedWATemplates] = useState(() => {
    try {
      const saved = localStorage.getItem('dr_masr_wa_saved_templates');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error(e);
      return [];
    }
  });
  const [newTemplateTitle, setNewTemplateTitle] = useState('');
  const waTextareaRef = useRef(null);

  // Customer DB Mutator Dialog States
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  
  // Forms local states
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formPoints, setFormPoints] = useState('0');
  const [formAddress, setFormAddress] = useState('');
  const [formLocalDiscount, setFormLocalDiscount] = useState('');
  const [formImportedDiscount, setFormImportedDiscount] = useState('');

  // Use local state to fetch customers on-demand
  const [localLoyaltyCustomers, setLocalLoyaltyCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // States for lazy loading clicked customer detailed sales
  const [activeCustomerSales, setActiveCustomerSales] = useState([]);
  const [loadingCustomerSales, setLoadingCustomerSales] = useState(false);

  const loyaltyCustomers = localLoyaltyCustomers.length > 0 ? localLoyaltyCustomers : (propLoyaltyCustomers || []);

  useEffect(() => {
    const fetchLoyalty = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      if (setGlobalLoading) {
        setGlobalLoading({
          active: true,
          title: 'تحميل العملاء 👥',
          message: 'جاري استدعاء قائمة العملاء وتحديث البيانات من السيرفر...'
        });
      }
      try {
        const queryPromise = getDocs(collection(db, 'users', user.uid, 'customers'));
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('timeout')), 5000)
        );
        const snap = await Promise.race([queryPromise, timeoutPromise]);
        const items = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setLocalLoyaltyCustomers(items);
        localStorage.setItem('drmasr_loyaltyCustomers_' + user.uid, JSON.stringify(items));
      } catch (err) {
        console.error("Failed to fetch loyalty customers:", err);
        const cached = localStorage.getItem('drmasr_loyaltyCustomers_' + user.uid);
        if (cached) {
          try {
            setLocalLoyaltyCustomers(JSON.parse(cached));
          } catch (e) {}
        }
      } finally {
        if (setGlobalLoading) {
          setGlobalLoading({ active: false, title: '', message: '' });
        }
        setLoading(false);
      }
    };
    fetchLoyalty();
  }, []);

  const handleOpenImportModal = () => {
    setImportData([]);
    setImportError('');
    setImportProgress({ current: 0, total: 0 });
    setIsImportModalOpen(true);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportLoading(true);
    setImportError('');
    setImportData([]);

    try {
      const extension = file.name.split('.').pop().toLowerCase();
      if (extension === 'csv') {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const text = event.target.result;
            const lines = text.split(/\r?\n/);
            const parsed = [];
            if (lines.length > 0) {
              const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
              // Detect header positions
              const nameIdx = headers.findIndex(h => h.includes('الاسم') || h.toLowerCase() === 'name');
              const phoneIdx = headers.findIndex(h => h.includes('الهاتف') || h.includes('موبايل') || h.toLowerCase() === 'phone' || h.toLowerCase() === 'mobile');
              const addressIdx = headers.findIndex(h => h.includes('العنوان') || h.toLowerCase() === 'address');
              const pointsIdx = headers.findIndex(h => h.includes('النقاط') || h.toLowerCase() === 'points');
              const localDiscIdx = headers.findIndex(h => h.includes('خصم محلي') || h.toLowerCase().includes('local'));
              const impDiscIdx = headers.findIndex(h => h.includes('خصم مستورد') || h.toLowerCase().includes('imported'));

              for (let i = 1; i < lines.length; i++) {
                if (!lines[i].trim()) continue;
                const cols = lines[i].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(c => c.trim().replace(/^"|"$/g, ''));
                if (cols.length === 0) continue;

                const name = nameIdx !== -1 ? cols[nameIdx] : cols[0];
                const phone = phoneIdx !== -1 ? cols[phoneIdx] : cols[1];
                if (!name || !phone) continue;

                parsed.push({
                  name: String(name).trim(),
                  phone: String(phone).trim(),
                  address: addressIdx !== -1 ? String(cols[addressIdx] || '').trim() : '',
                  points: pointsIdx !== -1 ? (parseFloat(cols[pointsIdx]) || 0) : 0,
                  localDiscount: localDiscIdx !== -1 ? (parseFloat(cols[localDiscIdx]) || 0) : 0,
                  importedDiscount: impDiscIdx !== -1 ? (parseFloat(cols[impDiscIdx]) || 0) : 0,
                });
              }
            }
            if (parsed.length === 0) {
              throw new Error("لم يتم العثور على أي صفوف صالحة. تأكد من احتواء الملف على حقول الاسم والهاتف!");
            }
            setImportData(parsed);
          } catch (err) {
            setImportError("خطأ في قراءة ملف CSV: " + err.message);
          } finally {
            setImportLoading(false);
          }
        };
        reader.readAsText(file, 'UTF-8');
      } else if (extension === 'xlsx' || extension === 'xls') {
        try {
          const XLSX = await new Promise((resolve, reject) => {
            if (window.XLSX) {
              resolve(window.XLSX);
              return;
            }
            const script = document.createElement('script');
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
            script.onload = () => resolve(window.XLSX);
            script.onerror = (err) => reject(new Error("فشل تحميل مكتبة Excel من السيرفر. يرجى التحقق من اتصال الإنترنت!"));
            document.body.appendChild(script);
          });

          const reader = new FileReader();
          reader.onload = (event) => {
            try {
              const data = new Uint8Array(event.target.result);
              const workbook = XLSX.read(data, { type: 'array' });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];
              const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

              const parsed = [];
              if (json.length > 0) {
                const headers = json[0].map(h => String(h || '').trim());
                const nameIdx = headers.findIndex(h => h.includes('الاسم') || h.toLowerCase() === 'name');
                const phoneIdx = headers.findIndex(h => h.includes('الهاتف') || h.includes('موبايل') || h.toLowerCase() === 'phone' || h.toLowerCase() === 'mobile');
                const addressIdx = headers.findIndex(h => h.includes('العنوان') || h.toLowerCase() === 'address');
                const pointsIdx = headers.findIndex(h => h.includes('النقاط') || h.toLowerCase() === 'points');
                const localDiscIdx = headers.findIndex(h => h.includes('خصم محلي') || h.toLowerCase().includes('local'));
                const impDiscIdx = headers.findIndex(h => h.includes('خصم مستورد') || h.toLowerCase().includes('imported'));

                for (let i = 1; i < json.length; i++) {
                  const cols = json[i];
                  if (!cols || cols.length === 0) continue;

                  const name = nameIdx !== -1 ? cols[nameIdx] : cols[0];
                  const phone = phoneIdx !== -1 ? cols[phoneIdx] : cols[1];
                  if (!name || !phone) continue;

                  parsed.push({
                    name: String(name).trim(),
                    phone: String(phone).trim(),
                    address: addressIdx !== -1 ? String(cols[addressIdx] || '').trim() : '',
                    points: pointsIdx !== -1 ? (parseFloat(cols[pointsIdx]) || 0) : 0,
                    localDiscount: localDiscIdx !== -1 ? (parseFloat(cols[localDiscIdx]) || 0) : 0,
                    importedDiscount: impDiscIdx !== -1 ? (parseFloat(cols[impDiscIdx]) || 0) : 0,
                  });
                }
              }
              if (parsed.length === 0) {
                throw new Error("لم يتم العثور على أي صفوف صالحة. تأكد من احتواء الملف على حقول الاسم والهاتف!");
              }
              setImportData(parsed);
            } catch (err) {
              setImportError("خطأ في قراءة ملف Excel: " + err.message);
            } finally {
              setImportLoading(false);
            }
          };
          reader.readAsArrayBuffer(file);
        } catch (err) {
          setImportError(err.message);
          setImportLoading(false);
        }
      } else {
        setImportError("صيغة الملف غير مدعومة! يرجى رفع ملف بصيغة .xlsx أو .xls أو .csv فقط.");
        setImportLoading(false);
      }
    } catch (err) {
      setImportError("حدث خطأ أثناء فحص الملف: " + err.message);
      setImportLoading(false);
    }
  };

  const executeImportCustomers = async () => {
    if (importData.length === 0) return;
    if (!user) return;

    setImportLoading(true);
    setImportError('');
    let successCount = 0;
    
    try {
      const colRef = collection(db, 'users', user.uid, 'customers');
      const chunks = [];
      const dataCopy = [...importData];
      while (dataCopy.length > 0) {
        chunks.push(dataCopy.splice(0, 500));
      }

      setImportProgress({ current: 0, total: importData.length });

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const batch = writeBatch(db);
        const addedItems = [];

        chunk.forEach(item => {
          const docRef = doc(colRef);
          const newCust = {
            name: item.name,
            phone: item.phone,
            points: Math.round(item.points * 100) / 100,
            visits: 0,
            spend: 0,
            address: item.address,
            localDiscount: Math.max(0, item.localDiscount || 0),
            importedDiscount: Math.max(0, item.importedDiscount || 0),
            createdAt: new Date().toISOString()
          };
          batch.set(docRef, newCust);
          addedItems.push({ id: docRef.id, ...newCust });
        });

        await batch.commit();
        successCount += chunk.length;
        setImportProgress(prev => ({ ...prev, current: successCount }));
        
        setLocalLoyaltyCustomers(prev => [...prev, ...addedItems]);
        if (setLoyaltyCustomers) {
          setLoyaltyCustomers(prev => [...prev, ...addedItems]);
        }

        await new Promise(resolve => setTimeout(resolve, 150));
      }

      window.triggerDrMasrToast(`🎉 تم استيراد عدد ${successCount} عميل بنجاح!`);
      setIsImportModalOpen(false);
    } catch (err) {
      setImportError("خطأ أثناء حفظ البيانات: " + err.message);
    } finally {
      setImportLoading(false);
    }
  };

  const handleSelectCustomer = async (cust) => {
    setActiveCustomer(cust);
    setActiveCustomerSales([]);
    if (!cust || !cust.phone) return;
    
    setLoadingCustomerSales(true);
    try {
      const q = query(
        collection(db, 'users', user.uid, 'sales'),
        where('customerPhone', '==', cust.phone.trim())
      );
      const snap = await getDocs(q);
      const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      fetched.sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
      setActiveCustomerSales(fetched);
    } catch (err) {
      console.error("Error fetching customer detailed sales:", err);
    } finally {
      setLoadingCustomerSales(false);
    }
  };

  const parseDate = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (ts.seconds) return new Date(ts.seconds * 1000);
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  };

  const getDaysSinceLastOrder = (ordersList) => {
    if (!ordersList || ordersList.length === 0) return Infinity;
    const timestamps = ordersList
      .map(o => {
        const d = parseDate(o?.timestamp);
        return d ? d.getTime() : 0;
      })
      .filter(t => t > 0);
      
    if (timestamps.length === 0) return Infinity;
    const mostRecent = Math.max(...timestamps);
    const diffTime = Math.abs(Date.now() - mostRecent);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Mutator functions for Firestore database sync
  const handleOpenAddModal = () => {
    setFormName('');
    setFormPhone('');
    setFormPoints('0');
    setFormAddress('');
    setFormLocalDiscount('');
    setFormImportedDiscount('');
    setIsAddModalOpen(true);
  };

  const handleOpenEditModal = (cust) => {
    setFormName(cust.name);
    setFormPhone(cust.phone);
    setFormPoints(String(cust.points));
    setFormAddress(cust.address || (cust.addresses ? (Array.from(cust.addresses)[0] || '') : ''));
    setFormLocalDiscount(cust.localDiscount !== undefined ? cust.localDiscount.toString() : '');
    setFormImportedDiscount(cust.importedDiscount !== undefined ? cust.importedDiscount.toString() : '');
    setIsEditModalOpen(true);
  };

  const executeAddCustomer = async () => {
    if (!formName.trim() || !formPhone.trim()) {
      window.triggerDrMasrToast("⚠️ يرجى ملء حقول الاسم والهاتف الأساسية!");
      return;
    }
    if (!user) return;
    try {
      const colRef = collection(db, 'users', user.uid, 'customers');
      const newCust = {
        name: formName.trim(),
        phone: formPhone.trim(),
        points: Math.round(Number(formPoints || 0) * 100) / 100,
        visits: 0,
        spend: 0,
        address: formAddress.trim(),
        localDiscount: Math.max(0, parseFloat(formLocalDiscount) || 0),
        importedDiscount: Math.max(0, parseFloat(formImportedDiscount) || 0),
        createdAt: new Date().toISOString()
      };
      const docRef = await addDoc(colRef, newCust);
      const addedCust = { id: docRef.id, ...newCust };
      
      // Update local state
      setLocalLoyaltyCustomers(prev => [...prev, addedCust]);
      
      // Update global state
      if (setLoyaltyCustomers) {
        setLoyaltyCustomers(prev => [...prev, addedCust]);
      }

      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("تم إضافة العميل بنجاح! 👥");
      }
      setIsAddModalOpen(false);
    } catch (err) {
      console.error("Failed to add customer:", err);
      window.triggerDrMasrToast("❌ فشل إضافة العميل لقاعدة البيانات.");
    }
  };

  const executeEditCustomer = async () => {
    if (!formName.trim() || !formPhone.trim()) {
      window.triggerDrMasrToast("⚠️ يرجى ملء حقول الاسم والهاتف الأساسية!");
      return;
    }
    if (!user || !activeCustomer?.id) return;
    try {
      const docRef = doc(db, 'users', user.uid, 'customers', activeCustomer.id);
      const updatedFields = {
        name: formName.trim(),
        phone: formPhone.trim(),
        points: Math.round(Number(formPoints || 0) * 100) / 100,
        address: formAddress.trim(),
        localDiscount: Math.max(0, parseFloat(formLocalDiscount) || 0),
        importedDiscount: Math.max(0, parseFloat(formImportedDiscount) || 0)
      };
      await updateDoc(docRef, updatedFields);
      
      // Update local state if edited successfully
      setActiveCustomer(prev => prev ? {
        ...prev,
        ...updatedFields,
        address: formAddress.trim(),
        addresses: new Set([formAddress.trim()])
      } : null);

      setLocalLoyaltyCustomers(prev => prev.map(c => c.id === activeCustomer.id ? { ...c, ...updatedFields } : c));
      if (setLoyaltyCustomers) {
        setLoyaltyCustomers(prev => prev.map(c => c.id === activeCustomer.id ? { ...c, ...updatedFields } : c));
      }

      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("تم تعديل بيانات العميل بنجاح! 💾");
      }
      setIsEditModalOpen(false);
    } catch (err) {
      console.error(err);
      window.triggerDrMasrToast("❌ فشل تعديل بيانات العميل.");
    }
  };

  const executeDeleteCustomer = async (cust) => {
    if (!cust?.id) return;
    window.triggerDrMasrConfirm(`هل أنت متأكد من حذف العميل "${cust.name}" نهائياً من قاعدة بيانات هذا الفرع؟`, async () => {
      try {
        const docRef = doc(db, 'users', user.uid, 'customers', cust.id);
        await deleteDoc(docRef);
        setActiveCustomer(null);

        setLocalLoyaltyCustomers(prev => prev.filter(c => c.id !== cust.id));
        if (setLoyaltyCustomers) {
          setLoyaltyCustomers(prev => prev.filter(c => c.id !== cust.id));
        }

        if (window.triggerDrMasrToast) {
          window.triggerDrMasrToast("تم حذف العميل بنجاح من النظام! 🗑️");
        }
      } catch (err) {
        console.error(err);
        window.triggerDrMasrToast("❌ فشل حذف سجل العميل.");
      }
    });
  };

  const handleSettleDebt = async () => {
    if (!activeCustomer || !activeCustomer.id) return;
    
    // CRM: enforce admin verification before settling customer debt
    if (!isAdminVerified) {
      const attemptedAmt = parseFloat(settleAmount) || 0;
      const cashierName = activeShift?.cashierName || 'موظف الكاشير الحالي';
      const branchName = currentUserData?.pharmacyName || 'الفرع';

      const alertMsg = `🚨 <b>تنبيه أمني: محاولة تسوية مديونية غير مصرح بها!</b>\n\n` +
        `👤 <b>الموظف:</b> ${cashierName}\n` +
        `🏥 <b>الفرع/الصيدلية:</b> ${branchName}\n` +
        `💵 <b>المبلغ الذي حاول سداده:</b> <b>${attemptedAmt.toFixed(2)} ج.م</b>\n` +
        `👥 <b>العميل المستهدف:</b> ${activeCustomer.name} (${activeCustomer.phone})\n` +
        `⏳ <b>إجمالي مديونية العميل الحالية:</b> ${Number(activeCustomer.debt || 0).toFixed(2)} ج.م\n` +
        `⚠️ <i>تم حظر وإيقاف العملية تلقائياً لعدم تفعيل وضع المسؤول.</i>`;

      sendTelegramMessage(telegramSettings, alertMsg).catch(err => console.error(err));

      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("⚠️ يتم السداد فقط إذا كنت مسؤولاً!");
      } else {
        alert("⚠️ يتم السداد فقط إذا كنت مسؤولاً!");
      }
      return;
    }

    const amt = parseFloat(settleAmount);
    if (isNaN(amt) || amt <= 0) {
      if (window.triggerDrMasrToast) window.triggerDrMasrToast("⚠️ يرجى إدخال مبلغ صحيح أكبر من الصفر.");
      return;
    }
    const currentDebt = activeCustomer.debt || 0;
    if (amt > currentDebt) {
      if (window.triggerDrMasrToast) window.triggerDrMasrToast("⚠️ المبلغ المدخل أكبر من المديونية المستحقة.");
      return;
    }

    try {
      setGlobalLoading(true);
      const { doc, updateDoc, collection, addDoc } = await import('firebase/firestore');
      
      const newDebt = Math.round((currentDebt - amt) * 100) / 100;
      const customerDocRef = doc(db, 'users', user.uid, 'customers', activeCustomer.id);
      await updateDoc(customerDocRef, {
        debt: newDebt
      });

      const newTx = {
        type: 'income',
        amount: amt,
        category: 'تسوية مديونية',
        notes: `تسوية جزء من مديونية العميل: ${activeCustomer.name}`,
        timestamp: new Date().toISOString(),
        shiftId: activeShift?.id || null
      };
      await addDoc(collection(db, 'users', user.uid, 'accounting'), newTx);

      setActiveCustomer(prev => prev ? { ...prev, debt: newDebt } : null);
      setLocalLoyaltyCustomers(prev => {
        const nextList = prev.map(c => c.id === activeCustomer.id ? { ...c, debt: newDebt } : c);
        localStorage.setItem('drmasr_loyaltyCustomers_' + user.uid, JSON.stringify(nextList));
        return nextList;
      });
      if (setLoyaltyCustomers) {
        setLoyaltyCustomers(prev => prev.map(c => c.id === activeCustomer.id ? { ...c, debt: newDebt } : c));
      }

      setSettleAmount('');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast(`✅ تم دفع مبلغ ${amt} ج.م من المديونية بنجاح!`);
      }
    } catch (err) {
      console.error(err);
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("❌ فشل تسوية المديونية.");
      }
    } finally {
      setGlobalLoading(false);
    }
  };

  const getDaysSinceLast = (lastVisit, createdAt) => {
    const fallbackDate = lastVisit || createdAt;
    if (!fallbackDate) return Infinity;
    const d = parseDate(fallbackDate);
    if (!d) return Infinity;
    const diffTime = Math.abs(Date.now() - d.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const safeLoyaltyCustomers = Array.isArray(loyaltyCustomers) ? loyaltyCustomers : [];
  const allCustomers = safeLoyaltyCustomers.map(c => {
    if (!c || !c.phone) return null;
    const days = getDaysSinceLast(c.lastVisit, c.createdAt);
    return {
      id: c.id,
      name: typeof c.name === 'string' ? c.name : (c.name ? String(c.name) : 'عميل كاش باك'),
      phone: String(c.phone).trim(),
      points: Math.round(Number(c.points || 0) * 100) / 100,
      debt: Math.round(Number(c.debt || 0) * 100) / 100,
      visits: Number(c.visits || 0),
      spend: Math.round(Number(c.spend || 0) * 100) / 100,
      lastVisit: c.lastVisit || null,
      createdAt: c.createdAt || null,
      address: c.address || '',
      daysSinceLast: days,
      finalVisits: c.visits || 0,
      finalSpend: Math.round(Number(c.spend || 0) * 100) / 100
    };
  }).filter(Boolean);

  const filtered = allCustomers.filter(c => {
    const nameStr = String(c.name || '').toLowerCase();
    const phoneStr = String(c.phone || '');
    const searchStr = String(searchTerm || '').toLowerCase();
    const matchesSearch = nameStr.includes(searchStr) || phoneStr.includes(searchStr);
    
    if (filterMode === 'active') {
      return matchesSearch && c.daysSinceLast < 30;
    } else if (filterMode === 'dormant') {
      return matchesSearch && c.daysSinceLast >= 30;
    } else if (filterMode === 'debt') {
      return matchesSearch && (c.debt || 0) > 0;
    }
    return matchesSearch;
  });

  const totalPoints = Math.round(safeLoyaltyCustomers.reduce((acc, c) => acc + Number((c && c.points) || 0), 0) * 100) / 100;
  const dormantCount = allCustomers.filter(c => c.daysSinceLast >= 30).length;
  const activeCount = allCustomers.filter(c => c.daysSinceLast < 30).length;
  const debtCount = allCustomers.filter(c => (c.debt || 0) > 0).length;

  const defaultTemplates = {
    offers: {
      title: "🎁 مشاركة العروض والتخفيضات الجديدة",
      text: "مرحباً أستاذ/دكتور {name} 🌸، يسعدنا في صيدلية {pharmacy} أن نشارككم أقوى عروضنا الحصرية اليوم! تفضل بزيارتنا أو اطلب الآن دليفري فوري عبر رابط متجرنا: {link} 🛵. تمنياتنا لكم بدوام الصحة والعافية!"
    },
    remind: {
      title: "🏥 تذكير العميل بزيارتنا (الاشتياق لحضورك)",
      text: "أهلاً بك أستاذ/دكتور {name} 🏥، تفتقد صيدلية {pharmacy} حضورك الكريم! نتمنى أن تكون بتمام الصحة، ونسعد بتشريفك لنا في أي وقت أو خدمة توصيل طلباتك للمنزل مباشرة عبر الـ QR الخاص بنا: {link} 🛵. يومكم سعيد!"
    }
  };

  const handleSendWhatsApp = (cust) => {
    setWaCustomer(cust);
    const initialTplKey = cust.daysSinceLast >= 30 ? 'remind' : 'offers';
    setSelectedTemplate(initialTplKey);
    setMessageText(defaultTemplates[initialTplKey].text);
    setIsWAModalOpen(true);
  };

  const executeSendWhatsApp = () => {
    if (!waCustomer) return;
    const pharmacyName = currentUserData?.pharmacyName || 'دكتور مصر';
    const shopUrl = `${window.location.origin}/?shop=${user?.uid}`;
    
    const resolvedText = messageText
      .replace(/{name}/g, waCustomer.name || '')
      .replace(/{points}/g, String(waCustomer.points || 0))
      .replace(/{pharmacy}/g, pharmacyName)
      .replace(/{link}/g, shopUrl);
      
    const formattedPhone = waCustomer.phone.startsWith('0') ? '2' + waCustomer.phone : waCustomer.phone;
    const url = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(resolvedText)}`;
    window.open(url, '_blank');
    setIsWAModalOpen(false);
  };

  const insertMention = (mentionTag) => {
    const textarea = waTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    setMessageText(before + mentionTag + after);
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + mentionTag.length;
    }, 0);
  };

  const handleSaveCurrentAsTemplate = () => {
    if (!newTemplateTitle.trim()) {
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("⚠️ يرجى كتابة اسم للنموذج أولاً!");
      }
      return;
    }
    if (!messageText.trim()) {
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("⚠️ لا يمكن حفظ نموذج رسالة فارغ!");
      }
      return;
    }
    const newTpl = {
      id: 'custom_' + Date.now(),
      title: newTemplateTitle.trim(),
      text: messageText
    };
    const updated = [...savedWATemplates, newTpl];
    setSavedWATemplates(updated);
    localStorage.setItem('dr_masr_wa_saved_templates', JSON.stringify(updated));
    setSelectedTemplate(newTpl.id);
    setNewTemplateTitle('');
    if (window.triggerDrMasrToast) {
      window.triggerDrMasrToast("✅ تم حفظ نموذج الرسالة بنجاح!");
    } else {
      alert("✅ تم حفظ نموذج الرسالة بنجاح!");
    }
  };

  const handleDeleteSavedTemplate = (id, e) => {
    e.stopPropagation();
    const deleteAction = () => {
      const updated = savedWATemplates.filter(t => t.id !== id);
      setSavedWATemplates(updated);
      localStorage.setItem('dr_masr_wa_saved_templates', JSON.stringify(updated));
      setSelectedTemplate('custom');
      setMessageText('');
      if (window.triggerDrMasrToast) {
        window.triggerDrMasrToast("🗑️ تم حذف النموذج!");
      } else {
        alert("🗑️ تم حذف النموذج!");
      }
    };
    
    if (window.triggerDrMasrConfirm) {
      window.triggerDrMasrConfirm("هل أنت متأكد من حذف هذا النموذج؟", deleteAction);
    } else if (confirm("هل أنت متأكد من حذف هذا النموذج؟")) {
      deleteAction();
    }
  };

  const totalCrmPages = Math.ceil(filtered.length / 4);
  const pageCustomers = filtered.slice((crmPage - 1) * 4, crmPage * 4);

  return (
    <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px', direction: 'rtl', textAlign: 'right', fontFamily: 'Cairo' }}>
      
      {/* Tab Switcher */}
      <div style={{ display: 'flex', gap: '12px', borderBottom: '1px solid var(--border)', paddingBottom: '16px', flexWrap: 'wrap' }}>
        <button onClick={() => setCrmTab('directory')} className={`delivery-tab-btn ${crmTab === 'directory' ? 'active' : ''}`} style={{ padding: '10px 24px', fontSize: '0.92rem', fontWeight: 'bold', borderRadius: '12px', border: 'none', cursor: 'pointer' }}>👥 دليل العملاء والـ CRM</button>
        <button onClick={() => setCrmTab('today_sales')} className={`delivery-tab-btn ${crmTab === 'today_sales' ? 'active' : ''}`} style={{ padding: '10px 24px', fontSize: '0.92rem', fontWeight: 'bold', borderRadius: '12px', border: 'none', cursor: 'pointer' }}>🧾 سجل عمليات اليوم التشغيلى</button>
        <button onClick={() => setCrmTab('reminders')} className={`delivery-tab-btn ${crmTab === 'reminders' ? 'active' : ''}`} style={{ padding: '10px 24px', fontSize: '0.92rem', fontWeight: 'bold', borderRadius: '12px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
          ⏰ منبهات وتجديد الأدوية
          {refillAlerts.filter(a => a.status === 'pending' && new Date(a.alertDate) <= new Date()).length > 0 && (
            <span style={{ background: '#ef4444', color: '#fff', fontSize: '0.72rem', padding: '2px 6px', borderRadius: '8px', fontWeight: 'bold' }}>
              {refillAlerts.filter(a => a.status === 'pending' && new Date(a.alertDate) <= new Date()).length}
            </span>
          )}
        </button>
        <button onClick={() => setCrmTab('backorders')} className={`delivery-tab-btn ${crmTab === 'backorders' ? 'active' : ''}`} style={{ padding: '10px 24px', fontSize: '0.92rem', fontWeight: 'bold', borderRadius: '12px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📦 حجوزات الأدوية الغير متوفرة
          {backorders.filter(b => b.status === 'pending').length > 0 && (
            <span style={{ background: '#f59e0b', color: '#fff', fontSize: '0.72rem', padding: '2px 6px', borderRadius: '8px', fontWeight: 'bold' }}>
              {backorders.filter(b => b.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      {crmTab === 'today_sales' ? (() => {
        const last24hDate = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const recentSales = sales.filter(s => {
          if (!s.timestamp) return false;
          return new Date(s.timestamp) >= last24hDate;
        });

        const searchFilteredSales = recentSales.filter(s => {
          if (!todaySalesSearch) return true;
          const term = todaySalesSearch.toLowerCase();
          const idMatch = s.invoiceId && s.invoiceId.toString().toLowerCase().includes(term);
          const phoneMatch = s.customerPhone && s.customerPhone.toString().toLowerCase().includes(term);
          const nameMatch = s.customerName && s.customerName.toString().toLowerCase().includes(term);
          return idMatch || phoneMatch || nameMatch;
        });

        const sortedSearchSales = searchFilteredSales.sort((a,b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
        const todaySalesTotalPages = Math.ceil(sortedSearchSales.length / 6) || 1;
        const paginatedTodaySales = sortedSearchSales.slice((todaySalesPage - 1) * 6, todaySalesPage * 6);

        return (
        <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>📊 سجل عمليات البيع لآخر ٢٤ ساعة</h3>
            <div style={{ width: '100%', maxWidth: '340px' }}>
              <input
                type="text"
                className="premium-input"
                placeholder="🔍 ابحث برقم الفاتورة، أو العميل، أو الاسم..."
                value={todaySalesSearch}
                onChange={(e) => setTodaySalesSearch(e.target.value)}
                style={{ width: '100%', height: '42px', paddingLeft: '40px', background: 'rgba(5, 8, 16, 0.4)' }}
              />
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="premium-table">
              <thead>
                <tr>
                  <th>رقم الفاتورة</th>
                  <th>تاريخ وتوقت</th>
                  <th>نوع العملية</th>
                  <th>بيانات العميل</th>
                  <th>قيمة الفاتورة</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTodaySales.length === 0 ? (
                  <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>لا توجد عمليات بيع مسجلة مطابقة.</td></tr>
                ) : (
                  paginatedTodaySales.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 700 }}>#{s.invoiceId || s.id.slice(-6).toUpperCase()}</td>
                      <td>{s.timestamp ? new Date(s.timestamp).toLocaleString('ar-EG', { hour12: true }) : '-'}</td>
                      <td>
                        {s.isDelivery ? <span className="status-badge warning">توصيل 🛵</span> : <span className="status-badge success">كاشير 🏪</span>}
                      </td>
                      <td style={{ direction: 'rtl', textAlign: 'right' }}>
                        {s.customerName ? <span style={{display: 'block', fontWeight: 'bold'}}>{s.customerName}</span> : null}
                        <span style={{ direction: 'ltr', display: 'inline-block' }}>{s.customerPhone || 'بدون رقم'}</span>
                      </td>
                      <td style={{ fontWeight: 'bold', color: 'var(--accent-green)' }}>{Number(s.grandTotal || 0).toLocaleString('ar-EG')} ج.م</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {sortedSearchSales.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button 
                type="button"
                onClick={() => setTodaySalesPage(prev => Math.max(1, prev - 1))}
                disabled={todaySalesPage === 1}
                className="gradient-btn"
                style={{ padding: '6px 14px', fontSize: '0.8rem', opacity: todaySalesPage === 1 ? 0.4 : 1, cursor: todaySalesPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                ◀ السابق
              </button>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                صفحة <strong>{todaySalesPage}</strong> من <strong>{todaySalesTotalPages}</strong>
              </span>
              <button 
                type="button"
                onClick={() => setTodaySalesPage(prev => Math.min(todaySalesTotalPages, prev + 1))}
                disabled={todaySalesPage === todaySalesTotalPages}
                className="gradient-btn"
                style={{ padding: '6px 14px', fontSize: '0.8rem', opacity: todaySalesPage === todaySalesTotalPages ? 0.4 : 1, cursor: todaySalesPage === todaySalesTotalPages ? 'not-allowed' : 'pointer' }}
              >
                التالي ▶
              </button>
            </div>
          )}
        </div>
        );
      })() : crmTab === 'reminders' ? (() => {
        const pendingAlerts = refillAlerts.filter(a => a.status === 'pending');
        const activeAlerts = pendingAlerts.filter(a => new Date(a.alertDate) <= new Date());
        const chronicPatients = loyaltyCustomers.filter(c => c.chronicMeds && c.chronicMeds.length > 0);

        const handleCompleteAlert = async (alert) => {
          try {
            setGlobalLoading?.(true);
            const batch = writeBatch(db);
            const alertRef = doc(db, 'users', user.uid, 'refill_alerts', alert.id);
            batch.set(alertRef, { status: 'completed' }, { merge: true });

            const interval = alert.intervalDays || 30;
            const nextAlertDate = new Date();
            nextAlertDate.setDate(nextAlertDate.getDate() + interval - 3);
            const nextAlertDateString = nextAlertDate.toISOString().split('T')[0];

            const nextAlertRef = doc(collection(db, 'users', user.uid, 'refill_alerts'));
            const nextAlertData = {
              id: nextAlertRef.id,
              customerId: alert.customerId,
              customerName: alert.customerName,
              customerPhone: alert.customerPhone,
              medicationName: alert.medicationName,
              intervalDays: interval,
              lastPurchaseDate: new Date().toISOString(),
              alertDate: nextAlertDateString,
              status: 'pending'
            };

            batch.set(nextAlertRef, nextAlertData);

            const customerRef = doc(db, 'users', user.uid, 'customers', alert.customerId);
            const currentCustObj = loyaltyCustomers.find(c => c.id === alert.customerId);
            if (currentCustObj) {
              const updatedMeds = (currentCustObj.chronicMeds || []).map(m => 
                m.medicationName === alert.medicationName 
                  ? { ...m, lastPurchaseDate: new Date().toISOString() } 
                  : m
              );
              batch.set(customerRef, { chronicMeds: updatedMeds }, { merge: true });

              setLoyaltyCustomers(prev => prev.map(c => c.id === alert.customerId ? {
                ...c,
                chronicMeds: updatedMeds
              } : c));
            }

            await batch.commit();

            setRefillAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, status: 'completed' } : a).concat([nextAlertData]));
            window.triggerDrMasrToast?.("✅ تم تسجيل صرف الدواء وجدولة التنبيه القادم بنجاح!");
          } catch (err) {
            console.error(err);
            window.triggerDrMasrAlert?.("⚠️ حدث خطأ أثناء إتمام العملية");
          } finally {
            setGlobalLoading?.(false);
          }
        };

        const handleRemoveChronicMed = async (cust, medName) => {
          const deleteAction = async () => {
            try {
              setGlobalLoading?.(true);
              const customerRef = doc(db, 'users', user.uid, 'customers', cust.id);
              const updatedMeds = (cust.chronicMeds || []).filter(m => m.medicationName !== medName);

              await setDoc(customerRef, { chronicMeds: updatedMeds }, { merge: true });

              const alertsToCancel = refillAlerts.filter(a => a.customerId === cust.id && a.medicationName === medName && a.status === 'pending');
              const batch = writeBatch(db);
              alertsToCancel.forEach(a => {
                const aRef = doc(db, 'users', user.uid, 'refill_alerts', a.id);
                batch.set(aRef, { status: 'cancelled' }, { merge: true });
              });
              await batch.commit();

              setLoyaltyCustomers(prev => prev.map(c => c.id === cust.id ? { ...c, chronicMeds: updatedMeds } : c));
              setRefillAlerts(prev => prev.map(a => (a.customerId === cust.id && a.medicationName === medName && a.status === 'pending') ? { ...a, status: 'cancelled' } : a));

              window.triggerDrMasrToast?.("🗑️ تم إزالة الدواء بنجاح!");
            } catch (err) {
              console.error(err);
              window.triggerDrMasrAlert?.("⚠️ حدث خطأ أثناء الحذف");
            } finally {
              setGlobalLoading?.(false);
            }
          };

          if (window.triggerDrMasrConfirm) {
            window.triggerDrMasrConfirm(`هل أنت متأكد من إلغاء تكرار دواء ${medName} للمريض ${cust.name}؟`, deleteAction);
          } else if (confirm(`هل أنت متأكد من إلغاء تكرار دواء ${medName} للمريض ${cust.name}؟`)) {
            deleteAction();
          }
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
              <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)', border: '1px solid rgba(239, 68, 68, 0.25)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>منبهات مستحقة اليوم</span>
                <h2 style={{ fontSize: '2rem', fontWeight: 900, color: '#ef4444', margin: '8px 0 0 0', fontFamily: 'Outfit' }}>
                  {activeAlerts.length} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>تنبيه</span>
                </h2>
              </div>
              <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>إجمالي المرضى المزمنين</span>
                <h2 style={{ fontSize: '2rem', fontWeight: 900, color: '#10b981', margin: '8px 0 0 0', fontFamily: 'Outfit' }}>
                  {chronicPatients.length} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>مريض</span>
                </h2>
              </div>
            </div>

            <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                🚨 تنبيهات اليوم المستحقة لتجديد الروشتات
              </h3>
              
              {activeAlerts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>
                  🎉 لا توجد أي تنبيهات مستحقة اليوم. جميع المرضى في أمان وجرعاتهم منتظمة!
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border)' }}>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>المريض</th>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>الهاتف</th>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>الدواء المطلوب</th>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>دورة التكرار</th>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>تاريخ التنبيه</th>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)', textAlign: 'left' }}>الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeAlerts.map(alert => (
                        <tr key={alert.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.2s' }}>
                          <td style={{ padding: '12px 8px', fontWeight: 'bold', color: '#fff' }}>{alert.customerName}</td>
                          <td style={{ padding: '12px 8px' }}>
                            <a href={`tel:${alert.customerPhone}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                              📞 {alert.customerPhone}
                            </a>
                          </td>
                          <td style={{ padding: '12px 8px', color: '#fbbf24', fontWeight: 'bold' }}>{alert.medicationName}</td>
                          <td style={{ padding: '12px 8px' }}>كل {alert.intervalDays || 30} يوم</td>
                          <td style={{ padding: '12px 8px', color: 'var(--accent-pink)' }}>{alert.alertDate}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'left' }}>
                            <div style={{ display: 'inline-flex', gap: '8px' }}>
                              <button 
                                onClick={() => {
                                  const text = `عزيزي المريض ${alert.customerName}، نذكرك بموعد تجديد دواء ${alert.medicationName} الخاص بك اليوم لضمان استمرارية العلاج بالصيدلية. نتمنى لك الشفاء العاجل 🌸`;
                                  window.open(`https://wa.me/2${alert.customerPhone}?text=${encodeURIComponent(text)}`, '_blank');
                                }}
                                className="gradient-btn"
                                style={{ padding: '6px 12px', fontSize: '0.8rem', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                              >
                                💬 واتساب
                              </button>
                              <button 
                                onClick={() => handleCompleteAlert(alert)}
                                className="gradient-btn"
                                style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                              >
                                ✅ تم التوصيل والصرف
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                📋 دليل المرضى المزمنين والأدوية النشطة
              </h3>

              {chronicPatients.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>
                  🔄 لم يتم تسجيل أي مرضى مزمنين حتى الآن.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {chronicPatients.map(cust => (
                    <div key={cust.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                        <div>
                          <strong style={{ color: '#fff', fontSize: '1rem' }}>{cust.name}</strong>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginRight: '10px' }}>📞 {cust.phone}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {(cust.chronicMeds || []).map((med, midx) => (
                          <div key={midx} style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ fontSize: '0.85rem' }}>
                              <strong style={{ color: '#fff' }}>{med.medicationName}</strong>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                كل {med.intervalDays || 30} يوم | آخر صرف: {med.lastPurchaseDate ? new Date(med.lastPurchaseDate).toLocaleDateString('ar-EG') : 'غير محدد'}
                              </div>
                            </div>
                            <button 
                              onClick={() => handleRemoveChronicMed(cust, med.medicationName)}
                              style={{ color: 'var(--accent-pink)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', padding: 0 }}
                              title="إلغاء المتابعة"
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })() : crmTab === 'backorders' ? (() => {
        const filteredBackorders = backorders.filter(b => {
          if (!backorderDrugSearch.trim()) return true;
          const s = backorderDrugSearch.toLowerCase();
          return b.drugName?.toLowerCase().includes(s) || b.customerName?.toLowerCase().includes(s) || b.customerPhone?.includes(s);
        });
        const pendingBo = filteredBackorders.filter(b => b.status === 'pending');
        const fulfilledBo = filteredBackorders.filter(b => b.status === 'fulfilled');

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
              <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(217,119,6,0.05) 100%)', border: '1px solid rgba(245,158,11,0.3)' }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>حجوزات معلقة</span>
                <h2 style={{ fontSize: '2rem', fontWeight: 900, color: '#f59e0b', margin: '6px 0 0 0', fontFamily: 'Outfit' }}>
                  {pendingBo.length} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>حجز</span>
                </h2>
              </div>
              <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(5,150,105,0.05) 100%)', border: '1px solid rgba(16,185,129,0.3)' }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>تم توفيرها</span>
                <h2 style={{ fontSize: '2rem', fontWeight: 900, color: '#10b981', margin: '6px 0 0 0', fontFamily: 'Outfit' }}>
                  {fulfilledBo.length} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>حجز</span>
                </h2>
              </div>
            </div>

            {/* Controls */}
            <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', margin: 0 }}>📦 قائمة حجوزات الأدوية الغير متوفرة</h3>
                <button
                  onClick={() => setIsAddBackorderModalOpen(true)}
                  style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 20px', fontSize: '0.9rem', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 12px rgba(245,158,11,0.3)' }}
                >
                  + إضافة حجز دواء
                </button>
              </div>
              <input
                type="text"
                className="premium-input"
                placeholder="🔍 ابحث بالاسم أو الدواء أو رقم الهاتف..."
                value={backorderDrugSearch}
                onChange={e => setBackorderDrugSearch(e.target.value)}
                style={{ padding: '10px 16px', fontSize: '0.9rem' }}
              />

              {filteredBackorders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                  📦 لا توجد حجوزات حتى الآن. اضغط «+ إضافة حجز دواء» لتسجيل أول حجز.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border)' }}>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>المريض</th>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>الهاتف</th>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>الدواء المطلوب</th>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>الكمية</th>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>ملاحظات</th>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>الحالة</th>
                        <th style={{ padding: '12px 8px', color: 'var(--text-secondary)', textAlign: 'left' }}>الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBackorders.map(bo => (
                        <tr key={bo.id} style={{ borderBottom: '1px solid var(--border)', opacity: bo.status === 'fulfilled' ? 0.55 : 1 }}>
                          <td style={{ padding: '12px 8px', fontWeight: 'bold', color: '#fff' }}>{bo.customerName}</td>
                          <td style={{ padding: '12px 8px' }}>
                            {bo.customerPhone ? (
                              <a href={`tel:${bo.customerPhone}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>📞 {bo.customerPhone}</a>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '12px 8px', color: '#fbbf24', fontWeight: 'bold' }}>{bo.drugName}</td>
                          <td style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>{bo.quantity}</td>
                          <td style={{ padding: '12px 8px', color: 'var(--text-secondary)', maxWidth: '150px' }}>{bo.notes || '—'}</td>
                          <td style={{ padding: '12px 8px' }}>
                            <span style={{ background: bo.status === 'pending' ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)', color: bo.status === 'pending' ? '#f59e0b' : '#10b981', padding: '3px 10px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 'bold' }}>
                              {bo.status === 'pending' ? '⏳ معلق' : '✅ منجز'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'left' }}>
                            <div style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
                              {bo.customerPhone && bo.status === 'pending' && (
                                <button
                                  onClick={() => {
                                    const msg = `مرحباً ${bo.customerName}، نُعلمك بسعادة أن الدواء (${bo.drugName}) الذي طلبته بالصيدلية قد أصبح متوفراً الآن 🎉 يسعدنا خدمتك!`;
                                    window.open(`https://wa.me/2${bo.customerPhone}?text=${encodeURIComponent(msg)}`, '_blank');
                                  }}
                                  style={{ background: 'rgba(37,211,102,0.15)', color: '#25d366', border: '1px solid rgba(37,211,102,0.3)', borderRadius: '8px', padding: '5px 10px', fontSize: '0.78rem', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                  💬 إبلاغ عبر واتساب
                                </button>
                              )}
                              {bo.status === 'pending' && (
                                <button
                                  onClick={() => handleFulfillBackorder(bo)}
                                  className="gradient-btn"
                                  style={{ padding: '5px 10px', fontSize: '0.78rem' }}
                                >
                                  ✅ تم التوفير
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteBackorder(bo)}
                                style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '8px', padding: '5px 10px', fontSize: '0.78rem', fontWeight: 'bold', cursor: 'pointer' }}
                              >
                                🗑️ حذف
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Add Backorder Modal - Dialog 1 Pattern */}
            {isAddBackorderModalOpen && createPortal(
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
                <div style={{ width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', background: '#141229', border: '1px solid rgba(139,92,246,0.25)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', borderRadius: '16px', padding: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: '1.15rem', fontWeight: 800 }}>📦 إضافة حجز دواء جديد</h3>
                    <button onClick={() => setIsAddBackorderModalOpen(false)} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', position: 'relative' }}>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>اسم المريض أو رقم الهاتف *</label>
                      <input
                        type="text"
                        className="premium-input"
                        placeholder="ابحث بالاسم أو الرقم..."
                        value={boCustomerName}
                        onChange={e => {
                          setBoCustomerName(e.target.value);
                          setBoShowSuggestions(true);
                        }}
                        onFocus={() => setBoShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setBoShowSuggestions(false), 180)}
                        style={{ padding: '10px 14px' }}
                        autoComplete="off"
                      />
                      {boShowSuggestions && boCustomerName.trim().length >= 1 && (() => {
                        const q = boCustomerName.trim().toLowerCase();
                        const matched = loyaltyCustomers.filter(c =>
                          c.name?.toLowerCase().includes(q) || c.phone?.includes(q)
                        ).slice(0, 6);
                        if (matched.length === 0) return null;
                        return (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
                            background: '#1a1635', border: '1px solid rgba(139,92,246,0.35)',
                            borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                            maxHeight: '200px', overflowY: 'auto', marginTop: '4px'
                          }}>
                            {matched.map(c => (
                              <div
                                key={c.id}
                                onMouseDown={() => {
                                  setBoCustomerName(c.name);
                                  setBoCustomerPhone(c.phone || '');
                                  setBoShowSuggestions(false);
                                }}
                                style={{
                                  padding: '10px 14px', cursor: 'pointer',
                                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.15)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              >
                                <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '0.88rem' }}>👤 {c.name}</span>
                                <span style={{ color: 'var(--primary)', fontSize: '0.78rem' }}>{c.phone}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>رقم الهاتف</label>
                      <input type="tel" className="premium-input" placeholder="01xxxxxxxxx" value={boCustomerPhone} onChange={e => setBoCustomerPhone(e.target.value)} style={{ padding: '10px 14px' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>اسم الدواء المطلوب *</label>
                      <input type="text" className="premium-input" placeholder="مثال: كونكور 10 مج" value={boDrugName} onChange={e => setBoDrugName(e.target.value)} style={{ padding: '10px 14px' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>الكمية</label>
                      <input type="number" min="1" className="premium-input" placeholder="1" value={boQuantity} onChange={e => setBoQuantity(e.target.value)} style={{ padding: '10px 14px' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>ملاحظات إضافية</label>
                      <textarea className="premium-input" placeholder="أي ملاحظات إضافية..." value={boNotes} onChange={e => setBoNotes(e.target.value)} style={{ padding: '10px 14px', minHeight: '80px', resize: 'vertical' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                      <button onClick={() => setIsAddBackorderModalOpen(false)} style={{ flex: 1, padding: '11px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '10px', cursor: 'pointer', fontFamily: 'Cairo', fontWeight: 'bold' }}>إلغاء</button>
                      <button onClick={handleAddBackorder} style={{ flex: 2, padding: '11px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontFamily: 'Cairo', fontWeight: 'bold', fontSize: '0.95rem' }}>📦 حفظ الحجز</button>
                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )}
          </div>
        );
      })() : (
      <>
      {/* Header Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
        <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%)', border: '1px solid rgba(139, 92, 246, 0.25)' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>إجمالي العملاء</span>
          <h2 style={{ fontSize: '2rem', fontWeight: 900, color: '#fff', margin: '8px 0 0 0', fontFamily: 'Outfit' }}>
            {allCustomers.length} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>عميل</span>
          </h2>
        </div>

        <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>نقاط الكاش باك الكلية</span>
          <h2 style={{ fontSize: '2rem', fontWeight: 900, color: '#fff', margin: '8px 0 0 0', fontFamily: 'Outfit' }}>
            {totalPoints} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>نقطة / ج.م</span>
          </h2>
        </div>

        <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%)', border: '1px solid rgba(59, 130, 246, 0.25)' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>العملاء النشطون (أقل من 30 يوم)</span>
          <h2 style={{ fontSize: '2rem', fontWeight: 900, color: '#fff', margin: '8px 0 0 0', fontFamily: 'Outfit' }}>
            {activeCount} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>عميل</span>
          </h2>
        </div>

        <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)', border: '1px solid rgba(245, 158, 11, 0.25)' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>العملاء الغائبون (30+ يوم ⚠️)</span>
          <h2 style={{ fontSize: '2rem', fontWeight: 900, color: '#fff', margin: '8px 0 0 0', fontFamily: 'Outfit' }}>
            {dormantCount} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>عميل غائب</span>
          </h2>
        </div>
      </div>

      {/* Grid Content */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '24px', alignItems: 'flex-start' }}>
        
        {/* Left pane: Customer List & Management */}
        <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px', minHeight: '520px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>👥 دليل عملاء الفرع والـ CRM</h3>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={handleOpenImportModal}
                style={{
                  background: 'rgba(16, 185, 129, 0.15)',
                  color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '10px', padding: '8px 16px',
                  fontSize: '0.85rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', gap: '6px', transition: 'all 0.2s'
                }}
              >
                <span>📥 رفع شيت إكسل</span>
              </button>
              <button 
                onClick={handleOpenAddModal}
                style={{
                  background: 'linear-gradient(135deg, var(--primary) 0%, #6366f1 100%)',
                  color: '#fff', border: 'none', borderRadius: '10px', padding: '8px 16px',
                  fontSize: '0.85rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', gap: '6px', boxShadow: '0 4px 10px rgba(99, 102, 241, 0.2)'
                }}
              >
                <span>+ إضافة عميل</span>
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input 
              type="text" 
              className="premium-input" 
              placeholder="🔍 ابحث هنا بالاسم أو رقم الهاتف..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ padding: '8px 16px', fontSize: '0.9rem', flex: 1 }}
            />
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '12px' }}>
            <button 
              onClick={() => setFilterMode('all')} 
              style={{ padding: '6px 14px', fontSize: '0.82rem', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', background: filterMode === 'all' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.02)', color: filterMode === 'all' ? '#a78bfa' : 'var(--text-secondary)', border: filterMode === 'all' ? '1px solid rgba(139, 92, 246, 0.4)' : '1px solid var(--border)' }}
            >
              الكل ({allCustomers.length})
            </button>
            <button 
              onClick={() => setFilterMode('active')} 
              style={{ padding: '6px 14px', fontSize: '0.82rem', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', background: filterMode === 'active' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255,255,255,0.02)', color: filterMode === 'active' ? '#60a5fa' : 'var(--text-secondary)', border: filterMode === 'active' ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid var(--border)' }}
            >
              النشطون ({activeCount})
            </button>
            <button 
              onClick={() => setFilterMode('dormant')} 
              style={{ padding: '6px 14px', fontSize: '0.82rem', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', background: filterMode === 'dormant' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255,255,255,0.02)', color: filterMode === 'dormant' ? '#f87171' : 'var(--text-secondary)', border: filterMode === 'dormant' ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid var(--border)' }}
            >
              الغائبون ({dormantCount})
            </button>
            <button 
              onClick={() => setFilterMode('debt')} 
              style={{ padding: '6px 14px', fontSize: '0.82rem', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', background: filterMode === 'debt' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(255,255,255,0.02)', color: filterMode === 'debt' ? '#f59e0b' : 'var(--text-secondary)', border: filterMode === 'debt' ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid var(--border)' }}
            >
              ⏳ عليهم مبالغ ({debtCount})
            </button>
          </div>

          {/* List content */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', maxHeight: '420px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>🔄 جاري تحميل سجلات العملاء...</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>🤷‍♂️ لا توجد سجلات مطابقة للبحث أو الفلتر.</div>
            ) : (
              pageCustomers.map(cust => (
                <div 
                  key={cust.phone}
                  onClick={() => handleSelectCustomer(cust)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: activeCustomer?.phone === cust.phone ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)' : 'rgba(30, 41, 59, 0.25)',
                    border: activeCustomer?.phone === cust.phone ? '1px solid rgba(99, 102, 241, 0.4)' : '1px solid var(--border)',
                    padding: '12px 16px', borderRadius: '14px', cursor: 'pointer', transition: 'all 0.2s'
                  }}
                >
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: 'bold', color: '#fff' }}>{cust.name}</h4>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block' }}>📱 {cust.phone}</span>
                    {cust.daysSinceLast >= 30 && (
                      <span style={{ display: 'inline-block', fontSize: '0.72rem', padding: '2px 8px', background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', borderRadius: '6px', fontWeight: 'bold', marginTop: '6px' }}>
                        ⚠️ {cust.daysSinceLast === Infinity ? 'غائب منذ مدة طويلة' : `غائب منذ ${cust.daysSinceLast} يوم`}
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ textAlign: 'left' }}>
                      <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>النقاط</span>
                      <strong style={{ fontSize: '1rem', color: 'var(--accent-gold)' }}>{cust.points} ج.م</strong>
                    </div>
                    {cust.debt > 0 && (
                      <div style={{ textAlign: 'left', borderLeft: '1px solid rgba(255,255,255,0.08)', paddingLeft: '10px', marginLeft: '6px' }}>
                        <span style={{ display: 'block', fontSize: '0.75rem', color: '#f59e0b' }}>المديونية</span>
                        <strong style={{ fontSize: '1rem', color: '#ef4444' }}>{cust.debt} ج.م</strong>
                      </div>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSendWhatsApp(cust); }}
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '8px',
                        fontSize: '0.78rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', gap: '4px', boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
                      }}
                    >
                      💬 واتساب
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* CRM Pagination Controls */}
          {filtered.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button 
                type="button"
                onClick={() => setCrmPage(prev => Math.max(1, prev - 1))}
                disabled={crmPage === 1}
                className="gradient-btn"
                style={{ padding: '6px 14px', fontSize: '0.8rem', opacity: crmPage === 1 ? 0.4 : 1, cursor: crmPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                ◀ السابق
              </button>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                صفحة <strong>{crmPage}</strong> من <strong>{totalCrmPages || 1}</strong>
              </span>
              <button 
                type="button"
                onClick={() => setCrmPage(prev => Math.min(totalCrmPages, prev + 1))}
                disabled={crmPage === totalCrmPages || totalCrmPages <= 1}
                className="gradient-btn"
                style={{ padding: '6px 14px', fontSize: '0.8rem', opacity: (crmPage === totalCrmPages || totalCrmPages <= 1) ? 0.4 : 1, cursor: (crmPage === totalCrmPages || totalCrmPages <= 1) ? 'not-allowed' : 'pointer' }}
              >
                التالي ▶
              </button>
            </div>
          )}
        </div>

        {/* Right pane: Customer detailed profile */}
        <div className="premium-card" style={{ minHeight: '520px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {!activeCustomer ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', padding: '40px 0', textAlign: 'center', gap: '16px' }}>
              <span style={{ fontSize: '4.5rem' }}>👥</span>
              <h3 style={{ fontSize: '1.15rem', color: '#94a3b8' }}>يرجى تحديد عميل من القائمة الجانبية لاستعراض ملفه الشخصي وتحليلات مبيعاته.</h3>
            </div>
          ) : loadingCustomerSales ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', padding: '40px 0', textAlign: 'center', gap: '16px' }}>
              <div className="pm-logo-glow-ring" style={{ border: '4px solid var(--primary)', borderTopColor: 'transparent', width: '48px', height: '48px' }}></div>
              <h3 style={{ fontSize: '1.1rem', color: '#94a3b8' }}>جاري تحميل فواتير ومبيعات العميل من السيرفر...</h3>
            </div>
          ) : (() => {
            const currentOrdersCount = activeCustomerSales.length;
            const currentOrdersVolume = activeCustomerSales.reduce((acc, s) => acc + Number(s.grandTotal || 0), 0);
            
            const customerAddresses = new Set();
            if (activeCustomer.address) {
              customerAddresses.add(activeCustomer.address);
            }
            activeCustomerSales.forEach(s => {
              if (s.deliveryDetails?.address) customerAddresses.add(s.deliveryDetails.address);
              if (s.deliveryAddress) customerAddresses.add(s.deliveryAddress);
            });

            const activeCustomerVisits = activeCustomer.visits > 0 ? activeCustomer.visits : currentOrdersCount;
            const activeCustomerSpend = activeCustomer.spend > 0 ? activeCustomer.spend : currentOrdersVolume;

            return (
              <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                
                {/* Header profile info */}
                <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ margin: '0 0 6px 0', color: '#fff', fontSize: '1.35rem', fontWeight: 900 }}>{activeCustomer.name}</h3>
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>الهاتف: <strong style={{ color: '#fff', fontFamily: 'Outfit' }}>{activeCustomer.phone}</strong></span>
                    {activeCustomer.lastVisit && (
                      <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', display: 'block', marginTop: '4px' }}>آخر زيارة: <strong style={{ color: '#38bdf8' }}>{parseDate(activeCustomer.lastVisit) ? parseDate(activeCustomer.lastVisit).toLocaleDateString('ar-EG') : 'غير محدد'}</strong></span>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button 
                      onClick={() => handleOpenEditModal(activeCustomer)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.3)',
                        borderRadius: '8px', padding: '6px 12px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer'
                      }}
                    >
                      ✏️ تعديل البيانات
                    </button>
                    <button 
                      onClick={() => executeDeleteCustomer(activeCustomer)}
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '8px', padding: '6px 12px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer'
                      }}
                    >
                      🗑️ حذف
                    </button>
                  </div>
                </div>

                {/* Metrics cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
                  <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>نقاط الكاش باك:</span>
                    <strong style={{ display: 'block', fontSize: '1.2rem', color: 'var(--accent-gold)', fontFamily: 'Outfit', marginTop: '4px' }}>{activeCustomer.points} ج.م</strong>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>عدد زيارات الفرع:</span>
                    <strong style={{ display: 'block', fontSize: '1.2rem', color: '#fff', fontFamily: 'Outfit', marginTop: '4px' }}>{activeCustomerVisits} زيارات</strong>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>إجمالي المشتريات:</span>
                    <strong style={{ display: 'block', fontSize: '1.2rem', color: 'var(--accent-green)', fontFamily: 'Outfit', marginTop: '4px' }}>{activeCustomerSpend} ج.م</strong>
                  </div>
                  <div style={{ background: (activeCustomer.debt || 0) > 0 ? 'rgba(239, 68, 68, 0.08)' : 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '12px', border: (activeCustomer.debt || 0) > 0 ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ fontSize: '0.75rem', color: (activeCustomer.debt || 0) > 0 ? '#ef4444' : 'var(--text-secondary)' }}>المديونية الحالية:</span>
                    <strong style={{ display: 'block', fontSize: '1.2rem', color: (activeCustomer.debt || 0) > 0 ? '#ef4444' : 'var(--accent-green)', fontFamily: 'Outfit', marginTop: '4px' }}>{activeCustomer.debt || 0} ج.م</strong>
                  </div>
                </div>

                {/* Customer specific discounts display */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ background: 'rgba(59, 130, 246, 0.08)', padding: '12px', borderRadius: '12px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>خصم الدواء المحلي للعميل:</span>
                    <strong style={{ display: 'block', fontSize: '1.25rem', color: 'var(--primary)', fontFamily: 'Outfit', marginTop: '4px' }}>
                      {activeCustomer.localDiscount !== undefined ? activeCustomer.localDiscount : 0}%
                    </strong>
                  </div>
                  <div style={{ background: 'rgba(16, 185, 129, 0.08)', padding: '12px', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>خصم الدواء المستورد للعميل:</span>
                    <strong style={{ display: 'block', fontSize: '1.25rem', color: 'var(--accent-green)', fontFamily: 'Outfit', marginTop: '4px' }}>
                      {activeCustomer.importedDiscount !== undefined ? activeCustomer.importedDiscount : 0}%
                    </strong>
                  </div>
                </div>

                {/* CRM Absence Message */}
                <div style={{ background: activeCustomer.daysSinceLast >= 30 ? 'rgba(239, 68, 68, 0.05)' : 'rgba(16, 185, 129, 0.05)', border: activeCustomer.daysSinceLast >= 30 ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid rgba(16, 185, 129, 0.2)', padding: '12px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>حالة تفاعل العميل:</span>
                  <strong style={{ fontSize: '0.9rem', color: activeCustomer.daysSinceLast >= 30 ? '#f87171' : '#34d399' }}>
                    {activeCustomer.daysSinceLast >= 30 
                      ? (activeCustomer.daysSinceLast === Infinity ? "⚠️ غائب منذ مدة طويلة" : "⚠️ غائب منذ " + activeCustomer.daysSinceLast + " يوم") 
                      : "✔️ متفاعل نشط (آخر شراء منذ " + activeCustomer.daysSinceLast + " يوم)"}
                  </strong>
                </div>

                {/* CRM Settle Debt box */}
                {(activeCustomer.debt || 0) > 0 && (
                  <div style={{ background: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.25)', padding: '16px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.85rem', color: '#f59e0b', fontWeight: 'bold' }}>💳 تسوية مديونية العميل:</span>
                      <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>المديونية المتبقية: <strong style={{ color: '#ef4444' }}>{activeCustomer.debt} ج.م</strong></span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input 
                        type="number"
                        placeholder="أدخل المبلغ المدفوع..."
                        value={settleAmount}
                        onChange={(e) => setSettleAmount(e.target.value)}
                        style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)', color: '#fff', fontSize: '0.9rem' }}
                      />
                      <button
                        onClick={handleSettleDebt}
                        className="gradient-btn"
                        style={{ padding: '8px 16px', fontSize: '0.85rem', borderRadius: '8px', cursor: 'pointer' }}
                      >
                        💵 تسجيل السداد
                      </button>
                    </div>
                  </div>
                )}

                {/* Registered Addresses */}
                <div>
                  <h4 style={{ fontSize: '0.92rem', color: '#fff', fontWeight: 'bold', margin: '0 0 8px 0' }}>📍 عناوين التوصيل المسجلة:</h4>
                  {customerAddresses.size === 0 ? (
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>لا توجد عناوين شحن مسجلة لهذا العميل بعد.</span>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {Array.from(customerAddresses).map((addr, idx) => (
                        <div key={idx} style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.03)', padding: '10px 14px', borderRadius: '10px', fontSize: '0.85rem', color: '#fff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>📍</span> {String(addr)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Purchase History */}
                <div>
                  <h4 style={{ fontSize: '0.92rem', color: '#fff', fontWeight: 'bold', margin: '0 0 8px 0' }}>📦 سجل العمليات والفواتير:</h4>
                  {activeCustomerSales.length === 0 ? (
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>لا توجد فواتير مبيعات مسجلة لهذا الهاتف.</span>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', maxHeight: '180px', paddingLeft: '4px' }}>
                      {activeCustomerSales.map((order, idx) => {
                        const isFullyRefunded = order.isRefunded || (order.items && order.items.length === 0) || (order.isDelivery && order.deliveryStatus === 'returned');
                        const isPartiallyRefunded = !isFullyRefunded && order.items && order.items.some(item => item.returnedQuantity > 0);
                        
                        return (
                          <div key={idx} style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.03)', padding: '12px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '6px', opacity: isFullyRefunded ? 0.6 : 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <strong style={{ fontSize: '0.85rem', color: '#fff', textDecoration: isFullyRefunded ? 'line-through' : 'none' }}>فاتورة #{String(order.invoiceId || '')}</strong>
                                <span style={{
                                  fontSize: '0.72rem',
                                  padding: '2px 6px',
                                  borderRadius: '6px',
                                  background: (order.onlineOrderId || order.isOnline || order.cashierName === 'أونلاين (QR Store)') ? 'rgba(59, 130, 246, 0.15)' : order.isDelivery ? 'rgba(139, 92, 246, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                                  color: (order.onlineOrderId || order.isOnline || order.cashierName === 'أونلاين (QR Store)') ? '#60a5fa' : order.isDelivery ? '#a78bfa' : '#34d399',
                                  border: (order.onlineOrderId || order.isOnline || order.cashierName === 'أونلاين (QR Store)') ? '1px solid rgba(59, 130, 246, 0.3)' : order.isDelivery ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(16, 185, 129, 0.3)',
                                  fontWeight: 'bold'
                                }}>
                                  {(order.onlineOrderId || order.isOnline || order.cashierName === 'أونلاين (QR Store)') ? 'البيع أونلاين 🌐' : order.isDelivery ? 'دليفري 🛵' : 'داخل الفرع 🏪'}
                                </span>
                              </div>
                              <span style={{ fontSize: '0.85rem', color: isFullyRefunded ? 'var(--text-secondary)' : isPartiallyRefunded ? '#f59e0b' : 'var(--accent-green)', fontWeight: 800, textDecoration: isFullyRefunded ? 'line-through' : 'none' }}>{order.grandTotal} ج.م</span>
                            </div>
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>التاريخ: {parseDate(order.timestamp) ? parseDate(order.timestamp).toLocaleDateString('ar-EG') : 'غير محدد'}</span>
                              
                              {/* Operation Status Badges */}
                              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                {isFullyRefunded ? (
                                  <span style={{ color: '#f87171', fontWeight: 'bold' }}>
                                    {order.isDelivery ? '❌ ملغي / مرتجع بالكامل' : '🔄 مرتجع بالكامل'}
                                  </span>
                                ) : isPartiallyRefunded ? (
                                  <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                                    ⚠️ مرتجع جزئي
                                  </span>
                                ) : order.isDelivery ? (
                                  <span style={{ 
                                    color: order.deliveryStatus === 'delivered' ? '#34d399' : '#fbbf24', 
                                    fontWeight: 'bold' 
                                  }}>
                                    🛵 {order.deliveryStatus === 'delivered' ? 'تم التوصيل' : 'تحت الشحن'}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)' }}>
                                    ✔️ مكتمل
                                  </span>
                                )}
                              </div>
                            </div>
                            {order.items && order.items.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px', borderTop: '1px dashed rgba(255,255,255,0.04)', paddingTop: '6px' }}>
                                {order.items.map((item, sidx) => (
                                  <span key={sidx} style={{ background: 'rgba(255,255,255,0.03)', padding: '2px 8px', borderRadius: '6px', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                                    💊 {String(item.tradeName || 'صنف')} ({item.subUnit ? `${Math.round(item.quantity * item.subUnit.ratio)} ${item.subUnit.name} (${Number(item.quantity.toFixed(4))} علبة)` : `${item.quantity} علبة`})
                                    {item.returnedQuantity > 0 && (
                                      <span style={{ color: '#f87171', marginRight: '4px', fontWeight: 'bold' }}>
                                        (↩️ مرتجع {item.returnedQuantity})
                                      </span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Backorders section for this customer */}
                {(() => {
                  const custBackorders = backorders.filter(b =>
                    (activeCustomer.phone && b.customerPhone && b.customerPhone === activeCustomer.phone) ||
                    (b.customerName && b.customerName === activeCustomer.name)
                  );
                  if (custBackorders.length === 0) return null;
                  const pendingBos = custBackorders.filter(b => b.status === 'pending');
                  return (
                    <div>
                      <h4 style={{ fontSize: '0.92rem', color: '#fbbf24', fontWeight: 'bold', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        📦 الأدوية الغير متوفرة المحجوزة
                        {pendingBos.length > 0 && (
                          <span style={{ background: 'rgba(245,158,11,0.2)', color: '#f59e0b', fontSize: '0.72rem', padding: '2px 8px', borderRadius: '8px', fontWeight: 'bold' }}>
                            {pendingBos.length} معلق
                          </span>
                        )}
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {custBackorders.map((bo, idx) => (
                          <div key={bo.id || idx} style={{
                            background: bo.status === 'pending' ? 'rgba(245,158,11,0.07)' : 'rgba(16,185,129,0.05)',
                            border: bo.status === 'pending' ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(16,185,129,0.2)',
                            borderRadius: '10px', padding: '10px 14px',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px',
                            opacity: bo.status === 'fulfilled' ? 0.65 : 1
                          }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: 1 }}>
                              <span style={{ fontWeight: 'bold', color: bo.status === 'pending' ? '#fbbf24' : '#34d399', fontSize: '0.88rem' }}>
                                💊 {bo.drugName}
                              </span>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                الكمية: {bo.quantity}{bo.notes ? ` | ${bo.notes}` : ''}
                              </span>
                              {bo.createdAt && (
                                <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                                  {new Date(bo.createdAt).toLocaleDateString('ar-EG')}
                                </span>
                              )}
                            </div>
                            <span style={{
                              background: bo.status === 'pending' ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)',
                              color: bo.status === 'pending' ? '#f59e0b' : '#10b981',
                              padding: '3px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold',
                              whiteSpace: 'nowrap', alignSelf: 'flex-start'
                            }}>
                              {bo.status === 'pending' ? '⏳ معلق' : '✅ منجز'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

              </div>
            );
          })()}
        </div>
      </div>

      {/* WhatsApp Message Template Customizer Modal Popup */}
      {isWAModalOpen && waCustomer && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(3, 7, 18, 0.85)', backdropFilter: 'blur(12px)',
          zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }} onClick={() => setIsWAModalOpen(false)}>
          
          <div style={{
            width: '100%', maxWidth: '520px',
            background: 'linear-gradient(135deg, #0f172a 0%, #030712 100%)',
            border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '24px',
            padding: '24px', boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
            position: 'relative', direction: 'rtl', textAlign: 'right'
          }} onClick={(e) => e.stopPropagation()}>
            
            <button 
              onClick={() => setIsWAModalOpen(false)}
              style={{ position: 'absolute', top: '16px', left: '16px', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '1.8rem', outline: 'none', lineHeight: 1 }}
            >
              &times;
            </button>

            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', marginBottom: '6px' }}>
              ✉️ تخصيص رسالة واتساب للعميل
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '20px' }}>
              المرسل إليه: <strong style={{ color: '#fff' }}>{waCustomer.name}</strong> ({waCustomer.phone})
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Select template */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>اختر نموذج الرسالة السريعة:</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select 
                    className="premium-input" 
                    value={selectedTemplate}
                    onChange={e => {
                      const val = e.target.value;
                      setSelectedTemplate(val);
                      if (val === 'custom') {
                        setMessageText('');
                      } else if (defaultTemplates[val]) {
                        setMessageText(defaultTemplates[val].text);
                      } else {
                        const customTpl = savedWATemplates.find(t => t.id === val);
                        if (customTpl) {
                          setMessageText(customTpl.text);
                        }
                      }
                    }}
                    style={{ flex: 1, background: 'var(--bg-card)', color: '#fff', border: '1px solid var(--border)', borderRadius: '10px' }}
                  >
                    <option value="offers">🎁 مشاركة العروض والتخفيضات الجديدة</option>
                    <option value="remind">🏥 تذكير العميل بزيارتنا (الاشتياق لحضورك)</option>
                    {savedWATemplates.map(t => (
                      <option key={t.id} value={t.id}>💾 {t.title}</option>
                    ))}
                    <option value="custom">✏️ رسالة مخصصة (كتابة حرة)</option>
                  </select>

                  {savedWATemplates.some(t => t.id === selectedTemplate) && (
                    <button
                      type="button"
                      onClick={(e) => handleDeleteSavedTemplate(selectedTemplate, e)}
                      style={{
                        background: 'rgba(239, 68, 68, 0.15)',
                        color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '10px', padding: '10px 14px', fontSize: '0.85rem',
                        cursor: 'pointer'
                      }}
                      title="حذف هذا النموذج المحفوظ"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>

              {/* Clickable badges/mentions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>اضغط لإدراج منشن في مؤشر النص:</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  <button
                    type="button"
                    onClick={() => insertMention('{name}')}
                    style={{
                      background: 'rgba(99, 102, 241, 0.12)',
                      color: '#a5b4fc', border: '1px solid rgba(99, 102, 241, 0.25)',
                      borderRadius: '8px', padding: '5px 10px', fontSize: '0.78rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '4px'
                    }}
                  >
                    👤 اسم العميل
                  </button>
                  <button
                    type="button"
                    onClick={() => insertMention('{points}')}
                    style={{
                      background: 'rgba(245, 158, 11, 0.12)',
                      color: '#fde047', border: '1px solid rgba(245, 158, 11, 0.25)',
                      borderRadius: '8px', padding: '5px 10px', fontSize: '0.78rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '4px'
                    }}
                  >
                    ✨ نقاط العميل ({waCustomer.points || 0})
                  </button>
                  <button
                    type="button"
                    onClick={() => insertMention('{pharmacy}')}
                    style={{
                      background: 'rgba(16, 185, 129, 0.12)',
                      color: '#6ee7b7', border: '1px solid rgba(16, 185, 129, 0.25)',
                      borderRadius: '8px', padding: '5px 10px', fontSize: '0.78rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '4px'
                    }}
                  >
                    🏥 الصيدلية
                  </button>
                  <button
                    type="button"
                    onClick={() => insertMention('{link}')}
                    style={{
                      background: 'rgba(236, 72, 153, 0.12)',
                      color: '#f9a8d4', border: '1px solid rgba(236, 72, 153, 0.25)',
                      borderRadius: '8px', padding: '5px 10px', fontSize: '0.78rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '4px'
                    }}
                  >
                    🔗 الرابط
                  </button>
                </div>
              </div>

              {/* Textarea edit */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>تعديل قالب الرسالة:</label>
                <textarea 
                  ref={waTextareaRef}
                  className="premium-input" 
                  value={messageText}
                  onChange={e => setMessageText(e.target.value)}
                  style={{ width: '100%', height: '110px', padding: '12px', fontSize: '0.9rem', resize: 'none', lineHeight: '1.6' }}
                  placeholder="اكتب قالب رسالتك هنا..."
                />
              </div>

              {/* Save Template Section */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.01)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 600 }}>💾 حفظ الرسالة الحالية كنموذج جديد:</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="premium-input"
                    placeholder="اسم النموذج..."
                    value={newTemplateTitle}
                    onChange={e => setNewTemplateTitle(e.target.value)}
                    style={{ flex: 1, padding: '6px 12px', fontSize: '0.8rem' }}
                  />
                  <button
                    type="button"
                    onClick={handleSaveCurrentAsTemplate}
                    style={{
                      background: 'rgba(59, 130, 246, 0.15)',
                      color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px', padding: '6px 14px', fontSize: '0.8rem',
                      fontWeight: 'bold', cursor: 'pointer'
                    }}
                  >
                    حفظ
                  </button>
                </div>
              </div>

              {/* Live Preview */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>👁️ المعاينة الحية للرسالة المرسلة:</label>
                <div style={{
                  background: 'rgba(16, 185, 129, 0.05)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  borderRadius: '12px',
                  padding: '10px 14px',
                  fontSize: '0.82rem',
                  color: '#e2e8f0',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                  maxHeight: '90px',
                  overflowY: 'auto',
                  fontFamily: 'Cairo, sans-serif'
                }}>
                  {messageText
                    .replace(/{name}/g, waCustomer.name || '')
                    .replace(/{points}/g, String(waCustomer.points || 0))
                    .replace(/{pharmacy}/g, currentUserData?.pharmacyName || 'دكتور مصر')
                    .replace(/{link}/g, `${window.location.origin}/?shop=${user?.uid}`)}
                </div>
              </div>

              {/* Send Button */}
              <button
                onClick={executeSendWhatsApp}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span>فتح المحادثة في واتساب</span>
                <span>💬</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mutator Modals */}
      {isAddModalOpen && createPortal(
        <div className="backdrop-fade-in" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(3, 7, 18, 0.7)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          zIndex: 99999,
          display: 'grid',
          placeItems: 'center',
          overflowY: 'auto',
          padding: '40px 20px'
        }} onClick={() => setIsAddModalOpen(false)}>
          <div className="modal-zoom-in" style={{
            width: '100%', maxWidth: '450px',
            background: 'linear-gradient(135deg, #0f172a 0%, #030712 100%)',
            border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '24px',
            padding: '28px 24px 24px 24px', boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
            position: 'relative', direction: 'rtl', textAlign: 'right'
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* Close Button */}
            <button 
              onClick={() => setIsAddModalOpen(false)}
              style={{
                position: 'absolute',
                top: '18px',
                left: '18px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '1.25rem',
                transition: 'all 0.2s ease',
                outline: 'none',
                lineHeight: 1
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                e.currentTarget.style.color = '#f87171';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
            >
              &times;
            </button>

            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>➕</span> إضافة عميل جديد
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>الاسم بالكامل *</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px' }}
                  value={formName} 
                  onChange={e => setFormName(e.target.value)} 
                  required 
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>رقم الهاتف *</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px', textAlign: 'left', fontFamily: 'Outfit' }}
                  value={formPhone} 
                  onChange={e => setFormPhone(e.target.value)} 
                  required 
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>نقاط الكاش باك الأولية (ج.م)</label>
                <input 
                  type="number" 
                  className="premium-input" 
                  style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px', fontFamily: 'Outfit' }}
                  value={formPoints} 
                  onChange={e => setFormPoints(e.target.value)} 
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>العنوان الأساسي</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px' }}
                  value={formAddress} 
                  onChange={e => setFormAddress(e.target.value)} 
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>خصم المحلي %</label>
                  <input 
                    type="number" 
                    min="0"
                    max="100"
                    className="premium-input" 
                    style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px', fontFamily: 'Outfit' }}
                    value={formLocalDiscount} 
                    onChange={e => setFormLocalDiscount(e.target.value)} 
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>خصم المستورد %</label>
                  <input 
                    type="number" 
                    min="0"
                    max="100"
                    className="premium-input" 
                    style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px', fontFamily: 'Outfit' }}
                    value={formImportedDiscount} 
                    onChange={e => setFormImportedDiscount(e.target.value)} 
                  />
                </div>
              </div>
              <button className="gradient-btn" onClick={executeAddCustomer} style={{ padding: '12px', fontWeight: 'bold', fontSize: '1rem', marginTop: '8px', borderRadius: '12px' }}>حفظ العميل</button>
            </div>
          </div>
        </div>, document.body )}

      {isEditModalOpen && createPortal(
        <div className="backdrop-fade-in" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(3, 7, 18, 0.7)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          zIndex: 99999,
          display: 'grid',
          placeItems: 'center',
          overflowY: 'auto',
          padding: '40px 20px'
        }} onClick={() => setIsEditModalOpen(false)}>
          <div className="modal-zoom-in" style={{
            width: '100%', maxWidth: '450px',
            background: 'linear-gradient(135deg, #0f172a 0%, #030712 100%)',
            border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '24px',
            padding: '28px 24px 24px 24px', boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
            position: 'relative', direction: 'rtl', textAlign: 'right'
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* Close Button */}
            <button 
              onClick={() => setIsEditModalOpen(false)}
              style={{
                position: 'absolute',
                top: '18px',
                left: '18px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '1.25rem',
                transition: 'all 0.2s ease',
                outline: 'none',
                lineHeight: 1
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                e.currentTarget.style.color = '#f87171';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
            >
              &times;
            </button>

            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>✏️</span> تعديل بيانات العميل
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>الاسم بالكامل *</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px' }}
                  value={formName} 
                  onChange={e => setFormName(e.target.value)} 
                  required 
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>رقم الهاتف *</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px', textAlign: 'left', fontFamily: 'Outfit' }}
                  value={formPhone} 
                  onChange={e => setFormPhone(e.target.value)} 
                  required 
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>رصيد النقاط الحالي (ج.م)</label>
                <input 
                  type="number" 
                  className="premium-input" 
                  style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px', fontFamily: 'Outfit' }}
                  value={formPoints} 
                  onChange={e => setFormPoints(e.target.value)} 
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>العنوان الأساسي</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px' }}
                  value={formAddress} 
                  onChange={e => setFormAddress(e.target.value)} 
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>خصم المحلي %</label>
                  <input 
                    type="number" 
                    min="0"
                    max="100"
                    className="premium-input" 
                    style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px', fontFamily: 'Outfit' }}
                    value={formLocalDiscount} 
                    onChange={e => setFormLocalDiscount(e.target.value)} 
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>خصم المستورد %</label>
                  <input 
                    type="number" 
                    min="0"
                    max="100"
                    className="premium-input" 
                    style={{ width: '100%', boxSizing: 'border-box', height: '42px', padding: '0 16px', fontFamily: 'Outfit' }}
                    value={formImportedDiscount} 
                    onChange={e => setFormImportedDiscount(e.target.value)} 
                  />
                </div>
              </div>
              <button className="gradient-btn" onClick={executeEditCustomer} style={{ padding: '12px', fontWeight: 'bold', fontSize: '1rem', marginTop: '8px', borderRadius: '12px' }}>حفظ التغييرات</button>
            </div>
          </div>
        </div>, document.body )}

      {isImportModalOpen && createPortal(
        <div className="backdrop-fade-in" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(3, 7, 18, 0.7)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          zIndex: 99999,
          display: 'grid',
          placeItems: 'center',
          overflowY: 'auto',
          padding: '40px 20px'
        }} onClick={() => { if (!importLoading) setIsImportModalOpen(false); }}>
          <div className="modal-zoom-in" style={{
            width: '100%', maxWidth: '600px',
            background: 'linear-gradient(135deg, #0f172a 0%, #030712 100%)',
            border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '24px',
            padding: '28px 24px 24px 24px', boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
            position: 'relative', direction: 'rtl', textAlign: 'right'
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* Close Button */}
            <button 
              onClick={() => setIsImportModalOpen(false)}
              disabled={importLoading}
              style={{
                position: 'absolute',
                top: '18px',
                left: '18px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '1.25rem',
                transition: 'all 0.2s ease',
                outline: 'none',
                lineHeight: 1
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                e.currentTarget.style.color = '#f87171';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
            >
              &times;
            </button>

            <h3 style={{ fontSize: '1.4rem', fontWeight: 900, color: '#fff', marginTop: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📥</span> استيراد العملاء من شيت إكسل / Excel
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: '1.6' }}>
              ارفع ملف Excel (.xlsx أو .xls) أو ملف CSV يحتوي على داتا عملائك لتسجيلهم في الصيدلية. 
              <br />
              <strong style={{ color: 'var(--primary)' }}>عناوين الأعمدة المقبولة في الملف:</strong> الاسم (مطلوب)، الهاتف (مطلوب)، العنوان، النقاط، خصم محلي %، خصم مستورد %.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div style={{
                border: '2px dashed rgba(255, 255, 255, 0.12)',
                borderRadius: '16px',
                padding: '24px',
                textAlign: 'center',
                background: 'rgba(255, 255, 255, 0.01)',
                cursor: 'pointer',
                position: 'relative'
              }}>
                <input 
                  type="file" 
                  accept=".xlsx, .xls, .csv" 
                  onChange={handleFileChange}
                  disabled={importLoading}
                  style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    opacity: 0, cursor: 'pointer', width: '100%', height: '100%'
                  }}
                />
                <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '8px' }}>📊</span>
                <span style={{ fontSize: '0.9rem', color: '#cbd5e1', fontWeight: 'bold' }}>اضغط هنا لاختيار الملف من جهازك</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginTop: '4px' }}>أو اسحب الملف وأفلته هنا (xlsx, xls, csv)</span>
              </div>

              {importLoading && (
                <div style={{ textAlign: 'center', padding: '12px' }}>
                  <div className="pm-logo-glow-ring" style={{ border: '3px solid var(--primary)', borderTopColor: 'transparent', width: '28px', height: '28px', margin: '0 auto 8px' }}></div>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    {importProgress.total > 0 
                      ? `جاري حفظ البيانات في قاعدة البيانات: (${importProgress.current} / ${importProgress.total})...` 
                      : 'جاري معالجة وقراءة الملف...'}
                  </span>
                </div>
              )}

              {importError && (
                <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', color: '#f87171', padding: '12px', borderRadius: '12px', fontSize: '0.85rem', lineHeight: '1.5' }}>
                  ⚠️ {importError}
                </div>
              )}

              {importData.length > 0 && !importLoading && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.85rem', color: '#10b981', fontWeight: 'bold' }}>✓ تم العثور على {importData.length} عميل جاهز للاستيراد:</span>
                  </div>
                  <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid rgba(255, 255, 255, 0.06)', borderRadius: '12px', background: 'rgba(0, 0, 0, 0.2)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', textAlign: 'right' }}>
                      <thead>
                        <tr style={{ background: 'rgba(255, 255, 255, 0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <th style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>الاسم</th>
                          <th style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>رقم الهاتف</th>
                          <th style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>النقاط</th>
                          <th style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>خصم محلي</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importData.slice(0, 5).map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                            <td style={{ padding: '8px 12px', color: '#fff' }}>{row.name}</td>
                            <td style={{ padding: '8px 12px', color: '#cbd5e1', fontFamily: 'Outfit' }}>{row.phone}</td>
                            <td style={{ padding: '8px 12px', color: 'var(--accent-gold)', fontFamily: 'Outfit' }}>{row.points}</td>
                            <td style={{ padding: '8px 12px', color: '#cbd5e1' }}>{row.localDiscount}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {importData.length > 5 && (
                      <div style={{ padding: '8px 12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.02)' }}>
                        والمزيد... ({importData.length - 5} عملاء آخرين)
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setIsImportModalOpen(false)}
                disabled={importLoading}
                className="premium-btn" 
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  color: 'var(--text-secondary)', padding: '10px 20px', borderRadius: '12px',
                  fontWeight: 'bold', fontSize: '0.9rem', cursor: 'pointer'
                }}
              >
                إلغاء
              </button>
              <button 
                onClick={executeImportCustomers}
                disabled={importLoading || importData.length === 0}
                className="gradient-btn" 
                style={{
                  padding: '10px 24px', borderRadius: '12px',
                  fontWeight: 'bold', fontSize: '0.9rem', opacity: (importLoading || importData.length === 0) ? 0.5 : 1
                }}
              >
                {importLoading ? 'جاري الاستيراد...' : 'حفظ واستيراد الداتا'}
              </button>
            </div>

          </div>
        </div>, document.body )}

      </>
      )}
    </div>
  );
}


// ==========================================
// 💼 INSURANCE & SYNDICATES VIEW COMPONENT
// ==========================================
export function InsuranceView({ user, sales, setSales, db, currentUserData, setGlobalLoading, inventory = [] }) {
  const [activeSubTab, setActiveSubTab] = React.useState('reports'); // 'reports' or 'contracts'
  const [contracts, setContracts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Form States
  const [editingContract, setEditingContract] = React.useState(null);
  const [formName, setFormName] = React.useState('');
  const [formCopayLocal, setFormCopayLocal] = React.useState(20);
  const [formCopayImported, setFormCopayImported] = React.useState(50);
  const [formMaxCoverage, setFormMaxCoverage] = React.useState('');
  const [formExcludedCats, setFormExcludedCats] = React.useState([]);
  const [formMonthlyCoverageLimit, setFormMonthlyCoverageLimit] = React.useState('');
  const [formRequireApprovalAbove, setFormRequireApprovalAbove] = React.useState('');
  const [formAllowDiscounts, setFormAllowDiscounts] = React.useState(true);
  const [formNotes, setFormNotes] = React.useState('');
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  // Filter States
  const [selectedContractId, setSelectedContractId] = React.useState('');
  const [startDate, setStartDate] = React.useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = React.useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Fetch contracts
  React.useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);
    const unsub = onSnapshot(collection(db, 'users', user.uid, 'insuranceContracts'), (snap) => {
      const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setContracts(fetched);
      if (fetched.length > 0 && !selectedContractId) {
        setSelectedContractId(fetched[0].id);
      }
      setLoading(false);
    }, (err) => {
      console.error("Failed to fetch insurance contracts:", err);
      setLoading(false);
    });
    return unsub;
  }, [user, db]);

  const handleOpenAdd = () => {
    setEditingContract(null);
    setFormName('');
    setFormCopayLocal(20);
    setFormCopayImported(50);
    setFormMaxCoverage('');
    setFormExcludedCats([]);
    setFormMonthlyCoverageLimit('');
    setFormRequireApprovalAbove('');
    setFormAllowDiscounts(true);
    setFormNotes('');
    setIsModalOpen(true);
  };

  const handleOpenEdit = (contract) => {
    setEditingContract(contract);
    setFormName(contract.name || '');
    setFormCopayLocal(contract.copayPercentageLocal !== undefined ? contract.copayPercentageLocal : (contract.copayPercentage !== undefined ? contract.copayPercentage : 20));
    setFormCopayImported(contract.copayPercentageImported !== undefined ? contract.copayPercentageImported : (contract.copayPercentage !== undefined ? contract.copayPercentage : 50));
    setFormMaxCoverage(contract.maxCoverageLimit !== undefined ? contract.maxCoverageLimit : '');
    
    let cats = [];
    if (Array.isArray(contract.excludedCategories)) {
      cats = contract.excludedCategories;
    } else if (typeof contract.excludedCategories === 'string') {
      cats = contract.excludedCategories.split(',').map(c => c.trim()).filter(Boolean);
    }
    setFormExcludedCats(cats);
    setFormMonthlyCoverageLimit(contract.monthlyCoverageLimit !== undefined ? contract.monthlyCoverageLimit : '');
    setFormRequireApprovalAbove(contract.requireApprovalAbove !== undefined ? contract.requireApprovalAbove : '');
    setFormAllowDiscounts(contract.allowDiscounts !== undefined ? contract.allowDiscounts : true);
    setFormNotes(contract.notes || '');
    setIsModalOpen(true);
  };

  const handleSaveContract = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      window.triggerDrMasrAlert('⚠️ يرجى إدخال اسم جهة التعاقد!');
      return;
    }
    setGlobalLoading(true);
    try {
      const contractData = {
        name: formName.trim(),
        copayPercentage: Number(formCopayLocal),
        coveragePercentage: 100 - Number(formCopayLocal),
        copayPercentageLocal: Number(formCopayLocal),
        copayPercentageImported: Number(formCopayImported),
        maxCoverageLimit: formMaxCoverage ? Number(formMaxCoverage) : 0,
        excludedCategories: formExcludedCats,
        monthlyCoverageLimit: formMonthlyCoverageLimit ? Number(formMonthlyCoverageLimit) : 0,
        requireApprovalAbove: formRequireApprovalAbove ? Number(formRequireApprovalAbove) : 0,
        allowDiscounts: formAllowDiscounts,
        notes: formNotes.trim(),
        isActive: editingContract ? (editingContract.isActive !== undefined ? editingContract.isActive : true) : true,
        updatedAt: new Date().toISOString()
      };

      if (editingContract) {
        const docRef = doc(db, 'users', user.uid, 'insuranceContracts', editingContract.id);
        await updateDoc(docRef, contractData);
        window.triggerDrMasrToast('✅ تم تعديل عقد التعاقد بنجاح!');
      } else {
        contractData.createdAt = new Date().toISOString();
        const colRef = collection(db, 'users', user.uid, 'insuranceContracts');
        await addDoc(colRef, contractData);
        window.triggerDrMasrToast('✅ تم إضافة عقد التعاقد بنجاح!');
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error("Error saving contract:", err);
      window.triggerDrMasrAlert('❌ حدث خطأ أثناء حفظ عقد التعاقد.');
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleToggleStatus = async (contract) => {
    try {
      const docRef = doc(db, 'users', user.uid, 'insuranceContracts', contract.id);
      await updateDoc(docRef, { isActive: !contract.isActive });
      window.triggerDrMasrToast(contract.isActive ? '🔒 تم تعطيل التعاقد بنجاح' : '🔓 تم تفعيل التعاقد بنجاح');
    } catch (err) {
      console.error("Failed to toggle contract status:", err);
    }
  };

  const handleDeleteContract = (contract) => {
    window.triggerDrMasrConfirm(
      `هل أنت متأكد من حذف عقد التعاقد (${contract.name})؟\nسيتم إزالة العقد من القائمة ولكن الفواتير السابقة المسجلة عليه لن تتأثر.`,
      async () => {
        setGlobalLoading(true);
        try {
          const docRef = doc(db, 'users', user.uid, 'insuranceContracts', contract.id);
          await deleteDoc(docRef);
          window.triggerDrMasrToast('🗑️ تم حذف التعاقد بنجاح');
        } catch (err) {
          console.error("Failed to delete contract:", err);
          window.triggerDrMasrAlert('❌ فشل حذف العقد.');
        } finally {
          setGlobalLoading(false);
        }
      }
    );
  };

  // Filtered sales for reporting (hiding settled claims)
  const filteredSales = React.useMemo(() => {
    return sales.filter(sale => {
      if (!sale.isInsurance) return false;
      // Hide settled bills from this management view
      if (sale.insuranceClaimStatus === 'settled') return false;
      if (selectedContractId && sale.insuranceContractId !== selectedContractId) return false;
      if (!sale.timestamp) return false;
      const saleDate = sale.timestamp.split('T')[0];
      return saleDate >= startDate && saleDate <= endDate;
    });
  }, [sales, selectedContractId, startDate, endDate]);

  const stats = React.useMemo(() => {
    let totalClaim = 0;
    let totalPatient = 0;
    filteredSales.forEach(s => {
      totalClaim += (s.insuranceShare || 0);
      totalPatient += (s.patientShare || 0);
    });
    return {
      totalClaim: Math.round(totalClaim * 100) / 100,
      totalPatient: Math.round(totalPatient * 100) / 100,
      count: filteredSales.length
    };
  }, [filteredSales]);

  // Settle all filtered claims
  const handleSettleAllClaims = () => {
    if (filteredSales.length === 0) return;
    const pendingSales = filteredSales.filter(s => s.insuranceClaimStatus !== 'settled');
    if (pendingSales.length === 0) {
      window.triggerDrMasrAlert('✅ جميع الفواتير في هذه الفترة مسواة بالفعل!');
      return;
    }
    window.triggerDrMasrConfirm(
      `هل ترغب في تسوية جميع الفواتير المعلقة في هذه الفترة (${pendingSales.length} فاتورة)؟\nسيتم وضع علامة "تمت التسوية" عليها كإثبات لاستلام المبالغ المستحقة من الشركة.`,
      async () => {
        setGlobalLoading(true);
        try {
          const batch = writeBatch(db);
          pendingSales.forEach(sale => {
            const docRef = doc(db, 'users', user.uid, 'sales', sale.id);
            batch.update(docRef, { insuranceClaimStatus: 'settled' });
          });
          await batch.commit();

          // Optimistically update local sales array
          setSales(prev => prev.map(s => {
            const isPending = pendingSales.find(ps => ps.id === s.id);
            return isPending ? { ...s, insuranceClaimStatus: 'settled' } : s;
          }));

          window.triggerDrMasrToast('✅ تم تسوية مطالبات الفترة بنجاح! 🤝');
        } catch (err) {
          console.error("Failed to settle claims:", err);
          window.triggerDrMasrAlert('❌ حدث خطأ أثناء إجراء التسوية.');
        } finally {
          setGlobalLoading(false);
        }
      }
    );
  };

  const handleUpdateSingleStatus = async (sale, newStatus) => {
    try {
      const docRef = doc(db, 'users', user.uid, 'sales', sale.id);
      await updateDoc(docRef, { insuranceClaimStatus: newStatus });
      
      // Update local state
      setSales(prev => prev.map(s => s.id === sale.id ? { ...s, insuranceClaimStatus: newStatus } : s));
      window.triggerDrMasrToast('✅ تم تحديث حالة المطالبة الفردية!');
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    if (filteredSales.length === 0) return;
    const contract = contracts.find(c => c.id === selectedContractId);
    const contractName = contract ? contract.name : 'مطالبة تأمين';
    
    let csv = '\ufeff'; // UTF-8 BOM for Arabic Excel support
    csv += 'رقم الفاتورة,التاريخ,اسم المريض,رقم الموافقة,إجمالي الفاتورة,تحمل الشركة,تحمل المريض,حالة المطالبة\n';
    
    filteredSales.forEach(s => {
      const dateStr = new Date(s.timestamp).toLocaleDateString('ar-EG');
      const statusStr = s.insuranceClaimStatus === 'settled' ? 'تمت التسوية' : s.insuranceClaimStatus === 'claimed' ? 'تم تقديمها' : 'معلقة';
      csv += `${s.invoiceId},${dateStr},${s.customerName || 'عميل نقدي عام'},${s.insuranceApprovalNumber || 'لا يوجد'},${s.grandTotal},${s.insuranceShare},${s.patientShare},${statusStr}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `مطالبة_${contractName}_${startDate}_إلى_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print Claim Sheet
  const handlePrintClaimSheet = () => {
    if (filteredSales.length === 0) return;
    const contract = contracts.find(c => c.id === selectedContractId);
    const contractName = contract ? contract.name : '';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="ar" dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>كشف مطالبة تأمينية - ${contractName}</title>
        <style>
          body {
            font-family: 'Cairo', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 30px;
            color: #333;
            background-color: #fff;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 3px double #333;
            padding-bottom: 15px;
            margin-bottom: 25px;
          }
          .header-title {
            text-align: center;
            flex-grow: 1;
          }
          .header-title h1 {
            margin: 0 0 10px 0;
            font-size: 24px;
            color: #111;
          }
          .header-title p {
            margin: 0;
            font-size: 14px;
            color: #666;
          }
          .logo-box {
            font-size: 28px;
            font-weight: bold;
            border: 2px solid #333;
            padding: 5px 15px;
            border-radius: 8px;
          }
          .summary-box {
            background-color: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 15px 20px;
            margin-bottom: 25px;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
          }
          .summary-item {
            text-align: center;
          }
          .summary-item label {
            display: block;
            font-size: 13px;
            color: #64748b;
            margin-bottom: 4px;
          }
          .summary-item span {
            font-size: 18px;
            font-weight: bold;
            color: #0f172a;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 40px;
          }
          th, td {
            border: 1px solid #cbd5e1;
            padding: 10px 12px;
            text-align: right;
            font-size: 13px;
          }
          th {
            background-color: #f1f5f9;
            color: #1e293b;
            font-weight: bold;
          }
          tr:nth-child(even) td {
            background-color: #f8fafc;
          }
          .footer-signs {
            margin-top: 50px;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 30px;
            text-align: center;
            font-size: 14px;
          }
          .sign-box {
            border-top: 1px dashed #94a3b8;
            padding-top: 10px;
            margin-top: 50px;
          }
          @media print {
            body { padding: 0; }
            button { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div style="text-align: right;">
            <strong style="font-size: 16px;">${currentUserData?.pharmacyName || 'صيدلية دكتور مصر'}</strong><br/>
            <span style="font-size: 12px; color: #666;">كشف حساب المطالبات المالية</span>
          </div>
          <div class="header-title">
            <h1>كشف مطالبة مالية شهرية</h1>
            <p>الفترة من: ${startDate} إلى ${endDate}</p>
          </div>
          <div class="logo-box">🏥</div>
        </div>

        <div style="margin-bottom: 20px; font-size: 14px;">
          جهة التعاقد المستحقة: <strong style="font-size: 16px; color: #0284c7;">${contractName}</strong>
        </div>

        <div class="summary-box">
          <div class="summary-item">
            <label>إجمالي مستحقات الشركة</label>
            <span style="color: #0284c7;">${stats.totalClaim} ج.م</span>
          </div>
          <div class="summary-item">
            <label>إجمالي قيمة المبيعات</label>
            <span>${Math.round((stats.totalClaim + stats.totalPatient)*100)/100} ج.م</span>
          </div>
          <div class="summary-item">
            <label>إجمالي عدد الفواتير</label>
            <span>${stats.count} فاتورة</span>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>رقم الفاتورة</th>
              <th>التاريخ والوقت</th>
              <th>اسم المريض / العميل</th>
              <th>رقم موافقة التأمين</th>
              <th>إجمالي الفاتورة</th>
              <th>تحمل الشركة</th>
              <th>تحمل المريض</th>
              <th>الحالة</th>
            </tr>
          </thead>
          <tbody>
            ${filteredSales.map(s => `
              <tr>
                <td style="font-weight: bold;">#${s.invoiceId}</td>
                <td>${new Date(s.timestamp).toLocaleString('ar-EG')}</td>
                <td>${s.customerName || 'عميل نقدي عام'}</td>
                <td style="font-family: monospace; font-weight: bold;">${s.insuranceApprovalNumber || 'لا يوجد'}</td>
                <td>${s.grandTotal} ج.م</td>
                <td style="font-weight: bold; color: #0284c7;">${s.insuranceShare} ج.م</td>
                <td>${s.patientShare} ج.م</td>
                <td>${s.insuranceClaimStatus === 'settled' ? 'تمت التسوية' : s.insuranceClaimStatus === 'claimed' ? 'تم تقديمها' : 'معلقة'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer-signs">
          <div>
            <span>توقيع الصيدلي المسؤول</span>
            <div class="sign-box"></div>
          </div>
          <div>
            <span>خاتم الصيدلية</span>
            <div class="sign-box"></div>
          </div>
          <div>
            <span>توقيع مراجع شركة التأمين</span>
            <div class="sign-box"></div>
          </div>
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px', direction: 'rtl', textAlign: 'right' }}>
      
      {/* Upper Navigation Tabs */}
      <div style={{ display: 'flex', gap: '12px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
        <button 
          onClick={() => setActiveSubTab('reports')}
          style={{
            padding: '10px 20px',
            borderRadius: '10px',
            fontSize: '0.95rem',
            fontWeight: 700,
            cursor: 'pointer',
            border: 'none',
            background: activeSubTab === 'reports' ? 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)' : 'rgba(255,255,255,0.03)',
            color: activeSubTab === 'reports' ? '#fff' : 'var(--text-secondary)',
            boxShadow: activeSubTab === 'reports' ? '0 4px 15px rgba(59, 130, 246, 0.25)' : 'none',
            transition: 'all 0.3s ease'
          }}
        >
          📊 التقارير والمطالبات المالية
        </button>
        <button 
          onClick={() => setActiveSubTab('contracts')}
          style={{
            padding: '10px 20px',
            borderRadius: '10px',
            fontSize: '0.95rem',
            fontWeight: 700,
            cursor: 'pointer',
            border: 'none',
            background: activeSubTab === 'contracts' ? 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)' : 'rgba(255,255,255,0.03)',
            color: activeSubTab === 'contracts' ? '#fff' : 'var(--text-secondary)',
            boxShadow: activeSubTab === 'contracts' ? '0 4px 15px rgba(59, 130, 246, 0.25)' : 'none',
            transition: 'all 0.3s ease'
          }}
        >
          🤝 عقود الشركات والنقابات
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', color: 'var(--text-secondary)' }}>جاري تحميل البيانات...</div>
      ) : activeSubTab === 'contracts' ? (
        
        /* Sub-tab 1: Contract Management */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff', margin: 0 }}>👥 إدارة عقود الجهات والنقابات</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: '4px 0 0 0' }}>قم بتعريف الشركات ونقابات التعاقد ونسب تحمل المريض وتغطية الفواتير</p>
            </div>
            <button 
              onClick={handleOpenAdd}
              className="premium-btn-primary"
              style={{
                padding: '12px 24px',
                borderRadius: '10px',
                fontSize: '0.92rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              ➕ إضافة جهة تعاقد جديدة
            </button>
          </div>

          {contracts.length === 0 ? (
            <div className="premium-card" style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
              🚫 لا توجد عقود تأمينية مسجلة حالياً. قم بالنقر على زر الإضافة في الأعلى لتعريف أول جهة تعاقد!
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
              {contracts.map(contract => (
                <div key={contract.id} className="premium-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  borderTop: `4px solid ${contract.isActive ? 'var(--accent-green)' : '#4b5563'}`,
                  background: 'rgba(13, 20, 36, 0.45)',
                  opacity: contract.isActive ? 1 : 0.8
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ fontSize: '1.18rem', fontWeight: 700, color: '#fff', margin: 0 }}>{contract.name}</h3>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        fontWeight: 'bold', 
                        padding: '2px 8px', 
                        borderRadius: '4px',
                        background: contract.isActive ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255,255,255,0.05)',
                        color: contract.isActive ? '#34d399' : '#9ca3af',
                        display: 'inline-block',
                        marginTop: '6px'
                      }}>
                        {contract.isActive ? '🔓 نشط ومتاح في الكاشير' : '🔒 معطل ومخفي'}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button 
                        onClick={() => handleOpenEdit(contract)} 
                        title="تعديل العقد"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', color: '#60a5fa', cursor: 'pointer', padding: '6px' }}
                      >
                        ✏️
                      </button>
                      <button 
                        onClick={() => handleToggleStatus(contract)} 
                        title={contract.isActive ? 'تعطيل' : 'تفعيل'}
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', color: contract.isActive ? '#fbbf24' : '#34d399', cursor: 'pointer', padding: '6px' }}
                      >
                        {contract.isActive ? '🔒' : '🔓'}
                      </button>
                      <button 
                        onClick={() => handleDeleteContract(contract)} 
                        title="حذف العقد"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', color: 'var(--accent-pink)', cursor: 'pointer', padding: '6px' }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    <div>🌐 محلي: <span style={{ color: '#fff', fontWeight: 'bold' }}>{contract.copayPercentageLocal !== undefined ? contract.copayPercentageLocal : contract.copayPercentage || 20}%</span></div>
                    <div>📦 مستورد: <span style={{ color: '#fff', fontWeight: 'bold' }}>{contract.copayPercentageImported !== undefined ? contract.copayPercentageImported : contract.copayPercentage || 50}%</span></div>
                    <div>📄 حد الفاتورة: <span style={{ color: '#fff', fontWeight: 'bold' }}>{contract.maxCoverageLimit ? `${contract.maxCoverageLimit} ج.م` : 'مفتوح'}</span></div>
                    <div>📅 حد شهري: <span style={{ color: '#fff', fontWeight: 'bold' }}>{contract.monthlyCoverageLimit ? `${contract.monthlyCoverageLimit} ج.م` : 'مفتوح'}</span></div>
                    <div>🔐 طلب موافقة: <span style={{ color: '#fff', fontWeight: 'bold' }}>{contract.requireApprovalAbove ? `فوق ${contract.requireApprovalAbove} ج.م` : 'لا يطلب'}</span></div>
                    <div>🏷️ دمج الخصومات: <span style={{ color: contract.allowDiscounts !== false ? '#34d399' : '#f87171', fontWeight: 'bold' }}>{contract.allowDiscounts !== false ? 'مسموح' : 'ممنوع'}</span></div>
                  </div>

                  {Array.isArray(contract.excludedCategories) && contract.excludedCategories.length > 0 && (
                    <div style={{ fontSize: '0.78rem', color: '#fbbf24', background: 'rgba(251, 191, 36, 0.05)', padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(251, 191, 36, 0.15)' }}>
                      🚫 مستبعد: {contract.excludedCategories.join('، ')}
                    </div>
                  )}

                  {contract.notes && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.15)', padding: '8px 10px', borderRadius: '6px' }}>
                      📝 {contract.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        
        /* Sub-tab 2: Claims & Monthly Reports */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Filters Card */}
          <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px', background: 'rgba(13, 20, 36, 0.5)' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff', margin: 0 }}>🔍 مرشحات كشف الحساب والمطالبات</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              
              {/* Select Company */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>جهة التعاقد المستهدفة</label>
                <select
                  className="premium-input"
                  style={{ height: '42px', padding: '0 12px', cursor: 'pointer' }}
                  value={selectedContractId}
                  onChange={e => setSelectedContractId(e.target.value)}
                >
                  <option value="">-- كل التعاقدات --</option>
                  {contracts.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Start Date */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>تاريخ البدء</label>
                <input 
                  type="date"
                  className="premium-input"
                  style={{ height: '42px', padding: '0 12px', fontFamily: 'Outfit' }}
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </div>

              {/* End Date */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>تاريخ الانتهاء</label>
                <input 
                  type="date"
                  className="premium-input"
                  style={{ height: '42px', padding: '0 12px', fontFamily: 'Outfit' }}
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                />
              </div>

            </div>

            {/* Quick Date Presets */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button 
                onClick={() => {
                  const now = new Date();
                  setStartDate(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
                  setEndDate(now.toISOString().split('T')[0]);
                }}
                className="gradient-btn-secondary" 
                style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '6px' }}
              >
                📅 الشهر الحالي
              </button>
              <button 
                onClick={() => {
                  const now = new Date();
                  const firstOfPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                  const lastOfPrev = new Date(now.getFullYear(), now.getMonth(), 0);
                  setStartDate(firstOfPrev.toISOString().split('T')[0]);
                  setEndDate(lastOfPrev.toISOString().split('T')[0]);
                }}
                className="gradient-btn-secondary" 
                style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '6px' }}
              >
                📅 الشهر الماضي
              </button>
              <button 
                onClick={() => {
                  const now = new Date();
                  setStartDate(now.toISOString().split('T')[0]);
                  setEndDate(now.toISOString().split('T')[0]);
                }}
                className="gradient-btn-secondary" 
                style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '6px' }}
              >
                📅 اليوم
              </button>
            </div>
          </div>

          {/* Stats Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
            <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.15) 100%)', border: '1px solid rgba(59, 130, 246, 0.25)' }}>
              <span style={{ fontSize: '0.85rem', color: '#93c5fd', display: 'block' }}>💵 إجمالي مطالبات الشركة</span>
              <strong style={{ fontSize: '1.6rem', color: '#3b82f6', display: 'block', marginTop: '6px', fontFamily: 'Outfit' }}>{stats.totalClaim} ج.م</strong>
            </div>
            <div className="premium-card" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.15) 100%)', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
              <span style={{ fontSize: '0.85rem', color: '#a7f3d0', display: 'block' }}>💊 إجمالي مدفوعات المرضى</span>
              <strong style={{ fontSize: '1.6rem', color: 'var(--accent-green)', display: 'block', marginTop: '6px', fontFamily: 'Outfit' }}>{stats.totalPatient} ج.م</strong>
            </div>
            <div className="premium-card" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)' }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block' }}>🧾 إجمالي عدد الفواتير</span>
              <strong style={{ fontSize: '1.6rem', color: '#fff', display: 'block', marginTop: '6px', fontFamily: 'Outfit' }}>{stats.count} فاتورة</strong>
            </div>
          </div>

          {/* Actions & Report Table */}
          <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ fontSize: '1.18rem', fontWeight: 800, color: '#fff', margin: 0 }}>📋 قائمة الفواتير ومطالبات التعاقد المالي</h3>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  onClick={handlePrintClaimSheet}
                  disabled={filteredSales.length === 0}
                  className="gradient-btn-secondary"
                  style={{ padding: '8px 16px', fontSize: '0.85rem', borderRadius: '8px', cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}
                >
                  🖨️ طباعة كشف المطالبة
                </button>
                <button 
                  onClick={handleExportCSV}
                  disabled={filteredSales.length === 0}
                  className="gradient-btn-secondary"
                  style={{ padding: '8px 16px', fontSize: '0.85rem', borderRadius: '8px', cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}
                >
                  📊 تصدير Excel / CSV
                </button>
                <button 
                  onClick={handleSettleAllClaims}
                  disabled={filteredSales.length === 0}
                  className="gradient-btn"
                  style={{ padding: '8px 16px', fontSize: '0.85rem', borderRadius: '8px', cursor: 'pointer', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff' }}
                >
                  🤝 تسوية مستحقات الفترة
                </button>
              </div>
            </div>

            {filteredSales.length === 0 ? (
              <div style={{ textAlgn: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
                🚫 لا توجد فواتير تأمين مسجلة تطابق المرشحات المحددة في هذه الفترة.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>رقم الفاتورة</th>
                      <th>التاريخ والوقت</th>
                      <th>الجهة</th>
                      <th>رقم الموافقة</th>
                      <th>إجمالي الفاتورة</th>
                      <th>تحمل الشركة</th>
                      <th>تحمل المريض</th>
                      <th>حالة المطالبة</th>
                      <th>تحديث الحالة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSales.map(sale => (
                      <tr key={sale.id}>
                        <td style={{ fontWeight: 700, color: '#fff' }}>#{sale.invoiceId}</td>
                        <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{new Date(sale.timestamp).toLocaleString('ar-EG')}</td>
                        <td>{sale.insuranceContractName}</td>
                        <td style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{sale.insuranceApprovalNumber || 'لا يوجد'}</td>
                        <td style={{ fontWeight: 600 }}>{sale.grandTotal} ج.م</td>
                        <td style={{ fontWeight: 700, color: 'var(--primary)' }}>{sale.insuranceShare} ج.م</td>
                        <td style={{ fontWeight: 600, color: 'var(--accent-green)' }}>{sale.patientShare} ج.م</td>
                        <td>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '0.78rem',
                            fontWeight: 'bold',
                            background: sale.insuranceClaimStatus === 'settled' ? 'rgba(16, 185, 129, 0.15)' : sale.insuranceClaimStatus === 'claimed' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                            color: sale.insuranceClaimStatus === 'settled' ? '#34d399' : sale.insuranceClaimStatus === 'claimed' ? '#60a5fa' : '#fbbf24',
                            display: 'inline-block'
                          }}>
                            {sale.insuranceClaimStatus === 'settled' ? '🤝 مسواة' : sale.insuranceClaimStatus === 'claimed' ? '📄 تم تقديمها' : '⏳ معلقة'}
                          </span>
                        </td>
                        <td>
                          <select
                            style={{ 
                              padding: '4px 8px', 
                              fontSize: '0.8rem', 
                              background: '#141229', 
                              color: '#fff', 
                              border: '1px solid rgba(255,255,255,0.08)', 
                              borderRadius: '6px',
                              cursor: 'pointer' 
                            }}
                            value={sale.insuranceClaimStatus || 'pending'}
                            onChange={e => handleUpdateSingleStatus(sale, e.target.value)}
                          >
                            <option value="pending">⏳ معلقة</option>
                            <option value="claimed">📄 تم تقديمها</option>
                            <option value="settled">🤝 مسواة</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contract Add/Edit Modal */}
      {isModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)', zIndex: 99999,
          display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px'
        }}>
          <div className="premium-card" style={{
            width: '100%', maxWidth: '480px',
            maxHeight: '90vh', overflowY: 'auto',
            background: '#141229', border: '1px solid rgba(139, 92, 246, 0.25)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', borderRadius: '16px',
            padding: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                {editingContract ? '✏️ تعديل عقد التعاقد' : '➕ إضافة جهة تعاقد جديدة'}
              </h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.2rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveContract} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>اسم جهة التعاقد / النيابة</label>
                <input 
                  type="text"
                  className="premium-input"
                  style={{ width: '100%', height: '42px', padding: '0 16px', boxSizing: 'border-box' }}
                  placeholder="مثال: نقابة المهندسين"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>تحمل المريض - محلي (%)</label>
                  <input 
                    type="number"
                    min="0"
                    max="100"
                    className="premium-input"
                    style={{ width: '100%', height: '42px', padding: '0 16px', boxSizing: 'border-box', fontFamily: 'Outfit' }}
                    value={formCopayLocal}
                    onChange={e => setFormCopayLocal(Math.max(0, Math.min(100, Number(e.target.value))))}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>تحمل المريض - مستورد (%)</label>
                  <input 
                    type="number"
                    min="0"
                    max="100"
                    className="premium-input"
                    style={{ width: '100%', height: '42px', padding: '0 16px', boxSizing: 'border-box', fontFamily: 'Outfit' }}
                    value={formCopayImported}
                    onChange={e => setFormCopayImported(Math.max(0, Math.min(100, Number(e.target.value))))}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>الحد الأقصى للفاتورة (ج.م)</label>
                  <input 
                    type="number"
                    min="0"
                    className="premium-input"
                    style={{ width: '100%', height: '42px', padding: '0 16px', boxSizing: 'border-box', fontFamily: 'Outfit' }}
                    placeholder="مفتوح"
                    value={formMaxCoverage}
                    onChange={e => setFormMaxCoverage(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>الحد الشهري للمريض (ج.م)</label>
                  <input 
                    type="number"
                    min="0"
                    className="premium-input"
                    style={{ width: '100%', height: '42px', padding: '0 16px', boxSizing: 'border-box', fontFamily: 'Outfit' }}
                    placeholder="مفتوح"
                    value={formMonthlyCoverageLimit}
                    onChange={e => setFormMonthlyCoverageLimit(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>طلب موافقة إذا تجاوز (ج.م)</label>
                  <input 
                    type="number"
                    min="0"
                    className="premium-input"
                    style={{ width: '100%', height: '42px', padding: '0 16px', boxSizing: 'border-box', fontFamily: 'Outfit' }}
                    placeholder="لا يطلب"
                    value={formRequireApprovalAbove}
                    onChange={e => setFormRequireApprovalAbove(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', justifyContent: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', color: '#fff', fontWeight: 'bold', cursor: 'pointer', marginTop: '20px' }}>
                    <input 
                      type="checkbox"
                      checked={formAllowDiscounts}
                      onChange={e => setFormAllowDiscounts(e.target.checked)}
                      style={{ accentColor: 'var(--primary)', width: '18px', height: '18px' }}
                    />
                    السماح بدمج الخصومات والعروض
                  </label>
                </div>
              </div>

              {/* Excluded Categories Checkbox Grid */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>التصنيفات المستبعدة من التغطية</label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  maxHeight: '120px',
                  overflowY: 'auto',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  padding: '10px'
                }}>
                  {Array.from(new Set((inventory || []).map(item => item.category).filter(Boolean))).map(cat => {
                    const isChecked = formExcludedCats.includes(cat);
                    return (
                      <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#fff', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            if (isChecked) {
                              setFormExcludedCats(formExcludedCats.filter(c => c !== cat));
                            } else {
                              setFormExcludedCats([...formExcludedCats, cat]);
                            }
                          }}
                          style={{ accentColor: 'var(--primary)' }}
                        />
                        {cat}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>ملاحظات وشروط إضافية</label>
                <textarea
                  className="premium-input"
                  style={{ width: '100%', minHeight: '80px', padding: '10px 16px', boxSizing: 'border-box', resize: 'vertical' }}
                  placeholder="اكتب أي شروط خاصة أو ملاحظات لعرضها للكاشير..."
                  value={formNotes}
                  onChange={e => setFormNotes(e.target.value)}
                />
              </div>

              <button 
                type="submit"
                className="gradient-btn"
                style={{ padding: '14px', fontWeight: 'bold', fontSize: '1rem', marginTop: '12px', border: 'none', borderRadius: '12px', cursor: 'pointer' }}
              >
                💾 حفظ التعاقد
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// 3.7.5. Discounts & Campaigns Management View (مكون إدارة العروض والخصومات والبروموكود)
// ─────────────────────────────────────────────────────────────────────────────
function DiscountsView({ user, db, setGlobalLoading, inventory, setInventory }) {
  const [activeSubTab, setActiveSubTab] = useState('general'); // general, products, promo_codes
  
  // General Campaigns
  const [generalCampaigns, setGeneralCampaigns] = useState([]);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [campName, setCampName] = useState('');
  const [campType, setCampType] = useState('percentage'); // percentage, fixed
  const [campValue, setCampValue] = useState('');
  const [campMinTotal, setCampMinTotal] = useState('0');
  const [campIsActive, setCampIsActive] = useState(true);
  const [editingCamp, setEditingCamp] = useState(null);

  // Product Discounts
  const [prodSearchQuery, setProdSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [prodDiscountType, setProdDiscountType] = useState('percentage'); // percentage, fixed
  const [prodDiscountValue, setProdDiscountValue] = useState('');
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  // Promo Codes (كوبونات الخصم)
  const [promoCodes, setPromoCodes] = useState([]);
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoCodeName, setPromoCodeName] = useState('');
  const [promoDiscountType, setPromoDiscountType] = useState('percentage'); // percentage, fixed
  const [promoDiscountValue, setPromoDiscountValue] = useState('');
  const [promoMinTotal, setPromoMinTotal] = useState('0');
  const [promoExpiry, setPromoExpiry] = useState('');
  const [promoUsageLimit, setPromoUsageLimit] = useState('');
  const [promoIsActive, setPromoIsActive] = useState(true);

  // Load General Campaigns & Promo codes from Firestore
  useEffect(() => {
    if (!user) return;
    const loadDiscountsData = async () => {
      if (setGlobalLoading) {
        setGlobalLoading({ active: true, title: 'تحميل البيانات 🏷️', message: 'جاري استدعاء العروض والبروموكود من السيرفر...' });
      }
      try {
        // Load Campaigns
        const campSnap = await getDocs(collection(db, 'users', user.uid, 'campaigns'));
        const campList = campSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setGeneralCampaigns(campList);

        // Load Promo Codes
        const promoSnap = await getDocs(collection(db, 'users', user.uid, 'promoCodes'));
        const promoList = promoSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPromoCodes(promoList);
      } catch (err) {
        console.error("Error loading discounts:", err);
      } finally {
        if (setGlobalLoading) setGlobalLoading({ active: false });
      }
    };
    loadDiscountsData();
  }, [user]);

  // Save General Campaign
  const handleSaveCampaign = async (e) => {
    e.preventDefault();
    if (!campName.trim() || !campValue) return;

    if (setGlobalLoading) setGlobalLoading({ active: true, title: 'حفظ العرض 💾', message: 'جاري تسجيل بيانات العرض...' });
    try {
      const campData = {
        name: campName.trim(),
        type: campType,
        value: parseFloat(campValue) || 0,
        minTotal: parseFloat(campMinTotal) || 0,
        isActive: campIsActive,
        updatedAt: Date.now()
      };

      if (editingCamp) {
        await setDoc(doc(db, 'users', user.uid, 'campaigns', editingCamp.id), campData, { merge: true });
        setGeneralCampaigns(prev => prev.map(c => c.id === editingCamp.id ? { ...c, ...campData } : c));
      } else {
        const newRef = doc(collection(db, 'users', user.uid, 'campaigns'));
        await setDoc(newRef, campData);
        setGeneralCampaigns(prev => [...prev, { id: newRef.id, ...campData }]);
      }

      setIsCampaignModalOpen(false);
      setEditingCamp(null);
      setCampName('');
      setCampValue('');
      setCampMinTotal('0');
      setCampIsActive(true);
      if (window.triggerDrMasrToast) window.triggerDrMasrToast("تم حفظ العرض بنجاح! 🏷️");
    } catch (err) {
      console.error(err);
    } finally {
      if (setGlobalLoading) setGlobalLoading({ active: false });
    }
  };

  // Toggle Campaign active status
  const handleToggleCampaignStatus = async (camp) => {
    try {
      const newStatus = !camp.isActive;
      await setDoc(doc(db, 'users', user.uid, 'campaigns', camp.id), { isActive: newStatus }, { merge: true });
      setGeneralCampaigns(prev => prev.map(c => c.id === camp.id ? { ...c, isActive: newStatus } : c));
      if (window.triggerDrMasrToast) window.triggerDrMasrToast(newStatus ? "تم تفعيل العرض! ✅" : "تم إيقاف العرض! 🔒");
    } catch (err) {
      console.error(err);
    }
  };

  // Delete Campaign
  const handleDeleteCampaign = async (campId) => {
    if (!window.confirm("هل أنت متأكد من رغبتك في حذف هذا العرض بالكامل؟")) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'campaigns', campId));
      setGeneralCampaigns(prev => prev.filter(c => c.id !== campId));
      if (window.triggerDrMasrToast) window.triggerDrMasrToast("تم حذف العرض بنجاح! 🗑️");
    } catch (err) {
      console.error(err);
    }
  };

  // Apply discount to specific product
  const handleApplyProductDiscount = async (e) => {
    e.preventDefault();
    if (!selectedProduct || !prodDiscountValue) return;

    if (setGlobalLoading) setGlobalLoading({ active: true, title: 'تطبيق الخصم 💊', message: 'جاري تعديل سعر وتفاصيل المنتج...' });
    try {
      const discountVal = parseFloat(prodDiscountValue) || 0;
      let calculatedPrice = selectedProduct.sellingPrice;

      if (prodDiscountType === 'percentage') {
        calculatedPrice = selectedProduct.sellingPrice * (1 - (discountVal / 100));
      } else {
        calculatedPrice = Math.max(0, selectedProduct.sellingPrice - discountVal);
      }
      calculatedPrice = Math.round(calculatedPrice * 100) / 100;

      const updatedFields = {
        discountType: prodDiscountType,
        discountValue: discountVal,
        discountedPrice: calculatedPrice,
        hasDiscount: discountVal > 0
      };

      // Save to Firebase
      await setDoc(doc(db, 'users', user.uid, 'inventory', selectedProduct.id), updatedFields, { merge: true });
      
      // Update local inventory state
      setInventory(prev => prev.map(item => item.id === selectedProduct.id ? { ...item, ...updatedFields } : item));

      setIsProductModalOpen(false);
      setSelectedProduct(null);
      setProdDiscountValue('');
      setProdSearchQuery('');
      if (window.triggerDrMasrToast) window.triggerDrMasrToast("تم تطبيق الخصم الخاص على المنتج بنجاح! 💊");
    } catch (err) {
      console.error(err);
    } finally {
      if (setGlobalLoading) setGlobalLoading({ active: false });
    }
  };

  // Remove discount from a product
  const handleRemoveProductDiscount = async (item) => {
    if (!window.confirm(`هل تريد إزالة الخصم عن المنتج "${item.tradeName}" والرجوع للسعر الأصلي؟`)) return;
    if (setGlobalLoading) setGlobalLoading({ active: true, title: 'إلغاء الخصم 🧹', message: 'جاري إعادة المنتج لسعره الطبيعي...' });
    try {
      const updatedFields = {
        discountType: '',
        discountValue: 0,
        discountedPrice: item.sellingPrice,
        hasDiscount: false
      };
      await setDoc(doc(db, 'users', user.uid, 'inventory', item.id), updatedFields, { merge: true });
      setInventory(prev => prev.map(p => p.id === item.id ? { ...p, ...updatedFields } : p));
      if (window.triggerDrMasrToast) window.triggerDrMasrToast("تم إرجاع سعر المنتج الأصلي! ✅");
    } catch (err) {
      console.error(err);
    } finally {
      if (setGlobalLoading) setGlobalLoading({ active: false });
    }
  };

  // Save Promo Code
  const handleSavePromo = async (e) => {
    e.preventDefault();
    if (!promoCodeName.trim() || !promoDiscountValue) return;

    if (setGlobalLoading) setGlobalLoading({ active: true, title: 'حفظ الكوبون 💾', message: 'جاري تسجيل الكوبون الترويجي...' });
    try {
      const promoData = {
        code: promoCodeName.trim().toUpperCase(),
        type: promoDiscountType,
        value: parseFloat(promoDiscountValue) || 0,
        minTotal: parseFloat(promoMinTotal) || 0,
        expiryDate: promoExpiry,
        usageLimit: parseInt(promoUsageLimit) || '',
        isActive: promoIsActive,
        updatedAt: Date.now()
      };

      if (editingPromo) {
        await setDoc(doc(db, 'users', user.uid, 'promoCodes', editingPromo.id), promoData, { merge: true });
        setPromoCodes(prev => prev.map(p => p.id === editingPromo.id ? { ...p, ...promoData } : p));
      } else {
        const newRef = doc(collection(db, 'users', user.uid, 'promoCodes'));
        await setDoc(newRef, promoData);
        setPromoCodes(prev => [...prev, { id: newRef.id, ...promoData }]);
      }

      setIsPromoModalOpen(false);
      setEditingPromo(null);
      setPromoCodeName('');
      setPromoDiscountValue('');
      setPromoMinTotal('0');
      setPromoExpiry('');
      setPromoUsageLimit('');
      setPromoIsActive(true);
      if (window.triggerDrMasrToast) window.triggerDrMasrToast("تم حفظ الكوبون الترويجي بنجاح! 🎟️");
    } catch (err) {
      console.error(err);
    } finally {
      if (setGlobalLoading) setGlobalLoading({ active: false });
    }
  };

  const handleTogglePromoStatus = async (promo) => {
    try {
      const newStatus = !promo.isActive;
      await setDoc(doc(db, 'users', user.uid, 'promoCodes', promo.id), { isActive: newStatus }, { merge: true });
      setPromoCodes(prev => prev.map(p => p.id === promo.id ? { ...p, isActive: newStatus } : p));
      if (window.triggerDrMasrToast) window.triggerDrMasrToast(newStatus ? "تم تفعيل البروموكود! ✅" : "تم تعطيل البروموكود! 🔒");
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا البروموكود؟")) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'promoCodes', promoId));
      setPromoCodes(prev => prev.filter(p => p.id !== promoId));
      if (window.triggerDrMasrToast) window.triggerDrMasrToast("تم حذف البروموكود! 🗑️");
    } catch (err) {
      console.error(err);
    }
  };

  // Filtering products for specific discount tab
  const productsWithDiscounts = inventory.filter(item => item.hasDiscount);
  const searchResults = prodSearchQuery.trim()
    ? inventory.filter(item => 
        (item.tradeName && item.tradeName.toLowerCase().includes(prodSearchQuery.toLowerCase())) ||
        (item.barcode && item.barcode.includes(prodSearchQuery))
      ).slice(0, 5)
    : [];

  return (
    <div className="fade-in-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px', direction: 'rtl', textAlign: 'right' }}>
      
      {/* Upper Navigation Tabs */}
      <div style={{ display: 'flex', gap: '12px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
        <button 
          onClick={() => setActiveSubTab('general')}
          style={{
            padding: '10px 20px', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', border: 'none',
            background: activeSubTab === 'general' ? 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)' : 'rgba(255,255,255,0.03)',
            color: activeSubTab === 'general' ? '#fff' : 'var(--text-secondary)',
            boxShadow: activeSubTab === 'general' ? '0 4px 15px rgba(59, 130, 246, 0.25)' : 'none',
            transition: 'all 0.3s ease'
          }}
        >
          📢 عروض الفواتير العامة
        </button>
        <button 
          onClick={() => setActiveSubTab('products')}
          style={{
            padding: '10px 20px', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', border: 'none',
            background: activeSubTab === 'products' ? 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)' : 'rgba(255,255,255,0.03)',
            color: activeSubTab === 'products' ? '#fff' : 'var(--text-secondary)',
            boxShadow: activeSubTab === 'products' ? '0 4px 15px rgba(59, 130, 246, 0.25)' : 'none',
            transition: 'all 0.3s ease'
          }}
        >
          💊 خصومات المنتجات الخاصة
        </button>
        <button 
          onClick={() => setActiveSubTab('promo_codes')}
          style={{
            padding: '10px 20px', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', border: 'none',
            background: activeSubTab === 'promo_codes' ? 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)' : 'rgba(255,255,255,0.03)',
            color: activeSubTab === 'promo_codes' ? '#fff' : 'var(--text-secondary)',
            boxShadow: activeSubTab === 'promo_codes' ? '0 4px 15px rgba(59, 130, 246, 0.25)' : 'none',
            transition: 'all 0.3s ease'
          }}
        >
          🎟️ الكوبونات والبروموكود
        </button>
      </div>

      {/* Subtab Content: General Campaigns */}
      {activeSubTab === 'general' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff', margin: 0 }}>📢 عروض الفواتير العامة والخصومات للجميع</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: '4px 0 0 0' }}>أنشئ خصومات تطبق تلقائياً على إجمالي الفاتورة عند الوصول لمبلغ معين</p>
            </div>
            <button 
              onClick={() => { setEditingCamp(null); setCampName(''); setCampValue(''); setCampMinTotal('0'); setCampIsActive(true); setIsCampaignModalOpen(true); }}
              className="premium-btn-primary"
              style={{
                padding: '12px 24px', borderRadius: '10px', fontSize: '0.92rem', fontWeight: 700,
                background: 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)', color: '#fff', border: 'none', cursor: 'pointer'
              }}
            >
              ➕ إنشاء عرض فاتورة جديد
            </button>
          </div>

          {generalCampaigns.length === 0 ? (
            <div className="premium-card" style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
              🚫 لا توجد عروض عامة مسجلة حالياً. قم بإضافة عرض جديد للعملاء!
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
              {generalCampaigns.map(camp => (
                <div key={camp.id} className="premium-card" style={{
                  display: 'flex', flexDirection: 'column', gap: '16px',
                  borderTop: `4px solid ${camp.isActive ? 'var(--accent-green)' : '#4b5563'}`,
                  background: 'rgba(13, 20, 36, 0.45)', opacity: camp.isActive ? 1 : 0.8
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ fontSize: '1.18rem', fontWeight: 700, color: '#fff', margin: 0 }}>{camp.name}</h3>
                      <span style={{ 
                        fontSize: '0.75rem', fontWeight: 'bold', padding: '2px 8px', borderRadius: '4px',
                        background: camp.isActive ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255,255,255,0.05)',
                        color: camp.isActive ? '#34d399' : '#9ca3af', display: 'inline-block', marginTop: '6px'
                      }}>
                        {camp.isActive ? '🔓 عرض نشط' : '🔒 معطل ومخفي'}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button 
                        onClick={() => {
                          setEditingCamp(camp);
                          setCampName(camp.name);
                          setCampType(camp.type);
                          setCampValue(camp.value.toString());
                          setCampMinTotal(camp.minTotal.toString());
                          setCampIsActive(camp.isActive);
                          setIsCampaignModalOpen(true);
                        }} 
                        title="تعديل العرض"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', color: '#60a5fa', cursor: 'pointer', padding: '6px' }}
                      >
                        ✏️
                      </button>
                      <button 
                        onClick={() => handleToggleCampaignStatus(camp)} 
                        title={camp.isActive ? 'تعطيل' : 'تفعيل'}
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', color: camp.isActive ? '#fbbf24' : '#34d399', cursor: 'pointer', padding: '6px' }}
                      >
                        {camp.isActive ? '🔒' : '🔓'}
                      </button>
                      <button 
                        onClick={() => handleDeleteCampaign(camp.id)} 
                        title="حذف العرض"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', color: 'var(--accent-pink)', cursor: 'pointer', padding: '6px' }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex', justifyContent: 'space-between', background: 'rgba(255,255,255,0.02)',
                    padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.03)'
                  }}>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block' }}>قيمة الخصم</span>
                      <strong style={{ fontSize: '1.15rem', color: 'var(--primary)', fontFamily: 'Outfit' }}>
                        {camp.value} {camp.type === 'percentage' ? '%' : 'ج.م'}
                      </strong>
                    </div>
                    <div style={{ borderLeft: '1px solid rgba(255,255,255,0.06)' }}></div>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block' }}>الحد الأدنى للفاتورة</span>
                      <strong style={{ fontSize: '1.15rem', color: '#fff', fontFamily: 'Outfit' }}>{camp.minTotal} ج.م</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Subtab Content: Products Discounts */}
      {activeSubTab === 'products' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff', margin: 0 }}>💊 خصومات وتخفيضات المنتجات الخاصة</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: '4px 0 0 0' }}>قم باستهداف منتج معين لعمل خصم خاص عليه، وتخفيض سعره الأصلي في الكاشير</p>
            </div>
            <button 
              onClick={() => { setSelectedProduct(null); setProdSearchQuery(''); setProdDiscountValue(''); setIsProductModalOpen(true); }}
              className="premium-btn-primary"
              style={{
                padding: '12px 24px', borderRadius: '10px', fontSize: '0.92rem', fontWeight: 700,
                background: 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)', color: '#fff', border: 'none', cursor: 'pointer'
              }}
            >
              ➕ خصم لمنتج معين
            </button>
          </div>

          {productsWithDiscounts.length === 0 ? (
            <div className="premium-card" style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
              🚫 لا توجد منتجات مسجل عليها خصومات خاصة حالياً. انقر على الزر بالأعلى لتخفيض سعر منتج.
            </div>
          ) : (
            <div className="premium-card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>اسم المنتج</th>
                    <th>الباركود</th>
                    <th>السعر الأصلي</th>
                    <th>نسبة/قيمة الخصم</th>
                    <th>السعر بعد الخصم</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {productsWithDiscounts.map(item => (
                    <tr key={item.id}>
                      <td style={{ fontWeight: 700, color: '#fff' }}>{item.tradeName}</td>
                      <td style={{ fontFamily: 'monospace' }}>{item.barcode || 'لا يوجد'}</td>
                      <td>{item.sellingPrice} ج.م</td>
                      <td style={{ color: 'var(--primary)', fontWeight: 'bold' }}>
                        {item.discountValue} {item.discountType === 'percentage' ? '%' : 'ج.م'}
                      </td>
                      <td style={{ color: 'var(--accent-green)', fontWeight: 700 }}>{item.discountedPrice} ج.م</td>
                      <td>
                        <button 
                          onClick={() => handleRemoveProductDiscount(item)}
                          className="gradient-btn-secondary"
                          style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '8px', color: 'var(--accent-pink)' }}
                        >
                          🗑️ إلغاء الخصم
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Subtab Content: Promo Codes */}
      {activeSubTab === 'promo_codes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff', margin: 0 }}>🎟️ الكوبونات والبروموكود (Promo Codes)</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: '4px 0 0 0' }}>أنشئ كوبونات خصم يدوية ليعرضها عملائك في الصيدلية لخصم فوري</p>
            </div>
            <button 
              onClick={() => { setEditingPromo(null); setPromoCodeName(''); setPromoDiscountValue(''); setPromoMinTotal('0'); setPromoExpiry(''); setPromoUsageLimit(''); setPromoIsActive(true); setIsPromoModalOpen(true); }}
              className="premium-btn-primary"
              style={{
                padding: '12px 24px', borderRadius: '10px', fontSize: '0.92rem', fontWeight: 700,
                background: 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)', color: '#fff', border: 'none', cursor: 'pointer'
              }}
            >
              ➕ إنشاء كود خصم جديد
            </button>
          </div>

          {promoCodes.length === 0 ? (
            <div className="premium-card" style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
              🚫 لا توجد بروموكودات مسجلة حالياً. قم بإضافة بروموكود جديد للعملاء!
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
              {promoCodes.map(promo => (
                <div key={promo.id} className="premium-card" style={{
                  display: 'flex', flexDirection: 'column', gap: '16px',
                  borderTop: `4px solid ${promo.isActive ? 'var(--primary)' : '#4b5563'}`,
                  background: 'rgba(13, 20, 36, 0.45)', opacity: promo.isActive ? 1 : 0.8
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <strong style={{ fontSize: '1.25rem', color: '#38bdf8', fontFamily: 'Outfit', letterSpacing: '1px' }}>{promo.code}</strong>
                      <span style={{ 
                        fontSize: '0.75rem', fontWeight: 'bold', padding: '2px 8px', borderRadius: '4px',
                        background: promo.isActive ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255,255,255,0.05)',
                        color: promo.isActive ? '#38bdf8' : '#9ca3af', display: 'block', marginTop: '6px'
                      }}>
                        {promo.isActive ? '🔓 كوبون نشط' : '🔒 معطل ومخفي'}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button 
                        onClick={() => {
                          setEditingPromo(promo);
                          setPromoCodeName(promo.code);
                          setPromoDiscountType(promo.type);
                          setPromoDiscountValue(promo.value.toString());
                          setPromoMinTotal(promo.minTotal.toString());
                          setPromoExpiry(promo.expiryDate || '');
                          setPromoUsageLimit(promo.usageLimit || '');
                          setPromoIsActive(promo.isActive);
                          setIsPromoModalOpen(true);
                        }} 
                        title="تعديل"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', color: '#60a5fa', cursor: 'pointer', padding: '6px' }}
                      >
                        ✏️
                      </button>
                      <button 
                        onClick={() => handleTogglePromoStatus(promo)} 
                        title={promo.isActive ? 'تعطيل' : 'تفعيل'}
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', color: promo.isActive ? '#fbbf24' : '#34d399', cursor: 'pointer', padding: '6px' }}
                      >
                        {promo.isActive ? '🔒' : '🔓'}
                      </button>
                      <button 
                        onClick={() => handleDeletePromo(promo.id)} 
                        title="حذف"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', color: 'var(--accent-pink)', cursor: 'pointer', padding: '6px' }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr', background: 'rgba(255,255,255,0.02)',
                    padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.03)', gap: '10px'
                  }}>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>قيمة الخصم:</span>
                      <div style={{ fontSize: '1rem', color: 'var(--accent-green)', fontWeight: 'bold' }}>
                        {promo.value} {promo.type === 'percentage' ? '%' : 'ج.م'}
                      </div>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>الحد الأدنى:</span>
                      <div style={{ fontSize: '1rem', color: '#fff' }}>{promo.minTotal} ج.م</div>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>تاريخ الصلاحية:</span>
                      <div style={{ fontSize: '0.8rem', color: '#fff' }}>{typeof promo.expiryDate === 'object' && promo.expiryDate ? (promo.expiryDate.year ? `${promo.expiryDate.year}-${String(promo.expiryDate.month).padStart(2, '0')}-${String(promo.expiryDate.day).padStart(2, '0')}` : JSON.stringify(promo.expiryDate)) : (promo.expiryDate || 'مفتوح')}</div>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>حد الاستخدام:</span>
                      <div style={{ fontSize: '1rem', color: '#fff' }}>{promo.usageLimit || 'لا نهائي'}</div>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>عدد مرات الاستخدام:</span>
                      <div style={{ fontSize: '1rem', color: 'var(--accent-gold)', fontWeight: 'bold' }}>{promo.usageCount || 0}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* MODAL 1: General Campaign Add/Edit */}
      {isCampaignModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
          <div className="premium-card" style={{ width: '100%', maxWidth: '480px', background: '#141229', border: '1px solid rgba(139, 92, 246, 0.25)', borderRadius: '16px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                {editingCamp ? '✏️ تعديل عرض الفاتورة' : '📢 إنشاء عرض فاتورة جديد'}
              </h3>
              <button onClick={() => setIsCampaignModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
            </div>

            <form onSubmit={handleSaveCampaign} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>اسم العرض الترويجي</label>
                <input type="text" required className="premium-input" placeholder="مثال: عرض نهاية العام" value={campName} onChange={e => setCampName(e.target.value)} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>نوع الخصم</label>
                  <select className="premium-input" value={campType} onChange={e => setCampType(e.target.value)}>
                    <option value="percentage">نسبة مئوية (%)</option>
                    <option value="fixed">مبلغ ثابت (ج.م)</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>قيمة الخصم</label>
                  <input type="number" step="any" required min="0.1" className="premium-input" placeholder="10" value={campValue} onChange={e => setCampValue(e.target.value)} />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>الحد الأدنى لقيمة الفاتورة لتطبيق العرض (ج.م)</label>
                <input type="number" required className="premium-input" placeholder="500" value={campMinTotal} onChange={e => setCampMinTotal(e.target.value)} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                <input type="checkbox" id="campActive" checked={campIsActive} onChange={e => setCampIsActive(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                <label htmlFor="campActive" style={{ color: '#fff', fontSize: '0.9rem', cursor: 'pointer' }}>تفعيل العرض فوراً للعملاء</label>
              </div>

              <button type="submit" className="gradient-btn" style={{ padding: '14px', fontWeight: 'bold', fontSize: '1rem', marginTop: '12px', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
                💾 حفظ وتعميم العرض
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Apply Discount to Specific Product */}
      {isProductModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
          <div className="premium-card" style={{ width: '100%', maxWidth: '480px', background: '#141229', border: '1px solid rgba(139, 92, 246, 0.25)', borderRadius: '16px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>💊 خصم وتخفيض منتج معين</h3>
              <button onClick={() => setIsProductModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
            </div>

            <form onSubmit={handleApplyProductDiscount} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', position: 'relative' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>ابحث عن المنتج المراد تخفيضه</label>
                <input 
                  type="text" 
                  className="premium-input" 
                  placeholder="اكتب اسم المنتج أو الباركود..." 
                  value={selectedProduct ? selectedProduct.tradeName : prodSearchQuery} 
                  disabled={!!selectedProduct}
                  onChange={e => setProdSearchQuery(e.target.value)} 
                />
                {selectedProduct && (
                  <button 
                    type="button" 
                    onClick={() => setSelectedProduct(null)} 
                    style={{ position: 'absolute', left: '10px', top: '35px', background: 'rgba(239,68,68,0.2)', border: 'none', color: '#ef4444', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '0.75rem' }}
                  >
                    تغيير المنتج ✕
                  </button>
                )}

                {/* Autocomplete suggestions */}
                {!selectedProduct && searchResults.length > 0 && (
                  <div style={{ position: 'absolute', right: 0, left: 0, top: '70px', background: '#1e1b4b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', zIndex: 1000, overflow: 'hidden' }}>
                    {searchResults.map(item => (
                      <div 
                        key={item.id} 
                        onClick={() => setSelectedProduct(item)}
                        style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', color: '#fff' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div>{item.tradeName}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>السعر: {item.sellingPrice} ج.م | الباركود: {item.barcode || 'لا يوجد'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedProduct && (
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)', fontSize: '0.9rem' }}>
                  السعر الأصلي الحالي للمنتج: <strong style={{ color: '#fff' }}>{selectedProduct.sellingPrice} ج.م</strong>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>نوع الخصم</label>
                  <select className="premium-input" value={prodDiscountType} onChange={e => setProdDiscountType(e.target.value)}>
                    <option value="percentage">نسبة مئوية (%)</option>
                    <option value="fixed">مبلغ ثابت (ج.م)</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>قيمة الخصم</label>
                  <input type="number" step="any" required min="0.1" className="premium-input" placeholder="15" value={prodDiscountValue} onChange={e => setProdDiscountValue(e.target.value)} />
                </div>
              </div>

              <button type="submit" disabled={!selectedProduct} className="gradient-btn" style={{ padding: '14px', fontWeight: 'bold', fontSize: '1rem', marginTop: '12px', border: 'none', borderRadius: '12px', cursor: selectedProduct ? 'pointer' : 'not-allowed', opacity: selectedProduct ? 1 : 0.6 }}>
                💾 تطبيق وتخفيض السعر للمنتج
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: Promo Code Add/Edit */}
      {isPromoModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
          <div className="premium-card" style={{ width: '100%', maxWidth: '480px', background: '#141229', border: '1px solid rgba(139, 92, 246, 0.25)', borderRadius: '16px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                {editingPromo ? '✏️ تعديل كود الخصم' : '🎟️ إنشاء بروموكود جديد'}
              </h3>
              <button onClick={() => setIsPromoModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
            </div>

            <form onSubmit={handleSavePromo} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>كود الخصم (Promo Code)</label>
                <input type="text" required className="premium-input" style={{ textTransform: 'uppercase', fontFamily: 'Outfit', fontWeight: 'bold' }} placeholder="مثال: MASR10" value={promoCodeName} onChange={e => setPromoCodeName(e.target.value)} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>نوع الخصم</label>
                  <select className="premium-input" value={promoDiscountType} onChange={e => setPromoDiscountType(e.target.value)}>
                    <option value="percentage">نسبة مئوية (%)</option>
                    <option value="fixed">مبلغ ثابت (ج.م)</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>قيمة الخصم</label>
                  <input type="number" step="any" required min="0.1" className="premium-input" placeholder="10" value={promoDiscountValue} onChange={e => setPromoDiscountValue(e.target.value)} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>الحد الأدنى للفاتورة</label>
                  <input type="number" className="premium-input" placeholder="100" value={promoMinTotal} onChange={e => setPromoMinTotal(e.target.value)} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>حد عدد مرات الاستخدام</label>
                  <input type="number" className="premium-input" placeholder="لا نهائي" value={promoUsageLimit} onChange={e => setPromoUsageLimit(e.target.value)} />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>تاريخ انتهاء الصلاحية</label>
                <input type="date" className="premium-input" style={{ fontFamily: 'Outfit' }} value={promoExpiry} onChange={e => setPromoExpiry(e.target.value)} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                <input type="checkbox" id="promoActive" checked={promoIsActive} onChange={e => setPromoIsActive(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                <label htmlFor="promoActive" style={{ color: '#fff', fontSize: '0.9rem', cursor: 'pointer' }}>تفعيل الكود فوراً</label>
              </div>

              <button type="submit" className="gradient-btn" style={{ padding: '14px', fontWeight: 'bold', fontSize: '1rem', marginTop: '12px', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
                💾 حفظ الكود الترويجي
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3.8. Purchases & Suppliers Management View (مكون إدارة المشتريات والموردين)
// ─────────────────────────────────────────────────────────────────────────────
